import React, { useState, useEffect, memo, useMemo, useCallback, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useDropzone } from "react-dropzone";
import SalaryPayments from "./components/SalaryPayments";
import { PaginationBar } from "./components/PaginationBar";
import { UpcomingNoteReminders } from "./components/UpcomingNoteReminders";
import { BackupHistoryCard } from "./components/BackupHistoryCard";
import { CollapsibleSection } from "./components/dashboard/CollapsibleSection";
import { PeriodStatSection } from "./components/dashboard/PeriodStatSection";
import { RecentOperations } from "./components/dashboard/RecentOperations";
import { BoyahaneList } from "./components/production/BoyahaneList";
import { StaffActivityLog } from "./components/production/StaffActivityLog";
import { InstallationTabPermissions } from "./components/users/InstallationTabPermissions";
import { ViewOnlyWrap } from "./components/ViewOnlyWrap";
import { ExpenseCategoryManager, ExpenseCategorySelect, ExpenseFilterBar } from "./components/expenses/ExpenseCategories";
import { SupplierRecentOps } from "./components/suppliers/SupplierRecentOps";
import { ReportsOverviewExtra } from "./components/reports/ReportsOverviewExtra";
import { ProductionLineExtra } from "./components/reports/ProductionLineExtra";
import { MonthlyTargets } from "./components/reports/MonthlyTargets";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import autoTable from "jspdf-autotable";

// ⚡ PERFORMANCE: Debounce helper for search inputs
const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
import {
  Home,
  User,
  Users,
  UserCheck,
  UserPlus,
  Package,
  DollarSign,
  ShoppingCart,
  Receipt,
  CreditCard,
  Banknote,
  ClipboardList,
  ClipboardCheck,
  Clipboard,
  Wrench,
  Factory,
  TrendingUp,
  TrendingDown,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  Plus,
  Search,
  CheckCircle,
  AlertTriangle,
  ArrowUpDown,
  Phone,
  MapPin,
  Download,
  FileText,
  Calendar as CalendarIcon,
  AlertCircle,
  XCircle,
  Upload,
  Printer,
  Building2,
  Settings,
  Send,
  Check,
  X,
  Menu,
  Cloud,
  Info,
  Archive,
  ArrowRight,
  ArrowLeft,
  ArrowLeftRight,
  Calendar,
  Wallet,
  Calculator,
  ChevronDown,
  ChevronRight,
  Database,
  Shield,
  Activity,
  Clock,
  HardDrive,
  RefreshCw,
  BarChart3,
  List,
  RotateCcw,
  LogIn,
  LogOut,
  Layers,
  BookOpen,
  Save,
  Bell,
  Target,
  Copy,
  StickyNote,
  BellRing,
  Loader2
} from "lucide-react";

// Runtime'da domain kontrolü: eskay.online gibi custom domain'lerde boş string kullan (relative path)
// Preview ve localhost'ta env var'daki URL'yi kullan
const BACKEND_URL = (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com'))
  ? ''
  : (process.env.REACT_APP_BACKEND_URL || '');
const API = `${BACKEND_URL}/api`;

const PRODUCTION_TABS = [
  { key: 'lake_beklemede', label: 'LAKE • BEKLEMEDE' },
  { key: 'lake_uretimde', label: 'LAKE • ÜRETİMDE' },
  { key: 'lake_boyahane', label: 'LAKE • BOYAHANE' },
  { key: 'melamin_beklemede', label: 'MELAMİN • BEKLEMEDE' },
  { key: 'melamin_uretimde', label: 'MELAMİN • ÜRETİMDE' },
  { key: 'melamin_hazir', label: 'MELAMİN • HAZIR' },
];

const INSTALLATION_TABS = [
  { key: 'hazir_melamin', label: 'HAZIR MELAMİN', badge: 'bg-orange-100 text-orange-800' },
  { key: 'hazir_boyahane', label: 'BOYANMIŞ LAKE KAPILAR', badge: 'bg-purple-100 text-purple-800' },
  { key: 'planlandi', label: 'PLANLANDI', badge: 'bg-blue-100 text-blue-800' },
  { key: 'tamamlandi', label: 'TAMAMLANDI', badge: 'bg-green-100 text-green-800' },
];

const normalizeTurkishText = (text) => (text || '').toString().toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c');

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && config.url && config.url.startsWith(API)) {
    config.headers['X-Session-Token'] = token;
    const path = config.url.slice(API.length);
    if ((path.startsWith('/productions/') || path.startsWith('/installation-schedules/')) && !config.url.includes('session_token=')) {
      config.params = { ...(config.params || {}), session_token: config.params?.session_token || token };
    }
  }
  return config;
});

// Ürün özellikleri ve ölçü tablosu listelerinin default/fallback değerleri.
// Backend `/api/product-options` endpoint'i bu yapıyı geri döner.
const DEFAULT_PRODUCT_OPTIONS_JS = {
  models: [
    "QUEEN", "ZEUS DİAMOND", "MERT P1", "MERT P2", "MERT P3", "NEW YORK", "TOKYO",
    "BERLİN", "PARİS", "VENEDİK", "HONG KONG", "LİZBON", "PETRA", "SİNGAPUR",
    "LYON PLUS", "FLORANSA", "SİDNEY", "BUDAPEŞTE", "SAN FRANSİNCO", "PRAG",
    "LYON", "RİO", "İSTANBUL", "MİAMİ", "BLADE", "ROMA", "OSLO", "BARCELONA",
    "BASEL", "SELANİK", "SARMAŞIK", "WANKEL", "LALE", "PLEKSİ A", "PALMİYE",
    "HİRA", "NEWA", "MODEL X", "PATRAS", "DALGA", "DİKDÖRTGEN", "ARTI", "PORTO",
    "ZAMBAK", "ATLANTİS", "ZEUS", "MELAMİN MERDİVEN", "MELAMİN TEK GÖBEK"
  ],
  colors: ["BEYAZ", "K-168", "RALL 7044"],
  door_handles: ["SİYAH PRİZMA", "KROM"],
  surfaces: ["6mm", "8mm", "10mm", "18mm", "6mm-18mm", "MELAMİN"],
  installations: ["DAHİL", "HARİÇ"],
  installation_locations: ["BOŞ", "DOLU"],
  inner_frames: ["DÜZ", "DEKORATİF"],
  outer_frames: ["DÜZ", "DEKORATİF"],
  widths: [61,66,71,76,81,86,91,96,101,106,111,116,121,126,131,136,141,146,151,156,161,166,171,176,181,186,191,196],
  heights: [190,191,192,193,194,195,196,197,198,199,200,201,202,203,204,205,206,207,208,209],
  frames: [8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30],
  directions: ["SAĞ", "SOL", "DIŞA SAĞ", "DIŞA SOL", "SÜRGÜLÜ"],
  glasses: ["CAMLI", "KAPALI"],
  rooms: ["YATAK ODASI", "OTURMA ODASI", "MUTFAK", "BANYO", "EVEBEYN BANYOSU", "HOL"],
};

const PRODUCT_OPTIONS_META = [
  { key: "models", label: "Model", numeric: false },
  { key: "colors", label: "Renk", numeric: false },
  { key: "door_handles", label: "Kapı Kolu", numeric: false },
  { key: "surfaces", label: "Yüzey", numeric: false },
  { key: "installations", label: "Montaj", numeric: false },
  { key: "installation_locations", label: "Montaj Yeri", numeric: false },
  { key: "inner_frames", label: "İç Çerçeve", numeric: false },
  { key: "outer_frames", label: "Dış Çerçeve", numeric: false },
  { key: "directions", label: "Yön", numeric: false },
  { key: "glasses", label: "Cam", numeric: false },
  { key: "rooms", label: "Oda", numeric: false },
  { key: "widths", label: "En (cm)", numeric: true },
  { key: "heights", label: "Boy (cm)", numeric: true },
  { key: "frames", label: "Kasa", numeric: true },
];

// Binlik ayırıcı fonksiyonları
const formatNumberWithThousandsSeparator = (value) => {
  // Sayıyı al, noktaları kaldır
  const numericValue = value.toString().replace(/\./g, '');
  // Sadece sayıları tut
  const cleanValue = numericValue.replace(/[^\d]/g, '');
  // Binlik ayırıcılarını ekle
  return cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseFormattedNumber = (formattedValue) => {
  // Binlik ayırıcıları kaldır ve sayıya çevir
  return parseFloat(formattedValue.replace(/\./g, '')) || 0;
};

// Installer Dashboard Component - Montaj Elemanı için özel dashboard
const InstallerDashboard = ({ currentUser, sessionToken }) => {
  const [incompleteWorks, setIncompleteWorks] = useState([]);
  const [myInstallations, setMyInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWork, setSelectedWork] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      fetchIncompleteWorks();
      fetchMyInstallations();
    }
  }, [currentUser]);

  // Auto-refresh every 30 seconds for real-time updates
  useEffect(() => {
    if (currentUser?.id) {
      const interval = setInterval(() => {
        fetchIncompleteWorks();
        fetchMyInstallations();
      }, 30000); // 30 saniyede bir yenile

      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchIncompleteWorks = async () => {
    try {
      const response = await axios.get(`${API}/completed-installations-for-collection`, {
        params: { session_token: sessionToken }
      });
      // Sadece bu montaj elemanına ait eksik işleri filtrele
      const myIncompleteWorks = response.data.filter(work => 
        work.installer_id === currentUser?.installer_id && work.has_incomplete_work
      );
      setIncompleteWorks(myIncompleteWorks);
    } catch (error) {
      console.error("Eksik işler yüklenirken hata:", error);
      toast.error("Eksik işler yüklenirken hata oluştu");
    }
  };

  const fetchMyInstallations = async () => {
    try {
      // Tüm installation schedules'ları al ve kendi işlerimi filtrele
      const response = await axios.get(`${API}/installation-schedules`, {
        params: { session_token: sessionToken }
      });
      const myWorks = response.data.filter(installation => 
        installation.installer_id === currentUser?.installer_id
      );
      setMyInstallations(myWorks);
      setLoading(false);
    } catch (error) {
      console.error("Montaj işleri yüklenirken hata:", error);
      toast.error("Montaj işleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const openWorkDetail = async (work) => {
    try {
      // İşin detaylarını al
      if (work.transaction_id) {
        const transactionResponse = await axios.get(`${API}/transactions/${work.transaction_id}`);
        const transaction = transactionResponse.data;
        
        // Müşteri detaylarını al
        let customer = null;
        try {
          const customerResponse = await axios.get(`${API}/customers/${work.customer_id}`);
          customer = customerResponse.data;
        } catch (err) {
          // Bayi olabilir
          try {
            const bayiResponse = await axios.get(`${API}/bayiler/${work.customer_id}`);
            customer = bayiResponse.data;
          } catch (bayiErr) {
            console.error("Müşteri/Bayi bilgisi alınamadı:", bayiErr);
          }
        }
        
        setSelectedWork({
          ...work,
          transaction: transaction,
          customer: customer
        });
        setIsDetailModalOpen(true);
      }
    } catch (error) {
      console.error("İş detayları yüklenirken hata:", error);
      toast.error("İş detayları yüklenirken hata oluştu");
    }
  };

  // Handler functions for incomplete work actions
  const handleCompleteTask = async (work) => {
    try {
      // Mark the incomplete work as completed
      await axios.put(`${API}/installation-schedules/${work.installation_id}/incomplete-work`, {
        has_incomplete_work: false,
        incomplete_work_notes: work.incomplete_work_notes + " - Tamamlandı olarak işaretlendi"
      }, {
        params: { session_token: sessionToken }
      });
      
      toast.success("İş tamamlandı olarak işaretlendi");
      fetchIncompleteWorks(); // Refresh the list
    } catch (error) {
      console.error("İş tamamlama hatası:", error);
      toast.error("İş tamamlama sırasında hata oluştu");
    }
  };

  const handleRescheduleTask = async (work) => {
    // For now, just show a message - this could be expanded to show a date picker
    toast.info("Yeniden tarihlendirme özelliği yakında eklenecek");
    // TODO: Implement rescheduling functionality
  };

  const handleAddTaskNote = async (work) => {
    const note = prompt("İş için not ekleyin:", work.incomplete_work_notes || "");
    if (note !== null) {
      try {
        await axios.put(`${API}/installation-schedules/${work.installation_id}/incomplete-work`, {
          has_incomplete_work: work.has_incomplete_work,
          incomplete_work_notes: note
        }, {
          params: { session_token: sessionToken }
        });
        
        toast.success("Not eklendi");
        fetchIncompleteWorks(); // Refresh the list
      } catch (error) {
        console.error("Not ekleme hatası:", error);
        toast.error("Not ekleme sırasında hata oluştu");
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Montaj Takip Paneli</h1>
              <p className="text-gray-600">Hoş geldin, {currentUser?.name}!</p>
            </div>
          </div>
          <div className="text-right text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Otomatik güncelleme: 30s</span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Eksik ve Yarım İşler</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{incompleteWorks.length}</div>
            <p className="text-xs text-muted-foreground">Acil müdahale gerekiyor</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Toplam İşlerim</CardTitle>
            <Wrench className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{myInstallations.length}</div>
            <p className="text-xs text-muted-foreground">Tüm montaj işleri</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tamamlanan İşler</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {myInstallations.filter(work => work.status === 'tamamlandi').length}
            </div>
            <p className="text-xs text-muted-foreground">Başarıyla tamamlandı</p>
          </CardContent>
        </Card>
      </div>

      {/* Hızlı Erişim Butonları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <Link to="/installations">
              <Button className="w-full h-16 flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white">
                <Wrench className="h-6 w-6" />
                <span className="text-lg font-medium">Montaj İşlerim</span>
              </Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Link to="/collection-tracking">
              <Button className="w-full h-16 flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white">
                <ClipboardList className="h-6 w-6" />
                <span className="text-lg font-medium">Tahsilat Takip</span>
              </Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Button 
              variant="outline" 
              className="w-full h-16 flex items-center justify-center gap-3"
              onClick={() => window.location.reload()}
            >
              <CheckCircle className="h-6 w-6" />
              <span className="text-lg font-medium">Sayfayı Yenile</span>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Eksik ve Yarım İşler */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Eksik ve Yarım İşler ({incompleteWorks.length})
          </CardTitle>
          <CardDescription>
            Müşteri memnuniyeti için en kısa sürede tamamlanması gereken işler
          </CardDescription>
        </CardHeader>
        <CardContent>
          {incompleteWorks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Mükemmel!</h3>
              <p className="text-gray-600">Eksik veya yarım işiniz bulunmuyor.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {incompleteWorks.map((work) => (
                <Card key={work.installation_id} className="border-l-4 border-l-orange-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">{work.customer_name}</h3>
                          <Badge variant="destructive" className="text-xs">
                            Eksik İş
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{work.customer_phone}</p>
                        <p className="text-sm text-gray-500 mb-2">{work.customer_address}</p>
                        {work.incomplete_work_notes && (
                          <div className="mt-2 p-2 bg-orange-50 rounded-md">
                            <p className="text-sm text-orange-800">
                              <strong>Notlar:</strong> {work.incomplete_work_notes}
                            </p>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Montaj Tarihi: {formatDate(work.installation_date)} • 
                          Tamamlanma: {formatDate(work.completed_date)}
                        </p>
                      </div>
                      <div className="flex flex-col space-y-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openWorkDetail(work)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          Sipariş Detayı
                        </Button>
                      </div>
                      
                      {/* İşlem Butonları - Eksik İşler için */}
                      <div className="mt-3 pt-2 border-t border-orange-200 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCompleteTask(work)}
                          className="text-green-600 hover:bg-green-50 hover:text-green-700 border-green-200"
                        >
                          ✅ Tamamlandı
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRescheduleTask(work)}
                          className="text-blue-600 hover:bg-blue-50 hover:text-blue-700 border-blue-200"
                        >
                          📅 Yeniden Tarih Ata
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddTaskNote(work)}
                          className="text-purple-600 hover:bg-purple-50 hover:text-purple-700 border-purple-200"
                        >
                          📝 Not Ekle
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* İş Detay Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              İş Detayları
            </DialogTitle>
          </DialogHeader>
          {selectedWork && (
            <div className="space-y-6">
              {/* Müşteri Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Müşteri Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-medium">Ad Soyad</Label>
                    <p>{selectedWork.customer_name}</p>
                  </div>
                  <div>
                    <Label className="font-medium">Telefon</Label>
                    <p>{selectedWork.customer_phone}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="font-medium">Adres</Label>
                    <p>{selectedWork.customer_address}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Sipariş Detayları */}
              {selectedWork.transaction && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Sipariş Detayları</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-medium">Sipariş Tutarı</Label>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(selectedWork.transaction.amount)}
                      </p>
                    </div>
                    <div>
                      <Label className="font-medium">Sipariş Tarihi</Label>
                      <p>{formatDate(selectedWork.transaction.created_at)}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="font-medium">Açıklama</Label>
                      <p>{selectedWork.transaction.description || 'Açıklama yok'}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Eksik İş Bilgileri */}
              {selectedWork.has_incomplete_work && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg text-orange-600">Eksik İş Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="font-medium">Durum</Label>
                        <Badge variant="destructive" className="ml-2">Eksik İş Var</Badge>
                      </div>
                      <div>
                        <Label className="font-medium">Notlar</Label>
                        <div className="mt-1 p-3 bg-orange-50 border border-orange-200 rounded-md">
                          <p>{selectedWork.incomplete_work_notes || 'Not eklenmemiş'}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setIsDetailModalOpen(false)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dashboard Component - Optimized with React.memo
const Dashboard = React.memo(({ sessionToken, currentUser }) => {
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [cashBankSummary, setCashBankSummary] = useState({});
  const [cashBankLoading, setCashBankLoading] = useState(true);

  useEffect(() => {
    if (sessionToken) {
      fetchDashboardSummary();
      fetchCashBankSummary();
    }
  }, [sessionToken]);

  const fetchDashboardSummary = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/summary`, {
        params: { session_token: sessionToken }
      });
      setSummary(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Dashboard verileri yüklenirken hata:", error);
      toast.error("Dashboard verileri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchCashBankSummary = async () => {
    try {
      const response = await axios.get(`${API}/cash-bank-summary`);
      setCashBankSummary(response.data);
      setCashBankLoading(false);
    } catch (error) {
      console.error("Nakit/Banka özeti yüklenirken hata:", error);
      setCashBankLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      beklemede: { color: 'bg-orange-100 text-orange-800', text: 'Onay Bekliyor' },
      onaylandi: { color: 'bg-green-100 text-green-800', text: 'Onaylandı' },
      reddedildi: { color: 'bg-red-100 text-red-800', text: 'Reddedildi' }
    };
    const config = statusConfig[status] || statusConfig.beklemede;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.text}</span>;
  };

  const parseMeasurements = (measurementsJson) => {
    try {
      return JSON.parse(measurementsJson || '[]');
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <div className="h-8 bg-gray-200 rounded-md animate-pulse mb-2 w-48"></div>
          <div className="h-4 bg-gray-200 rounded-md animate-pulse w-64"></div>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-3"></div>
              <div className="h-6 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-4"></div>
              <div className="flex space-x-2">
                <div className="h-8 bg-gray-200 rounded w-16"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Detaylı İş Raporu ve İstatistikler</p>
        </div>
        {/* ESKAY AHŞAP Logo - Dashboard */}
        <div className="hidden md:block">
          <img 
            src="https://customer-assets.emergentagent.com/job_finsys/artifacts/2r02pdgk_SUNTALAM-F3-K%20Kopyas%C4%B1.png" 
            alt="ESKAY AHŞAP" 
            className="h-10 object-contain opacity-80"
          />
        </div>
      </div>

      <UpcomingNoteReminders api={API} sessionToken={sessionToken} />

      <RecentOperations api={API} sessionToken={sessionToken} isAdmin={currentUser?.role === 'admin'} />

      {/* === GÜNLÜK SATIŞ ORTALAMASI VE TAHMİNİ CİRO === */}
      <CollapsibleSection id="avg" title="Satış Ortalaması & Tahmin" icon={<TrendingUp className="h-5 w-5" />} titleClass="text-indigo-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-indigo-600 uppercase">Günlük Satış Ortalaması</p>
                  <p className="text-2xl font-bold text-indigo-700">
                    ₺{(() => {
                      const today = new Date();
                      const startOfYear = new Date(today.getFullYear(), 0, 1);
                      const daysPassed = Math.ceil((today - startOfYear) / (1000 * 60 * 60 * 24));
                      const dailyAvg = daysPassed > 0 ? (summary.sales?.yearly || 0) / daysPassed : 0;
                      return dailyAvg.toLocaleString('tr-TR', { minimumFractionDigits: 2 });
                    })()}
                  </p>
                  <p className="text-xs text-indigo-500 mt-1">
                    Yıl başından itibaren ({Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24))} gün)
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-indigo-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-amber-600 uppercase">Tahmini Yıl Sonu Ciro</p>
                  <p className="text-2xl font-bold text-amber-700">
                    ₺{(() => {
                      const today = new Date();
                      const startOfYear = new Date(today.getFullYear(), 0, 1);
                      const endOfYear = new Date(today.getFullYear(), 11, 31);
                      const daysPassed = Math.ceil((today - startOfYear) / (1000 * 60 * 60 * 24));
                      const totalDays = Math.ceil((endOfYear - startOfYear) / (1000 * 60 * 60 * 24));
                      const dailyAvg = daysPassed > 0 ? (summary.sales?.yearly || 0) / daysPassed : 0;
                      const projectedYearly = dailyAvg * totalDays;
                      return projectedYearly.toLocaleString('tr-TR', { minimumFractionDigits: 2 });
                    })()}
                  </p>
                  <p className="text-xs text-amber-500 mt-1">
                    {new Date().getFullYear()} yıl sonu tahmini
                  </p>
                </div>
                <Target className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      </CollapsibleSection>

      {/* === CİRO İSTATİSTİKLERİ === */}
      <PeriodStatSection id="sales" category="sales" title="Ciro" icon={<DollarSign className="h-5 w-5" />} titleClass="text-blue-700" values={summary.sales} api={API} sessionToken={sessionToken} />

      {/* === TAHSİLAT İSTATİSTİKLERİ === */}
      <PeriodStatSection id="collections" category="collections" title="Tahsilat" icon={<CreditCard className="h-5 w-5" />} titleClass="text-green-700" values={summary.collections} api={API} sessionToken={sessionToken} />

      {/* === TEMLİK İSTATİSTİKLERİ === */}
      <PeriodStatSection id="temlik" category="temlik" title="Temlik" icon={<ArrowLeftRight className="h-5 w-5" />} titleClass="text-indigo-700" values={summary.temlik} api={API} sessionToken={sessionToken} />

      {/* === ÖDEME İSTATİSTİKLERİ === */}
      <PeriodStatSection id="payments" category="payments" title="Ödemeler" icon={<CreditCard className="h-5 w-5" />} titleClass="text-red-700" values={summary.payments} api={API} sessionToken={sessionToken} />

      {/* === ALACAK/BORÇ İSTATİSTİKLERİ === */}
      <CollapsibleSection id="receivables" title="Alacak/Borç" icon={<span className="text-base">📈</span>} titleClass="text-red-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Müşteri</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-green-600">
                ₺{(summary.receivables?.customers || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Alacak</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Bayi</CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-blue-600">
                ₺{(summary.receivables?.bayiler || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Alacak</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Toplam +</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-purple-600">
                ₺{(summary.receivables?.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Alacak</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Toplam -</CardTitle>
              <TrendingDown className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-red-600">
                ₺{(summary.payables?.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Borç</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-dashed">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Net</CardTitle>
              <Eye className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className={`text-lg font-bold ${
                (summary.financial_status?.net_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                ₺{Math.abs(summary.financial_status?.net_balance || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </div>
              <p className={`text-xs font-medium ${
                (summary.financial_status?.net_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {(summary.financial_status?.net_balance || 0) >= 0 ? '✓ Pozitif' : '⚠️ Negatif'}
              </p>
            </CardContent>
          </Card>
        </div>
      </CollapsibleSection>

      {/* === KAPI ADETİ İSTATİSTİKLERİ === */}
      <CollapsibleSection id="doors" title="Kapı Sayıları" icon={<span className="text-base">🚪</span>} titleClass="text-indigo-700">
        
        {/* Sipariş Verilen Kapılar - Kompakt Görünüm */}
        <div className="mb-4">
          <h3 className="text-md font-medium mb-3 text-gray-600">Siparişler</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Günlük */}
            <Card className="border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Günlük</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-green-600">
                  Mel: {summary.doors?.ordered?.daily?.melamin || 0}
                </div>
                <div className="text-sm font-bold text-green-600">
                  Lake: {summary.doors?.ordered?.daily?.lake || 0}  
                </div>
              </CardContent>
            </Card>

            {/* Aylık */}
            <Card className="border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Aylık</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-blue-600">
                  Mel: {summary.doors?.ordered?.monthly?.melamin || 0}
                </div>
                <div className="text-sm font-bold text-blue-600">
                  Lake: {summary.doors?.ordered?.monthly?.lake || 0}
                </div>
              </CardContent>
            </Card>

            {/* Yıllık */}
            <Card className="border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Yıllık</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-purple-600">
                  Mel: {summary.doors?.ordered?.yearly?.melamin || 0}
                </div>
                <div className="text-sm font-bold text-purple-600">
                  Lake: {summary.doors?.ordered?.yearly?.lake || 0}
                </div>
              </CardContent>
            </Card>

            {/* Teslim Edilen - Günlük */}
            <Card className="border-green-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Teslim G.</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-green-700">
                  Mel: {summary.doors?.delivered?.daily?.melamin || 0}
                </div>
                <div className="text-sm font-bold text-green-700">
                  Lake: {summary.doors?.delivered?.daily?.lake || 0}
                </div>
              </CardContent>
            </Card>

            {/* Teslim Edilen - Aylık */}
            <Card className="border-blue-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Teslim A.</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-blue-700">
                  Mel: {summary.doors?.delivered?.monthly?.melamin || 0}
                </div>
                <div className="text-sm font-bold text-blue-700">
                  Lake: {summary.doors?.delivered?.monthly?.lake || 0}
                </div>
              </CardContent>
            </Card>

            {/* Teslim Edilen - Yıllık */}
            <Card className="border-purple-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Teslim Y.</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-purple-700">
                  Mel: {summary.doors?.delivered?.yearly?.melamin || 0}
                </div>
                <div className="text-sm font-bold text-purple-700">
                  Lake: {summary.doors?.delivered?.yearly?.lake || 0}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        {/* Üretim Durumu - Kompakt */}
        <div className="mb-4">
          <h3 className="text-md font-medium mb-3 text-gray-600">Üretim Durumu</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="border-orange-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Üretim Hattında</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-orange-600">
                  Mel: {summary.doors?.in_production?.melamin || 0} kapı
                </div>
                <div className="text-sm font-bold text-orange-600">
                  Lake: {summary.doors?.in_production?.lake || 0} kapı
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Toplam Kapı</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-gray-600">
                  {(summary.doors?.in_production?.total || 0)} adet
                </div>
                <div className="text-xs text-muted-foreground">
                  üretimde
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-gray-600">Montaj</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-green-600">
                  Günlük: {summary.installations?.daily || 0}
                </div>
                <div className="text-sm font-bold text-green-600">
                  Aylık: {summary.installations?.monthly || 0}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </CollapsibleSection>

      {/* === GENEL BİLGİLER === */}
      <CollapsibleSection id="general" title="Genel" icon={<span className="text-base">📋</span>} titleClass="text-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Müşteriler</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-blue-600">{summary.total_customers || 0}</div>
              <div className="flex space-x-4 mt-1">
                <span className="text-xs text-green-600">A: {summary.active_customers || 0}</span>
                <span className="text-xs text-gray-500">P: {summary.inactive_customers || 0}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Tedarikçi</CardTitle>
              <UserCheck className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-purple-600">{summary.total_suppliers || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Aktif</p>
            </CardContent>
          </Card>
        </div>
      </CollapsibleSection>

      {/* === NAKİT/BANKA HESAPLARI === */}
      <CollapsibleSection id="cashbank" title="Nakit & Banka" icon={<span className="text-base">💳</span>} titleClass="text-green-700">
        {cashBankLoading ? (
          <div className="flex justify-center items-center h-32">Yükleniyor...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Nakit</CardTitle>
                <CreditCard className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">
                  ₺{(cashBankSummary.total_cash || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  G: ₺{(cashBankSummary.cash_receipts || 0).toLocaleString('tr-TR')} | 
                  Ç: ₺{(cashBankSummary.cash_payments || 0).toLocaleString('tr-TR')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Banka</CardTitle>
                <CreditCard className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">
                  ₺{(cashBankSummary.total_bank || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  G: ₺{(cashBankSummary.bank_receipts || 0).toLocaleString('tr-TR')} | 
                  Ç: ₺{(cashBankSummary.bank_payments || 0).toLocaleString('tr-TR')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Toplam</CardTitle>
                <TrendingUp className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-purple-600">
                  ₺{(cashBankSummary.total_balance || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-gray-500 mt-1">N+B</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium text-gray-600 uppercase tracking-wide">Durum</CardTitle>
                <Eye className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-orange-600">
                  {cashBankSummary.total_balance > 0 ? '✓' : '⚠️'} 
                  {cashBankSummary.total_balance > 0 ? ' +' : ' -'}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {cashBankSummary.total_balance > 0 ? 'Pozitif' : 'Negatif'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
});



// Quick Customer Query Component - Hızlı Müşteri Sorgulama
const QuickCustomerQuery = ({ sessionToken }) => {
  const [customerList, setCustomerList] = useState([]);
  const [endCustomerList, setEndCustomerList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchType, setSearchType] = useState("customer");
  const [orders, setOrders] = useState([]);
  const [accountMovements, setAccountMovements] = useState([]);  // CARİ HESAP HAREKETLERİ
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  
  // Arama state'leri
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchCustomerList();
  }, []);

  // Dropdown dışına tıklama ile kapanması
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.customer-search-dropdown')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const fetchCustomerList = async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    
    try {
      const response = await axios.get(
        `${API}/quick-customer-query?session_token=${sessionToken}`,
        {
          timeout: 10000, // 10 saniye timeout
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.data) {
        throw new Error("Boş yanıt alındı");
      }
      
      setCustomerList(response.data.customers || []);
      setEndCustomerList(response.data.end_customers || []);
      
    } catch (error) {
      console.error("Müşteri listesi yüklenirken hata:", error);
      
      // Retry mekanizması
      if (retryCount < MAX_RETRIES) {
        console.log(`Müşteri listesi tekrar deneniyor... (${retryCount + 1}/${MAX_RETRIES})`);
        
        setTimeout(() => {
          fetchCustomerList(retryCount + 1);
        }, 1500);
        return;
      }
      
      // Detaylı hata mesajı
      let errorMessage = "Müşteri listesi yüklenemedi";
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = "Zaman aşımı. Sayfa yenilenecek.";
      } else if (error.response?.status === 401) {
        errorMessage = "Oturum süresi dolmuş. Lütfen yeniden giriş yapın.";
      }
      
      toast.error(errorMessage);
    }
  };

  // Türkçe karakter normalize
  const normalizeTurkish = (text) => {
    if (!text) return "";
    const turkishMap = {
      'İ': 'i', 'I': 'i', 'ı': 'i',
      'Ş': 's', 'ş': 's',
      'Ğ': 'g', 'ğ': 'g',
      'Ü': 'u', 'ü': 'u',
      'Ö': 'o', 'ö': 'o',
      'Ç': 'c', 'ç': 'c'
    };
    let result = text;
    for (let [turkish, latin] of Object.entries(turkishMap)) {
      result = result.replace(new RegExp(turkish, 'g'), latin);
    }
    return result.toLowerCase();
  };

  // Birleşik liste (müşteri + nihai müşteri)
  const combinedList = [
    ...customerList.map(c => ({ ...c, displayType: 'Ana Müşteri' })),
    ...endCustomerList.map(ec => ({ ...ec, displayType: 'Nihai Müşteri' }))
  ];

  // Arama filtresi
  const filteredList = combinedList.filter(item => {
    if (!searchTerm.trim()) return true;
    const searchNormalized = normalizeTurkish(searchTerm);
    const nameNormalized = normalizeTurkish(item.name);
    const serialNormalized = normalizeTurkish(item.serial_number || "");
    const phoneNormalized = item.phone || "";
    
    return nameNormalized.includes(searchNormalized) || 
           serialNormalized.includes(searchNormalized) ||
           phoneNormalized.includes(searchNormalized);
  });

  const handleCustomerSelect = async (item, retryCount = 0) => {
    const MAX_RETRIES = 2;
    
    setSearchType(item.type);
    setSearchTerm(item.name);
    setShowDropdown(false);
    setLoading(true);
    
    try {
      // Timeout ile request gönder
      const response = await axios.get(
        `${API}/customer-order-timeline/${item.id}?search_type=${item.type}&session_token=${sessionToken}`,
        {
          timeout: 15000, // 15 saniye timeout
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Response kontrolü
      if (!response.data) {
        throw new Error("Boş yanıt alındı");
      }
      
      setCustomerInfo(response.data.customer_info || null);
      setOrders(response.data.orders || []);
      setAccountMovements(response.data.account_movements || []);  // CARİ HESAP HAREKETLERİ
      setSelectedCustomer(item.id);
      
      // Başarılı yükleme mesajı (opsiyonel)
      if (response.data.orders && response.data.orders.length > 0) {
        toast.success(`${response.data.orders.length} sipariş yüklendi`);
      } else {
        toast.info("Bu müşteri için sipariş bulunamadı");
      }
      
    } catch (error) {
      console.error("Sipariş timeline yüklenirken hata:", error);
      
      // Retry mekanizması
      if (retryCount < MAX_RETRIES) {
        console.log(`Tekrar deneniyor... (${retryCount + 1}/${MAX_RETRIES})`);
        toast.warning(`Yükleniyor, lütfen bekleyin... (${retryCount + 1}/${MAX_RETRIES})`);
        
        // 1 saniye bekle ve tekrar dene
        setTimeout(() => {
          handleCustomerSelect(item, retryCount + 1);
        }, 1000);
        return; // Bu çağrının sonlanmasını engelle
      }
      
      // Tüm denemeler başarısız olduysa detaylı hata mesajı
      let errorMessage = "Sipariş bilgileri yüklenemedi";
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = "İstek zaman aşımına uğradı. Lütfen tekrar deneyin.";
      } else if (error.response) {
        // Backend'den gelen hata
        if (error.response.status === 404) {
          errorMessage = "Müşteri veya sipariş bilgisi bulunamadı";
        } else if (error.response.status === 500) {
          errorMessage = "Sunucu hatası. Lütfen daha sonra tekrar deneyin.";
        } else if (error.response.status === 401) {
          errorMessage = "Oturum süresi dolmuş. Lütfen yeniden giriş yapın.";
        }
      } else if (error.request) {
        // İstek gönderildi ama cevap alınamadı
        errorMessage = "Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edin.";
      }
      
      toast.error(errorMessage);
      
      // Hata durumunda state'leri temizle
      setCustomerInfo(null);
      setOrders([]);
      setSelectedCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (transactionId, retryCount = 0) => {
    const MAX_RETRIES = 2;
    
    try {
      const response = await axios.get(
        `${API}/transactions/${transactionId}/details?session_token=${sessionToken}`,
        {
          timeout: 10000, // 10 saniye timeout
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.data) {
        throw new Error("Boş yanıt alındı");
      }
      
      setSelectedOrderDetails(response.data);
      setDetailDialogOpen(true);
      
    } catch (error) {
      console.error("Sipariş detayları yüklenirken hata:", error);
      
      // Retry mekanizması
      if (retryCount < MAX_RETRIES) {
        console.log(`Detaylar tekrar deneniyor... (${retryCount + 1}/${MAX_RETRIES})`);
        toast.warning(`Detaylar yükleniyor, lütfen bekleyin...`);
        
        setTimeout(() => {
          handleViewDetails(transactionId, retryCount + 1);
        }, 1000);
        return;
      }
      
      // Detaylı hata mesajı
      let errorMessage = "Sipariş detayları yüklenemedi";
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = "Zaman aşımı. Lütfen tekrar deneyin.";
      } else if (error.response?.status === 404) {
        errorMessage = "Sipariş detayı bulunamadı";
      } else if (error.response?.status === 500) {
        errorMessage = "Sunucu hatası. Lütfen daha sonra deneyin.";
      }
      
      toast.error(errorMessage);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return '✅';
    if (status === 'pending') return '⏳';
    return 'ℹ️';
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Hızlı Müşteri Sorgulama</h2>
      
      {/* Müşteri Arama */}
      <Card>
        <CardHeader>
          <CardTitle>Müşteri veya Nihai Müşteri Arayın</CardTitle>
          <CardDescription>
            Ana müşteri veya nihai müşteri adı ile arama yapın
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="customer-search-dropdown relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Müşteri adı, cari kod veya telefon ile ara..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setShowDropdown(false);
                    setSelectedCustomer(null);
                    setOrders([]);
                    setAccountMovements([]);
                    setCustomerInfo(null);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Dropdown Sonuçlar */}
            {showDropdown && filteredList.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                <div className="p-2 space-y-1">
                  {filteredList.slice(0, 50).map((item, idx) => (
                    <div
                      key={`${item.type}-${item.id}-${idx}`}
                      onClick={() => handleCustomerSelect(item)}
                      className="p-3 hover:bg-blue-50 rounded cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{item.name}</p>
                            <Badge variant="outline" className="text-xs">
                              {item.displayType}
                            </Badge>
                          </div>
                          {item.serial_number && (
                            <p className="text-xs text-gray-500">Cari: {item.serial_number}</p>
                          )}
                          {item.phone && (
                            <p className="text-xs text-gray-500">Tel: {item.phone}</p>
                          )}
                        </div>
                        {item.balance !== undefined && (
                          <div className="text-right">
                            <p className={`text-sm font-bold ${item.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {formatCurrency(item.balance)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredList.length > 50 && (
                    <div className="p-2 text-center text-xs text-gray-500 bg-gray-50">
                      {filteredList.length - 50} sonuç daha var, aramayı daraltın
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {showDropdown && searchTerm && filteredList.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                <p className="text-sm text-gray-500 text-center">Sonuç bulunamadı</p>
              </div>
            )}
          </div>
          
          {/* Müşteri Bilgileri */}
          {customerInfo && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Müşteri</p>
                  <p className="font-semibold">{customerInfo.name}</p>
                </div>
                {customerInfo.serial_number && (
                  <div>
                    <p className="text-gray-600">Cari No</p>
                    <p className="font-semibold">{customerInfo.serial_number}</p>
                  </div>
                )}
                {customerInfo.phone && (
                  <div>
                    <p className="text-gray-600">Telefon</p>
                    <p className="font-semibold">{customerInfo.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-600">Bakiye</p>
                  <p className={`font-bold text-lg ${customerInfo.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(customerInfo.balance)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Yükleniyor...</span>
        </div>
      )}

      {/* Sipariş Listesi */}
      {!loading && orders.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Sipariş İşlemleri ({orders.length})</h3>
            <Badge className="bg-blue-100 text-blue-800">En yeni sipariş en üstte</Badge>
          </div>
          
          {orders.map((order) => (
            <Card key={order.transaction_id} className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                {/* Sipariş Başlığı */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-semibold">{order.customer_name}</h4>
                      <Badge className={
                        order.current_status === 'Tamamlandı' ? 'bg-green-100 text-green-800' :
                        order.current_status === 'Montajda' ? 'bg-blue-100 text-blue-800' :
                        order.current_status === 'Üretimde' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {order.current_status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-sm text-gray-600">
                      <div><strong>Model:</strong> {order.model || '-'}</div>
                      <div><strong>Renk:</strong> {order.color || '-'}</div>
                      <div><strong>Telefon:</strong> {order.customer_phone || '-'}</div>
                      <div><strong>Tutar:</strong> <span className="font-bold text-green-600">{formatCurrency(order.amount)}</span></div>
                    </div>
                    {order.end_customer && (
                      <p className="text-sm text-purple-600 mt-1">
                        <User className="h-3 w-3 inline mr-1" />
                        Nihai Müşteri: {order.end_customer}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">{order.description}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleViewDetails(order.transaction_id)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Sipariş Detayı
                  </Button>
                </div>

                {/* Timeline */}
                <div className="border-t pt-4">
                  <h5 className="text-sm font-semibold mb-3 text-gray-700">İşlem Geçmişi:</h5>
                  <div className="space-y-2">
                    {order.timeline && order.timeline.map((event, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div className="flex-shrink-0 w-32 text-gray-600">
                          {formatDate(event.date)}
                        </div>
                        <div className="flex-shrink-0">
                          <span className="text-lg">{getStatusIcon(event.status)}</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{event.event}</p>
                          {event.details && (
                            <p className="text-xs text-gray-500">{event.details}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* CARİ HESAP HAREKETLERİ */}
      {!loading && accountMovements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Cari Hesap Hareketleri ({accountMovements.length})</CardTitle>
            <CardDescription>Müşterinin tüm borç/alacak hareketleri</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left p-3 font-semibold">Tarih</th>
                    <th className="text-left p-3 font-semibold">İşlem Tipi</th>
                    <th className="text-left p-3 font-semibold">Nihai Müşteri</th>
                    <th className="text-left p-3 font-semibold">Açıklama</th>
                    <th className="text-right p-3 font-semibold">Borç</th>
                    <th className="text-right p-3 font-semibold">Alacak</th>
                    <th className="text-right p-3 font-semibold">Bakiye</th>
                  </tr>
                </thead>
                <tbody>
                  {accountMovements.map((movement, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-600">
                        {new Date(movement.date).toLocaleDateString('tr-TR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="p-3">
                        <Badge className={
                          movement.type === 'Satış' ? 'bg-blue-100 text-blue-800' :
                          movement.type === 'Tahsilat' ? 'bg-green-100 text-green-800' :
                          movement.type === 'Temlik' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {movement.type}
                        </Badge>
                      </td>
                      <td className="p-3 text-gray-700">
                        {movement.end_customer ? (
                          <span className="text-sm bg-yellow-50 px-2 py-1 rounded border border-yellow-200">
                            {movement.end_customer}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-3 text-gray-700 max-w-md truncate">
                        {movement.description}
                      </td>
                      <td className="p-3 text-right font-semibold text-red-600">
                        {movement.debit > 0 ? formatCurrency(movement.debit) : '-'}
                      </td>
                      <td className="p-3 text-right font-semibold text-green-600">
                        {movement.credit > 0 ? formatCurrency(movement.credit) : '-'}
                      </td>
                      <td className={`p-3 text-right font-bold ${movement.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(movement.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                  <tr>
                    <td colSpan="4" className="p-3 font-bold text-right">TOPLAM:</td>
                    <td className="p-3 text-right font-bold text-red-600">
                      {formatCurrency(accountMovements.reduce((sum, m) => sum + m.debit, 0))}
                    </td>
                    <td className="p-3 text-right font-bold text-green-600">
                      {formatCurrency(accountMovements.reduce((sum, m) => sum + m.credit, 0))}
                    </td>
                    <td className={`p-3 text-right font-bold text-lg ${customerInfo?.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(customerInfo?.balance || 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sonuç Yok */}
      {!loading && selectedCustomer && orders.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <ShoppingCart className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Bu müşteri için sipariş bulunamadı</p>
          </CardContent>
        </Card>
      )}

      {/* İlk Durum */}
      {!loading && !selectedCustomer && (
        <Card>
          <CardContent className="p-12 text-center">
            <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Müşteri veya Nihai Müşteri seçerek sipariş geçmişini görüntüleyin</p>
          </CardContent>
        </Card>
      )}
      
      {/* Sipariş Detayı Dialog - En Kapsamlı */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Sipariş Detayları</DialogTitle>
          </DialogHeader>
          
          {selectedOrderDetails && selectedOrderDetails.transaction && (
            <div className="space-y-4">
              {/* Müşteri Bilgileri */}
              {selectedOrderDetails.customer && (
                <Card>
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="text-lg">👤 Müşteri Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><strong>Ad Soyad:</strong> {selectedOrderDetails.customer.name} {selectedOrderDetails.customer.surname}</div>
                      <div><strong>Cari No:</strong> {selectedOrderDetails.customer.serial_number}</div>
                      <div><strong>Telefon:</strong> {selectedOrderDetails.customer.phone}</div>
                      <div><strong>Bakiye:</strong> <span className={selectedOrderDetails.customer.balance >= 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>{formatCurrency(selectedOrderDetails.customer.balance)}</span></div>
                      <div className="col-span-2"><strong>Adres:</strong> {selectedOrderDetails.customer.address}</div>
                    </div>
                    {selectedOrderDetails.transaction.end_customer && (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
                        <strong className="text-purple-700">🏢 Nihai Tüketici:</strong>
                        <span className="ml-2 text-purple-900">{selectedOrderDetails.transaction.end_customer}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Sipariş Bilgileri */}
              <Card>
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-lg">🛒 Sipariş Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Sipariş No:</strong> {selectedOrderDetails.transaction.id?.slice(0, 8).toUpperCase()}</div>
                    <div><strong>Tarih:</strong> {formatDate(selectedOrderDetails.transaction.created_at)}</div>
                    <div><strong>Üretim Hattı:</strong> <Badge>{selectedOrderDetails.transaction.production_line?.toUpperCase()}</Badge></div>
                    <div><strong>Tutar:</strong> <span className="text-xl font-bold text-green-600">{formatCurrency(selectedOrderDetails.transaction.amount)}</span></div>
                  </div>
                  {selectedOrderDetails.transaction.description && (
                    <div className="mt-3 p-3 bg-yellow-50 rounded">
                      <strong>İmalat Notu:</strong>
                      <p className="mt-1">{selectedOrderDetails.transaction.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ürün Özellikleri */}
              <Card>
                <CardHeader className="bg-purple-50">
                  <CardTitle className="text-lg">📦 Ürün Özellikleri</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Model:</strong> {selectedOrderDetails.transaction.model}</div>
                    <div><strong>Renk:</strong> {selectedOrderDetails.transaction.color}</div>
                    <div><strong>Kapı Kolu:</strong> {selectedOrderDetails.transaction.door_handle || '-'}</div>
                    <div><strong>Yüzey:</strong> {selectedOrderDetails.transaction.surface || '-'}</div>
                    <div><strong>Montaj:</strong> {selectedOrderDetails.transaction.installation || '-'}</div>
                    <div><strong>Montaj Yeri:</strong> {selectedOrderDetails.transaction.installation_location || '-'}</div>
                    <div><strong>İç Pervaz:</strong> {selectedOrderDetails.transaction.inner_frame || '-'}</div>
                    <div><strong>Dış Pervaz:</strong> {selectedOrderDetails.transaction.outer_frame || '-'}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Ölçü Tablosu */}
              {selectedOrderDetails.transaction.measurements && (
                <Card>
                  <CardHeader className="bg-gray-50">
                    <CardTitle className="text-lg">📐 Ölçü Tablosu</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {(() => {
                      try {
                        const measurements = typeof selectedOrderDetails.transaction.measurements === 'string'
                          ? JSON.parse(selectedOrderDetails.transaction.measurements)
                          : selectedOrderDetails.transaction.measurements;
                        
                        if (Array.isArray(measurements) && measurements.length > 0) {
                          return (
                            <table className="w-full text-xs border">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="border p-2">#</th>
                                  <th className="border p-2">En</th>
                                  <th className="border p-2">Boy</th>
                                  <th className="border p-2">Kasa</th>
                                  <th className="border p-2">Yönü</th>
                                  <th className="border p-2">Cam</th>
                                  <th className="border p-2">Oda</th>
                                  {measurements.some(m => m.quantity) && <th className="border p-2">Adet</th>}
                                  <th className="border p-2">Açıklama</th>
                                </tr>
                              </thead>
                              <tbody>
                                {measurements.map((m, i) => (
                                  <tr key={i}>
                                    <td className="border p-2">{i + 1}</td>
                                    <td className="border p-2">{m.width || '-'}</td>
                                    <td className="border p-2">{m.height || '-'}</td>
                                    <td className="border p-2">{m.frame || m.thickness || '-'}</td>
                                    <td className="border p-2">{m.direction || '-'}</td>
                                    <td className="border p-2">{m.glass || '-'}</td>
                                    <td className="border p-2">{m.room || '-'}</td>
                                    {measurements.some(m => m.quantity) && <td className="border p-2">{m.quantity || 1}</td>}
                                    <td className="border p-2">{m.description || m.notes || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        }
                      } catch (e) {
                        return <p className="text-sm text-gray-500">Ölçü bilgisi yok</p>;
                      }
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Üretim Durumu */}
              {selectedOrderDetails.productions && selectedOrderDetails.productions.length > 0 && (
                <Card>
                  <CardHeader className="bg-orange-50">
                    <CardTitle className="text-lg">🏭 Üretim Durumu</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {selectedOrderDetails.productions.map((prod, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded border mb-2">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{prod.production_line?.toUpperCase()} - {prod.model}</p>
                            <p className="text-xs text-gray-600">Oluşturma: {formatDate(prod.created_at)}</p>
                          </div>
                          <Badge className={prod.status === 'hazir' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                            {prod.status?.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {prod.cnc_completed && <div className="px-2 py-1 bg-green-100 rounded">✅ CNC Tamamlandı</div>}
                          {prod.aksesuar_ready && <div className="px-2 py-1 bg-green-100 rounded">✅ Aksesuar Hazır</div>}
                          {prod.is_painted && <div className="px-2 py-1 bg-green-100 rounded">✅ {prod.production_line === 'melamin' ? 'Ebatlandı' : 'Boyandı'}</div>}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Montaj Durumu */}
              {selectedOrderDetails.installation && (
                <Card>
                  <CardHeader className="bg-indigo-50">
                    <CardTitle className="text-lg">🔧 Montaj Durumu</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><strong>Montajcı:</strong> {selectedOrderDetails.installation.installer_name}</div>
                      <div><strong>Durum:</strong> <Badge>{selectedOrderDetails.installation.status?.toUpperCase()}</Badge></div>
                      <div><strong>Montaj Tarihi:</strong> {formatDate(selectedOrderDetails.installation.installation_date)}</div>
                      <div><strong>Adres:</strong> {selectedOrderDetails.installation.address}</div>
                    </div>
                    {selectedOrderDetails.installation.is_painted && (
                      <div className="mt-2 px-3 py-2 bg-green-100 rounded">
                        ✅ {selectedOrderDetails.transaction?.production_line === 'melamin' ? 'Ebatlandı' : 'Boyandı'}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Finansal Özet */}
              {selectedOrderDetails.financial_summary && (
                <Card>
                  <CardHeader className="bg-emerald-50">
                    <CardTitle className="text-lg">💰 Finansal Bilgiler</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div><strong>Toplam:</strong> <span className="text-lg font-bold text-green-600">{formatCurrency(selectedOrderDetails.financial_summary.total_amount)}</span></div>
                      <div><strong>Tahsilat:</strong> {formatCurrency(selectedOrderDetails.financial_summary.total_collected)}</div>
                      <div className="col-span-2"><strong>Kalan:</strong> <span className={`text-xl font-bold ${selectedOrderDetails.financial_summary.final_balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(selectedOrderDetails.financial_summary.final_balance)}</span></div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setDetailDialogOpen(false)}>Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Collection Tracking Component (Tahsilat Takip - Ayrı Sayfa)
// CollectionTracking Component - Temiz Versiyonu
const CollectionTracking = ({ sessionToken, currentUser }) => {
  const [completedInstallations, setCompletedInstallations] = useState([]);
  const [completedBayiInstallations, setCompletedBayiInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [activeTab, setActiveTab] = useState("customer");
  
  // Müşteri/Nihai Müşteri arama state'i
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  
  // Bayi tahsilat state'leri
  const [bayiler, setBayiler] = useState([]);
  const [bayiCollections, setBayiCollections] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Bayi arama state'leri
  const [bayiSearchTerm, setBayiSearchTerm] = useState("");
  const [showBayiDropdown, setShowBayiDropdown] = useState(false);
  const [selectedBayi, setSelectedBayi] = useState(null);
  
  // Eksik iş işaretleme state'leri
  const [isIncompleteWorkDialogOpen, setIsIncompleteWorkDialogOpen] = useState(false);
  const [selectedInstallationForIncomplete, setSelectedInstallationForIncomplete] = useState(null);
  const [incompleteWorkData, setIncompleteWorkData] = useState({
    has_incomplete_work: true,
    incomplete_work_notes: ""
  });
  
  // Sipariş detayı modal state'leri
  const [isOrderDetailDialogOpen, setIsOrderDetailDialogOpen] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  
  // Not ekleme state'leri
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [selectedInstallationForNote, setSelectedInstallationForNote] = useState(null);
  const [collectionNote, setCollectionNote] = useState("");
  
  // Ajanda state'leri
  const [isAgendaDialogOpen, setIsAgendaDialogOpen] = useState(false);
  const [selectedInstallationForAgenda, setSelectedInstallationForAgenda] = useState(null);
  const [agendaDate, setAgendaDate] = useState("");
  
  const [formData, setFormData] = useState({
    bayi_id: "",
    amount: "",
    collection_type: "nakit",
    description: ""
  });

  useEffect(() => {
    fetchCompletedInstallations();
    fetchCompletedBayiInstallations();
    fetchBayiler();
    fetchBayiCollections();
  }, []);

  // Auto-refresh every 40 seconds for installation personnel  
  useEffect(() => {
    if (currentUser?.role === 'installation' && currentUser?.installer_id) {
      const interval = setInterval(() => {
        fetchCompletedInstallations();
        fetchCompletedBayiInstallations();
      }, 40000); // 40 saniyede bir yenile

      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Dropdown dışına tıklama ile kapanması için
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBayiDropdown && !event.target.closest('.bayi-search-container')) {
        setShowBayiDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBayiDropdown]);

  const fetchCompletedInstallations = async () => {
    try {
      const response = await axios.get(`${API}/completed-installations-for-collection`, {
        params: { session_token: sessionToken }
      });
      
      // Installation personnel için sadece kendi atandığı eksik işleri filtrele
      if (currentUser?.role === 'installation') {
        const myInstallations = response.data.filter(installation => 
          installation.installer_id === currentUser?.installer_id
        );
        setCompletedInstallations(myInstallations);
      } else {
        setCompletedInstallations(response.data);
      }
    } catch (error) {
      console.error("Tamamlanan montajlar yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedBayiInstallations = async () => {
    try {
      const response = await axios.get(`${API}/completed-bayi-installations-for-collection`, {
        params: { session_token: sessionToken }
      });
      
      // Installation personnel için sadece kendi atandığı eksik işleri filtrele
      if (currentUser?.role === 'installation') {
        const myBayiInstallations = response.data.filter(installation => 
          installation.installer_id === currentUser?.installer_id
        );
        setCompletedBayiInstallations(myBayiInstallations);
      } else {
        setCompletedBayiInstallations(response.data);
      }
    } catch (error) {
      console.error("Tamamlanan bayi montajları yüklenirken hata:", error);
    }
  };

  // Removed handleFixDataLinks and handleFixTransactions functions as buttons were removed

  const fetchBayiler = async () => {
    try {
      const response = await axios.get(`${API}/bayiler`, {
        params: { session_token: sessionToken }
      });
      setBayiler(response.data);
    } catch (error) {
      console.error("Bayiler yüklenirken hata:", error);
    }
  };

  const fetchBayiCollections = async () => {
    try {
      const response = await axios.get(`${API}/bayi-collections`, {
        params: { session_token: sessionToken }
      });
      setBayiCollections(response.data);
    } catch (error) {
      console.error("Bayi tahsilatları yüklenirken hata:", error);
    }
  };

  // Bayi arama fonksiyonları
  const filteredBayiler = bayiler.filter(bayi => 
    bayi.id && bayi.id.trim() !== '' && 
    `${bayi.name} ${bayi.surname}`.toLowerCase().includes(bayiSearchTerm.toLowerCase())
  ).slice(0, 20); // İlk 20 sonucu göster

  const handleBayiSelect = (bayi) => {
    setSelectedBayi(bayi);
    setBayiSearchTerm(`${bayi.name} ${bayi.surname}`);
    setFormData({...formData, bayi_id: bayi.id});
    setShowBayiDropdown(false);
  };

  const handleBayiSearchChange = (value) => {
    setBayiSearchTerm(value);
    setShowBayiDropdown(true);
    if (!value.trim()) {
      setSelectedBayi(null);
      setFormData({...formData, bayi_id: ""});
    }
  };

  const openBayiCollectionDialog = () => {
    // Dialog açılırken state'leri temizle
    setFormData({
      bayi_id: "",
      amount: "",
      collection_type: "nakit",
      description: ""
    });
    setBayiSearchTerm("");
    setSelectedBayi(null);
    setShowBayiDropdown(false);
    setIsDialogOpen(true);
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      // Dialog kapanırken state'leri temizle
      setFormData({
        bayi_id: "",
        amount: "",
        collection_type: "nakit",
        description: ""
      });
      setBayiSearchTerm("");
      setSelectedBayi(null);
      setShowBayiDropdown(false);
    }
  };

  const handleCreateCollection = async (installationData) => {
    try {
      // Sadece "tahsil edildi" olarak işaretle, gerçek tahsilat işlemi yapma
      await axios.put(`${API}/installation-schedules/${installationData.installation_id}/mark-collected`, {}, {
        params: { session_token: sessionToken }
      });
      
      toast.success("Tahsil edildi olarak işaretlendi");
      fetchCompletedInstallations(); // Listeyi yenile
      fetchCompletedBayiInstallations(); // Bayi listesini de yenile
    } catch (error) {
      console.error("Tahsil edildi işaretleme sırasında hata:", error);
      toast.error("Tahsil edildi işaretlenemedi");
    }
  };

  const handleUnmarkCollected = async (installationData) => {
    try {
      await axios.put(`${API}/installation-schedules/${installationData.installation_id}/unmark-collected`, {}, {
        params: { session_token: sessionToken }
      });
      
      toast.success("Bekleyenlere geri gönderildi");
      fetchCompletedInstallations(); // Listeyi yenile
      fetchCompletedBayiInstallations(); // Bayi listesini de yenile
    } catch (error) {
      console.error("Bekleyenlere geri gönderme sırasında hata:", error);
      toast.error("İşlem başarısız oldu");
    }
  };

  const handleCompleteIncompleteWork = async (installationData) => {
    try {
      await axios.put(`${API}/installation-schedules/${installationData.installation_id}/complete-work`, {}, {
        params: { session_token: sessionToken }
      });
      
      toast.success("Eksik iş tamamlandı, bekleyenlere gönderildi");
      fetchCompletedInstallations(); // Listeyi yenile
      fetchCompletedBayiInstallations(); // Bayi listesini de yenile
    } catch (error) {
      console.error("Eksik iş tamamlama sırasında hata:", error);
      toast.error("İşlem başarısız oldu");
    }
  };

  // Not ekleme fonksiyonları
  const handleOpenNoteDialog = (installation) => {
    setSelectedInstallationForNote(installation);
    setCollectionNote(installation.collection_notes || "");
    setIsNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!selectedInstallationForNote) return;
    
    try {
      await axios.put(`${API}/installation-schedules/${selectedInstallationForNote.installation_id}/collection-info`, null, {
        params: { 
          session_token: sessionToken,
          collection_notes: collectionNote
        }
      });
      
      toast.success("Not kaydedildi");
      setIsNoteDialogOpen(false);
      setSelectedInstallationForNote(null);
      setCollectionNote("");
      fetchCompletedInstallations();
      fetchCompletedBayiInstallations();
    } catch (error) {
      console.error("Not kaydetme sırasında hata:", error);
      toast.error("Not kaydedilemedi");
    }
  };

  // Ajanda fonksiyonları
  const handleOpenAgendaDialog = (installation) => {
    setSelectedInstallationForAgenda(installation);
    setAgendaDate(installation.agenda_date || "");
    setIsAgendaDialogOpen(true);
  };

  const handleSaveAgendaDate = async () => {
    if (!selectedInstallationForAgenda) return;
    if (!agendaDate) {
      toast.error("Lütfen bir tarih seçin");
      return;
    }
    
    try {
      // Sadece ajanda tarihini kaydet (not oluşturma)
      await axios.put(`${API}/installation-schedules/${selectedInstallationForAgenda.installation_id}/agenda-date`, null, {
        params: { 
          session_token: sessionToken,
          agenda_date: agendaDate
        }
      });
      
      toast.success("Ajandaya eklendi");
      setIsAgendaDialogOpen(false);
      setSelectedInstallationForAgenda(null);
      setAgendaDate("");
      fetchCompletedInstallations();
      fetchCompletedBayiInstallations();
    } catch (error) {
      console.error("Ajanda kaydetme sırasında hata:", error);
      toast.error("Ajandaya eklenemedi");
    }
  };

  // Eksik iş işaretleme fonksiyonları
  const handleMarkIncompleteWork = (installation) => {
    setSelectedInstallationForIncomplete(installation);
    setIncompleteWorkData({
      has_incomplete_work: !installation.has_incomplete_work, // Toggle
      incomplete_work_notes: installation.incomplete_work_notes || ""
    });
    setIsIncompleteWorkDialogOpen(true);
  };

  const handleIncompleteWorkSubmit = async () => {
    if (!selectedInstallationForIncomplete) return;
    
    try {
      await axios.put(`${API}/installation-schedules/${selectedInstallationForIncomplete.installation_id}/incomplete-work`, {
        has_incomplete_work: incompleteWorkData.has_incomplete_work,
        incomplete_work_notes: incompleteWorkData.incomplete_work_notes
      }, {
        params: { session_token: sessionToken }
      });
      
      toast.success(
        incompleteWorkData.has_incomplete_work 
          ? "Eksik iş işaretlendi" 
          : "Eksik iş işareti kaldırıldı"
      );
      
      setIsIncompleteWorkDialogOpen(false);
      setSelectedInstallationForIncomplete(null);
      fetchCompletedInstallations(); // Listeyi yenile
    } catch (error) {
      console.error("Eksik iş işaretleme hatası:", error);
      toast.error("Eksik iş işaretleme sırasında hata oluştu");
    }
  };

  const handleBayiCollectionSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.bayi_id) {
      toast.error("Lütfen bir bayi seçin");
      return;
    }
    
    if (!formData.amount || formData.amount <= 0) {
      toast.error("Lütfen geçerli bir tutar girin");
      return;
    }
    
    try {
      const formPayload = new FormData();
      formPayload.append('bayi_id', formData.bayi_id);
      formPayload.append('amount', parseFloat(formData.amount));
      formPayload.append('collection_type', formData.collection_type);
      formPayload.append('description', formData.description);

      await axios.post(`${API}/bayi-collections`, formPayload);
      toast.success("Bayi tahsilat işlemi oluşturuldu");
      setIsDialogOpen(false);
      
      // Form'u ve arama state'lerini temizle
      setFormData({
        bayi_id: "",
        amount: "",
        collection_type: "nakit",
        description: ""
      });
      setBayiSearchTerm("");
      setSelectedBayi(null);
      setShowBayiDropdown(false);
      
      fetchBayiler();
      fetchBayiCollections();
    } catch (error) {
      console.error("Bayi tahsilat işlemi oluşturulurken hata:", error);
      toast.error("Bayi tahsilat işlemi oluşturulurken hata oluştu");
    }
  };

  // Sipariş detaylarını görüntüleme fonksiyonu
  const handleViewOrderDetails = async (installation) => {
    try {
      // Müşteri bilgilerini al  
      let customer = null;
      if (installation.customer_id) {
        try {
          const customerResponse = await axios.get(`${API}/customers/${installation.customer_id}`, {
            params: { session_token: sessionToken }
          });
          customer = customerResponse.data;
        } catch (err) {
          console.error("Müşteri bilgileri alınamadı:", err);
        }
      }
      
      // Transaction bilgilerini MongoDB'den direkt ID ile çek
      let transaction = null;
      if (installation.transaction_id) {
        try {
          // Transaction collection'dan direkt ID ile sorgula
          const transactionResponse = await axios.get(`${API}/transactions-by-id/${installation.transaction_id}`, {
            params: { session_token: sessionToken }
          });
          transaction = transactionResponse.data;
        } catch (err) {
          console.error("Transaction bilgileri alınamadı:", err);
          // Transaction bulunamazsa, installation'daki bilgileri kullan
          transaction = {
            id: installation.transaction_id,
            description: installation.description || installation.door_type || "",
            amount: installation.sale_amount,
            created_at: installation.completed_date,
            model: installation.door_type?.split(' ')[0] || "",
            color: installation.door_type?.split(' ').slice(1).join(' ') || "",
            measurements: null
          };
        }
      }
      
      // Modal için veriyi hazırla
      setSelectedOrderDetail({
        ...installation,
        transaction: transaction || {},
        customer: customer,
        production_line: installation.production_line || "N/A"
      });
      setIsOrderDetailDialogOpen(true);
    } catch (error) {
      console.error("Sipariş detayları yüklenirken hata:", error);
      toast.error("Sipariş detayları yüklenirken hata oluştu");
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  const installationsWithDate = activeTab === "customer" 
    ? completedInstallations.map(installation => ({
        ...installation,
        completed_date: new Date(installation.completed_date)
      })).sort((a, b) => b.completed_date - a.completed_date)
    : completedBayiInstallations.map(installation => ({
        ...installation,
        completed_date: new Date(installation.completed_date)
      })).sort((a, b) => b.completed_date - a.completed_date);

  // Türkçe karakter normalize fonksiyonu
  const normalizeTurkish = (text) => {
    if (!text) return "";
    const turkishMap = {
      'İ': 'i', 'I': 'i', 'ı': 'i',
      'Ş': 's', 'ş': 's',
      'Ğ': 'g', 'ğ': 'g',
      'Ü': 'u', 'ü': 'u',
      'Ö': 'o', 'ö': 'o',
      'Ç': 'c', 'ç': 'c'
    };
    let result = text;
    for (let [turkish, latin] of Object.entries(turkishMap)) {
      result = result.replace(new RegExp(turkish, 'g'), latin);
    }
    return result.toLowerCase();
  };

  const filteredInstallations = installationsWithDate.filter(installation => {
    // Durum filtresi
    let statusMatch = true;
    if (filter === "pending") statusMatch = !installation.is_collected && !installation.has_incomplete_work;
    if (filter === "incomplete") statusMatch = installation.has_incomplete_work && !installation.is_collected;
    if (filter === "collected") statusMatch = installation.is_collected;
    if (filter === "agenda") statusMatch = installation.agenda_date;
    
    if (!statusMatch) return false;
    
    // Müşteri arama filtresi (hem müşteri adı hem nihai müşteri)
    if (customerSearchTerm.trim()) {
      const searchNormalized = normalizeTurkish(customerSearchTerm);
      const customerName = normalizeTurkish(activeTab === "customer" ? installation.customer_name : installation.bayi_name);
      const endCustomer = normalizeTurkish(installation.sale_details?.end_customer || "");
      
      // Müşteri adında VEYA nihai müşteri adında ara
      return customerName.includes(searchNormalized) || endCustomer.includes(searchNormalized);
    }
    
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tahsilat Yönetimi</h1>
          <p className="text-muted-foreground">
            {activeTab === "customer" 
              ? "Montajı tamamlanan siparişlerin tahsilat durumu" 
              : "Bayi'lerden tahsilat işlemleri"}
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Button
            variant={activeTab === "customer" ? "default" : "outline"}
            onClick={() => setActiveTab("customer")}
          >
            Müşteri Tahsilat
          </Button>
          <Button
            variant={activeTab === "bayi" ? "default" : "outline"}
            onClick={() => setActiveTab("bayi")}
          >
            Bayi Tahsilat
          </Button>
        </div>
      </div>

      {activeTab === "customer" && (
        <div>
          {/* Birinci Satır Butonlar - GÜNCEL SIRALAMA */}
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold">Tahsilat Yönetimi</h2>
            <div className="flex space-x-2 flex-wrap gap-y-2">
              <Button
                variant={filter === "quick-query" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("quick-query")}
                className={filter === "quick-query" ? "bg-purple-600 hover:bg-purple-700" : ""}
              >
                Hızlı Müşteri Sorgulama
              </Button>
              <Button
                variant={filter === "sales" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("sales")}
              >
                Satışlar
              </Button>
              <Button
                variant={filter === "production" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("production")}
              >
                Üretim
              </Button>
              <Button
                variant={filter === "target-installations" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("target-installations")}
              >
                Hedef Montaj
              </Button>
              <Button
                variant={filter === "installations" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("installations")}
              >
                Montaj
              </Button>
              <Button
                variant={filter === "pending" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("pending")}
              >
                Bekleyen ({installationsWithDate.filter(i => !i.is_collected && !i.has_incomplete_work).length})
              </Button>
              <Button
                variant={filter === "incomplete" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("incomplete")}
              >
                Eksik ve Yarım İşler ({installationsWithDate.filter(i => i.has_incomplete_work && !i.is_collected).length})
              </Button>
              <Button
                variant={filter === "collected" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("collected")}
              >
                Tahsil Edilenler ({installationsWithDate.filter(i => i.is_collected).length})
              </Button>
              <Button
                variant={filter === "agenda" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("agenda")}
                className={filter === "agenda" ? "" : "bg-indigo-50 hover:bg-indigo-100 border-indigo-500 text-indigo-700"}
              >
                Ajanda ({installationsWithDate.filter(i => i.agenda_date).length})
              </Button>
            </div>
          </div>

          {/* İkinci Satır Butonlar - GÜNCEL SIRALAMA */}
          <div className="flex justify-end mb-4">
            <div className="flex space-x-2 flex-wrap gap-y-2">
              <Button
                variant={filter === "customers" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("customers")}
              >
                Müşteriler
              </Button>
              <Button
                variant={filter === "suppliers" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("suppliers")}
              >
                Tedarikçiler
              </Button>
              <Button
                variant={filter === "purchases" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("purchases")}
              >
                Mal Alış
              </Button>
              <Button
                variant={filter === "expenses" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("expenses")}
              >
                Gider
              </Button>
              <Button
                variant={filter === "collection-receipts" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("collection-receipts")}
              >
                Tahsilat
              </Button>
              <Button
                variant={filter === "payment-receipts" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("payment-receipts")}
              >
                Ödeme
              </Button>
              <Button
                variant={filter === "temlik" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("temlik")}
              >
                Temlik
              </Button>
              <Button
                variant={filter === "cash-transactions" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("cash-transactions")}
              >
                Kasa Hareketleri
              </Button>
              <Button
                variant={filter === "bank-transactions" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("bank-transactions")}
              >
                Banka Hareketleri
              </Button>
            </div>
          </div>

          {/* Hızlı Müşteri Sorgulama Bölümü */}
          {filter === "quick-query" && (
            <QuickCustomerQuery sessionToken={sessionToken} />
          )}

          {(filter === "pending" || filter === "incomplete" || filter === "collected") && (
            <>
              {/* Müşteri Arama Input'u - Bekleyen, Eksik ve Tahsil Edilenler için */}
              <div className="mb-4">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Müşteri veya Nihai Müşteri adı ile ara..."
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  {customerSearchTerm && (
                    <button
                      onClick={() => setCustomerSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {customerSearchTerm && (
                  <p className="text-xs text-gray-500 mt-1">
                    {filteredInstallations.length} sonuç bulundu
                  </p>
                )}
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Montaj Tamamlanan Siparişler</CardTitle>
                    <CardDescription>
                      {filter === "pending" && "Tahsilatı bekleyen montajlar"}
                      {filter === "incomplete" && "Eksik veya yarım kalan işler"}
                      {filter === "collected" && "Tahsil edilmiş montajlar"}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                {filteredInstallations.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="w-[150px]">Müşteri</TableHead>
                        <TableHead className="w-[80px]">Durum</TableHead>
                        <TableHead className="w-[100px]">Montajcı</TableHead>
                        <TableHead className="w-[120px]">Telefon</TableHead>
                        {currentUser?.role !== 'installation' && (
                          <>
                            <TableHead className="w-[100px] text-right">Satış Tutarı</TableHead>
                            <TableHead className="w-[100px] text-right">Bakiye</TableHead>
                          </>
                        )}
                        {currentUser?.role === 'installation' && (
                          <TableHead className="w-[120px]">Kapı Türü</TableHead>
                        )}
                        <TableHead className="w-[100px]">Montaj Tarihi</TableHead>
                        <TableHead className="w-[100px]">Tamamlanma</TableHead>
                        <TableHead className="w-[100px]">Ödeme</TableHead>
                        <TableHead className="w-[100px]">Notlar</TableHead>
                        <TableHead className="w-[200px] text-center">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInstallations.map((installation) => (
                        <TableRow key={installation.installation_id} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            <div>
                              <p className="font-medium">
                                {activeTab === "customer" 
                                  ? installation.customer_name 
                                  : installation.bayi_name}
                              </p>
                              {installation.sale_details?.end_customer && (
                                <p className="text-xs text-purple-600 mt-1">
                                  <User className="h-3 w-3 inline mr-1" />
                                  {installation.sale_details.end_customer}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                              installation.is_collected 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {installation.collection_status}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {installation.installer_name ? (
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                                👷‍♂️ {installation.installer_name}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-xs">
                            📞 {activeTab === "customer" ? installation.customer_phone : installation.bayi_phone}
                          </TableCell>
                          {currentUser?.role !== 'installation' && (
                            <>
                              <TableCell className="text-right text-xs">
                                💰 ₺{installation.sale_amount?.toLocaleString('tr-TR') || '0'}
                              </TableCell>
                              <TableCell className="text-right text-xs">
                                <span className={installation.current_balance > 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                                  💳 ₺{installation.current_balance?.toLocaleString('tr-TR') || '0'}
                                </span>
                              </TableCell>
                            </>
                          )}
                          {currentUser?.role === 'installation' && (
                            <TableCell className="text-xs">
                              🚪 {installation.door_type || 'Belirtilmemiş'}
                            </TableCell>
                          )}
                          <TableCell className="text-xs">
                            📅 {installation.installation_date ? new Date(installation.installation_date).toLocaleDateString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell className="text-xs">
                            ✅ {installation.completed_date ? new Date(installation.completed_date).toLocaleDateString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell className="text-xs text-blue-700">
                            {installation.payment_method ? `💳 ${installation.payment_method}` : '-'}
                          </TableCell>
                          <TableCell className="text-xs text-purple-700">
                            {installation.collection_notes ? `📝 ${installation.collection_notes}` : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-center">
                              {currentUser?.role === 'installation' ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleViewOrderDetails(installation)}
                                  className="btn-professional text-blue-600 border-blue-600 hover:bg-blue-50 text-xs h-6 px-1"
                                >
                                  Detay
                                </Button>
                              ) : (
                                <>
                                  {!installation.is_collected && !installation.has_incomplete_work && (
                                    <>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleOpenAgendaDialog(installation)}
                                        className="btn-professional text-indigo-600 border-indigo-600 hover:bg-indigo-50 text-xs h-6 px-1"
                                      >
                                        {installation.agenda_date 
                                          ? new Date(installation.agenda_date).toLocaleDateString('tr-TR')
                                          : 'Ajanda+'}
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleOpenNoteDialog(installation)}
                                        className="btn-professional text-purple-600 border-purple-600 hover:bg-purple-50 text-xs h-6 px-1"
                                      >
                                        Not
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleCreateCollection(installation)}
                                        className="btn-professional text-xs h-6 px-1"
                                      >
                                        Tahsil
                                      </Button>
                                      <Button 
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleMarkIncompleteWork(installation)}
                                        className="text-xs h-6 px-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                                      >
                                        Eksik
                                      </Button>
                                    </>
                                  )}
                                  {!installation.is_collected && installation.has_incomplete_work && (
                                    <>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleOpenAgendaDialog(installation)}
                                        className="btn-professional text-indigo-600 border-indigo-600 hover:bg-indigo-50 text-xs h-6 px-1"
                                      >
                                        {installation.agenda_date 
                                          ? new Date(installation.agenda_date).toLocaleDateString('tr-TR')
                                          : 'Ajanda+'}
                                      </Button>
                                      <Button 
                                        variant="default"
                                        size="sm"
                                        onClick={() => handleMarkIncompleteWork(installation)}
                                        className="text-xs h-6 px-1 bg-orange-500 hover:bg-orange-600"
                                      >
                                        Eksik⚠️
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleCompleteIncompleteWork(installation)}
                                        className="btn-professional text-green-600 border-green-600 hover:bg-green-50 text-xs h-6 px-1"
                                      >
                                        Geri
                                      </Button>
                                    </>
                                  )}
                                  {installation.is_collected && (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleUnmarkCollected(installation)}
                                      className="btn-professional text-orange-600 border-orange-600 hover:bg-orange-50 text-xs h-6 px-1"
                                    >
                                      Geri
                                    </Button>
                                  )}
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleViewOrderDetails(installation)}
                                    className="btn-professional text-blue-600 border-blue-600 hover:bg-blue-50 text-xs h-6 px-1"
                                  >
                                    Detay
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Toplam Bakiye Satırı - Sadece Bekleyen ve Eksik İşler için */}
                      {(filter === "pending" || filter === "incomplete") && filteredInstallations.length > 0 && (
                        <TableRow className="bg-blue-50 border-t-2 border-blue-300">
                          <TableCell colSpan={currentUser?.role === 'installation' ? 5 : 5} className="font-bold text-right">
                            TOPLAM:
                          </TableCell>
                          {currentUser?.role !== 'installation' && (
                            <>
                              <TableCell className="text-right font-bold text-blue-900">
                                💰 ₺{filteredInstallations.reduce((sum, inst) => sum + (inst.sale_amount || 0), 0).toLocaleString('tr-TR')}
                              </TableCell>
                              <TableCell className="text-right font-bold text-red-600 text-lg">
                                💳 ₺{filteredInstallations.reduce((sum, inst) => sum + (inst.current_balance || 0), 0).toLocaleString('tr-TR')}
                              </TableCell>
                            </>
                          )}
                          <TableCell colSpan={4}></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {filter === "pending" && "Tahsilatı bekleyen montaj bulunamadı"}
                    {filter === "incomplete" && "Eksik iş bulunamadı"}
                    {filter === "collected" && "Tahsil edilmiş montaj bulunamadı"}
                    {filter === "agenda" && "Ajandaya atanmış sipariş bulunamadı"}
                  </div>
                )}
              </CardContent>
            </Card>
            </>
          )}
          
          {/* Ajanda Bölümü - Ajanda filtresinde göster */}
          {filter === "agenda" && (
            <div>
              <Agenda sessionToken={sessionToken} currentUser={currentUser} />
            </div>
          )}
          
          {/* Müşteriler Bölümü */}
          {filter === "customers" && (
            <div>
              <Customers sessionToken={sessionToken} currentUser={currentUser} />
            </div>
          )}
          
          {/* Tedarikçiler Bölümü */}
          {filter === "suppliers" && (
            <div>
              <Suppliers />
            </div>
          )}
          
          {/* Satış Bölümü */}
          {filter === "sales" && (
            <div>
              <Sales sessionToken={sessionToken} currentUser={currentUser} />
            </div>
          )}
          
          {/* Mal Alış Bölümü */}
          {filter === "purchases" && (
            <div>
              <Purchases sessionToken={sessionToken} />
            </div>
          )}
          
          {/* Gider Yönetimi Bölümü */}
          {filter === "expenses" && (
            <div>
              <Expenses sessionToken={sessionToken} />
            </div>
          )}
          
          {/* Tahsilat Bölümü */}
          {filter === "collection-receipts" && (
            <div>
              <CollectionReceipts />
            </div>
          )}
          
          {/* Ödeme Bölümü */}
          {filter === "payment-receipts" && (
            <div>
              <PaymentReceipts />
            </div>
          )}
          
          {/* Temlik Bölümü */}
          {filter === "temlik" && (
            <div>
              <Temlik sessionToken={sessionToken} />
            </div>
          )}
          
          {/* Kasa Hareketleri Bölümü */}
          {filter === "cash-transactions" && (
            <div>
              <CashTransactions sessionToken={sessionToken} />
            </div>
          )}
          
          {/* Banka Hareketleri Bölümü */}
          {filter === "bank-transactions" && (
            <div>
              <BankTransactions sessionToken={sessionToken} />
            </div>
          )}
          
          {/* Üretim Bölümü */}
          {filter === "production" && (
            <div>
              <Production sessionToken={sessionToken} currentUser={currentUser} />
            </div>
          )}
          
          {/* Montaj Bölümü */}
          {filter === "installations" && (
            <div>
              <InstallationSchedules sessionToken={sessionToken} currentUser={currentUser} />
            </div>
          )}
          
          {/* Hedef Montaj Bölümü */}
          {filter === "target-installations" && (
            <div>
              <TargetInstallations sessionToken={sessionToken} currentUser={currentUser} />
            </div>
          )}
        </div>
      )}

      {activeTab === "bayi" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Bayi Tahsilat İşlemleri</h2>
            <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
              <DialogTrigger asChild>
                <Button onClick={openBayiCollectionDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Yeni Bayi Tahsilat
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Yeni Bayi Tahsilat</DialogTitle>
                  <DialogDescription>
                    Bayi'den tahsilat işlemi yapın.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBayiCollectionSubmit} className="space-y-4">
                  <div className="relative bayi-search-container">
                    <Label htmlFor="bayi_search">Bayi</Label>
                    <Input
                      id="bayi_search"
                      type="text"
                      placeholder="Bayi adı ile ara..."
                      value={bayiSearchTerm}
                      onChange={(e) => handleBayiSearchChange(e.target.value)}
                      onFocus={() => setShowBayiDropdown(true)}
                      className="w-full"
                      autoComplete="off"
                    />
                    {showBayiDropdown && filteredBayiler.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredBayiler.map((bayi) => (
                          <div
                            key={bayi.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onClick={() => handleBayiSelect(bayi)}
                          >
                            <div className="font-medium">
                              {bayi.name} {bayi.surname}
                            </div>
                            <div className="text-sm text-gray-600">
                              Bakiye: ₺{bayi.balance.toLocaleString('tr-TR')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {bayiSearchTerm && filteredBayiler.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3">
                        <div className="text-gray-500 text-center">Bayi bulunamadı</div>
                      </div>
                    )}
                    {selectedBayi && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        ✓ Seçili: <strong>{selectedBayi.name} {selectedBayi.surname}</strong> - 
                        Bakiye: ₺{selectedBayi.balance.toLocaleString('tr-TR')}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="amount">Tutar</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="collection_type">Tahsilat Türü</Label>
                    <Select value={formData.collection_type} onValueChange={(value) => setFormData({...formData, collection_type: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tahsilat türü seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nakit">Nakit</SelectItem>
                        <SelectItem value="banka_havalesi">Banka Havalesi</SelectItem>
                        <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="description">Açıklama</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Tahsilat açıklaması"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      İptal
                    </Button>
                    <Button type="submit">
                      Tahsilat Yap
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Bayi Tahsilat Geçmişi</CardTitle>
              <CardDescription>Yapılan bayi tahsilat işlemleri</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Tarih</th>
                      <th className="text-left p-2">Bayi</th>
                      <th className="text-left p-2">Tutar</th>
                      <th className="text-left p-2">Türü</th>
                      <th className="text-left p-2">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bayiCollections.length > 0 ? bayiCollections.map((collection) => (
                      <tr key={collection.id} className="border-b">
                        <td className="p-2">
                          {new Date(collection.created_at).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="p-2">{collection.bayi_name}</td>
                        <td className="p-2 font-semibold text-green-600">
                          ₺{collection.amount.toLocaleString('tr-TR')}
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            collection.collection_type === 'nakit' ? 'bg-green-100 text-green-800' :
                            collection.collection_type === 'banka_havalesi' ? 'bg-blue-100 text-blue-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {collection.collection_type === 'nakit' ? 'Nakit' :
                             collection.collection_type === 'banka_havalesi' ? 'Banka Havalesi' : 'Kredi Kartı'}
                          </span>
                        </td>
                        <td className="p-2">{collection.description}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} className="text-center p-4 text-gray-500">
                          Henüz bayi tahsilat işlemi yapılmamış
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Eksik İş İşaretleme Dialog'u */}
      <Dialog open={isIncompleteWorkDialogOpen} onOpenChange={setIsIncompleteWorkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {incompleteWorkData.has_incomplete_work ? "Eksik İş İşaretle" : "Eksik İş İşaretini Kaldır"}
            </DialogTitle>
            <DialogDescription>
              {selectedInstallationForIncomplete && (
                <>
                  {selectedInstallationForIncomplete.customer_name} - 
                  {selectedInstallationForIncomplete.description}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="has_incomplete_work"
                checked={incompleteWorkData.has_incomplete_work}
                onChange={(e) => setIncompleteWorkData({
                  ...incompleteWorkData,
                  has_incomplete_work: e.target.checked
                })}
                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <label htmlFor="has_incomplete_work" className="text-sm font-medium text-gray-700">
                Bu montajda eksik/yarım iş var
              </label>
            </div>
            
            {incompleteWorkData.has_incomplete_work && (
              <div>
                <Label htmlFor="incomplete_work_notes">Eksik İş Notları</Label>
                <textarea
                  id="incomplete_work_notes"
                  rows={4}
                  value={incompleteWorkData.incomplete_work_notes}
                  onChange={(e) => setIncompleteWorkData({
                    ...incompleteWorkData,
                    incomplete_work_notes: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Eksik işlerle ilgili detayları açıklayın..."
                />
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setIsIncompleteWorkDialogOpen(false)}
            >
              İptal
            </Button>
            <Button 
              onClick={handleIncompleteWorkSubmit}
              className={incompleteWorkData.has_incomplete_work ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'}
            >
              {incompleteWorkData.has_incomplete_work ? "Eksik İş İşaretle" : "İşareti Kaldır"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Not Ekleme Dialog'u */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tahsilat Notu Ekle</DialogTitle>
            <DialogDescription>
              {selectedInstallationForNote && (
                <>
                  {selectedInstallationForNote.customer_name} - 
                  {selectedInstallationForNote.door_type}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="collection_note">Not</Label>
              <textarea
                id="collection_note"
                rows={4}
                value={collectionNote}
                onChange={(e) => setCollectionNote(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Tahsilat ile ilgili notunuzu yazın..."
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsNoteDialogOpen(false);
                setCollectionNote("");
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleSaveNote}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ajandaya Ekle Dialog'u */}
      <Dialog open={isAgendaDialogOpen} onOpenChange={setIsAgendaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajandaya Ekle</DialogTitle>
            <DialogDescription>
              {selectedInstallationForAgenda && (
                <>
                  {selectedInstallationForAgenda.customer_name} - 
                  {selectedInstallationForAgenda.door_type}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="agenda_date">Ajanda Tarihi</Label>
              <Input
                id="agenda_date"
                type="date"
                value={agendaDate}
                onChange={(e) => setAgendaDate(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsAgendaDialogOpen(false);
                setAgendaDate("");
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleSaveAgendaDate}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sipariş Detayı Modal - Üretim Sistemi Formatı */}
      <Dialog open={isOrderDetailDialogOpen} onOpenChange={setIsOrderDetailDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Sipariş Detayları</DialogTitle>
                <DialogDescription>
                  {selectedOrderDetail && `${selectedOrderDetail.customer_name} - Sipariş ve Üretim Bilgileri`}
                </DialogDescription>
              </div>
              <Button 
                onClick={() => window.print()} 
                variant="outline"
                className="no-print"
              >
                <Printer className="h-4 w-4 mr-2" />
                Yazdır
              </Button>
            </div>
          </DialogHeader>
          
          {selectedOrderDetail && selectedOrderDetail.transaction && (
            <div className="space-y-4">
              {/* Müşteri Bilgileri */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Müşteri Bilgileri</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Müşteri:</strong> {selectedOrderDetail.customer?.name} {selectedOrderDetail.customer?.surname}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Cari Kod:</strong> {selectedOrderDetail.customer?.serial_number || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Telefon:</strong> 
                      <a href={`tel:${selectedOrderDetail.customer?.phone}`} className="text-blue-600 hover:underline cursor-pointer ml-1">
                        📞 {selectedOrderDetail.customer?.phone || 'N/A'}
                      </a>
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Üretim Hattı:</strong> {selectedOrderDetail.production_line?.toUpperCase() || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 mt-3">
                  <div>
                    <p className="font-medium text-sm"><strong>Adres:</strong></p>
                    <p className="text-sm bg-white p-2 rounded border">
                      {selectedOrderDetail.customer?.address || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sipariş ve Finansal Bilgiler */}
              <div className="bg-green-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Sipariş ve Finansal Bilgiler</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Sipariş Tarihi:</strong> {new Date(selectedOrderDetail.transaction.created_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  {currentUser?.role !== 'installation' && (
                    <>
                      <div>
                        <p className="font-medium text-sm">
                          <strong>Satış Tutarı:</strong> 
                          <span className="text-green-600 ml-1">
                            ₺{selectedOrderDetail.sale_amount?.toLocaleString('tr-TR') || selectedOrderDetail.transaction.amount?.toLocaleString('tr-TR')}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          <strong>Güncel Bakiye:</strong> 
                          <span className={selectedOrderDetail.current_balance > 0 ? 'text-red-600 ml-1' : 'text-green-600 ml-1'}>
                            ₺{selectedOrderDetail.current_balance?.toLocaleString('tr-TR') || '0'}
                          </span>
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Durum:</strong> {selectedOrderDetail.status?.toUpperCase() || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>🎯 Hedef Montaj:</strong> 
                      <span className="text-blue-600 ml-1">
                        {selectedOrderDetail.installation_date ? 
                          new Date(selectedOrderDetail.installation_date).toLocaleDateString('tr-TR') : 
                          'Belirlenmemiş'
                        }
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Tamamlanma:</strong> 
                      {selectedOrderDetail.completed_date ? 
                        new Date(selectedOrderDetail.completed_date).toLocaleDateString('tr-TR') : 
                        'Tamamlanmamış'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Ürün Özellikleri */}
              <div className="bg-yellow-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Ürün Özellikleri</h3>
                <div className="grid grid-cols-4 gap-3">
                  {selectedOrderDetail.transaction.model && (
                    <div>
                      <p className="font-medium text-sm"><strong>Model:</strong> {selectedOrderDetail.transaction.model}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction.color && (
                    <div>
                      <p className="font-medium text-sm"><strong>Renk:</strong> {selectedOrderDetail.transaction.color}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction.door_handle && (
                    <div>
                      <p className="font-medium text-sm"><strong>Kapı Kolu:</strong> {selectedOrderDetail.transaction.door_handle}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction.surface && (
                    <div>
                      <p className="font-medium text-sm"><strong>Yüzey:</strong> {selectedOrderDetail.transaction.surface}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction.installation && (
                    <div>
                      <p className="font-medium text-sm"><strong>Montaj:</strong> {selectedOrderDetail.transaction.installation}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction.installation_location && (
                    <div>
                      <p className="font-medium text-sm"><strong>Montaj Yeri:</strong> {selectedOrderDetail.transaction.installation_location}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction.inner_frame && (
                    <div>
                      <p className="font-medium text-sm"><strong>İç Pervaz:</strong> {selectedOrderDetail.transaction.inner_frame}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction.outer_frame && (
                    <div>
                      <p className="font-medium text-sm"><strong>Dış Pervaz:</strong> {selectedOrderDetail.transaction.outer_frame}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Montaj Bilgileri */}
              <div className="bg-purple-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Montaj Bilgileri</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Montaj Elemanı:</strong> {selectedOrderDetail.installer_name || 'Atanmamış'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Montaj Tarihi:</strong> 
                      {selectedOrderDetail.installation_date ? 
                        new Date(selectedOrderDetail.installation_date).toLocaleDateString('tr-TR') : 
                        'Belirtilmemiş'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Tamamlanma Tarihi:</strong> 
                      {selectedOrderDetail.completed_date ? 
                        new Date(selectedOrderDetail.completed_date).toLocaleDateString('tr-TR') : 
                        'Tamamlanmamış'
                      }
                    </p>
                  </div>
                </div>
                {selectedOrderDetail.has_incomplete_work && (
                  <div className="mt-3 p-3 bg-orange-100 border-l-4 border-orange-500">
                    <p className="text-sm font-medium text-orange-800">⚠️ Eksik İş Mevcut</p>
                    {selectedOrderDetail.incomplete_work_notes && (
                      <p className="text-sm text-orange-700 mt-1">{selectedOrderDetail.incomplete_work_notes}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Ölçü Tabloları */}
              {selectedOrderDetail.transaction.measurements && (() => {
                let measurements = [];
                try {
                  measurements = JSON.parse(selectedOrderDetail.transaction.measurements);
                } catch (e) {
                  console.error("Ölçü verileri parse edilemedi:", e);
                  return null;
                }
                
                const validMeasurements = measurements.filter(m => 
                  m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description
                );

                if (validMeasurements.length === 0) return null;

                return (
                  <div className="space-y-3">
                    {/* Orijinal Ölçüler */}
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <h3 className="font-semibold mb-2 text-sm">Orijinal Ölçüler</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 p-1">Sıra</th>
                              <th className="border border-gray-300 p-1">En</th>
                              <th className="border border-gray-300 p-1">Boy</th>
                              <th className="border border-gray-300 p-1">Kasa</th>
                              <th className="border border-gray-300 p-1">Yön</th>
                              <th className="border border-gray-300 p-1">Cam</th>
                              <th className="border border-gray-300 p-1">Oda</th>
                              <th className="border border-gray-300 p-1">Açıklama</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validMeasurements.map((measurement, index) => (
                              <tr key={index}>
                                <td className="border border-gray-300 p-1 font-medium">{index + 1}</td>
                                <td className="border border-gray-300 p-1">{measurement.width || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.height || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.frame || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.room || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Kanat Ölçüsü */}
                    <div className="bg-red-50 p-3 rounded-lg">
                      <h3 className="font-semibold mb-2 text-sm">KANAT ÖLÇÜSÜ</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 p-1">Sıra</th>
                              <th className="border border-gray-300 p-1">En</th>
                              <th className="border border-gray-300 p-1">Boy</th>
                              <th className="border border-gray-300 p-1">Yön</th>
                              <th className="border border-gray-300 p-1">Cam</th>
                              <th className="border border-gray-300 p-1">Açıklama</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validMeasurements.map((measurement, index) => {
                              const originalWidth = parseFloat(measurement.width) || 0;
                              const originalHeight = parseFloat(measurement.height) || 0;
                              const productionWidth = originalWidth > 6 ? originalWidth - 6 : originalWidth;
                              const productionHeight = originalHeight > 4 ? originalHeight - 4 : originalHeight;
                              
                              return (
                                <tr key={index}>
                                  <td className="border border-gray-300 p-1 font-medium">{index + 1}</td>
                                  <td className="border border-gray-300 p-1 font-medium text-red-600">
                                    {productionWidth > 0 ? productionWidth : '-'}
                                  </td>
                                  <td className="border border-gray-300 p-1 font-medium text-red-600">
                                    {productionHeight > 0 ? productionHeight : '-'}
                                  </td>
                                  <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Notlar */}
              {(selectedOrderDetail.description || selectedOrderDetail.transaction.description) && (
                <div className="bg-orange-50 p-2 rounded-lg">
                  <h3 className="font-semibold mb-1 text-sm">Notlar</h3>
                  <p className="text-xs">
                    <strong>Üretim Notu:</strong> {selectedOrderDetail.description || selectedOrderDetail.transaction.description}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// CUSTOMER NOTE FINANCIAL INFO COMPONENT
// ============================================
const CustomerNoteFinancialInfo = ({ customerId, customerName, sessionToken }) => {
  const [financialInfo, setFinancialInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const fetchFinancialInfo = async () => {
    if (!customerId || loading) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customers/${customerId}/financial-info`, {
        params: { session_token: sessionToken }
      });
      setFinancialInfo(response.data);
    } catch (error) {
      console.error("Müşteri finansal bilgisi yüklenirken hata:", error);
      setFinancialInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = () => {
    if (!financialInfo && !loading) {
      fetchFinancialInfo();
    }
    setShowDetails(!showDetails);
  };

  if (loading) {
    return (
      <div className="mt-2 text-xs">
        <div className="flex items-center gap-2">
          <span>👤 {customerName}</span>
          <div className="animate-pulse bg-gray-200 h-4 w-16 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-blue-600">👤 {customerName}</span>
        {financialInfo && (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            financialInfo.current_balance > 0 ? 'bg-red-100 text-red-700' :
            financialInfo.current_balance < 0 ? 'bg-green-100 text-green-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            ₺{Math.abs(financialInfo.current_balance)?.toLocaleString('tr-TR') || '0'}
          </span>
        )}
        <button
          onClick={toggleDetails}
          className="text-xs text-blue-500 hover:text-blue-700 underline"
        >
          {showDetails ? 'Gizle' : 'Detaylar'}
        </button>
      </div>
      
      {showDetails && financialInfo && (
        <div className="mt-2 p-3 bg-white rounded border border-blue-200 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-gray-600">Durum:</span>
              <span className={`ml-1 font-medium ${
                financialInfo.debt_status === 'Borçlu' ? 'text-red-600' :
                financialInfo.debt_status === 'Alacaklı' ? 'text-green-600' :
                'text-gray-600'
              }`}>
                {financialInfo.debt_status}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Risk:</span>
              <span className={`ml-1 font-medium ${
                financialInfo.risk_level === 'Yüksek' ? 'text-red-600' :
                financialInfo.risk_level === 'Orta' ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {financialInfo.risk_level}
              </span>
            </div>
            {financialInfo.last_sale?.payment_method && (
              <div className="col-span-2">
                <span className="text-gray-600">Son Ödeme:</span>
                <span className="ml-1 font-medium">
                  {financialInfo.last_sale.payment_method}
                  {financialInfo.last_sale.amount > 0 && (
                    <span className="text-gray-500"> - ₺{financialInfo.last_sale.amount?.toLocaleString('tr-TR')}</span>
                  )}
                </span>
              </div>
            )}
            <div className="col-span-2 text-gray-500">
              📞 {financialInfo.phone}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// AGENDA COMPONENT
// ============================================
const Agenda = ({ sessionToken, currentUser }) => {
  const [activeTab, setActiveTab] = useState("today_future");
  const [loading, setLoading] = useState(true);
  const [agendaData, setAgendaData] = useState([]);
  const [collectionTrackingOrders, setCollectionTrackingOrders] = useState([]);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [customers, setCustomers] = useState([]);
  const [bayiler, setBayiler] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedCustomerInfo, setSelectedCustomerInfo] = useState(null);
  const [loadingCustomerInfo, setLoadingCustomerInfo] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  // Collection tracking order detail
  const [isOrderDetailDialogOpen, setIsOrderDetailDialogOpen] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  // Yeni state'ler
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [isTaskNoteDialogOpen, setIsTaskNoteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    new_date: new Date().toISOString().split('T')[0],
    notes: ""
  });
  const [taskNoteData, setTaskNoteData] = useState({
    note_text: ""
  });
  
  const [newNote, setNewNote] = useState({
    date: new Date().toISOString().split('T')[0],
    note_text: "",
    customer_id: "",
    bayi_id: "",
    note_type: "general"
  });

  const [editNote, setEditNote] = useState({
    date: "",
    note_text: "",
    customer_id: "",
    bayi_id: "",
    note_type: "general"
  });

  // Date calculations
  const today = new Date();
  const pastStart = new Date(today);
  pastStart.setDate(today.getDate() - 30);
  const futureEnd = new Date(today);
  futureEnd.setDate(today.getDate() + 30);

  useEffect(() => {
    fetchAgendaData();
    fetchCustomers();
    fetchBayiler();
  }, [activeTab]);

  // Click outside to close customer dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAgendaData = async () => {
    try {
      setLoading(true);
      const startDate = activeTab === "past" 
        ? pastStart.toISOString().split('T')[0]
        : today.toISOString().split('T')[0];
      
      const endDate = activeTab === "past"
        ? today.toISOString().split('T')[0]
        : futureEnd.toISOString().split('T')[0];

      // Fetch agenda notes
      const response = await axios.get(`${API}/agenda/range/${startDate}/${endDate}`, {
        params: { session_token: sessionToken }
      });
      
      // Fetch collection tracking orders
      const ordersResponse = await axios.get(`${API}/collection-tracking-for-agenda`, {
        params: { session_token: sessionToken }
      });
      setCollectionTrackingOrders(ordersResponse.data);
      
      // Merge orders into agenda data
      const agendaWithOrders = response.data.map(dayData => {
        const dayOrders = ordersResponse.data.filter(order => order.agenda_date === dayData.date);
        return {
          ...dayData,
          collection_orders: dayOrders
        };
      });
      
      setAgendaData(agendaWithOrders);
    } catch (error) {
      console.error("Ajanda verisi yüklenirken hata:", error);
      toast.error("Ajanda verisi yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers?limit=2000`);
      const customersData = response.data.customers || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error("Müşteriler yüklenirken hata:", error);
    }
  };

  const fetchBayiler = async () => {
    try {
      const response = await axios.get(`${API}/bayiler`);
      setBayiler(response.data);
    } catch (error) {
      console.error("Bayiler yüklenirken hata:", error);
    }
  };

  // Müşteri arama - debounced
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm.trim()) return customers.slice(0, 10); // İlk 10 müşteri
    return customers.filter(customer => 
      `${customer.name} ${customer.surname} ${customer.phone} ${customer.serial_number}`.toLowerCase()
        .includes(customerSearchTerm.toLowerCase())
    ).slice(0, 20); // En fazla 20 sonuç
  }, [customers, customerSearchTerm]);

  const handleCustomerSearch = (searchTerm) => {
    setCustomerSearchTerm(searchTerm);
    setShowCustomerDropdown(searchTerm.length > 0);
    
    if (searchTerm.length === 0) {
      setNewNote({...newNote, customer_id: ""});
      setSelectedCustomerInfo(null);
    }
  };

  const handleCustomerSelect = (customer) => {
    setNewNote({...newNote, customer_id: customer.id});
    setCustomerSearchTerm(`${customer.name} ${customer.surname} - ${customer.phone}`);
    setShowCustomerDropdown(false);
    fetchCustomerFinancialInfo(customer.id);
  };

  const fetchCustomerFinancialInfo = async (customerId) => {
    if (!customerId) {
      setSelectedCustomerInfo(null);
      return;
    }
    
    try {
      setLoadingCustomerInfo(true);
      const response = await axios.get(`${API}/customers/${customerId}/financial-info`, {
        params: { session_token: sessionToken }
      });
      setSelectedCustomerInfo(response.data);
    } catch (error) {
      console.error("Müşteri finansal bilgisi yüklenirken hata:", error);
      toast.error("Müşteri finansal bilgisi yüklenirken hata oluştu");
      setSelectedCustomerInfo(null);
    } finally {
      setLoadingCustomerInfo(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    if (customerId === "none") {
      setNewNote({...newNote, customer_id: ""});
      setSelectedCustomerInfo(null);
      setCustomerSearchTerm("");
      return;
    }
    
    const selectedCustomer = customers.find(c => c.id === customerId);
    if (selectedCustomer) {
      setNewNote({...newNote, customer_id: customerId});
      setCustomerSearchTerm(`${selectedCustomer.name} ${selectedCustomer.surname} - ${selectedCustomer.phone}`);
      fetchCustomerFinancialInfo(customerId);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/agenda/notes`, newNote, {
        params: { session_token: sessionToken }
      });
      
      toast.success("Not eklendi");
      setIsNoteDialogOpen(false);
      setSelectedCustomerInfo(null);
      setCustomerSearchTerm("");
      setShowCustomerDropdown(false);
      setNewNote({
        date: new Date().toISOString().split('T')[0],
        note_text: "",
        customer_id: "",
        bayi_id: "",
        note_type: "general"
      });
      fetchAgendaData();
    } catch (error) {
      console.error("Not eklenirken hata:", error);
      toast.error("Not eklenirken hata oluştu");
    }
  };

  // View order details for collection tracking orders
  const handleViewOrderDetails = async (installation) => {
    try {
      // Müşteri bilgilerini al  
      let customer = null;
      if (installation.customer_id) {
        try {
          const customerResponse = await axios.get(`${API}/customers/${installation.customer_id}`, {
            params: { session_token: sessionToken }
          });
          customer = customerResponse.data;
        } catch (err) {
          console.error("Müşteri bilgileri alınamadı:", err);
        }
      }
      
      // Transaction bilgilerini MongoDB'den direkt ID ile çek
      let transaction = null;
      if (installation.transaction_id) {
        try {
          const transactionResponse = await axios.get(`${API}/transactions-by-id/${installation.transaction_id}`, {
            params: { session_token: sessionToken }
          });
          transaction = transactionResponse.data;
        } catch (err) {
          console.error("Transaction bilgileri alınamadı:", err);
          transaction = {
            id: installation.transaction_id,
            description: installation.description || installation.door_type || "",
            amount: installation.sale_amount,
            created_at: installation.completed_date,
            model: installation.door_type?.split(' ')[0] || "",
            color: installation.door_type?.split(' ').slice(1).join(' ') || "",
            measurements: null
          };
        }
      }
      
      // Modal için veriyi hazırla
      setSelectedOrderDetail({
        ...installation,
        transaction: transaction || {},
        customer: customer,
        production_line: installation.production_line || "N/A"
      });
      setIsOrderDetailDialogOpen(true);
    } catch (error) {
      console.error("Sipariş detayları yüklenirken hata:", error);
      toast.error("Sipariş detayları yüklenirken hata oluştu");
    }
  };

  // Open agenda dialog for editing date
  const handleEditAgendaDate = (order) => {
    setSelectedInstallationForAgenda(order);
    setAgendaDate(order.agenda_date || "");
    setIsAgendaDialogOpen(true);
  };

  // Save agenda date from agenda page
  const handleSaveAgendaDateFromAgenda = async () => {
    if (!selectedInstallationForAgenda) return;
    if (!agendaDate) {
      toast.error("Lütfen bir tarih seçin");
      return;
    }
    
    try {
      await axios.put(`${API}/installation-schedules/${selectedInstallationForAgenda.installation_id}/agenda-date`, null, {
        params: { 
          session_token: sessionToken,
          agenda_date: agendaDate
        }
      });
      
      toast.success("Ajanda tarihi güncellendi");
      setIsAgendaDialogOpen(false);
      setSelectedInstallationForAgenda(null);
      setAgendaDate("");
      fetchAgendaData();
    } catch (error) {
      console.error("Ajanda tarihi güncelleme hatası:", error);
      toast.error("Ajanda tarihi güncellenemedi");
    }
  };

  // Toggle complete status for collection order
  const handleToggleCollectionComplete = async (order) => {
    try {
      if (order.is_collected) {
        await axios.put(`${API}/installation-schedules/${order.installation_id}/unmark-collected`, {}, {
          params: { session_token: sessionToken }
        });
        toast.success("Tamamlandı işareti kaldırıldı");
      } else {
        await axios.put(`${API}/installation-schedules/${order.installation_id}/mark-collected`, {}, {
          params: { session_token: sessionToken }
        });
        toast.success("Tamamlandı olarak işaretlendi");
      }
      fetchAgendaData();
    } catch (error) {
      console.error("Tamamlandı işareti değiştirme hatası:", error);
      toast.error("İşlem başarısız oldu");
    }
  };

  // Add note to collection order
  const [isAddNoteDialogOpen, setIsAddNoteDialogOpen] = useState(false);
  const [selectedOrderForNote, setSelectedOrderForNote] = useState(null);
  const [orderNote, setOrderNote] = useState("");

  const handleOpenAddNoteDialog = (order) => {
    setSelectedOrderForNote(order);
    setOrderNote(order.collection_notes || "");
    setIsAddNoteDialogOpen(true);
  };

  const handleSaveOrderNote = async () => {
    if (!selectedOrderForNote) return;
    
    try {
      await axios.put(`${API}/installation-schedules/${selectedOrderForNote.installation_id}/collection-info`, null, {
        params: { 
          session_token: sessionToken,
          collection_notes: orderNote
        }
      });
      
      toast.success("Not kaydedildi");
      setIsAddNoteDialogOpen(false);
      setSelectedOrderForNote(null);
      setOrderNote("");
      fetchAgendaData();
    } catch (error) {
      console.error("Not kaydetme hatası:", error);
      toast.error("Not kaydedilemedi");
    }
  };

  const [isAgendaDialogOpen, setIsAgendaDialogOpen] = useState(false);
  const [selectedInstallationForAgenda, setSelectedInstallationForAgenda] = useState(null);
  const [agendaDate, setAgendaDate] = useState("");

  const handleEditNote = (note) => {
    setEditingNote(note);
    setEditNote({
      date: note.date,
      note_text: note.note_text,
      customer_id: note.customer_id || "",
      bayi_id: note.bayi_id || "",
      note_type: note.note_type
    });
    
    // Müşteri seçiliyse finansal bilgilerini yükle
    if (note.customer_id) {
      const selectedCustomer = customers.find(c => c.id === note.customer_id);
      if (selectedCustomer) {
        setCustomerSearchTerm(`${selectedCustomer.name} ${selectedCustomer.surname} - ${selectedCustomer.phone}`);
        fetchCustomerFinancialInfo(note.customer_id);
      }
    }
    
    setIsEditDialogOpen(true);
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/agenda/notes/${editingNote.id}`, editNote, {
        params: { session_token: sessionToken }
      });
      
      toast.success("Not güncellendi");
      setIsEditDialogOpen(false);
      setEditingNote(null);
      setSelectedCustomerInfo(null);
      setCustomerSearchTerm("");
      setShowCustomerDropdown(false);
      fetchAgendaData();
    } catch (error) {
      console.error("Not güncellenirken hata:", error);
      toast.error("Not güncellenirken hata oluştu");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm("Bu notu silmek istediğinizden emin misiniz?")) {
      try {
        await axios.delete(`${API}/agenda/notes/${noteId}`, {
          params: { session_token: sessionToken }
        });
        
        toast.success("Not silindi");
        fetchAgendaData();
      } catch (error) {
        console.error("Not silinirken hata:", error);
        toast.error("Not silinirken hata oluştu");
      }
    }
  };


  const toggleNoteComplete = async (noteId) => {
    try {
      const response = await axios.put(
        `${API}/agenda/notes/${noteId}/toggle-complete`,
        {},
        { params: { session_token: sessionToken } }
      );
      
      toast.success(response.data.message);
      fetchAgendaData();
    } catch (error) {
      console.error("Not durumu güncellenirken hata:", error);
      toast.error("Not durumu güncellenemedi");
    }
  };


  // Yeni fonksiyonlar - İşlem yönetimi
  const handleCompleteTask = async (installation) => {
    if (window.confirm("Bu işlemi tamamlandı olarak işaretlemek istediğinizden emin misiniz?")) {
      try {
        await axios.put(`${API}/installation-schedules/${installation.id}/status`, null, {
          params: { 
            session_token: sessionToken,
            status: "tamamlandi"
          }
        });
        
        toast.success("İşlem tamamlandı olarak işaretlendi");
        fetchAgendaData();
      } catch (error) {
        console.error("İşlem tamamlanırken hata:", error);
        toast.error("İşlem tamamlanırken hata oluştu");
      }
    }
  };

  const handleRescheduleTask = (installation) => {
    setSelectedTask(installation);
    setRescheduleData({
      new_date: new Date().toISOString().split('T')[0],
      notes: ""
    });
    setIsRescheduleDialogOpen(true);
  };

  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/installation-schedules/${selectedTask.id}`, {
        installation_date: rescheduleData.new_date,
        notes: `${selectedTask.notes || ''}\n[Yeniden Zamanlandı: ${new Date().toLocaleDateString('tr-TR')}] ${rescheduleData.notes}`.trim()
      }, {
        params: { session_token: sessionToken }
      });
      
      toast.success("İşlem yeni tarihe taşındı");
      setIsRescheduleDialogOpen(false);
      setSelectedTask(null);
      fetchAgendaData();
    } catch (error) {
      console.error("İşlem yeniden zamanlanırken hata:", error);
      toast.error("İşlem yeniden zamanlanırken hata oluştu");
    }
  };

  const handleAddTaskNote = (installation) => {
    setSelectedTask(installation);
    setTaskNoteData({
      note_text: ""
    });
    setIsTaskNoteDialogOpen(true);
  };

  const handleTaskNoteSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/installation-schedules/${selectedTask.id}`, {
        notes: `${selectedTask.notes || ''}\n[Not: ${new Date().toLocaleDateString('tr-TR')}] ${taskNoteData.note_text}`.trim()
      }, {
        params: { session_token: sessionToken }
      });
      
      toast.success("Not eklendi");
      setIsTaskNoteDialogOpen(false);
      setSelectedTask(null);
      fetchAgendaData();
    } catch (error) {
      console.error("Not eklenirken hata:", error);
      toast.error("Not eklenirken hata oluştu");
    }
  };

  // Geçmiş günlerdeki tamamlanmamış işlemleri bugüne taşı
  const handleMoveOverdueTasks = async () => {
    if (window.confirm("Geçmiş günlerdeki tamamlanmamış işlemleri bugüne taşımak istediğinizden emin misiniz?")) {
      try {
        const today = new Date().toISOString().split('T')[0];
        const formData = new FormData();
        formData.append('target_date', today);
        formData.append('session_token', sessionToken);
        
        const response = await axios.put(`${API}/agenda/move-overdue-tasks`, formData);
        
        toast.success(`${response.data.moved_count || 0} işlem bugüne taşındı`);
        fetchAgendaData();
      } catch (error) {
        console.error("Geçmiş işlemler taşınırken hata:", error);
        toast.error("Geçmiş işlemler taşınırken hata oluştu");
      }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'beklemede': return 'bg-yellow-100 text-yellow-800';
      case 'planlandi': return 'bg-blue-100 text-blue-800';
      case 'devam_ediyor': return 'bg-orange-100 text-orange-800';
      case 'tamamlandi': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">📅 Ajanda</h1>
          <p className="text-muted-foreground">
            {activeTab === "past" ? "Geçmiş 30 gün" : "Bugün ve önümüzdeki 30 gün"}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={handleMoveOverdueTasks} 
            className="bg-orange-600 hover:bg-orange-700"
            size="sm"
          >
            ⏰ Geçmiş İşleri Bugüne Taşı
          </Button>
          <Button onClick={() => setIsNoteDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            ➕ Yeni Not Ekle
          </Button>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex space-x-2">
        <Button
          variant={activeTab === "past" ? "default" : "outline"}
          onClick={() => setActiveTab("past")}
        >
          📋 Geçmiş (Son 30 Gün)
        </Button>
        <Button
          variant={activeTab === "today_future" ? "default" : "outline"}
          onClick={() => setActiveTab("today_future")}
        >
          🎯 Bugün & Gelecek (30 Gün)
        </Button>
      </div>

      {/* Agenda Content */}
      <div className="space-y-4">
        {agendaData.length > 0 ? agendaData.map((dayData) => (
          <Card key={dayData.date} className="card-professional">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                📅 {formatDate(dayData.date)}
                {dayData.date === today.toISOString().split('T')[0] && (
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                    BUGÜN
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {/* Notes Section */}
              {dayData.notes && dayData.notes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1 text-sm">
                    📝 Notlar ({dayData.notes.length})
                  </h4>
                  <div className="space-y-1">
                    {dayData.notes.map((note) => (
                      <div key={note.id} className={`p-2 rounded border-l-2 ${
                        note.completed 
                          ? 'bg-gray-50 border-gray-400 opacity-75' 
                          : 'bg-blue-50 border-blue-400'
                      }`}>
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-xs">
                              <span className={`flex-1 ${note.completed ? 'line-through text-gray-600' : 'text-gray-700'}`}>
                                {note.note_text}
                              </span>
                              {note.customer_name && (
                                <span className="text-blue-600 whitespace-nowrap">👤 {note.customer_name}</span>
                              )}
                              {note.bayi_name && (
                                <span className="text-blue-600 whitespace-nowrap">🏪 {note.bayi_name}</span>
                              )}
                              {note.completed && (
                                <span className="text-green-600 whitespace-nowrap">
                                  ✅ {note.completed_at ? new Date(note.completed_at).toLocaleDateString('tr-TR') : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleNoteComplete(note.id)}
                              className={`h-6 w-6 p-0 text-xs ${
                                note.completed 
                                  ? 'hover:bg-gray-200 bg-gray-100' 
                                  : 'hover:bg-green-100 bg-white'
                              }`}
                              title={note.completed ? 'Tamamlanmadı olarak işaretle' : 'Tamamlandı olarak işaretle'}
                            >
                              {note.completed ? '↩️' : '✅'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditNote(note)}
                              className="h-6 w-6 p-0 text-xs hover:bg-blue-100"
                            >
                              ✏️
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteNote(note.id)}
                              className="h-6 w-6 p-0 text-xs hover:bg-red-100"
                            >
                              🗑️
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Installations Section */}
              {dayData.installations && dayData.installations.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1 text-sm">
                    🔧 Montaj Programı ({dayData.installations.length})
                  </h4>
                  <div className="space-y-1">
                    {dayData.installations.map((installation) => (
                      <div key={installation.id} className="bg-gray-50 p-2 rounded border">
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex-1 min-w-0 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-800">
                                {installation.customer_info?.name || 'Müşteri Bilgisi Yok'}
                              </span>
                              <span className="text-gray-600">{installation.door_type}</span>
                              <span className="text-gray-500">📞 {installation.customer_info?.phone}</span>
                              {installation.customer_info?.address && (
                                <span className="text-gray-500">📍 {installation.customer_info.address}</span>
                              )}
                              {installation.installer_name && installation.installer_name !== "Atanmamış" && (
                                <span className="text-gray-600">👷 {installation.installer_name}</span>
                              )}
                              <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(installation.status)}`}>
                                {installation.status}
                              </span>
                              <span className={`font-semibold whitespace-nowrap ${installation.remaining_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                ₺{installation.remaining_balance?.toLocaleString('tr-TR') || '0'}
                              </span>
                              <span className="text-gray-500 whitespace-nowrap">💳 {installation.payment_method}</span>
                              {installation.has_incomplete_work && (
                                <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                                  ⚠️ Eksik İş
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* İşlem Butonları */}
                          {installation.status !== "tamamlandi" && (
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCompleteTask(installation)}
                                className="text-green-600 hover:bg-green-50 hover:text-green-700 border-green-200 h-6 px-2 text-xs"
                              >
                                ✅
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRescheduleTask(installation)}
                                className="text-blue-600 hover:bg-blue-50 hover:text-blue-700 border-blue-200 h-6 px-2 text-xs"
                              >
                                📅
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAddTaskNote(installation)}
                                className="text-purple-600 hover:bg-purple-50 hover:text-purple-700 border-purple-200 h-6 px-2 text-xs"
                              >
                                📝
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Incomplete Works Section */}
              {dayData.incomplete_works && dayData.incomplete_works.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1 text-sm">
                    ⚠️ Eksik & Yarım İşler ({dayData.incomplete_works.length})
                  </h4>
                  <div className="space-y-1">
                    {dayData.incomplete_works.map((work) => (
                      <div key={work.id} className="bg-orange-50 p-2 rounded border-l-2 border-orange-400">
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex-1 min-w-0 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-800">
                                {work.customer_info?.name || 'Müşteri Bilgisi Yok'}
                              </span>
                              <span className="text-gray-600">{work.door_type}</span>
                              {work.incomplete_work_notes && (
                                <span className="text-orange-700">📋 {work.incomplete_work_notes}</span>
                              )}
                              {work.customer_info?.balance && (
                                <span className="text-red-600 font-semibold">
                                  ₺{work.customer_info.balance.toLocaleString('tr-TR')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Collection Tracking Orders Section */}
              {dayData.collection_orders && dayData.collection_orders.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1 text-sm">
                    📋 Tahsilat Takip Siparişleri ({dayData.collection_orders.length})
                  </h4>
                  <div className="space-y-1">
                    {dayData.collection_orders.map((order) => (
                      <div key={order.installation_id} className={`p-2 rounded border-l-2 border-indigo-400 ${
                        order.is_collected ? 'bg-gray-50 opacity-75' : 'bg-indigo-50'
                      }`}>
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex-1 min-w-0 text-xs">
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold text-gray-800 ${order.is_collected ? 'line-through' : ''}`}>
                                {order.customer_name || order.bayi_name}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                order.is_collected 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {order.collection_status}
                              </span>
                              <span className="text-gray-600">📞 {order.customer_phone || order.bayi_phone}</span>
                              <span className="text-gray-600">{order.door_type}</span>
                              <span className="text-gray-600">💰 ₺{order.sale_amount?.toLocaleString('tr-TR')}</span>
                              <span className={`font-semibold whitespace-nowrap ${order.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                💳 ₺{order.current_balance?.toLocaleString('tr-TR')}
                              </span>
                              {order.payment_method && (
                                <span className="text-blue-700 whitespace-nowrap">
                                  💳 {order.payment_method}
                                </span>
                              )}
                              {order.collection_notes && (
                                <span className="text-purple-700 whitespace-nowrap">
                                  📝 {order.collection_notes}
                                </span>
                              )}
                              {order.has_incomplete_work && (
                                <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                                  ⚠️ Eksik İş
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleCollectionComplete(order)}
                              className={`h-6 px-2 text-xs ${
                                order.is_collected 
                                  ? 'hover:bg-gray-200 bg-gray-100' 
                                  : 'hover:bg-green-100 bg-white text-green-600 border-green-600'
                              }`}
                              title={order.is_collected ? 'Tamamlanmadı olarak işaretle' : 'Tamamlandı olarak işaretle'}
                            >
                              {order.is_collected ? '↩️' : '✅'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditAgendaDate(order)}
                              className="h-6 px-2 text-xs text-indigo-600 border-indigo-600 hover:bg-indigo-50"
                              title="Ajanda tarihini düzenle"
                            >
                              📅
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenAddNoteDialog(order)}
                              className="h-6 px-2 text-xs text-purple-600 border-purple-600 hover:bg-purple-50"
                              title="Not ekle"
                            >
                              📝
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewOrderDetails(order)}
                              className="h-6 px-2 text-xs text-blue-600 border-blue-600 hover:bg-blue-50"
                              title="Sipariş detayları"
                            >
                              Detay
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Installation Date Section */}
              {dayData.target_installations && dayData.target_installations.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    🎯 Hedef Montaj Tarihi ({dayData.target_installations.length})
                  </h4>
                  <div className="space-y-2">
                    {dayData.target_installations.map((target) => (
                      <div key={target.id} className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h6 className="font-semibold text-gray-800">
                              {target.customer_info?.name || 'Müşteri Bilgisi Yok'}
                            </h6>
                            <p className="text-sm text-gray-600">{target.description}</p>
                            {target.model && (
                              <p className="text-xs text-gray-600">🚪 {target.model} • {target.color}</p>
                            )}
                            {target.door_handle && (
                              <p className="text-xs text-gray-600">🔧 {target.door_handle} • {target.surface}</p>
                            )}
                            <p className="text-xs text-blue-600 mt-1">
                              📅 Satış: {new Date(target.created_at).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-lg font-semibold text-green-600">
                              ₺{target.amount?.toLocaleString('tr-TR') || '0'}
                            </p>
                            <Badge variant="outline" className="text-xs mt-1">
                              {target.production_line === 'lake' ? 'Lake' : 'Melamin'}
                            </Badge>
                            {target.customer_info?.balance && (
                              <p className={`text-xs mt-1 ${target.customer_info.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                Bakiye: ₺{target.customer_info.balance.toLocaleString('tr-TR')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty Day - Removed as per user request */}
            </CardContent>
          </Card>
        )) : (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-gray-500">Bu dönemde program bulunamadı</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>📝 Yeni Not Ekle</DialogTitle>
            <DialogDescription>
              Ajandanıza yeni bir not ekleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateNote} className="space-y-4">
            <div>
              <Label htmlFor="note_date">Tarih</Label>
              <Input
                type="date"
                id="note_date"
                value={newNote.date}
                onChange={(e) => setNewNote({...newNote, date: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="note_text">Not Metni</Label>
              <textarea
                id="note_text"
                rows={4}
                value={newNote.note_text}
                onChange={(e) => setNewNote({...newNote, note_text: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Notunuzu buraya yazın..."
                required
              />
            </div>
            
            <div>
              <Label htmlFor="note_type">Not Türü</Label>
              <Select value={newNote.note_type} onValueChange={(value) => setNewNote({...newNote, note_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">🔸 Genel Not</SelectItem>
                  <SelectItem value="customer">👤 Müşteri Notu</SelectItem>
                  <SelectItem value="bayi">🏪 Bayi Notu</SelectItem>
                  <SelectItem value="installation">🔧 Montaj Notu</SelectItem>
                  <SelectItem value="incomplete_work">⚠️ Eksik İş Notu</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newNote.note_type === "customer" && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="customer_search">Müşteri Ara ve Seç</Label>
                  <div className="relative customer-search-container">
                    <Input
                      id="customer_search"
                      type="text"
                      placeholder="Müşteri adı, soyadı veya telefon ile arayın..."
                      value={customerSearchTerm}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      onFocus={() => setShowCustomerDropdown(customerSearchTerm.length > 0 || customers.length > 0)}
                      className="w-full"
                    />
                    
                    {/* Müşteri Arama Sonuçları Dropdown */}
                    {showCustomerDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredCustomers.length > 0 ? (
                          <div className="py-1">
                            {filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                onClick={() => handleCustomerSelect(customer)}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">
                                  {customer.name} {customer.surname}
                                </div>
                                <div className="text-sm text-gray-600 flex items-center gap-2">
                                  <span>📞 {customer.phone}</span>
                                  {customer.serial_number && (
                                    <span>🏷️ {customer.serial_number}</span>
                                  )}
                                </div>
                                {customer.address && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    📍 {customer.address}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : customerSearchTerm.length > 0 ? (
                          <div className="px-3 py-4 text-center text-gray-500">
                            "{customerSearchTerm}" için müşteri bulunamadı
                          </div>
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            Arama yapmak için yazmaya başlayın...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Müşteri Finansal Bilgileri - Genişletilmiş */}
                {loadingCustomerInfo && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                )}

                {selectedCustomerInfo && !loadingCustomerInfo && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg border border-blue-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-blue-900 text-lg flex items-center gap-2">
                        💰 {selectedCustomerInfo.customer_name}
                      </h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        selectedCustomerInfo.debt_status === 'Borçlu' ? 'bg-red-100 text-red-800' :
                        selectedCustomerInfo.debt_status === 'Alacaklı' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedCustomerInfo.debt_status}
                      </span>
                    </div>
                    
                    {/* Ana Finansal Bilgiler */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-800 mb-1">
                            ₺{Math.abs(selectedCustomerInfo.current_balance)?.toLocaleString('tr-TR') || '0'}
                          </div>
                          <div className="text-sm text-gray-600">Mevcut Bakiye</div>
                          <div className={`text-xs mt-1 px-2 py-1 rounded ${
                            selectedCustomerInfo.current_balance > 0 ? 'bg-red-100 text-red-700' :
                            selectedCustomerInfo.current_balance < 0 ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {selectedCustomerInfo.current_balance > 0 ? '↗️ Borç' : 
                             selectedCustomerInfo.current_balance < 0 ? '↙️ Alacak' : '➖ Sıfır'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="text-center">
                          <div className={`text-lg font-bold mb-1 ${
                            selectedCustomerInfo.risk_level === 'Yüksek' ? 'text-red-600' :
                            selectedCustomerInfo.risk_level === 'Orta' ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {selectedCustomerInfo.risk_level === 'Yüksek' ? '🚨' : 
                             selectedCustomerInfo.risk_level === 'Orta' ? '⚠️' : '✅'} 
                            {selectedCustomerInfo.risk_level}
                          </div>
                          <div className="text-sm text-gray-600">Risk Seviyesi</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedCustomerInfo.total_transactions} işlem
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600 mb-1">
                            💳 {selectedCustomerInfo.last_sale?.payment_method || 'Belirlenmemiş'}
                          </div>
                          <div className="text-sm text-gray-600">Son Ödeme Yöntemi</div>
                          <div className="text-xs text-gray-500 mt-1">
                            ₺{selectedCustomerInfo.last_sale?.amount?.toLocaleString('tr-TR') || '0'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Detaylar Accordion */}
                    <div className="space-y-3">
                      {/* Son Satış Detayları */}
                      {selectedCustomerInfo.last_sale?.amount > 0 && (
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                          <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            🛒 Son Satış Detayları
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Tutar:</span>
                              <span className="font-medium ml-2">₺{selectedCustomerInfo.last_sale?.amount?.toLocaleString('tr-TR')}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Ödeme Şekli:</span>
                              <span className="font-medium ml-2">{selectedCustomerInfo.last_sale?.payment_method}</span>
                            </div>
                            {selectedCustomerInfo.last_sale?.payment_description && (
                              <div className="md:col-span-2">
                                <span className="text-gray-600">Ödeme Notu:</span>
                                <span className="font-medium ml-2">{selectedCustomerInfo.last_sale?.payment_description}</span>
                              </div>
                            )}
                            {selectedCustomerInfo.last_sale?.description && (
                              <div className="md:col-span-2">
                                <span className="text-gray-600">Açıklama:</span>
                                <span className="font-medium ml-2">{selectedCustomerInfo.last_sale?.description}</span>
                              </div>
                            )}
                            {selectedCustomerInfo.last_sale?.date && (
                              <div>
                                <span className="text-gray-600">Tarih:</span>
                                <span className="font-medium ml-2">
                                  {new Date(selectedCustomerInfo.last_sale.date).toLocaleDateString('tr-TR')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Son Ödeme Detayları */}
                      {selectedCustomerInfo.last_payment?.amount > 0 && (
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                          <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            💰 Son Ödeme Detayları
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Tutar:</span>
                              <span className="font-medium ml-2">₺{selectedCustomerInfo.last_payment?.amount?.toLocaleString('tr-TR')}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Ödeme Şekli:</span>
                              <span className="font-medium ml-2">{selectedCustomerInfo.last_payment?.payment_method}</span>
                            </div>
                            {selectedCustomerInfo.last_payment?.date && (
                              <div>
                                <span className="text-gray-600">Tarih:</span>
                                <span className="font-medium ml-2">
                                  {new Date(selectedCustomerInfo.last_payment.date).toLocaleDateString('tr-TR')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* İletişim Bilgileri */}
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <h5 className="font-semibold text-gray-800 mb-2">📞 İletişim Bilgileri</h5>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-2">
                          <span>📱</span>
                          <span>{selectedCustomerInfo.phone}</span>
                        </div>
                        {selectedCustomerInfo.address && (
                          <div className="flex items-start gap-2">
                            <span>📍</span>
                            <span>{selectedCustomerInfo.address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {newNote.note_type === "bayi" && (
              <div>
                <Label htmlFor="bayi_id">Bayi</Label>
                <Select value={newNote.bayi_id} onValueChange={(value) => setNewNote({...newNote, bayi_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Bayi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {bayiler.map((bayi) => (
                      <SelectItem key={bayi.id} value={bayi.id}>
                        {bayi.name} {bayi.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsNoteDialogOpen(false);
                  setSelectedCustomerInfo(null);
                  setCustomerSearchTerm("");
                  setShowCustomerDropdown(false);
                }}
              >
                İptal
              </Button>
              <Button type="submit">
                📝 Not Ekle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>✏️ Not Düzenle</DialogTitle>
            <DialogDescription>
              Mevcut notunuzu düzenleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateNote} className="space-y-4">
            <div>
              <Label htmlFor="edit_note_date">Tarih</Label>
              <Input
                type="date"
                id="edit_note_date"
                value={editNote.date}
                onChange={(e) => setEditNote({...editNote, date: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit_note_text">Not Metni</Label>
              <textarea
                id="edit_note_text"
                rows={4}
                value={editNote.note_text}
                onChange={(e) => setEditNote({...editNote, note_text: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Notunuzu buraya yazın..."
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit_note_type">Not Türü</Label>
              <Select value={editNote.note_type} onValueChange={(value) => setEditNote({...editNote, note_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">🔸 Genel Not</SelectItem>
                  <SelectItem value="customer">👤 Müşteri Notu</SelectItem>
                  <SelectItem value="bayi">🏪 Bayi Notu</SelectItem>
                  <SelectItem value="installation">🔧 Montaj Notu</SelectItem>
                  <SelectItem value="incomplete_work">⚠️ Eksik İş Notu</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editNote.note_type === "customer" && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit_customer_search">Müşteri Ara ve Seç</Label>
                  <div className="relative customer-search-container">
                    <Input
                      id="edit_customer_search"
                      type="text"
                      placeholder="Müşteri adı, soyadı veya telefon ile arayın..."
                      value={customerSearchTerm}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      onFocus={() => setShowCustomerDropdown(customerSearchTerm.length > 0 || customers.length > 0)}
                      className="w-full"
                    />
                    
                    {/* Müşteri Arama Sonuçları Dropdown - Edit Dialog */}
                    {showCustomerDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredCustomers.length > 0 ? (
                          <div className="py-1">
                            {filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                onClick={() => {
                                  setEditNote({...editNote, customer_id: customer.id});
                                  setCustomerSearchTerm(`${customer.name} ${customer.surname} - ${customer.phone}`);
                                  setShowCustomerDropdown(false);
                                  fetchCustomerFinancialInfo(customer.id);
                                }}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">
                                  {customer.name} {customer.surname}
                                </div>
                                <div className="text-sm text-gray-600 flex items-center gap-2">
                                  <span>📞 {customer.phone}</span>
                                  {customer.serial_number && (
                                    <span>🏷️ {customer.serial_number}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : customerSearchTerm.length > 0 ? (
                          <div className="px-3 py-4 text-center text-gray-500">
                            "{customerSearchTerm}" için müşteri bulunamadı
                          </div>
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            Arama yapmak için yazmaya başlayın...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Müşteri Finansal Bilgileri - Edit Dialog'da Kompakt */}
                {selectedCustomerInfo && !loadingCustomerInfo && (
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <h5 className="font-semibold text-blue-800 text-sm">
                        💰 {selectedCustomerInfo.customer_name}
                      </h5>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        selectedCustomerInfo.debt_status === 'Borçlu' ? 'bg-red-100 text-red-700' :
                        selectedCustomerInfo.debt_status === 'Alacaklı' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedCustomerInfo.debt_status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                      <div>
                        <span className="text-gray-600">Bakiye:</span>
                        <span className={`ml-1 font-semibold ${
                          selectedCustomerInfo.current_balance > 0 ? 'text-red-600' : 
                          selectedCustomerInfo.current_balance < 0 ? 'text-green-600' : 'text-gray-600'
                        }`}>
                          ₺{Math.abs(selectedCustomerInfo.current_balance)?.toLocaleString('tr-TR') || '0'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Risk:</span>
                        <span className={`ml-1 font-medium ${
                          selectedCustomerInfo.risk_level === 'Yüksek' ? 'text-red-600' :
                          selectedCustomerInfo.risk_level === 'Orta' ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {selectedCustomerInfo.risk_level}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-600">Son Ödeme:</span>
                        <span className="ml-1 font-medium">
                          {selectedCustomerInfo.last_sale?.payment_method || 'Belirtilmemiş'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {editNote.note_type === "bayi" && (
              <div>
                <Label htmlFor="edit_bayi_id">Bayi</Label>
                <Select value={editNote.bayi_id} onValueChange={(value) => setEditNote({...editNote, bayi_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Bayi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Bayi seçimini kaldır</SelectItem>
                    {bayiler.map((bayi) => (
                      <SelectItem key={bayi.id} value={bayi.id}>
                        {bayi.name} {bayi.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedCustomerInfo(null);
                  setCustomerSearchTerm("");
                  setShowCustomerDropdown(false);
                }}
              >
                İptal
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                ✏️ Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reschedule Task Dialog */}
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>📅 Yeniden Tarih Ata</DialogTitle>
            <DialogDescription>
              İşlem için yeni bir tarih belirleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRescheduleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new_date">Yeni Tarih</Label>
              <Input
                type="date"
                id="new_date"
                value={rescheduleData.new_date}
                onChange={(e) => setRescheduleData({...rescheduleData, new_date: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="reschedule_notes">Açıklama (İsteğe Bağlı)</Label>
              <textarea
                id="reschedule_notes"
                rows={3}
                value={rescheduleData.notes}
                onChange={(e) => setRescheduleData({...rescheduleData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Tarih değişikliği ile ilgili açıklama..."
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsRescheduleDialogOpen(false)}
              >
                İptal
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                📅 Tarihi Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Task Note Dialog */}
      <Dialog open={isTaskNoteDialogOpen} onOpenChange={setIsTaskNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>📝 İşleme Not Ekle</DialogTitle>
            <DialogDescription>
              Seçili işlem için not ekleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTaskNoteSubmit} className="space-y-4">
            <div>
              <Label htmlFor="task_note_text">Not</Label>
              <textarea
                id="task_note_text"
                rows={4}
                value={taskNoteData.note_text}
                onChange={(e) => setTaskNoteData({...taskNoteData, note_text: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="İşlem ile ilgili notunuzu yazın..."
                required
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsTaskNoteDialogOpen(false)}
              >
                İptal
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                📝 Notu Ekle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ajanda Tarihi Düzenleme Dialog'u (Collection Orders için) */}
      <Dialog open={isAgendaDialogOpen} onOpenChange={setIsAgendaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajanda Tarihini Düzenle</DialogTitle>
            <DialogDescription>
              {selectedInstallationForAgenda && (
                <>
                  {selectedInstallationForAgenda.customer_name || selectedInstallationForAgenda.bayi_name} - 
                  {selectedInstallationForAgenda.door_type}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_agenda_date">Ajanda Tarihi</Label>
              <Input
                id="edit_agenda_date"
                type="date"
                value={agendaDate}
                onChange={(e) => setAgendaDate(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsAgendaDialogOpen(false);
                setAgendaDate("");
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleSaveAgendaDateFromAgenda}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Not Ekleme Dialog'u (Collection Orders için) */}
      <Dialog open={isAddNoteDialogOpen} onOpenChange={setIsAddNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Not Ekle</DialogTitle>
            <DialogDescription>
              {selectedOrderForNote && (
                <>
                  {selectedOrderForNote.customer_name || selectedOrderForNote.bayi_name} - 
                  {selectedOrderForNote.door_type}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="order_note">Not</Label>
              <textarea
                id="order_note"
                rows={4}
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Sipariş ile ilgili notunuzu yazın..."
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsAddNoteDialogOpen(false);
                setOrderNote("");
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleSaveOrderNote}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sipariş Detayı Modal - Collection Tracking Orders için */}
      <Dialog open={isOrderDetailDialogOpen} onOpenChange={setIsOrderDetailDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Sipariş Detayları</DialogTitle>
                <DialogDescription>
                  {selectedOrderDetail && `${selectedOrderDetail.customer_name || selectedOrderDetail.bayi_name} - Sipariş ve Üretim Bilgileri`}
                </DialogDescription>
              </div>
              <Button 
                onClick={() => window.print()} 
                variant="outline"
                className="no-print"
              >
                <Printer className="h-4 w-4 mr-2" />
                Yazdır
              </Button>
            </div>
          </DialogHeader>
          
          {selectedOrderDetail && selectedOrderDetail.transaction && (
            <div className="space-y-4">
              {/* Müşteri Bilgileri */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Müşteri Bilgileri</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Müşteri:</strong> {selectedOrderDetail.customer?.name} {selectedOrderDetail.customer?.surname}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Cari Kod:</strong> {selectedOrderDetail.customer?.serial_number || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Telefon:</strong> 
                      <a href={`tel:${selectedOrderDetail.customer?.phone}`} className="text-blue-600 hover:underline cursor-pointer ml-1">
                        📞 {selectedOrderDetail.customer?.phone || 'N/A'}
                      </a>
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Satış Tutarı:</strong> 
                      <span className="text-green-600 ml-1">
                        ₺{selectedOrderDetail.sale_amount?.toLocaleString('tr-TR') || selectedOrderDetail.transaction.amount?.toLocaleString('tr-TR')}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 mt-3">
                  <div>
                    <p className="font-medium text-sm"><strong>Adres:</strong></p>
                    <p className="text-sm bg-white p-2 rounded border">
                      {selectedOrderDetail.customer?.address || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sipariş ve Finansal Bilgiler */}
              <div className="bg-green-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Sipariş ve Finansal Bilgiler</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Sipariş Tarihi:</strong> {selectedOrderDetail.transaction?.created_at ? new Date(selectedOrderDetail.transaction.created_at).toLocaleDateString('tr-TR') : 'N/A'}
                    </p>
                  </div>
                  {currentUser?.role !== 'installation' && (
                    <>
                      <div>
                        <p className="font-medium text-sm">
                          <strong>Satış Tutarı:</strong> 
                          <span className="text-green-600 ml-1">
                            ₺{selectedOrderDetail.sale_amount?.toLocaleString('tr-TR') || selectedOrderDetail.transaction?.amount?.toLocaleString('tr-TR')}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          <strong>Güncel Bakiye:</strong> 
                          <span className={selectedOrderDetail.current_balance > 0 ? 'text-red-600 ml-1' : 'text-green-600 ml-1'}>
                            ₺{selectedOrderDetail.current_balance?.toLocaleString('tr-TR') || '0'}
                          </span>
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Durum:</strong> {selectedOrderDetail.status?.toUpperCase() || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>🎯 Hedef Montaj:</strong> 
                      <span className="text-blue-600 ml-1">
                        {selectedOrderDetail.installation_date ? 
                          new Date(selectedOrderDetail.installation_date).toLocaleDateString('tr-TR') : 
                          'Belirlenmemiş'
                        }
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Tamamlanma:</strong> 
                      {selectedOrderDetail.completed_date ? 
                        new Date(selectedOrderDetail.completed_date).toLocaleDateString('tr-TR') : 
                        'Tamamlanmamış'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Ürün Özellikleri */}
              <div className="bg-yellow-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Ürün Özellikleri</h3>
                <div className="grid grid-cols-4 gap-3">
                  {selectedOrderDetail.transaction?.model && (
                    <div>
                      <p className="font-medium text-sm"><strong>Model:</strong> {selectedOrderDetail.transaction.model}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction?.color && (
                    <div>
                      <p className="font-medium text-sm"><strong>Renk:</strong> {selectedOrderDetail.transaction.color}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction?.door_handle && (
                    <div>
                      <p className="font-medium text-sm"><strong>Kapı Kolu:</strong> {selectedOrderDetail.transaction.door_handle}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction?.surface && (
                    <div>
                      <p className="font-medium text-sm"><strong>Yüzey:</strong> {selectedOrderDetail.transaction.surface}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction?.installation && (
                    <div>
                      <p className="font-medium text-sm"><strong>Montaj:</strong> {selectedOrderDetail.transaction.installation}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction?.installation_location && (
                    <div>
                      <p className="font-medium text-sm"><strong>Montaj Yeri:</strong> {selectedOrderDetail.transaction.installation_location}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction?.inner_frame && (
                    <div>
                      <p className="font-medium text-sm"><strong>İç Pervaz:</strong> {selectedOrderDetail.transaction.inner_frame}</p>
                    </div>
                  )}
                  {selectedOrderDetail.transaction?.outer_frame && (
                    <div>
                      <p className="font-medium text-sm"><strong>Dış Pervaz:</strong> {selectedOrderDetail.transaction.outer_frame}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Montaj Bilgileri */}
              <div className="bg-purple-50 p-3 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">Montaj Bilgileri</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Montaj Elemanı:</strong> {selectedOrderDetail.installer_name || 'Atanmamış'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Montaj Tarihi:</strong> 
                      {selectedOrderDetail.installation_date ? 
                        new Date(selectedOrderDetail.installation_date).toLocaleDateString('tr-TR') : 
                        'Belirtilmemiş'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      <strong>Tamamlanma Tarihi:</strong> 
                      {selectedOrderDetail.completed_date ? 
                        new Date(selectedOrderDetail.completed_date).toLocaleDateString('tr-TR') : 
                        'Tamamlanmamış'
                      }
                    </p>
                  </div>
                </div>
                {selectedOrderDetail.has_incomplete_work && (
                  <div className="mt-3 p-3 bg-orange-100 border-l-4 border-orange-500">
                    <p className="text-sm font-medium text-orange-800">⚠️ Eksik İş Mevcut</p>
                    {selectedOrderDetail.incomplete_work_notes && (
                      <p className="text-sm text-orange-700 mt-1">{selectedOrderDetail.incomplete_work_notes}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Ölçü Tabloları */}
              {selectedOrderDetail.transaction?.measurements && (() => {
                let measurements = [];
                try {
                  measurements = JSON.parse(selectedOrderDetail.transaction.measurements);
                } catch (e) {
                  console.error("Ölçü verileri parse edilemedi:", e);
                  return null;
                }
                
                const validMeasurements = measurements.filter(m => 
                  m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description
                );

                if (validMeasurements.length === 0) return null;

                return (
                  <div className="space-y-3">
                    {/* Orijinal Ölçüler */}
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <h3 className="font-semibold mb-2 text-sm">Orijinal Ölçüler</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 p-1">Sıra</th>
                              <th className="border border-gray-300 p-1">En</th>
                              <th className="border border-gray-300 p-1">Boy</th>
                              <th className="border border-gray-300 p-1">Kasa</th>
                              <th className="border border-gray-300 p-1">Yön</th>
                              <th className="border border-gray-300 p-1">Cam</th>
                              <th className="border border-gray-300 p-1">Oda</th>
                              <th className="border border-gray-300 p-1">Açıklama</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validMeasurements.map((measurement, index) => (
                              <tr key={index}>
                                <td className="border border-gray-300 p-1 font-medium">{index + 1}</td>
                                <td className="border border-gray-300 p-1">{measurement.width || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.height || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.frame || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.room || '-'}</td>
                                <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Kanat Ölçüsü */}
                    <div className="bg-red-50 p-3 rounded-lg">
                      <h3 className="font-semibold mb-2 text-sm">KANAT ÖLÇÜSÜ</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 p-1">Sıra</th>
                              <th className="border border-gray-300 p-1">En</th>
                              <th className="border border-gray-300 p-1">Boy</th>
                              <th className="border border-gray-300 p-1">Yön</th>
                              <th className="border border-gray-300 p-1">Cam</th>
                              <th className="border border-gray-300 p-1">Açıklama</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validMeasurements.map((measurement, index) => {
                              const originalWidth = parseFloat(measurement.width) || 0;
                              const originalHeight = parseFloat(measurement.height) || 0;
                              const productionWidth = originalWidth > 6 ? originalWidth - 6 : originalWidth;
                              const productionHeight = originalHeight > 4 ? originalHeight - 4 : originalHeight;
                              
                              return (
                                <tr key={index}>
                                  <td className="border border-gray-300 p-1 font-medium">{index + 1}</td>
                                  <td className="border border-gray-300 p-1 font-medium text-red-600">
                                    {productionWidth > 0 ? productionWidth : '-'}
                                  </td>
                                  <td className="border border-gray-300 p-1 font-medium text-red-600">
                                    {productionHeight > 0 ? productionHeight : '-'}
                                  </td>
                                  <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Notlar */}
              {(selectedOrderDetail.description || selectedOrderDetail.transaction?.description) && (
                <div className="bg-orange-50 p-2 rounded-lg">
                  <h3 className="font-semibold mb-1 text-sm">Notlar</h3>
                  <p className="text-xs">
                    <strong>Üretim Notu:</strong> {selectedOrderDetail.description || selectedOrderDetail.transaction?.description}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Customers Component - Optimized with pagination
const Customers = ({ currentUser, sessionToken }) => {
  const location = useLocation(); // Router location değişimini dinlemek için
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [totalActive, setTotalActive] = useState(0);
  const [totalInactive, setTotalInactive] = useState(0);
  const [stats, setStats] = useState({ total_debt: 0, total_credit_balance: 0, net_balance: 0, debt_customer_count: 0, credit_customer_count: 0 });
  const customersPerPage = 50; // Server-side sayfalama
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  // Not dialog states
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteCustomer, setNoteCustomer] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteReminder, setNoteReminder] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [sortBy, setSortBy] = useState("created_desc");
  const sortRef = useRef("created_desc");
  const customersLoadedRef = useRef(false);
  const customersReqRef = useRef(0);

  // Müşteri filtreleme - Optimized with useMemo
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm.trim()) return customers;
    return customers.filter(customer => 
      `${customer.name} ${customer.surname} ${customer.serial_number}`.toLowerCase().includes(customerSearchTerm.toLowerCase())
    );
  }, [customers, customerSearchTerm]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");  // "all", "active", "inactive"
  const [selectedCustomerForStatement, setSelectedCustomerForStatement] = useState(null);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    serial_number: "",
    name: "",
    surname: "",
    phone: "",
    address: ""
  });

  // CRITICAL FIX: Sayfa her açıldığında fresh data fetch et (tediye güncellemelerini görmek için)
  useEffect(() => {
    fetchCustomers(1, searchTerm, activeTab);
    fetchStats(activeTab);
  }, [location.pathname]);

  // Search debounce + tab değişimi → sayfa 1'e sıfırla ve yeniden çek
  useEffect(() => {
    const t = setTimeout(() => {
      fetchCustomers(1, searchTerm, activeTab);
      fetchStats(activeTab);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm, activeTab]);

  // CRITICAL FIX: Component görünür olduğunda veriyi yenile (tab değiştirme için)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCustomers(currentPage, searchTerm, activeTab);
        fetchStats(activeTab);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentPage, searchTerm, activeTab]);

  useEffect(() => {
    sortRef.current = sortBy;
    fetchCustomers(1, searchTerm, activeTab);
  }, [sortBy]);

  const fetchCustomers = async (page = 1, q = "", status = "all") => {
    const reqId = ++customersReqRef.current;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customers`, {
        params: {
          limit: customersPerPage,
          skip: (page - 1) * customersPerPage,
          q: q.trim(),
          status,
          sort: sortRef.current,
        }
      });
      if (reqId !== customersReqRef.current) return;
      customersLoadedRef.current = true;
      const data = response.data || {};
      const customersData = data.customers || [];
      setCustomers(Array.isArray(customersData) ? customersData : []);
      setTotalCustomers(data.total ?? customersData.length);
      setTotalAll(data.total_all ?? 0);
      setTotalActive(data.total_active ?? 0);
      setTotalInactive(data.total_inactive ?? 0);
      setCurrentPage(page);
      setLoading(false);
    } catch (error) {
      console.error("Müşteriler yüklenirken hata:", error);
      toast.error("Müşteriler yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchStats = async (status = "all") => {
    try {
      const response = await axios.get(`${API}/customers/stats`, { params: { status } });
      setStats(response.data || { total_debt: 0, total_credit_balance: 0, net_balance: 0, debt_customer_count: 0, credit_customer_count: 0 });
    } catch (error) {
      console.error("İstatistikler yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
        toast.success("Müşteri güncellendi");
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success("Müşteri eklendi");
      }
      setIsDialogOpen(false);
      setEditingCustomer(null);
      setFormData({ serial_number: "", name: "", surname: "", phone: "", address: "", payment_method: "", payment_notes: "" });
      fetchCustomers(currentPage, searchTerm, activeTab);
      fetchStats(activeTab);
    } catch (error) {
      console.error("Müşteri kaydedilirken hata:", error);
      toast.error("Müşteri kaydedilirken hata oluştu");
    }
  };

  const handleEdit = useCallback((customer) => {
    setEditingCustomer(customer);
    setFormData({
      serial_number: customer.serial_number || "",
      name: customer.name,
      surname: customer.surname,
      phone: customer.phone,
      address: customer.address,
      payment_method: customer.payment_method || "",
      payment_notes: customer.payment_notes || ""
    });
    setIsDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async (customerId) => {
    // Gelişmiş güvenlik onayı
    const confirmed = window.confirm("⚠️ MÜŞTERİ VE İLİŞKİLİ KAYITLARI SİLME ONAYI\n\nBu müşteriyi ve ilişkili TÜM kayıtlarını silmek istediğinizden emin misiniz?\n\n🔴 SİLİNECEK KAYITLAR:\n• Müşteri bilgileri\n• Tüm satış işlemleri\n• Montaj programları\n• Tahsilat makbuzları\n• Üretim kayıtları\n• Ajanda notları\n• Bayi kayıtları (eğer varsa)\n\n⚠️ DİKKAT: Bu işlem GERİ ALINAMAZ!\n\nSilmek için 'Tamam'a, iptal etmek için 'İptal'e basın.");
    
    if (!confirmed) {
      return; // Kullanıcı iptal etti
    }

    try {
      const response = await axios.delete(`${API}/customers/${customerId}`, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.summary) {
          console.log("Silinen kayıtlar:", response.data.summary);
        }
      } else {
        toast.success("Müşteri ve ilişkili kayıtlar güvenli bir şekilde silindi");
      }
      
      fetchCustomers(currentPage, searchTerm, activeTab);
      fetchStats(activeTab);
    } catch (error) {
      console.error("Müşteri silinirken hata:", error);
      const errorMessage = error.response?.data?.detail || "Müşteri silinirken hata oluştu";
      toast.error(errorMessage);
    }
  }, [sessionToken, fetchCustomers, currentPage, searchTerm, activeTab]);

  const handleStatusChange = async (customerId, newStatus) => {
    try {
      await axios.put(`${API}/customers/${customerId}/status?status=${newStatus}`);
      toast.success(`Müşteri ${newStatus === 'active' ? 'aktif' : 'pasif'} olarak işaretlendi`);
      fetchCustomers(currentPage, searchTerm, activeTab);
      fetchStats(activeTab);
    } catch (error) {
      console.error("Müşteri durumu güncellenirken hata:", error);
      toast.error("Müşteri durumu güncellenirken hata oluştu");
    }
  };

  const handleShowStatement = (customer) => {
    setSelectedCustomerForStatement(customer);
    setIsStatementModalOpen(true);
  };

  const openNoteDialog = (customer) => {
    setNoteCustomer(customer);
    setNoteText(customer.notes || "");
    setNoteReminder(customer.note_reminder_date || "");
    setIsNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!noteCustomer) return;
    try {
      setNoteSaving(true);
      await axios.patch(
        `${API}/customers/${noteCustomer.id}/notes`,
        { notes: noteText, note_reminder_date: noteReminder },
        { params: { session_token: sessionToken } }
      );
      toast.success("Not kaydedildi");
      setIsNoteDialogOpen(false);
      setNoteCustomer(null);
      setNoteText("");
      setNoteReminder("");
      fetchCustomers(currentPage, searchTerm, activeTab);
    } catch (error) {
      console.error("Not kaydedilirken hata:", error);
      toast.error(error.response?.data?.detail || "Not kaydedilirken hata oluştu");
    } finally {
      setNoteSaving(false);
    }
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingCustomer(null);
      setFormData({ 
        serial_number: "", 
        name: "", 
        surname: "", 
        phone: "", 
        address: "", 
        payment_method: "", 
        payment_notes: "" 
      });
    }
  };

  // Server-side filtreleme kullanıldığı için client-side hesaplama artık gerekli değil.
  // Tablo doğrudan `customers` state'ini gösterir; sekme değişiminde useEffect yeniden fetch eder.

  if (loading && !customersLoadedRef.current) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  const CustomerCard = ({ customer }) => (
    <Card key={customer.id} className="w-full">
      <CardContent className="p-2">
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-3 flex-1 min-w-0 text-xs">
            {customer.serial_number && (
              <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 whitespace-nowrap font-mono">
                #{customer.serial_number}
              </span>
            )}
            <h3 className="font-semibold text-sm text-gray-900 whitespace-nowrap">
              {customer.name} {customer.surname}
            </h3>
            <Badge 
              variant={customer.status === 'active' ? 'default' : 'secondary'}
              className="text-xs whitespace-nowrap"
            >
              {customer.status === 'active' ? 'Aktif' : 'Pasif'}
            </Badge>
            <span className="text-gray-600 whitespace-nowrap">
              📞 <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline">{customer.phone}</a>
            </span>
            <span className="text-gray-500 whitespace-nowrap">🏠 {customer.address}</span>
            <Badge variant={customer.balance >= 0 ? "default" : "destructive"} className="text-xs whitespace-nowrap">
              💰 ₺{customer.balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </Badge>
            {customer.status === 'active' && customer.balance > 0 && (
              <span className="text-xs text-orange-600 font-medium whitespace-nowrap">Borçlu</span>
            )}
            {customer.last_transaction_payment_method && (
              <span className="text-blue-700 text-xs whitespace-nowrap">
                💳 {customer.last_transaction_payment_method}
              </span>
            )}
          </div>
          
          <div className="flex gap-1 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => handleEdit(customer)} className="h-7 px-2 text-xs" title="Düzenle">
              <Edit className="h-3 w-3" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleShowStatement(customer)}
              title="Hesap Hareketleri"
              className="h-7 px-2 text-xs"
            >
              <Eye className="h-3 w-3" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleStatusChange(customer.id, customer.status === 'active' ? 'inactive' : 'active')}
              className="h-7 px-2 text-xs"
              title={customer.status === 'active' ? 'Pasif Yap' : 'Aktif Yap'}
            >
              {customer.status === 'active' ? '⏸️' : '▶️'}
            </Button>
            {currentUser?.role === 'admin' && (
              <Button variant="outline" size="sm" onClick={() => handleDelete(customer.id)} className="h-7 px-2 text-xs text-red-600 hover:text-red-700" title="Sil">
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
        {customer.last_transaction_payment_description && (
          <div className="mt-1 text-xs text-blue-600">
            💳 {customer.last_transaction_payment_description}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Yazdırma fonksiyonu - Müşteri Hesap Ekstresi (tüm eşleşen kayıtları çeker)
  const handlePrint = async () => {
    let printData = customers;
    try {
      toast.info("Yazdırma verileri hazırlanıyor...");
      const status = activeTab === "all" ? "all" : activeTab;
      const resp = await axios.get(`${API}/customers`, {
        params: { q: searchTerm.trim(), status, all: true, limit: 999999, skip: 0, sort: sortRef.current }
      });
      printData = resp.data?.customers || customers;
    } catch (err) {
      console.error("Yazdırma verileri çekilemedi, mevcut sayfa kullanılacak:", err);
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Müşteri Hesap Ekstresi</title>
        <style>
          @media print {
            @page { margin: 1cm; }
            body { margin: 0; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0;
            font-size: 24px;
            color: #1a1a1a;
          }
          .header .date {
            margin-top: 5px;
            font-size: 12px;
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          th {
            background-color: #4a5568;
            color: white;
            padding: 12px;
            text-align: left;
            font-weight: bold;
            font-size: 14px;
          }
          td {
            padding: 10px 12px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          tr:hover {
            background-color: #f0f0f0;
          }
          .totals {
            margin-top: 20px;
            padding: 15px;
            background-color: #f8f9fa;
            border: 2px solid #333;
            border-radius: 5px;
          }
          .totals-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 14px;
            border-bottom: 1px solid #ddd;
          }
          .totals-row:last-child {
            border-bottom: none;
            font-weight: bold;
            font-size: 16px;
            color: #d32f2f;
          }
          .totals-label {
            font-weight: 600;
          }
          .totals-value {
            font-weight: bold;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .no-print {
            display: none;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>MÜŞTERİ HESAP EKSTRESİ</h1>
          <div class="date">Tarih: ${new Date().toLocaleDateString('tr-TR', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Müşteri Kodu</th>
              <th>Müşteri Adı Soyadı</th>
              <th class="text-right">Satış (Borç)</th>
              <th class="text-right">Tahsilat / İade</th>
              <th class="text-right">Temlik</th>
              <th class="text-right">Toplam Ödeme</th>
              <th class="text-right">Güncel Borç Tutarı</th>
            </tr>
          </thead>
          <tbody>
            ${printData.map(customer => `
              <tr>
                <td>${customer.serial_number || 'N/A'}</td>
                <td>${customer.name} ${customer.surname}${customer.needs_review ? ' <span style="color:#c2410c;font-size:11px;">(İncelenmesi Gereken Hesap)</span>' : ''}${customer.notes && customer.notes.trim() ? `<div style="margin-top:3px;font-size:11px;color:#713f12;background:#fefce8;border:1px solid #fde68a;border-radius:3px;padding:2px 4px;white-space:pre-wrap;"><b>Not:</b> ${String(customer.notes).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>` : ''}</td>
                <td class="text-right">₺${(customer.total_debt || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                <td class="text-right">₺${((customer.total_credit || 0) - (customer.total_temlik || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                <td class="text-right">₺${(customer.total_temlik || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                <td class="text-right">₺${(customer.total_credit || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                <td class="text-right">₺${(customer.balance || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="totals-row">
            <span class="totals-label">Toplam Borç Tutarı:</span>
            <span class="totals-value">₺${printData
              .reduce((sum, c) => sum + (c.total_debt || 0), 0)
              .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
          </div>
          <div class="totals-row">
            <span class="totals-label">Toplam Temlik Tutarı:</span>
            <span class="totals-value">₺${printData
              .reduce((sum, c) => sum + (c.total_temlik || 0), 0)
              .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
          </div>
          <div class="totals-row">
            <span class="totals-label">Toplam Ödeme Tutarı (Tahsilat + Temlik):</span>
            <span class="totals-value">₺${printData
              .reduce((sum, c) => sum + (c.total_credit || 0), 0)
              .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
          </div>
          <div class="totals-row">
            <span class="totals-label">Toplam Güncel Borç:</span>
            <span class="totals-value">₺${printData
              .reduce((sum, c) => sum + (c.balance || 0), 0)
              .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="space-y-6">
      {/* MÜŞTERİ ÖZET KARTLARI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase">Toplam Müşteri</p>
                <p className="text-2xl font-bold text-blue-700">{totalAll}</p>
                <p className="text-xs text-blue-500 mt-1">
                  Aktif: {totalActive} | 
                  Pasif: {totalInactive}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-red-600 uppercase">Toplam Borç</p>
                <p className="text-2xl font-bold text-red-700">
                  ₺{(stats.total_debt || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-red-500 mt-1">
                  {stats.debt_customer_count || 0} borçlu müşteri
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-600 uppercase">Toplam Alacak</p>
                <p className="text-2xl font-bold text-green-700">
                  ₺{(stats.total_credit_balance || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-green-500 mt-1">
                  {stats.credit_customer_count || 0} alacaklı müşteri
                </p>
              </div>
              <TrendingDown className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-purple-600 uppercase">Net Bakiye</p>
                <p className={`text-2xl font-bold ${(stats.net_balance || 0) >= 0 ? 'text-red-700' : 'text-green-700'}`}>
                  ₺{(stats.net_balance || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-purple-500 mt-1">
                  {(stats.net_balance || 0) >= 0 ? 'Alınacak' : 'Ödenecek'}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Müşteriler</h1>
          <p className="text-muted-foreground">Müşteri bilgilerini yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handlePrint}
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Yazdır
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingCustomer(null);
                setFormData({ serial_number: "", name: "", surname: "", phone: "", address: "", payment_method: "", payment_notes: "" });
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Müşteri
              </Button>
            </DialogTrigger>
            <DialogContent className="dialog-professional">
            <DialogHeader>
              <DialogTitle className="dialog-title">{editingCustomer ? "Müşteri Düzenle" : "Yeni Müşteri Ekle"}</DialogTitle>
              <DialogDescription className="dialog-description">
                Müşteri bilgilerini ekleyin veya düzenleyin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="serial_number" className="text-sm font-medium text-gray-700">Seri Numarası</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                  placeholder="Boş bırakırsanız otomatik atanır (8000'den başlar)"
                  className="input-professional"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Boş bırakırsanız sistem otomatik olarak seri numarası atayacaktır
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-gray-700">Ad</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="input-professional"
                  />
                </div>
                <div>
                  <Label htmlFor="surname" className="text-sm font-medium text-gray-700">Soyad</Label>
                  <Input
                    id="surname"
                    value={formData.surname}
                    onChange={(e) => setFormData({...formData, surname: e.target.value})}
                    required
                    className="input-professional"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  className="input-professional"
                />
              </div>
              <div>
                <Label htmlFor="address" className="text-sm font-medium text-gray-700">Adres</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                  className="input-professional min-h-[80px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="payment_method" className="text-sm font-medium text-gray-700">Varsayılan Ödeme Yöntemi</Label>
                  <select
                    id="payment_method"
                    value={formData.payment_method || ""}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-1 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seçiniz...</option>
                    <option value="nakit">Nakit</option>
                    <option value="kredi_karti">Kredi Kartı</option>
                    <option value="banka_havalesi">Banka Havalesi</option>
                    <option value="cek">Çek</option>
                    <option value="senet">Senet</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="payment_notes" className="text-sm font-medium text-gray-700">Ödeme Notları</Label>
                  <Input
                    id="payment_notes"
                    value={formData.payment_notes || ""}
                    onChange={(e) => setFormData({...formData, payment_notes: e.target.value})}
                    placeholder="Ödeme ile ilgili notlar..."
                    className="input-professional"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="btn-professional">
                  İptal
                </Button>
                <Button type="submit" className="btn-professional bg-primary-professional">
                  {editingCustomer ? "Güncelle" : "Ekle"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Müşteri adı, cari no veya telefon ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[300px]" data-testid="customers-sort-select">
            <ArrowUpDown className="h-4 w-4 mr-2 text-gray-500" />
            <SelectValue placeholder="Sıralama" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc" data-testid="customers-sort-created_desc">Son eklenen önce</SelectItem>
            <SelectItem value="serial_asc" data-testid="customers-sort-serial_asc">Cari hesap numarası</SelectItem>
            <SelectItem value="balance_asc" data-testid="customers-sort-balance_asc">Borç bakiyesi (artan)</SelectItem>
            <SelectItem value="balance_desc" data-testid="customers-sort-balance_desc">Borç bakiyesi (azalan)</SelectItem>
            <SelectItem value="last_activity_asc" data-testid="customers-sort-last_activity_asc">Son işlem tarihi (eskiden yeniye)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all" className="flex items-center space-x-2" data-testid="customers-tab-all">
            <span>Tüm Müşteriler</span>
            <Badge variant="default">{totalAll}</Badge>
          </TabsTrigger>
          <TabsTrigger value="active" className="flex items-center space-x-2" data-testid="customers-tab-active">
            <span>Aktif</span>
            <Badge variant="secondary">{totalActive}</Badge>
          </TabsTrigger>
          <TabsTrigger value="inactive" className="flex items-center space-x-2" data-testid="customers-tab-inactive">
            <span>Pasif</span>
            <Badge variant="outline">{totalInactive}</Badge>
          </TabsTrigger>
        </TabsList>
        
        {/* Müşteri Tablosu */}
        <Card className="mt-4">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[220px]">Cari No / Müşteri Adı</TableHead>
                  <TableHead className="w-[80px]">Durum</TableHead>
                  <TableHead className="w-[130px]">Telefon</TableHead>
                  <TableHead className="w-[250px]">Adres</TableHead>
                  <TableHead className="w-[100px] text-right">Bakiye</TableHead>
                  <TableHead className="w-[100px]">Ödeme</TableHead>
                  <TableHead className="w-[140px] text-center">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 py-8" data-testid="customers-empty-state">
                      {searchTerm.trim() ? `"${searchTerm}" için müşteri bulunamadı` : "Müşteri bulunamadı"}
                    </TableCell>
                  </TableRow>
                ) : customers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      <div className="flex flex-col gap-0.5">
                        {customer.serial_number && (
                          <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 w-fit font-mono">
                            #{customer.serial_number}
                          </span>
                        )}
                        <span>{customer.name} {customer.surname}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={customer.status === 'active' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {customer.status === 'active' ? 'Aktif' : 'Pasif'}
                      </Badge>
                      {customer.needs_review && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold leading-tight text-orange-800 bg-orange-50 border border-orange-300 rounded px-1.5 py-0.5"
                          title={customer.last_activity_date ? `Son işlem: ${new Date(customer.last_activity_date).toLocaleDateString('tr-TR')}` : 'Hiç işlem yok'}
                          data-testid={`customer-needs-review-${customer.id}`}
                        >
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          İncelenmesi Gereken Hesap
                        </div>
                      )}
                      <div className="text-[10px] text-gray-500 mt-0.5" data-testid={`customer-last-activity-${customer.id}`}>
                        Son işlem: {customer.last_activity_date ? new Date(customer.last_activity_date).toLocaleDateString('tr-TR') : 'yok'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline text-sm">
                        📞 {customer.phone}
                      </a>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-[280px]">
                      <div className="truncate">{customer.address}</div>
                      {customer.notes && customer.notes.trim() && (
                        <div
                          className="mt-1 text-xs text-yellow-900 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 whitespace-pre-wrap break-words"
                          data-testid={`customer-note-${customer.id}`}
                          title={customer.notes}
                        >
                          <span className="font-semibold">Not:</span> {customer.notes}
                        </div>
                      )}
                      {customer.note_reminder_date && (
                        <div className="mt-1 inline-flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid={`customer-note-reminder-${customer.id}`}>
                          <BellRing className="h-3 w-3" />
                          Hatırlatma: {new Date(`${customer.note_reminder_date}T00:00:00`).toLocaleDateString('tr-TR')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={customer.balance >= 0 ? "default" : "destructive"} className="text-xs">
                        ₺{customer.balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </Badge>
                      {customer.status === 'active' && customer.balance > 0 && (
                        <div className="text-xs text-orange-600 font-medium">Borçlu</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-blue-700">
                      {customer.last_transaction_payment_method || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-center">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEdit(customer)} 
                          className="h-7 px-2" 
                          title="Düzenle"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openNoteDialog(customer)}
                          className={`h-7 px-2 ${customer.notes && customer.notes.trim() ? 'bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100' : ''}`}
                          title={customer.notes && customer.notes.trim() ? `Not: ${customer.notes}` : "Not Ekle"}
                          data-testid={`customer-note-btn-${customer.id}`}
                        >
                          <StickyNote className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleShowStatement(customer)}
                          title="Hesap Hareketleri"
                          className="h-7 px-2"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleStatusChange(customer.id, customer.status === 'active' ? 'inactive' : 'active')}
                          className="h-7 px-2"
                          title={customer.status === 'active' ? 'Pasif Yap' : 'Aktif Yap'}
                        >
                          {customer.status === 'active' ? '⏸️' : '▶️'}
                        </Button>
                        {currentUser?.role === 'admin' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDelete(customer.id)} 
                            className="h-7 px-2 text-red-600 hover:text-red-700" 
                            title="Sil"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Sayfalama Kontrolleri */}
        {totalCustomers > customersPerPage && (
          <div className="flex items-center justify-between mt-4 px-2" data-testid="customers-pagination">
            <div className="text-sm text-gray-600">
              <span className="font-medium">
                {((currentPage - 1) * customersPerPage) + 1}
                {" - "}
                {Math.min(currentPage * customersPerPage, totalCustomers)}
              </span>
              {" / "}
              <span className="font-bold">{totalCustomers}</span> müşteri
              {searchTerm.trim() && <span className="ml-2 text-blue-600">("{searchTerm}" araması)</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchCustomers(1, searchTerm, activeTab)}
                disabled={currentPage === 1 || loading}
                data-testid="customers-pagination-first"
              >
                « İlk
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchCustomers(currentPage - 1, searchTerm, activeTab)}
                disabled={currentPage === 1 || loading}
                data-testid="customers-pagination-prev"
              >
                ‹ Önceki
              </Button>
              <span className="text-sm px-3 py-1 bg-gray-100 rounded font-medium" data-testid="customers-pagination-info">
                Sayfa {currentPage} / {Math.max(1, Math.ceil(totalCustomers / customersPerPage))}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchCustomers(currentPage + 1, searchTerm, activeTab)}
                disabled={currentPage >= Math.ceil(totalCustomers / customersPerPage) || loading}
                data-testid="customers-pagination-next"
              >
                Sonraki ›
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchCustomers(Math.ceil(totalCustomers / customersPerPage), searchTerm, activeTab)}
                disabled={currentPage >= Math.ceil(totalCustomers / customersPerPage) || loading}
                data-testid="customers-pagination-last"
              >
                Son »
              </Button>
            </div>
          </div>
        )}

        <TabsContent value="all" className="hidden">
        </TabsContent>
        
        <TabsContent value="active" className="hidden">
        </TabsContent>
        
        <TabsContent value="inactive" className="hidden">
        </TabsContent>
      </Tabs>
      
      <AccountStatementModal 
        isOpen={isStatementModalOpen} 
        onClose={() => setIsStatementModalOpen(false)}
        customer={selectedCustomerForStatement}
      />

      {/* Müşteri Notu Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={(open) => {
        setIsNoteDialogOpen(open);
        if (!open) {
          setNoteCustomer(null);
          setNoteText("");
        }
      }}>
        <DialogContent className="max-w-lg" data-testid="customer-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-yellow-600" />
              Müşteri Notu
            </DialogTitle>
            <DialogDescription>
              {noteCustomer ? (
                <>
                  <span className="font-semibold">{noteCustomer.serial_number ? `#${noteCustomer.serial_number} - ` : ""}{noteCustomer.name} {noteCustomer.surname}</span> için not ekleyin veya düzenleyin. Not, müşteriler listesinde adresin altında görünecek.
                </>
              ) : (
                "Müşteri hakkında notunuzu yazın"
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Örn: Vip müşteri, sadece hafta içi arayın, ödemeleri çek ile yapılıyor..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={6}
              className="resize-none"
              data-testid="customer-note-textarea"
              autoFocus
            />
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[180px]">
                <Label htmlFor="note-reminder-date" className="flex items-center gap-1 text-sm">
                  <BellRing className="h-4 w-4 text-amber-600" /> Hatırlatma Tarihi (isteğe bağlı)
                </Label>
                <Input
                  id="note-reminder-date"
                  type="date"
                  value={noteReminder}
                  onChange={(e) => setNoteReminder(e.target.value)}
                  data-testid="customer-note-reminder-input"
                />
              </div>
              {noteReminder && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setNoteReminder("")} data-testid="customer-note-reminder-clear">
                  Hatırlatmayı Kaldır
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-500">Tarih seçilirse not, o tarihten 7 gün önce Dashboard'daki "Yaklaşan Notlar" bölümünde görünür.</p>
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>{noteText.length} karakter</span>
              {noteText.trim() && (
                <button
                  type="button"
                  onClick={() => setNoteText("")}
                  className="text-red-600 hover:underline"
                  data-testid="customer-note-clear"
                >
                  Notu Temizle
                </button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNoteDialogOpen(false)}
              disabled={noteSaving}
              data-testid="customer-note-cancel"
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveNote}
              disabled={noteSaving}
              data-testid="customer-note-save"
            >
              {noteSaving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kaydediliyor...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Kaydet</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Bayiler Component
const Bayiler = ({ currentUser }) => {
  const [bayiler, setBayiler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBayi, setEditingBayi] = useState(null);
  const [isUserAssignOpen, setIsUserAssignOpen] = useState(false);
  const [selectedBayi, setSelectedBayi] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedBayiForStatement, setSelectedBayiForStatement] = useState(null);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  const [isBayiStatementModalOpen, setIsBayiStatementModalOpen] = useState(false);
  const [selectedCustomerForStatement, setSelectedCustomerForStatement] = useState(null); // AccountStatementModal için
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    phone: "",
    address: ""
  });

  useEffect(() => {
    fetchBayiler();
    fetchUsers();
  }, []);

  const fetchBayiler = async () => {
    try {
      const response = await axios.get(`${API}/bayiler`);
      setBayiler(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Bayiler yüklenirken hata:", error);
      toast.error("Bayiler yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/users`, {
        params: { session_token: sessionToken }
      });
      setUsers(response.data.filter(user => user.role === 'bayi')); // Sadece bayi rolündeki kullanıcılar
    } catch (error) {
      console.error("Kullanıcılar yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBayi) {
        await axios.put(`${API}/bayiler/${editingBayi.id}`, formData);
        toast.success("Bayi güncellendi");
      } else {
        await axios.post(`${API}/bayiler`, formData);
        toast.success("Bayi eklendi");
      }
      setIsDialogOpen(false);
      setEditingBayi(null);
      setFormData({ name: "", surname: "", phone: "", address: "" });
      fetchBayiler();
    } catch (error) {
      console.error("Bayi kaydedilirken hata:", error);
      toast.error("Bayi kaydedilirken hata oluştu");
    }
  };

  const handleEdit = (bayi) => {
    setEditingBayi(bayi);
    setFormData({
      name: bayi.name,
      surname: bayi.surname,
      phone: bayi.phone,
      address: bayi.address
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (bayiId) => {
    if (window.confirm("Bu bayiyi silmek istediğinizden emin misiniz?")) {
      try {
        await axios.delete(`${API}/bayiler/${bayiId}`);
        toast.success("Bayi silindi");
        fetchBayiler();
      } catch (error) {
        console.error("Bayi silinirken hata:", error);
        toast.error("Bayi silinirken hata oluştu");
      }
    }
  };

  const openUserAssign = (bayi) => {
    setSelectedBayi(bayi);
    setSelectedUserId(bayi.user_id || "");
    setIsUserAssignOpen(true);
  };

  const handleUserAssign = async () => {
    try {
      const finalUserId = selectedUserId === "none" ? "" : selectedUserId;
      await axios.put(`${API}/bayiler/${selectedBayi.id}`, {
        ...selectedBayi,
        user_id: finalUserId
      });
      toast.success("Kullanıcı ataması yapıldı");
      setIsUserAssignOpen(false);
      setSelectedBayi(null);
      setSelectedUserId("");
      fetchBayiler();
    } catch (error) {
      console.error("Kullanıcı ataması yapılırken hata:", error);
      toast.error("Kullanıcı ataması yapılırken hata oluştu");
    }
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingBayi(null);
      setFormData({ 
        name: "", 
        surname: "", 
        phone: "", 
        address: "" 
      });
    }
  };

  const handleViewTransactions = (bayi) => {
    // Bayi hesap hareketleri için özel modal'ı aç
    setSelectedBayiForStatement(bayi);
    setIsBayiStatementModalOpen(true);
  };

  const filteredBayiler = bayiler.filter(bayi =>
    bayi.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bayi.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bayi.phone.includes(searchTerm)
  );

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bayiler</h1>
          <p className="text-muted-foreground">Bayi bilgilerini yönetin</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Yeni Bayi
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingBayi ? "Bayi Düzenle" : "Yeni Bayi Ekle"}</DialogTitle>
              <DialogDescription>
                Bayi bilgilerini girin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Ad</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="surname">Soyad</Label>
                  <Input
                    id="surname"
                    value={formData.surname}
                    onChange={(e) => setFormData({...formData, surname: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="address">Adres</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                {editingBayi ? "Güncelle" : "Ekle"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Bayi ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="border rounded-lg">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-4 font-medium">Ad Soyad</th>
              <th className="text-left p-4 font-medium">Telefon</th>
              <th className="text-left p-4 font-medium">Adres</th>
              <th className="text-left p-4 font-medium">Atanmış Kullanıcı</th>
              <th className="text-left p-4 font-medium">Borç Durumu</th>
              <th className="text-right p-4 font-medium">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredBayiler.map((bayi) => (
              <tr key={bayi.id} className="border-b hover:bg-gray-50">
                <td className="p-4 font-medium">{bayi.name} {bayi.surname}</td>
                <td className="p-4">{bayi.phone}</td>
                <td className="p-4">{bayi.address}</td>
                <td className="p-4">
                  {bayi.user_id ? (
                    <span className="text-green-600 font-medium">
                      {users.find(u => u.id === bayi.user_id)?.full_name || 'Kullanıcı bulunamadı'}
                    </span>
                  ) : (
                    <span className="text-gray-500">Atanmadı</span>
                  )}
                </td>
                <td className="p-4">
                  <span className={`font-medium ${bayi.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {bayi.balance?.toLocaleString('tr-TR')} ₺
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleViewTransactions(bayi)}
                      title="Hesap Hareketleri"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openUserAssign(bayi)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <UserCheck className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(bayi)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {currentUser?.role === 'admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(bayi.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredBayiler.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg text-gray-600">Bayi bulunamadı</p>
            <p className="text-sm text-gray-500">İlk bayinizi eklemek için "Yeni Bayi" butonuna tıklayın</p>
          </div>
        )}
      </div>

      {/* Kullanıcı Atama Dialog */}
      <Dialog open={isUserAssignOpen} onOpenChange={setIsUserAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kullanıcı Ata</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bayi</Label>
              <p className="text-sm text-gray-600">
                {selectedBayi?.name} {selectedBayi?.surname}
              </p>
            </div>
            <div>
              <Label htmlFor="user-select">Kullanıcı Seč</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kullanıcı seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kullanıcı seçilmedi</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.username})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleUserAssign} className="flex-1">
                Ata
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setIsUserAssignOpen(false)}
                className="flex-1"
              >
                İptal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <AccountStatementModal 
        isOpen={isStatementModalOpen} 
        onClose={() => setIsStatementModalOpen(false)}
        customer={selectedCustomerForStatement}
      />

      <BayiAccountStatementModal 
        isOpen={isBayiStatementModalOpen} 
        onClose={() => setIsBayiStatementModalOpen(false)}
        bayi={selectedBayiForStatement}
      />
    </div>
  );
};

// Suppliers Component
const Suppliers = () => {
  const location = useLocation(); // Router location değişimini dinlemek için
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalDebt, setTotalDebt] = useState(0); // Toplam borç
  // Tedarikçi hesap hareketleri için state'ler
  const [isStatementOpen, setIsStatementOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierTransactions, setSupplierTransactions] = useState([]);
  const [supplierPayments, setSupplierPayments] = useState([]);
  const [stmtStart, setStmtStart] = useState("");
  const [stmtEnd, setStmtEnd] = useState("");
  const stmtDay = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const stmtInRange = (v) => {
    const day = stmtDay(v);
    return (!stmtStart || day >= stmtStart) && (!stmtEnd || day <= stmtEnd);
  };
  const stmtTx = useMemo(() => supplierTransactions.filter((t) => stmtInRange(t.created_at)), [supplierTransactions, stmtStart, stmtEnd]);
  const stmtPay = useMemo(() => supplierPayments.filter((p) => stmtInRange(p.payment_date)), [supplierPayments, stmtStart, stmtEnd]);
  const stmtOpening = useMemo(() => {
    if (!stmtStart) return 0;
    const debit = supplierTransactions.filter((t) => stmtDay(t.created_at) < stmtStart).reduce((s, t) => s + (t.amount || 0), 0);
    const credit = supplierPayments.filter((p) => stmtDay(p.payment_date) < stmtStart).reduce((s, p) => s + (p.amount || 0), 0);
    return debit - credit;
  }, [supplierTransactions, supplierPayments, stmtStart]);
  const setStmtPreset = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const fmt = (d) => stmtDay(d);
    if (preset === 'this_month') { setStmtStart(fmt(new Date(y, m, 1))); setStmtEnd(fmt(new Date(y, m + 1, 0))); }
    else if (preset === 'last_month') { setStmtStart(fmt(new Date(y, m - 1, 1))); setStmtEnd(fmt(new Date(y, m, 0))); }
    else if (preset === 'this_year') { setStmtStart(`${y}-01-01`); setStmtEnd(`${y}-12-31`); }
    else { setStmtStart(""); setStmtEnd(""); }
  };
  const [statementLoading, setStatementLoading] = useState(false);
  const [formData, setFormData] = useState({
    serial_number: "",
    name: "",
    surname: "",
    phone: "",
    address: ""
  });

  // CRITICAL FIX: Sayfa her açıldığında fresh data fetch et (tediye güncellemelerini görmek için)
  useEffect(() => {
    fetchSuppliers();
  }, [location.pathname]); // location.pathname değişince yenile

  // CRITICAL FIX: Component görünür olduğunda veriyi yenile (tab değiştirme için)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchSuppliers();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
      
      // Toplam borç hesapla (balance > 0 olanlar)
      const totalSupplierDebt = response.data.reduce((sum, supplier) => {
        return sum + (supplier.balance > 0 ? supplier.balance : 0);
      }, 0);
      setTotalDebt(totalSupplierDebt);
      
      setLoading(false);
    } catch (error) {
      console.error("Tedarikçiler yüklenirken hata:", error);
      toast.error("Tedarikçiler yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, formData);
        toast.success("Tedarikçi güncellendi");
      } else {
        await axios.post(`${API}/suppliers`, formData);
        toast.success("Tedarikçi eklendi");
      }
      setIsDialogOpen(false);
      setEditingSupplier(null);
      setFormData({ serial_number: "", name: "", surname: "", phone: "", address: "", payment_method: "", payment_notes: "" });
      fetchSuppliers();
    } catch (error) {
      console.error("Tedarikçi kaydedilirken hata:", error);
      toast.error("Tedarikçi kaydedilirken hata oluştu");
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      surname: supplier.surname,
      phone: supplier.phone,
      address: supplier.address
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (supplierId) => {
    if (window.confirm("Bu tedarikçiyi silmek istediğinizden emin misiniz?")) {
      try {
        await axios.delete(`${API}/suppliers/${supplierId}`);
        toast.success("Tedarikçi silindi");
        fetchSuppliers();
      } catch (error) {
        console.error("Tedarikçi silinirken hata:", error);
        toast.error("Tedarikçi silinirken hata oluştu");
      }
    }
  };

  // Tedarikçi hesap hareketleri fonksiyonları
  const handleShowSupplierStatement = async (supplier) => {
    setSelectedSupplier(supplier);
    setStmtStart("");
    setStmtEnd("");
    setStatementLoading(true);
    setIsStatementOpen(true);
    
    try {
      // Yeni supplier account statement endpoint'ini kullan
      const response = await axios.get(`${API}/suppliers/${supplier.id}/account-statement`, {
        params: { session_token: localStorage.getItem('session_token') }
      });
      
      const accountStatement = response.data;
      
      // Movements'ları transactions ve payments olarak ayır
      const transactions = accountStatement.movements.filter(m => m.type === 'transaction');
      const payments = accountStatement.movements.filter(m => m.type === 'payment_receipt');
      
      setSupplierTransactions(transactions);
      setSupplierPayments(payments);
      
      // Account summary bilgisini supplier'a ekle
      setSelectedSupplier({
        ...supplier,
        account_summary: accountStatement.account_summary
      });

    } catch (error) {
      console.error("Tedarikçi hesap hareketleri yüklenirken hata:", error);
      toast.error("Hesap hareketleri yüklenirken hata oluştu");
    } finally {
      setStatementLoading(false);
    }
  };

  const downloadSupplierStatement = async () => {
    if (!selectedSupplier || (!stmtTx.length && !stmtPay.length)) {
      toast.error("İndirilecek veri bulunamadı");
      return;
    }

    try {
      // Tüm işlemleri kronolojik sırada birleştir
      const allTransactions = [];
      
      // Alış işlemleri (borç artırır)
      if (stmtTx) {
        stmtTx.forEach(transaction => {
          allTransactions.push({
            date: transaction.created_at ? new Date(transaction.created_at) : new Date(),
            description: transaction.description || 'Alış İşlemi',
            debit: transaction.amount || 0, // Borç
            credit: 0, // Alacak
            type: 'alis'
          });
        });
      }
      
      // Ödemeler (borç azaltır)
      if (stmtPay) {
        stmtPay.forEach(payment => {
          allTransactions.push({
            date: payment.payment_date ? new Date(payment.payment_date) : new Date(),
            description: payment.description || 'Ödeme',
            debit: 0, // Borç
            credit: payment.amount || 0, // Alacak
            type: 'odeme'
          });
        });
      }
      
      // Tarih sırasına göre sırala
      allTransactions.sort((a, b) => a.date - b.date);
      
      // Running balance hesapla (tarih aralığı varsa devir bakiyesinden başlar)
      let runningBalance = stmtOpening;
      const transactionsWithBalance = allTransactions.map(transaction => {
        runningBalance = runningBalance + transaction.debit - transaction.credit;
        return {
          ...transaction,
          balance: runningBalance
        };
      });
      if (stmtStart) {
        transactionsWithBalance.unshift({
          date: new Date(`${stmtStart}T00:00:00`), description: 'Devir Bakiyesi', debit: 0, credit: 0, type: 'devir', balance: stmtOpening
        });
      }
      const rangeText = stmtStart || stmtEnd
        ? `${stmtStart ? new Date(`${stmtStart}T00:00:00`).toLocaleDateString('tr-TR') : 'Başlangıç'} - ${stmtEnd ? new Date(`${stmtEnd}T00:00:00`).toLocaleDateString('tr-TR') : 'Günümüz'}`
        : 'Tüm Zamanlar';

      // HTML içeriği oluştur
      const htmlContent = `
        <div id="pdf-content" style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="color: #333; margin-bottom: 20px; text-align: center;">TEDARİKÇİ HESAP EKSTRESİ</h1>
          
          <div style="margin-bottom: 20px; background-color: #f8f9fa; padding: 15px; border-radius: 5px;">
            <h2 style="color: #007bff; margin: 0 0 10px 0;">${selectedSupplier.name} ${selectedSupplier.surname}</h2>
            <p><strong>Telefon:</strong> ${selectedSupplier.phone || '-'}</p>
            <p><strong>Adres:</strong> ${selectedSupplier.address || '-'}</p>
            <p><strong>Ekstre Tarihi:</strong> ${new Date().toLocaleDateString('tr-TR')}</p>
            <p><strong>Tarih Aralığı:</strong> ${rangeText}</p>
            <p><strong>Güncel Bakiye:</strong> <span style="color: red; font-weight: bold;">₺${(selectedSupplier.balance || 0).toLocaleString('tr-TR')}</span></p>
          </div>
          
          <div style="margin: 20px 0;">
            <h3 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 5px;">HESAP HAREKETLERİ</h3>
            <table style="width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px;">
              <thead>
                <tr style="background-color: #007bff; color: white;">
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: center; width: 15%;">Tarih</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left; width: 35%;">Açıklama</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: right; width: 15%;">Borç</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: right; width: 15%;">Alacak</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: right; width: 20%;">Kalan Bakiye</th>
                </tr>
              </thead>
              <tbody>
                ${transactionsWithBalance.map((transaction, index) => {
                  const dateStr = transaction.date.toLocaleDateString('tr-TR');
                  const balanceColor = transaction.balance > 0 ? 'red' : transaction.balance < 0 ? 'green' : 'black';
                  return `
                    <tr style="${index % 2 === 0 ? 'background-color: #f9f9f9;' : ''}">
                      <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${dateStr}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${transaction.description}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: red;">
                        ${transaction.debit > 0 ? `₺${transaction.debit.toLocaleString('tr-TR')}` : '-'}
                      </td>
                      <td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: green;">
                        ${transaction.credit > 0 ? `₺${transaction.credit.toLocaleString('tr-TR')}` : '-'}
                      </td>
                      <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold; color: ${balanceColor};">
                        ₺${transaction.balance.toLocaleString('tr-TR')}
                      </td>
                    </tr>
                  `;
                }).join('')}
                
                <tr style="background-color: #e7f3ff; font-weight: bold;">
                  <td colspan="2" style="border: 1px solid #ddd; padding: 10px; text-align: center; font-size: 14px;">
                    <strong>TOPLAM</strong>
                  </td>
                  <td style="border: 1px solid #ddd; padding: 10px; text-align: right; color: red;">
                    ₺${transactionsWithBalance.reduce((sum, t) => sum + t.debit, 0).toLocaleString('tr-TR')}
                  </td>
                  <td style="border: 1px solid #ddd; padding: 10px; text-align: right; color: green;">
                    ₺${transactionsWithBalance.reduce((sum, t) => sum + t.credit, 0).toLocaleString('tr-TR')}
                  </td>
                  <td style="border: 1px solid #ddd; padding: 10px; text-align: right; color: blue;">
                    ₺${((stmtStart || stmtEnd) ? runningBalance : (selectedSupplier.balance || 0)).toLocaleString('tr-TR')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      `;

      // HTML'i PDF'e çevir (önceki kod aynı)...

      // Geçici div oluştur
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);

      // HTML'i canvas'a çevir
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      // Canvas'ı PDF'e çevir
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      
      const imgWidth = 210; // A4 genişliği (mm)
      const pageHeight = 295; // A4 yüksekliği (mm)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Geçici div'i kaldır
      document.body.removeChild(tempDiv);

      // PDF'i indir
      pdf.save(`tedarikci_ekstre_${selectedSupplier.name}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success("Tedarikçi ekstresi PDF olarak indirildi");
    } catch (error) {
      console.error("PDF oluşturma hatası:", error);
      toast.error("PDF oluşturulurken hata oluştu: " + error.message);
    }
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingSupplier(null);
      setFormData({ 
        serial_number: "", 
        name: "", 
        surname: "", 
        phone: "", 
        address: "", 
        payment_method: "", 
        payment_notes: "" 
      });
    }
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    `${supplier.name} ${supplier.surname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.phone.includes(searchTerm)
  );

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tedarikçiler</h1>
          <p className="text-muted-foreground">Tedarikçi bilgilerini yönetin</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingSupplier(null);
              setFormData({ serial_number: "", name: "", surname: "", phone: "", address: "", payment_method: "", payment_notes: "" });
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Yeni Tedarikçi
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSupplier ? "Tedarikçi Düzenle" : "Yeni Tedarikçi Ekle"}</DialogTitle>
              <DialogDescription>
                Tedarikçi bilgilerini ekleyin veya düzenleyin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="serial_number">Seri Numarası</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                  placeholder="Boş bırakırsanız otomatik atanır (8000'den başlar)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Boş bırakırsanız sistem otomatik olarak seri numarası atayacaktır
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Ad</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="surname">Soyad</Label>
                  <Input
                    id="surname"
                    value={formData.surname}
                    onChange={(e) => setFormData({...formData, surname: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="address">Adres</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                {editingSupplier ? "Güncelle" : "Ekle"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <SupplierRecentOps api={API} />

      {/* Toplam Borç Kartı */}
      <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Toplam Tedarikçi Borcum</p>
              <p className="text-3xl font-bold text-red-600">
                ₺{totalDebt.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {suppliers.filter(s => s.balance > 0).length} tedarikçiye borç var
              </p>
            </div>
            <div className="bg-red-100 rounded-full p-4">
              <DollarSign className="h-8 w-8 text-red-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Tedarikçi ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSuppliers.map((supplier) => (
          <Card key={supplier.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{supplier.name} {supplier.surname}</CardTitle>
                  <CardDescription>{supplier.phone}</CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleShowSupplierStatement(supplier)}
                    title="Hesap Hareketleri"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Hesap Hareketleri
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(supplier)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(supplier.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">{supplier.address}</p>
              <div className="flex justify-between items-center">
                <Badge variant={supplier.balance <= 0 ? "default" : "destructive"}>
                  Bakiye: ₺{supplier.balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Tedarikçi Hesap Hareketleri Modal */}
      <Dialog open={isStatementOpen} onOpenChange={setIsStatementOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <div>
                <DialogTitle className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5" />
                  <span>Tedarikçi Hesap Hareketleri</span>
                </DialogTitle>
                <DialogDescription>
                  {selectedSupplier && `${selectedSupplier.name} ${selectedSupplier.surname} - Hesap Durumu`}
                </DialogDescription>
              </div>
              <Button 
                onClick={downloadSupplierStatement} 
                variant="outline" 
                disabled={!selectedSupplier || (!stmtTx.length && !stmtPay.length)}
              >
                <Download className="h-4 w-4 mr-2" />
                Ekstre İndir
              </Button>
            </div>
          </DialogHeader>

          {selectedSupplier && (
            <div className="space-y-6">
              {/* Tedarikçi Genel Bilgileri */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Tedarikçi Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium text-gray-600">Ad Soyad</Label>
                    <p className="font-medium">{selectedSupplier.name} {selectedSupplier.surname}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-600">Telefon</Label>
                    <p className="font-medium">📞 {selectedSupplier.phone}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-medium text-gray-600">Adres</Label>
                    <p className="font-medium">{selectedSupplier.address}</p>
                  </div>
                </div>
              </div>

              {/* Finansal Özet */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Finansal Özet</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <Label className="text-xs font-medium text-gray-600">Toplam Borç</Label>
                    <p className="text-lg font-bold text-red-600">
                      ₺{selectedSupplier.account_summary?.total_debt?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0,00'}
                    </p>
                  </div>
                  <div className="text-center">
                    <Label className="text-xs font-medium text-gray-600">Toplam Ödeme</Label>
                    <p className="text-lg font-bold text-blue-600">
                      ₺{selectedSupplier.account_summary?.total_payments?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0,00'}
                    </p>
                  </div>
                  <div className="text-center">
                    <Label className="text-xs font-medium text-gray-600">Güncel Bakiye</Label>
                    <p className={`text-lg font-bold ${(selectedSupplier.account_summary?.current_balance || 0) <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ₺{selectedSupplier.account_summary?.current_balance?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0,00'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tarih Aralığı */}
              <div className="flex flex-wrap items-end gap-3 p-3 border rounded-lg bg-white" data-testid="supplier-statement-date-filter">
                <div>
                  <Label className="text-xs">Başlangıç Tarihi</Label>
                  <Input type="date" value={stmtStart} onChange={(e) => setStmtStart(e.target.value)} className="w-44" data-testid="supplier-statement-start" />
                </div>
                <div>
                  <Label className="text-xs">Bitiş Tarihi</Label>
                  <Input type="date" value={stmtEnd} onChange={(e) => setStmtEnd(e.target.value)} className="w-44" data-testid="supplier-statement-end" />
                </div>
                <div className="flex flex-wrap gap-1">
                  {[['this_month', 'Bu Ay'], ['last_month', 'Geçen Ay'], ['this_year', 'Bu Yıl'], ['all', 'Tümü']].map(([key, label]) => (
                    <Button key={key} type="button" size="sm" variant="outline" onClick={() => setStmtPreset(key)} data-testid={`supplier-statement-preset-${key}`}>{label}</Button>
                  ))}
                </div>
                {(stmtStart || stmtEnd) && (
                  <div className="ml-auto text-right text-sm" data-testid="supplier-statement-period-summary">
                    {stmtStart && <p>Devir Bakiyesi: <b>₺{stmtOpening.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</b></p>}
                    <p>Dönem Alış: <b className="text-red-600">₺{stmtTx.reduce((s, t) => s + (t.amount || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</b>
                      {' '}• Dönem Ödeme: <b className="text-blue-600">₺{stmtPay.reduce((s, p) => s + (p.amount || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</b></p>
                  </div>
                )}
              </div>

              {statementLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Hesap hareketleri yükleniyor...</span>
                </div>
              ) : (
                <>
                  {/* Alış İşlemleri */}
                  {stmtTx.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                          <ShoppingCart className="h-5 w-5 text-blue-600" />
                          <span>Alış İşlemleri ({stmtTx.length})</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Tarih</th>
                                <th className="text-left p-2">Açıklama</th>
                                <th className="text-left p-2">Tutar</th>
                                <th className="text-left p-2">Tip</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stmtTx.map((transaction, index) => {
                                const transactionDate = transaction.created_at ? 
                                  (typeof transaction.created_at === 'string' ? new Date(transaction.created_at) : transaction.created_at) : 
                                  null;
                                const isValidDate = transactionDate && !isNaN(transactionDate.getTime());
                                
                                return (
                                  <tr key={index} className="border-b hover:bg-gray-50">
                                    <td className="p-2">
                                      {isValidDate ? transactionDate.toLocaleDateString('tr-TR') : '-'}
                                    </td>
                                    <td className="p-2">{transaction.description}</td>
                                    <td className="p-2 font-medium text-red-600">
                                      +₺{transaction.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="p-2">
                                      <Badge variant="outline">Alış</Badge>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Ödeme İşlemleri */}
                  {stmtPay.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                          <CreditCard className="h-5 w-5 text-green-600" />
                          <span>Yapılan Ödemeler ({stmtPay.length})</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Tarih</th>
                                <th className="text-left p-2">Açıklama</th>
                                <th className="text-left p-2">Tutar</th>
                                <th className="text-left p-2">Tip</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stmtPay.map((payment, index) => {
                                const paymentDate = payment.payment_date ? 
                                  (typeof payment.payment_date === 'string' ? new Date(payment.payment_date) : payment.payment_date) : 
                                  null;
                                const isValidDate = paymentDate && !isNaN(paymentDate.getTime());
                                
                                return (
                                  <tr key={index} className="border-b hover:bg-gray-50">
                                    <td className="p-2">
                                      {isValidDate ? paymentDate.toLocaleDateString('tr-TR') : '-'}
                                    </td>
                                    <td className="p-2">{payment.description}</td>
                                    <td className="p-2 font-medium text-green-600">
                                      -₺{payment.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="p-2">
                                      <Badge variant="default" className="bg-green-600">Ödeme</Badge>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Veri yoksa */}
                  {stmtTx.length === 0 && stmtPay.length === 0 && (
                    <div className="text-center py-8">
                      <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg text-gray-600">Hesap hareketi bulunmuyor</p>
                      <p className="text-sm text-gray-500">{(stmtStart || stmtEnd) ? 'Seçilen tarih aralığında hareket yok' : 'Bu tedarikçi ile henüz işlem yapılmamış'}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Sale Details Modal Component
const SaleDetailsModal = ({ isOpen, onClose, saleDetails }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Sipariş Detayları</span>
          </DialogTitle>
          <DialogDescription>
            {saleDetails?.description} - {saleDetails && new Date(saleDetails.date).toLocaleDateString('tr-TR')}
          </DialogDescription>
        </DialogHeader>

        {saleDetails && (
          <div className="space-y-6">
            {/* Genel Bilgiler */}
            <div className="grid grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">İşlem Bilgileri</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p><strong>Tutar:</strong> ₺{saleDetails.amount?.toLocaleString('tr-TR')}</p>
                    <p><strong>Tarih:</strong> {new Date(saleDetails.date).toLocaleDateString('tr-TR')}</p>
                    <p><strong>Açıklama:</strong> {saleDetails.description}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ürün Özellikleri</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {saleDetails.details?.model && <p><strong>Model:</strong> {saleDetails.details.model}</p>}
                    {saleDetails.details?.color && <p><strong>Renk:</strong> {saleDetails.details.color}</p>}
                    {saleDetails.details?.door_handle && <p><strong>Kapı Kolu:</strong> {saleDetails.details.door_handle}</p>}
                    {saleDetails.details?.surface && <p><strong>Yüzey:</strong> {saleDetails.details.surface}</p>}
                    {saleDetails.details?.installation && <p><strong>Montaj:</strong> {saleDetails.details.installation}</p>}
                    {saleDetails.details?.installation_location && <p><strong>Montaj Yeri:</strong> {saleDetails.details.installation_location}</p>}
                    {saleDetails.details?.inner_frame && <p><strong>İç Pervaz:</strong> {saleDetails.details.inner_frame}</p>}
                    {saleDetails.details?.outer_frame && <p><strong>Dış Pervaz:</strong> {saleDetails.details.outer_frame}</p>}
                    {saleDetails.details?.production_line && <p><strong>Üretim Hattı:</strong> {saleDetails.details.production_line}</p>}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Ödeme Bilgileri */}
            {(saleDetails.details?.payment_method || saleDetails.details?.payment_description) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">💳 Ödeme Bilgileri</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {saleDetails.details?.payment_method && <p><strong>Ödeme Yöntemi:</strong> {saleDetails.details.payment_method}</p>}
                    {saleDetails.details?.payment_description && <p><strong>Ödeme Açıklaması:</strong> {saleDetails.details.payment_description}</p>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ölçü Bilgileri */}
            {saleDetails.details?.measurements && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ölçü Bilgileri</CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    try {
                      // JSON olarak parse etmeyi dene
                      const measurements = JSON.parse(saleDetails.details.measurements);
                      
                      if (Array.isArray(measurements) && measurements.length > 0) {
                        // JSON array formatındaysa tablo olarak göster
                        return (
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300 text-sm">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">#</th>
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">En</th>
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">Boy</th>
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">Kasa</th>
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">Yönü</th>
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">Cam</th>
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">Oda</th>
                                  <th className="border border-gray-300 px-3 py-2 text-center font-bold">Açıklama</th>
                                </tr>
                              </thead>
                              <tbody>
                                {measurements.map((measurement, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="border border-gray-300 px-3 py-2 text-center">
                                      {index + 1}
                                      {measurement.quantity && measurement.quantity > 1 && (
                                        <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                          x{measurement.quantity}
                                        </span>
                                      )}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">{measurement.width || '-'}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">{measurement.height || '-'}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">{measurement.thickness || measurement.frame || '-'}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">{measurement.direction || '-'}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">{measurement.glass || '-'}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">{measurement.room || '-'}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">{measurement.notes || measurement.description || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      } else if (typeof measurements === 'object') {
                        // Tek bir obje formatındaysa
                        return (
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {Object.entries(measurements).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium text-gray-600">{key}:</span>
                                <span className="ml-2">{String(value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      } else {
                        // Geçersiz format
                        return (
                          <div className="text-sm whitespace-pre-wrap font-mono bg-gray-100 p-3 rounded">
                            {saleDetails.details.measurements}
                          </div>
                        );
                      }
                    } catch (error) {
                      // JSON değilse raw text olarak göster
                      return (
                        <div className="text-sm whitespace-pre-wrap bg-gray-100 p-3 rounded">
                          {saleDetails.details.measurements}
                        </div>
                      );
                    }
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Detay Bilgileri */}
            {saleDetails.details?.details && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ek Detaylar</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{saleDetails.details.details}</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Bayi Account Statement Modal
const BayiAccountStatementModal = ({ isOpen, onClose, bayi }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isTransactionDetailOpen, setIsTransactionDetailOpen] = useState(false);

  const openTransactionDetail = async (movement) => {
    if (movement.type === 'sales' && movement.id) {
      try {
        // Get full transaction data from the transactions endpoint
        const sessionToken = localStorage.getItem('session_token');
        const response = await axios.get(`${API}/transactions/${bayi.id}`, {
          params: { session_token: sessionToken }
        });
        
        const transactions = response.data || [];
        const fullTransaction = transactions.find(t => t.id === movement.id);
        
        if (fullTransaction) {
          setSelectedTransaction(fullTransaction);
          setIsTransactionDetailOpen(true);
        } else {
          toast.error("İşlem detayları bulunamadı");
        }
      } catch (error) {
        console.error("İşlem detayları yüklenirken hata:", error);
        toast.error("İşlem detayları yüklenirken hata oluştu");
      }
    }
  };

  const parseMeasurements = (measurementsJson) => {
    try {
      return JSON.parse(measurementsJson || '[]');
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (isOpen && bayi) {
      fetchBayiTransactions();
    }
  }, [isOpen, bayi, startDate, endDate]);

  const fetchBayiTransactions = async () => {
    if (!bayi) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bayiler/${bayi.id}/transactions`);
      let transactions = response.data.transactions || [];
      
      // Tarih filtreleme
      if (startDate || endDate) {
        transactions = transactions.filter(transaction => {
          const transactionDate = new Date(transaction.date);
          const start = startDate ? new Date(startDate) : new Date('1900-01-01');
          const end = endDate ? new Date(endDate) : new Date('2100-12-31');
          return transactionDate >= start && transactionDate <= end;
        });
      }
      
      setMovements(transactions);
    } catch (error) {
      console.error("Bayi hesap hareketi yüklenirken hata:", error);
      toast.error("Bayi hesap hareketi yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMovementTypeColor = (type) => {
    switch (type) {
      case 'sales':
        return 'text-red-600 bg-red-50'; // Satış - bayi borçlanır
      case 'collection':
        return 'text-green-600 bg-green-50'; // Tahsilat - bayi öder
      case 'payment':
        return 'text-blue-600 bg-blue-50'; // Ödeme - bayiye ödeme
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'sales':
        return '📦'; // Satış
      case 'collection':
        return '💰'; // Tahsilat
      case 'payment':
        return '💳'; // Ödeme
      default:
        return '📄';
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            {bayi ? `${bayi.name} ${bayi.surname} - Hesap Hareketleri` : 'Bayi Hesap Hareketleri'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Bayi Bilgileri */}
          {bayi && (
            <Card className="p-4 bg-blue-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Bayi:</span>
                  <p className="font-bold">{bayi.name} {bayi.surname}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Telefon:</span>
                  <p>{bayi.phone}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Bakiye:</span>
                  <p className={`font-bold ${bayi.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(bayi.balance)}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Durum:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    bayi.balance >= 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {bayi.balance >= 0 ? 'Borçlu' : 'Alacaklı'}
                  </span>
                </div>
              </div>
            </Card>
          )}

          {/* Tarih Filtreleri */}
          <Card className="p-4">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="end_date">Bitiş Tarihi</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <Button 
                onClick={fetchBayiTransactions} 
                className="mt-6"
                disabled={loading}
              >
                {loading ? 'Yükleniyor...' : 'Filtrele'}
              </Button>
            </div>
          </Card>

          {/* Hareketler Tablosu */}
          <Card>
            <CardHeader>
              <CardTitle>Hesap Hareketleri ({movements.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Yükleniyor...</p>
                </div>
              ) : movements.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p>Bu bayi için henüz hareket kaydı bulunmuyor</p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 border-b">Tarih</th>
                        <th className="text-left p-3 border-b">İşlem Tipi</th>
                        <th className="text-left p-3 border-b">Açıklama</th>
                        <th className="text-right p-3 border-b">Tutar</th>
                        <th className="text-right p-3 border-b">Etki</th>
                        <th className="text-left p-3 border-b">Ödeme Yöntemi</th>
                        <th className="text-center p-3 border-b">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movements.map((movement, index) => (
                        <tr key={movement.id || index} className="hover:bg-gray-50 border-b">
                          <td className="p-3">
                            <div className="text-xs text-gray-500">
                              {formatDate(movement.date)}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMovementTypeColor(movement.type)}`}>
                              {getMovementIcon(movement.type)} {movement.type_label}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="max-w-xs truncate" title={movement.description}>
                              {movement.description || '-'}
                            </div>
                          </td>
                          <td className="p-3 text-right font-medium">
                            {formatCurrency(movement.amount)}
                          </td>
                          <td className="p-3 text-right">
                            <span className={`font-medium ${movement.balance_effect >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {movement.balance_effect >= 0 ? '+' : ''}{formatCurrency(movement.balance_effect)}
                            </span>
                          </td>
                          <td className="p-3">
                            {movement.payment_method ? (
                              <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                                {movement.payment_method === 'safe' ? 'Kasa' : 
                                 movement.payment_method === 'bank' ? 'Banka' : movement.payment_method}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="p-3 text-center">
                            {movement.type === 'sales' && movement.description && movement.description.includes('Bayi Siparişi') && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openTransactionDetail(movement)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Detay
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Kapat
          </Button>
        </div>
      </DialogContent>

      {/* İşlem Detayı Dialog - Enhanced with Order Details */}
      <Dialog open={isTransactionDetailOpen} onOpenChange={setIsTransactionDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>İşlem ve Sipariş Detayları</DialogTitle>
            <DialogDescription>
              {selectedTransaction && `${selectedTransaction.description} işlem ve sipariş detayları`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-6">
              {/* İşlem Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    İşlem Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p><strong>İşlem Türü:</strong> {selectedTransaction.type_label || 'Borç'}</p>
                      <p><strong>Tutar:</strong> {Math.abs(selectedTransaction.amount || 0).toLocaleString('tr-TR')} ₺</p>
                    </div>
                    <div>
                      <p><strong>Tarih:</strong> {selectedTransaction.date ? new Date(selectedTransaction.date).toLocaleString('tr-TR') : '-'}</p>
                      <p><strong>Hesap Türü:</strong> bayi</p>
                    </div>
                    <div>
                      <p><strong>Açıklama:</strong> {selectedTransaction.description}</p>
                      {selectedTransaction.payment_method && <p><strong>Ödeme Yöntemi:</strong> {selectedTransaction.payment_method}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ürün Detayları */}
              {(selectedTransaction.model || selectedTransaction.color || selectedTransaction.production_line) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="h-5 w-5" />
                      Ürün Detayları
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        {selectedTransaction.production_line && <p><strong>Üretim Hattı:</strong> {selectedTransaction.production_line.toUpperCase()}</p>}
                        {selectedTransaction.model && <p><strong>Model:</strong> {selectedTransaction.model}</p>}
                        {selectedTransaction.color && <p><strong>Renk:</strong> {selectedTransaction.color}</p>}
                        {selectedTransaction.door_handle && <p><strong>Kapı Kolu:</strong> {selectedTransaction.door_handle}</p>}
                      </div>
                      <div>
                        {selectedTransaction.surface && <p><strong>Yüzey:</strong> {selectedTransaction.surface}</p>}
                        {selectedTransaction.installation && <p><strong>Montaj:</strong> {selectedTransaction.installation}</p>}
                        {selectedTransaction.installation_location && <p><strong>Montaj Yeri:</strong> {selectedTransaction.installation_location}</p>}
                        {selectedTransaction.inner_frame && <p><strong>İç Pervaz:</strong> {selectedTransaction.inner_frame}</p>}
                        {selectedTransaction.outer_frame && <p><strong>Dış Pervaz:</strong> {selectedTransaction.outer_frame}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Nihai Müşteri */}
              {selectedTransaction.end_customer && (
                <Card className="bg-purple-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      🏢 Nihai Müşteri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-base">{selectedTransaction.end_customer}</p>
                  </CardContent>
                </Card>
              )}

              {/* Ölçü Bilgileri */}
              {selectedTransaction.measurements && parseMeasurements(selectedTransaction.measurements).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      Ölçü Bilgileri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border p-2 text-left">#</th>
                            <th className="border p-2 text-left">En</th>
                            <th className="border p-2 text-left">Boy</th>
                            <th className="border p-2 text-left">Kasa</th>
                            <th className="border p-2 text-left">Yönü</th>
                            <th className="border p-2 text-left">Cam</th>
                            <th className="border p-2 text-left">Oda</th>
                            <th className="border p-2 text-left">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseMeasurements(selectedTransaction.measurements).map((measurement, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border p-2 font-medium">{index + 1}</td>
                              <td className="border p-2">{measurement.width || '-'}</td>
                              <td className="border p-2">{measurement.height || '-'}</td>
                              <td className="border p-2">{measurement.thickness || measurement.frame || '-'}</td>
                              <td className="border p-2">{measurement.direction || '-'}</td>
                              <td className="border p-2">{measurement.glass || '-'}</td>
                              <td className="border p-2">{measurement.room || '-'}</td>
                              <td className="border p-2">{measurement.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ödeme Bilgileri */}
              {(selectedTransaction.payment_method || selectedTransaction.payment_description) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Ödeme Bilgileri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTransaction.payment_method && (
                        <p><strong>Ödeme Yöntemi:</strong> {selectedTransaction.payment_method === 'safe' ? 'Kasa' : selectedTransaction.payment_method === 'bank' ? 'Banka' : selectedTransaction.payment_method}</p>
                      )}
                      {selectedTransaction.payment_description && (
                        <p><strong>Ödeme Açıklaması:</strong> {selectedTransaction.payment_description}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ekstra Detaylar */}
              {selectedTransaction.details && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Ek Bilgiler
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTransaction.details}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// Account Statement Modal Component  
const AccountStatementModal = ({ isOpen, onClose, customer }) => {
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedSaleDetails, setSelectedSaleDetails] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [isSalesDetailOpen, setIsSaleDetailsOpen] = useState(false);

  useEffect(() => {
    if (isOpen && customer) {
      fetchAccountStatement();
    }
  }, [isOpen, customer, startDate, endDate]);

  const fetchAccountStatement = async () => {
    if (!customer) return;
    
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get(`${API}/customers/${customer.id}/account-statement`, { params });
      setMovements(response.data.movements || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error("Hesap hareketi yüklenirken hata:", error);
      toast.error("Hesap hareketi yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const openSaleDetails = (movement) => {
    setSelectedSaleDetails(movement);
    setIsSaleDetailsOpen(true);
  };

  const printStatement = () => {
    const esc = (v) => String(v || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <h2>Hesap Hareketleri - ${esc(customer?.name)} ${esc(customer?.surname)}</h2>
      <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
        <tr>
          <td style="border: 1px solid #ddd; padding: 6px; width: 20%; font-weight: bold; background:#f9fafb;">Hesap Kodu</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${esc(customer?.serial_number) || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 6px; width: 20%; font-weight: bold; background:#f9fafb;">Telefon</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${esc(customer?.phone) || '-'}</td>
        </tr>
        <tr>
          <td style="border: 1px solid #ddd; padding: 6px; font-weight: bold; background:#f9fafb;">Adres</td>
          <td colspan="3" style="border: 1px solid #ddd; padding: 6px;">${esc(customer?.address) || '-'}</td>
        </tr>
        ${customer?.notes && customer.notes.trim() ? `
        <tr>
          <td style="border: 1px solid #ddd; padding: 6px; font-weight: bold; background:#fefce8;">Not</td>
          <td colspan="3" style="border: 1px solid #ddd; padding: 6px; white-space: pre-wrap; background:#fefce8;">${esc(customer.notes)}</td>
        </tr>` : ''}
      </table>
      <p>Tarih Aralığı: ${startDate || 'Başlangıç'} - ${endDate || 'Günümüz'}</p>
      <br/>
      <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
        <thead>
          <tr style="background-color: #f2f2f2;">
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tarih</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Açıklama</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Borç</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Alacak</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">İşlem Sonrası Bakiye</th>
          </tr>
        </thead>
        <tbody>
          ${movements.map(movement => `
            <tr>
              <td style="border: 1px solid #ddd; padding: 8px;">${new Date(movement.date).toLocaleDateString('tr-TR')}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">${movement.description}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: #dc2626;">${movement.debit > 0 ? `₺${movement.debit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: #059669;">${movement.credit > 0 ? `₺${movement.credit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold; color: ${movement.balance >= 0 ? '#dc2626' : '#059669'};">₺${Math.abs(movement.balance).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      <br/>
      <div style="margin-top: 30px; padding: 20px; background-color: #f9fafb; border: 2px solid #e5e7eb; border-radius: 8px;">
        <h3 style="margin-top: 0; margin-bottom: 15px; color: #374151;">Özet Bilgiler</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #e5e7eb;">Toplam Borç:</td>
            <td style="padding: 8px; text-align: right; color: #dc2626; font-size: 18px; font-weight: bold; border-bottom: 1px solid #e5e7eb;">₺${summary.total_debit?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0,00'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #e5e7eb;">Toplam Alacak:</td>
            <td style="padding: 8px; text-align: right; color: #059669; font-size: 18px; font-weight: bold; border-bottom: 1px solid #e5e7eb;">₺${summary.total_credit?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0,00'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold; padding-top: 12px;">Toplam Bakiye:</td>
            <td style="padding: 8px; text-align: right; font-size: 20px; font-weight: bold; padding-top: 12px; color: ${summary.current_balance >= 0 ? '#dc2626' : '#059669'};">₺${Math.abs(summary.current_balance || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
          </tr>
        </table>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Hesap Hareketi - ${customer?.name} ${customer?.surname}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .debit { color: #dc2626; }
            .credit { color: #059669; }
            .balance { font-weight: bold; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5" />
              <span>Hesap Hareketleri - {customer?.name} {customer?.surname}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Müşteri Bilgileri */}
            {customer && (
              <div className="flex flex-wrap items-start gap-x-6 gap-y-2 p-3 border rounded-lg bg-white text-sm" data-testid="statement-customer-info">
                <span className="inline-flex items-center gap-1" data-testid="statement-customer-code">
                  <span className="text-gray-500">Hesap Kodu:</span>
                  <span className="font-mono font-bold text-blue-700">{customer.serial_number ? `#${customer.serial_number}` : '-'}</span>
                </span>
                <span className="inline-flex items-center gap-1" data-testid="statement-customer-phone">
                  <Phone className="h-4 w-4 text-gray-500" />
                  {customer.phone ? <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline">{customer.phone}</a> : '-'}
                </span>
                <span className="inline-flex items-start gap-1 min-w-0" data-testid="statement-customer-address">
                  <MapPin className="h-4 w-4 text-gray-500 shrink-0 mt-0.5" />
                  <span className="break-words">{customer.address || '-'}</span>
                </span>
                {customer.notes && customer.notes.trim() && (
                  <div className="w-full text-xs text-yellow-900 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 whitespace-pre-wrap break-words" data-testid="statement-customer-notes">
                    <span className="font-semibold">Not:</span> {customer.notes}
                  </div>
                )}
              </div>
            )}
            {/* Tarih Filtreleri */}
            <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="end_date">Bitiş Tarihi</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex flex-col justify-end">
                <Button onClick={fetchAccountStatement} disabled={loading} className="whitespace-nowrap">
                  {loading ? "Yükleniyor..." : "Filtrele"}
                </Button>
              </div>
            </div>

            {/* Özet Bilgiler */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Toplam Satış (Borç)</p>
                  <p className="text-xl font-bold text-red-600">₺{summary.total_debit?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0,00'}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Toplam Ödeme (Tahsilat + Temlik)</p>
                  <p className="text-xl font-bold text-green-600">₺{summary.total_credit?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0,00'}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Güncel Bakiye</p>
                  <p className={`text-xl font-bold ${summary.current_balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ₺{Math.abs(summary.current_balance || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}

            {/* Hesap Hareketleri Tablosu */}
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-center">
                  <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 rounded-full mx-auto mb-2"></div>
                  <p>Hesap hareketleri yükleniyor...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Hesap Hareketleri ({movements.length} kayıt)</h3>
                  <Button onClick={printStatement} variant="outline" size="sm">
                    <Printer className="h-4 w-4 mr-2" />
                    Yazdır
                  </Button>
                </div>
                
                <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                  <div className="table-container">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="p-3 text-left font-medium">Tarih</th>
                          <th className="p-3 text-left font-medium">Açıklama</th>
                          <th className="p-3 text-right font-medium">Borç</th>
                          <th className="p-3 text-right font-medium">Alacak</th>
                          <th className="p-3 text-right font-medium">Bakiye</th>
                          <th className="p-3 text-center font-medium">İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.length > 0 ? movements.map((movement, index) => (
                          <tr key={index} className="border-t hover:bg-gray-50">
                            <td className="p-3">{new Date(movement.date).toLocaleDateString('tr-TR')}</td>
                            <td className="p-3">{movement.description}</td>
                            <td className="p-3 text-right text-red-600">
                              {movement.debit > 0 ? `₺${movement.debit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}
                            </td>
                            <td className="p-3 text-right text-green-600">
                              {movement.credit > 0 ? `₺${movement.credit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}
                            </td>
                            <td className={`p-3 text-right font-bold ${movement.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              ₺{Math.abs(movement.balance).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="p-3 text-center">
                              {(movement.type === 'sale' || movement.type === 'payment') && movement.details && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openSaleDetails(movement)}
                                  className="touch-target"
                                  title="Sipariş detaylarını görüntüle"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="6" className="p-8 text-center text-gray-500">
                              Henüz hesap hareketi bulunmamaktadır.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <SaleDetailsModal 
        isOpen={isSalesDetailOpen}
        onClose={() => setIsSaleDetailsOpen(false)}
        saleDetails={selectedSaleDetails}
      />
    </>
  );
};

// Excel File Upload Component
const ExcelUploadDialog = ({ isOpen, onClose, onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/excel-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      toast.success(`Excel dosyası başarıyla yüklendi! ${response.data.imported_records} kayıt eklendi.`);
      onUploadSuccess();
    } catch (error) {
      console.error("Excel yükleme hatası:", error);
      toast.error("Excel dosyası yüklenirken hata oluştu");
      setUploadResult({ status: 'failed', error: error.response?.data?.detail || 'Bilinmeyen hata' });
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false,
    disabled: uploading
  });

  const resetDialog = () => {
    setUploadResult(null);
    setUploading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excel Dosyası Yükle</DialogTitle>
          <DialogDescription>
            Ölçü defteri Excel dosyanızı yükleyerek toplu işlem ekleyebilirsiniz.
          </DialogDescription>
        </DialogHeader>
        
        {!uploadResult ? (
          <div className="space-y-4">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              {uploading ? (
                <p className="text-lg">Dosya yükleniyor...</p>
              ) : isDragActive ? (
                <p className="text-lg">Dosyayı buraya bırakın</p>
              ) : (
                <>
                  <p className="text-lg font-semibold mb-2">Excel dosyasını buraya sürükleyin</p>
                  <p className="text-sm text-gray-500">veya tıklayarak dosya seçin</p>
                  <p className="text-xs text-gray-400 mt-2">Sadece .xlsx ve .xls dosyaları kabul edilir</p>
                </>
              )}
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Excel Dosyası Formatı:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>MÜŞTERİ:</strong> Müşteri adı (zorunlu)</li>
                <li>• <strong>TELEFON:</strong> Telefon numarası</li>
                <li>• <strong>ADRES:</strong> Adres bilgisi</li>
                <li>• <strong>TOPLAM TUTAR:</strong> İşlem tutarı</li>
                <li>• <strong>KAPARO:</strong> Ön ödeme tutarı (opsiyonel)</li>
                <li>• <strong>MODEL, RENK, EN, BOY:</strong> Ürün detayları</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg flex items-center space-x-3 ${
              uploadResult.status === 'completed' ? 'bg-green-50' :
              uploadResult.status === 'partial' ? 'bg-yellow-50' :
              'bg-red-50'
            }`}>
              {uploadResult.status === 'completed' ? (
                <CheckCircle className="h-8 w-8 text-green-600" />
              ) : uploadResult.status === 'partial' ? (
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              ) : (
                <XCircle className="h-8 w-8 text-red-600" />
              )}
              <div>
                <h4 className={`font-medium ${
                  uploadResult.status === 'completed' ? 'text-green-900' :
                  uploadResult.status === 'partial' ? 'text-yellow-900' :
                  'text-red-900'
                }`}>
                  {uploadResult.status === 'completed' ? 'Başarıyla Tamamlandı' :
                   uploadResult.status === 'partial' ? 'Kısmen Tamamlandı' :
                   'Yükleme Başarısız'}
                </h4>
                <p className={`text-sm ${
                  uploadResult.status === 'completed' ? 'text-green-700' :
                  uploadResult.status === 'partial' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  {uploadResult.error || `${uploadResult.imported_records}/${uploadResult.total_records} kayıt işlendi`}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-gray-900">{uploadResult.total_records || 0}</div>
                <div className="text-sm text-gray-600">Toplam Kayıt</div>
              </div>
              <div className="p-3 bg-green-50 rounded">
                <div className="text-2xl font-bold text-green-600">{uploadResult.imported_records || 0}</div>
                <div className="text-sm text-green-600">Başarılı</div>
              </div>
              <div className="p-3 bg-red-50 rounded">
                <div className="text-2xl font-bold text-red-600">{uploadResult.failed_records || 0}</div>
                <div className="text-sm text-red-600">Başarısız</div>
              </div>
            </div>
            
            <Button onClick={resetDialog} className="w-full">
              Tamam
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Sales Component (Müşteri Satışları) - Optimized with React.memo
const ProductOptionsDialog = ({ open, onOpenChange, sessionToken, currentUser, onSaved }) => {
  const [options, setOptions] = useState(DEFAULT_PRODUCT_OPTIONS_JS);
  const [activeKey, setActiveKey] = useState("models");
  const [newItem, setNewItem] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/product-options`);
        setOptions({ ...DEFAULT_PRODUCT_OPTIONS_JS, ...(res.data || {}) });
      } catch (e) {
        toast.error("Listeler yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const activeMeta = PRODUCT_OPTIONS_META.find(m => m.key === activeKey) || PRODUCT_OPTIONS_META[0];
  const activeItems = options[activeKey] || [];

  const addItem = () => {
    const raw = newItem.trim();
    if (!raw) return;
    let value = raw;
    if (activeMeta.numeric) {
      const num = parseInt(raw, 10);
      if (Number.isNaN(num)) { toast.error("Lütfen bir sayı girin"); return; }
      value = num;
      if (activeItems.includes(num)) { toast.error("Bu değer zaten mevcut"); return; }
    } else {
      const upper = raw.toUpperCase();
      if (activeItems.some(x => String(x).toUpperCase() === upper)) { toast.error("Bu değer zaten mevcut"); return; }
    }
    setOptions(prev => ({ ...prev, [activeKey]: [...(prev[activeKey] || []), value] }));
    setNewItem("");
  };

  const removeItem = (idx) => {
    setOptions(prev => ({ ...prev, [activeKey]: (prev[activeKey] || []).filter((_, i) => i !== idx) }));
  };

  const moveItem = (idx, dir) => {
    setOptions(prev => {
      const arr = [...(prev[activeKey] || [])];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return prev;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...prev, [activeKey]: arr };
    });
  };

  const handleSave = async () => {
    if (currentUser?.role !== "admin") {
      toast.error("Bu ayarları sadece yönetici düzenleyebilir");
      return;
    }
    try {
      setSaving(true);
      const res = await axios.put(
        `${API}/product-options`,
        options,
        { params: { session_token: sessionToken } }
      );
      setOptions({ ...DEFAULT_PRODUCT_OPTIONS_JS, ...(res.data || {}) });
      toast.success("Listeler güncellendi");
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="product-options-dialog">
        <DialogHeader>
          <DialogTitle>Ürün Özellikleri ve Ölçü Listeleri</DialogTitle>
          <DialogDescription>
            Yeni Satış İşlemi'ndeki ürün özellikleri (Model, Renk, Kapı Kolu, Yüzey, Montaj vb.) ile ölçü tablosundaki listeleri (En, Boy, Kasa, Yön, Cam, Oda) buradan düzenleyin.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-4 flex-1 overflow-hidden">
          <div className="col-span-1 border rounded-md p-2 overflow-y-auto max-h-[55vh]" data-testid="product-options-tabs">
            {PRODUCT_OPTIONS_META.map(meta => (
              <button
                key={meta.key}
                type="button"
                onClick={() => { setActiveKey(meta.key); setNewItem(""); }}
                className={`w-full text-left text-sm px-3 py-2 rounded transition ${activeKey === meta.key ? 'bg-blue-100 text-blue-900 font-semibold' : 'hover:bg-gray-100'}`}
                data-testid={`product-options-tab-${meta.key}`}
              >
                {meta.label}
                <span className="ml-2 text-xs text-gray-500">({(options[meta.key] || []).length})</span>
              </button>
            ))}
          </div>

          <div className="col-span-3 flex flex-col overflow-hidden">
            <div className="flex items-center gap-2 mb-3">
              <Input
                placeholder={activeMeta.numeric ? "Yeni sayı ekle" : "Yeni değer ekle"}
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                type={activeMeta.numeric ? "number" : "text"}
                data-testid="product-options-new-input"
              />
              <Button type="button" onClick={addItem} data-testid="product-options-add-btn">
                <Plus className="h-4 w-4 mr-1" /> Ekle
              </Button>
            </div>

            <div className="border rounded-md flex-1 overflow-y-auto max-h-[45vh]" data-testid="product-options-list">
              {loading ? (
                <div className="p-6 text-center text-gray-500">Yükleniyor...</div>
              ) : activeItems.length === 0 ? (
                <div className="p-6 text-center text-gray-500">Bu listede henüz kayıt yok. Yukarıdan yeni ekleyebilirsiniz.</div>
              ) : (
                <ul className="divide-y">
                  {activeItems.map((item, idx) => (
                    <li key={`${activeKey}-${idx}-${item}`} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50" data-testid={`product-options-item-${activeKey}-${idx}`}>
                      <span className="text-sm">
                        <span className="text-gray-400 mr-2 tabular-nums">{idx + 1}.</span>
                        <span className="font-medium">{String(item)}</span>
                      </span>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="h-7 w-7 p-0" title="Yukarı">▲</Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => moveItem(idx, +1)} disabled={idx === activeItems.length - 1} className="h-7 w-7 p-0" title="Aşağı">▼</Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" title="Sil" data-testid={`product-options-remove-${activeKey}-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="product-options-cancel">
            İptal
          </Button>
          <Button onClick={handleSave} disabled={saving || currentUser?.role !== 'admin'} data-testid="product-options-save">
            {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kaydediliyor...</>) : (<><Save className="h-4 w-4 mr-2" /> Kaydet</>)}
          </Button>
        </DialogFooter>
        {currentUser?.role !== 'admin' && (
          <p className="text-xs text-red-600 mt-2 text-right">Bu listeleri sadece yönetici düzenleyebilir.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};


const Sales = React.memo(({ sessionToken, currentUser }) => {
  const salesLoadedRef = useRef(false);
  const salesReqRef = useRef(0);
  // === STATE MANAGEMENT ===
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [salesCurrentPage, setSalesCurrentPage] = useState(1);
  const [totalSalesTransactions, setTotalSalesTransactions] = useState(0);
  const salesPerPage = 50;
  
  // Dialog states
  const [isNewSaleDialogOpen, setIsNewSaleDialogOpen] = useState(false);
  const [isOtherSaleDialogOpen, setIsOtherSaleDialogOpen] = useState(false);  // Diğer Satış Dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isNewCustomerDialogOpen, setIsNewCustomerDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  
  // Current transaction being edited/deleted/viewed
  const [currentTransaction, setCurrentTransaction] = useState(null);
  
  // Diğer Satış Form States
  const [otherSaleCustomer, setOtherSaleCustomer] = useState(null);
  const [otherSaleItems, setOtherSaleItems] = useState([{ name: '', quantity: 1, unit_price: 0, total: 0 }]);
  const [otherSaleDescription, setOtherSaleDescription] = useState('');
  
  // Legacy compatibility states
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // Sipariş Detayı Dialog states (üretim sayfasıyla aynı)
  const [showSaleDetailModal, setShowSaleDetailModal] = useState(false);
  const [loadingSaleDetail, setLoadingSaleDetail] = useState(false);
  const [saleDetailProduction, setSaleDetailProduction] = useState(null);
  const [saleDetailTransaction, setSaleDetailTransaction] = useState(null);
  const [saleDetailCustomer, setSaleDetailCustomer] = useState(null);
  
  // Üretim Sipariş Detayı Dialog states (üretim sayfasındakiyle birebir aynı modal)
  const [isSalesProdDetailOpen, setIsSalesProdDetailOpen] = useState(false);
  const [isSalesProdDetailLoading, setIsSalesProdDetailLoading] = useState(false);
  const [salesProdDetailProduction, setSalesProdDetailProduction] = useState(null);
  const [salesProdDetailTransaction, setSalesProdDetailTransaction] = useState(null);
  const [salesProdDetailCustomer, setSalesProdDetailCustomer] = useState(null);
  
  // Üretime aktarılan transaction id'leri (Set)
  const [productionTransactionIds, setProductionTransactionIds] = useState(new Set());
  
  // Custom dropdown states
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [isCustomColor, setIsCustomColor] = useState(false);
  const [isCustomDoorHandle, setIsCustomDoorHandle] = useState(false);
  const [isCustomSurface, setIsCustomSurface] = useState(false);
  const [isCustomInstallation, setIsCustomInstallation] = useState(false);
  const [isCustomInstallationLocation, setIsCustomInstallationLocation] = useState(false);
  const [isCustomInnerFrame, setIsCustomInnerFrame] = useState(false);
  const [isCustomOuterFrame, setIsCustomOuterFrame] = useState(false);
  
  // Customer search with debouncing
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Sales personnel state
  const [salesPersonnel, setSalesPersonnel] = useState([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState("");
  
  // Debounce customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(customerSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchTerm]);
  
  // Filter customers based on debounced search term - Optimized
  const filteredCustomers = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return customers.slice(0, 20); // Show only first 20
    return customers.filter(customer => 
      `${customer.name} ${customer.surname} ${customer.serial_number}`.toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase())
    ).slice(0, 20); // Limit results to 20
  }, [customers, debouncedSearchTerm]);
  const [selectKey, setSelectKey] = useState(0);
  
  // Helper function to parse measurements JSON
  const parseMeasurements = (measurementsJson) => {
    try {
      return JSON.parse(measurementsJson || '[]');
    } catch {
      return [];
    }
  };
  
  // Form data for new sale
  const [newSaleForm, setNewSaleForm] = useState({
    customer_id: "",
    transaction_type: "debit",
    amount: "",
    description: "",
    model: "",
    color: "",
    door_handle: "",
    surface: "",
    installation: "",
    installation_location: "",
    inner_frame: "",
    outer_frame: "",
    measurements: "",
    details: "",
    production_line: "lake",
    payment_method: "kasa",
    payment_description: "",
    target_installation_date: null, // Manuel belirlenecek
    sales_person_name: "" // Satış personeli adı (serbest metin)
  });
  
  // Form güvenliği - Kaydedilmeyen değişiklikleri takip et
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  
  // Çift kayıt önleme ve hız iyileştirmesi
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form değişikliklerini izle
  useEffect(() => {
    const hasChanges = JSON.stringify(newSaleForm) !== JSON.stringify(originalFormData);
    setHasUnsavedChanges(hasChanges && isNewSaleDialogOpen);
  }, [newSaleForm, originalFormData, isNewSaleDialogOpen]);
  
  // Sayfa kapatma/yenileme uyarısı
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Kaydedilmemiş değişiklikleriniz var. Sayfayı terk etmek istediğinizden emin misiniz?';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  
  // Güvenli dialog kapatma fonksiyonu
  const closeNewSaleDialog = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm("⚠️ KAYDEDİLMEMİŞ DEĞİŞİKLİKLER\n\nFormda kaydedilmemiş değişiklikleriniz var. Bu değişiklikler kaybolacak.\n\nFormu kapatmak istediğinizden emin misiniz?\n\n• Kaydetmek için: İptal'e basıp formu kaydedin\n• Çıkmak için: Tamam'a basın (değişiklikler kaybolacak)");
      if (!confirmed) {
        return; // Kullanıcı iptal etti, dialog açık kalsın
      }
    }
    
    // Dialog'u kapat ve form'u temizle
    setIsNewSaleDialogOpen(false);
    setHasUnsavedChanges(false);
    setOriginalFormData({});
    
    // Form'u varsayılan değerlere sıfırla
    setNewSaleForm({
      customer_id: "",
      transaction_type: "debit",
      amount: "",
      description: "",
      model: "",
      color: "",
      door_handle: "",
      surface: "",
      installation: "",
      installation_location: "",
      inner_frame: "",
      outer_frame: "",
      measurements: "",
      details: "",
      production_line: "lake",
      payment_method: "kasa",
      payment_description: "",
      target_installation_date: new Date() // Hedef montaj tarihi
    });
  }, [hasUnsavedChanges]);
  
  // Dialog açma fonksiyonu
  const openNewSaleDialog = useCallback(() => {
    setIsNewSaleDialogOpen(true);
    setOriginalFormData({...newSaleForm});
    setHasUnsavedChanges(false);
  }, [newSaleForm]);
  
  // New customer form
  const [newCustomerForm, setNewCustomerForm] = useState({
    serial_number: "",
    name: "",
    surname: "",
    phone: "",
    address: ""
  });
  
  // Legacy compatibility
  const [newCustomerData, setNewCustomerData] = useState({
    serial_number: "",
    name: "",
    surname: "",
    phone: "",
    address: ""
  });
  
  // Measurements table - Optimized with fewer initial rows
  const [measurements, setMeasurements] = useState([
    { id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }
  ]);
  
  // Adet sütunu aktif/pasif state
  const [showQuantityColumn, setShowQuantityColumn] = useState(false);
  
  // Optimized measurements state with minimal initial rows
  const [measurementRows, setMeasurementRows] = useState(() => {
    const initialRows = [];
    for (let i = 1; i <= 3; i++) { // Reduced from 20 to 3
      initialRows.push({
        id: i,
        width: "",
        height: "",
        frame: "",
        direction: "",
        glass: "",
        room: "",
        description: "",
        quantity: 1
      });
    }
    return initialRows;
  });

  // Legacy form data state (for compatibility)
  const [formData, setFormData] = useState({
    account_id: "",
    transaction_type: "debit",
    amount: "",
    description: "",
    model: "",
    color: "",
    door_handle: "",
    surface: "",
    installation: "",
    installation_location: "",
    inner_frame: "",
    outer_frame: "",
    measurements: "",
    details: "",
    production_line: "lake",
    payment_method: "kasa",
    payment_description: "",
    target_installation_date: null, // Manuel belirlenecek
    sales_person_name: "", // Satış personeli adı (serbest metin)
    end_customer: "" // Nihai müşteri
  });

  // === UTILITY FUNCTIONS ===
  const formatAmount = (value) => {
    if (!value && value !== 0) return "";
    const numericValue = String(value).replace(/[^\d]/g, "");
    if (!numericValue) return "";
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const parseAmount = (formattedValue) => {
    if (!formattedValue && formattedValue !== 0) return "";
    const stringValue = String(formattedValue);
    return stringValue.replace(/[.,]/g, "");
  };
  
  // === DROPDOWN OPTIONS - Optimized with useMemo ===
  // Server-side product options (backend'den fetch edilir, ProductOptionsDialog ile düzenlenir)
  const [productOptions, setProductOptions] = useState(DEFAULT_PRODUCT_OPTIONS_JS);
  const [isProductOptionsOpen, setIsProductOptionsOpen] = useState(false);
  const fetchProductOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/product-options`);
      setProductOptions(prev => ({ ...prev, ...(res.data || {}) }));
    } catch (e) {
      console.error("Ürün özellikleri yüklenemedi:", e);
    }
  }, []);
  useEffect(() => { fetchProductOptions(); }, [fetchProductOptions]);

  const modelOptions = productOptions.models || [];
  const colorOptions = productOptions.colors || [];
  const doorHandleOptions = productOptions.door_handles || [];
  const surfaceOptions = productOptions.surfaces || [];
  const installationOptions = productOptions.installations || [];
  const installationLocationOptions = productOptions.installation_locations || [];
  const innerFrameOptions = productOptions.inner_frames || [];
  const outerFrameOptions = productOptions.outer_frames || [];
  const widthOptions = productOptions.widths || [];
  const heightOptions = productOptions.heights || [];
  const frameOptions = productOptions.frames || [];
  const directionOptions = productOptions.directions || [];
  const glassOptions = productOptions.glasses || [];
  const roomOptions = productOptions.rooms || [];
  
  
  
  // === HELPER FUNCTIONS ===
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.name} ${customer.surname}` : 'Bilinmeyen Müşteri';
  };
  
  const getCustomerSerial = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.serial_number : 'N/A';
  };

  const getSalesPersonName = (salesPersonId) => {
    if (!salesPersonId) return 'Atanmamış';
    const person = salesPersonnel.find(p => p.id === salesPersonId);
    return person ? person.name : 'Bilinmeyen';
  };

  useEffect(() => {
    fetchTransactions(1, "");
    fetchCustomers();
    fetchSalesPersonnel();
    fetchProductionTransactionIds();
  }, []);

  // Search debounce → sayfa 1'e sıfırla ve yeniden çek
  useEffect(() => {
    const t = setTimeout(() => {
      fetchTransactions(1, searchTerm);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchProductionTransactionIds = async () => {
    try {
      const response = await axios.get(`${API}/productions`);
      const ids = new Set((response.data || []).map(p => p.transaction_id).filter(Boolean));
      setProductionTransactionIds(ids);
    } catch (error) {
      console.error("Üretim listesi yüklenirken hata:", error);
    }
  };

  const fetchTransactions = async (page = 1, q = "") => {
    const reqId = ++salesReqRef.current;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/transactions`, {
        params: {
          account_type: "customer",
          limit: salesPerPage,
          skip: (page - 1) * salesPerPage,
          q: (q || "").trim(),
        }
      });
      if (reqId !== salesReqRef.current) return;
      salesLoadedRef.current = true;
      const data = response.data || {};
      const customerTransactions = data.transactions || [];
      setTransactions(Array.isArray(customerTransactions) ? customerTransactions : []);
      setTotalSalesTransactions(data.total ?? customerTransactions.length);
      setSalesCurrentPage(page);
      setLoading(false);
    } catch (error) {
      console.error("Satış işlemleri yüklenirken hata:", error);
      toast.error("Satış işlemleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers?limit=2000`);
      // Backend returns {customers, total, limit, skip, has_more}
      const customersData = response.data.customers || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error("Müşteriler yüklenirken hata:", error);
    }
  };
  const fetchSalesPersonnel = async () => {
    try {
      const response = await axios.get(`${API}/sales-personnel`);
      setSalesPersonnel(response.data);
    } catch (error) {
      console.error("Satış personelleri yüklenirken hata:", error);
      toast.error("Satış personelleri yüklenirken hata oluştu");
    }
  };

  const resetForm = useCallback(() => {
    setFormData({
      account_id: "",
      transaction_type: "debit",
      amount: "",
      description: "",
      model: "",
      color: "",
      door_handle: "",
      surface: "",
      installation: "",
      installation_location: "",
      inner_frame: "",
      outer_frame: "",
      measurements: "",
      details: "",
      payment_method: "kasa",
      payment_description: "",
      production_line: "lake",
      target_installation_date: new Date() // Hedef montaj tarihi
    });
    // Custom seçenekleri de sıfırla
    setIsCustomModel(false);
    setIsCustomColor(false);
    setIsCustomDoorHandle(false);
    setIsCustomSurface(false);
    setIsCustomInstallation(false);
    setIsCustomInstallationLocation(false);
    setIsCustomInnerFrame(false);
    setIsCustomOuterFrame(false);
    setCustomerSearchTerm("");
    setShowCustomerDropdown(false);
    setMeasurements(() => {
      const initialRows = [];
      for (let i = 1; i <= 3; i++) { // Reduced from 20 to 3
        initialRows.push({
          id: i,
          width: "",
          height: "",
          frame: "",
          direction: "",
          glass: "",
          room: "",
          description: ""
        });
      }
      return initialRows;
    });
    setMeasurementRows(() => {
      const initialRows = [];
      for (let i = 1; i <= 3; i++) { // Reduced from 20 to 3
        initialRows.push({
          id: i,
          width: "",
          height: "",
          frame: "",
          direction: "",
          glass: "",
          room: "",
          description: ""
        });
      }
      return initialRows;
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Çift kayıt önleme
    if (isSubmitting) return;
    
    // Satış personeli validasyonu
    if (!formData.sales_person_name || !formData.sales_person_name.trim()) {
      toast.error("Lütfen satış personeli adını girin");
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Measurements verisini JSON string'e çevir
      const measurementsJson = JSON.stringify(measurements.filter(m => 
        m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description
      ));

      await axios.post(`${API}/transactions`, {
        ...formData,
        account_type: "customer",
        amount: parseFloat(formData.amount),
        measurements: measurementsJson,
        target_installation_date: formData.target_installation_date ? formData.target_installation_date.toISOString() : null
      });
      toast.success("Satış işlemi eklendi");
      setIsNewSaleDialogOpen(false);
      setFormData({
        account_id: "",
        transaction_type: "debit",
        amount: "",
        description: "",
        model: "",
        color: "",
        door_handle: "",
        surface: "",
        installation: "",
        installation_location: "",
        inner_frame: "",
        outer_frame: "",
        measurements: "",
        details: "",
        production_line: "lake",  // varsayılan değer
        sales_person_name: "", // Satış personeli sıfırla
        end_customer: "" // Nihai müşteri sıfırla
      });
      // Measurements tablosunu da sıfırla
      const resetRows = [];
      for (let i = 1; i <= 3; i++) { // Reduced from 20 to 3
        resetRows.push({
          id: i,
          width: "",
          height: "",
          frame: "",
          direction: "",
          glass: "",
          room: "",
          description: ""
        });
      }
      setMeasurements(resetRows);
      fetchTransactions(1, searchTerm);
      fetchCustomers();
      fetchSalesPersonnel();
    } catch (error) {
      console.error("Satış işlemi eklenirken hata:", error);
      toast.error("Satış işlemi eklenirken hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/customers`, newCustomerData);
      const newCustomerId = response.data.id;
      
      toast.success(`Yeni müşteri eklendi: ${newCustomerData.name} ${newCustomerData.surname}`);
      setIsNewCustomerDialogOpen(false);
      setNewCustomerData({
        serial_number: "",
        name: "",
        surname: "",
        phone: "",
        address: ""
      });
      
      // Yeni müşteriyi customers listesine manuel ekle (hızlı güncelleme için)
      const newCustomer = response.data;
      setCustomers(prevCustomers => [...prevCustomers, newCustomer]);
      
      // Müşteri listesini de yenile (güvenlik için)
      fetchCustomers();
      
      // Select component'ini yeniden render etmek için key'i güncelle
      setSelectKey(prev => prev + 1);
      
      // Yeni müşteriyi otomatik seç
      setFormData(prevFormData => ({...prevFormData, account_id: newCustomerId}));
      
      // Customer search term'i de güncelle
      setCustomerSearchTerm(`${newCustomer.serial_number} - ${newCustomer.name} ${newCustomer.surname}`);
      
      // Ek bilgilendirme
      toast.success("Müşteri otomatik olarak seçildi");
    } catch (error) {
      console.error("Müşteri eklenirken hata:", error);
      toast.error("Müşteri eklenirken hata oluştu");
    }
  };

  const openNewCustomerDialog = () => {
    setIsNewCustomerDialogOpen(true);
  };

  const handleResendToProduction = async (transaction) => {
    if (!confirm(`"${getAccountName(transaction.account_id)}" siparişini yeniden üretime göndermek istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/transactions/${transaction.id}/create-production`, {}, {
        params: { session_token: sessionToken }
      });
      toast.success("Sipariş üretime gönderildi");
      fetchTransactions(salesCurrentPage, searchTerm);
      fetchProductionTransactionIds();
    } catch (error) {
      console.error("Üretime gönderme hatası:", error);
      toast.error(error.response?.data?.detail || "Üretime gönderme başarısız");
    }
  };

  const openSaleDetails = async (transaction) => {
    try {
      setShowSaleDetailModal(true);
      setLoadingSaleDetail(true);
      setSaleDetailTransaction(transaction);
      // Production-like obje oluştur (üretim sayfasındaki selectedProduction ile aynı yapı)
      setSaleDetailProduction({
        customer_name: getAccountName(transaction.account_id),
        customer_id: transaction.account_id,
        production_line: transaction.production_line,
        status: transaction.status || '-',
        target_installation_date: transaction.target_installation_date,
        notes: transaction.notes || transaction.description || ''
      });
      // Müşteri detaylarını getir
      const customerRes = await axios.get(`${API}/customers/${transaction.account_id}?session_token=${sessionToken}`);
      setSaleDetailCustomer(customerRes.data);
      setLoadingSaleDetail(false);
    } catch (error) {
      console.error("Sipariş detayları yüklenirken hata:", error);
      toast.error("Sipariş detayları yüklenemedi");
      setLoadingSaleDetail(false);
    }
  };

  const handleSaleDetailPrint = () => {
    if (!saleDetailTransaction || !saleDetailProduction) {
      toast.error('Sipariş verisi yüklenmedi');
      return;
    }
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      let y = 12;

      // Başlık
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('SIPARIS DETAYLARI', pageWidth / 2, y, { align: 'center' });
      y += 6;
      pdf.setFontSize(10);
      pdf.text(saleDetailProduction.customer_name || '-', pageWidth / 2, y, { align: 'center' });
      y += 6;

      // Musteri Bilgileri
      pdf.setFillColor(219, 234, 254);
      pdf.rect(8, y, pageWidth - 16, 22, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('MUSTERI BILGILERI', 10, y + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.text(`Musteri: ${saleDetailProduction.customer_name || '-'}`, 10, y + 11);
      pdf.text(`Cari Kod: ${saleDetailCustomer?.serial_number || 'N/A'}`, 80, y + 11);
      pdf.text(`Telefon: ${saleDetailCustomer?.phone || 'N/A'}`, 130, y + 11);
      pdf.text(`Uretim Hatti: ${(saleDetailProduction.production_line || '').toUpperCase()}`, 10, y + 17);
      pdf.text(`Adres: ${saleDetailCustomer?.address || 'N/A'}`, 80, y + 17);
      y += 26;

      // Siparis Bilgileri
      pdf.setFillColor(220, 252, 231);
      pdf.rect(8, y, pageWidth - 16, 16, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('SIPARIS BILGILERI', 10, y + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.text(`Tarih: ${new Date(saleDetailTransaction.created_at).toLocaleDateString('tr-TR')}`, 10, y + 11);
      pdf.text(`Tutar: TL ${(saleDetailTransaction.amount || 0).toLocaleString('tr-TR')}`, 80, y + 11);
      const hedef = saleDetailProduction.target_installation_date
        ? new Date(saleDetailProduction.target_installation_date).toLocaleDateString('tr-TR')
        : 'Belirlenmemis';
      pdf.text(`Hedef Montaj: ${hedef}`, 130, y + 11);
      y += 20;

      // Urun Ozellikleri
      const props = [
        ['Model', saleDetailTransaction.model],
        ['Renk', saleDetailTransaction.color],
        ['Kapi Kolu', saleDetailTransaction.door_handle],
        ['Yuzey', saleDetailTransaction.surface],
        ['Montaj', saleDetailTransaction.installation],
        ['Montaj Yeri', saleDetailTransaction.installation_location],
        ['Ic Pervaz', saleDetailTransaction.inner_frame],
        ['Dis Pervaz', saleDetailTransaction.outer_frame],
      ].filter(([, v]) => v);
      const propRows = Math.ceil(props.length / 4);
      const propBlockHeight = 6 + propRows * 5 + 3;
      pdf.setFillColor(254, 249, 195);
      pdf.rect(8, y, pageWidth - 16, propBlockHeight, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('URUN OZELLIKLERI', 10, y + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      props.forEach(([label, value], i) => {
        const col = i % 4;
        const row = Math.floor(i / 4);
        const x = 10 + col * 48;
        const yy = y + 11 + row * 5;
        pdf.text(`${label}: ${value}`, x, yy);
      });
      y += propBlockHeight + 4;

      // Nihai Musteri
      if (saleDetailTransaction.end_customer) {
        pdf.setFillColor(243, 232, 255);
        pdf.rect(8, y, pageWidth - 16, 10, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text('NIHAI MUSTERI:', 10, y + 6);
        pdf.setFont('helvetica', 'normal');
        pdf.text(saleDetailTransaction.end_customer, 45, y + 6);
        y += 14;
      }

      // Olcu Tablolari
      let measurements = [];
      try {
        measurements = JSON.parse(saleDetailTransaction.measurements || '[]');
      } catch (e) {}
      const valid = measurements.filter(m => m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description);

      if (valid.length > 0) {
        // Orijinal Olculer
        autoTable(pdf, {
          startY: y,
          head: [['Sira', 'En', 'Boy', 'Kasa', 'Yon', 'Cam', 'Oda', 'Aciklama']],
          body: valid.map((m, i) => [
            `${i + 1}${m.quantity && m.quantity > 1 ? ` x${m.quantity}` : ''}`,
            m.width || '-',
            m.height || '-',
            m.frame || '-',
            m.direction || '-',
            m.glass || '-',
            m.room || '-',
            m.description || '-',
          ]),
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 1.5 },
          headStyles: { fillColor: [219, 234, 254], textColor: 0, fontStyle: 'bold' },
          margin: { left: 8, right: 8 },
          didDrawPage: () => {},
        });
        y = pdf.lastAutoTable.finalY + 3;

        // Kanat Olcusu
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(185, 28, 28);
        pdf.text('KANAT OLCUSU', 10, y + 4);
        pdf.setTextColor(0, 0, 0);
        y += 6;
        autoTable(pdf, {
          startY: y,
          head: [['Sira', 'En', 'Boy', 'Yon', 'Cam', 'Aciklama']],
          body: valid.map((m, i) => {
            const ow = parseFloat(m.width) || 0;
            const oh = parseFloat(m.height) || 0;
            const pw = ow > 6 ? ow - 6 : ow;
            const ph = oh > 4 ? oh - 4 : oh;
            return [
              `${i + 1}${m.quantity && m.quantity > 1 ? ` x${m.quantity}` : ''}`,
              pw > 0 ? String(pw) : '-',
              ph > 0 ? String(ph) : '-',
              m.direction || '-',
              m.glass || '-',
              m.description || '-',
            ];
          }),
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 1.5 },
          headStyles: { fillColor: [254, 226, 226], textColor: 0, fontStyle: 'bold' },
          bodyStyles: { textColor: [185, 28, 28] },
          columnStyles: { 3: { textColor: 0 }, 4: { textColor: 0 }, 5: { textColor: 0 } },
          margin: { left: 8, right: 8 },
        });
        y = pdf.lastAutoTable.finalY + 4;
      }

      // Notlar
      const notes = saleDetailTransaction.description || saleDetailProduction.notes;
      if (notes) {
        pdf.setFillColor(255, 237, 213);
        const lines = pdf.splitTextToSize(notes, pageWidth - 20);
        const noteH = 8 + lines.length * 4;
        pdf.rect(8, y, pageWidth - 16, noteH, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text('IMALAT NOTLARI', 10, y + 5);
        pdf.setFont('helvetica', 'normal');
        pdf.text(lines, 10, y + 10);
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => { printWindow.print(); };
      } else {
        toast.error('Pop-up engellendi. Lütfen yeni pencerelere izin verin.');
      }
    } catch (error) {
      console.error('PDF olusturma hatasi:', error);
      toast.error('PDF olusturulamadi');
    }
  };

  // Üretim Sipariş Detayı - Üretim sayfasındakiyle birebir aynı modal
  const openSalesProductionDetail = async (transaction) => {
    try {
      setIsSalesProdDetailOpen(true);
      setIsSalesProdDetailLoading(true);
      setSalesProdDetailProduction(null);
      setSalesProdDetailTransaction(null);
      setSalesProdDetailCustomer(null);

      // İlgili üretim kaydını bul
      const prodResp = await axios.get(`${API}/productions`);
      const production = (prodResp.data || []).find(p => p.transaction_id === transaction.id);
      if (!production) {
        toast.error('Bu sipariş için üretim kaydı bulunamadı');
        setIsSalesProdDetailOpen(false);
        setIsSalesProdDetailLoading(false);
        return;
      }
      setSalesProdDetailProduction(production);

      // Transaction detayları
      const transactionResponse = await axios.get(`${API}/transactions/${production.transaction_id}`, {
        params: { session_token: sessionToken }
      });
      setSalesProdDetailTransaction(transactionResponse.data);

      // Müşteri detayları
      const customerResponse = await axios.get(`${API}/customers/${production.customer_id}`);
      setSalesProdDetailCustomer(customerResponse.data);

      setIsSalesProdDetailLoading(false);
    } catch (error) {
      console.error("Üretim sipariş detayları yüklenirken hata:", error);
      toast.error("Üretim sipariş detayları yüklenirken hata oluştu");
      setIsSalesProdDetailLoading(false);
    }
  };

  // Ortak: Modal'ı tek sayfa A4 PDF'e çevir (auto-fit, en/boy oranı korunur)
  const generateSalesProdDetailPdf = async () => {
    const printArea = document.getElementById('sales-uretim-print-area');
    if (!printArea) {
      toast.error('Yazdırma alanı bulunamadı');
      return null;
    }
    const existingClone = document.getElementById('sales-uretim-pdf-clone');
    if (existingClone) existingClone.remove();
    const clone = printArea.cloneNode(true);
    clone.id = 'sales-uretim-pdf-clone';
    clone.style.position = 'fixed';
    clone.style.left = '-99999px';
    clone.style.top = '0';
    clone.style.width = '1100px';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.background = '#ffffff';
    clone.style.zIndex = '-1';
    document.body.appendChild(clone);
    await new Promise(resolve => setTimeout(resolve, 50));
    try {
      const canvas = await html2canvas(clone, {
        scale: 1.2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1100,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const availW = pdfWidth - margin * 2;
      const availH = pdfHeight - margin * 2;
      // Auto-fit: tek sayfaya sığdır, en/boy oranını koru
      const ratio = Math.min(availW / canvas.width, availH / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      const x = (pdfWidth - w) / 2;
      const y = margin;
      pdf.addImage(imgData, 'JPEG', x, y, w, h);
      return pdf;
    } finally {
      const cloneEl = document.getElementById('sales-uretim-pdf-clone');
      if (cloneEl) cloneEl.remove();
    }
  };

  // Veri tabanlı tek sayfa PDF üretici (instant, sayfayı kilitlemez)
  const generateSalesProdDetailPdfFast = () => {
    // jsPDF helvetica fontu Türkçe Ş/İ/Ğ render edemiyor - ASCII'ye çevir
    const tr = (s) => String(s ?? '')
      .replace(/Ğ/g, 'G').replace(/ğ/g, 'g')
      .replace(/Ş/g, 'S').replace(/ş/g, 's')
      .replace(/İ/g, 'I').replace(/ı/g, 'i')
      .replace(/Ç/g, 'C').replace(/ç/g, 'c')
      .replace(/Ö/g, 'O').replace(/ö/g, 'o')
      .replace(/Ü/g, 'U').replace(/ü/g, 'u');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 6;
    const contentW = pageWidth - margin * 2;
    let y = margin;

    // Müşteri Bilgileri (mavi)
    pdf.setFillColor(219, 234, 254);
    pdf.rect(margin, y, contentW, 26, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('MUSTERI BILGILERI', margin + 2, y + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(tr(`Musteri: ${salesProdDetailProduction?.customer_name || '-'}`), margin + 2, y + 13);
    pdf.text(tr(`Cari Kod: ${salesProdDetailCustomer?.serial_number || 'N/A'}`), margin + 80, y + 13);
    pdf.text(tr(`Telefon: ${salesProdDetailCustomer?.phone || 'N/A'}`), margin + 130, y + 13);
    pdf.text(tr(`Uretim Hatti: ${(salesProdDetailProduction?.production_line || '').toUpperCase()}`), margin + 2, y + 20);
    pdf.text(tr(`Adres: ${(salesProdDetailCustomer?.address || 'N/A').substring(0, 120)}`), margin + 80, y + 20);
    y += 30;

    // Sipariş Bilgileri (yeşil)
    pdf.setFillColor(220, 252, 231);
    pdf.rect(margin, y, contentW, 18, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('SIPARIS BILGILERI', margin + 2, y + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(tr(`Tarih: ${new Date(salesProdDetailTransaction.created_at).toLocaleDateString('tr-TR')}`), margin + 2, y + 13);
    pdf.text(tr(`Durum: ${(salesProdDetailProduction?.status || '').toUpperCase()}`), margin + 80, y + 13);
    const hedef = salesProdDetailProduction?.target_installation_date
      ? new Date(salesProdDetailProduction.target_installation_date).toLocaleDateString('tr-TR')
      : 'Belirlenmemis';
    pdf.text(tr(`Hedef Montaj: ${hedef}`), margin + 130, y + 13);
    y += 22;

    // Ürün Özellikleri (sarı) - 2 sütun (her item için yeterli alan)
    const props = [
      ['Model', salesProdDetailTransaction.model],
      ['Renk', salesProdDetailTransaction.color],
      ['Kapi Kolu', salesProdDetailTransaction.door_handle],
      ['Yuzey', salesProdDetailTransaction.surface],
      ['Montaj', salesProdDetailTransaction.installation],
      ['Montaj Yeri', salesProdDetailTransaction.installation_location],
      ['Ic Pervaz', salesProdDetailTransaction.inner_frame],
      ['Dis Pervaz', salesProdDetailTransaction.outer_frame],
    ].filter(([, v]) => v);
    const propCols = 2;
    const propRows = Math.ceil(props.length / propCols);
    const propBlockH = 8 + propRows * 6 + 2;
    pdf.setFillColor(254, 249, 195);
    pdf.rect(margin, y, contentW, propBlockH, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('URUN OZELLIKLERI', margin + 2, y + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    const colW = contentW / propCols;
    props.forEach(([label, value], i) => {
      const col = i % propCols;
      const row = Math.floor(i / propCols);
      const cx = margin + 2 + col * colW;
      const cy = y + 13 + row * 6;
      // Etiket ve değer arasında yeterli boşluk - değeri otomatik kısalt
      const fullText = tr(`${label}: ${value}`);
      const maxW = colW - 4;
      const truncated = pdf.splitTextToSize(fullText, maxW)[0] || fullText;
      pdf.text(truncated, cx, cy);
    });
    y += propBlockH + 2;

    // Nihai Müşteri (mor)
    if (salesProdDetailTransaction.end_customer) {
      pdf.setFillColor(243, 232, 255);
      pdf.rect(margin, y, contentW, 10, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('NIHAI MUSTERI:', margin + 2, y + 6);
      pdf.setFont('helvetica', 'normal');
      pdf.text(tr(salesProdDetailTransaction.end_customer).substring(0, 80), margin + 40, y + 6);
      y += 12;
    }

    // Ölçü Tabloları (tek sayfaya sığacak, OKUNAKLI font)
    let measurements = [];
    try { measurements = JSON.parse(salesProdDetailTransaction.measurements || '[]'); } catch (e) {}
    const valid = measurements.filter(m => m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description);

    if (valid.length > 0) {
      const notesH = salesProdDetailProduction?.notes ? 18 : 0;
      const remainH = pageHeight - margin - y - notesH;
      // İki tablo için toplam: (1 başlık + N veri) * 2 satır + 1 KANAT başlığı
      const totalRows = (valid.length + 1) * 2;
      const titleH = 8;
      const availableForTables = remainH - titleH - 4;
      // Daha büyük font için min 5mm satır yüksekliği, max 9mm
      const rowH = Math.max(5, Math.min(9, availableForTables / totalRows));
      // Font satır yüksekliğiyle orantılı - DAHA BÜYÜK aralık
      const fontSize = Math.max(9, Math.min(13, rowH * 1.4));

      autoTable(pdf, {
        startY: y,
        head: [['Sira', 'En', 'Boy', 'Kasa', 'Yon', 'Cam', 'Oda', 'Aciklama']],
        body: valid.map((m, i) => [
          `${i + 1}${m.quantity && m.quantity > 1 ? ` x${m.quantity}` : ''}`,
          tr(m.width || '-'),
          tr(m.height || '-'),
          tr(m.frame || '-'),
          tr(m.direction || '-'),
          tr(m.glass || '-'),
          tr(m.room || '-'),
          tr(m.description || '-'),
        ]),
        theme: 'grid',
        styles: { fontSize, cellPadding: 1.2, halign: 'center', valign: 'middle' },
        headStyles: { fillColor: [219, 234, 254], textColor: 0, fontStyle: 'bold', fontSize: fontSize + 0.5 },
        margin: { left: margin, right: margin },
      });
      y = pdf.lastAutoTable.finalY + 2;

      // KANAT ÖLÇÜSÜ başlığı
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(185, 28, 28);
      pdf.text('KANAT OLCUSU', margin + 2, y + 4);
      pdf.setTextColor(0, 0, 0);
      y += 6;

      autoTable(pdf, {
        startY: y,
        head: [['Sira', 'En', 'Boy', 'Yon', 'Cam', 'Aciklama']],
        body: valid.map((m, i) => {
          const ow = parseFloat(m.width) || 0;
          const oh = parseFloat(m.height) || 0;
          const pw = ow > 6 ? ow - 6 : ow;
          const ph = oh > 4 ? oh - 4 : oh;
          return [
            `${i + 1}${m.quantity && m.quantity > 1 ? ` x${m.quantity}` : ''}`,
            pw > 0 ? String(pw) : '-',
            ph > 0 ? String(ph) : '-',
            tr(m.direction || '-'),
            tr(m.glass || '-'),
            tr(m.description || '-'),
          ];
        }),
        theme: 'grid',
        styles: { fontSize, cellPadding: 1.2, halign: 'center', valign: 'middle' },
        headStyles: { fillColor: [254, 226, 226], textColor: 0, fontStyle: 'bold', fontSize: fontSize + 0.5 },
        bodyStyles: { textColor: [185, 28, 28] },
        columnStyles: { 3: { textColor: 0 }, 4: { textColor: 0 }, 5: { textColor: 0 } },
        margin: { left: margin, right: margin },
      });
      y = pdf.lastAutoTable.finalY + 2;
    }

    // İmalat Notları
    if (salesProdDetailProduction?.notes) {
      const remainH = pageHeight - margin - y;
      if (remainH > 12) {
        pdf.setFillColor(255, 237, 213);
        const lines = pdf.splitTextToSize(tr(salesProdDetailProduction.notes), contentW - 4);
        const maxLines = Math.floor((remainH - 8) / 4);
        const showLines = lines.slice(0, maxLines);
        const noteH = Math.min(remainH, 8 + showLines.length * 4);
        pdf.rect(margin, y, contentW, noteH, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('IMALAT NOTLARI', margin + 2, y + 5);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.text(showLines, margin + 2, y + 10);
      }
    }

    // Tek sayfa garantisi
    const totalPages = pdf.internal.getNumberOfPages();
    if (totalPages > 1) {
      for (let i = totalPages; i > 1; i--) {
        pdf.deletePage(i);
      }
    }
    return pdf;
  };

  // PDF Yazdır: Anında veri tabanlı PDF, yeni sekmede yazdırma diyalogu
  const handleSalesProdDetailPrintPDF = () => {
    if (!salesProdDetailProduction || !salesProdDetailTransaction) {
      toast.error('Sipariş verisi yüklenmedi');
      return;
    }
    try {
      const pdf = generateSalesProdDetailPdfFast();
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
        };
      } else {
        toast.error('Pop-up engellendi. Lütfen yeni pencerelere izin verin.');
      }
    } catch (error) {
      console.error('PDF yazdırılırken hata:', error);
      toast.error('PDF yazdırılırken hata oluştu');
    }
  };

  // PDF İndir: Tek sayfa PDF'i dosya olarak indir
  const handleSalesProdDetailDownloadPDF = async () => {
    if (!salesProdDetailProduction || !salesProdDetailTransaction) {
      toast.error('Sipariş verisi yüklenmedi');
      return;
    }
    toast.info('PDF oluşturuluyor, lütfen bekleyin...');
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
      const pdf = await generateSalesProdDetailPdf();
      if (!pdf) return;
      const customerCode = salesProdDetailCustomer?.serial_number || 'N-A';
      const safeName = (salesProdDetailProduction?.customer_name || 'Musteri').replace(/[^a-zA-Z0-9_-]/g, '_');
      const dateStr = new Date().toLocaleDateString('tr-TR').replace(/\./g, '_');
      pdf.save(`${customerCode}_Siparis_Detay_${safeName}_${dateStr}.pdf`);
      toast.success('PDF başarıyla indirildi');
    } catch (error) {
      console.error('PDF oluşturulurken hata:', error);
      toast.error('PDF oluşturulurken hata oluştu');
    }
  };

  const handleEditTransaction = async (transaction) => {
    console.log("handleEditTransaction çağrıldı:", transaction);
    
    try {
      // Backend'den fresh transaction data çek
      const response = await axios.get(`${API}/transactions-by-id/${transaction.id}?session_token=${sessionToken}`);
      const freshTransaction = response.data;
      
      console.log("Fresh transaction from backend:", freshTransaction);
      console.log("End customer from backend:", freshTransaction.end_customer);
      
      setEditingTransaction(freshTransaction);
      
      // Customer search term'i doldur ve form data'yı set et
      const customer = customers.find(c => c.id === freshTransaction.account_id);
      if (customer) {
        setCustomerSearchTerm(`${customer.serial_number} - ${customer.name} ${customer.surname}`);
        setShowCustomerDropdown(false); // Dropdown'ı kapat çünkü müşteri zaten seçili
      } else {
        // Müşteri bulunamazsa searchTerm'i temizle
        setCustomerSearchTerm("");
        setShowCustomerDropdown(true); // Dropdown'ı aç ki kullanıcı seçebilsin
      }
      
      // Form data'yı doldur - FRESH DATA kullan
      setFormData({
        account_id: freshTransaction.account_id,
        transaction_type: freshTransaction.transaction_type,
        amount: freshTransaction.amount,
        description: freshTransaction.description,
        model: freshTransaction.model || "",
        color: freshTransaction.color || "",
        door_handle: freshTransaction.door_handle || "",
        surface: freshTransaction.surface || "",
        installation: freshTransaction.installation || "",
        installation_location: freshTransaction.installation_location || "",
        inner_frame: freshTransaction.inner_frame || "",
        outer_frame: freshTransaction.outer_frame || "",
        details: freshTransaction.details || "",
        production_line: freshTransaction.production_line || "lake",
        payment_method: freshTransaction.payment_method || "kasa",
        payment_description: freshTransaction.payment_description || "",
        target_installation_date: freshTransaction.target_installation_date ? new Date(freshTransaction.target_installation_date) : null,
        end_customer: freshTransaction.end_customer || ""
      });
    
      // Measurements'ı doldur - FRESH DATA kullan
      if (freshTransaction.measurements && freshTransaction.measurements.trim() !== "") {
        try {
          const parsedMeasurements = JSON.parse(freshTransaction.measurements);
          if (Array.isArray(parsedMeasurements) && parsedMeasurements.length > 0) {
            setMeasurements(parsedMeasurements);
          } else {
            // Boş ya da geçersiz measurements varsa default değer ver
            setMeasurements([{ id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
          }
        } catch (error) {
          console.error("Measurements parse hatası:", error);
          setMeasurements([{ id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
        }
      } else {
        setMeasurements([{ id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
      }
      
      // Custom state'leri kontrol et ve ayarla - FRESH DATA kullan
      setIsCustomModel(!modelOptions.includes(freshTransaction.model) && freshTransaction.model);
      setIsCustomColor(!colorOptions.includes(freshTransaction.color) && freshTransaction.color);
      setIsCustomDoorHandle(!doorHandleOptions.includes(freshTransaction.door_handle) && freshTransaction.door_handle);
      setIsCustomSurface(!surfaceOptions.includes(freshTransaction.surface) && freshTransaction.surface);
      setIsCustomInstallation(!installationOptions.includes(freshTransaction.installation) && freshTransaction.installation);
      setIsCustomInstallationLocation(!installationLocationOptions.includes(freshTransaction.installation_location) && freshTransaction.installation_location);
      setIsCustomInnerFrame(!innerFrameOptions.includes(freshTransaction.inner_frame) && freshTransaction.inner_frame);
      setIsCustomOuterFrame(!outerFrameOptions.includes(freshTransaction.outer_frame) && freshTransaction.outer_frame);
      
      setIsEditDialogOpen(true);
    } catch (error) {
      console.error("Transaction fresh data çekilirken hata:", error);
      toast.error("Transaction bilgileri yüklenirken hata oluştu");
    }
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    
    // Validation checks
    if (!editingTransaction || !editingTransaction.id) {
      toast.error("Düzenlenecek işlem bulunamadı");
      return;
    }
    
    if (!formData.account_id) {
      toast.error("Lütfen bir müşteri seçin");
      return;
    }
    
    try {
      // Amount değerini güvenli şekilde hazırla - formData'dan al
      let finalAmount = 0;
      if (formData.amount !== undefined && formData.amount !== null && formData.amount !== "") {
        if (typeof formData.amount === 'number') {
          finalAmount = formData.amount;
        } else if (typeof formData.amount === 'string') {
          // String ise formatlanmış olabilir (1.500 gibi)
          const cleanAmount = parseAmount(formData.amount);
          finalAmount = Number(cleanAmount) || 0;
        }
      }
      
      // Measurements'ı düzgün şekilde hazırla
      const measurementsJson = JSON.stringify(
        measurements.filter(row => 
          row.width || row.height || row.frame || row.direction || row.glass || row.room || row.description
        )
      );
      
      const updateData = {
        account_id: formData.account_id,
        account_type: "customer",
        transaction_type: formData.transaction_type || "debit",
        amount: finalAmount,
        description: formData.description || "",
        model: formData.model || "",
        color: formData.color || "",
        door_handle: formData.door_handle || "",
        surface: formData.surface || "",
        installation: formData.installation || "",
        installation_location: formData.installation_location || "",
        inner_frame: formData.inner_frame || "",
        outer_frame: formData.outer_frame || "",
        details: formData.details || "",
        production_line: formData.production_line || "lake",
        payment_method: formData.payment_method || "nakit",
        payment_description: formData.payment_description || "",
        target_installation_date: formData.target_installation_date ? formData.target_installation_date.toISOString() : null,
        end_customer: formData.end_customer || "",  // Nihai Müşteri - EKLENDİ!
        measurements: measurementsJson
      };
      
      console.log("Güncelleme verileri:", updateData);
      console.log("Transaction ID:", editingTransaction.id);
      console.log("Amount değeri:", finalAmount, "Tipi:", typeof finalAmount);
      
      await axios.put(`${API}/transactions/${editingTransaction.id}`, updateData, {
        params: { session_token: sessionToken }
      });
      toast.success("İşlem başarıyla güncellendi");
      setIsEditDialogOpen(false);
      setEditingTransaction(null);
      fetchTransactions(salesCurrentPage, searchTerm);
      resetForm();
    } catch (error) {
      console.error("İşlem güncellenirken hata:", error);
      
      // Hata mesajını düzgün çıkar
      let errorMessage = "Bilinmeyen hata";
      if (error.response) {
        // Sunucu hatası
        const responseData = error.response.data;
        
        if (responseData && typeof responseData === 'object') {
          if (responseData.detail && Array.isArray(responseData.detail)) {
            // Pydantic validation errors
            const validationErrors = responseData.detail.map(err => err.msg).join(', ');
            errorMessage = `Doğrulama hatası: ${validationErrors}`;
          } else if (responseData.detail) {
            errorMessage = responseData.detail;
          } else if (responseData.message) {
            errorMessage = responseData.message;
          } else {
            errorMessage = `HTTP ${error.response.status} hatası`;
          }
        } else {
          errorMessage = `HTTP ${error.response.status} hatası`;
        }
        
        console.error("Response data:", responseData);
        console.error("Response status:", error.response.status);
      } else if (error.request) {
        // Network hatası
        errorMessage = "Sunucuya bağlanamıyor";
        console.error("Request error:", error.request);
      } else {
        // Diğer hatalar
        errorMessage = error.message || "Bilinmeyen hata";
        console.error("Other error:", error.message);
      }
      
      toast.error(`İşlem güncellenirken hata oluştu: ${errorMessage}`);
    }
  };

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    
    try {
      const response = await axios.delete(`${API}/transactions/${transactionToDelete.id}`, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.summary) {
          console.log("Silinen ilişkili kayıtlar:", response.data.summary);
        }
      } else {
        toast.success("İşlem ve ilişkili kayıtlar silindi");
      }
      
      setDeleteConfirmOpen(false);
      setTransactionToDelete(null);
      fetchTransactions(salesCurrentPage, searchTerm);
    } catch (error) {
      console.error("İşlem silinirken hata:", error);
      const errorMessage = error.response?.data?.detail || "İşlem silinirken hata oluştu";
      toast.error(errorMessage);
    }
  };

  const openDeleteConfirm = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteConfirmOpen(true);
  };

  const handleViewTransaction = (transaction) => {
    setCurrentTransaction(transaction);
    setIsViewDialogOpen(true);
  };

  const getAccountName = (accountId) => {
    const customer = customers.find(c => c.id === accountId);
    return customer ? `${customer.name} ${customer.surname}` : 'Bilinmeyen Müşteri';
  };

  // Server-side arama + sayfalama kullanıldığı için client-side filtre kaldırıldı.
  // Tablo doğrudan `transactions` state'ini gösterir.

  if (loading && !salesLoadedRef.current) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Satış İşlemleri</h1>
          <p className="text-muted-foreground">Müşteri satışlarını yönetin</p>
        </div>
        <div className="flex space-x-2">
          {/* Diğer Satış İşlemi Butonu */}
          <Dialog open={isOtherSaleDialogOpen} onOpenChange={(open) => {
            setIsOtherSaleDialogOpen(open);
            if (!open) {
              // Dialog kapatıldığında formu temizle
              setOtherSaleCustomer(null);
              setOtherSaleItems([{ name: '', quantity: 1, unit_price: 0, total: 0 }]);
              setOtherSaleDescription('');
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Diğer Satış İşlemi
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Diğer Satış İşlemi</DialogTitle>
                <DialogDescription>
                  Kapı harici satışlar (aksesuar, parça, hizmet vb.) için kullanın
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Müşteri Seçimi */}
                <div>
                  <Label>Müşteri *</Label>
                  <div className="relative">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <Input
                          placeholder="Müşteri adı yazarak arayın..."
                          value={otherSaleCustomer ? `${otherSaleCustomer.serial_number || ''} - ${otherSaleCustomer.name} ${otherSaleCustomer.surname}` : customerSearchTerm}
                          onChange={(e) => {
                            setCustomerSearchTerm(e.target.value);
                            setOtherSaleCustomer(null);
                            setShowCustomerDropdown(true);
                          }}
                          onFocus={() => setShowCustomerDropdown(true)}
                          onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                          className="w-full"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsNewCustomerDialogOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Yeni
                      </Button>
                    </div>
                    
                    {/* Müşteri dropdown */}
                    {showCustomerDropdown && !otherSaleCustomer && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {(customerSearchTerm.length === 0 ? customers.slice(0, 10) : filteredCustomers.slice(0, 10)).map((customer) => (
                          <div
                            key={customer.id}
                            className="px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
                            onClick={() => {
                              setOtherSaleCustomer(customer);
                              setShowCustomerDropdown(false);
                            }}
                          >
                            <span className="text-sm">
                              <span className="font-medium">{customer.serial_number}</span> - {customer.name} {customer.surname}
                            </span>
                            <span className="text-xs text-gray-500">{customer.phone || 'Tel yok'}</span>
                          </div>
                        ))}
                        <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t">
                          {customerSearchTerm.length === 0 
                            ? `İlk 10 müşteri gösteriliyor (Toplam: ${customers.length})`
                            : `${filteredCustomers.length} sonuç bulundu`}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Ürün/Hizmet Listesi */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Ürün/Hizmet Listesi *</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setOtherSaleItems([...otherSaleItems, { name: '', quantity: 1, unit_price: 0, total: 0 }]);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ürün Ekle
                    </Button>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Ürün/Hizmet Adı</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-24">Miktar</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-32">Birim Fiyat</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-32">Toplam</th>
                          <th className="px-3 py-2 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {otherSaleItems.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-3 py-2">
                              <Input
                                placeholder="Örn: Menteşe, Kol, İşçilik"
                                value={item.name}
                                onChange={(e) => {
                                  const newItems = [...otherSaleItems];
                                  newItems[index].name = e.target.value;
                                  setOtherSaleItems(newItems);
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => {
                                  const newItems = [...otherSaleItems];
                                  newItems[index].quantity = parseFloat(e.target.value) || 0;
                                  newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
                                  setOtherSaleItems(newItems);
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => {
                                  const newItems = [...otherSaleItems];
                                  newItems[index].unit_price = parseFloat(e.target.value) || 0;
                                  newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
                                  setOtherSaleItems(newItems);
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-green-600">
                                ₺{item.total.toFixed(2)}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              {otherSaleItems.length > 1 && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const newItems = otherSaleItems.filter((_, i) => i !== index);
                                    setOtherSaleItems(newItems);
                                  }}
                                >
                                  <X className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2">
                        <tr>
                          <td colSpan="3" className="px-3 py-3 text-right font-bold">
                            GENEL TOPLAM:
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-lg font-bold text-green-600">
                              ₺{otherSaleItems.reduce((sum, item) => sum + item.total, 0).toFixed(2)}
                            </div>
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                
                {/* Açıklama/Not */}
                <div>
                  <Label>Açıklama/Not</Label>
                  <Input
                    placeholder="Opsiyonel açıklama veya not ekleyin"
                    value={otherSaleDescription}
                    onChange={(e) => setOtherSaleDescription(e.target.value)}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsOtherSaleDialogOpen(false)}
                >
                  İptal
                </Button>
                <Button
                  onClick={async () => {
                    // Validasyon
                    if (!otherSaleCustomer) {
                      toast.error('Lütfen müşteri seçin');
                      return;
                    }
                    
                    const validItems = otherSaleItems.filter(item => item.name.trim() && item.quantity > 0 && item.unit_price >= 0);
                    if (validItems.length === 0) {
                      toast.error('Lütfen en az bir ürün/hizmet ekleyin');
                      return;
                    }
                    
                    try {
                      const response = await axios.post(
                        `${API}/other-sales/create`,
                        {
                          customer_id: otherSaleCustomer.id,
                          customer_name: `${otherSaleCustomer.name} ${otherSaleCustomer.surname}`,
                          items: validItems,
                          description: otherSaleDescription,
                          period_id: null  // Period sistemi kullanılmıyorsa null
                        },
                        { params: { session_token: sessionToken } }
                      );
                      
                      toast.success(`Diğer satış kaydedildi! Toplam: ₺${response.data.total_amount.toFixed(2)}`);
                      setIsOtherSaleDialogOpen(false);
                      
                      // Satış listesini yenile
                      await fetchTransactions(salesCurrentPage, searchTerm);
                      
                    } catch (error) {
                      console.error('Diğer satış kaydetme hatası:', error);
                      toast.error(error.response?.data?.detail || 'Satış kaydedilemedi');
                    }
                  }}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Kaydet
                </Button>
              </DialogFooter>
            </DialogContent>
            
          </Dialog>
          
          {/* Yeni Satış (Kapı) Butonu */}
          <Dialog open={isNewSaleDialogOpen} onOpenChange={setIsNewSaleDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Satış
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </div>

      {/* Son Satış İşlemleri */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Satış İşlemleri</CardTitle>
              <CardDescription>
                {searchTerm ? (
                  <>{transactions.length} sonuç gösteriliyor (Toplam eşleşen: {totalSalesTransactions})</>
                ) : (
                  <>Toplam {totalSalesTransactions} satış işlemi</>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Müşteri, açıklama, tutar veya ürün ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-72"
                />
              </div>
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {transactions.length === 0 ? (
              <div className="text-center py-12" data-testid="sales-empty-state">
                <p className="text-muted-foreground">
                  {searchTerm ? "Arama kriterlerine uygun satış işlemi bulunamadı" : "Henüz satış işlemi bulunmuyor"}
                </p>
                {!searchTerm && (
                  <Button onClick={() => setIsNewSaleDialogOpen(true)} className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    İlk Satışı Ekle
                  </Button>
                )}
              </div>
            ) : (
              transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-3 h-3 rounded-full ${transaction.transaction_type === 'debit' ? 'bg-green-500' : 'bg-blue-500'}`} />
                  <div className="flex-1">
                    <p className="font-medium">{getAccountName(transaction.account_id)}</p>
                    <p className="text-sm text-muted-foreground">{transaction.description}</p>
                    
                    {/* Ürün Özellikleri */}
                    {(transaction.model || transaction.color || transaction.door_handle) && (
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                        {transaction.model && (
                          <span><strong>Model:</strong> {transaction.model}</span>
                        )}
                        {transaction.color && (
                          <span><strong>Renk:</strong> {transaction.color}</span>
                        )}
                        {transaction.door_handle && (
                          <span><strong>Kapı Kolu:</strong> {transaction.door_handle}</span>
                        )}
                        {transaction.surface && (
                          <span><strong>Yüzey:</strong> {transaction.surface}</span>
                        )}
                        {transaction.installation && (
                          <span><strong>Montaj:</strong> {transaction.installation}</span>
                        )}
                        {transaction.installation_location && (
                          <span><strong>Montaj Yeri:</strong> {transaction.installation_location}</span>
                        )}
                        {transaction.inner_frame && (
                          <span><strong>İç Çerçeve:</strong> {transaction.inner_frame}</span>
                        )}
                        {transaction.outer_frame && (
                          <span><strong>Dış Çerçeve:</strong> {transaction.outer_frame}</span>
                        )}
                      </div>
                    )}
                    
                    {/* Nihai Müşteri */}
                    {transaction.end_customer && (
                      <div className="mt-2 text-xs bg-purple-50 p-2 rounded border border-purple-200">
                        <span><strong>🏢 Nihai Müşteri:</strong> {transaction.end_customer}</span>
                      </div>
                    )}
                    
                    {/* Ölçüler Tablosu - İyileştirme ile */}
                    {transaction.measurements && (
                      <div className="mt-2">
                        <details className="cursor-pointer">
                          <summary className="text-xs font-medium text-blue-600 hover:text-blue-800">
                            📏 Ölçüler ({parseMeasurements(transaction.measurements).length} kayıt)
                          </summary>
                          <div className="mt-2 max-w-full overflow-x-auto">
                            {parseMeasurements(transaction.measurements).length > 0 && (
                              <div className="table-container">
                                <table className="measurements-table-mobile min-w-full text-xs border border-gray-300">
                                  <thead>
                                    <tr className="bg-gray-50">
                                      <th className="border border-gray-300 px-2 py-1 text-left">#</th>
                                      <th className="border border-gray-300 px-2 py-1 text-left">En</th>
                                      <th className="border border-gray-300 px-2 py-1 text-left">Boy</th>
                                      <th className="border border-gray-300 px-2 py-1 text-left">Kasa</th>
                                      <th className="border border-gray-300 px-2 py-1 text-left">Yönü</th>
                                      <th className="border border-gray-300 px-2 py-1 text-left">Cam</th>
                                      <th className="border border-gray-300 px-2 py-1 text-left">Oda</th>
                                      <th className="border border-gray-300 px-2 py-1 text-left">Açıklama</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {parseMeasurements(transaction.measurements).map((row, idx) => (
                                      <tr key={idx} className="hover:bg-gray-50">
                                        <td className="border border-gray-300 px-2 py-1">{idx + 1}</td>
                                        <td className="border border-gray-300 px-2 py-1">{row.width || '-'}</td>
                                        <td className="border border-gray-300 px-2 py-1">{row.height || '-'}</td>
                                        <td className="border border-gray-300 px-2 py-1">{row.thickness || row.frame || '-'}</td>
                                        <td className="border border-gray-300 px-2 py-1">{row.direction || '-'}</td>
                                        <td className="border border-gray-300 px-2 py-1">{row.glass || '-'}</td>
                                        <td className="border border-gray-300 px-2 py-1">{row.room || '-'}</td>
                                        <td className="border border-gray-300 px-2 py-1">{row.notes || row.description || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(transaction.created_at).toLocaleDateString('tr-TR')} • {transaction.production_line || 'Genel'} • {getSalesPersonName(transaction.sales_person_id)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center gap-1 mr-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openSaleDetails(transaction)}
                      className="flex items-center gap-1 text-xs"
                      title="Sipariş Detayı"
                      data-testid={`sale-detail-btn-${transaction.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      <span>Sipariş Detayı</span>
                    </Button>
                    {productionTransactionIds.has(transaction.id) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSalesProductionDetail(transaction)}
                        className="flex items-center gap-1 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                        title="Üretim Sipariş Detayı"
                        data-testid={`prod-order-detail-btn-${transaction.id}`}
                      >
                        <FileText className="h-3.5 w-3.5" />
                        <span>Üretim Sipariş Detayı</span>
                      </Button>
                    )}
                    {productionTransactionIds.has(transaction.id) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                        className="flex items-center gap-1 text-xs text-green-700 border-green-300 bg-green-50 cursor-not-allowed opacity-90"
                        title="Üretime Aktarıldı"
                        data-testid={`production-sent-btn-${transaction.id}`}
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                        <span>Üretime Aktarıldı</span>
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendToProduction(transaction)}
                        className="flex items-center gap-1 text-xs text-purple-600 border-purple-300 hover:bg-purple-50"
                        title="Üretime Aktar"
                        data-testid={`send-to-production-btn-${transaction.id}`}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        <span>Üretime Aktar</span>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditTransaction(transaction)}
                      title="Düzenle"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openDeleteConfirm(transaction)}
                      className="text-red-600 hover:text-red-700"
                      title="Sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className={`font-bold ${transaction.transaction_type === 'debit' ? 'text-green-600' : 'text-blue-600'}`}>
                    {transaction.transaction_type === 'debit' ? '+' : '-'}₺{Math.abs(transaction.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                  <Badge variant={transaction.transaction_type === 'debit' ? 'default' : 'secondary'}>
                    {transaction.transaction_type === 'debit' ? 'Satış' : 'İade/İndirim'}
                  </Badge>
                </div>
              </div>
            ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sayfalama Kontrolleri */}
      {totalSalesTransactions > salesPerPage && (
        <div className="flex items-center justify-between mt-4 px-2" data-testid="sales-pagination">
          <div className="text-sm text-gray-600">
            <span className="font-medium">
              {((salesCurrentPage - 1) * salesPerPage) + 1}
              {" - "}
              {Math.min(salesCurrentPage * salesPerPage, totalSalesTransactions)}
            </span>
            {" / "}
            <span className="font-bold">{totalSalesTransactions}</span> satış
            {searchTerm.trim() && <span className="ml-2 text-blue-600">("{searchTerm}" araması)</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTransactions(1, searchTerm)}
              disabled={salesCurrentPage === 1 || loading}
              data-testid="sales-pagination-first"
            >
              « İlk
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTransactions(salesCurrentPage - 1, searchTerm)}
              disabled={salesCurrentPage === 1 || loading}
              data-testid="sales-pagination-prev"
            >
              ‹ Önceki
            </Button>
            <span className="text-sm px-3 py-1 bg-gray-100 rounded font-medium" data-testid="sales-pagination-info">
              Sayfa {salesCurrentPage} / {Math.max(1, Math.ceil(totalSalesTransactions / salesPerPage))}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTransactions(salesCurrentPage + 1, searchTerm)}
              disabled={salesCurrentPage >= Math.ceil(totalSalesTransactions / salesPerPage) || loading}
              data-testid="sales-pagination-next"
            >
              Sonraki ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTransactions(Math.ceil(totalSalesTransactions / salesPerPage), searchTerm)}
              disabled={salesCurrentPage >= Math.ceil(totalSalesTransactions / salesPerPage) || loading}
              data-testid="sales-pagination-last"
            >
              Son »
            </Button>
          </div>
        </div>
      )}

      {/* Dialog for New Sale - Continuing from header */}
        <ProductOptionsDialog
          open={isProductOptionsOpen}
          onOpenChange={setIsProductOptionsOpen}
          sessionToken={sessionToken}
          currentUser={currentUser}
          onSaved={fetchProductOptions}
        />
        <Dialog open={isNewSaleDialogOpen} onOpenChange={setIsNewSaleDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto dialog-professional">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle>Yeni Satış İşlemi</DialogTitle>
                  <DialogDescription>
                    Müşteriye yapılan satış işlemini ekleyin.
                  </DialogDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsProductOptionsOpen(true)}
                  className="shrink-0"
                  title="Ürün özellikleri ve ölçü listelerini düzenle"
                  data-testid="new-sale-edit-lists-btn"
                >
                  <List className="h-4 w-4 mr-1" /> Listeleri Düzenle
                </Button>
              </div>
            </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 form-grid-mobile">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="account_id">Müşteri</Label>
                  </div>
                  <div className="relative">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <Input
                          placeholder="Müşteri adı yazarak arayın..."
                          value={customerSearchTerm}
                          onChange={(e) => {
                            setCustomerSearchTerm(e.target.value);
                            setShowCustomerDropdown(true); // Her zaman dropdown'ı göster
                          }}
                          onFocus={() => {
                            // Focus olduğunda dropdown'ı her zaman aç
                            setShowCustomerDropdown(true);
                          }}
                          onBlur={() => {
                            // Kısa bir delay ile dropdown'ı kapat (click işleminin tamamlanması için)
                            setTimeout(() => setShowCustomerDropdown(false), 150);
                          }}
                          className="w-full"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openNewCustomerDialog}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Yeni
                      </Button>
                    </div>
                    
                    {/* Müşteri seçimi dropdown'u */}
                    {showCustomerDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {/* Eğer arama terimi yoksa ilk 10 müşteriyi göster, varsa filtrelenmiş sonuçları göster */}
                        {(customerSearchTerm.length === 0 ? customers.slice(0, 10) : filteredCustomers.slice(0, 10)).map((customer) => (
                          <div
                            key={customer.id}
                            className="px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
                            onClick={() => {
                              setFormData({...formData, account_id: customer.id});
                              setCustomerSearchTerm(`${customer.serial_number} - ${customer.name} ${customer.surname}`);
                              setShowCustomerDropdown(false);
                            }}
                          >
                            <span className="text-sm">
                              <span className="font-medium">{customer.serial_number}</span> - {customer.name} {customer.surname}
                            </span>
                            <span className="text-xs text-gray-500">{customer.phone || 'Tel yok'}</span>
                          </div>
                        ))}
                        {/* Toplam müşteri sayısı bilgisi */}
                        <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t">
                          {customerSearchTerm.length === 0 
                            ? `İlk 10 müşteri gösteriliyor (Toplam: ${customers.length} müşteri)`
                            : filteredCustomers.length > 10 
                              ? `${filteredCustomers.length - 10} müşteri daha var, aramayı daraltın`
                              : `${filteredCustomers.length} müşteri bulundu`
                          }
                        </div>
                      </div>
                    )}
                    
                    {/* Arama sonucu bulunamadığında */}
                    {showCustomerDropdown && customerSearchTerm.length > 0 && filteredCustomers.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                        <div className="px-4 py-3 text-sm text-gray-500">
                          Müşteri bulunamadı. Yeni müşteri eklemek için "Yeni" butonunu kullanın.
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Nihai Müşteri */}
                <div>
                  <Label htmlFor="end_customer">Nihai Müşteri (İsteğe Bağlı)</Label>
                  <Input
                    id="end_customer"
                    value={formData.end_customer}
                    onChange={(e) => setFormData({...formData, end_customer: e.target.value})}
                    placeholder="Müşterinizin müşterisi varsa buraya yazın..."
                  />
                </div>

                {/* Satış Personeli İsmi */}
                <div>
                  <Label htmlFor="sales_person_name">Satış Personeli *</Label>
                  <Input
                    id="sales_person_name"
                    value={formData.sales_person_name || ""}
                    onChange={(e) => setFormData({...formData, sales_person_name: e.target.value})}
                    placeholder="Satış personeli adını girin"
                    required
                  />
                </div>

                {/* İşlem Türü ve Üretim Hattı - Yan Yana */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="transaction_type">İşlem Türü</Label>
                    <Select value={formData.transaction_type} onValueChange={(value) => setFormData({...formData, transaction_type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="debit">Satış (Müşteri Borçlu)</SelectItem>
                        <SelectItem value="credit">İade/İndirim</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="production_line">Üretim Hattı *</Label>
                    <Select value={formData.production_line} onValueChange={(value) => setFormData({...formData, production_line: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Üretim hattı seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lake">LAKE</SelectItem>
                        <SelectItem value="melamin">MELAMİN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Ürün Özellikleri - 4 Sütunlu Grid */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Ürün Özellikleri</h3>
                  
                  {/* İlk satır: Model, Renk, Kapı Kolu, Yüzey */}
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="model">Model</Label>
                      {isCustomModel ? (
                        <Input
                          value={formData.model}
                          onChange={(e) => setFormData({...formData, model: e.target.value})}
                          placeholder="Özel model adını yazın"
                          onBlur={() => {
                            if (!formData.model) {
                              setIsCustomModel(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.model} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomModel(true);
                              setFormData({...formData, model: ""});
                            } else {
                              setFormData({...formData, model: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Model seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {modelOptions.map((model) => (
                              <SelectItem key={model} value={model}>{model}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="color">Renk</Label>
                      {isCustomColor ? (
                        <Input
                          value={formData.color}
                          onChange={(e) => setFormData({...formData, color: e.target.value})}
                          placeholder="Özel renk adını yazın"
                          onBlur={() => {
                            if (!formData.color) {
                              setIsCustomColor(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.color} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomColor(true);
                              setFormData({...formData, color: ""});
                            } else {
                              setFormData({...formData, color: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Renk seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {colorOptions.map((color) => (
                              <SelectItem key={color} value={color}>{color}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="door_handle">Kapı Kolu</Label>
                      {isCustomDoorHandle ? (
                        <Input
                          value={formData.door_handle}
                          onChange={(e) => setFormData({...formData, door_handle: e.target.value})}
                          placeholder="Özel kapı kolu türünü yazın"
                          onBlur={() => {
                            if (!formData.door_handle) {
                              setIsCustomDoorHandle(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.door_handle} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomDoorHandle(true);
                              setFormData({...formData, door_handle: ""});
                            } else {
                              setFormData({...formData, door_handle: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Kapı kolu seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {doorHandleOptions.map((handle) => (
                              <SelectItem key={handle} value={handle}>{handle}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="surface">Yüzey</Label>
                      {isCustomSurface ? (
                        <Input
                          value={formData.surface}
                          onChange={(e) => setFormData({...formData, surface: e.target.value})}
                          placeholder="Özel yüzey türünü yazın"
                          onBlur={() => {
                            if (!formData.surface) {
                              setIsCustomSurface(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.surface} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomSurface(true);
                              setFormData({...formData, surface: ""});
                            } else {
                              setFormData({...formData, surface: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Yüzey seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {surfaceOptions.map((surface) => (
                              <SelectItem key={surface} value={surface}>{surface}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  
                  {/* İkinci satır: Montaj, Montaj Yeri, İç Pervaz, Dış Pervaz */}
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="installation">Montaj</Label>
                      {isCustomInstallation ? (
                        <Input
                          value={formData.installation}
                          onChange={(e) => setFormData({...formData, installation: e.target.value})}
                          placeholder="Özel montaj türünü yazın"
                          onBlur={() => {
                            if (!formData.installation) {
                              setIsCustomInstallation(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.installation} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomInstallation(true);
                              setFormData({...formData, installation: ""});
                            } else {
                              setFormData({...formData, installation: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Montaj seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {installationOptions.map((installation) => (
                              <SelectItem key={installation} value={installation}>{installation}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="installation_location">Montaj Yeri</Label>
                      {isCustomInstallationLocation ? (
                        <Input
                          value={formData.installation_location}
                          onChange={(e) => setFormData({...formData, installation_location: e.target.value})}
                          placeholder="Özel montaj yeri yazın"
                          onBlur={() => {
                            if (!formData.installation_location) {
                              setIsCustomInstallationLocation(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.installation_location} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomInstallationLocation(true);
                              setFormData({...formData, installation_location: ""});
                            } else {
                              setFormData({...formData, installation_location: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Montaj yeri seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {installationLocationOptions.map((location) => (
                              <SelectItem key={location} value={location}>{location}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="inner_frame">İç Pervaz</Label>
                      {isCustomInnerFrame ? (
                        <Input
                          value={formData.inner_frame}
                          onChange={(e) => setFormData({...formData, inner_frame: e.target.value})}
                          placeholder="Özel iç pervaz yazın"
                          onBlur={() => {
                            if (!formData.inner_frame) {
                              setIsCustomInnerFrame(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.inner_frame} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomInnerFrame(true);
                              setFormData({...formData, inner_frame: ""});
                            } else {
                              setFormData({...formData, inner_frame: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="İç pervaz seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {innerFrameOptions.map((frame) => (
                              <SelectItem key={frame} value={frame}>{frame}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="outer_frame">Dış Pervaz</Label>
                      {isCustomOuterFrame ? (
                        <Input
                          value={formData.outer_frame}
                          onChange={(e) => setFormData({...formData, outer_frame: e.target.value})}
                          placeholder="Özel dış pervaz yazın"
                          onBlur={() => {
                            if (!formData.outer_frame) {
                              setIsCustomOuterFrame(false);
                            }
                          }}
                        />
                      ) : (
                        <Select 
                          value={formData.outer_frame} 
                          onValueChange={(value) => {
                            if (value === "özel") {
                              setIsCustomOuterFrame(true);
                              setFormData({...formData, outer_frame: ""});
                            } else {
                              setFormData({...formData, outer_frame: value});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Dış pervaz seçin" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {outerFrameOptions.map((frame) => (
                              <SelectItem key={frame} value={frame}>{frame}</SelectItem>
                            ))}
                            <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </div>

                {/* Kanat Ölçüsü tablosu kaldırıldı - kullanıcı isteği */}

                <div>
                  <Label htmlFor="details">Detay</Label>
                  <Textarea
                    id="details"
                    value={formData.details}
                    onChange={(e) => setFormData({...formData, details: e.target.value})}
                    placeholder="Ek detaylar ve notlar"
                    rows="3"
                  />
                </div>

                {/* Ölçü Tablosu */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>Ölçü Tablosu</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant={showQuantityColumn ? "default" : "outline"}
                      onClick={() => setShowQuantityColumn(!showQuantityColumn)}
                      className="h-7"
                    >
                      {showQuantityColumn ? "✓ Adet Aktif" : "+ Adet Ekle"}
                    </Button>
                  </div>
                  <div className="max-h-96 overflow-y-auto border rounded-md">
                    <div className="table-container">
                      <table className="w-full text-sm measurements-table-mobile">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="w-8 p-2 text-left font-medium min-w-[40px]">#</th>
                            <th className="p-2 text-left font-medium min-w-[80px]">En</th>
                            <th className="p-2 text-left font-medium min-w-[80px]">Boy</th>
                            <th className="p-2 text-left font-medium min-w-[90px]">Kasa</th>
                            <th className="p-2 text-left font-medium min-w-[80px]">Yönü</th>
                            <th className="p-2 text-left font-medium min-w-[80px]">Cam</th>
                            <th className="p-2 text-left font-medium min-w-[90px]">Oda</th>
                            <th className="p-2 text-left font-medium min-w-[120px]">Açıklama</th>
                            {showQuantityColumn && (
                              <th className="p-2 text-left font-medium min-w-[70px] bg-purple-50">Adet</th>
                            )}
                            <th className="p-2 text-left font-medium min-w-[80px]">İşlem</th>
                          </tr>
                        </thead>
                      <tbody>
                        {measurements.map((row, index) => (
                          <tr key={row.id} className="border-t">
                            <td className="p-2 font-medium text-gray-500">{row.id}</td>
                            <td className="p-1">
                              {row.width === "özel" || (row.width && !widthOptions.includes(parseInt(row.width))) ? (
                                <Input
                                  value={row.width === "özel" ? "" : row.width}
                                  onChange={(e) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].width = e.target.value;
                                    setMeasurements(newMeasurements);
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Özel en ölçüsü"
                                />
                              ) : (
                                <Select
                                  value={widthOptions.includes(parseInt(row.width)) ? row.width : ""}
                                  onValueChange={(value) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].width = value;
                                    setMeasurements(newMeasurements);
                                  }}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="En" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[400px] overflow-y-auto">
                                    {widthOptions.map((width) => (
                                      <SelectItem key={width} value={width.toString()}>{width}</SelectItem>
                                    ))}
                                    <SelectItem value="özel">Özel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-1">
                              {row.height === "özel" || (row.height && !heightOptions.includes(parseInt(row.height))) ? (
                                <Input
                                  value={row.height === "özel" ? "" : row.height}
                                  onChange={(e) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].height = e.target.value;
                                    setMeasurements(newMeasurements);
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Özel boy ölçüsü"
                                />
                              ) : (
                                <Select
                                  value={heightOptions.includes(parseInt(row.height)) ? row.height : ""}
                                  onValueChange={(value) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].height = value;
                                    setMeasurements(newMeasurements);
                                  }}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Boy" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[400px] overflow-y-auto">
                                    {heightOptions.map((height) => (
                                      <SelectItem key={height} value={height.toString()}>{height}</SelectItem>
                                    ))}
                                    <SelectItem value="özel">Özel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-1">
                              {row.frame === "özel" || (row.frame && !frameOptions.includes(parseInt(row.frame))) ? (
                                <Input
                                  value={row.frame === "özel" ? "" : row.frame}
                                  onChange={(e) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].frame = e.target.value;
                                    setMeasurements(newMeasurements);
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Özel kasa ölçüsü"
                                />
                              ) : (
                                <Select
                                  value={frameOptions.includes(parseInt(row.frame)) ? row.frame : ""}
                                  onValueChange={(value) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].frame = value;
                                    setMeasurements(newMeasurements);
                                  }}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Kasa" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[400px] overflow-y-auto">
                                    {frameOptions.map((frame) => (
                                      <SelectItem key={frame} value={frame.toString()}>{frame}</SelectItem>
                                    ))}
                                    <SelectItem value="özel">Özel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-1">
                              {row.direction === "özel" || (row.direction && !directionOptions.includes(row.direction)) ? (
                                <Input
                                  value={row.direction === "özel" ? "" : row.direction}
                                  onChange={(e) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].direction = e.target.value;
                                    setMeasurements(newMeasurements);
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Özel yön"
                                />
                              ) : (
                                <Select
                                  value={directionOptions.includes(row.direction) ? row.direction : ""}
                                  onValueChange={(value) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].direction = value;
                                    setMeasurements(newMeasurements);
                                  }}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Yön" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[400px] overflow-y-auto">
                                    {directionOptions.map((direction) => (
                                      <SelectItem key={direction} value={direction}>{direction}</SelectItem>
                                    ))}
                                    <SelectItem value="özel">Özel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-1">
                              {row.glass === "özel" || (row.glass && !glassOptions.includes(row.glass)) ? (
                                <Input
                                  value={row.glass === "özel" ? "" : row.glass}
                                  onChange={(e) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].glass = e.target.value;
                                    setMeasurements(newMeasurements);
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Özel cam türü"
                                />
                              ) : (
                                <Select
                                  value={glassOptions.includes(row.glass) ? row.glass : ""}
                                  onValueChange={(value) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].glass = value;
                                    setMeasurements(newMeasurements);
                                  }}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Cam" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[400px] overflow-y-auto">
                                    {glassOptions.map((glass) => (
                                      <SelectItem key={glass} value={glass}>{glass}</SelectItem>
                                    ))}
                                    <SelectItem value="özel">Özel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-1">
                              {row.room === "özel" || (row.room && !roomOptions.includes(row.room)) ? (
                                <Input
                                  value={row.room === "özel" ? "" : row.room}
                                  onChange={(e) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].room = e.target.value;
                                    setMeasurements(newMeasurements);
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Özel oda türü"
                                />
                              ) : (
                                <Select
                                  value={roomOptions.includes(row.room) ? row.room : ""}
                                  onValueChange={(value) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].room = value;
                                    setMeasurements(newMeasurements);
                                  }}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Oda" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[400px] overflow-y-auto">
                                    {roomOptions.map((room) => (
                                      <SelectItem key={room} value={room}>{room}</SelectItem>
                                    ))}
                                    <SelectItem value="özel">Özel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-1">
                              <Input
                                placeholder="Açıklama"
                                value={row.description}
                                onChange={(e) => {
                                  const newMeasurements = [...measurements];
                                  newMeasurements[index].description = e.target.value;
                                  setMeasurements(newMeasurements);
                                }}
                                className="h-8"
                              />
                            </td>
                            {showQuantityColumn && (
                              <td className="p-1 bg-purple-50">
                                <Input
                                  type="number"
                                  min="1"
                                  placeholder="Adet"
                                  value={row.quantity || 1}
                                  onChange={(e) => {
                                    const newMeasurements = [...measurements];
                                    newMeasurements[index].quantity = parseInt(e.target.value) || 1;
                                    setMeasurements(newMeasurements);
                                  }}
                                  className="h-8 text-center font-medium"
                                />
                              </td>
                            )}
                            <td className="p-1">
                              <div className="flex gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  title="Satırı kopyala"
                                  onClick={() => {
                                    const newId = Math.max(...measurements.map(m => m.id), 0) + 1;
                                    const copy = { ...row, id: newId };
                                    const newList = [...measurements];
                                    newList.splice(index + 1, 0, copy);
                                    setMeasurements(newList);
                                  }}
                                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (measurements.length > 1) {
                                      setMeasurements(measurements.filter((_, i) => i !== index));
                                    }
                                  }}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  disabled={measurements.length <= 1}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                  
                  {/* Yeni Satır Ekle Butonu */}
                  <div className="mt-2 flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newId = Math.max(...measurements.map(m => m.id), 0) + 1;
                        setMeasurements([...measurements, {
                          id: newId,
                          width: "",
                          height: "",
                          frame: "",
                          direction: "",
                          glass: "",
                          room: "",
                          description: "",
                          quantity: 1
                        }]);
                      }}
                      className="flex items-center space-x-1"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Yeni Satır Ekle</span>
                    </Button>
                  </div>
                </div>

                {/* Tutar */}
                <div>
                  <Label htmlFor="amount">Tutar (TL) *</Label>
                  <Input
                    id="amount"
                    type="text"
                    value={formatAmount(formData.amount)}
                    onChange={(e) => {
                      const numericValue = parseAmount(e.target.value);
                      setFormData({...formData, amount: numericValue});
                    }}
                    placeholder="0"
                    required
                  />
                </div>

                {/* Ödeme Yöntemi */}
                <div>
                  <Label htmlFor="payment_method">Ödeme Yöntemi *</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Ödeme yöntemi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kasa">Kasa</SelectItem>
                      <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                      <SelectItem value="yarim_nakit_yarim_kart">Yarısı Kasa - Yarısı Kredi Kartı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Ödeme Açıklaması */}
                <div>
                  <Label htmlFor="payment_description">Ödeme Açıklaması</Label>
                  <Textarea
                    id="payment_description"
                    value={formData.payment_description}
                    onChange={(e) => setFormData({...formData, payment_description: e.target.value})}
                    placeholder="Ödeme ile ilgili ek bilgiler (isteğe bağlı)"
                    rows={2}
                  />
                </div>

                {/* Hedef Montaj Tarihi */}
                <div>
                  <Label htmlFor="target_installation_date">Hedef Montaj Tarihi (Opsiyonel)</Label>
                  <Input
                    type="date"
                    id="target_installation_date"
                    value={formData.target_installation_date ? formData.target_installation_date.toISOString().split('T')[0] : ""}
                    onChange={(e) => setFormData({...formData, target_installation_date: e.target.value ? new Date(e.target.value) : null})}
                  />
                  <p className="text-xs text-blue-600 mt-1">
                    📅 Bu tarih ajanda ekranında görünecek ve montaj planlamasında kullanılacak
                  </p>
                </div>

                <div>
                  <Label htmlFor="description">İMALAT İÇİN ÖNEMLİ OLAN NOTLAR</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Kaydediliyor..." : "Ekle"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

      {/* Yeni Müşteri Ekleme Dialog'u */}
      <Dialog open={isNewCustomerDialogOpen} onOpenChange={setIsNewCustomerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Müşteri Ekle</DialogTitle>
            <DialogDescription>
              Satış işlemi için yeni müşteri bilgilerini girin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleNewCustomerSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_customer_name">Ad *</Label>
                <Input
                  id="new_customer_name"
                  value={newCustomerData.name}
                  onChange={(e) => setNewCustomerData({...newCustomerData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new_customer_surname">Soyad *</Label>
                <Input
                  id="new_customer_surname"
                  value={newCustomerData.surname}
                  onChange={(e) => setNewCustomerData({...newCustomerData, surname: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="new_customer_phone">Telefon *</Label>
              <Input
                id="new_customer_phone"
                value={newCustomerData.phone}
                onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
                placeholder="05xxxxxxxxx"
                required
              />
            </div>
            <div>
              <Label htmlFor="new_customer_address">Adres *</Label>
              <Textarea
                id="new_customer_address"
                value={newCustomerData.address}
                onChange={(e) => setNewCustomerData({...newCustomerData, address: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="new_customer_serial">Seri No (Opsiyonel)</Label>
              <Input
                id="new_customer_serial"
                value={newCustomerData.serial_number}
                onChange={(e) => setNewCustomerData({...newCustomerData, serial_number: e.target.value})}
                placeholder="Boş bırakılırsa otomatik atanır"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsNewCustomerDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                <Plus className="h-4 w-4 mr-2" />
                Müşteri Ekle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle>İşlem Düzenle</DialogTitle>
                <DialogDescription>
                  Satış işlemi bilgilerini güncelleyin.
                </DialogDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsProductOptionsOpen(true)}
                className="shrink-0"
                title="Ürün özellikleri ve ölçü listelerini düzenle"
                data-testid="edit-sale-edit-lists-btn"
              >
                <List className="h-4 w-4 mr-1" /> Listeleri Düzenle
              </Button>
            </div>
          </DialogHeader>
          <form onSubmit={handleUpdateTransaction} className="space-y-4">
            <div className="grid grid-cols-2 gap-4 print:block">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="account_id">Müşteri</Label>
                </div>
                <div className="relative">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Müşteri adı yazarak arayın..."
                        value={customerSearchTerm}
                        onChange={(e) => {
                          setCustomerSearchTerm(e.target.value);
                          setShowCustomerDropdown(e.target.value.length > 0);
                        }}
                        onFocus={() => {
                          // Focus olduğunda dropdown'ı aç, ama sadece customerSearchTerm boş değilse
                          if (customerSearchTerm.length > 0) {
                            setShowCustomerDropdown(true);
                          }
                        }}
                        className="w-full"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={openNewCustomerDialog}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Yeni
                    </Button>
                  </div>
                  
                  {/* Müşteri seçimi dropdown'u */}
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredCustomers.slice(0, 10).map((customer) => (
                        <div
                          key={customer.id}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
                          onClick={() => {
                            setFormData({...formData, account_id: customer.id});
                            setCustomerSearchTerm(`${customer.serial_number} - ${customer.name} ${customer.surname}`);
                            setShowCustomerDropdown(false);
                          }}
                        >
                          <span className="text-sm">
                            <span className="font-medium">{customer.serial_number}</span> - {customer.name} {customer.surname}
                          </span>
                          <span className="text-xs text-gray-500">{customer.phone || 'Tel yok'}</span>
                        </div>
                      ))}
                      {filteredCustomers.length > 10 && (
                        <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50">
                          {filteredCustomers.length - 10} müşteri daha var, aramayı daraltın
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Arama sonucu bulunamadığında veya geçersiz müşteri ID'si */}
                  {showCustomerDropdown && customerSearchTerm.length > 2 && filteredCustomers.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                      <div className="px-4 py-3 text-sm text-gray-500">
                        {formData.account_id && !customers.find(c => c.id === formData.account_id) ? 
                          "Bu işlem için kayıtlı müşteri bulunamadı. Lütfen yeni müşteri seçin." :
                          "Müşteri bulunamadı. Yeni müşteri eklemek için \"Yeni\" butonunu kullanın."
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <Label htmlFor="amount">Tutar (TL)</Label>
                <Input
                  type="text"
                  value={formatAmount(formData.amount)}
                  onChange={(e) => {
                    const numericValue = parseAmount(e.target.value);
                    setFormData({...formData, amount: numericValue});
                  }}
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 print:block">
              <div>
                <Label htmlFor="transaction_type">İşlem Türü</Label>
                <Select value={formData.transaction_type} onValueChange={(value) => setFormData({...formData, transaction_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="İşlem türü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Satış (Müşteri Borçlu)</SelectItem>
                    <SelectItem value="credit">Alış (Müşteri Alacaklı)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="production_line">Üretim Hattı *</Label>
                <Select value={formData.production_line} onValueChange={(value) => setFormData({...formData, production_line: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Üretim hattı seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lake">LAKE</SelectItem>
                    <SelectItem value="melamin">MELAMİN</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="payment_method">Ödeme Yöntemi *</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ödeme yöntemi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safe">Kasa</SelectItem>
                    <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                    <SelectItem value="mixed">Yarısı Kasa - Yarısı Kredi Kartı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ödeme Açıklaması */}
            <div>
              <Label htmlFor="payment_description">Ödeme Açıklaması</Label>
              <Textarea
                id="payment_description"
                value={formData.payment_description}
                onChange={(e) => setFormData({...formData, payment_description: e.target.value})}
                placeholder="Ödeme ile ilgili ek bilgiler (isteğe bağlı)"
                rows={2}
              />
            </div>

            {/* Ürün Özellikleri */}
            <div className="grid grid-cols-2 gap-4 print:block">
              <div>
                <Label htmlFor="model">Model</Label>
                {isCustomModel ? (
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    placeholder="Özel model adını yazın"
                    onBlur={() => {
                      if (!formData.model) {
                        setIsCustomModel(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.model} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomModel(true);
                        setFormData({...formData, model: ""});
                      } else {
                        setFormData({...formData, model: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Model seçin" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {modelOptions.map((model) => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label htmlFor="color">Renk</Label>
                {isCustomColor ? (
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    placeholder="Özel renk adını yazın"
                    onBlur={() => {
                      if (!formData.color) {
                        setIsCustomColor(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.color} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomColor(true);
                        setFormData({...formData, color: ""});
                      } else {
                        setFormData({...formData, color: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Renk seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color} value={color}>{color}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 print:block">
              <div>
                <Label htmlFor="door_handle">Kapı Kolu</Label>
                {isCustomDoorHandle ? (
                  <Input
                    value={formData.door_handle}
                    onChange={(e) => setFormData({...formData, door_handle: e.target.value})}
                    placeholder="Özel kapı kolu türünü yazın"
                    onBlur={() => {
                      if (!formData.door_handle) {
                        setIsCustomDoorHandle(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.door_handle} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomDoorHandle(true);
                        setFormData({...formData, door_handle: ""});
                      } else {
                        setFormData({...formData, door_handle: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kapı kolu seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {doorHandleOptions.map((handle) => (
                        <SelectItem key={handle} value={handle}>{handle}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label htmlFor="surface">Yüzey</Label>
                {isCustomSurface ? (
                  <Input
                    value={formData.surface}
                    onChange={(e) => setFormData({...formData, surface: e.target.value})}
                    placeholder="Özel yüzey türünü yazın"
                    onBlur={() => {
                      if (!formData.surface) {
                        setIsCustomSurface(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.surface} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomSurface(true);
                        setFormData({...formData, surface: ""});
                      } else {
                        setFormData({...formData, surface: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Yüzey seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {surfaceOptions.map((surface) => (
                        <SelectItem key={surface} value={surface}>{surface}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 print:block">
              <div>
                <Label htmlFor="installation">Montaj</Label>
                {isCustomInstallation ? (
                  <Input
                    value={formData.installation}
                    onChange={(e) => setFormData({...formData, installation: e.target.value})}
                    placeholder="Özel montaj türünü yazın"
                    onBlur={() => {
                      if (!formData.installation) {
                        setIsCustomInstallation(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.installation} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomInstallation(true);
                        setFormData({...formData, installation: ""});
                      } else {
                        setFormData({...formData, installation: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Montaj seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {installationOptions.map((installation) => (
                        <SelectItem key={installation} value={installation}>{installation}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label htmlFor="installation_location">Montaj Yeri</Label>
                {isCustomInstallationLocation ? (
                  <Input
                    value={formData.installation_location}
                    onChange={(e) => setFormData({...formData, installation_location: e.target.value})}
                    placeholder="Özel montaj yerini yazın"
                    onBlur={() => {
                      if (!formData.installation_location) {
                        setIsCustomInstallationLocation(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.installation_location} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomInstallationLocation(true);
                        setFormData({...formData, installation_location: ""});
                      } else {
                        setFormData({...formData, installation_location: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Montaj yeri seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {installationLocationOptions.map((location) => (
                        <SelectItem key={location} value={location}>{location}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 print:block">
              <div>
                <Label htmlFor="inner_frame">İç Pervaz</Label>
                {isCustomInnerFrame ? (
                  <Input
                    value={formData.inner_frame}
                    onChange={(e) => setFormData({...formData, inner_frame: e.target.value})}
                    placeholder="Özel iç pervaz türünü yazın"
                    onBlur={() => {
                      if (!formData.inner_frame) {
                        setIsCustomInnerFrame(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.inner_frame} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomInnerFrame(true);
                        setFormData({...formData, inner_frame: ""});
                      } else {
                        setFormData({...formData, inner_frame: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="İç pervaz seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {innerFrameOptions.map((frame) => (
                        <SelectItem key={frame} value={frame}>{frame}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label htmlFor="outer_frame">Dış Pervaz</Label>
                {isCustomOuterFrame ? (
                  <Input
                    value={formData.outer_frame}
                    onChange={(e) => setFormData({...formData, outer_frame: e.target.value})}
                    placeholder="Özel dış pervaz türünü yazın"
                    onBlur={() => {
                      if (!formData.outer_frame) {
                        setIsCustomOuterFrame(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.outer_frame} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomOuterFrame(true);
                        setFormData({...formData, outer_frame: ""});
                      } else {
                        setFormData({...formData, outer_frame: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Dış pervaz seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {outerFrameOptions.map((frame) => (
                        <SelectItem key={frame} value={frame}>{frame}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="details">Detay</Label>
              <Textarea
                id="details"
                value={formData.details}
                onChange={(e) => setFormData({...formData, details: e.target.value})}
                placeholder="Ek detaylar, özel istekler"
                rows={3}
              />
            </div>

            {/* Ölçü Tablosu */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <Label className="text-lg font-semibold">Ölçü Tablosu</Label>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="w-8 p-2 text-left font-medium">#</th>
                      <th className="w-20 p-2 text-left font-medium">En</th>
                      <th className="w-20 p-2 text-left font-medium">Boy</th>
                      <th className="w-20 p-2 text-left font-medium">Kasa</th>
                      <th className="w-24 p-2 text-left font-medium">Yönü</th>
                      <th className="w-20 p-2 text-left font-medium">Cam</th>
                      <th className="w-24 p-2 text-left font-medium">Oda</th>
                      <th className="flex-1 p-2 text-left font-medium">Açıklama</th>
                      <th className="w-14 p-2 text-center font-medium bg-purple-50">Adet</th>
                      <th className="w-20 p-2 text-left font-medium">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {measurements.map((row, index) => (
                      <tr key={row.id} className="border-t">
                        <td className="p-2 font-medium text-gray-500">{row.id}</td>
                        <td className="p-1">
                          {row.width === "özel" || (row.width && !widthOptions.includes(parseInt(row.width))) ? (
                            <Input
                              value={row.width === "özel" ? "" : row.width}
                              onChange={(e) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].width = e.target.value;
                                setMeasurements(newMeasurements);
                              }}
                              className="h-8 text-xs"
                              placeholder="Özel en ölçüsü"
                            />
                          ) : (
                            <Select
                              value={widthOptions.includes(parseInt(row.width)) ? row.width : ""}
                              onValueChange={(value) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].width = value;
                                setMeasurements(newMeasurements);
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="En" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60 overflow-y-auto">
                                {widthOptions.map((width) => (
                                  <SelectItem key={width} value={width.toString()}>{width}</SelectItem>
                                ))}
                                <SelectItem value="özel">Özel</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="p-1">
                          {row.height === "özel" || (row.height && !heightOptions.includes(parseInt(row.height))) ? (
                            <Input
                              value={row.height === "özel" ? "" : row.height}
                              onChange={(e) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].height = e.target.value;
                                setMeasurements(newMeasurements);
                              }}
                              className="h-8 text-xs"
                              placeholder="Özel boy ölçüsü"
                            />
                          ) : (
                            <Select
                              value={heightOptions.includes(parseInt(row.height)) ? row.height : ""}
                              onValueChange={(value) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].height = value;
                                setMeasurements(newMeasurements);
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Boy" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60 overflow-y-auto">
                                {heightOptions.map((height) => (
                                  <SelectItem key={height} value={height.toString()}>{height}</SelectItem>
                                ))}
                                <SelectItem value="özel">Özel</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="p-1">
                          {row.frame === "özel" || (row.frame && !frameOptions.includes(parseInt(row.frame))) ? (
                            <Input
                              value={row.frame === "özel" ? "" : row.frame}
                              onChange={(e) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].frame = e.target.value;
                                setMeasurements(newMeasurements);
                              }}
                              className="h-8 text-xs"
                              placeholder="Özel kasa ölçüsü"
                            />
                          ) : (
                            <Select
                              value={frameOptions.includes(parseInt(row.frame)) ? row.frame : ""}
                              onValueChange={(value) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].frame = value;
                                setMeasurements(newMeasurements);
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Kasa" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60 overflow-y-auto">
                                {frameOptions.map((frame) => (
                                  <SelectItem key={frame} value={frame.toString()}>{frame}</SelectItem>
                                ))}
                                <SelectItem value="özel">Özel</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="p-1">
                          {row.direction === "özel" || (row.direction && !directionOptions.includes(row.direction)) ? (
                            <Input
                              value={row.direction === "özel" ? "" : row.direction}
                              onChange={(e) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].direction = e.target.value;
                                setMeasurements(newMeasurements);
                              }}
                              className="h-8 text-xs"
                              placeholder="Özel yön"
                            />
                          ) : (
                            <Select
                              value={directionOptions.includes(row.direction) ? row.direction : ""}
                              onValueChange={(value) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].direction = value;
                                setMeasurements(newMeasurements);
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Yön" />
                              </SelectTrigger>
                              <SelectContent>
                                {directionOptions.map((direction) => (
                                  <SelectItem key={direction} value={direction}>{direction}</SelectItem>
                                ))}
                                <SelectItem value="özel">Özel</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="p-1">
                          {row.glass === "özel" || (row.glass && !glassOptions.includes(row.glass)) ? (
                            <Input
                              value={row.glass === "özel" ? "" : row.glass}
                              onChange={(e) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].glass = e.target.value;
                                setMeasurements(newMeasurements);
                              }}
                              className="h-8 text-xs"
                              placeholder="Özel cam türü"
                            />
                          ) : (
                            <Select
                              value={glassOptions.includes(row.glass) ? row.glass : ""}
                              onValueChange={(value) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].glass = value;
                                setMeasurements(newMeasurements);
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Cam" />
                              </SelectTrigger>
                              <SelectContent>
                                {glassOptions.map((glass) => (
                                  <SelectItem key={glass} value={glass}>{glass}</SelectItem>
                                ))}
                                <SelectItem value="özel">Özel</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="p-1">
                          {row.room === "özel" || (row.room && !roomOptions.includes(row.room)) ? (
                            <Input
                              value={row.room === "özel" ? "" : row.room}
                              onChange={(e) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].room = e.target.value;
                                setMeasurements(newMeasurements);
                              }}
                              className="h-8 text-xs"
                              placeholder="Özel oda türü"
                            />
                          ) : (
                            <Select
                              value={roomOptions.includes(row.room) ? row.room : ""}
                              onValueChange={(value) => {
                                const newMeasurements = [...measurements];
                                newMeasurements[index].room = value;
                                setMeasurements(newMeasurements);
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Oda" />
                              </SelectTrigger>
                              <SelectContent>
                                {roomOptions.map((room) => (
                                  <SelectItem key={room} value={room}>{room}</SelectItem>
                                ))}
                                <SelectItem value="özel">Özel</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="p-1">
                          <Input
                            placeholder="Açıklama"
                            value={row.description}
                            onChange={(e) => {
                              const newMeasurements = [...measurements];
                              newMeasurements[index].description = e.target.value;
                              setMeasurements(newMeasurements);
                            }}
                            className="h-8"
                          />
                        </td>
                        <td className="p-1 bg-purple-50">
                          <Input
                            type="number"
                            min="1"
                            value={row.quantity || 1}
                            onChange={(e) => {
                              const newMeasurements = [...measurements];
                              newMeasurements[index].quantity = parseInt(e.target.value) || 1;
                              setMeasurements(newMeasurements);
                            }}
                            className="h-8 w-12 text-center font-medium"
                          />
                        </td>
                        <td className="p-1">
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              title="Satırı kopyala"
                              onClick={() => {
                                const newId = Math.max(...measurements.map(m => m.id), 0) + 1;
                                const copy = { ...row, id: newId };
                                const newList = [...measurements];
                                newList.splice(index + 1, 0, copy);
                                setMeasurements(newList);
                              }}
                              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (measurements.length > 1) {
                                  setMeasurements(measurements.filter((_, i) => i !== index));
                                }
                              }}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={measurements.length <= 1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const newId = Math.max(...measurements.map(m => m.id), 0) + 1;
                      setMeasurements([...measurements, { 
                        id: newId, 
                        width: "", 
                        height: "", 
                        frame: "", 
                        direction: "", 
                        glass: "", 
                        room: "", 
                        description: "" 
                      }]);
                    }}
                    className="flex items-center space-x-1"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Yeni Ölçü Ekle</span>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Hedef Montaj Tarihi */}
            <div>
              <Label htmlFor="target_installation_date">Hedef Montaj Tarihi (Opsiyonel)</Label>
              <Input
                type="date"
                id="target_installation_date"
                value={formData.target_installation_date ? formData.target_installation_date.toISOString().split('T')[0] : ""}
                onChange={(e) => setFormData({...formData, target_installation_date: e.target.value ? new Date(e.target.value) : null})}
              />
              <p className="text-xs text-blue-600 mt-1">
                📅 Bu tarih ajanda ekranında görünecek ve montaj planlamasında kullanılacak
              </p>
            </div>
            
            {/* Nihai Müşteri */}
            <div>
              <Label htmlFor="edit_end_customer">Nihai Müşteri (İsteğe Bağlı)</Label>
              <Input
                id="edit_end_customer"
                value={formData.end_customer || ""}
                onChange={(e) => setFormData({...formData, end_customer: e.target.value})}
                placeholder="Müşterinizin müşterisi varsa buraya yazın..."
              />
            </div>
            
            <div>
              <Label htmlFor="description">İMALAT İÇİN ÖNEMLİ OLAN NOTLAR</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="İşlem açıklaması"
                required
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>İşlem ve İlişkili Kayıtları Sil</DialogTitle>
            <DialogDescription>
              <div className="space-y-2 text-left">
                <p className="font-medium text-red-600">⚠️ Bu işlem GERİ ALINAMAZ!</p>
                <p>Aşağıdaki kayıtlar SİLİNECEK:</p>
                <ul className="list-disc list-inside text-sm text-gray-600 ml-2">
                  <li>İşlem kaydı</li>
                  <li>İlgili üretim kayıtları</li>
                  <li>İlgili montaj programları</li>
                  <li>İlgili tahsilat makbuzları</li>
                  <li>İlgili ajanda notları</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          {transactionToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p><strong>Açıklama:</strong> {transactionToDelete.description}</p>
              <p><strong>Tutar:</strong> ₺{transactionToDelete.amount?.toLocaleString('tr-TR')}</p>
              <p><strong>Tarih:</strong> {new Date(transactionToDelete.created_at).toLocaleDateString('tr-TR')}</p>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteTransaction}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Transaction Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Satış İşlemi Detayları</DialogTitle>
            <DialogDescription>
              İşlem detaylarını görüntüleyin
            </DialogDescription>
          </DialogHeader>
          {currentTransaction && (
            <div className="space-y-6">
              {/* Müşteri Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Müşteri</Label>
                  <p className="text-lg font-semibold">{getAccountName(currentTransaction.account_id)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">İşlem Tarihi</Label>
                  <p className="text-lg">{new Date(currentTransaction.created_at).toLocaleString('tr-TR')}</p>
                </div>
              </div>

              {/* İşlem Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">İşlem Türü</Label>
                  <Badge variant={currentTransaction.transaction_type === 'debit' ? 'default' : 'secondary'} className="text-lg px-3 py-1">
                    {currentTransaction.transaction_type === 'debit' ? 'Satış' : 'İade/İndirim'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Tutar</Label>
                  <p className={`text-2xl font-bold ${currentTransaction.transaction_type === 'debit' ? 'text-green-600' : 'text-red-600'}`}>
                    {currentTransaction.transaction_type === 'debit' ? '+' : '-'}₺{Math.abs(currentTransaction.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Açıklama */}
              <div>
                <Label className="text-sm font-medium text-gray-600">Açıklama</Label>
                <p className="text-lg p-3 bg-gray-50 rounded-lg">{currentTransaction.description}</p>
              </div>

              {/* Ürün Özellikleri */}
              {(currentTransaction.model || currentTransaction.color || currentTransaction.door_handle) && (
                <div>
                  <Label className="text-sm font-medium text-gray-600 mb-2 block">Ürün Özellikleri</Label>
                  <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg">
                    {currentTransaction.model && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Model:</span>
                        <p className="text-lg">{currentTransaction.model}</p>
                      </div>
                    )}
                    {currentTransaction.color && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Renk:</span>
                        <p className="text-lg">{currentTransaction.color}</p>
                      </div>
                    )}
                    {currentTransaction.door_handle && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Kapı Kolu:</span>
                        <p className="text-lg">{currentTransaction.door_handle}</p>
                      </div>
                    )}
                    {currentTransaction.surface && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Yüzey:</span>
                        <p className="text-lg">{currentTransaction.surface}</p>
                      </div>
                    )}
                    {currentTransaction.installation && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Montaj:</span>
                        <p className="text-lg">{currentTransaction.installation}</p>
                      </div>
                    )}
                    {currentTransaction.installation_location && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Montaj Yeri:</span>
                        <p className="text-lg">{currentTransaction.installation_location}</p>
                      </div>
                    )}
                    {currentTransaction.inner_frame && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">İç Çerçeve:</span>
                        <p className="text-lg">{currentTransaction.inner_frame}</p>
                      </div>
                    )}
                    {currentTransaction.outer_frame && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Dış Çerçeve:</span>
                        <p className="text-lg">{currentTransaction.outer_frame}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ölçüler */}
              {currentTransaction.measurements && (
                <div>
                  <Label className="text-sm font-medium text-gray-600 mb-2 block">Ölçüler</Label>
                  <div className="p-3 bg-green-50 rounded-lg overflow-x-auto">
                    {parseMeasurements(currentTransaction.measurements).length > 0 ? (
                      <table className="min-w-full text-sm border border-gray-300">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-3 py-2 text-left">#</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">En</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Boy</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Kasa</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Yönü</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Cam</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Oda</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseMeasurements(currentTransaction.measurements).map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-3 py-2">{idx + 1}</td>
                              <td className="border border-gray-300 px-3 py-2">{row.width || '-'}</td>
                              <td className="border border-gray-300 px-3 py-2">{row.height || '-'}</td>
                              <td className="border border-gray-300 px-3 py-2">{row.thickness || row.frame || '-'}</td>
                              <td className="border border-gray-300 px-3 py-2">{row.direction || '-'}</td>
                              <td className="border border-gray-300 px-3 py-2">{row.glass || '-'}</td>
                              <td className="border border-gray-300 px-3 py-2">{row.room || '-'}</td>
                              <td className="border border-gray-300 px-3 py-2">{row.notes || row.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-gray-500">Ölçü bilgisi yok</p>
                    )}
                  </div>
                </div>
              )}

              {/* Detaylar */}
              {currentTransaction.details && (
                <div>
                  <Label className="text-sm font-medium text-gray-600">Detaylar</Label>
                  <p className="text-lg p-3 bg-yellow-50 rounded-lg">{currentTransaction.details}</p>
                </div>
              )}

              {/* Üretim ve Ödeme Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Üretim Hattı</Label>
                  <Badge variant="outline" className="text-lg px-3 py-1">
                    {currentTransaction.production_line === 'lake' ? 'Lake' : 'Melamin'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Ödeme Yöntemi</Label>
                  <p className="text-lg">{currentTransaction.payment_method === 'safe' ? 'Kasa' : currentTransaction.payment_method === 'credit_card' ? 'Kredi Kartı' : 'Banka Havalesi'}</p>
                </div>
              </div>

              {currentTransaction.payment_description && (
                <div>
                  <Label className="text-sm font-medium text-gray-600">Ödeme Açıklaması</Label>
                  <p className="text-lg p-3 bg-purple-50 rounded-lg">{currentTransaction.payment_description}</p>
                </div>
              )}

              {/* Hedef Montaj Tarihi */}
              {currentTransaction.target_installation_date && (
                <div>
                  <Label className="text-sm font-medium text-gray-600">Hedef Montaj Tarihi</Label>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-lg px-3 py-1 bg-blue-50 text-blue-700">
                      📅 {new Date(currentTransaction.target_installation_date).toLocaleDateString('tr-TR')}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      (Ajanda ve montaj planlamasında kullanılır)
                    </span>
                  </div>
                </div>
              )}

              {/* Satış Personeli Bilgisi */}
              <div>
                <Label className="text-sm font-medium text-gray-600">Satış Personeli</Label>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="text-lg px-3 py-1 bg-blue-50 text-blue-700">
                    👤 {getSalesPersonName(currentTransaction.sales_person_id)}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sipariş Detayı Dialog - Üretim sayfasındakiyle aynı */}
      {showSaleDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b no-print">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Sipariş Detayları</h2>
                {saleDetailProduction && <p className="text-sm text-gray-500 mt-1">{saleDetailProduction.customer_name}</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSaleDetailPrint} data-testid="sale-detail-print-btn"><Printer className="h-4 w-4 mr-1" />PDF Yazdır</Button>
                <button onClick={() => { setShowSaleDetailModal(false); setSaleDetailProduction(null); setSaleDetailTransaction(null); setSaleDetailCustomer(null); }} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
              </div>
            </div>

            {loadingSaleDetail ? (
              <div className="flex items-center justify-center p-20"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>
            ) : saleDetailProduction && saleDetailTransaction ? (
              <div className="p-6 space-y-4 print-area" id="sale-detail-print-area">
                {/* Müşteri Bilgileri */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Müşteri Bilgileri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div><p className="font-medium text-sm"><strong>Müşteri:</strong> {saleDetailProduction.customer_name}</p></div>
                    <div><p className="font-medium text-sm"><strong>Cari Kod:</strong> {saleDetailCustomer?.serial_number || 'N/A'}</p></div>
                    <div><p className="font-medium text-sm"><strong>Telefon:</strong> {saleDetailCustomer?.phone || 'N/A'}</p></div>
                    <div><p className="font-medium text-sm"><strong>Üretim Hattı:</strong> {(saleDetailProduction.production_line || '').toUpperCase()}</p></div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 mt-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Adres:</strong></p>
                      <p className="text-sm bg-white p-2 rounded border">{saleDetailCustomer?.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Sipariş Bilgileri */}
                <div className="bg-green-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Sipariş Bilgileri</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div><p className="font-medium text-sm"><strong>Sipariş Tarihi:</strong> {new Date(saleDetailTransaction.created_at).toLocaleDateString('tr-TR')}</p></div>
                    <div><p className="font-medium text-sm"><strong>Tutar:</strong> ₺{(saleDetailTransaction.amount || 0).toLocaleString('tr-TR')}</p></div>
                    <div>
                      {saleDetailProduction.target_installation_date ? (
                        <p className="font-medium text-sm"><strong>Hedef Montaj:</strong> <span className="text-blue-600 ml-1">{new Date(saleDetailProduction.target_installation_date).toLocaleDateString('tr-TR')}</span></p>
                      ) : (
                        <p className="font-medium text-sm text-gray-500"><strong>Hedef Montaj:</strong> Belirlenmemiş</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ürün Özellikleri */}
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Ürün Özellikleri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {saleDetailTransaction.model && (<div><p className="font-medium text-sm"><strong>Model:</strong> {saleDetailTransaction.model}</p></div>)}
                    {saleDetailTransaction.color && (<div><p className="font-medium text-sm"><strong>Renk:</strong> {saleDetailTransaction.color}</p></div>)}
                    {saleDetailTransaction.door_handle && (<div><p className="font-medium text-sm"><strong>Kapı Kolu:</strong> {saleDetailTransaction.door_handle}</p></div>)}
                    {saleDetailTransaction.surface && (<div><p className="font-medium text-sm"><strong>Yüzey:</strong> {saleDetailTransaction.surface}</p></div>)}
                    {saleDetailTransaction.installation && (<div><p className="font-medium text-sm"><strong>Montaj:</strong> {saleDetailTransaction.installation}</p></div>)}
                    {saleDetailTransaction.installation_location && (<div><p className="font-medium text-sm"><strong>Montaj Yeri:</strong> {saleDetailTransaction.installation_location}</p></div>)}
                    {saleDetailTransaction.inner_frame && (<div><p className="font-medium text-sm"><strong>İç Pervaz:</strong> {saleDetailTransaction.inner_frame}</p></div>)}
                    {saleDetailTransaction.outer_frame && (<div><p className="font-medium text-sm"><strong>Dış Pervaz:</strong> {saleDetailTransaction.outer_frame}</p></div>)}
                  </div>
                </div>

                {/* Nihai Müşteri */}
                {saleDetailTransaction.end_customer && (
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <h3 className="font-semibold mb-2 text-sm">Nihai Müşteri</h3>
                    <p className="font-medium text-sm">{saleDetailTransaction.end_customer}</p>
                  </div>
                )}

                {/* Ölçü Tabloları */}
                {saleDetailTransaction.measurements && (() => {
                  let measurements = [];
                  try {
                    measurements = JSON.parse(saleDetailTransaction.measurements);
                  } catch (e) {
                    return null;
                  }
                  const validMeasurements = measurements.filter(m => 
                    m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description
                  );
                  if (validMeasurements.length === 0) return null;
                  return (
                    <div className="space-y-3">
                      {/* Orijinal Ölçüler */}
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <h3 className="font-semibold mb-2 text-sm">Orijinal Ölçüler</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Kasa</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Oda</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((m, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-300 p-1 font-medium">
                                    {index + 1}
                                    {m.quantity && m.quantity > 1 && (
                                      <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">x{m.quantity}</span>
                                    )}
                                  </td>
                                  <td className="border border-gray-300 p-1">{m.width || '-'}</td>
                                  <td className="border border-gray-300 p-1">{m.height || '-'}</td>
                                  <td className="border border-gray-300 p-1">{m.frame || '-'}</td>
                                  <td className="border border-gray-300 p-1">{m.direction || '-'}</td>
                                  <td className="border border-gray-300 p-1">{m.glass || '-'}</td>
                                  <td className="border border-gray-300 p-1">{m.room || '-'}</td>
                                  <td className="border border-gray-300 p-1">{m.description || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* KANAT ÖLÇÜSÜ */}
                      <div className="bg-red-50 p-3 rounded-lg">
                        <h3 className="font-semibold mb-2 text-sm">KANAT ÖLÇÜSÜ</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((m, index) => {
                                const originalWidth = parseFloat(m.width) || 0;
                                const originalHeight = parseFloat(m.height) || 0;
                                const productionWidth = originalWidth > 6 ? originalWidth - 6 : originalWidth;
                                const productionHeight = originalHeight > 4 ? originalHeight - 4 : originalHeight;
                                return (
                                  <tr key={index}>
                                    <td className="border border-gray-300 p-1 font-medium">
                                      {index + 1}
                                      {m.quantity && m.quantity > 1 && (
                                        <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">x{m.quantity}</span>
                                      )}
                                    </td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">{productionWidth > 0 ? productionWidth : '-'}</td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">{productionHeight > 0 ? productionHeight : '-'}</td>
                                    <td className="border border-gray-300 p-1">{m.direction || '-'}</td>
                                    <td className="border border-gray-300 p-1">{m.glass || '-'}</td>
                                    <td className="border border-gray-300 p-1">{m.description || '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* İmalat Notları */}
                {(saleDetailTransaction.description || saleDetailProduction.notes) && (
                  <div className="bg-orange-50 p-2 rounded-lg border-2 border-orange-300">
                    <h3 className="font-semibold mb-1 text-sm">İmalat İçin Önemli Notlar</h3>
                    <p className="text-xs font-medium whitespace-pre-wrap">{saleDetailTransaction.description || saleDetailProduction.notes}</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Üretim Sipariş Detayı Modal - Üretim sayfasındaki modal birebir kopya */}
      <Dialog open={isSalesProdDetailOpen} onOpenChange={setIsSalesProdDetailOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="no-print">
                <DialogTitle>Sipariş Detayları</DialogTitle>
                <DialogDescription>
                  {salesProdDetailProduction && `${salesProdDetailProduction.customer_name} - Sipariş ve Üretim Bilgileri`}
                </DialogDescription>
              </div>
              <div className="flex gap-2 no-print">
                <Button onClick={handleSalesProdDetailPrintPDF} variant="outline" data-testid="sales-prod-pdf-print-btn">
                  <Printer className="h-4 w-4 mr-2" />
                  PDF Yazdır
                </Button>
                <Button onClick={handleSalesProdDetailDownloadPDF} variant="outline" data-testid="sales-prod-pdf-download-btn">
                  <Download className="h-4 w-4 mr-2" />
                  PDF İndir
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {isSalesProdDetailLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium">Sipariş detayları yükleniyor...</p>
              <p className="text-sm text-muted-foreground">Lütfen bekleyin</p>
            </div>
          ) : salesProdDetailProduction && salesProdDetailTransaction && salesProdDetailCustomer ? (
            <div className="space-y-4 print-area" id="sales-uretim-print-area">
              <div className="print-header-section">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Müşteri Bilgileri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div><p className="font-medium text-sm"><strong>Müşteri:</strong> {salesProdDetailProduction.customer_name}</p></div>
                    <div><p className="font-medium text-sm"><strong>Cari Kod:</strong> {salesProdDetailCustomer?.serial_number || 'N/A'}</p></div>
                    <div>
                      <p className="font-medium text-sm">
                        <strong>Telefon:</strong> 
                        <a href={`tel:${salesProdDetailCustomer?.phone}`} className="text-blue-600 hover:underline cursor-pointer ml-1">
                          📞 {salesProdDetailCustomer?.phone || 'N/A'}
                        </a>
                      </p>
                    </div>
                    <div><p className="font-medium text-sm"><strong>Üretim Hattı:</strong> {salesProdDetailProduction.production_line?.toUpperCase()}</p></div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 mt-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Adres:</strong></p>
                      <p className="text-sm bg-white p-2 rounded border">{salesProdDetailCustomer?.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Sipariş Bilgileri</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div><p className="font-medium text-sm"><strong>Sipariş Tarihi:</strong> {new Date(salesProdDetailTransaction.created_at).toLocaleDateString('tr-TR')}</p></div>
                    <div><p className="font-medium text-sm"><strong>Durum:</strong> {salesProdDetailProduction.status?.toUpperCase()}</p></div>
                    <div>
                      {salesProdDetailProduction.target_installation_date ? (
                        <p className="font-medium text-sm">
                          <strong>🎯 Hedef Montaj:</strong> 
                          <span className="text-blue-600 ml-1">{new Date(salesProdDetailProduction.target_installation_date).toLocaleDateString('tr-TR')}</span>
                        </p>
                      ) : (
                        <p className="font-medium text-sm text-gray-500"><strong>🎯 Hedef Montaj:</strong> Belirlenmemiş</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Ürün Özellikleri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {salesProdDetailTransaction.model && (<div><p className="font-medium text-sm"><strong>Model:</strong> {salesProdDetailTransaction.model}</p></div>)}
                    {salesProdDetailTransaction.color && (<div><p className="font-medium text-sm"><strong>Renk:</strong> {salesProdDetailTransaction.color}</p></div>)}
                    {salesProdDetailTransaction.door_handle && (<div><p className="font-medium text-sm"><strong>Kapı Kolu:</strong> {salesProdDetailTransaction.door_handle}</p></div>)}
                    {salesProdDetailTransaction.surface && (<div><p className="font-medium text-sm"><strong>Yüzey:</strong> {salesProdDetailTransaction.surface}</p></div>)}
                    {salesProdDetailTransaction.installation && (<div><p className="font-medium text-sm"><strong>Montaj:</strong> {salesProdDetailTransaction.installation}</p></div>)}
                    {salesProdDetailTransaction.installation_location && (<div><p className="font-medium text-sm"><strong>Montaj Yeri:</strong> {salesProdDetailTransaction.installation_location}</p></div>)}
                    {salesProdDetailTransaction.inner_frame && (<div><p className="font-medium text-sm"><strong>İç Pervaz:</strong> {salesProdDetailTransaction.inner_frame}</p></div>)}
                    {salesProdDetailTransaction.outer_frame && (<div><p className="font-medium text-sm"><strong>Dış Pervaz:</strong> {salesProdDetailTransaction.outer_frame}</p></div>)}
                  </div>
                </div>

                {salesProdDetailTransaction.end_customer && (
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <h3 className="font-semibold mb-2 text-sm">🏢 Nihai Müşteri</h3>
                    <p className="font-medium text-sm">{salesProdDetailTransaction.end_customer}</p>
                  </div>
                )}
              </div>

              <div className="print-measurements-section">
                {salesProdDetailTransaction.measurements && (() => {
                  let measurements = [];
                  try { measurements = JSON.parse(salesProdDetailTransaction.measurements); } catch (e) { return null; }
                  const validMeasurements = measurements.filter(m => m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description);
                  if (validMeasurements.length === 0) return null;
                  return (
                    <div className="space-y-3">
                      <div className="bg-blue-50 p-3 rounded-lg measurement-table">
                        <h3 className="font-semibold mb-2 text-sm">Orijinal Ölçüler</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Kasa</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Oda</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((measurement, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-300 p-1 font-medium">
                                    {index + 1}
                                    {measurement.quantity && measurement.quantity > 1 && (
                                      <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">x{measurement.quantity}</span>
                                    )}
                                  </td>
                                  <td className="border border-gray-300 p-1">{measurement.width || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.height || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.frame || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.room || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="bg-red-50 p-3 rounded-lg measurement-table">
                        <h3 className="font-semibold mb-2 text-sm">KANAT ÖLÇÜSÜ</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((measurement, index) => {
                                const ow = parseFloat(measurement.width) || 0;
                                const oh = parseFloat(measurement.height) || 0;
                                const pw = ow > 6 ? ow - 6 : ow;
                                const ph = oh > 4 ? oh - 4 : oh;
                                return (
                                  <tr key={index}>
                                    <td className="border border-gray-300 p-1 font-medium">
                                      {index + 1}
                                      {measurement.quantity && measurement.quantity > 1 && (
                                        <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">x{measurement.quantity}</span>
                                      )}
                                    </td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">{pw > 0 ? pw : '-'}</td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">{ph > 0 ? ph : '-'}</td>
                                    <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                    <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                    <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="print-notes-section">
                {salesProdDetailProduction.notes && (
                  <div className="bg-orange-50 p-2 rounded-lg border-2 border-orange-300">
                    <h3 className="font-semibold mb-1 text-sm">⚠️ İmalat İçin Önemli Notlar</h3>
                    <p className="text-xs font-medium whitespace-pre-wrap">{salesProdDetailProduction.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-yellow-600 mb-4" />
              <p className="text-lg font-medium">Sipariş detayları yüklenemedi</p>
              <p className="text-sm text-muted-foreground">Lütfen tekrar deneyin</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

// Purchases Component (Tedarikçi Alışları)
const Purchases = ({ sessionToken }) => {
  const [transactions, setTransactions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [formData, setFormData] = useState({
    account_id: "",
    transaction_type: "debit",
    amount: "",
    description: "",
    model: "",
    color: "",
    door_handle: "",
    surface: "",
    installation: "",
    installation_location: "",
    inner_frame: "",
    outer_frame: "",
    measurements: "",
    details: ""
  });

  const PURCHASES_PER_PAGE = 50;
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [purchaseFetching, setPurchaseFetching] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchTransactions(1, purchaseSearch), purchaseSearch ? 400 : 0);
    return () => clearTimeout(t);
  }, [purchaseSearch]);

  const fetchTransactions = async (page = purchasePage, q = purchaseSearch) => {
    try {
      setPurchaseFetching(true);
      const response = await axios.get(`${API}/transactions`, {
        params: {
          session_token: sessionToken,
          account_type: 'supplier',
          limit: PURCHASES_PER_PAGE,
          skip: (page - 1) * PURCHASES_PER_PAGE,
          q: q.trim()
        }
      });
      setTransactions(response.data.transactions || []);
      setPurchaseTotal(response.data.total || 0);
      setPurchasePage(page);
      setPurchaseFetching(false);
      setLoading(false);
    } catch (error) {
      console.error("Mal alış işlemleri yüklenirken hata:", error);
      toast.error("Mal alış işlemleri yüklenirken hata oluştu");
      setPurchaseFetching(false);
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        params: { session_token: sessionToken }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error("Tedarikçiler yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/transactions`, {
        ...formData,
        account_type: "supplier",
        amount: parseFloat(formData.amount),
        session_token: sessionToken
      });
      toast.success("Mal alış işlemi eklendi");
      setIsDialogOpen(false);
      setFormData({
        account_id: "",
        transaction_type: "debit",
        amount: "",
        description: "",
        model: "",
        color: "",
        door_handle: "",
        surface: "",
        installation: "",
        installation_location: "",
        inner_frame: "",
        outer_frame: "",
        measurements: "",
        details: ""
      });
      fetchTransactions();
      fetchSuppliers();
    } catch (error) {
      console.error("Mal alış işlemi eklenirken hata:", error);
      toast.error("Mal alış işlemi eklenirken hata oluştu");
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      account_id: transaction.account_id,
      transaction_type: transaction.transaction_type,
      amount: transaction.amount,
      description: transaction.description,
      model: transaction.model || "",
      color: transaction.color || "",
      door_handle: transaction.door_handle || "",
      surface: transaction.surface || "",
      installation: transaction.installation || "",
      installation_location: transaction.installation_location || "",
      inner_frame: transaction.inner_frame || "",
      outer_frame: transaction.outer_frame || "",
      details: transaction.details || "",
      payment_method: transaction.payment_method || ""
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/transactions/${editingTransaction.id}`, {
        ...formData,
        account_type: "supplier",
        amount: parseFloat(formData.amount)
      }, {
        params: { session_token: sessionToken }
      });
      toast.success("İşlem güncellendi");
      setIsEditDialogOpen(false);
      setEditingTransaction(null);
      fetchTransactions();
      setFormData({
        account_id: "",
        transaction_type: "debit",
        amount: "",
        description: "",
        model: "",
        color: "",
        door_handle: "",
        surface: "",
        installation: "",
        installation_location: "",
        inner_frame: "",
        outer_frame: "",
        details: "",
        payment_method: ""
      });
    } catch (error) {
      console.error("İşlem güncellenirken hata:", error);
      toast.error("İşlem güncellenirken hata oluştu");
    }
  };

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    
    try {
      const response = await axios.delete(`${API}/transactions/${transactionToDelete.id}`, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.summary) {
          console.log("Silinen ilişkili kayıtlar:", response.data.summary);
        }
      } else {
        toast.success("İşlem ve ilişkili kayıtlar silindi");
      }
      
      setDeleteConfirmOpen(false);
      setTransactionToDelete(null);
      fetchTransactions();
    } catch (error) {
      console.error("İşlem silinirken hata:", error);
      const errorMessage = error.response?.data?.detail || "İşlem silinirken hata oluştu";
      toast.error(errorMessage);
    }
  };

  const openDeleteConfirm = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteConfirmOpen(true);
  };

  const getAccountName = (accountId) => {
    const supplier = suppliers.find(s => s.id === accountId);
    return supplier ? `${supplier.name} ${supplier.surname}` : 'Bilinmeyen Tedarikçi';
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setFormData({
        account_id: "",
        transaction_type: "debit",
        amount: "",
        description: "",
        model: "",
        color: "",
        door_handle: "",
        surface: "",
        installation: "",
        installation_location: "",
        inner_frame: "",
        outer_frame: "",
        details: "",
        payment_method: ""
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mal Alış İşlemleri</h1>
          <p className="text-muted-foreground">Tedarikçi alışlarını yönetin</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Yeni Mal Alış
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Mal Alış İşlemi</DialogTitle>
              <DialogDescription>
                Tedarikçiden yapılan mal alış işlemini ekleyin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="account_id">Tedarikçi</Label>
                <Select value={formData.account_id} onValueChange={(value) => setFormData({...formData, account_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tedarikçi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.filter(supplier => supplier.id && supplier.id.trim() !== '').map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} {supplier.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="transaction_type">İşlem Türü</Label>
                <Select value={formData.transaction_type} onValueChange={(value) => setFormData({...formData, transaction_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Mal Alış (Biz Borçlu)</SelectItem>
                    <SelectItem value="credit">İade/İndirim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Tutar</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full">Ekle</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle>Mal Alış İşlemleri</CardTitle>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Tedarikçi, açıklama, model ara..."
              value={purchaseSearch}
              onChange={(e) => setPurchaseSearch(e.target.value)}
              className="pl-8"
              data-testid="purchases-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4" data-testid="purchases-list">
            {transactions.length === 0 && (
              <p className="text-center py-8 text-sm text-gray-500" data-testid="purchases-empty">Kayıt bulunamadı</p>
            )}
            {transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-3 h-3 rounded-full ${transaction.transaction_type === 'debit' ? 'bg-red-500' : 'bg-orange-500'}`} />
                  <div>
                    <p className="font-medium">{getAccountName(transaction.account_id)}</p>
                    <p className="text-sm text-muted-foreground">{transaction.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(transaction.created_at).toLocaleDateString('tr-TR')} - Tedarikçi Alışı
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center justify-end space-x-2 mb-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditTransaction(transaction)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openDeleteConfirm(transaction)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className={`font-bold ${transaction.transaction_type === 'debit' ? 'text-red-600' : 'text-orange-600'}`}>
                    {transaction.transaction_type === 'debit' ? '+' : '-'}₺{Math.abs(transaction.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                  <Badge variant={transaction.transaction_type === 'debit' ? 'destructive' : 'secondary'}>
                    {transaction.transaction_type === 'debit' ? 'Mal Alış' : 'İade/İndirim'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
          <PaginationBar
            page={purchasePage}
            total={purchaseTotal}
            perPage={PURCHASES_PER_PAGE}
            onPageChange={(p) => fetchTransactions(p, purchaseSearch)}
            loading={purchaseFetching}
            label="mal alış"
            search={purchaseSearch.trim()}
            testIdPrefix="purchases-pagination"
          />
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Mal Alış İşlemi Düzenle</DialogTitle>
            <DialogDescription>
              Tedarikçi alış işlemi bilgilerini güncelleyin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateTransaction} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="account_id">Tedarikçi</Label>
                <Select value={formData.account_id} onValueChange={(value) => setFormData({...formData, account_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tedarikçi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.filter(supplier => supplier.id && supplier.id.trim() !== '').map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} {supplier.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Tutar</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Açıklama</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>İşlem ve İlişkili Kayıtları Sil</DialogTitle>
            <DialogDescription>
              <div className="space-y-2 text-left">
                <p className="font-medium text-red-600">⚠️ Bu işlem GERİ ALINAMAZ!</p>
                <p>Aşağıdaki kayıtlar SİLİNECEK:</p>
                <ul className="list-disc list-inside text-sm text-gray-600 ml-2">
                  <li>İşlem kaydı</li>
                  <li>İlgili üretim kayıtları</li>
                  <li>İlgili montaj programları</li>
                  <li>İlgili tahsilat makbuzları</li>
                  <li>İlgili ajanda notları</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          {transactionToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p><strong>Açıklama:</strong> {transactionToDelete.description}</p>
              <p><strong>Tutar:</strong> ₺{transactionToDelete.amount?.toLocaleString('tr-TR')}</p>
              <p><strong>Tarih:</strong> {new Date(transactionToDelete.created_at).toLocaleDateString('tr-TR')}</p>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteTransaction}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Payment Receipts Component (Tedarikçi Ödemeleri)
const PaymentReceipts = () => {
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [bayiAccounts, setBayiAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Session token'ı localStorage'dan al
  const sessionToken = localStorage.getItem('session_token');
  // Tedarikçi arama state'leri
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [formData, setFormData] = useState({
    account_type: "supplier", // "supplier" veya "bayi"
    supplier_id: "",
    account_id: "", // Genel account ID
    amount: "",
    payment_method: "safe",
    description: ""
  });

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
    fetchBayiAccounts();
  }, []);

  const fetchPayments = async () => {
    try {
      // Session token al
      const token = localStorage.getItem('session_token');
      
      // Tedarikçi ödemelerini getir
      const paymentResponse = await axios.get(`${API}/payment-receipts`);
      const supplierPayments = paymentResponse.data;
      
      // Maaş ödemelerini getir
      const salaryResponse = await axios.get(`${API}/salary-payments-list`, {
        params: { session_token: token }
      });
      const salaryPayments = salaryResponse.data;
      
      // İki listeyi birleştir ve tarihe göre sırala
      const allPayments = [...supplierPayments, ...salaryPayments].sort((a, b) => {
        const dateA = new Date(a.payment_date || a.transaction_date);
        const dateB = new Date(b.payment_date || b.transaction_date);
        return dateB - dateA; // En yeni en üstte
      });
      
      setPayments(allPayments);
      setLoading(false);
    } catch (error) {
      console.error("Ödeme kayıtları yüklenirken hata:", error);
      toast.error("Ödeme kayıtları yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error("Tedarikçiler yüklenirken hata:", error);
    }
  };

  const fetchBayiAccounts = async () => {
    try {
      const response = await axios.get(`${API}/bayiler`);
      setBayiAccounts(response.data);
    } catch (error) {
      console.error("Bayi hesapları yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // account_type'a göre account_id belirleme
      const accountId = formData.account_type === "supplier" ? formData.supplier_id : formData.account_id;
      
      await axios.post(`${API}/payment-receipts`, {
        account_id: accountId,
        account_type: formData.account_type,
        supplier_id: formData.account_type === "supplier" ? formData.supplier_id : null, // Geriye uyumluluk
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        description: formData.description
      });
      toast.success("Ödeme kaydı oluşturuldu");
      setIsDialogOpen(false);
      setFormData({
        account_type: "supplier",
        supplier_id: "",
        account_id: "",
        amount: "",
        payment_method: "safe",
        description: ""
      });
      setSupplierSearchTerm("");
      setShowSupplierDropdown(false);
      fetchPayments();
      fetchSuppliers();
      fetchBayiAccounts();
    } catch (error) {
      console.error("Ödeme kaydı oluşturulurken hata:", error);
      toast.error("Ödeme kaydı oluşturulurken hata oluştu");
    }
  };
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setFormData({
      supplier_id: payment.supplier_id,
      amount: payment.amount,
      payment_method: payment.payment_method,
      description: payment.description
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    setPendingAction({
      type: 'edit',
      data: {
        ...formData,
        amount: parseFloat(formData.amount)
      }
    });
    setIsPasswordDialogOpen(true);
  };

  const handleDeletePayment = async () => {
    setPendingAction({
      type: 'delete',
      payment: paymentToDelete
    });
    setDeleteConfirmOpen(false);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async (password) => {
    try {
      if (pendingAction.type === 'delete') {
        await axios.delete(`${API}/payment-receipts/${pendingAction.payment.id}`, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Ödeme silindi");
        setPaymentToDelete(null);
        fetchPayments();
      } else if (pendingAction.type === 'edit') {
        await axios.put(`${API}/payment-receipts/${editingPayment.id}`, pendingAction.data, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Ödeme güncellendi");
        setIsEditDialogOpen(false);
        setEditingPayment(null);
        fetchPayments();
        setFormData({
          supplier_id: "",
          amount: "",
          payment_method: "cash",
          description: ""
        });
      }
      
      setIsPasswordDialogOpen(false);
      setPendingAction(null);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Güvenlik şifresi hatalı!");
      } else {
        console.error("İşlem sırasında hata:", error);
        toast.error("İşlem sırasında hata oluştu");
      }
    }
  };

  const openDeleteConfirm = (payment) => {
    setPaymentToDelete(payment);
    setDeleteConfirmOpen(true);
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'cash': return 'Nakit';
      case 'bank': return 'Banka';
      case 'safe': return 'Kasa';
      default: return method;
    }
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setFormData({
        account_type: "supplier",
        supplier_id: "",
        account_id: "",
        amount: "",
        payment_method: "safe",
        description: ""
      });
      setSupplierSearchTerm("");
      setShowSupplierDropdown(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ödeme Kayıtları</h1>
          <p className="text-muted-foreground">Tedarikçi ödemelerini yönetin</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button>
              <CreditCard className="h-4 w-4 mr-2" />
              Yeni Ödeme
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Ödeme Kaydı</DialogTitle>
              <DialogDescription>
                Tedarikçiye veya bayiye yapılan ödeme işlemini kaydedin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Hesap Tipi Seçimi */}
              <div>
                <Label htmlFor="account_type">Hesap Tipi</Label>
                <Select value={formData.account_type} onValueChange={(value) => setFormData({...formData, account_type: value, supplier_id: "", account_id: ""})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Hesap tipi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplier">Tedarikçi</SelectItem>
                    <SelectItem value="bayi">Bayi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Tedarikçi Seçimi - Yazarak Arama */}
              {formData.account_type === "supplier" && (
                <div>
                  <Label htmlFor="supplier_id">Tedarikçi</Label>
                  <div className="relative">
                    <Input
                      placeholder="Tedarikçi adı, soyadı veya telefon ile ara..."
                      value={supplierSearchTerm}
                      onChange={(e) => {
                        setSupplierSearchTerm(e.target.value);
                        setShowSupplierDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowSupplierDropdown(supplierSearchTerm.length > 0)}
                      className="w-full"
                    />
                    {showSupplierDropdown && supplierSearchTerm.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {suppliers
                          .filter(supplier => supplier.id && supplier.id.trim() !== '')
                          .filter(supplier => 
                            `${supplier.name} ${supplier.surname} ${supplier.phone}`.toLowerCase()
                            .includes(supplierSearchTerm.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((supplier) => (
                            <div
                              key={supplier.id}
                              onClick={() => {
                                setFormData({...formData, supplier_id: supplier.id});
                                setSupplierSearchTerm(`${supplier.name} ${supplier.surname}`);
                                setShowSupplierDropdown(false);
                              }}
                              className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium">{supplier.name} {supplier.surname}</p>
                                  <p className="text-sm text-gray-600">📞 {supplier.phone}</p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-sm font-medium ${supplier.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    Bakiye: ₺{supplier.balance.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        {suppliers
                          .filter(supplier => supplier.id && supplier.id.trim() !== '')
                          .filter(supplier => 
                            `${supplier.name} ${supplier.surname} ${supplier.phone}`.toLowerCase()
                            .includes(supplierSearchTerm.toLowerCase())
                          ).length === 0 && (
                          <div className="p-3 text-gray-500 text-center">
                            Tedarikçi bulunamadı
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Bayi Seçimi */}
              {formData.account_type === "bayi" && (
                <div>
                  <Label htmlFor="account_id">Bayi</Label>
                  <Select value={formData.account_id} onValueChange={(value) => setFormData({...formData, account_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Bayi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {bayiAccounts.map((bayi) => (
                        <SelectItem key={bayi.id} value={bayi.id}>
                          {bayi.name} {bayi.surname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="amount">Ödeme Tutarı</Label>
                <Input
                  id="amount"
                  type="text"
                  value={formData.amount ? formatNumberWithThousandsSeparator(formData.amount) : ''}
                  onChange={(e) => {
                    const formatted = formatNumberWithThousandsSeparator(e.target.value);
                    const numeric = parseFormattedNumber(formatted);
                    setFormData({...formData, amount: numeric.toString()});
                  }}
                  placeholder="0"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Örnek: 1.000.000
                </p>
              </div>
              <div>
                <Label htmlFor="payment_method">Ödeme Yöntemi</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safe">Kasa</SelectItem>
                    <SelectItem value="bank">Banka</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full">Ödeme Yap</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Liste Görünümü */}
      <div className="space-y-3">
        {payments.length > 0 ? payments.map((payment) => (
          <Card key={payment.id} className="w-full">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    payment.payment_type === 'salary' ? 'bg-purple-100' : 'bg-red-100'
                  }`}>
                    {payment.payment_type === 'salary' ? (
                      <Wallet className="h-5 w-5 text-purple-600" />
                    ) : (
                      <CreditCard className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold">{payment.supplier_name}</h3>
                      <Badge variant="outline" className={
                        payment.payment_type === 'salary' 
                          ? 'bg-purple-50 text-purple-700' 
                          : 'bg-red-50 text-red-700'
                      }>
                        {payment.payment_type === 'salary' ? 'Maaş' : getPaymentMethodText(payment.payment_method)}
                      </Badge>
                      {payment.payment_type === 'salary' && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          {payment.payment_method}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{payment.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(payment.payment_date).toLocaleDateString('tr-TR')} - 
                      {payment.payment_type === 'salary' ? ' Maaş Ödemesi' : ' Ödeme'} #{payment.id.slice(-6)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {payment.payment_type !== 'salary' && (
                    <div className="flex items-center justify-end space-x-2 mb-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditPayment(payment)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDeleteConfirm(payment)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Sil
                      </Button>
                    </div>
                  )}
                  <div className={`text-xl font-bold ${
                    payment.payment_type === 'salary' ? 'text-purple-600' : 'text-red-600'
                  }`}>
                    ₺{payment.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-500">Ödenen Tutar</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <div className="text-center py-12">
            <CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg text-gray-600">Henüz ödeme kaydı bulunmamaktadır</p>
            <p className="text-sm text-gray-500">İlk ödeme kaydınızı oluşturmak için "Yeni Ödeme" butonuna tıklayın</p>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ödeme Düzenle</DialogTitle>
            <DialogDescription>
              Ödeme bilgilerini güncelleyin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePayment} className="space-y-4">
            <div>
              <Label htmlFor="supplier_id">Tedarikçi</Label>
              <Select value={formData.supplier_id} onValueChange={(value) => setFormData({...formData, supplier_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Tedarikçi seçin" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.filter(supplier => supplier.id && supplier.id.trim() !== '').map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name} {supplier.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="amount">Ödeme Tutarı</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Açıklama</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ödeme Sil</DialogTitle>
            <DialogDescription>
              Bu ödemeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          {paymentToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p><strong>Açıklama:</strong> {paymentToDelete.description}</p>
              <p><strong>Tutar:</strong> ₺{paymentToDelete.amount?.toLocaleString('tr-TR')}</p>
              <p><strong>Tarih:</strong> {new Date(paymentToDelete.payment_date).toLocaleDateString('tr-TR')}</p>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeletePayment}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SecurePasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title="Güvenlik Onayı"
        message="Bu işlemi yapmak için admin şifresini girin"
      />
    </div>
  );
};

// Supplier Payments Component (Tedarikçi Tediye)
const SupplierPayments = ({ sessionToken }) => {
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bayiler, setBayiler] = useState([]);
  const [suppliersDebtSummary, setSuppliersDebtSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState(""); // Supplier filter state
  // Müşteri arama state'leri
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [formData, setFormData] = useState({
    payment_type: "customer", // "customer" or "bayi"
    supplier_id: "",
    customer_id: "",
    bayi_id: "",
    amount: "",
    description: ""
  });

  useEffect(() => {
    fetchSupplierPayments();
    fetchSuppliers();
    fetchCustomers();
    fetchBayiler();
    fetchSuppliersDebtSummary();
  }, []);

  // Dropdown dışına tıklama ile kapanması için
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCustomerDropdown]);

  // Dialog açılırken form'u temizle
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (open) {
      // Dialog açılırken tüm form state'lerini temizle
      setFormData({
        payment_type: "customer",
        supplier_id: "",
        customer_id: "",
        bayi_id: "",
        amount: "",
        description: ""
      });
      setCustomerSearchTerm("");
      setShowCustomerDropdown(false);
    }
  };

  const fetchSupplierPayments = async () => {
    try {
      const response = await axios.get(`${API}/supplier-payments`, {
        params: { session_token: sessionToken }
      });
      setPayments(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Tedarikçi ödemeleri yüklenirken hata:", error);
      toast.error("Tedarikçi ödemeleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        params: { session_token: sessionToken }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error("Tedarikçiler yüklenirken hata:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        params: { session_token: sessionToken }
      });
      // Sadece borcu olan müşterileri göster
      const customersWithDebt = response.data.filter(customer => customer.balance > 0);
      setCustomers(customersWithDebt);
    } catch (error) {
      console.error("Müşteriler yüklenirken hata:", error);
    }
  };

  const fetchBayiler = async () => {
    try {
      const response = await axios.get(`${API}/bayiler`);
      setBayiler(response.data);
    } catch (error) {
      console.error("Bayiler yüklenirken hata:", error);
    }
  };

  const fetchSuppliersDebtSummary = async () => {
    try {
      const response = await axios.get(`${API}/suppliers-debt-summary`);
      setSuppliersDebtSummary(response.data);
    } catch (error) {
      console.error("Tedarikçi borç özeti yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.payment_type === "bayi") {
        // Bayi ödeme - supplier-payments API'sine JSON olarak gönder
        await axios.post(`${API}/supplier-payments`, {
          supplier_id: formData.supplier_id,
          account_id: formData.bayi_id,
          account_type: "bayi",
          amount: parseFloat(formData.amount),
          description: formData.description
        }, {
          params: { session_token: sessionToken }
        });
        toast.success("Bayi tediyesi oluşturuldu");
      } else {
        // Müşteri ödeme (mevcut) - geriye uyumluluk için customer_id de gönder
        await axios.post(`${API}/supplier-payments`, {
          supplier_id: formData.supplier_id,
          account_id: formData.customer_id,
          account_type: "customer", 
          customer_id: formData.customer_id,  // Geriye uyumluluk
          amount: parseFloat(formData.amount),
          description: formData.description
        }, {
          params: { session_token: sessionToken }
        });
        toast.success("Müşteri tediyesi oluşturuldu");
      }
      
      setIsDialogOpen(false);
      setFormData({
        payment_type: "customer",
        supplier_id: "",
        customer_id: "",
        bayi_id: "",
        amount: "",
        description: ""
      });
      setCustomerSearchTerm("");
      setShowCustomerDropdown(false);
      fetchSupplierPayments();
      fetchCustomers(); // Müşteri borçları güncellendiği için
      fetchBayiler(); // Bayi borçları güncellendiği için
      fetchSuppliersDebtSummary();
    } catch (error) {
      console.error("Ödeme işlemi oluşturulurken hata:", error);
      toast.error("Ödeme işlemi oluşturulurken hata oluştu");
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setFormData({
      payment_type: "customer", // Edit sadece müşteri ödemeleri için şimdilik
      supplier_id: payment.supplier_id,
      customer_id: payment.customer_id,
      bayi_id: "",
      amount: payment.amount,
      description: payment.description
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/supplier-payments/${editingPayment.id}`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, {
        params: { session_token: sessionToken }
      });
      toast.success("Tedarikçi tediyesi güncellendi");
      setIsEditDialogOpen(false);
      setEditingPayment(null);
      fetchSupplierPayments();
      fetchCustomers();
      fetchSuppliersDebtSummary();
    } catch (error) {
      console.error("Tedarikçi tediyesi güncellenirken hata:", error);
      toast.error("Tedarikçi tediyesi güncellenirken hata oluştu");
    }
  };

  const handleDeletePayment = async () => {
    try {
      await axios.delete(`${API}/supplier-payments/${paymentToDelete.id}`, {
        params: { session_token: sessionToken }
      });
      toast.success("Tedarikçi tediyesi silindi");
      setDeleteConfirmOpen(false);
      setPaymentToDelete(null);
      fetchSupplierPayments();
      fetchCustomers();
      fetchSuppliersDebtSummary();
    } catch (error) {
      console.error("Tedarikçi tediyesi silinirken hata:", error);
      toast.error("Tedarikçi tediyesi silinirken hata oluştu");
    }
  };

  // Filter payments based on selected supplier
  const filteredPayments = selectedSupplierFilter && selectedSupplierFilter !== "all"
    ? payments.filter(payment => payment.supplier_id === selectedSupplierFilter)
    : payments;

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tedarikçi Tediye</h1>
          <p className="text-muted-foreground">Müşteri borcundan tedarikçilere ödeme yapın</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Yeni Tediye
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Ödeme İşlemi</DialogTitle>
              <DialogDescription>
                {formData.payment_type === "customer" 
                  ? "Müşteri borcundan tedarikçiye ödeme yapın." 
                  : "Bayi'den tedarikçiye ödeme yapın."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="payment_type">Ödeme Türü</Label>
                <Select value={formData.payment_type} onValueChange={(value) => setFormData({...formData, payment_type: value, customer_id: "", bayi_id: ""})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ödeme türü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Müşteri Borcundan</SelectItem>
                    <SelectItem value="bayi">Bayi Ödemesi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="supplier_id">Tedarikçi</Label>
                <Select value={formData.supplier_id} onValueChange={(value) => setFormData({...formData, supplier_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tedarikçi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.filter(supplier => supplier.id && supplier.id.trim() !== '').map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} {supplier.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Conditional rendering based on payment_type */}
              {formData.payment_type === "customer" ? (
                <div>
                  <Label htmlFor="customer_id">Borcu Azaltılacak Müşteri</Label>
                  <div className="relative customer-search-container">
                    <Input
                      placeholder="Müşteri adı, soyadı, telefon veya seri numarası ile ara..."
                      value={customerSearchTerm}
                      onChange={(e) => {
                        setCustomerSearchTerm(e.target.value);
                        setShowCustomerDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowCustomerDropdown(customerSearchTerm.length > 0)}
                      className="w-full"
                    />
                    {showCustomerDropdown && customerSearchTerm.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {customers
                          .filter(customer => customer.id && customer.id.trim() !== '')
                          .filter(customer => 
                            `${customer.name} ${customer.surname} ${customer.phone} ${customer.serial_number}`.toLowerCase()
                            .includes(customerSearchTerm.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((customer) => (
                            <div
                              key={customer.id}
                              onClick={() => {
                                setFormData({...formData, customer_id: customer.id});
                                setCustomerSearchTerm(`${customer.serial_number} - ${customer.name} ${customer.surname}`);
                                setShowCustomerDropdown(false);
                              }}
                              className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium">{customer.name} {customer.surname}</p>
                                  <p className="text-sm text-gray-600">#{customer.serial_number} • 📞 {customer.phone}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium text-red-600">
                                    Borç: ₺{customer.balance.toLocaleString('tr-TR')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        {customers
                          .filter(customer => customer.id && customer.id.trim() !== '')
                          .filter(customer => 
                            `${customer.name} ${customer.surname} ${customer.phone} ${customer.serial_number}`.toLowerCase()
                            .includes(customerSearchTerm.toLowerCase())
                          ).length === 0 && (
                          <div className="p-3 text-gray-500 text-center">
                            Müşteri bulunamadı
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="bayi_id">Ödeme Yapacak Bayi</Label>
                  <Select value={formData.bayi_id} onValueChange={(value) => setFormData({...formData, bayi_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Bayi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {bayiler.filter(bayi => bayi.id && bayi.id.trim() !== '').map((bayi) => (
                        <SelectItem key={bayi.id} value={bayi.id}>
                          {bayi.name} {bayi.surname} - Bakiye: ₺{bayi.balance.toLocaleString('tr-TR')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="amount">Tutar</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Tediye Yap
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tedarikçi Borç Özeti */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <span>Tedarikçi Borç Özeti</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suppliersDebtSummary.map((supplier) => (
              <Card key={supplier.supplier_id} className="border-blue-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg">{supplier.supplier_name}</h3>
                  <div className="space-y-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Toplam Borç:</span>
                      <span className="font-medium text-red-600">
                        ₺{supplier.total_debt.toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Yapılan Ödeme:</span>
                      <span className="font-medium text-green-600">
                        ₺{supplier.total_payments.toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-sm font-medium">Kalan Borç:</span>
                      <span className="font-bold text-orange-600">
                        ₺{supplier.remaining_debt.toLocaleString('tr-TR')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tediye Geçmişi */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Tediye Geçmişi</CardTitle>
            <div className="flex items-center space-x-2">
              <Label htmlFor="supplier-filter" className="text-sm font-medium">Tedarikçi:</Label>
              <Select value={selectedSupplierFilter} onValueChange={setSelectedSupplierFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Tüm tedarikçiler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm tedarikçiler</SelectItem>
                  {suppliers.filter(supplier => supplier.id && supplier.id.trim() !== '').map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name} {supplier.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Tarih</th>
                  <th className="text-left p-2">Tedarikçi</th>
                  <th className="text-left p-2">Müşteri</th>
                  <th className="text-left p-2">Tutar</th>
                  <th className="text-left p-2">Açıklama</th>
                  <th className="text-left p-2">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length > 0 ? filteredPayments.map((payment) => (
                  <tr key={payment.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{new Date(payment.created_at).toLocaleDateString('tr-TR')}</td>
                    <td className="p-2">{payment.supplier_name}</td>
                    <td className="p-2 text-blue-600">{payment.customer_name}</td>
                    <td className="p-2 text-green-600 font-medium">
                      ₺{payment.amount.toLocaleString('tr-TR')}
                    </td>
                    <td className="p-2">{payment.description}</td>
                    <td className="p-2">
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEditPayment(payment)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            setPaymentToDelete(payment);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center p-8 text-gray-500">
                      Henüz tediye kaydı bulunmamaktadır.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tediye Düzenle</DialogTitle>
            <DialogDescription>
              Tedarikçi tediye bilgilerini düzenleyin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePayment} className="space-y-4">
            <div>
              <Label htmlFor="supplier_id">Tedarikçi</Label>
              <Select value={formData.supplier_id} onValueChange={(value) => setFormData({...formData, supplier_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Tedarikçi seçin" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.filter(supplier => supplier.id && supplier.id.trim() !== '').map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name} {supplier.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="customer_id">Borcu Azaltılacak Müşteri</Label>
              <Select value={formData.customer_id} onValueChange={(value) => setFormData({...formData, customer_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Müşteri seçin" />
                </SelectTrigger>
                <SelectContent>
                  {customers.filter(customer => customer.id && customer.id.trim() !== '').map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} {customer.surname} - Borç: ₺{customer.balance.toLocaleString('tr-TR')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="amount">Tutar</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Açıklama</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tediye Sil</DialogTitle>
            <DialogDescription>
              Bu tediyeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          {paymentToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p><strong>Tedarikçi:</strong> {paymentToDelete.supplier_name}</p>
              <p><strong>Müşteri:</strong> {paymentToDelete.customer_name}</p>
              <p><strong>Tutar:</strong> ₺{paymentToDelete.amount?.toLocaleString('tr-TR')}</p>
              <p><strong>Açıklama:</strong> {paymentToDelete.description}</p>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeletePayment}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Temlik Component - Müşteriden Tedarikçiye Temlik İşlemleri
const Temlik = ({ sessionToken }) => {
  const [temlikList, setTemlikList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [supplierReportOpen, setSupplierReportOpen] = useState(false);
  const [supplierReport, setSupplierReport] = useState(null);
  const [supplierReportLoading, setSupplierReportLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('month');
  const [supplierFilter, setSupplierFilter] = useState('all'); // 'all', 'firma', 'sahis'
  
  // Password dialog states
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordActionType, setPasswordActionType] = useState(''); // 'edit' or 'delete'
  const [itemToSecure, setItemToSecure] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTemlik, setEditingTemlik] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    supplier_id: '',
    amount: '',
    description: ''
  });

  useEffect(() => {
    fetchTemlikList();
    fetchSuppliers();
    fetchStatistics();
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [statsPeriod]);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(
        `${API}/temlik/statistics?session_token=${sessionToken}&period=${statsPeriod}`
      );
      setStatistics(response.data);
    } catch (error) {
      console.error("İstatistikler yüklenirken hata:", error);
    }
  };

  const fetchTemlikList = async () => {
    try {
      const response = await axios.get(`${API}/temlik/list?session_token=${sessionToken}`);
      setTemlikList(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Temlik listesi yüklenirken hata:", error);
      toast.error("Temlik listesi yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error("Tedarikçiler yüklenirken hata:", error);
    }
  };

  const searchCustomers = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(
        `${API}/customers/search?q=${encodeURIComponent(searchTerm)}&session_token=${sessionToken}&exclude_zero=true`
      );
      // Backend artık bakiyesi 0 olmayanları döndürüyor
      setSearchResults(response.data);
    } catch (error) {
      console.error("Müşteri arama hatası:", error);
      toast.error("Müşteri arama sırasında hata oluştu");
    } finally {
      setSearching(false);
    }
  };

  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    searchCustomers(value);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData({ ...formData, customer_id: customer.id });
    setCustomerSearch(`${customer.name} ${customer.surname} - ${customer.serial_number}`);
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id || !formData.supplier_id || !formData.amount) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast.error("Tutar sıfırdan büyük olmalıdır");
      return;
    }

    try {
      await axios.post(
        `${API}/temlik/create?session_token=${sessionToken}`,
        formData
      );

      toast.success("Temlik işlemi başarıyla oluşturuldu");
      setIsDialogOpen(false);
      resetForm();
      fetchTemlikList();
    } catch (error) {
      console.error("Temlik oluşturma hatası:", error);
      toast.error(error.response?.data?.detail || "Temlik oluşturulurken hata oluştu");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      supplier_id: '',
      amount: '',
      description: ''
    });
    setSelectedCustomer(null);
    setCustomerSearch('');
    setSelectedSupplier('');
    setSearchResults([]);
  };

  const viewSupplierReport = async (supplierId) => {
    setSupplierReportLoading(true);
    setSupplierReportOpen(true);
    
    try {
      const response = await axios.get(
        `${API}/temlik/supplier-report/${supplierId}?session_token=${sessionToken}`
      );
      setSupplierReport(response.data);
    } catch (error) {
      console.error("Tedarikçi raporu yüklenirken hata:", error);
      toast.error("Tedarikçi raporu yüklenirken hata oluştu");
    } finally {
      setSupplierReportLoading(false);
    }
  };

  // Edit işlemi için password dialog aç
  const handleEditClick = (temlik) => {
    setItemToSecure(temlik);
    setPasswordActionType('edit');
    setIsPasswordDialogOpen(true);
  };

  // Delete işlemi için password dialog aç
  const handleDeleteClick = (temlik) => {
    setItemToSecure(temlik);
    setPasswordActionType('delete');
    setIsPasswordDialogOpen(true);
  };

  // Password doğrulama sonrası işlem
  const handlePasswordSubmit = async (password) => {
    if (passwordActionType === 'edit') {
      setEditingTemlik(itemToSecure);
      setIsEditDialogOpen(true);
      setIsPasswordDialogOpen(false);
    } else if (passwordActionType === 'delete') {
      await deleteTemlik(itemToSecure.id, password);
      setIsPasswordDialogOpen(false);
    }
    setItemToSecure(null);
    setPasswordActionType('');
  };

  // Password dialog iptal
  const handleCancelPasswordDialog = () => {
    setIsPasswordDialogOpen(false);
    setItemToSecure(null);
    setPasswordActionType('');
  };

  // Temlik güncelleme
  const updateTemlik = async (e) => {
    e.preventDefault();
    
    if (!editingTemlik) return;

    try {
      await axios.put(
        `${API}/temlik/${editingTemlik.id}?session_token=${sessionToken}`,
        {
          amount: parseFloat(editingTemlik.amount),
          description: editingTemlik.description || '',
          admin_password: editingTemlik.admin_password
        }
      );

      toast.success("Temlik işlemi güncellendi");
      setIsEditDialogOpen(false);
      setEditingTemlik(null);
      fetchTemlikList();
    } catch (error) {
      console.error("Güncelleme hatası:", error);
      toast.error(error.response?.data?.detail || "Güncelleme başarısız");
    }
  };

  // Temlik silme
  const deleteTemlik = async (temlikId, password) => {
    try {
      await axios.delete(
        `${API}/temlik/${temlikId}?session_token=${sessionToken}&admin_password=${password}`
      );

      toast.success("Temlik işlemi silindi");
      fetchTemlikList();
    } catch (error) {
      console.error("Silme hatası:", error);
      toast.error(error.response?.data?.detail || "Silme başarısız");
    }
  };


  // Firma kontrolü - tedarikçi adında firma ifadeleri var mı?
  const isFirma = (supplierName) => {
    const firmaKeywords = ['LTD', 'A.Ş', 'A.S', 'SAN.', 'TİC.', 'TIC.', 'LİMİTED', 'LIMITED', 'ŞTİ', 'STI', 'OĞLU', 'VE'];
    const upperName = (supplierName || '').toUpperCase();
    return firmaKeywords.some(keyword => upperName.includes(keyword));
  };

  // Filtrelenmiş temlik listesi
  const filteredTemlikList = temlikList.filter(temlik => {
    if (supplierFilter === 'all') return true;
    if (supplierFilter === 'firma') return isFirma(temlik.supplier_name);
    if (supplierFilter === 'sahis') return !isFirma(temlik.supplier_name);
    return true;
  });

  // Firma ve şahıs istatistikleri
  const firmaTemliks = temlikList.filter(t => isFirma(t.supplier_name));
  const sahisTemliks = temlikList.filter(t => !isFirma(t.supplier_name));
  
  const firmaTotal = firmaTemliks.reduce((sum, t) => sum + (t.amount || 0), 0);
  const sahisTotal = sahisTemliks.reduce((sum, t) => sum + (t.amount || 0), 0);



  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Temlik İşlemleri</h1>
          <p className="text-gray-500 mt-1">
            Müşteriden tahsil edilen tutarı tedarikçiye temlik et
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Yeni İşlem
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Yeni Temlik İşlemi</DialogTitle>
              <DialogDescription>
                Müşteriden tahsil edilecek tutarı tedarikçiye temlik edin
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Müşteri Arama */}
              <div className="space-y-2">
                <Label>Müşteri Seç *</Label>
                <Input
                  type="text"
                  value={customerSearch}
                  onChange={handleCustomerSearch}
                  placeholder="Müşteri adı, soyadı, telefon veya seri numarası ile ara..."
                />
                {searching && <p className="text-sm text-gray-500">Aranıyor...</p>}
                {searchResults.length > 0 && (
                  <div className="border rounded-md max-h-60 overflow-y-auto">
                    {searchResults.map((customer) => (
                      <div
                        key={customer.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => selectCustomer(customer)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">
                              {customer.name} {customer.surname}
                            </p>
                            <p className="text-sm text-gray-500">
                              Cari Kod: {customer.serial_number} | Tel: {customer.phone}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${customer.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {customer.balance > 0 ? `+${customer.balance.toLocaleString('tr-TR')}₺` : `${customer.balance.toLocaleString('tr-TR')}₺`}
                            </p>
                            <p className="text-xs text-gray-500">Bakiye</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">
                            {selectedCustomer.name} {selectedCustomer.surname}
                          </p>
                          <p className="text-sm text-gray-600">
                            Cari Kod: {selectedCustomer.serial_number}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">
                            +{selectedCustomer.balance.toLocaleString('tr-TR')}₺
                          </p>
                          <p className="text-xs text-gray-600">Borç Bakiyesi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Tedarikçi Seçimi */}
              <div className="space-y-2">
                <Label htmlFor="supplier_id">Temlik Edilecek Tedarikçi *</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, supplier_id: value });
                    setSelectedSupplier(value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tedarikçi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} {supplier.surname} - Borç: {supplier.balance.toLocaleString('tr-TR')}₺
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tutar */}
              <div className="space-y-2">
                <Label htmlFor="amount">Tutar *</Label>
                <Input
                  id="amount"
                  type="text"
                  value={formData.amount ? formatNumberWithThousandsSeparator(formData.amount) : ''}
                  onChange={(e) => {
                    const formatted = formatNumberWithThousandsSeparator(e.target.value);
                    const numeric = parseFormattedNumber(formatted);
                    setFormData({ ...formData, amount: numeric.toString() });
                  }}
                  placeholder="0"
                  required
                />
                <p className="text-xs text-gray-500">
                  Örnek: 1.000.000
                </p>
              </div>

              {/* Açıklama */}
              <div className="space-y-2">
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Temlik açıklaması (opsiyonel)"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  İptal
                </Button>
                <Button type="submit">Temlik Et</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* İstatistik Kartları */}
      {statistics && (
        <div className="space-y-4">
          {/* Dönem Seçici */}
          <div className="flex justify-end">
            <Tabs value={statsPeriod} onValueChange={setStatsPeriod}>
              <TabsList>
                <TabsTrigger value="day">Günlük</TabsTrigger>
                <TabsTrigger value="month">Aylık</TabsTrigger>
                <TabsTrigger value="year">Yıllık</TabsTrigger>
                <TabsTrigger value="all">Tümü</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Firma/Şahıs Özet Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Firmalara Temlik
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-blue-600">
                  {firmaTotal.toLocaleString('tr-TR')}₺
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {firmaTemliks.length} işlem
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Şahıslara Temlik
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600">
                  {sahisTotal.toLocaleString('tr-TR')}₺
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {sahisTemliks.length} işlem
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Ana İstatistik Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">
                  Toplam Temlik
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-blue-600">
                  {statistics.total_amount.toLocaleString('tr-TR')}₺
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {statistics.total_count} işlem
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">
                  Ortalama Temlik
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600">
                  {statistics.average_amount.toLocaleString('tr-TR')}₺
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  İşlem başına
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">
                  En Çok Temlik Alan
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statistics.top_suppliers.length > 0 ? (
                  <>
                    <p className="text-lg font-bold text-purple-600 truncate">
                      {statistics.top_suppliers[0].supplier_name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.top_suppliers[0].total_amount.toLocaleString('tr-TR')}₺
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">Veri yok</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">
                  En Çok Temlik Eden
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statistics.top_customers.length > 0 ? (
                  <>
                    <p className="text-lg font-bold text-orange-600 truncate">
                      {statistics.top_customers[0].customer_name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.top_customers[0].total_amount.toLocaleString('tr-TR')}₺
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">Veri yok</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detaylı İstatistikler */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* En Çok Temlik Yapılan Tedarikçiler */}
            <Card>
              <CardHeader>
                <CardTitle>En Çok Temlik Yapılan Tedarikçiler</CardTitle>
              </CardHeader>
              <CardContent>
                {statistics.top_suppliers.length > 0 ? (
                  <div className="space-y-3">
                    {statistics.top_suppliers.map((supplier, index) => (
                      <div key={supplier.supplier_id} className="flex items-center justify-between border-b pb-2">
                        <div className="flex items-center space-x-3">
                          <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                          <div>
                            <p className="font-medium">{supplier.supplier_name}</p>
                            <p className="text-xs text-gray-500">{supplier.count} işlem</p>
                          </div>
                        </div>
                        <p className="font-bold text-blue-600">
                          {supplier.total_amount.toLocaleString('tr-TR')}₺
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">Veri bulunmuyor</p>
                )}
              </CardContent>
            </Card>

            {/* En Çok Temlik Eden Müşteriler */}
            <Card>
              <CardHeader>
                <CardTitle>En Çok Temlik Eden Müşteriler</CardTitle>
              </CardHeader>
              <CardContent>
                {statistics.top_customers.length > 0 ? (
                  <div className="space-y-3">
                    {statistics.top_customers.map((customer, index) => (
                      <div key={customer.customer_id} className="flex items-center justify-between border-b pb-2">
                        <div className="flex items-center space-x-3">
                          <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                          <div>
                            <p className="font-medium">{customer.customer_name}</p>
                            <p className="text-xs text-gray-500">
                              Kod: {customer.customer_code} • {customer.count} işlem
                            </p>
                          </div>
                        </div>
                        <p className="font-bold text-green-600">
                          {customer.total_amount.toLocaleString('tr-TR')}₺
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">Veri bulunmuyor</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Temlik İşlemleri Listesi</CardTitle>
                <CardDescription>
                  Müşterilerden tedarikçilere yapılan temlik işlemleri
                </CardDescription>
              </div>
              
              {/* Filtre Butonları */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={supplierFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setSupplierFilter('all')}
                >
                  Tümü ({temlikList.length})
                </Button>
                <Button
                  size="sm"
                  variant={supplierFilter === 'firma' ? 'default' : 'outline'}
                  onClick={() => setSupplierFilter('firma')}
                  className={supplierFilter === 'firma' ? 'bg-blue-600' : ''}
                >
                  <Building2 className="h-4 w-4 mr-1" />
                  Firmalar ({firmaTemliks.length})
                </Button>
                <Button
                  size="sm"
                  variant={supplierFilter === 'sahis' ? 'default' : 'outline'}
                  onClick={() => setSupplierFilter('sahis')}
                  className={supplierFilter === 'sahis' ? 'bg-green-600' : ''}
                >
                  <User className="h-4 w-4 mr-1" />
                  Şahıslar ({sahisTemliks.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredTemlikList.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ArrowLeftRight className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>
                  {supplierFilter === 'all' 
                    ? 'Henüz temlik işlemi bulunmuyor'
                    : supplierFilter === 'firma'
                    ? 'Firmalara yapılmış temlik işlemi bulunmuyor'
                    : 'Şahıslara yapılmış temlik işlemi bulunmuyor'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">Tarih</th>
                      <th className="text-left p-3 font-medium">Müşteri</th>
                      <th className="text-left p-3 font-medium">Tedarikçi</th>
                      <th className="text-right p-3 font-medium">Tutar</th>
                      <th className="text-left p-3 font-medium">Açıklama</th>
                      <th className="text-left p-3 font-medium">İşlem Yapan</th>
                      <th className="text-center p-3 font-medium">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTemlikList.map((temlik) => (
                      <tr key={temlik.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          {new Date(temlik.created_at).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{temlik.customer_name}</p>
                            <p className="text-sm text-gray-500">Kod: {temlik.customer_code}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{temlik.supplier_name}</p>
                              {isFirma(temlik.supplier_name) ? (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  <Building2 className="h-3 w-3 mr-1" />
                                  Firma
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <User className="h-3 w-3 mr-1" />
                                  Şahıs
                                </Badge>
                              )}
                            </div>
                            <button
                              onClick={() => viewSupplierReport(temlik.supplier_id)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Rapor Görüntüle
                            </button>
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <span className="font-bold text-green-600">
                            {temlik.amount.toLocaleString('tr-TR')}₺
                          </span>
                        </td>
                        <td className="p-3 text-sm">{temlik.description || '-'}</td>
                        <td className="p-3 text-sm">{temlik.created_by}</td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(temlik)}
                              title="Düzenle"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(temlik)}
                              className="text-red-600 hover:text-red-700"
                              title="Sil"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewSupplierReport(temlik.supplier_id)}
                              title="Rapor"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tedarikçi Raporu Dialog */}
      <Dialog open={supplierReportOpen} onOpenChange={setSupplierReportOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tedarikçi Temlik Raporu</DialogTitle>
          </DialogHeader>
          {supplierReportLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : supplierReport ? (
            <div className="space-y-6">
              {/* Tedarikçi Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle>Tedarikçi Bilgileri</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Ad Soyad</p>
                      <p className="font-medium">
                        {supplierReport.supplier.name} {supplierReport.supplier.surname}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Telefon</p>
                      <p className="font-medium">{supplierReport.supplier.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Toplam Temlik</p>
                      <p className="font-bold text-green-600 text-lg">
                        {supplierReport.total_temlik.toLocaleString('tr-TR')}₺
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">İşlem Sayısı</p>
                      <p className="font-medium">{supplierReport.transaction_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Temlik Hareketleri */}
              <Card>
                <CardHeader>
                  <CardTitle>Temlik Hareketleri</CardTitle>
                </CardHeader>
                <CardContent>
                  {supplierReport.temlik_transactions.length === 0 ? (
                    <p className="text-center text-gray-500 py-6">
                      Bu tedarikçiye temlik işlemi bulunmuyor
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-2 font-medium">Tarih</th>
                            <th className="text-left p-2 font-medium">Müşteri</th>
                            <th className="text-right p-2 font-medium">Tutar</th>
                            <th className="text-left p-2 font-medium">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supplierReport.temlik_transactions.map((transaction) => (
                            <tr key={transaction.id} className="border-b">
                              <td className="p-2">
                                {new Date(transaction.created_at).toLocaleDateString('tr-TR')}
                              </td>
                              <td className="p-2">
                                <div>
                                  <p className="font-medium">{transaction.customer_name}</p>
                                  <p className="text-xs text-gray-500">Kod: {transaction.customer_code}</p>
                                </div>
                              </td>
                              <td className="p-2 text-right font-bold text-green-600">
                                +{transaction.amount.toLocaleString('tr-TR')}₺
                              </td>
                              <td className="p-2 text-sm">{transaction.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Password Confirmation Dialog */}
      <SecurePasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={handleCancelPasswordDialog}
        onConfirm={handlePasswordSubmit}
        title={passwordActionType === 'edit' ? 'Temlik Düzenleme Onayı' : 'Temlik Silme Onayı'}
        message={passwordActionType === 'edit' 
          ? 'Temlik işlemini düzenlemek için güvenlik şifresini girin' 
          : 'Temlik işlemini silmek için güvenlik şifresini girin'}
      />

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temlik İşlemini Düzenle</DialogTitle>
            <DialogDescription>
              Temlik tutarını ve açıklamasını güncelleyebilirsiniz
            </DialogDescription>
          </DialogHeader>
          {editingTemlik && (
            <form onSubmit={updateTemlik} className="space-y-4">
              <div>
                <Label>Müşteri</Label>
                <Input
                  value={editingTemlik.customer_name}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div>
                <Label>Tedarikçi</Label>
                <Input
                  value={editingTemlik.supplier_name}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div>
                <Label>Tutar *</Label>
                <Input
                  type="number"
                  value={editingTemlik.amount}
                  onChange={(e) => setEditingTemlik({...editingTemlik, amount: e.target.value})}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Açıklama</Label>
                <Textarea
                  value={editingTemlik.description || ''}
                  onChange={(e) => setEditingTemlik({...editingTemlik, description: e.target.value})}
                  placeholder="İsteğe bağlı açıklama"
                />
              </div>
              <div>
                <Label>Admin Şifresi *</Label>
                <Input
                  type="password"
                  value={editingTemlik.admin_password || ''}
                  onChange={(e) => setEditingTemlik({...editingTemlik, admin_password: e.target.value})}
                  required
                  placeholder="Güvenlik şifresini tekrar girin"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingTemlik(null);
                }}>
                  İptal
                </Button>
                <Button type="submit">Güncelle</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== GÜVENLİK ŞİFRESİ DIALOG COMPONENT =====
const SecurePasswordDialog = ({ isOpen, onClose, onConfirm, title = "Güvenlik Onayı", message = "Bu işlemi yapmak için güvenlik şifresini girin" }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password) {
      setError("Lütfen şifre girin");
      return;
    }
    
    onConfirm(password);
    setPassword("");
    setError("");
  };

  const handleCancel = () => {
    setPassword("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="secure_password">Güvenlik Şifresi</Label>
            <Input
              id="secure_password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              placeholder="Şifre girin"
              autoFocus
            />
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              İptal
            </Button>
            <Button type="submit">Onayla</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ===== KASA DEFTERİ COMPONENT =====
const CashLedger = ({ sessionToken }) => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [ledgerData, setLedgerData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);
  
  const API = BACKEND_URL || '';
  
  // İlk yükleme - mevcut bakiyeyi al
  useEffect(() => {
    fetchCurrentBalance();
    fetchDailyLedger(selectedDate);
    fetchMonthlySummary();
  }, []);
  
  const fetchCurrentBalance = async () => {
    try {
      const response = await axios.get(`${API}/api/cash-ledger/current-balance`, {
        params: { session_token: sessionToken }
      });
      setCurrentBalance(response.data.cash.balance);
    } catch (error) {
      console.error('Bakiye yüklenirken hata:', error);
    }
  };
  
  const fetchDailyLedger = async (date) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/cash-ledger/daily`, {
        params: { 
          ledger_type: 'cash',
          date: date,
          session_token: sessionToken 
        }
      });
      
      if (response.data.found) {
        setLedgerData(response.data.ledger);
      } else {
        setLedgerData(null);
        toast.info(`${date} tarihinde kayıt bulunamadı`);
      }
    } catch (error) {
      console.error('Defter yüklenirken hata:', error);
      toast.error('Defter yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMonthlySummary = async () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    try {
      const response = await axios.get(`${API}/api/cash-ledger/summary`, {
        params: { 
          ledger_type: 'cash',
          date_from: firstDay.toISOString().split('T')[0],
          date_to: lastDay.toISOString().split('T')[0],
          session_token: sessionToken 
        }
      });
      
      if (response.data.found) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Özet yüklenirken hata:', error);
    }
  };
  
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchDailyLedger(newDate);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">💰 Kasa Defteri</h1>
          <p className="text-muted-foreground">Günlük kasa hareketlerini görüntüleyin</p>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <Label>Tarih</Label>
            <Input 
              type="date" 
              value={selectedDate} 
              onChange={handleDateChange}
              className="w-48"
            />
          </div>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Güncel Bakiye</div>
            <div className="text-2xl font-bold">₺{currentBalance.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
          </Card>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div>Yükleniyor...</div>
        </div>
      ) : ledgerData ? (
        <div className="space-y-6">
          {/* Özet Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Devreden Bakiye</div>
              <div className="text-xl font-bold">₺{(ledgerData.opening_balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
            </Card>
            <Card className="p-4 bg-green-50">
              <div className="text-sm text-muted-foreground">Tahsilat</div>
              <div className="text-xl font-bold text-green-600">₺{((ledgerData.collections || []).reduce((sum, item) => sum + (item.amount || 0), 0)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
              <div className="text-xs text-muted-foreground">{(ledgerData.collections || []).length} işlem</div>
            </Card>
            <Card className="p-4 bg-red-50">
              <div className="text-sm text-muted-foreground">Ödeme</div>
              <div className="text-xl font-bold text-red-600">₺{((ledgerData.payments || []).reduce((sum, item) => sum + (item.amount || 0), 0)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
              <div className="text-xs text-muted-foreground">{(ledgerData.payments || []).length} işlem</div>
            </Card>
            <Card className="p-4 bg-blue-50">
              <div className="text-sm text-muted-foreground">Gün Sonu Bakiye</div>
              <div className="text-xl font-bold text-blue-600">₺{(ledgerData.closing_balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
            </Card>
          </div>
          
          {/* Tahsilat Listesi */}
          {ledgerData.collections.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>📥 Tahsilat İşlemleri</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Saat</TableHead>
                      <TableHead>Müşteri</TableHead>
                      <TableHead>Açıklama</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerData.collections.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.time}</TableCell>
                        <TableCell className="font-medium">{item.customer_or_supplier}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right text-green-600 font-semibold">
                          ₺{item.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          
          {/* Ödeme Listesi */}
          {ledgerData.payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>📤 Ödeme İşlemleri</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Saat</TableHead>
                      <TableHead>Tedarikçi</TableHead>
                      <TableHead>Açıklama</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerData.payments.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.time}</TableCell>
                        <TableCell className="font-medium">{item.customer_or_supplier}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right text-red-600 font-semibold">
                          ₺{item.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Bu tarihte kayıt bulunamadı</p>
        </Card>
      )}
    </div>
  );
};

// ===== BANKA DEFTERİ COMPONENT =====
const BankLedger = ({ sessionToken }) => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [ledgerData, setLedgerData] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);
  
  const API = BACKEND_URL || '';
  
  useEffect(() => {
    fetchCurrentBalance();
    fetchDailyLedger(selectedDate);
  }, []);
  
  const fetchCurrentBalance = async () => {
    try {
      const response = await axios.get(`${API}/api/cash-ledger/current-balance`, {
        params: { session_token: sessionToken }
      });
      setCurrentBalance(response.data.bank.balance);
    } catch (error) {
      console.error('Bakiye yüklenirken hata:', error);
    }
  };
  
  const fetchDailyLedger = async (date) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/cash-ledger/daily`, {
        params: { 
          ledger_type: 'bank',
          date: date,
          session_token: sessionToken 
        }
      });
      
      if (response.data.found) {
        setLedgerData(response.data.ledger);
      } else {
        setLedgerData(null);
        toast.info(`${date} tarihinde kayıt bulunamadı`);
      }
    } catch (error) {
      console.error('Defter yüklenirken hata:', error);
      toast.error('Defter yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchDailyLedger(newDate);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">🏦 Banka Defteri</h1>
          <p className="text-muted-foreground">Günlük banka hareketlerini görüntüleyin</p>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <Label>Tarih</Label>
            <Input 
              type="date" 
              value={selectedDate} 
              onChange={handleDateChange}
              className="w-48"
            />
          </div>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Güncel Bakiye</div>
            <div className="text-2xl font-bold">₺{currentBalance.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
          </Card>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div>Yükleniyor...</div>
        </div>
      ) : ledgerData ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Devreden Bakiye</div>
              <div className="text-xl font-bold">₺{(ledgerData.opening_balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
            </Card>
            <Card className="p-4 bg-green-50">
              <div className="text-sm text-muted-foreground">Tahsilat</div>
              <div className="text-xl font-bold text-green-600">₺{((ledgerData.collections || []).reduce((sum, item) => sum + (item.amount || 0), 0)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
              <div className="text-xs text-muted-foreground">{(ledgerData.collections || []).length} işlem</div>
            </Card>
            <Card className="p-4 bg-red-50">
              <div className="text-sm text-muted-foreground">Ödeme</div>
              <div className="text-xl font-bold text-red-600">₺{((ledgerData.payments || []).reduce((sum, item) => sum + (item.amount || 0), 0)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
              <div className="text-xs text-muted-foreground">{(ledgerData.payments || []).length} işlem</div>
            </Card>
            <Card className="p-4 bg-blue-50">
              <div className="text-sm text-muted-foreground">Gün Sonu Bakiye</div>
              <div className="text-xl font-bold text-blue-600">₺{(ledgerData.closing_balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
            </Card>
          </div>
          
          {ledgerData.collections.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>📥 Tahsilat İşlemleri</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Saat</TableHead>
                      <TableHead>Müşteri</TableHead>
                      <TableHead>Açıklama</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerData.collections.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.time}</TableCell>
                        <TableCell className="font-medium">{item.customer_or_supplier}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right text-green-600 font-semibold">
                          ₺{item.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          
          {ledgerData.payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>📤 Ödeme İşlemleri</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Saat</TableHead>
                      <TableHead>Tedarikçi</TableHead>
                      <TableHead>Açıklama</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerData.payments.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.time}</TableCell>
                        <TableCell className="font-medium">{item.customer_or_supplier}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right text-red-600 font-semibold">
                          ₺{item.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Bu tarihte kayıt bulunamadı</p>
        </Card>
      )}
    </div>
  );
};

// Collection Receipts Component
const CollectionReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bayiAccounts, setBayiAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Session token'ı localStorage'dan al
  const sessionToken = localStorage.getItem('session_token');
  // Müşteri arama state'leri
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Çift kayıt önleme
  const [formData, setFormData] = useState({
    account_type: "customer", // "customer" veya "bayi"
    customer_id: "",
    account_id: "", // Genel account ID
    amount: "",
    payment_method: "safe",
    description: ""
  });

  useEffect(() => {
    fetchReceipts();
    fetchCustomers();
    fetchBayiAccounts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const response = await axios.get(`${API}/collection-receipts`, {
        params: { session_token: sessionToken }
      });
      setReceipts(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Tahsilat dekontları yüklenirken hata:", error);
      toast.error("Tahsilat dekontları yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers?limit=2000`);
      const customersData = response.data.customers || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error("Müşteriler yüklenirken hata:", error);
    }
  };

  const fetchBayiAccounts = async () => {
    try {
      const response = await axios.get(`${API}/bayiler`);
      setBayiAccounts(response.data);
    } catch (error) {
      console.error("Bayi hesapları yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // account_type'a göre account_id belirleme
      const accountId = formData.account_type === "customer" ? formData.customer_id : formData.account_id;
      
      await axios.post(`${API}/collection-receipts`, {
        account_id: accountId,
        account_type: formData.account_type,
        customer_id: formData.account_type === "customer" ? formData.customer_id : null, // Geriye uyumluluk
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        description: formData.description
      });
      toast.success("Tahsilat dekontı oluşturuldu");
      setIsDialogOpen(false);
      setFormData({
        account_type: "customer",
        customer_id: "",
        account_id: "",
        amount: "",
        payment_method: "safe",
        description: ""
      });
      setCustomerSearchTerm("");
      setShowCustomerDropdown(false);
      fetchReceipts();
      fetchCustomers();
      fetchBayiAccounts();
    } catch (error) {
      console.error("Tahsilat dekontı oluşturulurken hata:", error);
      toast.error("Tahsilat dekontı oluşturulurken hata oluştu");
    }
  };
  const handleEditReceipt = (receipt) => {
    setEditingReceipt(receipt);
    setFormData({
      customer_id: receipt.customer_id,
      amount: receipt.amount,
      payment_method: receipt.payment_method,
      description: receipt.description
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateReceipt = async (e) => {
    e.preventDefault();
    setPendingAction({
      type: 'edit',
      data: {
        ...formData,
        amount: parseFloat(formData.amount)
      }
    });
    setIsPasswordDialogOpen(true);
  };

  const handleDeleteReceipt = async () => {
    setPendingAction({
      type: 'delete',
      receipt: receiptToDelete
    });
    setDeleteConfirmOpen(false);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async (password) => {
    try {
      if (pendingAction.type === 'delete') {
        await axios.delete(`${API}/collection-receipts/${pendingAction.receipt.id}`, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Tahsilat silindi");
        setReceiptToDelete(null);
        fetchReceipts();
        fetchCustomers();
      } else if (pendingAction.type === 'edit') {
        await axios.put(`${API}/collection-receipts/${editingReceipt.id}`, pendingAction.data, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Tahsilat güncellendi");
        setIsEditDialogOpen(false);
        setEditingReceipt(null);
        fetchReceipts();
        fetchCustomers();
        setFormData({
          customer_id: "",
          amount: "",
          payment_method: "cash",
          description: ""
        });
      }
      
      setIsPasswordDialogOpen(false);
      setPendingAction(null);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Güvenlik şifresi hatalı!");
      } else {
        console.error("İşlem sırasında hata:", error);
        toast.error("İşlem sırasında hata oluştu");
      }
    }
  };

  const openDeleteConfirm = (receipt) => {
    setReceiptToDelete(receipt);
    setDeleteConfirmOpen(true);
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'cash': return 'Nakit';
      case 'bank': return 'Banka';
      case 'safe': return 'Kasa';
      default: return method;
    }
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setFormData({
        account_type: "customer",
        customer_id: "",
        account_id: "",
        amount: "",
        payment_method: "safe",
        description: ""
      });
      setCustomerSearchTerm("");
      setShowCustomerDropdown(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tahsilat Dekontları</h1>
          <p className="text-muted-foreground">Tahsilat işlemlerini yönetin</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button>
              <Receipt className="h-4 w-4 mr-2" />
              Yeni Tahsilat
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Tahsilat Dekontı</DialogTitle>
              <DialogDescription>
                Müşteri veya bayiden tahsilat işlemi gerçekleştirin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Hesap Tipi Seçimi */}
              <div>
                <Label htmlFor="account_type">Hesap Tipi</Label>
                <Select value={formData.account_type} onValueChange={(value) => setFormData({...formData, account_type: value, customer_id: "", account_id: ""})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Hesap tipi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Müşteri</SelectItem>
                    <SelectItem value="bayi">Bayi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Müşteri Seçimi - Yazarak Arama */}
              {formData.account_type === "customer" && (
                <div>
                  <Label htmlFor="customer_id">Müşteri</Label>
                  <div className="relative">
                    <Input
                      placeholder="Müşteri adı, soyadı veya telefon ile ara..."
                      value={customerSearchTerm}
                      onChange={(e) => {
                        setCustomerSearchTerm(e.target.value);
                        setShowCustomerDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowCustomerDropdown(customerSearchTerm.length > 0)}
                      className="w-full"
                    />
                    {showCustomerDropdown && customerSearchTerm.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {customers
                          .filter(customer => 
                            `${customer.name} ${customer.surname} ${customer.phone} ${customer.serial_number}`.toLowerCase()
                            .includes(customerSearchTerm.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((customer) => (
                            <div
                              key={customer.id}
                              onClick={() => {
                                setFormData({...formData, customer_id: customer.id});
                                setCustomerSearchTerm(`${customer.serial_number} - ${customer.name} ${customer.surname}`);
                                setShowCustomerDropdown(false);
                              }}
                              className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium">{customer.name} {customer.surname}</p>
                                  <p className="text-sm text-gray-600">#{customer.serial_number} • 📞 {customer.phone}</p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-sm font-medium ${customer.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    Bakiye: ₺{customer.balance.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        {customers.filter(customer => 
                          `${customer.name} ${customer.surname} ${customer.phone} ${customer.serial_number}`.toLowerCase()
                          .includes(customerSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <div className="p-3 text-gray-500 text-center">
                            Müşteri bulunamadı
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Bayi Seçimi */}
              {formData.account_type === "bayi" && (
                <div>
                  <Label htmlFor="account_id">Bayi</Label>
                  <Select value={formData.account_id} onValueChange={(value) => setFormData({...formData, account_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Bayi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {bayiAccounts.map((bayi) => (
                        <SelectItem key={bayi.id} value={bayi.id}>
                          {bayi.name} {bayi.surname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="amount">Tahsilat Tutarı</Label>
                <Input
                  id="amount"
                  type="text"
                  value={formData.amount ? formatNumberWithThousandsSeparator(formData.amount) : ''}
                  onChange={(e) => {
                    const formatted = formatNumberWithThousandsSeparator(e.target.value);
                    const numeric = parseFormattedNumber(formatted);
                    setFormData({...formData, amount: numeric.toString()});
                  }}
                  placeholder="0"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Örnek: 1.000.000
                </p>
              </div>
              <div>
                <Label htmlFor="payment_method">Ödeme Yöntemi</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safe">Kasa</SelectItem>
                    <SelectItem value="bank">Banka</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full">Tahsilat Yap</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Liste Görünümü */}
      <div className="space-y-3">
        {receipts.length > 0 ? receipts.map((receipt) => (
          <Card key={receipt.id} className="w-full">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Receipt className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold">{receipt.customer_name}</h3>
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {getPaymentMethodText(receipt.payment_method)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{receipt.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(receipt.receipt_date).toLocaleDateString('tr-TR')} - 
                      Dekont #{receipt.id.slice(-6)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center justify-end space-x-2 mb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditReceipt(receipt)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Düzenle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDeleteConfirm(receipt)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Sil
                    </Button>
                  </div>
                  <div className="text-xl font-bold text-green-600">
                    ₺{receipt.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-500">Tahsil Edilen</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <div className="text-center py-12">
            <Receipt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg text-gray-600">Henüz tahsilat dekontı bulunmamaktadır</p>
            <p className="text-sm text-gray-500">İlk tahsilat dekontunuzu oluşturmak için "Yeni Tahsilat" butonuna tıklayın</p>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tahsilat Düzenle</DialogTitle>
            <DialogDescription>
              Tahsilat bilgilerini güncelleyin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateReceipt} className="space-y-4">
            <div>
              <Label htmlFor="customer_id">Müşteri</Label>
              <Select value={formData.customer_id} onValueChange={(value) => setFormData({...formData, customer_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Müşteri seçin" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} {customer.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="amount">Tahsilat Tutarı</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tahsilat Sil</DialogTitle>
            <DialogDescription>
              Bu tahsilatı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          {receiptToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p><strong>Açıklama:</strong> {receiptToDelete.description}</p>
              <p><strong>Tutar:</strong> ₺{receiptToDelete.amount?.toLocaleString('tr-TR')}</p>
              <p><strong>Tarih:</strong> {new Date(receiptToDelete.receipt_date).toLocaleDateString('tr-TR')}</p>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteReceipt}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SecurePasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title="Güvenlik Onayı"
        message="Bu işlemi yapmak için admin şifresini girin"
      />
    </div>
  );
};

// Installation Schedules Component
const InstallationSchedules = ({ currentUser, sessionToken }) => {
  const [schedules, setSchedules] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInstallerDialogOpen, setIsInstallerDialogOpen] = useState(false);
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);
  const [isTargetDateDialogOpen, setIsTargetDateDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTargetDate, setSelectedTargetDate] = useState(new Date());
  const [formData, setFormData] = useState({
    customer_id: "",
    installer_id: "",
    installation_date: new Date(),
    target_date: new Date(), // Hedef montaj tarihi
    address: "",
    door_type: "",
    notes: ""
  });
  const [installerFormData, setInstallerFormData] = useState({
    name: "",
    surname: "",
    phone: ""
  });
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedInstaller, setSelectedInstaller] = useState("");
  const [saleDetailsDialogOpen, setSaleDetailsDialogOpen] = useState(false);
  const [selectedSaleDetails, setSelectedSaleDetails] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [customersData, setCustomersData] = useState({});
  const [scheduleSearch, setScheduleSearch] = useState("");

  // Check if current user is installation personnel
  const isInstallationPersonnel = currentUser?.role === 'installation';

  useEffect(() => {
    fetchSchedules();
    if (!isInstallationPersonnel) {
      fetchCustomers();
      fetchInstallers();
      fetchCustomersData();
    }
  }, []);

  // Auto-refresh every 45 seconds for installation personnel
  useEffect(() => {
    if (isInstallationPersonnel && currentUser?.installer_id) {
      const interval = setInterval(() => {
        fetchSchedules();
      }, 45000); // 45 saniyede bir yenile

      return () => clearInterval(interval);
    }
  }, [isInstallationPersonnel, currentUser]);

  const fetchSchedules = async () => {
    try {
      const endpoint = isInstallationPersonnel 
        ? `${API}/installation-schedules-with-sales?session_token=${sessionToken}`
        : `${API}/installation-schedules-with-sales`;
      const response = await axios.get(endpoint);
      
      // Installation personnel için sadece kendi işlerini filtrele
      if (isInstallationPersonnel) {
        const mySchedules = response.data.filter(schedule => 
          schedule.installer_id === currentUser?.installer_id
        );
        setSchedules(mySchedules);
      } else {
        setSchedules(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Montaj programları yüklenirken hata:", error);
      toast.error("Montaj programları yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers?limit=2000`);
      const customersData = response.data.customers || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error("Müşteriler yüklenirken hata:", error);
    }
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API}/installers`);
      setInstallers(response.data);
    } catch (error) {
      console.error("Montajcılar yüklenirken hata:", error);
    }
  };

  const fetchCustomersData = async () => {
    try {
      const response = await axios.get(`${API}/customers`, { params: { all: true } });
      const customersMap = {};
      (response.data.customers || response.data || []).forEach(customer => {
        customersMap[customer.id] = customer;
      });
      setCustomersData(customersMap);
    } catch (error) {
      console.error("Müşteri verileri yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/installation-schedules`, {
        ...formData,
        installation_date: formData.installation_date.toISOString(),
        target_date: formData.target_date.toISOString()
      }, {
        params: { session_token: sessionToken }
      });
      toast.success("Montaj programı eklendi");
      setIsDialogOpen(false);
      setFormData({
        customer_id: "",
        installer_id: "",
        installation_date: new Date(),
        target_date: new Date(),
        address: "",
        door_type: "",
        notes: ""
      });
      fetchSchedules();
    } catch (error) {
      console.error("Montaj programı eklenirken hata:", error);
      toast.error("Montaj programı eklenirken hata oluştu");
    }
  };

  const handleInstallerSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/installers`, installerFormData);
      toast.success("Montajcı eklendi");
      setIsInstallerDialogOpen(false);
      setInstallerFormData({ name: "", surname: "", phone: "" });
      fetchInstallers();
    } catch (error) {
      console.error("Montajcı eklenirken hata:", error);
      toast.error("Montajcı eklenirken hata oluştu");
    }
  };

  const handleStatusChange = async (scheduleId, newStatus) => {
    try {
      await axios.put(`${API}/installation-schedules/${scheduleId}/status?status=${newStatus}&session_token=${sessionToken}`);
      alert("Durum güncellendi");
      fetchSchedules();
    } catch (error) {
      console.error("Status update failed:", error);
      alert("Durum güncellenirken hata oluştu: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleReturnToProduction = async (schedule) => {
    if (!confirm(`"${schedule.customer_name}" siparişini üretime geri göndermek istediğinize emin misiniz?\n\nBu işlem montaj kaydını silecek ve üretim durumunu "HAZIR" yapacaktır.`)) {
      return;
    }

    try {
      await axios.put(
        `${API}/installation-schedules/${schedule.id}/return-to-production?session_token=${sessionToken}`
      );
      alert("Sipariş üretime geri gönderildi");
      fetchSchedules();
    } catch (error) {
      console.error("Return to production failed:", error);
      alert("Üretime geri gönderme başarısız: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleAssignInstaller = async () => {
    if (!selectedInstaller || !selectedSchedule) {
      toast.error("Montajcı ve montaj programı seçilmelidir");
      return;
    }

    try {
      // Seçilen montajcı bilgisini bul
      const installer = installers.find(i => i.id === selectedInstaller);
      const installerName = installer ? `${installer.name} ${installer.surname}` : "Bilinmeyen Montajcı";
      
      await axios.put(`${API}/installation-schedules/${selectedSchedule.id}/assign-installer`, {
        installer_id: selectedInstaller,
        installer_name: installerName
      }, {
        params: { session_token: sessionToken }
      });
      
      toast.success("Montajcı atandı ve durum güncellendi");
      setAssignDialogOpen(false);
      setSelectedSchedule(null);
      setSelectedInstaller("");
      fetchSchedules();
    } catch (error) {
      console.error("Montajcı atanırken hata:", error);
      toast.error("Montajcı atanırken hata oluştu");
    }
  };

  const openAssignDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setSelectedInstaller("");
    setAssignDialogOpen(true);
  };

  const openSaleDetailsDialog = (schedule) => {
    setSelectedSaleDetails(schedule);
    setSaleDetailsDialogOpen(true);
  };

  const openDateDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setSelectedDate(schedule.installation_date ? new Date(schedule.installation_date) : new Date());
    setIsDateDialogOpen(true);
  };

  const openTargetDateDialog = (schedule) => {
    setSelectedSchedule(schedule);
    // Transaction'daki target_installation_date'i kullan
    const currentTargetDate = schedule.sale_details?.target_installation_date;
    setSelectedTargetDate(currentTargetDate ? new Date(currentTargetDate) : new Date());
    setIsTargetDateDialogOpen(true);
  };

  const handleSetDate = async () => {
    if (!selectedSchedule) return;
    
    try {
      await axios.put(`${API}/installation-schedules/${selectedSchedule.id}/set-date`, null, {
        params: { 
          installation_date: selectedDate.toISOString(),
          session_token: sessionToken 
        }
      });
      toast.success("Montaj tarihi ayarlandı");
      fetchSchedules();
      setIsDateDialogOpen(false);
    } catch (error) {
      console.error("Montaj tarihi ayarlanırken hata:", error);
      toast.error("Montaj tarihi ayarlanırken hata oluştu");
    }
  };

  const handleSetTargetDate = async () => {
    if (!selectedSchedule) return;
    
    try {
      // Transaction'ın target_installation_date'ini güncelle
      await axios.put(`${API}/transactions/${selectedSchedule.transaction_id}/target-date`, null, {
        params: { 
          target_date: selectedTargetDate.toISOString(),
          session_token: sessionToken 
        }
      });
      toast.success("Hedef montaj tarihi güncellendi");
      fetchSchedules();
      setIsTargetDateDialogOpen(false);
    } catch (error) {
      console.error("Hedef montaj tarihi güncellenirken hata:", error);
      toast.error("Hedef montaj tarihi güncellenirken hata oluştu");
    }
  };

  const handleTogglePainted = async (scheduleId, currentStatus, schedule) => {
    const toBoyahane = currentStatus && schedule?.status === 'beklemede' && getScheduleLine(schedule) === 'lake';
    if (toBoyahane && !window.confirm(`${schedule.customer_name} boyanmadı olarak işaretlenip Üretim > Boyahane'ye aktarılacak. Onaylıyor musunuz?`)) return;
    try {
      const response = await axios.put(
        `${API}/installation-schedules/${scheduleId}/toggle-painted`,
        null,
        { params: { session_token: sessionToken } }
      );
      
      const newStatus = response.data.is_painted ? "boyandı" : "boyanmadı";
      toast.success(toBoyahane ? "Sipariş Üretim > Boyahane'ye aktarıldı" : `Sipariş ${newStatus} olarak işaretlendi`);
      fetchSchedules();
    } catch (error) {
      console.error("Boya durumu güncellenirken hata:", error);
      toast.error("Boya durumu güncellenirken hata oluştu");
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'beklemede': return 'BEKLEMEDE';
      case 'planlandi': return 'PLANLANDI';
      case 'tamamlandi': return 'TAMAMLANDI';
      case 'iptal_edildi': return 'İPTAL EDİLDİ';
      default: return status;
    }
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setFormData({
        customer_id: "",
        installer_id: "",
        installation_date: new Date().toISOString().split('T')[0],
        address: "",
        door_type: "",
        notes: ""
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'beklemede': return 'secondary';
      case 'planlandi': return 'default';
      case 'tamamlandi': return 'default';
      case 'iptal_edildi': return 'destructive';
      default: return 'default';
    }
  };

  // Tarihe göre sıralama fonksiyonu
  const sortSchedulesByDate = (schedules) => {
    return schedules.sort((a, b) => new Date(a.installation_date) - new Date(b.installation_date));
  };

  // Status'e göre filtreleme (hazir_melamin / hazir_boyahane = beklemede + üretim hattı) + arama
  const getScheduleLine = (schedule) =>
    schedule.sale_details?.production_line || schedule.production_line || (schedule.boyahane_info ? 'lake' : 'melamin');

  const matchesScheduleSearch = (schedule) => {
    const q = normalizeTurkishText(scheduleSearch.trim());
    if (!q) return true;
    const c = customersData[schedule.customer_id] || {};
    const haystack = [
      schedule.customer_name, schedule.address, schedule.door_type, schedule.notes, schedule.installer_name,
      schedule.boyahane_info, schedule.sale_details?.end_customer, schedule.sale_details?.model, schedule.sale_details?.color,
      c.serial_number, c.phone, c.address, c.name, c.surname,
    ].filter(Boolean).join(' ');
    return normalizeTurkishText(haystack).includes(q);
  };

  const installationTabPerm = (tab) =>
    currentUser?.role === 'admin' ? 'edit' : (currentUser?.installation_tab_permissions?.[tab] || 'edit');
  const visibleInstallationTabs = INSTALLATION_TABS.filter((tab) => installationTabPerm(tab.key) !== 'hidden');

  const filterSchedulesByStatus = (status) => {
    const line = status === 'hazir_melamin' ? 'melamin' : status === 'hazir_boyahane' ? 'lake' : null;
    const realStatus = line ? 'beklemede' : status;
    return sortSchedulesByDate(schedules.filter(schedule =>
      schedule.status === realStatus &&
      (!line || (line === 'lake' ? getScheduleLine(schedule) === 'lake' && schedule.is_painted : getScheduleLine(schedule) !== 'lake')) &&
      matchesScheduleSearch(schedule)
    ));
  };

  const handleToggleAksesuar = async (schedule) => {
    try {
      const response = await axios.put(`${API}/installation-schedules/${schedule.id}/toggle-aksesuar`, null, { params: { session_token: sessionToken } });
      toast.success(response.data.aksesuar_ready ? "Aksesuar hazır olarak işaretlendi" : "Aksesuar hazır işareti kaldırıldı");
      fetchSchedules();
    } catch (error) {
      console.error("Aksesuar durumu güncellenirken hata:", error);
      toast.error("Aksesuar durumu güncellenirken hata oluştu");
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header Section - Title, Description and Action Buttons */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isInstallationPersonnel ? "Montaj İşlerim" : "Montaj Programları"}
          </h1>
          <p className="text-muted-foreground">
            {isInstallationPersonnel 
              ? `${currentUser.full_name} - Sadece sizin atandığınız montaj işleri`
              : "Kapı montaj işlerini yönetin"
            }
          </p>
        </div>
        {!isInstallationPersonnel && ['hazir_melamin', 'hazir_boyahane'].some((t) => installationTabPerm(t) === 'edit') && (
          <div className="flex space-x-2">
            <Dialog open={isInstallerDialogOpen} onOpenChange={setIsInstallerDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <UserCheck className="h-4 w-4 mr-2" />
                  Montajcı Ekle
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Yeni Montajcı Ekle</DialogTitle>
                  <DialogDescription>
                    Montajcı bilgilerini ekleyin.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleInstallerSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 print:block">
                    <div>
                      <Label htmlFor="installer_name">Ad</Label>
                      <Input
                        id="installer_name"
                        value={installerFormData.name}
                        onChange={(e) => setInstallerFormData({...installerFormData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="installer_surname">Soyad</Label>
                      <Input
                        id="installer_surname"
                        value={installerFormData.surname}
                        onChange={(e) => setInstallerFormData({...installerFormData, surname: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="installer_phone">Telefon</Label>
                    <Input
                      id="installer_phone"
                      value={installerFormData.phone}
                      onChange={(e) => setInstallerFormData({...installerFormData, phone: e.target.value})}
                      placeholder="İsteğe bağlı"
                    />
                  </div>
                  <Button type="submit" className="w-full">Montajcı Ekle</Button>
                </form>
              </DialogContent>
            </Dialog>
            
            <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Yeni Montaj
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Yeni Montaj Programı</DialogTitle>
                  <DialogDescription>
                    Yeni montaj programı oluşturun.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="customer_id">Müşteri</Label>
                    <Select value={formData.customer_id} onValueChange={(value) => setFormData({...formData, customer_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Müşteri seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} {customer.surname}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="installer_id">Montajcı</Label>
                    <Select value={formData.installer_id} onValueChange={(value) => setFormData({...formData, installer_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Montajcı seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {installers.map((installer) => (
                          <SelectItem key={installer.id} value={installer.id}>
                            {installer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="address">Adres</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="installation_date">Montaj Tarihi</Label>
                      <Input
                        type="date"
                        id="installation_date"
                        value={formData.installation_date.toISOString().split('T')[0]}
                        onChange={(e) => setFormData({...formData, installation_date: new Date(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_date">Hedef Montaj Tarihi</Label>
                      <Input
                        type="date"
                        id="target_date"
                        value={formData.target_date.toISOString().split('T')[0]}
                        onChange={(e) => setFormData({...formData, target_date: new Date(e.target.value)})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Bilgi amaçlı hedef tarih (sonradan değiştirilebilir)</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="door_type">Kapı Türü</Label>
                    <Input
                      id="door_type"
                      value={formData.door_type}
                      onChange={(e) => setFormData({...formData, door_type: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notlar</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="İsteğe bağlı notlar"
                    />
                  </div>
                  <Button type="submit" className="w-full">Programla</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Arama */}
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Müşteri adı, cari no, telefon, adres, kapı türü, montajcı, not ile ara..."
          value={scheduleSearch}
          onChange={(e) => setScheduleSearch(e.target.value)}
          className="pl-9"
          data-testid="installation-search-input"
        />
      </div>

      {/* Status Tabs Section */}
      <Tabs defaultValue={visibleInstallationTabs[0]?.key || "hazir_melamin"} className="w-full">
        <TabsList className={`grid w-full grid-cols-2 md:grid-cols-${Math.max(1, visibleInstallationTabs.length)} h-auto`}>
          {visibleInstallationTabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key} className="relative" data-testid={`installation-tab-${tab.key}`}>
              {tab.label}
              {installationTabPerm(tab.key) === 'view' && <Eye className="h-3 w-3 ml-1 text-gray-500" />}
              <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${tab.badge}`}>
                {filterSchedulesByStatus(tab.key).length}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleInstallationTabs.map(({ key: status }) => (
          <TabsContent key={status} value={status} className="space-y-3 mt-6">
            {installationTabPerm(status) === 'view' && (
              <div className="flex items-center gap-2 p-2 text-sm rounded border border-amber-200 bg-amber-50 text-amber-800" data-testid={`installation-viewonly-${status}`}>
                <Eye className="h-4 w-4" /> Bu sekmede sadece görüntüleme yetkiniz var.
              </div>
            )}
            <div
              className={`space-y-3 min-w-0 ${installationTabPerm(status) === 'view' ? 'view-only-tab' : ''}`}
              onKeyDownCapture={(e) => {
                if (installationTabPerm(status) === 'view' && (e.key === 'Enter' || e.key === ' ') && !e.target.closest('[data-view-allowed]')) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
            {filterSchedulesByStatus(status).length > 0 ? (
              filterSchedulesByStatus(status).map((schedule) => (
                <Card key={schedule.id} className="w-full">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          schedule.status === 'tamamlandi' ? 'bg-green-100' :
                          schedule.status === 'planlandi' ? 'bg-blue-100' :
                          schedule.status === 'iptal_edildi' ? 'bg-red-100' :
                          'bg-orange-100'
                        }`}>
                          <Wrench className={`h-5 w-5 ${
                            schedule.status === 'tamamlandi' ? 'text-green-600' :
                            schedule.status === 'planlandi' ? 'text-blue-600' :
                            schedule.status === 'iptal_edildi' ? 'text-red-600' :
                            'text-orange-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold">{schedule.customer_name}</h3>
                            <Badge variant={getStatusColor(schedule.status)}>
                              {getStatusText(schedule.status)}
                            </Badge>
                          </div>
                          {customersData[schedule.customer_id] && (
                            <div className="flex items-center space-x-4 mb-2">
                              <p className="text-sm text-blue-600 font-medium">
                                Hesap: {customersData[schedule.customer_id].serial_number || schedule.customer_id?.slice(-6)}
                              </p>
                              <p className="text-sm text-green-600 font-medium">
                                Tel: {customersData[schedule.customer_id].phone}
                              </p>
                            </div>
                          )}
                          <p className="text-sm text-gray-600">Montajcı: {schedule.installer_name}</p>
                          <p className="text-sm text-gray-600">Kapı Türü: {schedule.door_type}</p>
                          
                          {/* Boya/Ebatlama Durumu Butonu */}
                          <div className="mt-2">
                            <Button
                              size="sm"
                              variant={schedule.is_painted ? "default" : "destructive"}
                              onClick={() => handleTogglePainted(schedule.id, schedule.is_painted, schedule)}
                              data-testid={`installation-toggle-painted-${schedule.id}`}
                              className={`${
                                schedule.is_painted 
                                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                                  : 'bg-orange-600 hover:bg-orange-700 text-white'
                              }`}
                            >
                              {schedule.sale_details?.production_line === 'melamin' ? (
                                schedule.is_painted ? '✅ Ebatlandı' : '⏳ Ebatlanmadı'
                              ) : (
                                schedule.is_painted ? '✅ Boyandı' : '⏳ Boyanmadı'
                              )}
                            </Button>
                            {schedule.is_painted && schedule.painted_date && (
                              <span className="ml-2 text-xs text-gray-500">
                                {schedule.sale_details?.production_line === 'melamin' ? 'Ebatlama: ' : 'Boyanma: '}
                                {new Date(schedule.painted_date).toLocaleDateString('tr-TR')}
                              </span>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleToggleAksesuar(schedule)}
                              disabled={isInstallationPersonnel}
                              className={`ml-2 text-white ${schedule.aksesuar_ready ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-500 hover:bg-gray-600'}`}
                              data-testid={`installation-aksesuar-btn-${schedule.id}`}
                            >
                              {schedule.aksesuar_ready ? '✅ Aksesuar Hazır' : '⏳ Aksesuar Hazırla'}
                            </Button>
                            {schedule.aksesuar_ready && schedule.aksesuar_date && (
                              <span className="ml-2 text-xs text-gray-500">
                                Aksesuar: {new Date(schedule.aksesuar_date).toLocaleDateString('tr-TR')}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-500">{schedule.address}</p>
                          {schedule.boyahane_info && (
                            <p className="text-xs text-purple-600 font-medium mt-1">
                              Boyahane: {schedule.boyahane_info}
                            </p>
                          )}
                          {schedule.notes && (
                            <p className="text-xs text-gray-500 mt-1">Not: {schedule.notes}</p>
                          )}
                          {/* Nihai Müşteri */}
                          {schedule.sale_details?.end_customer && (
                            <p className="text-xs text-purple-600 font-medium mt-1">
                              🏢 Nihai Müşteri: {schedule.sale_details.end_customer}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right space-y-2">
                        <div className="text-lg font-bold text-gray-900">
                          {schedule.installation_date ? (
                            <div 
                              onClick={() => openDateDialog(schedule)}
                              className="cursor-pointer hover:bg-blue-50 px-3 py-1 rounded transition-colors inline-block"
                              title="Tarihi değiştirmek için tıklayın"
                            >
                              <CalendarIcon className="h-4 w-4 inline mr-1 text-blue-600" />
                              {new Date(schedule.installation_date).toLocaleDateString('tr-TR')}
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openDateDialog(schedule)}
                              className="text-orange-600 border-orange-600 hover:bg-orange-50"
                            >
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              Tarih Seç
                            </Button>
                          )}
                        </div>
                        
                        {/* Hedef Montaj Tarihi - Transaction'dan */}
                        <div className="text-sm text-blue-600">
                          <span className="font-medium">Hedef: </span>
                          {schedule.sale_details?.target_installation_date ? 
                            <span className="cursor-pointer hover:underline" onClick={() => openTargetDateDialog(schedule)}>
                              {new Date(schedule.sale_details.target_installation_date).toLocaleDateString('tr-TR')}
                            </span> :
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openTargetDateDialog(schedule)}
                              className="text-blue-600 border-blue-600 hover:bg-blue-50 h-6 px-2 text-xs"
                            >
                              Hedef Belirle
                            </Button>
                          }
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        {schedule.sale_details && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => openSaleDetailsDialog(schedule)}
                            className="w-full text-blue-600 hover:text-blue-700"
                            data-view-allowed="true"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Sipariş Detayı
                          </Button>
                        )}
                        {!isInstallationPersonnel && schedule.status === 'beklemede' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openAssignDialog(schedule)}
                            className="w-full"
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Montajcı Ata
                          </Button>
                        )}
                        {!isInstallationPersonnel && (
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleReturnToProduction(schedule)}
                            className="w-full"
                          >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Üretime Geri Gönder
                          </Button>
                        )}
                        {!isInstallationPersonnel && (
                          <Select 
                            value={schedule.status} 
                            onValueChange={(value) => handleStatusChange(schedule.id, value)}
                          >
                            <SelectTrigger className="w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beklemede">BEKLEMEDE</SelectItem>
                              <SelectItem value="planlandi">PLANLANDI</SelectItem>
                              <SelectItem value="tamamlandi">TAMAMLANDI</SelectItem>
                              <SelectItem value="iptal_edildi">İPTAL EDİLDİ</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-12">
                <Wrench className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg text-gray-600">
                  {scheduleSearch.trim() && `"${scheduleSearch}" aramasına uygun montaj bulunamadı`}
                  {!scheduleSearch.trim() && status === 'hazir_melamin' && 'Hazır melamin montaj bulunmamaktadır'}
                  {!scheduleSearch.trim() && status === 'hazir_boyahane' && 'Boyanmış lake kapı bulunmamaktadır'}
                  {!scheduleSearch.trim() && status === 'planlandi' && 'Planlanmış montaj bulunmamaktadır'}
                  {!scheduleSearch.trim() && status === 'tamamlandi' && 'Tamamlanmış montaj bulunmamaktadır'}
                </p>
                <p className="text-sm text-gray-500">İlk montaj programınızı oluşturmak için "Yeni Montaj" butonuna tıklayın</p>
              </div>
            )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Satış Detayları Dialog'u */}
      <Dialog open={saleDetailsDialogOpen} onOpenChange={setSaleDetailsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="no-print">
                <DialogTitle>Sipariş Detayları</DialogTitle>
                <DialogDescription>
                  {selectedSaleDetails && `${selectedSaleDetails.customer_name} - Sipariş ve Montaj Bilgileri`}
                </DialogDescription>
              </div>
              <Button onClick={() => {
                // CRITICAL FIX: Hide all other print areas before printing
                const allPrintAreas = document.querySelectorAll('.print-area');
                const activePrintArea = document.getElementById('montaj-print-area');
                
                allPrintAreas.forEach(area => {
                  if (area !== activePrintArea) {
                    area.style.display = 'none';
                  }
                });
                
                window.print();
                
                setTimeout(() => {
                  allPrintAreas.forEach(area => {
                    area.style.display = '';
                  });
                }, 100);
              }} variant="outline" className="no-print">
                <Printer className="h-4 w-4 mr-2" />
                Yazdır
              </Button>
            </div>
          </DialogHeader>
          
          {selectedSaleDetails && selectedSaleDetails.sale_details && (
            <div className="space-y-4 print-area" id="montaj-print-area">
              {/* Üst Bilgiler - Sayfanın 1/4'ü */}
              <div className="print-header-section">
                {/* Müşteri Bilgileri */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Müşteri Bilgileri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Müşteri:</strong> {selectedSaleDetails.customer_name}</p>
                    </div>
                    <div>
                      <p className="font-medium text-sm"><strong>Cari Kod:</strong> {selectedSaleDetails.customer_serial || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        <strong>Telefon:</strong> 
                        <a href={`tel:${selectedSaleDetails.customer_phone}`} className="text-blue-600 hover:underline cursor-pointer ml-1">
                          📞 {selectedSaleDetails.customer_phone || 'N/A'}
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-sm"><strong>Montaj Durumu:</strong> {selectedSaleDetails.status?.toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 mt-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Adres:</strong></p>
                      <p className="text-sm bg-white p-2 rounded border">{selectedSaleDetails.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Sipariş Bilgileri */}
                <div className="bg-green-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Sipariş Bilgileri</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Sipariş Tarihi:</strong> {new Date(selectedSaleDetails.sale_details.created_at).toLocaleDateString('tr-TR')}</p>
                    </div>
                    <div>
                      <p className="font-medium text-sm"><strong>Montaj Durumu:</strong> {selectedSaleDetails.status?.toUpperCase()}</p>
                    </div>
                    {/* Hedef Montaj Tarihi */}
                    <div>
                      {selectedSaleDetails.target_installation_date ? (
                        <p className="font-medium text-sm">
                          <strong>🎯 Hedef Montaj:</strong> 
                          <span className="text-blue-600 ml-1">
                            {new Date(selectedSaleDetails.target_installation_date).toLocaleDateString('tr-TR')}
                          </span>
                        </p>
                      ) : (
                        <p className="font-medium text-sm text-gray-500">
                          <strong>🎯 Hedef Montaj:</strong> Belirlenmemiş
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ürün Özellikleri */}
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Ürün Özellikleri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {selectedSaleDetails.sale_details.model && (
                      <div>
                        <p className="font-medium text-sm"><strong>Model:</strong> {selectedSaleDetails.sale_details.model}</p>
                      </div>
                    )}
                    {selectedSaleDetails.sale_details.color && (
                      <div>
                        <p className="font-medium text-sm"><strong>Renk:</strong> {selectedSaleDetails.sale_details.color}</p>
                      </div>
                    )}
                    {selectedSaleDetails.sale_details.door_handle && (
                      <div>
                        <p className="font-medium text-sm"><strong>Kapı Kolu:</strong> {selectedSaleDetails.sale_details.door_handle}</p>
                      </div>
                    )}
                    {selectedSaleDetails.sale_details.surface && (
                      <div>
                        <p className="font-medium text-sm"><strong>Yüzey:</strong> {selectedSaleDetails.sale_details.surface}</p>
                      </div>
                    )}
                    {selectedSaleDetails.sale_details.installation && (
                      <div>
                        <p className="font-medium text-sm"><strong>Montaj:</strong> {selectedSaleDetails.sale_details.installation}</p>
                      </div>
                    )}
                    {selectedSaleDetails.sale_details.installation_location && (
                      <div>
                        <p className="font-medium text-sm"><strong>Montaj Yeri:</strong> {selectedSaleDetails.sale_details.installation_location}</p>
                      </div>
                    )}
                    {selectedSaleDetails.sale_details.inner_frame && (
                      <div>
                        <p className="font-medium text-sm"><strong>İç Pervaz:</strong> {selectedSaleDetails.sale_details.inner_frame}</p>
                      </div>
                    )}
                    {selectedSaleDetails.sale_details.outer_frame && (
                      <div>
                        <p className="font-medium text-sm"><strong>Dış Pervaz:</strong> {selectedSaleDetails.sale_details.outer_frame}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Nihai Müşteri */}
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <h3 className="font-semibold mb-2 text-sm">🏢 Nihai Müşteri</h3>
                  <p className="font-medium text-sm">
                    {selectedSaleDetails.sale_details.end_customer || 'Belirtilmemiş'}
                  </p>
                </div>
              </div>

              {/* Ölçü Tabloları - Sayfanın 3/4'ü */}
              <div className="print-measurements-section">
                {selectedSaleDetails.sale_details.measurements && (() => {
                  let measurements = [];
                  try {
                    measurements = JSON.parse(selectedSaleDetails.sale_details.measurements);
                  } catch (e) {
                    console.error("Ölçü verileri parse edilemedi:", e);
                    return null;
                  }
                  
                  const validMeasurements = measurements.filter(m => 
                    m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description
                  );

                  if (validMeasurements.length === 0) return null;

                  return (
                    <div className="space-y-3">
                      {/* Orijinal Ölçüler */}
                      <div className="bg-blue-50 p-3 rounded-lg measurement-table">
                        <h3 className="font-semibold mb-2 text-sm">Orijinal Ölçüler</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Kasa</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Oda</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((measurement, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-300 p-1 font-medium">
                                    {index + 1}
                                    {measurement.quantity && measurement.quantity > 1 && (
                                      <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                        x{measurement.quantity}
                                      </span>
                                    )}
                                  </td>
                                  <td className="border border-gray-300 p-1">{measurement.width || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.height || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.frame || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.room || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Üretim Ölçüleri */}
                      <div className="bg-red-50 p-3 rounded-lg measurement-table">
                        <h3 className="font-semibold mb-2 text-sm">KANAT ÖLÇÜSÜ</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((measurement, index) => {
                                const originalWidth = parseFloat(measurement.width) || 0;
                                const originalHeight = parseFloat(measurement.height) || 0;
                                const productionWidth = originalWidth > 6 ? originalWidth - 6 : originalWidth;
                                const productionHeight = originalHeight > 4 ? originalHeight - 4 : originalHeight;
                                
                                return (
                                  <tr key={index}>
                                    <td className="border border-gray-300 p-1 font-medium">
                                      {index + 1}
                                      {measurement.quantity && measurement.quantity > 1 && (
                                        <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                          x{measurement.quantity}
                                        </span>
                                      )}
                                    </td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">
                                      {productionWidth > 0 ? productionWidth : '-'}
                                    </td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">
                                      {productionHeight > 0 ? productionHeight : '-'}
                                    </td>
                                    <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                    <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                    <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* İmalat İçin Önemli Notlar */}
              {selectedSaleDetails.sale_details.description && (
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">⚠️ İmalat İçin Önemli Notlar</h3>
                  <p className="text-sm whitespace-pre-wrap font-medium">{selectedSaleDetails.sale_details.description}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Montajcı Atama Dialog'u */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Montajcı Ata</DialogTitle>
            <DialogDescription>
              {selectedSchedule && `${selectedSchedule.customer_name} için montajcı seçin`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="installer_select">Montajcı</Label>
              <Select value={selectedInstaller} onValueChange={setSelectedInstaller}>
                <SelectTrigger>
                  <SelectValue placeholder="Montajcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {installers.map((installer) => (
                    <SelectItem key={installer.id} value={installer.id}>
                      {installer.name} {installer.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedSchedule && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700">Montaj Detayları:</h4>
                <p className="text-sm"><strong>Müşteri:</strong> {selectedSchedule.customer_name}</p>
                <p className="text-sm"><strong>Kapı Türü:</strong> {selectedSchedule.door_type}</p>
                <p className="text-sm"><strong>Adres:</strong> {selectedSchedule.address}</p>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleAssignInstaller}>
                <UserCheck className="h-4 w-4 mr-2" />
                Montajcı Ata
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tarih Seçme Dialog */}
      <Dialog open={isDateDialogOpen} onOpenChange={setIsDateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Montaj Tarihi Seç</DialogTitle>
            <DialogDescription>
              {selectedSchedule && `${selectedSchedule.customer_name} için montaj tarihi belirleyin`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Montaj Tarihi</Label>
              <Input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
              />
            </div>
            {selectedSchedule && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700">Montaj Detayları:</h4>
                <p className="text-sm"><strong>Müşteri:</strong> {selectedSchedule.customer_name}</p>
                <p className="text-sm"><strong>Kapı Türü:</strong> {selectedSchedule.door_type}</p>
                <p className="text-sm"><strong>Adres:</strong> {selectedSchedule.address}</p>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsDateDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleSetDate}>
                <CalendarIcon className="h-4 w-4 mr-2" />
                Tarihi Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hedef Montaj Tarihi Seçme Dialog */}
      <Dialog open={isTargetDateDialogOpen} onOpenChange={setIsTargetDateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hedef Montaj Tarihi Belirle</DialogTitle>
            <DialogDescription>
              {selectedSchedule && `${selectedSchedule.customer_name} için hedef montaj tarihi belirleyin (Bilgi amaçlı)`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Hedef Montaj Tarihi</Label>
              <Input
                type="date"
                value={selectedTargetDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedTargetDate(new Date(e.target.value))}
              />
              <p className="text-xs text-blue-600 mt-1">
                Bu tarih bilgi amaçlıdır ve istediğiniz zaman değiştirilebilir
              </p>
            </div>
            {selectedSchedule && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <h4 className="font-medium text-sm text-blue-700">Montaj Detayları:</h4>
                <p className="text-sm"><strong>Müşteri:</strong> {selectedSchedule.customer_name}</p>
                <p className="text-sm"><strong>Kapı Türü:</strong> {selectedSchedule.door_type}</p>
                <p className="text-sm"><strong>Adres:</strong> {selectedSchedule.address}</p>
                {selectedSchedule.installation_date && (
                  <p className="text-sm"><strong>Mevcut Montaj Tarihi:</strong> {new Date(selectedSchedule.installation_date).toLocaleDateString('tr-TR')}</p>
                )}
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsTargetDateDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleSetTargetDate}>
                <CalendarIcon className="h-4 w-4 mr-2" />
                Hedef Tarihi Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Production Component
const Production = ({ currentUser, sessionToken }) => {
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const prodPerm = (key) => (currentUser?.role === 'admin' ? 'edit' : (currentUser?.production_tab_permissions?.[key] || 'edit'));
  const lineVisible = (line) => PRODUCTION_TABS.some((t) => t.key.startsWith(`${line}_`) && prodPerm(t.key) !== 'hidden');
  const visibleLineCount = (line) => PRODUCTION_TABS
    .filter((t) => t.key.startsWith(`${line}_`) && prodPerm(t.key) !== 'hidden')
    .reduce((sum, t) => sum + (productions[line]?.[t.key.split('_')[1]]?.length || 0), 0);
  const [productions, setProductions] = useState({
    lake: { 
      beklemede: [], 
      uretimde: [], 
      bodrum_boyahane: [], 
      birinci_kat_boyahane: [], 
      ikinci_kat_boyahane: [] 
    },
    melamin: { beklemede: [], uretimde: [], hazir: [] }
  });
  const [loading, setLoading] = useState(true);
  const [activeProductionLine, setActiveProductionLine] = useState('lake');
  const [activeStatus, setActiveStatus] = useState('beklemede');
  useEffect(() => {
    const visible = PRODUCTION_TABS.filter((t) => t.key.startsWith(`${activeProductionLine}_`) && prodPerm(t.key) !== 'hidden');
    if (visible.length && !visible.some((t) => t.key === `${activeProductionLine}_${activeStatus}`)) {
      setActiveStatus(visible[0].key.split('_')[1]);
    }
    if (!lineVisible(activeProductionLine)) {
      const other = activeProductionLine === 'lake' ? 'melamin' : 'lake';
      if (lineVisible(other)) setActiveProductionLine(other);
    }
  }, [activeProductionLine, activeStatus, currentUser]);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isAssignBoyahaneOpen, setIsAssignBoyahaneOpen] = useState(false);
  const [selectedBoyahane, setSelectedBoyahane] = useState('');
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // ⚡ NEW: Montaj tarihi belirleme state'leri
  const [isInstallationDateDialogOpen, setIsInstallationDateDialogOpen] = useState(false);
  const [selectedProductionForDate, setSelectedProductionForDate] = useState(null);
  const [newInstallationDate, setNewInstallationDate] = useState('');

  // Check if current user is installation personnel
  const isInstallationPersonnel = currentUser?.role === 'installation';

  useEffect(() => {
    fetchProductions('lake');
    fetchProductions('melamin');
  }, []);

  const fetchProductions = async (productionLine) => {
    try {
      const response = await axios.get(`${API}/productions/by-line/${productionLine}`);
      
      // Sıralama fonksiyonu - Kullanıcının istediği öncelik sıralaması
      const sortProductionsByPriority = (productions) => {
        return productions.sort((a, b) => {
          // 1. Hedef montaj tarihi olanlar öncelikli
          const aHasTarget = a.target_installation_date;
          const bHasTarget = b.target_installation_date;
          
          // Hedef tarihi olmayanlar en sona
          if (aHasTarget && !bHasTarget) return -1;
          if (!aHasTarget && bHasTarget) return 1;
          
          // 2. İkisinin de hedef tarihi varsa, hedef tarihe göre sırala (yakın olanlar önce)
          if (aHasTarget && bHasTarget) {
            const aTargetDate = new Date(a.target_installation_date);
            const bTargetDate = new Date(b.target_installation_date);
            const dateDiff = aTargetDate - bTargetDate;
            
            // Aynı hedef tarihleri varsa sipariş tarihi göre sırala (eski sipariş önce)
            if (dateDiff === 0) {
              return new Date(a.created_at) - new Date(b.created_at);
            }
            return dateDiff;
          }
          
          // 3. İkisinin de hedef tarihi yoksa, sipariş tarihi göre sırala (eski olanlar önce)
          return new Date(a.created_at) - new Date(b.created_at);
        });
      };
      
      // Her status kategorisini sırala
      const sortedData = {
        beklemede: sortProductionsByPriority([...(response.data.beklemede || [])]),
        uretimde: sortProductionsByPriority([...(response.data.uretimde || [])]),
        hazir: sortProductionsByPriority([...(response.data.hazir || [])]),
        bodrum_boyahane: sortProductionsByPriority([...(response.data.bodrum_boyahane || [])]),
        birinci_kat_boyahane: sortProductionsByPriority([...(response.data.birinci_kat_boyahane || [])]),
        ikinci_kat_boyahane: sortProductionsByPriority([...(response.data.ikinci_kat_boyahane || [])]),
        boyahane: sortProductionsByPriority([...(response.data.boyahane || [])])
      };
      
      setProductions(prev => ({
        ...prev,
        [productionLine]: sortedData
      }));
      setActivityRefreshKey(k => k + 1);
      setLoading(false);
    } catch (error) {
      console.error(`${productionLine} üretim verileri yüklenirken hata:`, error);
      toast.error(`${productionLine} üretim verileri yüklenirken hata oluştu`);
      setLoading(false);
    }
  };

  const updateProductionStatus = async (productionId, newStatus) => {
    try {
      await axios.put(`${API}/productions/${productionId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success("Üretim durumu güncellendi");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Üretim durumu güncellenirken hata:", error);
      toast.error("Üretim durumu güncellenirken hata oluştu");
    }
  };

  const assignToBoyahane = async () => {
    if (!selectedProduction || !selectedBoyahane) {
      toast.error("Lütfen boyahane seçin");
      return;
    }

    try {
      await axios.put(`${API}/productions/${selectedProduction.id}/assign-boyahane`, null, {
        params: { boyahane: selectedBoyahane, session_token: sessionToken }
      });
      toast.success("Boyahane seçimi yapıldı ve ürün montaj programına aktarıldı");
      setIsAssignBoyahaneOpen(false);
      setSelectedProduction(null);
      setSelectedBoyahane('');
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Boyahane atama hatası:", error);
      toast.error("Boyahane ataması yapılırken hata oluştu");
    }
  };

  const openBoyahaneDialog = (production) => {
    setSelectedProduction(production);
    setSelectedBoyahane('');
    setIsAssignBoyahaneOpen(true);
  };

  const transferToInstallation = async (productionId) => {
    try {
      await axios.post(`${API}/productions/${productionId}/transfer-to-installation`, null, { params: { session_token: sessionToken } });
      toast.success("Ürün başarıyla montaj programına aktarıldı");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Montaja aktarma hatası:", error);
      toast.error("Montaja aktarma sırasında hata oluştu");
    }
  };

  const sendToBoyahane = async (production) => {
    if (!window.confirm(`${production.customer_name} siparişi boyahaneye aktarılacak. Onaylıyor musunuz?`)) return;
    try {
      await axios.put(`${API}/productions/${production.id}/send-to-boyahane`, null, { params: { session_token: sessionToken } });
      toast.success(`${production.customer_name} boyahaneye aktarıldı`);
      fetchProductions('lake');
    } catch (error) {
      console.error("Boyahaneye aktarma hatası:", error);
      toast.error(error.response?.data?.detail || "Boyahaneye aktarılırken hata oluştu");
    }
  };

  const transferFromBoyahane = async (productionId) => {
    if (!window.confirm("Sipariş boyandı olarak işaretlenip montaj sayfasına aktarılacak. Onaylıyor musunuz?")) return;
    try {
      await axios.post(`${API}/productions/${productionId}/transfer-from-boyahane`, null, { params: { session_token: sessionToken } });
      toast.success("Ürün boyahaneden başarıyla montaj programına aktarıldı");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Boyahaneden montaja aktarma hatası:", error);
      toast.error("Boyahaneden montaja aktarırken hata oluştu");
    }
  };

  const returnFromBoyahane = async (productionId) => {
    try {
      await axios.post(`${API}/productions/${productionId}/return-from-boyahane`, null, { params: { session_token: sessionToken } });
      toast.success("Ürün boyahaneden üretim ekranına başarıyla geri döndürüldü");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Boyahaneden üretim ekranına döndürme hatası:", error);
      toast.error("Boyahaneden üretim ekranına döndürme sırasında hata oluştu");
    }
  };

  const deleteProduction = async (productionId) => {
    // Güvenlik onayı iste
    const confirmed = window.confirm("⚠️ ÜRETİM KAYDINI SİLME ONAYI\n\nBu üretim kaydını silmek istediğinizden emin misiniz?\n\n• Bu işlem geri alınamaz\n• İlişkili veriler de etkilenebilir\n• Silinen kayıt kurtarılamaz\n\nSilmek için 'Tamam'a, iptal etmek için 'İptal'e basın.");
    
    if (!confirmed) {
      return; // Kullanıcı iptal etti
    }

    try {
      await axios.delete(`${API}/productions/${productionId}`);
      toast.success("Üretim kaydı güvenli bir şekilde silindi");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Üretim kaydı silinirken hata:", error);
      toast.error("Üretim kaydı silinirken hata oluştu");
    }
  };

  const markCncComplete = async (productionId) => {
    try {
      await axios.put(`${API}/productions/${productionId}/cnc-complete`);
      toast.success("CNC işlemi durumu güncellendi");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("CNC işlemi işaretlenirken hata:", error);
      toast.error(error.response?.data?.detail || "CNC işlemi işaretlenirken hata oluştu");
    }
  };

  const markAksesuarComplete = async (productionId) => {
    try {
      await axios.put(`${API}/productions/${productionId}/aksesuar-complete`);
      toast.success("Aksesuar hazırlama durumu güncellendi");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Aksesuar işlemi işaretlenirken hata:", error);
      toast.error(error.response?.data?.detail || "Aksesuar işlemi işaretlenirken hata oluştu");
    }
  };


  const toggleProductionPainted = async (productionId) => {
    try {
      const response = await axios.put(`${API}/productions/${productionId}/toggle-painted`, null, {
        params: { session_token: sessionToken }
      });
      toast.success(response.data.message || "Boya/Ebatlama durumu güncellendi");
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Boya/Ebatlama işlemi işaretlenirken hata:", error);
      toast.error(error.response?.data?.detail || "Boya/Ebatlama işlemi işaretlenirken hata oluştu");
    }
  };

  // ⚡ NEW: Montaj tarihi belirleme/değiştirme fonksiyonları
  const openInstallationDateDialog = (production) => {
    setSelectedProductionForDate(production);
    // Production'daki planlanan montaj tarihini al
    setNewInstallationDate(production.planned_installation_date || '');
    setIsInstallationDateDialogOpen(true);
  };

  const updateInstallationDate = async () => {
    if (!newInstallationDate) {
      toast.error("Lütfen montaj tarihi seçin");
      return;
    }

    try {
      // Production kaydının planlanan montaj tarihini güncelle
      await axios.put(
        `${API}/productions/${selectedProductionForDate.id}/planned-installation-date`,
        null,
        { 
          params: { 
            planned_date: newInstallationDate,
            session_token: sessionToken 
          } 
        }
      );
      
      toast.success("Planlanan montaj tarihi güncellendi");
      
      setIsInstallationDateDialogOpen(false);
      setSelectedProductionForDate(null);
      setNewInstallationDate('');
      
      // Üretim listesini yenile
      fetchProductions('lake');
      fetchProductions('melamin');
    } catch (error) {
      console.error("Montaj tarihi güncellenirken hata:", error);
      toast.error(error.response?.data?.detail || "Montaj tarihi güncellenirken hata oluştu");
    }
  };



  const openProductionDetails = async (production) => {
    try {
      setSelectedProduction(production);
      setIsDetailsOpen(true);
      setIsDetailsLoading(true);
      
      // Transaction detaylarını ID'ye göre getir (pagination sorunu çözümü)
      const transactionResponse = await axios.get(`${API}/transactions/${production.transaction_id}`, {
        params: { session_token: sessionToken }
      });
      setTransactionDetails(transactionResponse.data);
      
      // Müşteri detaylarını ID'ye göre getir (pagination sorunu çözümü)
      const customerResponse = await axios.get(`${API}/customers/${production.customer_id}`);
      setCustomerDetails(customerResponse.data);
      
      setIsDetailsLoading(false);
    } catch (error) {
      console.error("Sipariş detayları yüklenirken hata:", error);
      toast.error("Sipariş detayları yüklenirken hata oluştu");
      setIsDetailsLoading(false);
    }
  };

  const handlePrint = () => {
    // CRITICAL FIX: Hide all other print areas before printing
    const allPrintAreas = document.querySelectorAll('.print-area');
    const activePrintArea = document.getElementById('uretim-print-area');
    
    // Hide all print areas except the active one
    allPrintAreas.forEach(area => {
      if (area !== activePrintArea) {
        area.style.display = 'none';
      }
    });
    
    // Print
    window.print();
    
    // Restore display after print
    setTimeout(() => {
      allPrintAreas.forEach(area => {
        area.style.display = '';
      });
    }, 100);
  };

  const handleDownloadPDF = async () => {
    try {
      // Yazdırma alanını bul
      const printArea = document.getElementById('uretim-print-area');
      if (!printArea) {
        toast.error('PDF oluşturulamadı');
        return;
      }

      // Loading toast
      toast.info('PDF oluşturuluyor...');

      // HTML'i canvas'a çevir
      const canvas = await html2canvas(printArea, {
        scale: 2, // Yüksek kalite için
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Canvas'ı PDF'e çevir
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Oranı koru
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      // PDF'i indir
      const customerCode = customerDetails?.serial_number || 'N/A';
      const fileName = `${customerCode}_Siparis_Detay_${selectedProduction?.customer_name}_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.pdf`;
      pdf.save(fileName);
      
      toast.success('PDF başarıyla indirildi');
    } catch (error) {
      console.error('PDF oluşturulurken hata:', error);
      toast.error('PDF oluşturulurken hata oluştu');
    }
  };

  const handlePrintPDF = async () => {
    try {
      // Yazdırma alanını bul
      const printArea = document.getElementById('uretim-print-area');
      if (!printArea) {
        toast.error('PDF oluşturulamadı');
        return;
      }

      // Loading toast
      toast.info('PDF yazdırma ekranı hazırlanıyor...');

      // HTML'i canvas'a çevir
      const canvas = await html2canvas(printArea, {
        scale: 2, // Yüksek kalite için
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Canvas'ı PDF'e çevir
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Oranı koru
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      // PDF'i indirmeden yazdır
      // Blob oluştur
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Yeni pencerede aç ve yazdır
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          // URL'i temizle (bellek sızıntısını önle)
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
        };
        toast.success('PDF yazdırma ekranı açıldı');
      } else {
        toast.error('Pop-up engellenmiş olabilir. Lütfen pop-up engellemeyi kaldırın.');
      }
    } catch (error) {
      console.error('PDF yazdırma hatası:', error);
      toast.error('PDF yazdırma sırasında hata oluştu');
    }
  };

  const currentProductionData = productions[activeProductionLine] || { 
    beklemede: [], 
    uretimde: [], 
    hazir: [], 
    bodrum_boyahane: [], 
    birinci_kat_boyahane: [], 
    ikinci_kat_boyahane: [] 
  };
  
  // Müşteri ismine göre filtreleme fonksiyonu
  const filterBySearch = (items) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  // Her durum için filtrelenmiş listeler
  const filteredBeklemede = filterBySearch(currentProductionData.beklemede || []);
  const filteredUretimde = filterBySearch(currentProductionData.uretimde || []);
  const filteredBoyahane = filterBySearch(currentProductionData.boyahane || []);
  const filteredHazir = filterBySearch(currentProductionData.hazir || []);

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Üretim Planlaması</h1>
          <p className="text-gray-600">Üretim hatlarına göre sipariş takibi ve yönetimi</p>
        </div>
        <div className="w-80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Müşteri adı ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      <StaffActivityLog api={API} sessionToken={sessionToken} refreshKey={activityRefreshKey} />

      {/* Üretim Hattı Sekmeleri */}
      <Tabs value={activeProductionLine} onValueChange={setActiveProductionLine}>
        <TabsList className={`grid w-full ${lineVisible('lake') && lineVisible('melamin') ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {lineVisible('lake') && <TabsTrigger value="lake" data-testid="production-line-lake">
            <Factory className="h-4 w-4 mr-2" />
            LAKE ({visibleLineCount('lake')})
          </TabsTrigger>}
          {lineVisible('melamin') && <TabsTrigger value="melamin" data-testid="production-line-melamin">
            <Factory className="h-4 w-4 mr-2" />
            MELAMİN ({visibleLineCount('melamin')})
          </TabsTrigger>}
        </TabsList>

        <TabsContent value="lake" className="space-y-4">
          {/* Status Sekmeleri */}
          <Tabs value={activeStatus} onValueChange={setActiveStatus}>
            <TabsList className={`grid w-full grid-cols-${Math.max(1, PRODUCTION_TABS.filter((t) => t.key.startsWith("lake_") && prodPerm(t.key) !== "hidden").length)}`}>
              {prodPerm("lake_beklemede") !== "hidden" && <TabsTrigger value="beklemede" className="text-orange-700">
                BEKLEMEDEKİ SİPARİŞLER ({productions.lake.beklemede?.length || 0})
              </TabsTrigger>}
              {prodPerm("lake_uretimde") !== "hidden" && <TabsTrigger value="uretimde" className="text-blue-700">
                ÜRETİMDE ({productions.lake.uretimde?.length || 0})
              </TabsTrigger>}
              {prodPerm("lake_boyahane") !== "hidden" && <TabsTrigger value="boyahane" className="text-purple-700" data-testid="lake-tab-boyahane">
                BOYAHANE ({productions.lake.boyahane?.length || 0})
              </TabsTrigger>}
            </TabsList>

            <TabsContent value="boyahane">
              <ViewOnlyWrap perm={prodPerm("lake_boyahane")} testId="production-viewonly-lake_boyahane">
              <BoyahaneList
                items={filteredBoyahane}
                canEdit={!isInstallationPersonnel}
                onDetails={openProductionDetails}
                onReturn={returnFromBoyahane}
                onPainted={transferFromBoyahane}
                onAksesuar={markAksesuarComplete}
              />
              </ViewOnlyWrap>
            </TabsContent>

            <TabsContent value="beklemede">
              <ViewOnlyWrap perm={prodPerm("lake_beklemede")} testId="production-viewonly-lake_beklemede">
              {filteredBeklemede?.length > 0 ? (
                <div className="grid gap-4">
                  {filteredBeklemede.map((production) => (
                    <Card key={production.id} className="border-orange-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{production.customer_name}</h3>
                            <p className="text-sm text-gray-600">{production.model} - {production.color}</p>
                            {/* Nihai Müşteri */}
                            {production.end_customer && (
                              <p className="text-xs text-blue-600 mt-1">
                                <User className="h-3 w-3 inline mr-1" />
                                Nihai Tüketici: {production.end_customer}
                              </p>
                            )}
                            {/* Hedef Montaj Tarihi */}
                            {production.target_installation_date && (
                              <p className="text-xs text-amber-600 font-medium mt-1">
                                🎯 Hedef Montaj: {new Date(production.target_installation_date).toLocaleDateString('tr-TR')}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openProductionDetails(production)}
                              data-view-allowed="true"
                              className="flex items-center space-x-1"
                            >
                              <Eye className="h-4 w-4" />
                              <span>Sipariş Detayı</span>
                            </Button>
                            {!isInstallationPersonnel && (
                              <>
                                {/* Montaj Tarihi Belirleme Butonu */}
                                <Button 
                                  size="sm" 
                                  variant={production.planned_installation_date ? "outline" : "default"}
                                  onClick={() => openInstallationDateDialog(production)}
                                  className={production.planned_installation_date ? "border-blue-500 text-blue-600" : "bg-purple-600 hover:bg-purple-700"}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {production.planned_installation_date ? (
                                    <>
                                      📅 {new Date(production.planned_installation_date).toLocaleDateString('tr-TR')}
                                    </>
                                  ) : (
                                    "Planlanan Belirle"
                                  )}
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => updateProductionStatus(production.id, 'uretimde')}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  Üretime Geç
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => deleteProduction(production.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Factory className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">LAKE üretim hattında bekleyen sipariş yok</p>
                </div>
              )}
              </ViewOnlyWrap>
            </TabsContent>

            <TabsContent value="uretimde">
              <ViewOnlyWrap perm={prodPerm("lake_uretimde")} testId="production-viewonly-lake_uretimde">
              {filteredUretimde?.length > 0 ? (
                <div className="grid gap-4">
                  {filteredUretimde.map((production) => (
                    <Card key={production.id} className="border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{production.customer_name}</h3>
                              {production.cnc_completed && (
                                <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded">
                                  ✅ CNC Tamamlandı
                                </span>
                              )}
                              {production.aksesuar_ready && (
                                <span className="bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-1 rounded">
                                  ✅ Aksesuar Hazır
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{production.model} - {production.color}</p>
                            {/* Nihai Müşteri */}
                            {production.end_customer && (
                              <p className="text-xs text-blue-600 mt-1">
                                <User className="h-3 w-3 inline mr-1" />
                                Nihai Tüketici: {production.end_customer}
                              </p>
                            )}
                            {/* Hedef Montaj Tarihi */}
                            {production.target_installation_date && (
                              <p className="text-xs text-amber-600 font-medium mt-1">
                                🎯 Hedef Montaj: {new Date(production.target_installation_date).toLocaleDateString('tr-TR')}
                              </p>
                            )}
                            {/* Planlanan Montaj Tarihi */}
                            {production.planned_installation_date && (
                              <p className="text-xs text-blue-600 font-medium mt-1">
                                📅 Planlanan Montaj: {new Date(production.planned_installation_date).toLocaleDateString('tr-TR')}
                              </p>
                            )}
                            {/* CNC Tamamlanma Tarihi */}
                            {production.cnc_completed && production.cnc_date && (
                              <p className="text-xs text-green-600 font-medium mt-1">
                                🔧 CNC: {new Date(production.cnc_date).toLocaleDateString('tr-TR')} {new Date(production.cnc_date).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openProductionDetails(production)}
                              data-view-allowed="true"
                              className="flex items-center space-x-1"
                            >
                              <Eye className="h-4 w-4" />
                              <span>Sipariş Detayı</span>
                            </Button>
                            {!isInstallationPersonnel && (
                              <>
                                {/* Montaj Tarihi Belirleme Butonu */}
                                <Button 
                                  size="sm" 
                                  variant={production.planned_installation_date ? "outline" : "default"}
                                  onClick={() => openInstallationDateDialog(production)}
                                  className={production.planned_installation_date ? "border-blue-500 text-blue-600" : "bg-purple-600 hover:bg-purple-700"}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {production.planned_installation_date ? (
                                    <>
                                      📅 {new Date(production.planned_installation_date).toLocaleDateString('tr-TR')}
                                    </>
                                  ) : (
                                    "Planlanan Belirle"
                                  )}
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => markCncComplete(production.id)}
                                  className={production.cnc_completed ? "bg-green-600 hover:bg-green-700" : "bg-cyan-600 hover:bg-cyan-700"}
                                >
                                  {production.cnc_completed ? "✅ CNC Tamamlandı" : "CNC İşlemi Tamamla"}
                                </Button>

                                <Button 
                                  size="sm" 
                                  onClick={() => updateProductionStatus(production.id, 'beklemede')}
                                  className="bg-orange-600 hover:bg-orange-700"
                                >
                                  Beklemede'ye Geri Al
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => sendToBoyahane(production)}
                                  className="bg-purple-600 hover:bg-purple-700"
                                  data-testid={`send-to-boyahane-${production.id}`}
                                >
                                  Boyahaneye Aktar
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => deleteProduction(production.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Factory className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">LAKE üretim hattında üretilen sipariş yok</p>
                </div>
              )}
              </ViewOnlyWrap>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="melamin" className="space-y-4">
          {/* Status Sekmeleri */}
          <Tabs value={activeStatus} onValueChange={setActiveStatus}>
            <TabsList className={`grid w-full grid-cols-${Math.max(1, PRODUCTION_TABS.filter((t) => t.key.startsWith("melamin_") && prodPerm(t.key) !== "hidden").length)}`}>
              {prodPerm("melamin_beklemede") !== "hidden" && <TabsTrigger value="beklemede" className="text-orange-700">
                BEKLEMEDEKİ SİPARİŞLER ({productions.melamin.beklemede?.length || 0})
              </TabsTrigger>}
              {prodPerm("melamin_uretimde") !== "hidden" && <TabsTrigger value="uretimde" className="text-blue-700">
                ÜRETİMDE ({productions.melamin.uretimde?.length || 0})
              </TabsTrigger>}
              {prodPerm("melamin_hazir") !== "hidden" && <TabsTrigger value="hazir" className="text-green-700">
                HAZIR ({productions.melamin.hazir?.length || 0})
              </TabsTrigger>}
            </TabsList>

            <TabsContent value="beklemede">
              <ViewOnlyWrap perm={prodPerm("melamin_beklemede")} testId="production-viewonly-melamin_beklemede">
              {filteredBeklemede?.length > 0 ? (
                <div className="grid gap-4">
                  {filteredBeklemede.map((production) => (
                    <Card key={production.id} className="border-orange-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{production.customer_name}</h3>
                            <p className="text-sm text-gray-600">{production.model} - {production.color}</p>
                            {/* Nihai Müşteri */}
                            {production.end_customer && (
                              <p className="text-xs text-blue-600 mt-1">
                                <User className="h-3 w-3 inline mr-1" />
                                Nihai Tüketici: {production.end_customer}
                              </p>
                            )}
                            {/* Hedef Montaj Tarihi */}
                            {production.target_installation_date && (
                              <p className="text-xs text-amber-600 font-medium mt-1">
                                🎯 Hedef Montaj: {new Date(production.target_installation_date).toLocaleDateString('tr-TR')}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openProductionDetails(production)}
                              data-view-allowed="true"
                              className="flex items-center space-x-1"
                            >
                              <Eye className="h-4 w-4" />
                              <span>Sipariş Detayı</span>
                            </Button>
                            {!isInstallationPersonnel && (
                              <>
                                {/* Montaj Tarihi Belirleme Butonu */}
                                <Button 
                                  size="sm" 
                                  variant={production.planned_installation_date ? "outline" : "default"}
                                  onClick={() => openInstallationDateDialog(production)}
                                  className={production.planned_installation_date ? "border-blue-500 text-blue-600" : "bg-purple-600 hover:bg-purple-700"}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {production.planned_installation_date ? (
                                    <>
                                      📅 {new Date(production.planned_installation_date).toLocaleDateString('tr-TR')}
                                    </>
                                  ) : (
                                    "Planlanan Belirle"
                                  )}
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => updateProductionStatus(production.id, 'uretimde')}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  Üretime Geç
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => deleteProduction(production.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Factory className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">MELAMİN üretim hattında bekleyen sipariş yok</p>
                </div>
              )}
              </ViewOnlyWrap>
            </TabsContent>

            <TabsContent value="uretimde">
              <ViewOnlyWrap perm={prodPerm("melamin_uretimde")} testId="production-viewonly-melamin_uretimde">
              {filteredUretimde?.length > 0 ? (
                <div className="grid gap-4">
                  {filteredUretimde.map((production) => (
                    <Card key={production.id} className="border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{production.customer_name}</h3>
                            <p className="text-sm text-gray-600">{production.model} - {production.color}</p>
                            {/* Nihai Müşteri */}
                            {production.end_customer && (
                              <p className="text-xs text-blue-600 mt-1">
                                <User className="h-3 w-3 inline mr-1" />
                                Nihai Tüketici: {production.end_customer}
                              </p>
                            )}
                            {/* Hedef Montaj Tarihi */}
                            {production.target_installation_date && (
                              <p className="text-xs text-amber-600 font-medium mt-1">
                                🎯 Hedef Montaj: {new Date(production.target_installation_date).toLocaleDateString('tr-TR')}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openProductionDetails(production)}
                              data-view-allowed="true"
                              className="flex items-center space-x-1"
                            >
                              <Eye className="h-4 w-4" />
                              <span>Sipariş Detayı</span>
                            </Button>
                            {!isInstallationPersonnel && (
                              <>
                                {/* Montaj Tarihi Belirleme Butonu */}
                                <Button 
                                  size="sm" 
                                  variant={production.planned_installation_date ? "outline" : "default"}
                                  onClick={() => openInstallationDateDialog(production)}
                                  className={production.planned_installation_date ? "border-blue-500 text-blue-600" : "bg-purple-600 hover:bg-purple-700"}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {production.planned_installation_date ? (
                                    <>
                                      📅 {new Date(production.planned_installation_date).toLocaleDateString('tr-TR')}
                                    </>
                                  ) : (
                                    "Planlanan Belirle"
                                  )}
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => markAksesuarComplete(production.id)}
                                  className={production.aksesuar_ready ? "bg-green-600 hover:bg-green-700" : "bg-indigo-600 hover:bg-indigo-700"}
                                >
                                  {production.aksesuar_ready ? "✅ Aksesuar Hazır" : "Aksesuar Hazırla"}
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => toggleProductionPainted(production.id)}
                                  className={production.is_painted ? "bg-green-600 hover:bg-green-700" : "bg-purple-600 hover:bg-purple-700"}
                                >
                                  {production.is_painted ? "✅ Ebatlandı" : "Ebatla"}
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => updateProductionStatus(production.id, 'beklemede')}
                                  className="bg-orange-600 hover:bg-orange-700"
                                >
                                  Beklemede'ye Geri Al
                                </Button>
                                {/* MELAMİN üretim hattı için boyahane seçimi yok, direkt montaja aktarılır */}
                                <Button 
                                  size="sm" 
                                  onClick={() => transferToInstallation(production.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  Montaja Aktar
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => deleteProduction(production.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Factory className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">MELAMİN üretim hattında üretilen sipariş yok</p>
                </div>
              )}
              </ViewOnlyWrap>
            </TabsContent>

            <TabsContent value="hazir">
              <ViewOnlyWrap perm={prodPerm("melamin_hazir")} testId="production-viewonly-melamin_hazir">
              {filteredHazir?.length > 0 ? (
                <div className="grid gap-4">
                  {filteredHazir.map((production) => (
                    <Card key={production.id} className="border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{production.customer_name}</h3>
                            <p className="text-sm text-gray-600">{production.model} - {production.color}</p>
                            {/* Nihai Müşteri */}
                            {production.end_customer && (
                              <p className="text-xs text-blue-600 mt-1">
                                <User className="h-3 w-3 inline mr-1" />
                                Nihai Tüketici: {production.end_customer}
                              </p>
                            )}
                            {/* Hedef Montaj Tarihi */}
                            {production.target_installation_date && (
                              <p className="text-xs text-amber-600 font-medium mt-1">
                                🎯 Hedef Montaj: {new Date(production.target_installation_date).toLocaleDateString('tr-TR')}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openProductionDetails(production)}
                              data-view-allowed="true"
                              className="flex items-center space-x-1"
                            >
                              <Eye className="h-4 w-4" />
                              <span>Sipariş Detayı</span>
                            </Button>
                            {!isInstallationPersonnel && (
                              <>
                                {/* Montaj Tarihi Belirleme Butonu */}
                                <Button 
                                  size="sm" 
                                  variant={production.planned_installation_date ? "outline" : "default"}
                                  onClick={() => openInstallationDateDialog(production)}
                                  className={production.planned_installation_date ? "border-blue-500 text-blue-600" : "bg-purple-600 hover:bg-purple-700"}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {production.planned_installation_date ? (
                                    <>
                                      📅 {new Date(production.planned_installation_date).toLocaleDateString('tr-TR')}
                                    </>
                                  ) : (
                                    "Planlanan Belirle"
                                  )}
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => transferToInstallation(production.id)}
                                  className="bg-purple-600 hover:bg-purple-700"
                                >
                                  Montaja Gönder
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => deleteProduction(production.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Factory className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">MELAMİN üretim hattında hazır sipariş yok</p>
                </div>
              )}
              </ViewOnlyWrap>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Sipariş Detayları Modal */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="no-print">
                <DialogTitle>Sipariş Detayları</DialogTitle>
                <DialogDescription>
                  {selectedProduction && `${selectedProduction.customer_name} - Sipariş ve Üretim Bilgileri`}
                </DialogDescription>
              </div>
              <div className="flex gap-2 no-print">
                <Button onClick={handlePrintPDF} variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  PDF Yazdır
                </Button>
                <Button onClick={handleDownloadPDF} variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  PDF İndir
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {isDetailsLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium">Sipariş detayları yükleniyor...</p>
              <p className="text-sm text-muted-foreground">Lütfen bekleyin</p>
            </div>
          ) : selectedProduction && transactionDetails && customerDetails ? (
            <div className="space-y-4 print-area" id="uretim-print-area">
              {/* Üst Bilgiler - Sayfanın 1/4'ü */}
              <div className="print-header-section">
                {/* Müşteri Bilgileri */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Müşteri Bilgileri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Müşteri:</strong> {selectedProduction.customer_name}</p>
                    </div>
                    <div>
                      <p className="font-medium text-sm"><strong>Cari Kod:</strong> {customerDetails?.serial_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        <strong>Telefon:</strong> 
                        <a href={`tel:${customerDetails?.phone}`} className="text-blue-600 hover:underline cursor-pointer ml-1">
                          📞 {customerDetails?.phone || 'N/A'}
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-sm"><strong>Üretim Hattı:</strong> {selectedProduction.production_line?.toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 mt-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Adres:</strong></p>
                      <p className="text-sm bg-white p-2 rounded border">{customerDetails?.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Sipariş Bilgileri */}
                <div className="bg-green-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Sipariş Bilgileri</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="font-medium text-sm"><strong>Sipariş Tarihi:</strong> {new Date(transactionDetails.created_at).toLocaleDateString('tr-TR')}</p>
                    </div>
                    <div>
                      <p className="font-medium text-sm"><strong>Durum:</strong> {selectedProduction.status?.toUpperCase()}</p>
                    </div>
                    {/* Hedef Montaj Tarihi */}
                    <div>
                      {selectedProduction.target_installation_date ? (
                        <p className="font-medium text-sm">
                          <strong>🎯 Hedef Montaj:</strong> 
                          <span className="text-blue-600 ml-1">
                            {new Date(selectedProduction.target_installation_date).toLocaleDateString('tr-TR')}
                          </span>
                        </p>
                      ) : (
                        <p className="font-medium text-sm text-gray-500">
                          <strong>🎯 Hedef Montaj:</strong> Belirlenmemiş
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ürün Özellikleri */}
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm">Ürün Özellikleri</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {transactionDetails.model && (
                      <div>
                        <p className="font-medium text-sm"><strong>Model:</strong> {transactionDetails.model}</p>
                      </div>
                    )}
                    {transactionDetails.color && (
                      <div>
                        <p className="font-medium text-sm"><strong>Renk:</strong> {transactionDetails.color}</p>
                      </div>
                    )}
                    {transactionDetails.door_handle && (
                      <div>
                        <p className="font-medium text-sm"><strong>Kapı Kolu:</strong> {transactionDetails.door_handle}</p>
                      </div>
                    )}
                    {transactionDetails.surface && (
                      <div>
                        <p className="font-medium text-sm"><strong>Yüzey:</strong> {transactionDetails.surface}</p>
                      </div>
                    )}
                    {transactionDetails.installation && (
                      <div>
                        <p className="font-medium text-sm"><strong>Montaj:</strong> {transactionDetails.installation}</p>
                      </div>
                    )}
                    {transactionDetails.installation_location && (
                      <div>
                        <p className="font-medium text-sm"><strong>Montaj Yeri:</strong> {transactionDetails.installation_location}</p>
                      </div>
                    )}
                    {transactionDetails.inner_frame && (
                      <div>
                        <p className="font-medium text-sm"><strong>İç Pervaz:</strong> {transactionDetails.inner_frame}</p>
                      </div>
                    )}
                    {transactionDetails.outer_frame && (
                      <div>
                        <p className="font-medium text-sm"><strong>Dış Pervaz:</strong> {transactionDetails.outer_frame}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Nihai Müşteri */}
                {transactionDetails.end_customer && (
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <h3 className="font-semibold mb-2 text-sm">🏢 Nihai Müşteri</h3>
                    <p className="font-medium text-sm">{transactionDetails.end_customer}</p>
                  </div>
                )}
              </div>

              {/* Ölçü Tabloları - Sayfanın 3/4'ü */}
              <div className="print-measurements-section">
                {transactionDetails.measurements && (() => {
                  let measurements = [];
                  try {
                    measurements = JSON.parse(transactionDetails.measurements);
                  } catch (e) {
                    console.error("Ölçü verileri parse edilemedi:", e);
                    return null;
                  }
                  
                  const validMeasurements = measurements.filter(m => 
                    m.width || m.height || m.frame || m.direction || m.glass || m.room || m.description
                  );

                  if (validMeasurements.length === 0) return null;

                  return (
                    <div className="space-y-3">
                      {/* Orijinal Ölçüler */}
                      <div className="bg-blue-50 p-3 rounded-lg measurement-table">
                        <h3 className="font-semibold mb-2 text-sm">Orijinal Ölçüler</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Kasa</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Oda</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((measurement, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-300 p-1 font-medium">
                                    {index + 1}
                                    {measurement.quantity && measurement.quantity > 1 && (
                                      <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                        x{measurement.quantity}
                                      </span>
                                    )}
                                  </td>
                                  <td className="border border-gray-300 p-1">{measurement.width || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.height || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.frame || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.room || '-'}</td>
                                  <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Üretim Ölçüleri */}
                      <div className="bg-red-50 p-3 rounded-lg measurement-table">
                        <h3 className="font-semibold mb-2 text-sm">KANAT ÖLÇÜSÜ</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 p-1">Sıra</th>
                                <th className="border border-gray-300 p-1">En</th>
                                <th className="border border-gray-300 p-1">Boy</th>
                                <th className="border border-gray-300 p-1">Yön</th>
                                <th className="border border-gray-300 p-1">Cam</th>
                                <th className="border border-gray-300 p-1">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validMeasurements.map((measurement, index) => {
                                const originalWidth = parseFloat(measurement.width) || 0;
                                const originalHeight = parseFloat(measurement.height) || 0;
                                const productionWidth = originalWidth > 6 ? originalWidth - 6 : originalWidth;
                                const productionHeight = originalHeight > 4 ? originalHeight - 4 : originalHeight;
                                
                                return (
                                  <tr key={index}>
                                    <td className="border border-gray-300 p-1 font-medium">
                                      {index + 1}
                                      {measurement.quantity && measurement.quantity > 1 && (
                                        <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                          x{measurement.quantity}
                                        </span>
                                      )}
                                    </td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">
                                      {productionWidth > 0 ? productionWidth : '-'}
                                    </td>
                                    <td className="border border-gray-300 p-1 font-medium text-red-600">
                                      {productionHeight > 0 ? productionHeight : '-'}
                                    </td>
                                    <td className="border border-gray-300 p-1">{measurement.direction || '-'}</td>
                                    <td className="border border-gray-300 p-1">{measurement.glass || '-'}</td>
                                    <td className="border border-gray-300 p-1">{measurement.description || '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* İmalat İçin Önemli Notlar - Compact */}
              <div className="print-notes-section">
                {selectedProduction.notes && (
                  <div className="bg-orange-50 p-2 rounded-lg border-2 border-orange-300">
                    <h3 className="font-semibold mb-1 text-sm">⚠️ İmalat İçin Önemli Notlar</h3>
                    <p className="text-xs font-medium whitespace-pre-wrap">{selectedProduction.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-yellow-600 mb-4" />
              <p className="text-lg font-medium">Sipariş detayları yüklenemedi</p>
              <p className="text-sm text-muted-foreground">Lütfen tekrar deneyin</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Boyahane Atama Dialog */}
      <Dialog open={isAssignBoyahaneOpen} onOpenChange={setIsAssignBoyahaneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Boyahane Seç</DialogTitle>
            <DialogDescription>
              {selectedProduction && `${selectedProduction.customer_name} için boyahane seçin`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="boyahane_select">Boyahane</Label>
              <Select value={selectedBoyahane} onValueChange={setSelectedBoyahane}>
                <SelectTrigger>
                  <SelectValue placeholder="Boyahane seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bodrum">Bodrum Boyahane</SelectItem>
                  <SelectItem value="birinci_kat">1.Kat Boyahane</SelectItem>
                  <SelectItem value="ikinci_kat">2.Kat Boyahane</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedProduction && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700">Ürün Detayları:</h4>
                <p className="text-sm"><strong>Müşteri:</strong> {selectedProduction.customer_name}</p>
                <p className="text-sm"><strong>Model:</strong> {selectedProduction.model} - {selectedProduction.color}</p>
                <p className="text-sm"><strong>Üretim Hattı:</strong> LAKE</p>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsAssignBoyahaneOpen(false)}>
                İptal
              </Button>
              <Button onClick={assignToBoyahane}>
                <Building2 className="h-4 w-4 mr-2" />
                Boyahaneye Ata
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ⚡ NEW: Montaj Tarihi Belirleme/Değiştirme Dialog */}
      <Dialog open={isInstallationDateDialogOpen} onOpenChange={setIsInstallationDateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedProductionForDate?.planned_installation_date ? "Planlanan Montaj Tarihini Değiştir" : "Planlanan Montaj Tarihi Belirle"}
            </DialogTitle>
            <DialogDescription>
              {selectedProductionForDate?.customer_name} - {selectedProductionForDate?.model}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="installation-date">Planlanan Montaj Tarihi</Label>
              <Input
                id="installation-date"
                type="date"
                value={newInstallationDate}
                onChange={(e) => setNewInstallationDate(e.target.value)}
                className="mt-1"
              />
            </div>
            {selectedProductionForDate?.target_installation_date && (
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>🎯 Hedef Montaj Tarihi:</strong>{' '}
                  {new Date(selectedProductionForDate.target_installation_date).toLocaleDateString('tr-TR')}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  (Müşterinin talep ettiği tarih)
                </p>
              </div>
            )}
            {selectedProductionForDate?.planned_installation_date && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>📅 Mevcut Planlanan Tarih:</strong>{' '}
                  {new Date(selectedProductionForDate.planned_installation_date).toLocaleDateString('tr-TR')}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  (Üretimde belirlenen planlanan tarih)
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsInstallationDateDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={updateInstallationDate} className="bg-purple-600 hover:bg-purple-700">
                <Calendar className="h-4 w-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Target Installations Component (Hedef Montaj Yönetimi)
const TargetInstallations = React.memo(({ currentUser, sessionToken }) => {
  const [targetData, setTargetData] = useState({
    unassigned: [],
    assigned: [],
    overdue: [],
    counts: {}
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("unassigned");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [newTargetDate, setNewTargetDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [installers, setInstallers] = useState([]);
  const [assignInstallerOpen, setAssignInstallerOpen] = useState(false);
  const [selectedForAssignment, setSelectedForAssignment] = useState(null);
  const [selectedInstallerId, setSelectedInstallerId] = useState("");

  // Admin kontrolü
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    fetchTargetInstallations();
    fetchInstallers();
  }, []);

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API}/users?session_token=${sessionToken}`);
      // Filter for installation role users
      const installationUsers = response.data.filter(user => user.role === 'installation');
      setInstallers(installationUsers);
    } catch (error) {
      console.error("Montajcılar yüklenirken hata:", error);
    }
  };

  const fetchTargetInstallations = async () => {
    try {
      const response = await axios.get(`${API}/target-installations?session_token=${sessionToken}`);
      setTargetData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Hedef montaj verileri yüklenirken hata:", error);
      toast.error("Hedef montaj verileri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const handleEditTargetDate = async () => {
    if (!selectedTransaction || !isAdmin) return;

    try {
      const formData = new FormData();
      formData.append('target_date', newTargetDate);
      formData.append('session_token', sessionToken);

      await axios.put(`${API}/transactions/${selectedTransaction.id}/target-date`, formData);
      
      toast.success("Hedef montaj tarihi güncellendi");
      setEditDialogOpen(false);
      setSelectedTransaction(null);
      setNewTargetDate("");
      fetchTargetInstallations(); // Verileri yenile
    } catch (error) {
      console.error("Hedef tarih güncellenirken hata:", error);
      toast.error("Hedef tarih güncellenirken hata oluştu");
    }
  };

  const openEditDialog = (transaction) => {
    if (!isAdmin) {
      toast.error("Bu işlem için yönetici yetkisi gerekli");
      return;
    }
    setSelectedTransaction(transaction);
    // Mevcut tarihi form'a set et (eğer varsa)
    if (transaction.target_installation_date) {
      const date = new Date(transaction.target_installation_date);
      setNewTargetDate(date.toISOString().split('T')[0]);
    } else {
      setNewTargetDate("");
    }
    setEditDialogOpen(true);
  };
  const handleViewDetails = async (transaction) => {
    try {
      // Detaylı bilgileri backend'den çek
      const response = await axios.get(
        `${API}/transactions/${transaction.id}/details?session_token=${sessionToken}`
      );
      setSelectedOrderDetails(response.data);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error('Sipariş detayları yüklenirken hata:', error);
      // Hata durumunda temel bilgileri göster
      setSelectedOrderDetails({ transaction });
      setDetailsDialogOpen(true);
    }
  };

  const handleAssignInstaller = async () => {
    if (!selectedForAssignment || !selectedInstallerId) {
      toast.error("Lütfen montajcı seçin");
      return;
    }

    try {
      // Get installer name
      const installer = installers.find(i => i.id === selectedInstallerId);
      if (!installer) {
        toast.error("Seçilen montajcı bulunamadı");
        return;
      }

      // Check if installation schedule exists
      const installationResponse = await axios.get(
        `${API}/installation-schedules?session_token=${sessionToken}`
      );
      const installation = installationResponse.data.find(
        inst => inst.transaction_id === selectedForAssignment.id
      );

      if (installation) {
        // Update existing installation schedule
        await axios.put(
          `${API}/installation-schedules/${installation.id}/assign-installer`,
          {
            installer_id: selectedInstallerId,
            installer_name: `${installer.name} ${installer.surname || ''}`.trim()
          },
          { params: { session_token: sessionToken } }
        );
        toast.success("Montajcı atandı");
      } else {
        toast.error("Bu sipariş için montaj programı oluşturulmamış");
      }

      setAssignInstallerOpen(false);
      setSelectedForAssignment(null);
      setSelectedInstallerId("");
      fetchTargetInstallations();
    } catch (error) {
      console.error("Montajcı atanırken hata:", error);
      toast.error(error.response?.data?.detail || "Montajcı atanırken hata oluştu");
    }
  };

  const openAssignInstallerDialog = (transaction) => {
    if (!isAdmin) {
      toast.error("Bu işlem için yönetici yetkisi gerekli");
      return;
    }
    setSelectedForAssignment(transaction);
    // Get existing installer if any
    if (transaction.installer_id) {
      setSelectedInstallerId(transaction.installer_id);
    } else {
      setSelectedInstallerId("");
    }
    setAssignInstallerOpen(true);
  };

  const formatCurrency = (amount) => {
    return `₺${Math.abs(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Belirlenmemiş";
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getStatusBadge = (transaction, tabType) => {
    if (tabType === "unassigned") {
      return <Badge className="bg-gray-100 text-gray-800">Belirlenmemiş</Badge>;
    } else if (tabType === "assigned") {
      return <Badge className="bg-blue-100 text-blue-800">Planlandı</Badge>;
    } else if (tabType === "overdue") {
      return <Badge className="bg-red-100 text-red-800">Geçmiş</Badge>;
    }
  };

  const renderTransactionCard = (transaction, tabType) => (
    <Card key={transaction.id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{transaction.customer_name}</h3>
            <p className="text-sm text-gray-600 mt-1">{transaction.description}</p>
            {transaction.end_customer && (
              <p className="text-xs text-blue-600 mt-1">
                <User className="h-3 w-3 inline mr-1" />
                Nihai Tüketici: {transaction.end_customer}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {getStatusBadge(transaction, tabType)}
            <span className="text-lg font-bold text-green-600">{formatCurrency(transaction.amount)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Sipariş Tarihi:</span>
            <p className="text-gray-600">{formatDate(transaction.created_at)}</p>
          </div>
          <div>
            <span className="font-medium">Hedef Montaj:</span>
            <p className={transaction.target_installation_date ? "text-blue-600" : "text-gray-400"}>
              {formatDate(transaction.target_installation_date)}
            </p>
          </div>
        </div>

        {(transaction.model || transaction.color || transaction.production_line) && (
          <div className="mt-3 p-2 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 space-y-1">
              {transaction.model && <div><strong>Model:</strong> {transaction.model}</div>}
              {transaction.color && <div><strong>Renk:</strong> {transaction.color}</div>}
              {transaction.production_line && <div><strong>Üretim Hattı:</strong> {transaction.production_line.toUpperCase()}</div>}
            </div>
          </div>
        )}

        {transaction.installer_name && (
          <div className="mt-2 p-2 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="text-xs flex items-center gap-1">
              <Wrench className="h-3 w-3 text-indigo-600" />
              <span className="font-medium text-indigo-700">Montajcı:</span>
              <span className="text-indigo-600">{transaction.installer_name}</span>
            </div>
          </div>
        )}

        <div className="mt-3 pt-3 border-t space-y-2">
          <Button 
            onClick={() => handleViewDetails(transaction)}
            variant="outline" 
            size="sm"
            className="w-full"
          >
            <Eye className="h-4 w-4 mr-2" />
            Sipariş Detayları
          </Button>
          {isAdmin && (
            <>
              <Button 
                onClick={() => openEditDialog(transaction)}
                variant="outline" 
                size="sm"
                className="w-full"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Hedef Tarih {transaction.target_installation_date ? 'Düzenle' : 'Belirle'}
              </Button>
              <Button 
                onClick={() => openAssignInstallerDialog(transaction)}
                variant="outline" 
                size="sm"
                className="w-full"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Montajcı {transaction.installer_name ? 'Değiştir' : 'Ata'}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  // Müşteri ismine göre filtreleme
  const getFilteredData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredUnassigned = getFilteredData(targetData.unassigned || []);
  const filteredAssigned = getFilteredData(targetData.assigned || []);
  const filteredOverdue = getFilteredData(targetData.overdue || []);

  // Tarih bazlı gruplama (assigned ve overdue için)
  const groupByDate = (items) => {
    const grouped = {};
    items.forEach(item => {
      const date = item.target_installation_date ? new Date(item.target_installation_date).toLocaleDateString('tr-TR') : 'Tarih Yok';
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(item);
    });
    return grouped;
  };

  const groupedAssigned = groupByDate(filteredAssigned);
  const groupedOverdue = groupByDate(filteredOverdue);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Hedef Montaj Yönetimi</h1>
          <p className="text-muted-foreground">
            Tüm satış işlemlerinin hedef montaj tarihlerini görüntüleyin ve yönetin
          </p>
        </div>
        <div className="w-80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Müşteri adı ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="unassigned" className="relative">
            Belirlenmemiş
            {targetData.counts.unassigned > 0 && (
              <Badge className="ml-2 bg-gray-200 text-gray-800">
                {targetData.counts.unassigned}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="assigned" className="relative">
            Belirlenmiş
            {targetData.counts.assigned > 0 && (
              <Badge className="ml-2 bg-blue-200 text-blue-800">
                {targetData.counts.assigned}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="overdue" className="relative">
            Geçmiş Tarihler
            {targetData.counts.overdue > 0 && (
              <Badge className="ml-2 bg-red-200 text-red-800">
                {targetData.counts.overdue}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="unassigned" className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Hedef Montaj Tarihi Belirlenmemiş İşlemler</h3>
            <p className="text-sm text-gray-600">
              Bu işlemler için henüz hedef montaj tarihi belirlenmemiştir. 
              {isAdmin ? " Tarih belirlemek için kartların üzerindeki düzenle butonunu kullanın." : " Tarih belirleme yetkisi yöneticide bulunmaktadır."}
              {searchTerm && ` (${filteredUnassigned.length} sonuç bulundu)`}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredUnassigned.length > 0 ? (
              filteredUnassigned.map(transaction => renderTransactionCard(transaction, "unassigned"))
            ) : (
              <div className="col-span-full text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg text-gray-600">
                  {searchTerm ? "Arama sonucu bulunamadı" : "Hedef tarihi belirlenmemiş işlem yok"}
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="assigned" className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Hedef Montaj Tarihi Belirlenmiş İşlemler</h3>
            <p className="text-sm text-gray-600">
              Bu işlemler için hedef montaj tarihleri belirlenmiştir.
              {searchTerm && ` (${filteredAssigned.length} sonuç bulundu)`}
            </p>
          </div>
          {Object.keys(groupedAssigned).length > 0 ? (
            <div className="space-y-6">
              {Object.keys(groupedAssigned)
                .sort((a, b) => {
                  if (a === 'Tarih Yok') return 1;
                  if (b === 'Tarih Yok') return -1;
                  const dateA = a.split('.').reverse().join('-');
                  const dateB = b.split('.').reverse().join('-');
                  return new Date(dateA) - new Date(dateB);
                })
                .map(date => (
                  <div key={date} className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-600 text-white px-4 py-2 rounded-lg">
                        <Calendar className="h-5 w-5 inline mr-2" />
                        <span className="font-semibold">{date}</span>
                        <Badge className="ml-2 bg-white text-blue-600">{groupedAssigned[date].length}</Badge>
                      </div>
                    </div>
                    <div className="space-y-2 pl-4 border-l-2 border-blue-300">
                      {groupedAssigned[date].map(transaction => (
                        <Card key={transaction.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold text-lg">{transaction.customer_name}</h4>
                                <p className="text-sm text-gray-600">{transaction.description}</p>
                                {transaction.end_customer && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    <User className="h-3 w-3 inline mr-1" />
                                    Nihai Tüketici: {transaction.end_customer}
                                  </p>
                                )}
                                <div className="mt-2 flex gap-4 text-sm">
                                  <span><strong>Model:</strong> {transaction.model || '-'}</span>
                                  <span><strong>Renk:</strong> {transaction.color || '-'}</span>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <Badge className="bg-blue-100 text-blue-800">Planlandı</Badge>
                                <span className="text-lg font-bold text-green-600">{formatCurrency(transaction.amount)}</span>
                              </div>
                            </div>
                            
                            {transaction.installer_name ? (
                              <div className="mt-3 p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                                <div className="text-xs flex items-center gap-1">
                                  <Wrench className="h-3 w-3 text-indigo-600" />
                                  <span className="font-medium text-indigo-700">Montajcı:</span>
                                  <span className="text-indigo-600">{transaction.installer_name}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
                                <div className="text-xs flex items-center gap-1 text-orange-700">
                                  <AlertCircle className="h-3 w-3" />
                                  <span>Montajcı atanmamış</span>
                                </div>
                              </div>
                            )}
                            
                            <div className="mt-3 flex gap-2">
                              <Button onClick={() => handleViewDetails(transaction)} variant="outline" size="sm" className="flex-1">
                                <Eye className="h-4 w-4 mr-2" />
                                Detaylar
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button onClick={() => openEditDialog(transaction)} variant="outline" size="sm" className="flex-1">
                                    <Calendar className="h-4 w-4 mr-2" />
                                    Tarih Düzenle
                                  </Button>
                                  <Button onClick={() => openAssignInstallerDialog(transaction)} variant="outline" size="sm" className="flex-1">
                                    <UserCheck className="h-4 w-4 mr-2" />
                                    {transaction.installer_name ? 'Değiştir' : 'Ata'}
                                  </Button>
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg text-gray-600">
                {searchTerm ? "Arama sonucu bulunamadı" : "Hedef tarihi belirlenmiş işlem yok"}
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="overdue" className="space-y-4">
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Hedef Montaj Tarihi Geçmiş İşlemler</h3>
            <p className="text-sm text-gray-600">
              Bu işlemlerin hedef montaj tarihleri geçmiştir. Öncelikli olarak ele alınması gerekebilir.
              {searchTerm && ` (${filteredOverdue.length} sonuç bulundu)`}
            </p>
          </div>
          {Object.keys(groupedOverdue).length > 0 ? (
            <div className="space-y-6">
              {Object.keys(groupedOverdue)
                .sort((a, b) => {
                  if (a === 'Tarih Yok') return 1;
                  if (b === 'Tarih Yok') return -1;
                  const dateA = a.split('.').reverse().join('-');
                  const dateB = b.split('.').reverse().join('-');
                  return new Date(dateA) - new Date(dateB);
                })
                .map(date => (
                  <div key={date} className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-red-600 text-white px-4 py-2 rounded-lg">
                        <AlertCircle className="h-5 w-5 inline mr-2" />
                        <span className="font-semibold">{date}</span>
                        <Badge className="ml-2 bg-white text-red-600">{groupedOverdue[date].length}</Badge>
                      </div>
                    </div>
                    <div className="space-y-2 pl-4 border-l-2 border-red-300">
                      {groupedOverdue[date].map(transaction => (
                        <Card key={transaction.id} className="hover:shadow-md transition-shadow border-red-200">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold text-lg">{transaction.customer_name}</h4>
                                <p className="text-sm text-gray-600">{transaction.description}</p>
                                {transaction.end_customer && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    <User className="h-3 w-3 inline mr-1" />
                                    Nihai Tüketici: {transaction.end_customer}
                                  </p>
                                )}
                                <div className="mt-2 flex gap-4 text-sm">
                                  <span><strong>Model:</strong> {transaction.model || '-'}</span>
                                  <span><strong>Renk:</strong> {transaction.color || '-'}</span>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <Badge className="bg-red-100 text-red-800">Geçmiş</Badge>
                                <span className="text-lg font-bold text-green-600">{formatCurrency(transaction.amount)}</span>
                              </div>
                            </div>
                            
                            {transaction.installer_name ? (
                              <div className="mt-3 p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                                <div className="text-xs flex items-center gap-1">
                                  <Wrench className="h-3 w-3 text-indigo-600" />
                                  <span className="font-medium text-indigo-700">Montajcı:</span>
                                  <span className="text-indigo-600">{transaction.installer_name}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
                                <div className="text-xs flex items-center gap-1 text-orange-700">
                                  <AlertCircle className="h-3 w-3" />
                                  <span>Montajcı atanmamış</span>
                                </div>
                              </div>
                            )}
                            
                            <div className="mt-3 flex gap-2">
                              <Button onClick={() => handleViewDetails(transaction)} variant="outline" size="sm" className="flex-1">
                                <Eye className="h-4 w-4 mr-2" />
                                Detaylar
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button onClick={() => openEditDialog(transaction)} variant="outline" size="sm" className="flex-1">
                                    <Calendar className="h-4 w-4 mr-2" />
                                    Tarih Düzenle
                                  </Button>
                                  <Button onClick={() => openAssignInstallerDialog(transaction)} variant="outline" size="sm" className="flex-1">
                                    <UserCheck className="h-4 w-4 mr-2" />
                                    {transaction.installer_name ? 'Değiştir' : 'Ata'}
                                  </Button>
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg text-gray-600">
                {searchTerm ? "Arama sonucu bulunamadı" : "Geçmiş hedef tarihi olan işlem yok"}
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Hedef Tarih Düzenleme Dialog'u */}
      {/* Sipariş Detayları Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Sipariş Detayları</DialogTitle>
            <DialogDescription>Sipariş ile ilgili tüm bilgiler</DialogDescription>
          </DialogHeader>
          {selectedOrderDetails && selectedOrderDetails.transaction && (
            <div className="space-y-4">
              {/* Müşteri Bilgileri */}
              {selectedOrderDetails.customer && (
                <Card>
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Müşteri Bilgileri
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><strong>Ad Soyad:</strong> {selectedOrderDetails.customer.name} {selectedOrderDetails.customer.surname}</div>
                      <div><strong>Cari Hesap No:</strong> <span className="text-blue-600 font-medium">{selectedOrderDetails.customer.serial_number || '-'}</span></div>
                      <div><strong>Telefon:</strong> <a href={`tel:${selectedOrderDetails.customer.phone}`} className="text-blue-600 hover:underline">{selectedOrderDetails.customer.phone || '-'}</a></div>
                      <div><strong>Email:</strong> {selectedOrderDetails.customer.email || '-'}</div>
                      <div className="col-span-2"><strong>Adres:</strong> {selectedOrderDetails.customer.address || '-'}</div>
                      {selectedOrderDetails.customer.city && <div><strong>Şehir:</strong> {selectedOrderDetails.customer.city}</div>}
                      {selectedOrderDetails.customer.district && <div><strong>İlçe:</strong> {selectedOrderDetails.customer.district}</div>}
                      <div className="col-span-2 pt-2 border-t">
                        <strong>Güncel Bakiye:</strong>
                        <span className={`ml-2 text-lg font-bold ${selectedOrderDetails.customer.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(selectedOrderDetails.customer.balance || 0)}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({selectedOrderDetails.customer.balance >= 0 ? 'Borç' : 'Alacak'})
                        </span>
                      </div>
                    </div>
                    {selectedOrderDetails.transaction.end_customer && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-600" />
                          <strong className="text-sm text-blue-700">Nihai Tüketici:</strong>
                          <span className="text-sm text-blue-900">{selectedOrderDetails.transaction.end_customer}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Sipariş Bilgileri */}
              <Card>
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Sipariş Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Sipariş No:</strong> {selectedOrderDetails.transaction.id?.slice(0, 8).toUpperCase()}</div>
                    <div><strong>Sipariş Tarihi:</strong> {formatDate(selectedOrderDetails.transaction.created_at)}</div>
                    <div><strong>Açıklama:</strong> {selectedOrderDetails.transaction.description || '-'}</div>
                    <div><strong>Üretim Hattı:</strong> <Badge>{selectedOrderDetails.transaction.production_line?.toUpperCase() || '-'}</Badge></div>
                    <div><strong>Hedef Montaj:</strong> <span className="text-blue-600">{formatDate(selectedOrderDetails.transaction.target_installation_date) || 'Belirlenmemiş'}</span></div>
                  </div>
                  {selectedOrderDetails.transaction.details && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <strong className="text-sm flex items-center gap-1">
                        <Clipboard className="h-4 w-4" />
                        Satış Detay Notu:
                      </strong>
                      <p className="text-sm mt-1">{selectedOrderDetails.transaction.details}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ürün Özellikleri */}
              <Card>
                <CardHeader className="bg-purple-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Ürün Özellikleri
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Model:</strong> {selectedOrderDetails.transaction.model || '-'}</div>
                    <div><strong>Renk:</strong> {selectedOrderDetails.transaction.color || '-'}</div>
                    <div><strong>Kapı Kolu:</strong> {selectedOrderDetails.transaction.door_handle || '-'}</div>
                    <div><strong>Yüzey:</strong> {selectedOrderDetails.transaction.surface || '-'}</div>
                    <div><strong>Montaj:</strong> {selectedOrderDetails.transaction.installation || '-'}</div>
                    <div><strong>Montaj Yeri:</strong> {selectedOrderDetails.transaction.installation_location || '-'}</div>
                    <div><strong>İç Pervaz:</strong> {selectedOrderDetails.transaction.inner_frame || '-'}</div>
                    <div><strong>Dış Pervaz:</strong> {selectedOrderDetails.transaction.outer_frame || '-'}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Ölçü Tablosu */}
              {selectedOrderDetails.transaction.measurements && (
                <Card>
                  <CardHeader className="bg-gray-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      Ölçü Tablosu
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {(() => {
                      try {
                        const measurements = typeof selectedOrderDetails.transaction.measurements === 'string' 
                          ? JSON.parse(selectedOrderDetails.transaction.measurements)
                          : selectedOrderDetails.transaction.measurements;
                        
                        if (Array.isArray(measurements) && measurements.length > 0) {
                          return (
                            <div className="overflow-x-auto">
                              <table className="measurements-table-mobile min-w-full text-xs border border-gray-300">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="border border-gray-300 px-2 py-1 text-left">#</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">En</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Boy</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Kasa</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Yönü</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Cam</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Oda</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Açıklama</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {measurements.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="border border-gray-300 px-2 py-1">{idx + 1}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.width || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.height || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.thickness || row.frame || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.direction || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.glass || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.room || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.notes || row.description || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        } else {
                          return (
                            <pre className="text-xs p-3 bg-gray-50 rounded whitespace-pre-wrap">
                              {typeof selectedOrderDetails.transaction.measurements === 'string' 
                                ? selectedOrderDetails.transaction.measurements 
                                : JSON.stringify(selectedOrderDetails.transaction.measurements, null, 2)}
                            </pre>
                          );
                        }
                      } catch (e) {
                        return (
                          <pre className="text-xs p-3 bg-gray-50 rounded whitespace-pre-wrap">
                            {selectedOrderDetails.transaction.measurements}
                          </pre>
                        );
                      }
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Finansal Özet */}
              <Card>
                <CardHeader className="bg-emerald-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Finansal Bilgiler
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Toplam Tutar:</strong> <span className="text-lg font-bold text-green-600">{formatCurrency(selectedOrderDetails.financial_summary?.total_amount)}</span></div>
                    <div><strong>Peşinat:</strong> <span className="text-blue-600">{formatCurrency(selectedOrderDetails.financial_summary?.advance_payment)}</span></div>
                    <div><strong>Kalan (Başlangıç):</strong> <span className="text-orange-600">{formatCurrency(selectedOrderDetails.financial_summary?.remaining)}</span></div>
                    <div><strong>Toplam Tahsilat:</strong> <span className="text-green-600">{formatCurrency(selectedOrderDetails.financial_summary?.total_collected)}</span></div>
                    <div className="col-span-2 pt-2 border-t">
                      <strong>Güncel Bakiye:</strong> 
                      <span className={`text-xl font-bold ml-2 ${selectedOrderDetails.financial_summary?.final_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(selectedOrderDetails.financial_summary?.final_balance)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ödeme Geçmişi */}
              {selectedOrderDetails.payments && selectedOrderDetails.payments.length > 0 && (
                <Card>
                  <CardHeader className="bg-purple-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Ödeme Geçmişi ({selectedOrderDetails.payments.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      {selectedOrderDetails.payments.map((payment, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <div className="text-sm">
                            <div><strong>{formatDate(payment.created_at)}</strong></div>
                            <div className="text-xs text-gray-600">{payment.payment_method || 'Nakit'}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-green-600">{formatCurrency(payment.amount)}</div>
                            <Badge variant="outline" className="text-xs">{payment.type}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Üretim ve Sipariş Durumu */}
              {selectedOrderDetails.productions && selectedOrderDetails.productions.length > 0 && (
                <Card>
                  <CardHeader className="bg-orange-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Üretim ve Sipariş Durumu ({selectedOrderDetails.productions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {selectedOrderDetails.productions.map((prod, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                          <div className="flex justify-between items-start mb-3">
                            <div className="text-sm">
                              <div className="text-lg font-semibold">{prod.production_line?.toUpperCase()} - {prod.model}</div>
                              <div className="text-xs text-gray-600 mt-1">
                                Oluşturma: {formatDate(prod.created_at)}
                              </div>
                              {prod.production_date && (
                                <div className="text-xs text-gray-600">
                                  Üretim: {formatDate(prod.production_date)}
                                </div>
                              )}
                            </div>
                            <Badge className={
                              prod.status === 'hazir' ? 'bg-green-100 text-green-800' :
                              prod.status === 'uretimde' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {prod.status?.toUpperCase()}
                            </Badge>
                          </div>
                          
                          {/* Üretim İşlem Durumları */}
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            {/* CNC İşlemi */}
                            {prod.production_line === 'lake' && (
                              <div className={`p-3 rounded-lg border ${prod.cnc_completed ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                                <div className={`text-sm font-semibold ${prod.cnc_completed ? 'text-green-700' : 'text-orange-700'}`}>
                                  {prod.cnc_completed ? '✅ CNC İşlemi Tamamlandı' : '⏳ CNC İşlemi Yapılmadı'}
                                </div>
                                {prod.cnc_completed && prod.cnc_date && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    Tamamlanma: {formatDate(prod.cnc_date)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Aksesuar Hazırlama */}
                            <div className={`p-3 rounded-lg border ${prod.aksesuar_ready ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                              <div className={`text-sm font-semibold ${prod.aksesuar_ready ? 'text-green-700' : 'text-orange-700'}`}>
                                {prod.aksesuar_ready ? '✅ Aksesuar Tamamlandı' : '⏳ Aksesuar Hazır Değil'}
                              </div>
                              {prod.aksesuar_ready && prod.aksesuar_date && (
                                <div className="text-xs text-gray-600 mt-1">
                                  Tamamlanma: {formatDate(prod.aksesuar_date)}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {prod.notes && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <strong className="text-xs flex items-center gap-1 text-yellow-700">
                                <Clipboard className="h-3 w-3" />
                                Üretim Notu:
                              </strong>
                              <p className="text-xs mt-1 text-gray-700">{prod.notes}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Montaj Durumu */}
              {selectedOrderDetails.installation && (
                <Card>
                  <CardHeader className="bg-indigo-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="h-5 w-5" />
                      Montaj Durumu
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><strong>Montaj Tarihi:</strong> {formatDate(selectedOrderDetails.installation.installation_date) || 'Belirlenmemiş'}</div>
                        <div><strong>Durum:</strong> <Badge>{selectedOrderDetails.installation.status?.toUpperCase()}</Badge></div>
                        {selectedOrderDetails.installation.installer_name && (
                          <div><strong>Montajcı:</strong> {selectedOrderDetails.installation.installer_name}</div>
                        )}
                      </div>
                      
                      {/* Boya/Ebatlama Durumu */}
                      <div className={`p-3 rounded-lg border ${selectedOrderDetails.installation.is_painted ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                        <div className={`text-sm font-semibold ${selectedOrderDetails.installation.is_painted ? 'text-green-700' : 'text-orange-700'}`}>
                          {selectedOrderDetails.transaction?.production_line === 'melamin' ? (
                            selectedOrderDetails.installation.is_painted ? '✅ Ebatlandı' : '⏳ Ebatlanmadı'
                          ) : (
                            selectedOrderDetails.installation.is_painted ? '✅ Boyandı' : '⏳ Boyanmadı'
                          )}
                        </div>
                        {selectedOrderDetails.installation.is_painted && selectedOrderDetails.installation.painted_date && (
                          <div className="text-xs text-gray-600 mt-1">
                            {selectedOrderDetails.transaction?.production_line === 'melamin' ? 'Ebatlama Tarihi: ' : 'Boyanma Tarihi: '}
                            {formatDate(selectedOrderDetails.installation.painted_date)}
                          </div>
                        )}
                      </div>
                      
                      {selectedOrderDetails.installation.notes && (
                        <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                          <strong className="text-xs">Montaj Notu:</strong>
                          <p className="text-xs mt-1">{selectedOrderDetails.installation.notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={() => setDetailsDialogOpen(false)}>Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hedef Montaj Tarihi Düzenle</DialogTitle>
            <DialogDescription>
              {selectedTransaction && 
                `${selectedTransaction.customer_name} - ${selectedTransaction.description} için hedef montaj tarihi belirleyin`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="target_date">Hedef Montaj Tarihi</Label>
              <Input
                id="target_date"
                type="date"
                value={newTargetDate}
                onChange={(e) => setNewTargetDate(e.target.value)}
                className="mt-1"
              />
              {newTargetDate === "" && (
                <p className="text-sm text-gray-500 mt-1">
                  Boş bırakırsanız hedef tarih kaldırılacaktır.
                </p>
              )}
            </div>
            
            {selectedTransaction && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium mb-2">İşlem Bilgileri</h4>
                <div className="text-sm space-y-1">
                  <div><strong>Müşteri:</strong> {selectedTransaction.customer_name}</div>
                  <div><strong>Tutar:</strong> {formatCurrency(selectedTransaction.amount)}</div>
                  <div><strong>Sipariş Tarihi:</strong> {formatDate(selectedTransaction.created_at)}</div>
                  <div><strong>Mevcut Hedef Tarih:</strong> {formatDate(selectedTransaction.target_installation_date)}</div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleEditTargetDate}>
              <Calendar className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Montajcı Atama Dialog */}
      <Dialog open={assignInstallerOpen} onOpenChange={setAssignInstallerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Montajcı Ata</DialogTitle>
            <DialogDescription>
              {selectedForAssignment && 
                `${selectedForAssignment.customer_name} - ${selectedForAssignment.description} için montajcı seçin`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="installer_select">Montajcı Seç</Label>
              <Select value={selectedInstallerId} onValueChange={setSelectedInstallerId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Montajcı seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {installers.map(installer => (
                    <SelectItem key={installer.id} value={installer.id}>
                      {installer.name} {installer.surname || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {installers.length === 0 && (
                <p className="text-sm text-red-500 mt-1">
                  Sistemde montajcı bulunamadı. Lütfen önce montajcı ekleyin.
                </p>
              )}
            </div>
            
            {selectedForAssignment && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium mb-2">Sipariş Bilgileri</h4>
                <div className="text-sm space-y-1">
                  <div><strong>Müşteri:</strong> {selectedForAssignment.customer_name}</div>
                  <div><strong>Tutar:</strong> {formatCurrency(selectedForAssignment.amount)}</div>
                  <div><strong>Model:</strong> {selectedForAssignment.model || '-'}</div>
                  <div><strong>Hedef Tarih:</strong> {formatDate(selectedForAssignment.target_installation_date)}</div>
                  {selectedForAssignment.installer_name && (
                    <div className="pt-2 border-t">
                      <strong>Mevcut Montajcı:</strong> {selectedForAssignment.installer_name}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setAssignInstallerOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleAssignInstaller} disabled={!selectedInstallerId}>
              <UserCheck className="h-4 w-4 mr-2" />
              Ata
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

// Cash Transactions Component (Kasa Hareketleri)
const CashTransactions = ({ sessionToken }) => {
  const [cashFlows, setCashFlows] = useState([]);
  const [cashSummary, setCashSummary] = useState({
    total_inflow: 0,
    total_outflow: 0,
    current_balance: 0
  });
  const [dailySummary, setDailySummary] = useState({
    date: new Date().toISOString().split('T')[0],
    opening_balance: 0,
    daily_collections: 0,
    daily_payments: 0,
    closing_balance: 0,
    transaction_count: 0
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [editingFlow, setEditingFlow] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Çift kayıt önleme
  const [formData, setFormData] = useState({
    amount: "",
    operation: "in", // "in" (giriş) veya "out" (çıkış)
    description: ""
  });

  useEffect(() => {
    fetchCashFlows();
    fetchDailySummary(selectedDate);
  }, []);

  useEffect(() => {
    fetchDailySummary(selectedDate);
  }, [selectedDate]);

  const fetchCashFlows = async () => {
    try {
      const response = await axios.get(`${API}/cash-flows`);
      setCashFlows(response.data);
      
      // Calculate summary from cash flows data
      calculateCashSummary(response.data);
      
      setLoading(false);
    } catch (error) {
      console.error("Kasa hareketleri yüklenirken hata:", error);
      toast.error("Kasa hareketleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const calculateCashSummary = (flows) => {
    let totalInflow = 0;
    let totalOutflow = 0;
    let currentBalance = 0;

    flows.forEach(flow => {
      if (flow.operation === "in") {
        totalInflow += flow.amount;
        currentBalance += flow.amount;
      } else if (flow.operation === "out") {
        totalOutflow += flow.amount;
        currentBalance -= flow.amount;
      }
    });

    setCashSummary({
      total_inflow: totalInflow,
      total_outflow: totalOutflow,
      current_balance: currentBalance
    });
  };

  const fetchDailySummary = async (date) => {
    try {
      const response = await axios.get(`${API}/cash-ledger/daily`, {
        params: { 
          ledger_type: 'cash',
          date: date,
          session_token: sessionToken 
        }
      });
      
      if (response.data.found && response.data.ledger) {
        const ledger = response.data.ledger;
        const collections_total = (ledger.collections || []).reduce((sum, item) => sum + (item.amount || 0), 0);
        const payments_total = (ledger.payments || []).reduce((sum, item) => sum + (item.amount || 0), 0);
        
        setDailySummary({
          date: date,
          opening_balance: ledger.opening_balance || 0,
          daily_collections: collections_total,
          daily_payments: payments_total,
          closing_balance: ledger.closing_balance || 0,
          transaction_count: (ledger.collections || []).length + (ledger.payments || []).length
        });
      } else {
        // Defter yoksa boş göster
        setDailySummary({
          date: date,
          opening_balance: 0,
          daily_collections: 0,
          daily_payments: 0,
          closing_balance: 0,
          transaction_count: 0
        });
      }
    } catch (error) {
      console.error("Günlük özet yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Çift kayıt önleme
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const flow_data = {
        flow_type: "cash",
        amount: parseFloat(formData.amount),
        operation: formData.operation,
        description: formData.description,
        reference_id: ""
      };

      const response = await axios.post(`${API}/cash-flows`, flow_data);
      toast.success("Kasa hareketi eklendi");
      setIsDialogOpen(false);
      setFormData({
        amount: "",
        operation: "in",
        description: ""
      });
      fetchCashFlows();
      fetchDailySummary(selectedDate); // Günlük özeti güncelle
    } catch (error) {
      console.error("Kasa hareketi eklenirken hata:", error);
      toast.error("Kasa hareketi eklenirken hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (flow) => {
    setEditingFlow(flow);
    setFormData({
      amount: flow.amount.toString(),
      operation: flow.operation,
      description: flow.description
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setPendingAction({
      type: 'edit',
      data: {
        amount: parseFloat(formData.amount),
        operation: formData.operation,
        description: formData.description
      }
    });
    setIsPasswordDialogOpen(true);
  };

  const handleDeleteClick = (flow) => {
    setPendingAction({
      type: 'delete',
      flow: flow
    });
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async (password) => {
    try {
      if (pendingAction.type === 'delete') {
        await axios.delete(`${API}/cash-flows/${pendingAction.flow.id}`, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Kasa hareketi silindi");
        fetchCashFlows();
        fetchDailySummary(selectedDate); // Günlük özeti güncelle
      } else if (pendingAction.type === 'edit') {
        await axios.put(`${API}/cash-flows/${editingFlow.id}`, pendingAction.data, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Kasa hareketi güncellendi");
        setIsEditDialogOpen(false);
        setEditingFlow(null);
        fetchCashFlows();
        fetchDailySummary(selectedDate); // Günlük özeti güncelle
      }
      
      setIsPasswordDialogOpen(false);
      setPendingAction(null);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Güvenlik şifresi hatalı!");
      } else {
        toast.error("İşlem sırasında hata oluştu");
      }
    }
  };

  const formatAmount = (amount, operation) => {
    const formattedAmount = `₺${Math.abs(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
    return operation === 'in' ? `+${formattedAmount}` : `-${formattedAmount}`;
  };

  const getOperationBadge = (operation) => {
    return operation === 'in' ? 
      <Badge className="bg-green-100 text-green-800">Giriş</Badge> : 
      <Badge className="bg-red-100 text-red-800">Çıkış</Badge>;
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setFormData({
        amount: "",
        operation: "in",
        description: ""
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Kasa Hareketleri</h1>
          <p className="text-muted-foreground">Nakit giriş ve çıkış hareketlerini görüntüleyin</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Yeni Hareket
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Kasa Hareketi</DialogTitle>
              <DialogDescription>
                Nakit giriş veya çıkış hareketi ekleyin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="operation">İşlem Türü</Label>
                <Select value={formData.operation} onValueChange={(value) => setFormData({...formData, operation: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="İşlem türü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Giriş (+)</SelectItem>
                    <SelectItem value="out">Çıkış (-)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Tutar (TL)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Hareket açıklaması"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  <Plus className="h-4 w-4 mr-2" />
                  {isSubmitting ? "Kaydediliyor..." : "Ekle"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Günlük Kasa Özeti */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Günlük Kasa Özeti
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Tarih:</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="bg-white">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Devreden Bakiye</div>
                <div className="text-xl font-bold text-gray-700">
                  ₺{dailySummary.opening_balance.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-green-200">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Günlük Tahsilat</div>
                <div className="text-xl font-bold text-green-600">
                  ₺{dailySummary.daily_collections.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(cashFlows.filter(f => f.created_at?.startsWith(selectedDate) && f.operation === 'in').length)} işlem
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-red-200">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Günlük Ödeme</div>
                <div className="text-xl font-bold text-red-600">
                  ₺{dailySummary.daily_payments.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(cashFlows.filter(f => f.created_at?.startsWith(selectedDate) && f.operation === 'out').length)} işlem
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-blue-200">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Gün Sonu Bakiye</div>
                <div className="text-xl font-bold text-blue-600">
                  ₺{dailySummary.closing_balance.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Yarına Devir</div>
                <div className={`text-xl font-bold ${
                  dailySummary.closing_balance >= 0 
                    ? 'text-blue-600' 
                    : 'text-red-600'
                }`}>
                  ₺{dailySummary.closing_balance.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Gün Sonu = Yarın Açılış
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Kasa Özeti Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Toplam Giriş</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ₺{cashFlows
                .filter(flow => flow.operation === 'in')
                .reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0)
                .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">
              {cashFlows.filter(flow => flow.operation === 'in').length} işlem
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Toplam Çıkış</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              ₺{cashFlows
                .filter(flow => flow.operation === 'out')
                .reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0)
                .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">
              {cashFlows.filter(flow => flow.operation === 'out').length} işlem
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Bakiye</CardTitle>
            <Wallet className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              (cashFlows.filter(flow => flow.operation === 'in').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0) -
               cashFlows.filter(flow => flow.operation === 'out').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0)) >= 0
                ? 'text-green-600' : 'text-red-600'
            }`}>
              ₺{(cashFlows.filter(flow => flow.operation === 'in').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0) -
                 cashFlows.filter(flow => flow.operation === 'out').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0))
                .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">
              Toplam {cashFlows.length} işlem
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Kasa Hareketleri Listesi */}
      <Card>
        <CardHeader>
          <CardTitle>Nakit Hareketleri</CardTitle>
          <CardDescription>Tüm kasa giriş ve çıkış hareketleri</CardDescription>
        </CardHeader>
        <CardContent>
          {cashFlows.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Henüz kasa hareketi yok</p>
              <p className="text-sm text-gray-400">İlk hareketi eklemek için "Yeni Hareket" butonunu kullanın</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cashFlows.map((flow) => (
                <div key={flow.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 rounded-full ${flow.operation === 'in' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div className="flex-1">
                      <p className="font-medium">{flow.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(flow.created_at).toLocaleDateString('tr-TR')} • {new Date(flow.created_at).toLocaleTimeString('tr-TR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getOperationBadge(flow.operation)}
                    <p className={`text-lg font-bold ${flow.operation === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatAmount(flow.amount, flow.operation)}
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(flow)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(flow)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Düzenleme Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kasa Hareketini Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label>İşlem Türü</Label>
              <Select value={formData.operation} onValueChange={(value) => setFormData({...formData, operation: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in">Giriş (+)</SelectItem>
                  <SelectItem value="out">Çıkış (-)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tutar (TL)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>İptal</Button>
              <Button type="submit">Güncelle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Şifre Dialog */}
      <SecurePasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title={pendingAction?.type === 'delete' ? 'Silme Onayı' : 'Düzenleme Onayı'}
        message={pendingAction?.type === 'delete' ? 'Bu hareketi silmek için güvenlik şifresini girin' : 'Bu hareketi düzenlemek için güvenlik şifresini girin'}
      />
    </div>
  );
};

// Bank Transactions Component (Banka Hareketleri)
const BankTransactions = ({ sessionToken }) => {
  const [bankFlows, setBankFlows] = useState([]);
  const [bankSummary, setBankSummary] = useState({
    total_inflow: 0,
    total_outflow: 0,
    current_balance: 0
  });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    amount: "",
    operation: "in", // "in" (giriş) veya "out" (çıkış)
    description: ""
  });
  const [transferData, setTransferData] = useState({
    amount: "",
    direction: "cash_to_bank", // "cash_to_bank" veya "bank_to_cash"
    description: ""
  });

  useEffect(() => {
    fetchBankFlows();
  }, []);

  const fetchBankFlows = async () => {
    try {
      const response = await axios.get(`${API}/bank-flows`);
      setBankFlows(response.data);
      
      // Calculate summary from bank flows data
      calculateBankSummary(response.data);
      
      setLoading(false);
    } catch (error) {
      console.error("Banka hareketleri yüklenirken hata:", error);
      toast.error("Banka hareketleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const calculateBankSummary = (flows) => {
    let totalInflow = 0;
    let totalOutflow = 0;
    let currentBalance = 0;

    flows.forEach(flow => {
      if (flow.operation === "in") {
        totalInflow += flow.amount;
        currentBalance += flow.amount;
      } else if (flow.operation === "out") {
        totalOutflow += flow.amount;
        currentBalance -= flow.amount;
      }
    });

    setBankSummary({
      total_inflow: totalInflow,
      total_outflow: totalOutflow,
      current_balance: currentBalance
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const flow_data = {
        flow_type: "bank",
        amount: parseFloat(formData.amount),
        operation: formData.operation,
        description: formData.description,
        reference_id: ""
      };

      await axios.post(`${API}/bank-flows`, flow_data);
      toast.success("Banka hareketi eklendi");
      setIsDialogOpen(false);
      setFormData({
        amount: "",
        operation: "in",
        description: ""
      });
      fetchBankFlows();
    } catch (error) {
      console.error("Banka hareketi eklenirken hata:", error);
      toast.error("Banka hareketi eklenirken hata oluştu");
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/transfer-cash-bank`, {
        amount: parseFloat(transferData.amount),
        direction: transferData.direction,
        description: transferData.description
      });
      
      const transferMessage = transferData.direction === "cash_to_bank" 
        ? "Kasadan bankaya para transferi yapıldı" 
        : "Bankadan kasaya para transferi yapıldı";
      
      toast.success(transferMessage);
      setIsTransferDialogOpen(false);
      setTransferData({
        amount: "",
        direction: "cash_to_bank",
        description: ""
      });
      fetchBankFlows();
    } catch (error) {
      console.error("Para transferi yapılırken hata:", error);
      toast.error("Para transferi yapılırken hata oluştu");
    }
  };

  const formatAmount = (amount, operation) => {
    const formattedAmount = `₺${Math.abs(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
    return operation === 'in' ? `+${formattedAmount}` : `-${formattedAmount}`;
  };

  const getOperationBadge = (operation) => {
    return operation === 'in' ? 
      <Badge className="bg-green-100 text-green-800">Giriş</Badge> : 
      <Badge className="bg-red-100 text-red-800">Çıkış</Badge>;
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setFormData({
        amount: "",
        operation: "in",
        description: ""
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Banka Hareketleri</h1>
          <p className="text-muted-foreground">Banka giriş, çıkış hareketleri ve kasa-banka transferleri</p>
        </div>
        <div className="flex space-x-2">
          <Dialog open={isTransferDialogOpen} onOpenChange={setIsTransferDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <ArrowRight className="h-4 w-4 mr-2" />
                Para Transferi
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Kasa-Banka Para Transferi</DialogTitle>
                <DialogDescription>
                  Kasa ve banka hesabı arasında para transferi yapın.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleTransfer} className="space-y-4">
                <div>
                  <Label htmlFor="transfer_direction">Transfer Yönü</Label>
                  <Select value={transferData.direction} onValueChange={(value) => setTransferData({...transferData, direction: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Transfer yönü seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash_to_bank">Kasadan Bankaya (📤➡️🏦)</SelectItem>
                      <SelectItem value="bank_to_cash">Bankadan Kasaya (🏦➡️📤)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="transfer_amount">Tutar (TL)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={transferData.amount}
                    onChange={(e) => setTransferData({...transferData, amount: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="transfer_description">Açıklama</Label>
                  <Textarea
                    id="transfer_description"
                    value={transferData.description}
                    onChange={(e) => setTransferData({...transferData, description: e.target.value})}
                    placeholder="Transfer açıklaması"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsTransferDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button type="submit">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Transfer Yap
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Hareket
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Yeni Banka Hareketi</DialogTitle>
                <DialogDescription>
                  Banka giriş veya çıkış hareketi ekleyin.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="operation">İşlem Türü</Label>
                  <Select value={formData.operation} onValueChange={(value) => setFormData({...formData, operation: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="İşlem türü seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in">Giriş (+)</SelectItem>
                      <SelectItem value="out">Çıkış (-)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="amount">Tutar (TL)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Açıklama</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Hareket açıklaması"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button type="submit">
                    <Plus className="h-4 w-4 mr-2" />
                    Ekle
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Banka Özeti Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Toplam Giriş</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ₺{bankFlows
                .filter(flow => flow.operation === 'in')
                .reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0)
                .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">
              {bankFlows.filter(flow => flow.operation === 'in').length} işlem
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Toplam Çıkış</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              ₺{bankFlows
                .filter(flow => flow.operation === 'out')
                .reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0)
                .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">
              {bankFlows.filter(flow => flow.operation === 'out').length} işlem
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Bakiye</CardTitle>
            <Wallet className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              (bankFlows.filter(flow => flow.operation === 'in').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0) -
               bankFlows.filter(flow => flow.operation === 'out').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0)) >= 0
                ? 'text-green-600' : 'text-red-600'
            }`}>
              ₺{(bankFlows.filter(flow => flow.operation === 'in').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0) -
                 bankFlows.filter(flow => flow.operation === 'out').reduce((sum, flow) => sum + parseFloat(flow.amount || 0), 0))
                .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">
              Toplam {bankFlows.length} işlem
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Banka Hareketleri Listesi */}
      <Card>
        <CardHeader>
          <CardTitle>Banka Hareketleri</CardTitle>
          <CardDescription>Tüm banka giriş, çıkış hareketleri ve transferler</CardDescription>
        </CardHeader>
        <CardContent>
          {bankFlows.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Henüz banka hareketi yok</p>
              <p className="text-sm text-gray-400">İlk hareketi eklemek için "Yeni Hareket" butonunu kullanın</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bankFlows.map((flow) => (
                <div key={flow.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 rounded-full ${flow.operation === 'in' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div className="flex-1">
                      <p className="font-medium">{flow.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(flow.created_at).toLocaleDateString('tr-TR')} • {new Date(flow.created_at).toLocaleTimeString('tr-TR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getOperationBadge(flow.operation)}
                    <p className={`text-lg font-bold ${flow.operation === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatAmount(flow.amount, flow.operation)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Employee Management Component
const EmployeeManagement = ({ currentUser }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNewEmployeeDialogOpen, setIsNewEmployeeDialogOpen] = useState(false);
  const [isEditEmployeeDialogOpen, setIsEditEmployeeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    phone: '',
    start_date: '',
    monthly_salary: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Personeller yüklenirken hata:", error);
      toast.error("Personeller yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const handleNewEmployeeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employees`, {
        ...formData,
        monthly_salary: parseFloat(formData.monthly_salary)
      });
      
      toast.success("Personel başarıyla eklendi!");
      setIsNewEmployeeDialogOpen(false);
      setFormData({ name: '', surname: '', phone: '', start_date: '', monthly_salary: '' });
      fetchEmployees();
    } catch (error) {
      console.error("Personel eklenirken hata:", error);
      toast.error("Personel eklenirken hata oluştu");
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      surname: employee.surname, 
      phone: employee.phone,
      start_date: employee.start_date,
      monthly_salary: employee.monthly_salary.toString()
    });
    setIsEditEmployeeDialogOpen(true);
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/employees/${editingEmployee.id}`, {
        ...formData,
        monthly_salary: parseFloat(formData.monthly_salary)
      });
      
      toast.success("Personel başarıyla güncellendi!");
      setIsEditEmployeeDialogOpen(false);
      setEditingEmployee(null);
      setFormData({ name: '', surname: '', phone: '', start_date: '', monthly_salary: '' });
      fetchEmployees();
    } catch (error) {
      console.error("Personel güncellenirken hata:", error);
      toast.error("Personel güncellenirken hata oluştu");
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm("Bu personeli silmek istediğinizden emin misiniz?")) {
      try {
        await axios.delete(`${API}/employees/${employeeId}`);
        toast.success("Personel başarıyla silindi!");
        fetchEmployees();
      } catch (error) {
        console.error("Personel silinirken hata:", error);
        toast.error("Personel silinirken hata oluştu");
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Personel Yönetimi</h1>
          <p className="text-muted-foreground">Personel bilgilerini yönetin</p>
        </div>
        <Button onClick={() => setIsNewEmployeeDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni Personel
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Personel Listesi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Ad Soyad</th>
                  <th className="text-left p-2">Telefon</th>
                  <th className="text-left p-2">İşe Başlama</th>
                  <th className="text-left p-2">Aylık Maaş</th>
                  <th className="text-left p-2">Durum</th>
                  <th className="text-left p-2">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.id} className="border-b">
                    <td className="p-2">{employee.name} {employee.surname}</td>
                    <td className="p-2">{employee.phone}</td>
                    <td className="p-2">{new Date(employee.start_date).toLocaleDateString('tr-TR')}</td>
                    <td className="p-2">₺{employee.monthly_salary.toLocaleString('tr-TR')}</td>
                    <td className="p-2">
                      <Badge variant={employee.status === 'active' ? 'default' : 'secondary'}>
                        {employee.status === 'active' ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </td>
                    <td className="p-2">
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditEmployee(employee)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteEmployee(employee.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Yeni Personel Dialog */}
      <Dialog open={isNewEmployeeDialogOpen} onOpenChange={setIsNewEmployeeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Personel Ekle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNewEmployeeSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Ad</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="surname">Soyad</Label>
                <Input
                  id="surname"
                  value={formData.surname}
                  onChange={(e) => setFormData({...formData, surname: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="start_date">İşe Başlama Tarihi</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="monthly_salary">Aylık Maaş (₺)</Label>
              <Input
                id="monthly_salary"
                type="number"
                step="0.01"
                value={formData.monthly_salary}
                onChange={(e) => setFormData({...formData, monthly_salary: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsNewEmployeeDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Düzenleme Dialog */}
      <Dialog open={isEditEmployeeDialogOpen} onOpenChange={setIsEditEmployeeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Personel Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEmployee} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_name">Ad</Label>
                <Input
                  id="edit_name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_surname">Soyad</Label>
                <Input
                  id="edit_surname"
                  value={formData.surname}
                  onChange={(e) => setFormData({...formData, surname: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_phone">Telefon</Label>
              <Input
                id="edit_phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_start_date">İşe Başlama Tarihi</Label>
              <Input
                id="edit_start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_monthly_salary">Aylık Maaş (₺)</Label>
              <Input
                id="edit_monthly_salary"
                type="number"
                step="0.01"
                value={formData.monthly_salary}
                onChange={(e) => setFormData({...formData, monthly_salary: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditEmployeeDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">Güncelle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Navigation Component
const Navigation = ({ onLogout, currentUser }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Hiyerarşik menü yapısı
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const toggleCategory = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  const navItems = [
    // Ana sayfalar (direkt erişim)
    { path: "/", icon: TrendingUp, label: "Dashboard" },
    { path: "/customer-search", icon: Search, label: "Müşteri Detay Arama" },
    { path: "/agenda", icon: Calendar, label: "Ajanda" },
    { path: "/control", icon: ClipboardCheck, label: "Kontrol" },
    { path: "/collection-tracking", icon: ClipboardList, label: "Tahsilat Takip" },
    
    // Hiyerarşik kategoriler
    {
      key: "cari-hesaplar",
      label: "CARİ HESAPLAR",
      icon: Users,
      children: [
        { path: "/customers", icon: Users, label: "Müşteriler" },
        { path: "/suppliers", icon: Building2, label: "Tedarikçiler" }
      ]
    },
    {
      key: "ch-islemleri", 
      label: "C.H. İŞLEMLERİ",
      icon: DollarSign,
      children: [
        { path: "/sales", icon: DollarSign, label: "Satış" },
        { path: "/purchases", icon: ShoppingCart, label: "Mal Alış" },
        { path: "/expenses", icon: Receipt, label: "Gider Yönetimi" },
        { path: "/pre-sales", icon: FileText, label: "Müşteri Ön Satışları" }
      ]
    },
    {
      key: "fis-islemleri",
      label: "FİŞ İŞLEMLERİ", 
      icon: Receipt,
      children: [
        { path: "/receipts", icon: Receipt, label: "Tahsilat" },
        { path: "/payments", icon: CreditCard, label: "Ödeme" },
        { path: "/temlik", icon: ArrowLeftRight, label: "Temlik" }
        // { path: "/supplier-payments", icon: Banknote, label: "Tediye" } // HIDDEN per user request
      ]
    },
    {
      key: "kasa-banka",
      label: "KASA BANKA İŞLEMLERİ",
      icon: Wallet,
      children: [
        { path: "/cash-transactions", icon: Wallet, label: "Kasa Hareketleri" },
        { path: "/bank-transactions", icon: CreditCard, label: "Banka Hareketleri" }
      ]
    },
    // BAYİ PANELİ - KALDIRILDI
    /*
    {
      key: "bayi-panel",
      label: "BAYİ PANELİ",
      icon: Building2,
      children: [
        { path: "/bayiler", icon: Building2, label: "Bayiler" },
        { path: "/bayi-orders", icon: FileText, label: "Bayi Siparişleri" }
      ]
    },
    */
    ...(currentUser?.role === 'installation' ? [{
      key: "montaj-islerim",
      label: "MONTAJ İŞLERİM",
      icon: Wrench,
      children: [
        { path: "/installations", icon: Wrench, label: "Montaj İşlerim" }
      ]
    }] : []),
    ...(currentUser?.role !== 'installation' ? [{
      key: "erp-sistem",
      label: "ERP SİSTEMİ",
      icon: Factory,
      children: [
        { path: "/production", icon: Factory, label: "Üretim" },
        { path: "/production-monitoring", icon: Activity, label: "Üretim İzleme" },
        { path: "/installations", icon: Wrench, label: "Montaj" },
        { path: "/target-installations", icon: Calendar, label: "Hedef Montaj" },
        { path: "/stock-management", icon: Package, label: "Stok Yönetimi" },
        { path: "/salary-management", icon: Wallet, label: "Personel Maaş" }
      ]
    }] : []),
    ...(currentUser?.role === 'admin' ? [{
      key: "yonetici-panel",
      label: "YÖNETİCİ PANELİ",
      icon: Settings,
      children: [
        { path: "/admin/users", icon: UserCheck, label: "Kullanıcı Yönetimi" },
        // { path: "/admin/personnel", icon: Users, label: "Personel Yönetimi" }, // HIDDEN per user request
        { path: "/admin/activities", icon: ClipboardList, label: "Etkinlikler" },
        { path: "/admin/data", icon: Settings, label: "Veri Yönetimi" },
        { path: "/admin/backup", icon: Cloud, label: "Otomatik Yedekleme" },
        { path: "/admin/reports", icon: TrendingUp, label: "Raporlar" }
      ]
    }] : []),
    
    // Bayi için özel menü yapısı
    ...(currentUser?.role === 'bayi' ? [
      { path: "/bayi/dashboard", icon: Home, label: "İç Muhasebe" },
      {
        key: "bayi-muhasebe",
        label: "İÇ MUHASEBE",
        icon: Calculator,
        children: [
          { path: "/bayi/internal/customers", icon: Users, label: "Müşterilerim" },
          { path: "/bayi/internal/suppliers", icon: Building2, label: "Tedarikçilerim" },
          { path: "/bayi/internal/sales", icon: DollarSign, label: "Satışlarım" },
          { path: "/bayi/internal/purchases", icon: ShoppingCart, label: "Mal Alış" },
          { path: "/bayi/internal/collections", icon: Receipt, label: "Tahsilat" },
          { path: "/bayi/internal/payments", icon: CreditCard, label: "Tediye" },
          { path: "/bayi/internal/data", icon: Settings, label: "Veri Yönetimi" }
        ]
      },
      {
        key: "eskay-islemler",
        label: "ESKAY İŞLEMLERİ",
        icon: Send,
        children: [
          { path: "/bayi/sales", icon: Send, label: "ESKAY'a Sipariş" },
          { path: "/bayi/orders", icon: ClipboardList, label: "ESKAY Siparişlerim" },
          { path: "/bayi/account", icon: Receipt, label: "ESKAY Hesap Durumu" }
        ]
      }
    ] : [])
  ];

  // Filter navigation items based on user permissions - handle both flat items and nested categories
  const filteredNavItems = navItems.filter(item => {
    // Admin can see all items
    if (currentUser?.role === 'admin') return true;
    
    // Bayi can only see their specific items
    if (currentUser?.role === 'bayi') {
      const bayiAllowedPaths = [
        '/bayi/dashboard',
        '/bayi/internal/customers',
        '/bayi/internal/suppliers', 
        '/bayi/internal/sales',
        '/bayi/internal/purchases',
        '/bayi/internal/collections',
        '/bayi/internal/payments',
        '/bayi/internal/data',
        '/bayi/sales',
        '/bayi/orders',
        '/bayi/account'
      ];
      
      // If it's a category with children, check if any children are allowed
      if (item.children) {
        return item.children.some(child => bayiAllowedPaths.includes(child.path));
      }
      
      return bayiAllowedPaths.includes(item.path);
    }
    
    // For other non-admin users, check if they have permission for this screen
    if (item.children) {
      return item.children.some(child => 
        currentUser?.allowed_screens?.includes(child.path) || 
        currentUser?.allowed_screens?.includes('*')
      );
    }
    
    return currentUser?.allowed_screens?.includes(item.path) || 
           currentUser?.allowed_screens?.includes('*');
  });

  // Auto-expand category if current path matches any of its children
  useEffect(() => {
    const currentPath = window.location.pathname;
    const newExpanded = new Set();
    
    navItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => child.path === currentPath);
        if (hasActiveChild) {
          newExpanded.add(item.key);
        }
      }
    });
    
    setExpandedCategories(newExpanded);
  }, [window.location.pathname]);

  // Menü dışında bir yere tıklandığında menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.sidebar-menu') && !event.target.closest('.hamburger-btn')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <>
      {/* Header Bar with Logo and Hamburger Menu */}
      <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 h-16">
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center space-x-3">
            {/* Hamburger Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="hamburger-btn p-2 hover:bg-gray-100 touch-target"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <Menu className="h-6 w-6" />
            </Button>
            
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_finsys/artifacts/2r02pdgk_SUNTALAM-F3-K%20Kopyas%C4%B1.png" 
                alt="ESKAY AHŞAP" 
                className="h-8 object-contain"
              />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Muhasebe Pro</h2>
                <p className="text-xs text-gray-500">ESKAY AHŞAP</p>
              </div>
            </div>
          </div>

          {/* User Info - Sağ tarafta */}
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">
                {currentUser?.full_name || 'Kullanıcı'}
              </div>
              <div className="text-xs text-gray-500">
                {currentUser?.username || 'user'}
              </div>
            </div>
            <Button 
              onClick={onLogout}
              variant="outline" 
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50 touch-target"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Çıkış
            </Button>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-45"
          style={{ zIndex: 45 }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Collapsible Sidebar */}
      <nav 
        className={`
          sidebar-menu ${isMenuOpen ? 'mobile-menu-open' : ''}
          fixed top-16 left-0 h-[calc(100vh-4rem)] bg-white border-r border-gray-200
          w-64 p-4 flex flex-col
          md:w-72
        `} 
        style={{ 
          zIndex: 50,
          transform: isMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 300ms ease-in-out'
        }}
      >
        <ul className="space-y-1 flex-1 overflow-y-auto overflow-x-hidden pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">{filteredNavItems.map((item) => {
            // Eğer item'ın children'ı varsa kategori olarak render et
            if (item.children) {
              const isExpanded = expandedCategories.has(item.key);
              const hasActiveChild = item.children.some(child => 
                location.pathname === child.path
              );
              
              return (
                <li key={item.key} className="space-y-1">
                  {/* Kategori başlığı */}
                  <button
                    onClick={() => toggleCategory(item.key)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm font-medium touch-target ${
                      hasActiveChild || isExpanded
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50"
                    } ${isMobile ? 'min-h-[44px] text-base' : ''}`}
                  >
                    <div className="flex items-center space-x-2">
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {/* Alt menü öğeleri */}
                  {isExpanded && (
                    <ul className="ml-6 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm touch-target ${
                              location.pathname === child.path
                                ? "bg-blue-100 text-blue-700"
                                : "text-gray-600 hover:bg-gray-100"
                            } ${isMobile ? 'min-h-[44px] text-base' : ''}`}
                          >
                            <child.icon className="h-4 w-4" />
                            <span>{child.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            } else {
              // Normal menü öğesi olarak render et
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors touch-target ${
                      location.pathname === item.path
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-700 hover:bg-gray-100"
                    } ${isMobile ? 'min-h-[48px] text-base' : ''}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            }
          })}
        </ul>
      </nav>
    </>
  );
};

// Bayi Orders Component (Admin için - Bayi siparişlerini onaylama/reddetme)
const BayiOrders = ({ currentUser }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [price, setPrice] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/bayi-orders`);
      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Bayi siparişleri yüklenirken hata:", error);
      toast.error("Bayi siparişleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!price || !selectedOrder) {
      toast.error("Lütfen fiyat girin");
      return;
    }

    try {
      await axios.put(`${API}/bayi-orders/${selectedOrder.id}/approve`, null, {
        params: { price: parseFloat(price) }
      });
      toast.success("Sipariş onaylandı ve üretime gönderildi");
      setIsApproveOpen(false);
      setSelectedOrder(null);
      setPrice('');
      fetchOrders();
    } catch (error) {
      console.error("Sipariş onaylanırken hata:", error);
      toast.error("Sipariş onaylanırken hata oluştu");
    }
  };

  const handleReject = async () => {
    if (!rejectReason || !selectedOrder) {
      toast.error("Lütfen red sebebi girin");
      return;
    }

    try {
      await axios.put(`${API}/bayi-orders/${selectedOrder.id}/reject`, null, {
        params: { reason: rejectReason }
      });
      toast.success("Sipariş reddedildi");
      setIsRejectOpen(false);
      setSelectedOrder(null);
      setRejectReason('');
      fetchOrders();
    } catch (error) {
      console.error("Sipariş reddedilirken hata:", error);
      toast.error("Sipariş reddedilirken hata oluştu");
    }
  };

  const openApproveDialog = (order) => {
    setSelectedOrder(order);
    setPrice('');
    setIsApproveOpen(true);
  };

  const openRejectDialog = (order) => {
    setSelectedOrder(order);
    setRejectReason('');
    setIsRejectOpen(true);
  };

  const openDetailDialog = (order) => {
    setSelectedOrder(order);
    setIsDetailOpen(true);
  };

  const openDeleteDialog = (order) => {
    setSelectedOrder(order);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.delete(`${API}/bayi-orders/${selectedOrder.id}`);
      toast.success("Sipariş başarıyla silindi!");
      setIsDeleteOpen(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error("Sipariş silinirken hata:", error);
      toast.error("Sipariş silinirken hata oluştu");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      beklemede: { color: 'bg-orange-100 text-orange-800', text: 'Onay Bekliyor' },
      onaylandi: { color: 'bg-green-100 text-green-800', text: 'Onaylandı' },
      reddedildi: { color: 'bg-red-100 text-red-800', text: 'Reddedildi' }
    };
    const config = statusConfig[status] || statusConfig.beklemede;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.text}</span>;
  };

  const parseMeasurements = (measurementsJson) => {
    try {
      return JSON.parse(measurementsJson || '[]');
    } catch {
      return [];
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bayi Siparişleri</h1>
          <p className="text-muted-foreground">Bayilerden gelen siparişleri onaylayın veya reddedin</p>
        </div>
      </div>

      <div className="grid gap-4">
        {orders.length > 0 ? (
          orders.map((order) => (
            <Card key={order.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">{order.bayi_name}</h3>
                    <p className="text-sm text-gray-600">Müşteri: {order.customer_name}</p>
                    <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleString('tr-TR')}</p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(order.status)}
                    {order.price > 0 && (
                      <p className="text-lg font-bold text-green-600 mt-2">
                        {order.price.toLocaleString('tr-TR')} ₺
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm"><strong>Model:</strong> {order.model}</p>
                    <p className="text-sm"><strong>Renk:</strong> {order.color}</p>
                    <p className="text-sm"><strong>Kapı Kolu:</strong> {order.door_handle}</p>
                    <p className="text-sm"><strong>Yüzey:</strong> {order.surface}</p>
                  </div>
                  <div>
                    <p className="text-sm"><strong>Üretim Hattı:</strong> {order.production_line?.toUpperCase()}</p>
                    <p className="text-sm"><strong>Müşteri Tel:</strong> {order.customer_phone}</p>
                    <p className="text-sm"><strong>Adres:</strong> {order.customer_address}</p>
                  </div>
                </div>

                {order.notes && (
                  <div className="mb-4">
                    <p className="text-sm"><strong>Notlar:</strong> {order.notes}</p>
                  </div>
                )}

                <div className="flex space-x-2">
                  <Button 
                    onClick={() => openDetailDialog(order)}
                    variant="outline"
                    size="sm"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Sipariş Detayı
                  </Button>
                  
                  {order.status === 'beklemede' && (
                    <>
                      <Button 
                        onClick={() => openApproveDialog(order)}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Onayla & Fiyatlandır
                      </Button>
                      <Button 
                        onClick={() => openRejectDialog(order)}
                        variant="outline"
                        className="border-red-600 text-red-600 hover:bg-red-50"
                        size="sm"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reddet
                      </Button>
                    </>
                  )}
                  
                  {/* Admin Only - Delete Button */}
                  {currentUser?.role === 'admin' && (
                    <Button 
                      onClick={() => openDeleteDialog(order)}
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-50"
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Sil
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg text-gray-600">Bayi siparişi bulunamadı</p>
          </div>
        )}
      </div>

      {/* Sipariş Detayı Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sipariş Detayları</DialogTitle>
            <DialogDescription>
              {selectedOrder && `${selectedOrder.bayi_name} - ${selectedOrder.customer_name} siparişi`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Bayi ve Müşteri Bilgileri */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Bayi Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p><strong>Bayi:</strong> {selectedOrder.bayi_name}</p>
                    <p><strong>Sipariş Tarihi:</strong> {new Date(selectedOrder.created_at).toLocaleString('tr-TR')}</p>
                    <p><strong>Durum:</strong> {getStatusBadge(selectedOrder.status)}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Müşteri Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p><strong>Ad:</strong> {selectedOrder.customer_name}</p>
                    <p><strong>Telefon:</strong> {selectedOrder.customer_phone}</p>
                    <p><strong>Adres:</strong> {selectedOrder.customer_address}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Ürün Detayları */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ürün Detayları</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p><strong>Üretim Hattı:</strong> {selectedOrder.production_line?.toUpperCase()}</p>
                      <p><strong>Model:</strong> {selectedOrder.model || '-'}</p>
                      <p><strong>Renk:</strong> {selectedOrder.color || '-'}</p>
                      <p><strong>Kapı Kolu:</strong> {selectedOrder.door_handle || '-'}</p>
                    </div>
                    <div>
                      <p><strong>Yüzey:</strong> {selectedOrder.surface || '-'}</p>
                      <p><strong>Montaj:</strong> {selectedOrder.installation || '-'}</p>
                      <p><strong>Montaj Yeri:</strong> {selectedOrder.installation_location || '-'}</p>
                      <p><strong>İç Pervaz:</strong> {selectedOrder.inner_frame || '-'}</p>
                      <p><strong>Dış Pervaz:</strong> {selectedOrder.outer_frame || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ölçü Tablosu */}
              {selectedOrder.measurements && parseMeasurements(selectedOrder.measurements).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ölçü Tablosu</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border p-2 text-left">#</th>
                            <th className="border p-2 text-left">En (cm)</th>
                            <th className="border p-2 text-left">Boy (cm)</th>
                            <th className="border p-2 text-left">Kasa</th>
                            <th className="border p-2 text-left">Yön</th>
                            <th className="border p-2 text-left">Cam</th>
                            <th className="border p-2 text-left">Oda</th>
                            <th className="border p-2 text-left">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseMeasurements(selectedOrder.measurements).map((measurement, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border p-2 font-medium">{index + 1}</td>
                              <td className="border p-2">{measurement.width || '-'}</td>
                              <td className="border p-2">{measurement.height || '-'}</td>
                              <td className="border p-2">{measurement.frame || '-'}</td>
                              <td className="border p-2">{measurement.direction || '-'}</td>
                              <td className="border p-2">{measurement.glass || '-'}</td>
                              <td className="border p-2">{measurement.room || '-'}</td>
                              <td className="border p-2">{measurement.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ek Bilgiler */}
              {(selectedOrder.details || selectedOrder.notes) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ek Bilgiler</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedOrder.details && (
                      <div className="mb-4">
                        <p><strong>Ek Detaylar:</strong></p>
                        <p className="text-sm text-gray-600 mt-1">{selectedOrder.details}</p>
                      </div>
                    )}
                    {selectedOrder.notes && (
                      <div>
                        <p><strong>Notlar:</strong></p>
                        <p className="text-sm text-gray-600 mt-1">{selectedOrder.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Fiyat Bilgisi */}
              {selectedOrder.price > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Fiyat Bilgisi</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedOrder.price.toLocaleString('tr-TR')} ₺
                    </p>
                    {selectedOrder.approved_at && (
                      <p className="text-sm text-gray-600 mt-2">
                        Onaylanma Tarihi: {new Date(selectedOrder.approved_at).toLocaleString('tr-TR')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Onaylama Dialog */}
      <Dialog open={isApproveOpen} onOpenChange={setIsApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Siparişi Onayla ve Fiyatlandır</DialogTitle>
            <DialogDescription>
              {selectedOrder && `${selectedOrder.bayi_name} - ${selectedOrder.customer_name} siparişi için fiyat belirleyin`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="price">Fiyat (₺)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
            {selectedOrder && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700">Sipariş Özeti:</h4>
                <p className="text-sm"><strong>Bayi:</strong> {selectedOrder.bayi_name}</p>
                <p className="text-sm"><strong>Müşteri:</strong> {selectedOrder.customer_name}</p>
                <p className="text-sm"><strong>Ürün:</strong> {selectedOrder.model} - {selectedOrder.color}</p>
                <p className="text-sm"><strong>Hat:</strong> {selectedOrder.production_line?.toUpperCase()}</p>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsApproveOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleApprove}>
                <Check className="h-4 w-4 mr-2" />
                Onayla ve Üretime Gönder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reddetme Dialog */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Siparişi Reddet</DialogTitle>
            <DialogDescription>
              {selectedOrder && `${selectedOrder.bayi_name} - ${selectedOrder.customer_name} siparişini neden reddediyorsunuz?`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reject_reason">Red Sebebi</Label>
              <Textarea
                id="reject_reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Red sebebini açıklayın..."
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsRejectOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleReject} variant="destructive">
                <X className="h-4 w-4 mr-2" />
                Reddet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Silme Onay Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Siparişi Sil</DialogTitle>
            <DialogDescription>
              Bu siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">{selectedOrder.bayi_name}</p>
                <p className="text-sm text-gray-600">Müşteri: {selectedOrder.customer_name}</p>
                <p className="text-sm text-gray-600">Model: {selectedOrder.model} - {selectedOrder.color}</p>
                <p className="text-xs text-gray-500">{new Date(selectedOrder.created_at).toLocaleString('tr-TR')}</p>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
                  İptal
                </Button>
                <Button onClick={handleDelete} variant="destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sil
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// BayiSales Component (Bayi kullanıcıları için sipariş verme)
const BayiSales = ({ currentUser }) => {
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    customer_address: "",
    model: "",
    color: "",
    door_handle: "",
    surface: "",
    installation: "",
    installation_location: "",
    inner_frame: "",
    outer_frame: "",
    measurements: "",
    details: "",
    production_line: "lake",
    notes: ""
  });
  const [measurements, setMeasurements] = useState([
    { id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "" }
  ]);

  // Custom dropdown states (same as Sales component)
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [isCustomColor, setIsCustomColor] = useState(false);
  const [isCustomDoorHandle, setIsCustomDoorHandle] = useState(false);
  const [isCustomSurface, setIsCustomSurface] = useState(false);
  const [isCustomInstallation, setIsCustomInstallation] = useState(false);
  const [isCustomInstallationLocation, setIsCustomInstallationLocation] = useState(false);
  const [isCustomInnerFrame, setIsCustomInnerFrame] = useState(false);
  const [isCustomOuterFrame, setIsCustomOuterFrame] = useState(false);

  // Dropdown options (same as Sales component)
  // Server-side product options (Sales component ile paylaşılır)
  const [productOptions, setProductOptions] = useState(DEFAULT_PRODUCT_OPTIONS_JS);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/product-options`);
        setProductOptions(prev => ({ ...prev, ...(res.data || {}) }));
      } catch (e) { console.error(e); }
    })();
  }, []);
  const _modelOptionsBackup_UNUSED2 = [
    "QUEEN", "ZEUS DİAMOND", "MERT P1", "MERT P2", "MERT P3", "NEW YORK", "TOKYO", 
    "BERLİN", "PARİS", "VENEDİK", "HONG KONG", "LİZBON", "PETRA", "SİNGAPUR", 
    "LYON PLUS", "FLORANSA", "SİDNEY", "BUDAPEŞTE", "SAN FRANSİNCO", "PRAG", 
    "LYON", "RİO", "İSTANBUL", "MİAMİ", "BLADE", "ROMA", "OSLO", "BARCELONA", 
    "BASEL", "SELANİK", "SARMAŞIK", "WANKEL", "LALE", "PLEKSİ A", "PALMİYE", 
    "HİRA", "NEWA", "MODEL X", "PATRAS", "DALGA", "DİKDÖRTGEN", "ARTI", "PORTO", 
    "ZAMBAK", "ATLANTİS", "ZEUS", "MELAMİN MERDİVEN", "MELAMİN TEK GÖBEK"
  ];
  const modelOptions = productOptions.models || [];
  const colorOptions = productOptions.colors || [];
  const doorHandleOptions = productOptions.door_handles || [];
  const surfaceOptions = productOptions.surfaces || [];
  const installationOptions = productOptions.installations || [];
  const installationLocationOptions = productOptions.installation_locations || [];
  const innerFrameOptions = productOptions.inner_frames || [];
  const outerFrameOptions = productOptions.outer_frames || [];

  // Ölçüm tablosu seçenekleri
  const widthOptions = productOptions.widths || [];
  const heightOptions = productOptions.heights || [];
  const frameOptions = productOptions.frames || [];
  const directionOptions = productOptions.directions || [];
  const glassOptions = productOptions.glasses || [];
  const roomOptions = productOptions.rooms || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser?.id) {
      toast.error("Kullanıcı bilgisi bulunamadı");
      return;
    }

    try {
      const orderData = {
        ...formData,
        measurements: JSON.stringify(measurements.filter(row => row.width || row.height || row.frame || row.direction || row.glass || row.room || row.description))
      };

      await axios.post(`${API}/bayi-orders/by-user/${currentUser.id}`, orderData);
      toast.success("Sipariş tedarikçiye gönderildi! Onay bekleniyor.");
      
      // Formu temizle
      setFormData({
        customer_name: "",
        customer_phone: "",
        customer_address: "",
        model: "",
        color: "",
        door_handle: "",
        surface: "",
        installation: "",
        installation_location: "",
        inner_frame: "",
        outer_frame: "",
        measurements: "",
        details: "",
        production_line: "lake",
        notes: ""
      });
      setMeasurements([{ id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "" }]);
      
      // Reset custom states
      setIsCustomModel(false);
      setIsCustomColor(false);
      setIsCustomDoorHandle(false);
      setIsCustomSurface(false);
      setIsCustomInstallation(false);
      setIsCustomInstallationLocation(false);
      setIsCustomInnerFrame(false);
      setIsCustomOuterFrame(false);
    } catch (error) {
      console.error("Sipariş gönderilirken hata:", error);
      toast.error("Sipariş gönderilirken hata oluştu");
    }
  };

  const addMeasurement = () => {
    const newId = Math.max(...measurements.map(m => m.id), 0) + 1;
    setMeasurements([...measurements, { id: newId, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
  };

  const copyMeasurement = (measurement) => {
    const newId = Math.max(...measurements.map(m => m.id), 0) + 1;
    const copy = { ...measurement, id: newId };
    const idx = measurements.findIndex(m => m.id === measurement.id);
    const newList = [...measurements];
    newList.splice(idx + 1, 0, copy);
    setMeasurements(newList);
  };

  const removeMeasurement = (idToRemove) => {
    if (measurements.length > 1) {
      setMeasurements(measurements.filter(m => m.id !== idToRemove));
    }
  };

  const updateMeasurement = (id, field, value) => {
    setMeasurements(measurements.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sipariş Ver</h1>
          <p className="text-muted-foreground">Müşteriniz için sipariş oluşturun ve tedarikçiye gönderin</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Müşteri Bilgileri */}
        <Card>
          <CardHeader>
            <CardTitle>Müşteri Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_name">Müşteri Adı *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Telefon *</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="customer_address">Adres *</Label>
              <Textarea
                id="customer_address"
                value={formData.customer_address}
                onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Ürün Bilgileri */}
        <Card>
          <CardHeader>
            <CardTitle>Ürün Detayları</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="production_line">Üretim Hattı *</Label>
              <Select value={formData.production_line} onValueChange={(value) => setFormData({...formData, production_line: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lake">LAKE</SelectItem>
                  <SelectItem value="melamin">MELAMİN</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ürün Özellikleri Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="model">Model</Label>
                {isCustomModel ? (
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    placeholder="Özel model adını yazın"
                    onBlur={() => {
                      if (!formData.model) {
                        setIsCustomModel(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.model} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomModel(true);
                        setFormData({...formData, model: ""});
                      } else {
                        setFormData({...formData, model: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Model seçin" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {modelOptions.map((model) => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="color">Renk</Label>
                {isCustomColor ? (
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    placeholder="Özel renk adını yazın"
                    onBlur={() => {
                      if (!formData.color) {
                        setIsCustomColor(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.color} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomColor(true);
                        setFormData({...formData, color: ""});
                      } else {
                        setFormData({...formData, color: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Renk seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color} value={color}>{color}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="door_handle">Kapı Kolu</Label>
                {isCustomDoorHandle ? (
                  <Input
                    value={formData.door_handle}
                    onChange={(e) => setFormData({...formData, door_handle: e.target.value})}
                    placeholder="Özel kapı kolu adını yazın"
                    onBlur={() => {
                      if (!formData.door_handle) {
                        setIsCustomDoorHandle(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.door_handle} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomDoorHandle(true);
                        setFormData({...formData, door_handle: ""});
                      } else {
                        setFormData({...formData, door_handle: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kapı kolu seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {doorHandleOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="surface">Yüzey</Label>
                {isCustomSurface ? (
                  <Input
                    value={formData.surface}
                    onChange={(e) => setFormData({...formData, surface: e.target.value})}
                    placeholder="Özel yüzey adını yazın"
                    onBlur={() => {
                      if (!formData.surface) {
                        setIsCustomSurface(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.surface} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomSurface(true);
                        setFormData({...formData, surface: ""});
                      } else {
                        setFormData({...formData, surface: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Yüzey seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {surfaceOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="installation">Montaj</Label>
                {isCustomInstallation ? (
                  <Input
                    value={formData.installation}
                    onChange={(e) => setFormData({...formData, installation: e.target.value})}
                    placeholder="Özel montaj türü yazın"
                    onBlur={() => {
                      if (!formData.installation) {
                        setIsCustomInstallation(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.installation} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomInstallation(true);
                        setFormData({...formData, installation: ""});
                      } else {
                        setFormData({...formData, installation: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Montaj türü seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {installationOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="installation_location">Montaj Yeri</Label>
                {isCustomInstallationLocation ? (
                  <Input
                    value={formData.installation_location}
                    onChange={(e) => setFormData({...formData, installation_location: e.target.value})}
                    placeholder="Özel montaj yeri yazın"
                    onBlur={() => {
                      if (!formData.installation_location) {
                        setIsCustomInstallationLocation(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.installation_location} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomInstallationLocation(true);
                        setFormData({...formData, installation_location: ""});
                      } else {
                        setFormData({...formData, installation_location: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Montaj yeri seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {installationLocationOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="inner_frame">İç Pervaz</Label>
                {isCustomInnerFrame ? (
                  <Input
                    value={formData.inner_frame}
                    onChange={(e) => setFormData({...formData, inner_frame: e.target.value})}
                    placeholder="Özel iç pervaz yazın"
                    onBlur={() => {
                      if (!formData.inner_frame) {
                        setIsCustomInnerFrame(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.inner_frame} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomInnerFrame(true);
                        setFormData({...formData, inner_frame: ""});
                      } else {
                        setFormData({...formData, inner_frame: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="İç pervaz seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {innerFrameOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="outer_frame">Dış Pervaz</Label>
                {isCustomOuterFrame ? (
                  <Input
                    value={formData.outer_frame}
                    onChange={(e) => setFormData({...formData, outer_frame: e.target.value})}
                    placeholder="Özel dış pervaz yazın"
                    onBlur={() => {
                      if (!formData.outer_frame) {
                        setIsCustomOuterFrame(false);
                      }
                    }}
                  />
                ) : (
                  <Select 
                    value={formData.outer_frame} 
                    onValueChange={(value) => {
                      if (value === "özel") {
                        setIsCustomOuterFrame(true);
                        setFormData({...formData, outer_frame: ""});
                      } else {
                        setFormData({...formData, outer_frame: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Dış pervaz seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {outerFrameOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                      <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ölçü Tablosu */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Ölçü Tablosu
              <Button type="button" onClick={addMeasurement} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Ölçü Ekle
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border p-2 text-left font-medium">#</th>
                    <th className="border p-2 text-left font-medium">En (cm)</th>
                    <th className="border p-2 text-left font-medium">Boy (cm)</th>
                    <th className="border p-2 text-left font-medium">Kasa</th>
                    <th className="border p-2 text-left font-medium">Yön</th>
                    <th className="border p-2 text-left font-medium">Cam</th>
                    <th className="border p-2 text-left font-medium">Oda</th>
                    <th className="border p-2 text-left font-medium">Açıklama</th>
                    <th className="border p-2 text-center font-medium bg-purple-50">Adet</th>
                    <th className="border p-2 text-left font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {measurements.map((measurement) => (
                    <tr key={measurement.id} className="hover:bg-gray-50">
                      <td className="border p-2 font-medium text-gray-600">{measurement.id}</td>
                      <td className="border p-2">
                        <Select
                          value={measurement.width.toString()}
                          onValueChange={(value) => updateMeasurement(measurement.id, 'width', value)}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue placeholder="En" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {widthOptions.map((option) => (
                              <SelectItem key={option} value={option.toString()}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border p-2">
                        <Select
                          value={measurement.height.toString()}
                          onValueChange={(value) => updateMeasurement(measurement.id, 'height', value)}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue placeholder="Boy" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {heightOptions.map((option) => (
                              <SelectItem key={option} value={option.toString()}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border p-2">
                        <Select
                          value={measurement.frame.toString()}
                          onValueChange={(value) => updateMeasurement(measurement.id, 'frame', value)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Kasa" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {frameOptions.map((option) => (
                              <SelectItem key={option} value={option.toString()}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border p-2">
                        <Select
                          value={measurement.direction}
                          onValueChange={(value) => updateMeasurement(measurement.id, 'direction', value)}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue placeholder="Yön" />
                          </SelectTrigger>
                          <SelectContent>
                            {directionOptions.map((option) => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border p-2">
                        <Select
                          value={measurement.glass}
                          onValueChange={(value) => updateMeasurement(measurement.id, 'glass', value)}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue placeholder="Cam" />
                          </SelectTrigger>
                          <SelectContent>
                            {glassOptions.map((option) => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border p-2">
                        <Select
                          value={measurement.room}
                          onValueChange={(value) => updateMeasurement(measurement.id, 'room', value)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Oda" />
                          </SelectTrigger>
                          <SelectContent>
                            {roomOptions.map((option) => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border p-2">
                        <Input
                          value={measurement.description}
                          onChange={(e) => updateMeasurement(measurement.id, 'description', e.target.value)}
                          className="w-32"
                        />
                      </td>
                      <td className="border p-2 bg-purple-50">
                        <Input
                          type="number"
                          min="1"
                          value={measurement.quantity || 1}
                          onChange={(e) => updateMeasurement(measurement.id, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-16 text-center font-medium"
                        />
                      </td>
                      <td className="border p-2">
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            title="Satırı kopyala"
                            onClick={() => copyMeasurement(measurement)}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {measurements.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeMeasurement(measurement.id)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const newId = Math.max(...measurements.map(m => m.id), 0) + 1;
                    setMeasurements([...measurements, { 
                      id: newId, 
                      width: "", 
                      height: "", 
                      frame: "", 
                      direction: "", 
                      glass: "", 
                      room: "", 
                      description: "" 
                    }]);
                  }}
                  className="flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Yeni Ölçü Ekle</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ek Detaylar */}
        <Card>
          <CardHeader>
            <CardTitle>Ek Bilgiler</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="details">Ek Detaylar</Label>
              <Textarea
                id="details"
                value={formData.details}
                onChange={(e) => setFormData({...formData, details: e.target.value})}
                placeholder="Ek açıklamalar, özel istekler..."
              />
            </div>
            <div>
              <Label htmlFor="notes">Notlar</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="İç notlar, hatırlatmalar..."
              />
            </div>
          </CardContent>
        </Card>

        <Button type="submit" className="w-full" size="lg">
          <Send className="h-4 w-4 mr-2" />
          Tedarikçiye Gönder
        </Button>
      </form>
    </div>
  );
};

// BayiMyOrders Component (Bayi kendi siparişlerini görme)
const BayiMyOrders = ({ currentUser, sessionToken }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      fetchMyOrders();
    }
  }, [currentUser]);

  const fetchMyOrders = async () => {
    try {
      const response = await axios.get(`${API}/bayi-orders/by-user/${currentUser.id}`, {
        params: { session_token: sessionToken }
      });
      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Siparişler yüklenirken hata:", error);
      toast.error("Siparişler yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      beklemede: { color: 'bg-orange-100 text-orange-800', text: 'Onay Bekliyor' },
      onaylandi: { color: 'bg-green-100 text-green-800', text: 'Onaylandı' },
      reddedildi: { color: 'bg-red-100 text-red-800', text: 'Reddedildi' }
    };
    const config = statusConfig[status] || statusConfig.beklemede;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.text}</span>;
  };

  const parseMeasurements = (measurementsJson) => {
    try {
      return JSON.parse(measurementsJson || '[]');
    } catch {
      return [];
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Siparişlerim</h1>
        <p className="text-muted-foreground">Vermiş olduğunuz siparişlerin durumunu takip edin</p>
      </div>

      <div className="grid gap-4">
        {orders.length > 0 ? (
          orders.map((order) => (
            <Card key={order.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">{order.customer_name}</h3>
                    <p className="text-sm text-gray-600">Tel: {order.customer_phone}</p>
                    <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleString('tr-TR')}</p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(order.status)}
                    {order.price > 0 && (
                      <p className="text-lg font-bold text-green-600 mt-2">
                        {order.price.toLocaleString('tr-TR')} ₺
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm"><strong>Model:</strong> {order.model}</p>
                    <p className="text-sm"><strong>Renk:</strong> {order.color}</p>
                    <p className="text-sm"><strong>Kapı Kolu:</strong> {order.door_handle}</p>
                  </div>
                  <div>
                    <p className="text-sm"><strong>Üretim Hattı:</strong> {order.production_line?.toUpperCase()}</p>
                    <p className="text-sm"><strong>Adres:</strong> {order.customer_address}</p>
                  </div>
                </div>

                {/* Detay Gör butonu */}
                <div className="flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setSelectedOrder(order);
                      setIsDetailOpen(true);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Detay Gör
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-12">
            <ClipboardList className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg text-gray-600">Henüz sipariş vermediniz</p>
          </div>
        )}
      </div>

      {/* Sipariş Detayı Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sipariş Detayları</DialogTitle>
            <DialogDescription>
              {selectedOrder && `${selectedOrder.customer_name} müşterisi için sipariş detayları`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Sipariş ve Müşteri Bilgileri */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Sipariş Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p><strong>Sipariş Tarihi:</strong> {new Date(selectedOrder.created_at).toLocaleString('tr-TR')}</p>
                    <p><strong>Durum:</strong> {getStatusBadge(selectedOrder.status)}</p>
                    {selectedOrder.approved_at && (
                      <p><strong>Onaylanma Tarihi:</strong> {new Date(selectedOrder.approved_at).toLocaleString('tr-TR')}</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Müşteri Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p><strong>Ad:</strong> {selectedOrder.customer_name}</p>
                    <p><strong>Telefon:</strong> {selectedOrder.customer_phone}</p>
                    <p><strong>Adres:</strong> {selectedOrder.customer_address}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Ürün Detayları */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ürün Detayları</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p><strong>Üretim Hattı:</strong> {selectedOrder.production_line?.toUpperCase()}</p>
                      <p><strong>Model:</strong> {selectedOrder.model || '-'}</p>
                      <p><strong>Renk:</strong> {selectedOrder.color || '-'}</p>
                      <p><strong>Kapı Kolu:</strong> {selectedOrder.door_handle || '-'}</p>
                    </div>
                    <div>
                      <p><strong>Yüzey:</strong> {selectedOrder.surface || '-'}</p>
                      <p><strong>Montaj:</strong> {selectedOrder.installation || '-'}</p>
                      <p><strong>Montaj Yeri:</strong> {selectedOrder.installation_location || '-'}</p>
                      <p><strong>İç Pervaz:</strong> {selectedOrder.inner_frame || '-'}</p>
                      <p><strong>Dış Pervaz:</strong> {selectedOrder.outer_frame || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ölçü Tablosu */}
              {selectedOrder.measurements && parseMeasurements(selectedOrder.measurements).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ölçü Tablosu</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border p-2 text-left">#</th>
                            <th className="border p-2 text-left">En (cm)</th>
                            <th className="border p-2 text-left">Boy (cm)</th>
                            <th className="border p-2 text-left">Kasa</th>
                            <th className="border p-2 text-left">Yön</th>
                            <th className="border p-2 text-left">Cam</th>
                            <th className="border p-2 text-left">Oda</th>
                            <th className="border p-2 text-left">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseMeasurements(selectedOrder.measurements).map((measurement, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border p-2 font-medium">{index + 1}</td>
                              <td className="border p-2">{measurement.width || '-'}</td>
                              <td className="border p-2">{measurement.height || '-'}</td>
                              <td className="border p-2">{measurement.frame || '-'}</td>
                              <td className="border p-2">{measurement.direction || '-'}</td>
                              <td className="border p-2">{measurement.glass || '-'}</td>
                              <td className="border p-2">{measurement.room || '-'}</td>
                              <td className="border p-2">{measurement.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ek Bilgiler */}
              {(selectedOrder.details || selectedOrder.notes) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ek Bilgiler</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedOrder.details && (
                      <div className="mb-4">
                        <p><strong>Ek Detaylar:</strong></p>
                        <p className="text-sm text-gray-600 mt-1">{selectedOrder.details}</p>
                      </div>
                    )}
                    {selectedOrder.notes && (
                      <div>
                        <p><strong>Notlar:</strong></p>
                        <p className="text-sm text-gray-600 mt-1">{selectedOrder.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Fiyat Bilgisi */}
              {selectedOrder.price > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Fiyat Bilgisi</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedOrder.price.toLocaleString('tr-TR')} ₺
                    </p>
                    {selectedOrder.approved_at && (
                      <p className="text-sm text-gray-600 mt-2">
                        Onaylanma Tarihi: {new Date(selectedOrder.approved_at).toLocaleString('tr-TR')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// BayiAccount Component (Bayi cari hesap durumu)
const BayiAccount = ({ currentUser, sessionToken }) => {
  const [debtSummary, setDebtSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isTransactionDetailOpen, setIsTransactionDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      fetchAccountData();
    }
  }, [currentUser]);

  const fetchAccountData = async () => {
    try {
      // Borç özeti
      const debtResponse = await axios.get(`${API}/bayiler/debt-summary-by-user/${currentUser.id}`, {
        params: { session_token: sessionToken }
      });
      setDebtSummary(debtResponse.data);

      // Hesap hareketleri (transactions)
      const transactionsResponse = await axios.get(`${API}/transactions/${debtResponse.data.bayi_id}`, {
        params: { session_token: sessionToken }
      });
      setTransactions(transactionsResponse.data);

      // Siparişler
      const ordersResponse = await axios.get(`${API}/bayi-orders/by-user/${currentUser.id}`, {
        params: { session_token: sessionToken }
      });
      setOrders(ordersResponse.data);

      setLoading(false);
    } catch (error) {
      console.error("Hesap bilgileri yüklenirken hata:", error);
      toast.error("Hesap bilgileri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      beklemede: { color: 'bg-orange-100 text-orange-800', text: 'Onay Bekliyor' },
      onaylandi: { color: 'bg-green-100 text-green-800', text: 'Onaylandı' },
      reddedildi: { color: 'bg-red-100 text-red-800', text: 'Reddedildi' }
    };
    const config = statusConfig[status] || statusConfig.beklemede;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.text}</span>;
  };

  const parseMeasurements = (measurementsJson) => {
    try {
      return JSON.parse(measurementsJson || '[]');
    } catch {
      return [];
    }
  };

  const exportAccountStatement = () => {
    // Hesap ekstresi verileri
    const statementData = {
      bayi_info: debtSummary,
      summary: {
        total_debt: debtSummary.total_debt,
        total_payment: debtSummary.total_payment,
        current_balance: debtSummary.current_balance
      },
      transactions: transactions,
      orders: orders,
      export_date: new Date().toLocaleString('tr-TR')
    };

    // CSV formatında ekstre oluştur
    let csvContent = "BAYI HESAP EKSTRESİ\n";
    csvContent += `Bayi: ${debtSummary.bayi_name}\n`;
    csvContent += `Ekstre Tarihi: ${statementData.export_date}\n\n`;
    csvContent += "ÖZET BİLGİLER\n";
    csvContent += `Toplam Borç: ${debtSummary.total_debt.toLocaleString('tr-TR')} ₺\n`;
    csvContent += `Toplam Ödeme: ${debtSummary.total_payment.toLocaleString('tr-TR')} ₺\n`;
    csvContent += `Güncel Bakiye: ${debtSummary.current_balance.toLocaleString('tr-TR')} ₺\n\n`;
    
    csvContent += "HESAP HAREKETLERİ\n";
    csvContent += "Tarih,İşlem Türü,Açıklama,Tutar\n";
    transactions.forEach(transaction => {
      const type = transaction.transaction_type === 'debit' ? 'Borç' : 'Alacak';
      const amount = transaction.transaction_type === 'debit' ? transaction.amount : -transaction.amount;
      csvContent += `${new Date(transaction.created_at).toLocaleDateString('tr-TR')},${type},"${transaction.description}",${amount.toLocaleString('tr-TR')} ₺\n`;
    });

    csvContent += "\nSİPARİŞLER\n";
    csvContent += "Tarih,Müşteri,Durum,Tutar\n";
    orders.forEach(order => {
      csvContent += `${new Date(order.created_at).toLocaleDateString('tr-TR')},"${order.customer_name}",${order.status},${(order.price || 0).toLocaleString('tr-TR')} ₺\n`;
    });

    // Dosyayı indir
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `hesap_ekstresi_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Hesap ekstresi indirildi");
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ESKAY Hesap Durumu</h1>
          <p className="text-muted-foreground">ESKAY ile olan hesap durumunuz ve işlem geçmişiniz</p>
        </div>
        <Button onClick={exportAccountStatement} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Ekstre İndir
        </Button>
      </div>

      {/* Özet Kartları */}
      {debtSummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Borç</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {debtSummary.total_debt.toLocaleString('tr-TR')} ₺
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Ödeme</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {debtSummary.total_payment.toLocaleString('tr-TR')} ₺
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Güncel Bakiye</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${debtSummary.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {debtSummary.current_balance.toLocaleString('tr-TR')} ₺
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Hesap Hareketleri */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Hesap Hareketleri
          </CardTitle>
          <CardDescription>Son işlemlerinizin detayları</CardDescription>
        </CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <div className="space-y-3">
              {transactions.slice(0, 10).map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${transaction.transaction_type === 'debit' ? 'bg-red-500' : 'bg-green-500'}`}></div>
                      <p className="font-medium">{transaction.description}</p>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {new Date(transaction.created_at).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${transaction.transaction_type === 'debit' ? 'text-red-600' : 'text-green-600'}`}>
                      {transaction.transaction_type === 'debit' ? '+' : '-'}{Math.abs(transaction.amount).toLocaleString('tr-TR')} ₺
                    </p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedTransaction(transaction);
                        setIsTransactionDetailOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Detay
                    </Button>
                  </div>
                </div>
              ))}
              {transactions.length > 10 && (
                <p className="text-center text-gray-500 text-sm">
                  Ve {transactions.length - 10} işlem daha...
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClipboardList className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg text-gray-600">Henüz hesap hareketi bulunamadı</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sipariş Geçmişi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Sipariş Geçmişi
          </CardTitle>
          <CardDescription>Vermiş olduğunuz siparişlerin özeti</CardDescription>
        </CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <div className="space-y-3">
              {orders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{order.customer_name}</p>
                      {getStatusBadge(order.status)}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {new Date(order.created_at).toLocaleString('tr-TR')} • {order.model} - {order.color}
                    </p>
                  </div>
                  <div className="text-right">
                    {order.price > 0 && (
                      <p className="font-bold text-green-600 mb-2">
                        {order.price.toLocaleString('tr-TR')} ₺
                      </p>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedOrder(order);
                        setIsOrderDetailOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Detay
                    </Button>
                  </div>
                </div>
              ))}
              {orders.length > 5 && (
                <p className="text-center text-gray-500 text-sm">
                  Ve {orders.length - 5} sipariş daha...
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg text-gray-600">Henüz sipariş bulunamadı</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* İşlem Detayı Dialog - Enhanced with Order Details */}
      <Dialog open={isTransactionDetailOpen} onOpenChange={setIsTransactionDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>İşlem ve Sipariş Detayları</DialogTitle>
            <DialogDescription>
              {selectedTransaction && `${selectedTransaction.description} işlem ve sipariş detayları`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-6">
              {/* İşlem Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    İşlem Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p><strong>İşlem Türü:</strong> {selectedTransaction.transaction_type === 'debit' ? 'Borç' : 'Alacak'}</p>
                      <p><strong>Tutar:</strong> {Math.abs(selectedTransaction.amount).toLocaleString('tr-TR')} ₺</p>
                    </div>
                    <div>
                      <p><strong>Tarih:</strong> {new Date(selectedTransaction.created_at).toLocaleString('tr-TR')}</p>
                      <p><strong>Hesap Türü:</strong> {selectedTransaction.account_type || 'bayi'}</p>
                    </div>
                    <div>
                      <p><strong>Açıklama:</strong> {selectedTransaction.description}</p>
                      {selectedTransaction.payment_method && <p><strong>Ödeme Yöntemi:</strong> {selectedTransaction.payment_method}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ürün Detayları */}
              {(selectedTransaction.model || selectedTransaction.color || selectedTransaction.production_line) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="h-5 w-5" />
                      Ürün Detayları
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        {selectedTransaction.production_line && <p><strong>Üretim Hattı:</strong> {selectedTransaction.production_line.toUpperCase()}</p>}
                        {selectedTransaction.model && <p><strong>Model:</strong> {selectedTransaction.model}</p>}
                        {selectedTransaction.color && <p><strong>Renk:</strong> {selectedTransaction.color}</p>}
                        {selectedTransaction.door_handle && <p><strong>Kapı Kolu:</strong> {selectedTransaction.door_handle}</p>}
                      </div>
                      <div>
                        {selectedTransaction.surface && <p><strong>Yüzey:</strong> {selectedTransaction.surface}</p>}
                        {selectedTransaction.installation && <p><strong>Montaj:</strong> {selectedTransaction.installation}</p>}
                        {selectedTransaction.installation_location && <p><strong>Montaj Yeri:</strong> {selectedTransaction.installation_location}</p>}
                        {selectedTransaction.inner_frame && <p><strong>İç Pervaz:</strong> {selectedTransaction.inner_frame}</p>}
                        {selectedTransaction.outer_frame && <p><strong>Dış Pervaz:</strong> {selectedTransaction.outer_frame}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Nihai Müşteri */}
              {selectedTransaction.end_customer && (
                <Card className="bg-purple-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      🏢 Nihai Müşteri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-base">{selectedTransaction.end_customer}</p>
                  </CardContent>
                </Card>
              )}

              {/* Ölçü Bilgileri */}
              {selectedTransaction.measurements && parseMeasurements(selectedTransaction.measurements).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      Ölçü Bilgileri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border p-2 text-left">#</th>
                            <th className="border p-2 text-left">En</th>
                            <th className="border p-2 text-left">Boy</th>
                            <th className="border p-2 text-left">Kasa</th>
                            <th className="border p-2 text-left">Yönü</th>
                            <th className="border p-2 text-left">Cam</th>
                            <th className="border p-2 text-left">Oda</th>
                            <th className="border p-2 text-left">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseMeasurements(selectedTransaction.measurements).map((measurement, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border p-2 font-medium">{index + 1}</td>
                              <td className="border p-2">{measurement.width || '-'}</td>
                              <td className="border p-2">{measurement.height || '-'}</td>
                              <td className="border p-2">{measurement.thickness || measurement.frame || '-'}</td>
                              <td className="border p-2">{measurement.direction || '-'}</td>
                              <td className="border p-2">{measurement.glass || '-'}</td>
                              <td className="border p-2">{measurement.room || '-'}</td>
                              <td className="border p-2">{measurement.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ödeme Bilgileri */}
              {(selectedTransaction.payment_method || selectedTransaction.payment_description) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Ödeme Bilgileri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTransaction.payment_method && (
                        <p><strong>Ödeme Yöntemi:</strong> {selectedTransaction.payment_method}</p>
                      )}
                      {selectedTransaction.payment_description && (
                        <p><strong>Ödeme Açıklaması:</strong> {selectedTransaction.payment_description}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ekstra Detaylar */}
              {selectedTransaction.details && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Ek Bilgiler
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTransaction.details}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sipariş Detayı Dialog */}
      <Dialog open={isOrderDetailOpen} onOpenChange={setIsOrderDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sipariş Detayları</DialogTitle>
            <DialogDescription>
              {selectedOrder && `${selectedOrder.customer_name} müşterisi için sipariş detayları`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Sipariş ve Müşteri Bilgileri */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Sipariş Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p><strong>Sipariş Tarihi:</strong> {new Date(selectedOrder.created_at).toLocaleString('tr-TR')}</p>
                    <p><strong>Durum:</strong> {getStatusBadge(selectedOrder.status)}</p>
                    {selectedOrder.approved_at && (
                      <p><strong>Onaylanma Tarihi:</strong> {new Date(selectedOrder.approved_at).toLocaleString('tr-TR')}</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Müşteri Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p><strong>Ad:</strong> {selectedOrder.customer_name}</p>
                    <p><strong>Telefon:</strong> {selectedOrder.customer_phone}</p>
                    <p><strong>Adres:</strong> {selectedOrder.customer_address}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Ürün Detayları */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ürün Detayları</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p><strong>Üretim Hattı:</strong> {selectedOrder.production_line?.toUpperCase()}</p>
                      <p><strong>Model:</strong> {selectedOrder.model || '-'}</p>
                      <p><strong>Renk:</strong> {selectedOrder.color || '-'}</p>
                      <p><strong>Kapı Kolu:</strong> {selectedOrder.door_handle || '-'}</p>
                    </div>
                    <div>
                      <p><strong>Yüzey:</strong> {selectedOrder.surface || '-'}</p>
                      <p><strong>Montaj:</strong> {selectedOrder.installation || '-'}</p>
                      <p><strong>Montaj Yeri:</strong> {selectedOrder.installation_location || '-'}</p>
                      <p><strong>İç Pervaz:</strong> {selectedOrder.inner_frame || '-'}</p>
                      <p><strong>Dış Pervaz:</strong> {selectedOrder.outer_frame || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ölçü Tablosu */}
              {selectedOrder.measurements && parseMeasurements(selectedOrder.measurements).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ölçü Tablosu</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border p-2 text-left">#</th>
                            <th className="border p-2 text-left">En (cm)</th>
                            <th className="border p-2 text-left">Boy (cm)</th>
                            <th className="border p-2 text-left">Kasa</th>
                            <th className="border p-2 text-left">Yön</th>
                            <th className="border p-2 text-left">Cam</th>
                            <th className="border p-2 text-left">Oda</th>
                            <th className="border p-2 text-left">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseMeasurements(selectedOrder.measurements).map((measurement, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border p-2 font-medium">{index + 1}</td>
                              <td className="border p-2">{measurement.width || '-'}</td>
                              <td className="border p-2">{measurement.height || '-'}</td>
                              <td className="border p-2">{measurement.frame || '-'}</td>
                              <td className="border p-2">{measurement.direction || '-'}</td>
                              <td className="border p-2">{measurement.glass || '-'}</td>
                              <td className="border p-2">{measurement.room || '-'}</td>
                              <td className="border p-2">{measurement.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ek Bilgiler */}
              {(selectedOrder.details || selectedOrder.notes) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ek Bilgiler</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedOrder.details && (
                      <div className="mb-4">
                        <p><strong>Ek Detaylar:</strong></p>
                        <p className="text-sm text-gray-600 mt-1">{selectedOrder.details}</p>
                      </div>
                    )}
                    {selectedOrder.notes && (
                      <div>
                        <p><strong>Notlar:</strong></p>
                        <p className="text-sm text-gray-600 mt-1">{selectedOrder.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Fiyat Bilgisi */}
              {selectedOrder.price > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Fiyat Bilgisi</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedOrder.price.toLocaleString('tr-TR')} ₺
                    </p>
                    {selectedOrder.approved_at && (
                      <p className="text-sm text-gray-600 mt-2">
                        Onaylanma Tarihi: {new Date(selectedOrder.approved_at).toLocaleString('tr-TR')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// User Management Component (Admin Only)
const UserManagement = ({ currentUser, sessionToken }) => {
  const [users, setUsers] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCustomerUserDialogOpen, setIsCustomerUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "sales",
    full_name: "",
    allowed_screens: [],
    installer_id: ""
  });
  
  const [customerUserForm, setCustomerUserForm] = useState({
    customer_id: "",
    username: "",
    password: "",
    full_name: ""
  });
  const [portalCustomerSearch, setPortalCustomerSearch] = useState("");
  const [showPortalDropdown, setShowPortalDropdown] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchInstallers();
    fetchCustomers();
  }, []);

  const fetchUsers = async () => {
    try {
      console.log('🔍 DEBUG: Fetching users from API...');
      const response = await axios.get(`${API}/users?session_token=${sessionToken}`);
      console.log('🔍 DEBUG: API response received:', response.data.length, 'users');
      console.log('🔍 DEBUG: First 3 users:', response.data.slice(0, 3).map(u => u.username));
      setUsers(response.data);
      console.log('🔍 DEBUG: Users state updated');
      setLoading(false);
    } catch (error) {
      console.error("Kullanıcılar yüklenirken hata:", error);
      toast.error("Kullanıcılar yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API}/installers?session_token=${sessionToken}`);
      setInstallers(response.data);
    } catch (error) {
      console.error("Montaj elemanları yüklenirken hata:", error);
    }
  };
  
  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers?limit=2000`);
      const customersData = response.data.customers || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error("Müşteriler yüklenirken hata:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userData = { ...formData };
      if (userData.role !== 'installation') {
        userData.installer_id = null; // Montaj personeli değilse installer_id'yi temizle
      }
      await axios.post(`${API}/users?session_token=${sessionToken}`, userData);
      toast.success("Kullanıcı oluşturuldu");
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error("Kullanıcı oluşturulurken hata:", error);
      toast.error(error.response?.data?.detail || "Kullanıcı oluşturulurken hata oluştu");
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: "",
      role: user.role,
      full_name: user.full_name,
      allowed_screens: user.allowed_screens || [],
      installation_tab_permissions: user.installation_tab_permissions || {},
      production_tab_permissions: user.production_tab_permissions || {},
      installer_id: user.installer_id || ""
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password; // Don't send empty password
      }
      if (updateData.role !== 'installation') {
        updateData.installer_id = null; // Montaj personeli değilse installer_id'yi temizle
      }
      await axios.put(`${API}/users/${editingUser.id}?session_token=${sessionToken}`, updateData);
      toast.success("Kullanıcı güncellendi");
      setIsEditDialogOpen(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error("Kullanıcı güncellenirken hata:", error);
      toast.error(error.response?.data?.detail || "Kullanıcı güncellenirken hata oluştu");
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${userToDelete.id}?session_token=${sessionToken}`);
      toast.success("Kullanıcı silindi");
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (error) {
      console.error("Kullanıcı silinirken hata:", error);
      toast.error(error.response?.data?.detail || "Kullanıcı silinirken hata oluştu");
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      role: "sales",
      full_name: "",
      allowed_screens: [],
      installer_id: ""
    });
  };

  
  const handleCreateCustomerUser = async (e) => {
    e.preventDefault();
    
    // Şifre validasyonu - minimum 6 karakter
    if (customerUserForm.password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalıdır");
      return;
    }
    
    try {
      const userData = {
        username: customerUserForm.username,
        password: customerUserForm.password,
        role: "customer",
        full_name: customerUserForm.full_name,
        allowed_screens: [],
        customer_id: customerUserForm.customer_id
      };
      
      await axios.post(`${API}/users?session_token=${sessionToken}`, userData);
      toast.success("Müşteri kullanıcısı oluşturuldu");
      setIsCustomerUserDialogOpen(false);
      setCustomerUserForm({
        customer_id: "",
        username: "",
        password: "",
        full_name: ""
      });
      fetchUsers();
    } catch (error) {
      console.error("Müşteri kullanıcısı oluşturulurken hata:", error);
      toast.error(error.response?.data?.detail || "Müşteri kullanıcısı oluşturulurken hata oluştu");
    }
  };
  
  const handleCustomerSelect = (customer) => {
    setCustomerUserForm({
      ...customerUserForm,
      customer_id: customer.id,
      username: `musteri_${customer.serial_number || customer.id.slice(0, 6)}`,
      full_name: `${customer.name} ${customer.surname}`
    });
  };


  const getRoleLabel = (role) => {
    const roleLabels = {
      admin: "Yönetici",
      sales: "Satış Personeli",
      accounting: "Muhasebe Personeli",
      production: "İmalat Personeli",
      installation: "Montaj Personeli",
      customer: "Müşteri (Portal)"
    };
    return roleLabels[role] || role;
  };

  const getInstallerName = (installerId) => {
    const installer = installers.find(i => i.id === installerId);
    return installer ? installer.name : "Bilinmiyor";
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const toggleScreenPermission = (screenPath) => {
    setFormData(prev => ({
      ...prev,
      allowed_screens: prev.allowed_screens.includes(screenPath)
        ? prev.allowed_screens.filter(s => s !== screenPath)
        : [...prev.allowed_screens, screenPath]
    }));
  };

  const availableScreens = [
    // Ana Sayfalar
    { path: "/", label: "Dashboard" },
    { path: "/customer-search", label: "Müşteri Detay Arama" },
    { path: "/agenda", label: "Ajanda" },
    { path: "/offers", label: "Teklifler" },
    { path: "/collection-tracking", label: "Tahsilat Takip" },
    
    // Cari Hesaplar
    { path: "/customers", label: "Müşteriler" },
    { path: "/suppliers", label: "Tedarikçiler" },
    
    // C.H. İşlemleri
    { path: "/sales", label: "Satış" },
    { path: "/purchases", label: "Mal Alış" },
    { path: "/expenses", label: "Gider Yönetimi" },
    { path: "/temlik", label: "Temlik" },
    
    // Fiş İşlemleri
    { path: "/receipts", label: "Tahsilat Dekontları" },
    { path: "/payments", label: "Ödeme Kayıtları" },
    // { path: "/supplier-payments", label: "Tediye" }, // HIDDEN per user request
    
    // Kasa Banka İşlemleri
    { path: "/cash-transactions", label: "Kasa Hareketleri" },
    { path: "/bank-transactions", label: "Banka Hareketleri" },
    
    // Bayi Paneli
    { path: "/bayiler", label: "Bayiler" },
    { path: "/bayi-orders", label: "Bayi Siparişleri" },
    
    // ERP Sistemi
    { path: "/production", label: "Üretim" },
    { path: "/production-monitoring", label: "Üretim İzleme" },
    { path: "/installations", label: "Montaj" },
    { path: "/target-installations", label: "Hedef Montaj" },
    
    // Yönetici Paneli (sadece admin için görünür olacak)
    { path: "/admin/users", label: "Kullanıcı Yönetimi" },
    { path: "/admin/personnel", label: "Personel Yönetimi" },
    { path: "/admin/activities", label: "Sistem Etkinlikleri" },
    { path: "/admin/data", label: "Veri Yönetimi" },
    { path: "/admin/reports", label: "Raporlar" }
  ];

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Kullanıcı Yönetimi</h1>
          <p className="text-muted-foreground">Sistem kullanıcılarını yönetin ve yetkilendirin</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isCustomerUserDialogOpen} onOpenChange={setIsCustomerUserDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <UserPlus className="h-4 w-4 mr-2" />
                Müşteri Kullanıcı Oluştur
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Müşteri Portal Kullanıcısı Oluştur</DialogTitle>
                <DialogDescription>
                  Müşterileriniz için portal erişimi oluşturun. Müşteriler kendi sipariş ve bakiye bilgilerini görüntüleyebilecek.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCustomerUser} className="space-y-4">
                <div>
                  <Label htmlFor="customer_select">Müşteri Seçin *</Label>
                  <div className="relative">
                    <div className="flex items-center border rounded-md px-3 py-2 gap-2 bg-white focus-within:ring-2 focus-within:ring-blue-500">
                      <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <input
                        type="text"
                        className="flex-1 outline-none text-sm bg-transparent"
                        placeholder="İsim veya cari no ile ara..."
                        value={portalCustomerSearch}
                        onChange={(e) => {
                          setPortalCustomerSearch(e.target.value);
                          setShowPortalDropdown(true);
                          if (!e.target.value) {
                            setCustomerUserForm({...customerUserForm, customer_id: "", username: "", full_name: ""});
                          }
                        }}
                        onFocus={() => setShowPortalDropdown(true)}
                        onBlur={() => setTimeout(() => setShowPortalDropdown(false), 200)}
                        autoComplete="off"
                      />
                      {portalCustomerSearch && (
                        <button type="button" onClick={() => { setPortalCustomerSearch(""); setShowPortalDropdown(false); setCustomerUserForm({...customerUserForm, customer_id: "", username: "", full_name: ""}); }} className="text-gray-400 hover:text-gray-600">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {showPortalDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto">
                        {customers
                          .filter(c => {
                            const q = portalCustomerSearch.toLowerCase();
                            return !q || 
                              (c.name + " " + c.surname).toLowerCase().includes(q) ||
                              String(c.serial_number || "").includes(q);
                          })
                          .slice(0, 50)
                          .map((customer) => (
                            <button
                              key={customer.id}
                              type="button"
                              onMouseDown={() => {
                                handleCustomerSelect(customer);
                                setPortalCustomerSearch(`${customer.serial_number ? "#" + customer.serial_number + " - " : ""}${customer.name} ${customer.surname}`);
                                setShowPortalDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 flex items-center gap-2 ${customerUserForm.customer_id === customer.id ? 'bg-blue-50 font-medium' : ''}`}
                            >
                              {customer.serial_number && (
                                <span className="text-xs font-mono text-blue-600 bg-blue-50 border border-blue-200 rounded px-1">#{customer.serial_number}</span>
                              )}
                              <span>{customer.name} {customer.surname}</span>
                            </button>
                          ))}
                        {customers.filter(c => {
                          const q = portalCustomerSearch.toLowerCase();
                          return !q || (c.name + " " + c.surname).toLowerCase().includes(q) || String(c.serial_number || "").includes(q);
                        }).length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">Müşteri bulunamadı</div>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Bir müşteriye sadece 1 kullanıcı tanımlanabilir</p>
                </div>
                
                <div>
                  <Label htmlFor="customer_username">Kullanıcı Adı *</Label>
                  <Input
                    id="customer_username"
                    value={customerUserForm.username}
                    onChange={(e) => setCustomerUserForm({...customerUserForm, username: e.target.value})}
                    placeholder="musteri_5837"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Otomatik oluşturuldu, değiştirebilirsiniz</p>
                </div>
                
                <div>
                  <Label htmlFor="customer_full_name">Ad Soyad *</Label>
                  <Input
                    id="customer_full_name"
                    value={customerUserForm.full_name}
                    onChange={(e) => setCustomerUserForm({...customerUserForm, full_name: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_password">Şifre * (Minimum 6 karakter)</Label>
                  <Input
                    id="customer_password"
                    type="password"
                    value={customerUserForm.password}
                    onChange={(e) => setCustomerUserForm({...customerUserForm, password: e.target.value})}
                    minLength={6}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Bu şifreyi müşteri ile paylaşacaksınız</p>
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">ℹ️ Müşteri Portal Özellikleri:</h4>
                  <ul className="text-xs text-gray-700 space-y-1">
                    <li>✓ Borç/Alacak bakiyesini görüntüleyebilir</li>
                    <li>✓ Siparişlerinin üretim durumunu takip edebilir</li>
                    <li>✓ Montaj programlarını görüntüleyebilir</li>
                    <li>✓ Cari hesap hareketlerini inceleyebilir</li>
                    <li>✗ Herhangi bir veri düzenleyemez/silemez</li>
                  </ul>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsCustomerUserDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button type="submit">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Müşteri Kullanıcısı Oluştur
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Kullanıcı
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Yeni Kullanıcı Oluştur</DialogTitle>
              <DialogDescription>
                Sistem için yeni bir kullanıcı hesabı oluşturun.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="username">Kullanıcı Adı</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="full_name">Ad Soyad</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Şifre</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="role">Rol</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Rol seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales">Satış Personeli</SelectItem>
                      <SelectItem value="accounting">Muhasebe Personeli</SelectItem>
                      <SelectItem value="production">İmalat Personeli</SelectItem>
                      <SelectItem value="installation">Montaj Personeli</SelectItem>
                      <SelectItem value="bayi">Bayi</SelectItem>
                      <SelectItem value="admin">Yönetici</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Montaj Personeli için Installer seçimi */}
              {formData.role === 'installation' && (
                <div>
                  <Label htmlFor="installer_id">Montaj Elemanı</Label>
                  <Select value={formData.installer_id} onValueChange={(value) => setFormData({...formData, installer_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Montaj elemanı seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {installers.map((installer) => (
                        <SelectItem key={installer.id} value={installer.id}>
                          {installer.name} {installer.phone && `(${installer.phone})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label>Yetkili Ekranlar</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 p-4 border rounded-lg max-h-40 overflow-y-auto">
                  {availableScreens.map((screen) => (
                    <div key={screen.path} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`screen-${screen.path}`}
                        checked={formData.allowed_screens.includes(screen.path)}
                        onChange={() => toggleScreenPermission(screen.path)}
                        className="rounded"
                      />
                      <Label htmlFor={`screen-${screen.path}`} className="text-sm">
                        {screen.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full">
                Kullanıcı Oluştur
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sistem Kullanıcıları</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Kullanıcı</th>
                  <th className="text-left p-3">Rol</th>
                  <th className="text-left p-3">Montaj Elemanı</th>
                  <th className="text-left p-3">Durum</th>
                  <th className="text-left p-3">Son Giriş</th>
                  <th className="text-left p-3">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div>
                        <div className="font-medium">{user.full_name}</div>
                        <div className="text-xs text-gray-500">@{user.username}</div>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={user.role === 'admin' ? "default" : "secondary"}>
                        {getRoleLabel(user.role)}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {user.role === 'installation' && user.installer_id ? (
                        <div className="text-sm text-blue-600">
                          {getInstallerName(user.installer_id)}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">-</div>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant={user.is_active ? "default" : "destructive"}>
                        {user.is_active ? "Aktif" : "Pasif"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">
                        {user.last_login ? 
                          new Date(user.last_login).toLocaleDateString('tr-TR') : 
                          'Hiç giriş yapmamış'
                        }
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEditUser(user)} data-testid={`edit-user-${user.username}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {user.id !== currentUser.id && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center p-8 text-gray-500">
                      Henüz kullanıcı bulunmamaktadır.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Kullanıcı Düzenle</DialogTitle>
            <DialogDescription>
              Kullanıcı bilgilerini ve yetkilerini düzenleyin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_username">Kullanıcı Adı</Label>
                <Input
                  id="edit_username"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_full_name">Ad Soyad</Label>
                <Input
                  id="edit_full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_password">Yeni Şifre (boş bırakılırsa değişmez)</Label>
                <Input
                  id="edit_password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Yeni şifre girin (opsiyonel)"
                />
              </div>
              <div>
                <Label htmlFor="edit_role">Rol</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Rol seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Satış Personeli</SelectItem>
                    <SelectItem value="accounting">Muhasebe Personeli</SelectItem>
                    <SelectItem value="production">İmalat Personeli</SelectItem>
                    <SelectItem value="installation">Montaj Personeli</SelectItem>
                    <SelectItem value="bayi">Bayi</SelectItem>
                    <SelectItem value="admin">Yönetici</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Montaj Personeli için Installer seçimi */}
            {formData.role === 'installation' && (
              <div>
                <Label htmlFor="edit_installer_id">Montaj Elemanı</Label>
                <Select value={formData.installer_id} onValueChange={(value) => setFormData({...formData, installer_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Montaj elemanı seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {installers.map((installer) => (
                      <SelectItem key={installer.id} value={installer.id}>
                        {installer.name} {installer.phone && `(${installer.phone})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Yetkili Ekranlar</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 p-4 border rounded-lg max-h-40 overflow-y-auto">
                {availableScreens.map((screen) => (
                  <div key={screen.path} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`edit-screen-${screen.path}`}
                      checked={formData.allowed_screens.includes(screen.path)}
                      onChange={() => toggleScreenPermission(screen.path)}
                      className="rounded"
                    />
                    <Label htmlFor={`edit-screen-${screen.path}`} className="text-sm">
                      {screen.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            {formData.allowed_screens.includes('/installations') && (
              <InstallationTabPermissions
                tabs={INSTALLATION_TABS}
                value={formData.installation_tab_permissions || {}}
                onChange={(perms) => setFormData({ ...formData, installation_tab_permissions: perms })}
              />
            )}
            {formData.allowed_screens.includes('/production') && (
              <InstallationTabPermissions
                title="Üretim Sayfası Sekme Yetkileri"
                testId="production-tab-permissions"
                tabs={PRODUCTION_TABS}
                value={formData.production_tab_permissions || {}}
                onChange={(perms) => setFormData({ ...formData, production_tab_permissions: perms })}
              />
            )}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kullanıcı Sil</DialogTitle>
            <DialogDescription>
              Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          {userToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p><strong>Ad Soyad:</strong> {userToDelete.full_name}</p>
              <p><strong>Kullanıcı Adı:</strong> {userToDelete.username}</p>
              <p><strong>Rol:</strong> {getRoleLabel(userToDelete.role)}</p>
              {userToDelete.role === 'installation' && userToDelete.installer_id && (
                <p><strong>Montaj Elemanı:</strong> {getInstallerName(userToDelete.installer_id)}</p>
              )}
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Personnel Management Component (Admin Only)
const PersonnelManagement = ({ currentUser, sessionToken }) => {
  const [personnel, setPersonnel] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState(null);
  const [formData, setFormData] = useState({ name: '' });

  // Yetki kontrolü
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="flex items-center justify-center p-8">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Erişim Reddedildi</h2>
              <p className="text-gray-600">Bu sayfaya erişim için yönetici yetkisi gereklidir.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  useEffect(() => {
    fetchPersonnel();
  }, []);

  const fetchPersonnel = async () => {
    try {
      const response = await axios.get(`${API}/sales-personnel`);
      setPersonnel(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Personel verileri yüklenirken hata:', error);
      toast.error('Personel verileri yüklenirken hata oluştu');
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Personel adı giriniz');
      return;
    }

    try {
      if (editingPersonnel) {
        // Güncelleme
        await axios.put(`${API}/sales-personnel/${editingPersonnel.id}`, formData);
        toast.success('Personel başarıyla güncellendi');
      } else {
        // Yeni ekleme
        await axios.post(`${API}/sales-personnel`, formData);
        toast.success('Personel başarıyla eklendi');
      }

      setIsDialogOpen(false);
      setEditingPersonnel(null);
      setFormData({ name: '' });
      fetchPersonnel();
    } catch (error) {
      console.error('Personel kaydedilirken hata:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('kayıt')) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Personel kaydedilirken hata oluştu');
      }
    }
  };

  const handleEdit = (person) => {
    setEditingPersonnel(person);
    setFormData({ name: person.name });
    setIsDialogOpen(true);
  };

  const handleDelete = async (person) => {
    const confirmed = window.confirm(
      `"${person.name}" personelini silmek istediğinizden emin misiniz?\n\n⚠️ DİKKAT: Bu personelin satış kayıtları varsa silme işlemi başarısız olacaktır.`
    );
    
    if (confirmed) {
      try {
        await axios.delete(`${API}/sales-personnel/${person.id}`, {
          params: { session_token: sessionToken }
        });
        toast.success('Personel başarıyla silindi');
        fetchPersonnel();
      } catch (error) {
        console.error('Personel silinirken hata:', error);
        if (error.response?.status === 400 && error.response?.data?.detail?.includes('satış kaydı')) {
          toast.error(error.response.data.detail);
        } else {
          toast.error('Personel silinirken hata oluştu');
        }
      }
    }
  };

  const openCreateDialog = () => {
    setEditingPersonnel(null);
    setFormData({ name: '' });
    setIsDialogOpen(true);
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingPersonnel(null);
      setFormData({ name: '' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Users className="h-8 w-8 text-blue-600" />
                Satış Personeli Yönetimi
              </h1>
              <p className="text-gray-600 mt-2">
                Satış işlemlerini gerçekleştiren personellerin yönetimi
              </p>
            </div>
            <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Personel
            </Button>
          </div>
        </div>

        {/* Personnel List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Personel Listesi ({personnel.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {personnel.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz personel yok</h3>
                <p className="text-gray-600 mb-4">İlk personeli ekleyerek başlayın</p>
                <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Personeli Ekle
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {personnel.map((person) => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{person.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span 
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              person.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {person.status === 'active' ? 'Aktif' : 'Pasif'}
                          </span>
                          <span>•</span>
                          <span>{new Date(person.created_at).toLocaleDateString('tr-TR')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(person)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(person)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Sil
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {editingPersonnel ? 'Personel Düzenle' : 'Yeni Personel Ekle'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Personel Adı *</Label>
                <Input
                  id="name"
                  placeholder="örn: Ali Veli"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
                {editingPersonnel ? 'Güncelle' : 'Ekle'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};



// Google Drive Otomatik Yedekleme Component
const GoogleDriveBackup = ({ currentUser, sessionToken }) => {
  const [backupStatus, setBackupStatus] = useState(null);
  const [lastBackups, setLastBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualBackupRunning, setManualBackupRunning] = useState(false);

  // Yedekleme durumunu yükle
  const fetchBackupStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/backup/dropbox-status?session_token=${sessionToken}`);
      setBackupStatus(response.data);
    } catch (error) {
      console.error('Yedekleme durumu alınamadı:', error);
      toast.error('Yedekleme durumu alınamadı');
    } finally {
      setLoading(false);
    }
  };

  // Son yedeklemeleri yükle
  const fetchLastBackups = async () => {
    try {
      const response = await axios.get(`${API}/backup/last-backups?session_token=${sessionToken}&limit=10`);
      setLastBackups(response.data.backups || []);
    } catch (error) {
      console.error('Son yedeklemeler alınamadı:', error);
    }
  };

  // Manuel yedekleme başlat
  const triggerManualBackup = async () => {
    try {
      setManualBackupRunning(true);
      toast.info('Yedekleme başlatılıyor...');
      
      const response = await axios.post(`${API}/backup/manual-trigger?session_token=${sessionToken}`);
      
      toast.success('Yedekleme başarıyla tamamlandı!');
      
      // Listeyi yenile
      await fetchLastBackups();
    } catch (error) {
      console.error('Manuel yedekleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Yedekleme başlatılamadı');
    } finally {
      setManualBackupRunning(false);
    }
  };

  useEffect(() => {
    fetchBackupStatus();
    fetchLastBackups();
    
    // Her 30 saniyede bir durum güncelle
    const interval = setInterval(() => {
      fetchBackupStatus();
      fetchLastBackups();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    if (kb >= 1) return `${kb.toFixed(2)} KB`;
    return `${bytes} B`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR');
  };

  if (loading && !backupStatus) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="animate-spin h-8 w-8 mx-auto mb-4 text-blue-500" />
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Cloud className="h-8 w-8 text-blue-500" />
          Dropbox Otomatik Yedekleme
        </h1>
        <p className="text-gray-600 mt-2">
          Verileriniz otomatik olarak Dropbox'a yedeklenir
        </p>
      </div>

      {/* Durum Kartı */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Yedekleme Sistemi Durumu</span>
            <Badge variant={backupStatus?.is_active ? "success" : "destructive"}>
              {backupStatus?.is_active ? "Aktif" : "Devre Dışı"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className={`h-5 w-5 ${backupStatus?.is_configured ? 'text-green-500' : 'text-gray-400'}`} />
                <span className="font-medium">Yapılandırma:</span>
                <span className={backupStatus?.is_configured ? 'text-green-600' : 'text-red-600'}>
                  {backupStatus?.is_configured ? 'Tamamlandı' : 'Yapılmadı'}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className={`h-5 w-5 ${backupStatus?.scheduler_running ? 'text-green-500' : 'text-gray-400'}`} />
                <span className="font-medium">Zamanlayıcı:</span>
                <span className={backupStatus?.scheduler_running ? 'text-green-600' : 'text-red-600'}>
                  {backupStatus?.scheduler_running ? 'Çalışıyor' : 'Durduruldu'}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Yedekleme Saatleri:</span>
                <span className="text-sm text-gray-600">
                  {backupStatus?.backup_schedule}
                </span>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Zamanlanmış İşler:</h4>
              {backupStatus?.scheduled_jobs && backupStatus.scheduled_jobs.length > 0 ? (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {backupStatus.scheduled_jobs.filter(job => job.id.startsWith('backup_')).map(job => (
                    <div key={job.id} className="text-sm bg-blue-50 p-2 rounded">
                      <div className="font-medium">{job.name}</div>
                      <div className="text-gray-600 text-xs">
                        Sonraki çalışma: {formatDate(job.next_run)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Zamanlanmış iş bulunamadı</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Yapılandırma Talimatları */}
      {!backupStatus?.is_configured && (
        <Card className="mb-6 border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              Yapılandırma Gerekli
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-700">
              Dropbox otomatik yedekleme sistemini aktifleştirmek için şu adımları izleyin:
            </p>
            
            <div className="space-y-3">
              <div className="bg-white p-3 rounded border border-orange-200">
                <div className="font-semibold text-sm mb-1">1. Dropbox App Console'a Gidin</div>
                <p className="text-xs text-gray-600">
                  <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Dropbox App Console
                  </a> üzerinden yeni bir app oluşturun.
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border border-orange-200">
                <div className="font-semibold text-sm mb-1">2. App Ayarlarını Yapın</div>
                <p className="text-xs text-gray-600">
                  "Scoped access" ve "Full Dropbox" seçeneklerini seçin.
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border border-orange-200">
                <div className="font-semibold text-sm mb-1">3. Permissions Ekleyin</div>
                <p className="text-xs text-gray-600">
                  "Permissions" tab'ında <code className="bg-gray-100 px-1 rounded">files.content.write</code> ve <code className="bg-gray-100 px-1 rounded">files.content.read</code> izinlerini etkinleştirin.
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border border-orange-200">
                <div className="font-semibold text-sm mb-1">4. Access Token Oluşturun</div>
                <p className="text-xs text-gray-600 mb-2">
                  "Settings" tab'ında "Generate access token" butonuna tıklayın ve token'ı kopyalayın.
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border border-orange-200">
                <div className="font-semibold text-sm mb-1">5. Environment Variable Ekleyin</div>
                <p className="text-xs text-gray-600 mb-2">
                  Backend <code className="bg-gray-100 px-1 rounded">.env</code> dosyasına şu değişkeni ekleyin:
                </p>
                <pre className="bg-gray-900 text-gray-100 p-2 rounded text-xs overflow-x-auto">
{`DROPBOX_ACCESS_TOKEN=sl.xxxxxxxxxx`}
                </pre>
              </div>
              
              <div className="bg-white p-3 rounded border border-orange-200">
                <div className="font-semibold text-sm mb-1">6. Backend'i Yeniden Başlatın</div>
                <p className="text-xs text-gray-600">
                  <code className="bg-gray-100 px-1 rounded">sudo supervisorctl restart backend</code>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manuel Yedekleme */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Manuel Yedekleme</CardTitle>
          <CardDescription>
            Zamanlanmış yedeklemelerin dışında manuel yedekleme başlatabilirsiniz
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={triggerManualBackup}
            disabled={!backupStatus?.is_active || manualBackupRunning}
            className="w-full"
          >
            {manualBackupRunning ? (
              <>
                <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                Yedekleme Yapılıyor...
              </>
            ) : (
              <>
                <Cloud className="h-4 w-4 mr-2" />
                Manuel Yedekleme Başlat
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Son Yedeklemeler */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Son Yedeklemeler</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                fetchLastBackups();
                toast.success('Liste yenilendi');
              }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lastBackups.length > 0 ? (
            <div className="space-y-2">
              {lastBackups.map((backup, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{backup.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      <span>{formatDate(backup.modified || backup.created_at)}</span>
                      <span className="mx-2">•</span>
                      <span>{formatFileSize(backup.size)}</span>
                    </div>
                  </div>
                  {backup.link && (
                    <a
                      href={backup.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4"
                    >
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Cloud className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Henüz yedekleme bulunamadı</p>
              {backupStatus?.is_active && (
                <p className="text-sm mt-1">İlk otomatik yedekleme yakında başlayacak</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <BackupHistoryCard api={API} sessionToken={sessionToken} />
    </div>
  );
};

//✅ DataManagement component fully functional
const DataManagement = ({ currentUser, sessionToken }) => {
  // States
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [dataStats, setDataStats] = useState(null);
  
  // Period Management States
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [newPeriodData, setNewPeriodData] = useState({
    year: new Date().getFullYear() + 1,
    name: '',
    start_date: '',
    end_date: ''
  });
  const [carryForwardData, setCarryForwardData] = useState({
    from_period_id: '',
    to_period_id: '',
    include_balances: true,
    include_customers: true,
    include_suppliers: true
  });
  
  // Backup & Export States
  const [exportSettings, setExportSettings] = useState({
    collections: 'all', // SABIT: Her zaman tüm veriler export edilir
    format: 'json',
    dateFrom: '',
    dateTo: '',
    period_id: 'all',
    include_system_data: true,
    include_user_data: true,
    compression: true
  });
  const [exportLoading, setExportLoading] = useState(false);
  
  // Import States
  const [importData, setImportData] = useState('');
  const [importMode, setImportMode] = useState('merge');
  const [importResults, setImportResults] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  // Backup States
  const [backupSettings, setBackupSettings] = useState({
    name: '',
    description: '',
    include_periods: true,
    include_transactions: true,
    include_customers: true,
    include_suppliers: true,
    include_productions: true,
    include_installations: true,
    include_system_settings: true
  });
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  
  // Data Cleansing States
  const [deleteSettings, setDeleteSettings] = useState({
    collections: '',
    dateFrom: '',
    dateTo: '',
    conditions: '',
    period_id: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // System Reset States
  const [resetConfirmCode, setResetConfirmCode] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  // Integrity Check States
  const [integrityResults, setIntegrityResults] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);

  // Telegram States
  const [telegramConfig, setTelegramConfig] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramSetup, setTelegramSetup] = useState({ bot_token: '', chat_id: '' });
  const [telegramTestLoading, setTelegramTestLoading] = useState(false);

  const API = `${BACKEND_URL}/api`;
  
  // Authorization check
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-red-600 mb-4">Erişim Reddedildi</h2>
            <p className="text-red-700">Bu sayfaya erişim için yönetici yetkisi gereklidir.</p>
          </div>
        </div>
      </div>
    );
  }

  // Load data when tab changes
  React.useEffect(() => {
    if (activeTab === 'overview') {
      fetchDataStatistics();
    } else if (activeTab === 'periods') {
      fetchPeriods();
    } else if (activeTab === 'backup') {
      fetchBackups();
    } else if (activeTab === 'telegram') {
      fetchTelegramConfig();
    }
  }, [activeTab]);

  const fetchDataStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/data-management/statistics`, {
        params: { session_token: sessionToken }
      });
      setDataStats(response.data);
    } catch (error) {
      console.error('Data statistics error:', error);
      toast.error('Veri istatistikleri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const periodsResponse = await axios.get(`${API}/periods`, {
        params: { session_token: sessionToken }
      });
      setPeriods(periodsResponse.data);

      const activeResponse = await axios.get(`${API}/periods/active`, {
        params: { session_token: sessionToken }
      });
      setActivePeriod(activeResponse.data);
    } catch (error) {
      console.error('Periods fetch error:', error);
      toast.error('Mali dönemler alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/data-management/backups`, {
        params: { session_token: sessionToken }
      });
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Backups fetch error:', error);
      toast.error('Yedekleme listesi alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const createPeriod = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/periods`, newPeriodData, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success('Mali dönem başarıyla oluşturuldu');
        fetchPeriods();
        setNewPeriodData({
          year: new Date().getFullYear() + 1,
          name: '',
          start_date: '',
          end_date: ''
        });
      }
    } catch (error) {
      console.error('Period creation error:', error);
      toast.error(error.response?.data?.detail || 'Mali dönem oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const activatePeriod = async (periodId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/periods/${periodId}/activate`, {}, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success('Mali dönem başarıyla aktive edildi');
        fetchPeriods();
      }
    } catch (error) {
      console.error('Period activation error:', error);
      toast.error(error.response?.data?.detail || 'Mali dönem aktive edilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const carryForwardPeriod = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/periods/carry-forward`, carryForwardData, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success(`Dönem devri tamamlandı: ${response.data.summary}`);
        fetchPeriods();
      }
    } catch (error) {
      console.error('Carry forward error:', error);
      toast.error(error.response?.data?.detail || 'Dönem devri sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setBackupLoading(true);
      const backupData = {
        ...backupSettings,
        name: backupSettings.name || `Yedek_${new Date().toISOString().split('T')[0]}`,
        description: backupSettings.description || 'Otomatik yedekleme'
      };

      const response = await axios.post(`${API}/data-management/create-backup`, backupData, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success('Yedekleme başarıyla oluşturuldu');
        fetchBackups();
      }
    } catch (error) {
      console.error('Backup creation error:', error);
      toast.error('Yedekleme oluşturulurken hata oluştu');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const params = {
        collections: 'all', // GÜVENLİK: Her zaman tüm veriler export edilir
        format: exportSettings.format,
        session_token: sessionToken,
        include_system_data: exportSettings.include_system_data,
        include_user_data: exportSettings.include_user_data,
        compression: exportSettings.compression
      };
      
      if (exportSettings.dateFrom) params.date_from = exportSettings.dateFrom;
      if (exportSettings.dateTo) params.date_to = exportSettings.dateTo;
      if (exportSettings.period_id && exportSettings.period_id !== 'all') params.period_id = exportSettings.period_id;
      
      const response = await axios.get(`${API}/data-management/export`, { params });
      
      if (exportSettings.format === 'json') {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { 
          type: 'application/json' 
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DoorNeo_Professional_Export_${new Date().getTime()}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else if (exportSettings.format === 'excel') {
        const XLSX = await import('xlsx');
        const workbook = XLSX.utils.book_new();
        
        // Create summary sheet
        const summaryData = [{
          'Export Date': new Date().toLocaleString('tr-TR'),
          'Export Type': 'Tüm Veriler (36 Koleksiyon)',
          'Period': exportSettings.period_id || 'Tüm Dönemler',
          'Records Count': response.data.total_records || 0,
          'System Data': exportSettings.include_system_data ? 'Dahil' : 'Hariç',
          'User Data': exportSettings.include_user_data ? 'Dahil' : 'Hariç'
        }];
        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Export Summary');
        
        // Add data sheets
        if (response.data.workbook_data) {
          response.data.workbook_data.forEach(sheet => {
            if (sheet.data && sheet.data.length > 0) {
              const worksheet = XLSX.utils.json_to_sheet(sheet.data);
              XLSX.utils.book_append_sheet(workbook, worksheet, sheet.sheet_name);
            }
          });
        }
        
        XLSX.writeFile(workbook, `DoorNeo_Professional_Export_${new Date().getTime()}.xlsx`);
      }
      
      toast.success(`Profesyonel veri exportu tamamlandı (${exportSettings.format.toUpperCase()})`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export işleminde hata oluştu');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImport = async (validateOnly = false) => {
    try {
      setImportLoading(true);
      let importPayload;
      let response;

      if (importFile) {
        const formData = new FormData();
        formData.append('file', importFile);
        formData.append('mode', importMode);
        formData.append('session_token', sessionToken || '');
        
        response = await axios.post(`${API}/data-management/import-file`, formData, {
          params: { session_token: sessionToken, validate_only: validateOnly },
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setImportResults(response.data);
      } else if (importData.trim()) {
        const parsedData = JSON.parse(importData);
        
        // Dropbox/Export formatlarını normalize et: her koleksiyon liste olmalı
        let dataToImport;
        if (parsedData.export_data) {
          // Export dosyası - export_data bölümünü çıkar
          dataToImport = parsedData.export_data;
          toast.info('Export dosyası tespit edildi - veri çıkarılıyor...');
        } else {
          dataToImport = parsedData;
        }
        // Dropbox full-backup nested formatı: {collection, count, exported_at, data: [...]}
        for (const key of Object.keys(dataToImport)) {
          const val = dataToImport[key];
          if (val && typeof val === 'object' && !Array.isArray(val) && Array.isArray(val.data)) {
            dataToImport[key] = val.data;
          }
        }
        
        response = await axios.post(`${API}/data-management/import`, dataToImport, {
          params: { 
            mode: importMode,
            session_token: sessionToken,
            validate_only: validateOnly
          }
        });
        setImportResults(response.data);
      } else {
        toast.error('İçe aktarılacak veri seçiniz');
        return;
      }
      
      const summary = response.data?.summary || 
        (validateOnly ? 'Validasyon tamamlandı' : 'Import tamamlandı');
      toast.success(`Veri işlemi tamamlandı: ${summary}`);
      if (activeTab === 'overview') fetchDataStatistics();
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.detail || 'Import işleminde hata oluştu';
      toast.error(errorMsg);
      setImportResults({ error: errorMsg });
    } finally {
      setImportLoading(false);
    }
  };

  const handleSelectiveDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await axios.post(`${API}/data-management/selective-delete`, deleteSettings, {
        params: { session_token: sessionToken }
      });
      
      if (response.data.success) {
        toast.success(`Seçici silme tamamlandı: ${response.data.summary}`);
        if (activeTab === 'overview') fetchDataStatistics();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Silme işleminde hata oluştu');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleIntegrityCheck = async () => {
    try {
      setIntegrityLoading(true);
      const response = await axios.get(`${API}/data-management/integrity-check`, {
        params: { session_token: sessionToken }
      });
      setIntegrityResults(response.data);
      
      if (response.data.issues && response.data.issues.length === 0) {
        toast.success('Veri bütünlüğü kontrolü: Sorun bulunamadı');
      } else {
        toast.warning(`Veri bütünlüğü kontrolü: ${response.data.issues?.length || 0} sorun tespit edildi`);
      }
    } catch (error) {
      console.error('Integrity check error:', error);
      toast.error('Bütünlük kontrolü sırasında hata oluştu');
    } finally {
      setIntegrityLoading(false);
    }
  };

  const handleReset = async () => {
    if (resetConfirmCode !== 'RESET_ALL_DATA_PERMANENTLY') {
      toast.error('Onay kodu hatalı!');
      return;
    }

    const confirmed = window.confirm(
      '🚨 SİSTEM TAMAMEN SIFIRLANACAK!\n\n' +
      'Bu işlem GERİ ALINAMAZ!\n\n' +
      'Tüm veriler silinecek:\n' +
      '• Müşteriler\n' +
      '• Satışlar\n' +
      '• Üretim kayıtları\n' +
      '• Mali kayıtlar\n' +
      '• Sistem ayarları\n\n' +
      'Devam etmek istediğinizden emin misiniz?'
    );

    if (!confirmed) return;

    try {
      setResetLoading(true);
      const response = await axios.post(`${API}/data-management/complete-reset`, {}, {
        params: { 
          confirm_code: resetConfirmCode,
          backup_before_reset: true,
          session_token: sessionToken
        }
      });
      
      if (response.data.reset_completed) {
        const recordsDeleted = response.data.reset_results?.total_records_deleted || 0;
        
        toast.success(`✅ BAŞARILI! Sistem sıfırlandı! ${recordsDeleted} kayıt silindi.`, {
          duration: 5000,
          position: 'top-center'
        });
        
        alert(`✅ BAŞARILI!\n\nSistem tamamen sıfırlandı!\n${recordsDeleted} kayıt silindi.\n\nTüm veriler kalıcı olarak silindi.`);
        
        setResetConfirmCode('');
        
        // Try to refresh stats but don't show error if it fails
        try {
          if (activeTab === 'overview') await fetchDataStatistics();
        } catch (error) {
          console.log('Statistics refresh skipped after reset (expected)');
        }
      }
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Sistem sıfırlama sırasında hata oluştu');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCompleteReset = async () => {
    // Direkt final confirmation'a geç, onay kodu yazdırma zorunluluğu yok
    const finalConfirm = window.confirm(
      '🚨 SİSTEM TAMAMEN SIFIRLANACAK!\n\n' +
      'Bu işlem TÜM verileri kalıcı olarak silecektir:\n' +
      '• Tüm müşteriler\n' +
      '• Tüm satışlar\n' +
      '• Tüm tedarikçiler\n' +
      '• Tüm ödemeler\n' +
      '• Tüm raporlar\n\n' +
      'Bu işlem GERİ ALINAMAZ!\n' +
      'Devam etmek istediğinizden emin misiniz?'
    );
    
    if (!finalConfirm) return;
    
    try {
      setResetLoading(true);
      const response = await axios.post(`${API}/data-management/complete-reset`, {}, {
        params: { 
          confirm_code: 'RESET_ALL_DATA_PERMANENTLY', // Otomatik gönder
          backup_before_reset: true,
          session_token: sessionToken 
        }
      });
      
      console.log('Reset response:', response.data);
      console.log('Total records deleted:', response.data.reset_results?.total_records_deleted);
      
      const recordsDeleted = response.data.reset_results?.total_records_deleted || 0;
      const successMessage = `Sistem sıfırlandı! ${recordsDeleted} kayıt silindi`;
      
      // Show success message FIRST
      toast.success(successMessage, {
        duration: 5000,
        position: 'top-center'
      });
      
      // Also show browser alert as fallback to ensure user sees the success
      alert(`✅ BAŞARILI!\n\n${successMessage}\n\nSistem tamamen sıfırlandı ve tüm veriler silindi.`);
      
      setResetConfirmCode(''); // Reset form
      
      // Try to refresh stats but don't show error if it fails
      try {
        await fetchDataStatistics();
      } catch (error) {
        console.log('Statistics refresh skipped after reset (expected)');
      }
      
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Sistem sıfırlama işleminde hata oluştu');
    } finally {
      setResetLoading(false);
    }
  };

  // ===== TELEGRAM FONKSİYONLARI =====
  const fetchTelegramConfig = async () => {
    try {
      setTelegramLoading(true);
      const response = await axios.get(`${API}/telegram/config`, {
        params: { session_token: sessionToken }
      });
      setTelegramConfig(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Telegram config error:', error);
      }
      setTelegramConfig(null);
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramSetup = async () => {
    if (!telegramSetup.bot_token || !telegramSetup.chat_id) {
      toast.error('Bot Token ve Chat ID gerekli');
      return;
    }
    try {
      setTelegramLoading(true);
      const response = await axios.post(`${API}/telegram/setup`, telegramSetup, {
        params: { session_token: sessionToken }
      });
      toast.success(`Telegram bot kuruldu: @${response.data.bot_username}`);
      setTelegramSetup({ bot_token: '', chat_id: '' });
      fetchTelegramConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Telegram bot kurulumu başarısız');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramConfigUpdate = async (updates) => {
    try {
      await axios.post(`${API}/telegram/config`, updates, {
        params: { session_token: sessionToken }
      });
      setTelegramConfig(prev => ({ ...prev, ...updates }));
      toast.success('Telegram ayarları güncellendi');
    } catch (error) {
      toast.error('Ayar güncellenirken hata oluştu');
    }
  };

  const handleTelegramTest = async () => {
    try {
      setTelegramTestLoading(true);
      await axios.post(`${API}/telegram/test`, {}, {
        params: { session_token: sessionToken }
      });
      toast.success('Test bildirimi gönderildi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test bildirimi gönderilemedi');
    } finally {
      setTelegramTestLoading(false);
    }
  };

  const handleSendReport = async (type) => {
    try {
      setTelegramTestLoading(true);
      await axios.post(`${API}/telegram/send-${type}-report`, {}, {
        params: { session_token: sessionToken }
      });
      toast.success('Rapor gönderildi');
    } catch (error) {
      toast.error('Rapor gönderilemedi');
    } finally {
      setTelegramTestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Professional Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Profesyonel Veri Yönetimi Sistemi</h1>
            <p className="text-blue-100">Mali dönem yönetimi, kapsamlı yedekleme ve veri operasyonları</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => activeTab === 'overview' ? fetchDataStatistics() : activeTab === 'periods' ? fetchPeriods() : fetchBackups()} 
              variant="outline" 
              size="sm"
              className="text-white border-white hover:bg-white hover:text-blue-600"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Yenile
            </Button>
            {activePeriod && (
              <div className="bg-white/20 rounded-lg px-3 py-2">
                <div className="text-xs text-blue-100">Aktif Dönem</div>
                <div className="font-semibold">{activePeriod.name}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Tabs */}
      <Card>
        <CardHeader className="pb-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-8 h-12">
              <TabsTrigger value="overview" className="flex flex-col gap-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs">Genel Bakış</span>
              </TabsTrigger>
              <TabsTrigger value="periods" className="flex flex-col gap-1">
                <Calendar className="h-4 w-4" />
                <span className="text-xs">Mali Dönem</span>
              </TabsTrigger>
              <TabsTrigger value="export" className="flex flex-col gap-1">
                <Download className="h-4 w-4" />
                <span className="text-xs">Export</span>
              </TabsTrigger>
              <TabsTrigger value="import" className="flex flex-col gap-1">
                <Upload className="h-4 w-4" />
                <span className="text-xs">Import</span>
              </TabsTrigger>
              <TabsTrigger value="delete" className="flex flex-col gap-1">
                <Trash2 className="h-4 w-4" />
                <span className="text-xs">Temizleme</span>
              </TabsTrigger>
              <TabsTrigger value="integrity" className="flex flex-col gap-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-xs">Bütünlük</span>
              </TabsTrigger>
              <TabsTrigger value="telegram" className="flex flex-col gap-1" data-testid="telegram-tab">
                <Bell className="h-4 w-4" />
                <span className="text-xs">Telegram</span>
              </TabsTrigger>
              <TabsTrigger value="reset" className="flex flex-col gap-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs">Sıfırlama</span>
              </TabsTrigger>
            </TabsList>
            
            {/* Enhanced Overview Tab */}
            <TabsContent value="overview" className="mt-6">
              {dataStats && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-2xl font-bold text-blue-600">
                              {dataStats.summary.total_collections || 0}
                            </div>
                            <div className="text-sm text-gray-600">Veri Koleksiyonu</div>
                          </div>
                          <Database className="h-8 w-8 text-blue-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-2xl font-bold text-green-600">
                              {(dataStats.summary.total_records || 0).toLocaleString('tr-TR')}
                            </div>
                            <div className="text-sm text-gray-600">Toplam Kayıt</div>
                          </div>
                          <FileText className="h-8 w-8 text-green-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-2xl font-bold text-purple-600">
                              {dataStats.summary.estimated_size_mb || 0} MB
                            </div>
                            <div className="text-sm text-gray-600">Veri Boyutu</div>
                          </div>
                          <HardDrive className="h-8 w-8 text-purple-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-orange-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-2xl font-bold text-orange-600">
                              {(dataStats.summary.net_receivables || 0).toLocaleString('tr-TR')} ₺
                            </div>
                            <div className="text-sm text-gray-600">Net Alacak</div>
                          </div>
                          <TrendingUp className="h-8 w-8 text-orange-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Data Collections Grid */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5" />
                        Veri Koleksiyonları Detayı
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dataStats.collections && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {Object.entries(dataStats.collections).map(([key, value]) => {
                            // Collection name mapping for Turkish display
                            const collectionNames = {
                              'customers': 'Müşteriler',
                              'suppliers': 'Tedarikçiler', 
                              'transactions': 'Satış İşlemleri',
                              'collection_receipts': 'Tahsilat Dekontları',
                              'payment_receipts': 'Ödeme Dekontları',
                              'supplier_payments': 'Tedarikçi Tediye İşlemleri',
                              'bayiler': 'Bayiler',
                              'bayi_orders': 'Bayi Siparişleri',
                              'productions': 'Üretim Kayıtları',
                              'installation_schedules': 'Montaj Programları',
                              'offers': 'Teklifler',
                              'activities': 'Sistem Etkinlikleri',
                              'sales_personnel': 'Satış Personeli',
                              'bank_transactions': 'Banka İşlemleri',
                              'cash_flows': 'Kasa Hareketleri',
                              'expenses': 'Gider Kayıtları',
                              'sessions': 'Oturum Kayıtları'
                            };
                            
                            const displayName = collectionNames[key] || key.charAt(0).toUpperCase() + key.slice(1);
                            
                            return (
                              <div key={key} className="bg-gray-50 rounded-lg p-4 border hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">{displayName}</span>
                                  <Badge variant="secondary" className="text-sm">
                                    {typeof value === 'object' && value.count !== undefined ? 
                                      value.count.toLocaleString('tr-TR') : 
                                      typeof value === 'number' ? value.toLocaleString('tr-TR') : value} kayıt
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Mali Dönem Tab */}
            <TabsContent value="periods" className="mt-6">
              <div className="space-y-6">
                {/* Active Period */}
                <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-green-600" />
                      Aktif Mali Dönem
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {activePeriod ? (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-sm text-gray-600">Dönem</div>
                          <div className="font-bold text-green-700">{activePeriod.name}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-sm text-gray-600">Yıl</div>
                          <div className="font-bold text-blue-700">{activePeriod.year}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-sm text-gray-600">Başlangıç</div>
                          <div className="font-bold">{new Date(activePeriod.start_date).toLocaleDateString('tr-TR')}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-sm text-gray-600">Bitiş</div>
                          <div className="font-bold">{new Date(activePeriod.end_date).toLocaleDateString('tr-TR')}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <AlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-orange-700 mb-2">Aktif Mali Dönem Bulunamadı</h3>
                        <p className="text-gray-600">Mali işlemler için bir dönem oluşturun ve aktive edin</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Create Period */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="h-5 w-5" />
                      Yeni Mali Dönem
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Yıl</Label>
                        <Input 
                          type="number" 
                          value={newPeriodData.year}
                          onChange={(e) => {
                            const year = parseInt(e.target.value);
                            setNewPeriodData(prev => ({
                              ...prev,
                              year: year,
                              name: `${year} Mali Dönemi`,
                              start_date: `${year}-01-01`,
                              end_date: `${year}-12-31`
                            }));
                          }}
                        />
                      </div>
                      <div>
                        <Label>Dönem Adı</Label>
                        <Input 
                          value={newPeriodData.name}
                          onChange={(e) => setNewPeriodData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="2025 Mali Dönemi"
                        />
                      </div>
                      <div>
                        <Label>Başlangıç</Label>
                        <Input 
                          type="date" 
                          value={newPeriodData.start_date}
                          onChange={(e) => setNewPeriodData(prev => ({ ...prev, start_date: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Bitiş</Label>
                        <Input 
                          type="date" 
                          value={newPeriodData.end_date}
                          onChange={(e) => setNewPeriodData(prev => ({ ...prev, end_date: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button 
                        onClick={createPeriod} 
                        disabled={loading || !newPeriodData.name}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Mali Dönem Oluştur
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Periods List */}
                {periods.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Mali Dönemler ({periods.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {periods.map((period) => (
                          <div key={period.id} className={`border rounded p-3 ${period.is_active ? 'bg-green-50 border-green-300' : ''}`}>
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="font-semibold">{period.name}</span>
                                {period.is_active && <Badge className="ml-2 bg-green-100 text-green-800">Aktif</Badge>}
                              </div>
                              {!period.is_active && (
                                <Button size="sm" onClick={() => activatePeriod(period.id)}>
                                  Aktive Et
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Carry Forward */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowRight className="h-5 w-5" />
                      Dönem Devri
                    </CardTitle>
                    <CardDescription>
                      Bir dönemden diğerine bakiyeleri ve müşteri/tedarikçi bilgilerini devredin
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Kaynak Dönem</Label>
                        <Select value={carryForwardData.from_period_id} onValueChange={(value) => setCarryForwardData(prev => ({ ...prev, from_period_id: value }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Kaynak dönem seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {periods.map((period) => (
                              <SelectItem key={period.id} value={period.id}>
                                {period.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Hedef Dönem</Label>
                        <Select value={carryForwardData.to_period_id} onValueChange={(value) => setCarryForwardData(prev => ({ ...prev, to_period_id: value }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Hedef dönem seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {periods.map((period) => (
                              <SelectItem key={period.id} value={period.id}>
                                {period.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          id="include_balances"
                          checked={carryForwardData.include_balances}
                          onChange={(e) => setCarryForwardData(prev => ({ ...prev, include_balances: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="include_balances">Bakiyeleri dahil et</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          id="include_customers"
                          checked={carryForwardData.include_customers}
                          onChange={(e) => setCarryForwardData(prev => ({ ...prev, include_customers: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="include_customers">Müşteri bilgilerini dahil et</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          id="include_suppliers"
                          checked={carryForwardData.include_suppliers}
                          onChange={(e) => setCarryForwardData(prev => ({ ...prev, include_suppliers: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="include_suppliers">Tedarikçi bilgilerini dahil et</Label>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button 
                        onClick={carryForwardPeriod} 
                        disabled={loading || !carryForwardData.from_period_id || !carryForwardData.to_period_id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <ArrowRight className="h-4 w-4 mr-2" />
                        Dönem Devrini Başlat
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Gelişmiş Export Tab */}
            <TabsContent value="export" className="mt-6">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="h-5 w-5" />
                      Profesyonel Veri Export Sistemi
                    </CardTitle>
                    <CardDescription>Kapsamlı veri export işlemleri ve yedekleme</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        {/* Export Kapsamı - SABİT: Her zaman tüm veriler */}
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <Database className="h-6 w-6 text-blue-600" />
                            <div>
                              <div className="font-semibold text-blue-900">Export Kapsamı: Tüm Veriler</div>
                              <div className="text-sm text-blue-700">Tüm koleksiyonlar dahil - 36 veri tablosu</div>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-base font-medium">Export Formatı</Label>
                          <Select value={exportSettings.format} onValueChange={(value) => setExportSettings(prev => ({ ...prev, format: value }))}>
                            <SelectTrigger className="h-12">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="json">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  JSON (Kapsamlı - Tüm Detaylar)
                                </div>
                              </SelectItem>
                              <SelectItem value="excel">
                                <div className="flex items-center gap-2">
                                  <BarChart3 className="h-4 w-4" />
                                  Excel (Analiz & Raporlama)
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-base font-medium">Mali Dönem Filtresi</Label>
                          <Select value={exportSettings.period_id} onValueChange={(value) => setExportSettings(prev => ({ ...prev, period_id: value }))}>
                            <SelectTrigger className="h-12">
                              <SelectValue placeholder="Dönem seçin (hepsi için boş bırakın)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  Tüm Mali Dönemler
                                </div>
                              </SelectItem>
                              {periods.map((period) => (
                                <SelectItem key={period.id} value={period.id}>
                                  {period.name} ({period.year})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <Label className="text-base font-medium">Tarih Aralığı (Opsiyonel)</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-sm">Başlangıç</Label>
                              <Input 
                                type="date" 
                                value={exportSettings.dateFrom}
                                onChange={(e) => setExportSettings(prev => ({ ...prev, dateFrom: e.target.value }))}
                                className="h-10"
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Bitiş</Label>
                              <Input 
                                type="date" 
                                value={exportSettings.dateTo}
                                onChange={(e) => setExportSettings(prev => ({ ...prev, dateTo: e.target.value }))}
                                className="h-10"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Shield className="h-5 w-5 text-blue-600" />
                            <span className="font-semibold text-blue-900">Export İçeriği</span>
                          </div>
                          <div className="text-sm text-blue-800 space-y-1">
                            <div>✅ Tüm müşteri ve tedarikçi bilgileri</div>
                            <div>✅ Satış, mal alış ve ödeme kayıtları</div>
                            <div>✅ Üretim ve montaj takip verileri</div>
                            <div>✅ Personel ve maaş ödeme kayıtları</div>
                            <div>✅ Stok yönetimi ve hareket kayıtları</div>
                            <div>✅ Banka, kasa ve nakit akış bilgileri</div>
                            <div>✅ Kasa Defteri ve Banka Defteri kayıtları</div>
                            <div>✅ Mali dönem ve bakiye bilgileri</div>
                            <div>✅ Ölçü tabloları ve sipariş detayları</div>
                            <div className="font-semibold text-blue-900 mt-2">📦 Toplam 36 Koleksiyon</div>
                          </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="text-sm text-green-800 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span><strong>%100 Güvenli Export</strong> - Tüm verileriniz eksiksiz yedeklenir</span>
                          </div>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="text-sm text-yellow-800 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            <span><strong>Önemli:</strong> Export işlemi tüm koleksiyonları içerir. Kısmi yedekleme yapılamaz.</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex gap-3">
                      <Button 
                        onClick={handleExport} 
                        disabled={exportLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white flex-1 md:flex-none"
                        size="lg"
                      >
                        {exportLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white mr-2" />
                            Export Hazırlanıyor...
                          </>
                        ) : (
                          <>
                            <Download className="h-5 w-5 mr-2" />
                            Kapsamlı Export Başlat
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setExportSettings({
                          collections: 'all', // GÜVENLİK: Her zaman tüm veriler
                          format: 'json',
                          dateFrom: '',
                          dateTo: '',
                          period_id: 'all'
                        })}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Sıfırla
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Import Tab */}
            <TabsContent value="import" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Veri Import İşlemleri</CardTitle>
                  <CardDescription>JSON formatında veri yükleyin</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Import Güvenlik Uyarısı */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold text-blue-900 mb-2">Import Güvenlik Bilgilendirmesi</div>
                        <div className="text-sm text-blue-800 space-y-1">
                          <div>✅ Sadece bu sistemden export edilmiş JSON dosyalarını import edin</div>
                          <div>✅ Import işlemi TÜM 36 koleksiyonu otomatik olarak işler</div>
                          <div>✅ Personel, maaş, stok, müşteri - tüm veriler dahildir</div>
                          <div className="mt-2 pt-2 border-t border-blue-300">
                            <strong>Import Modları:</strong>
                          </div>
                          <div>🔗 <strong>Merge (Birleştir) - ÖNERİLEN:</strong> Aynı ID'li kayıtları günceller, yenileri ekler (Duplicate önlenir)</div>
                          <div>⚠️ <strong>Append (Ekle):</strong> Verileri direkt ekler (Duplicate oluşabilir!)</div>
                          <div>⚠️ <strong>Replace (Değiştir):</strong> Mevcut verileri tamamen siler, yeni verileri yükler</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label>Import Modu</Label>
                        <Select value={importMode} onValueChange={setImportMode}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="merge">🔗 Birleştir (Merge) - ÖNERİLEN</SelectItem>
                            <SelectItem value="append">➕ Ekle (Append) - Dikkat!</SelectItem>
                            <SelectItem value="replace">🔄 Değiştir (Replace) - Dikkat!</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>JSON Verisi</Label>
                        <div className="space-y-3">
                          {/* File Upload Area */}
                          <div 
                            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const files = Array.from(e.dataTransfer.files);
                              const jsonFile = files.find(file => file.name.endsWith('.json'));
                              if (jsonFile) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  try {
                                    const content = event.target.result;
                                    JSON.parse(content); // Validate JSON
                                    setImportData(content);
                                    toast.success(`JSON dosyası yüklendi (${(jsonFile.size / 1024).toFixed(1)} KB)`);
                                  } catch {
                                    toast.error('Geçersiz JSON dosyası');
                                  }
                                };
                                reader.readAsText(jsonFile);
                              } else {
                                toast.error('Lütfen JSON dosyası sürükleyin');
                              }
                            }}
                          >
                            <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                            <div className="text-sm text-gray-600 mb-2">
                              JSON dosyasını <strong>sürükleyip bırakın</strong> veya seçin
                            </div>
                            <input
                              type="file"
                              accept=".json"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                const allowedTypes = ['.json', '.xlsx', '.csv'];
                                const fileExtension = file.name.toLowerCase().slice(-5);
                                const isValidType = allowedTypes.some(type => file.name.toLowerCase().endsWith(type));
                                
                                if (!isValidType) {
                                  toast.error('Desteklenen formatlar: JSON, Excel (.xlsx), CSV');
                                  return;
                                }
                                if (file.size > 100 * 1024 * 1024) {
                                  toast.error('Dosya boyutu 100MB\'dan küçük olmalı');
                                  return;
                                }
                                const reader = new FileReader();
                                reader.onload = async (event) => {
                                  try {
                                    const content = event.target.result;
                                    
                                    if (file.name.endsWith('.json')) {
                                      JSON.parse(content); // Validate JSON
                                      setImportData(content);
                                      toast.success(`JSON dosyası yüklendi (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
                                    } else if (file.name.endsWith('.xlsx')) {
                                      // Excel dosyası yüklendi - backend'de işlenecek
                                      setImportFile(file);
                                      toast.success(`Excel dosyası yüklendi (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
                                    } else if (file.name.endsWith('.csv')) {
                                      // CSV dosyası yüklendi - backend'de işlenecek
                                      setImportFile(file);
                                      toast.success(`CSV dosyası yüklendi (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
                                    }
                                  } catch {
                                    toast.error('Geçersiz dosya formatı');
                                  }
                                };
                                reader.readAsText(file);
                                e.target.value = ''; // Reset input
                              }}
                              className="hidden"
                              id="fileUpload"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById('fileUpload').click()}
                              type="button"
                            >
                              📁 Dosya Seç
                            </Button>
                            <div className="text-xs text-gray-500 mt-2">
                              Desteklenen: .json, .xlsx, .csv • Max: 100MB
                            </div>
                          </div>
                          
                          {/* Manual JSON Input */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm text-gray-600">Manuel JSON Girişi:</span>
                              {importData && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setImportData('')}
                                  type="button"
                                >
                                  🗑️ Temizle
                                </Button>
                              )}
                            </div>
                            <Textarea
                              placeholder="JSON verilerini buraya yapıştırın veya yukarıdan dosya yükleyin..."
                              value={importData}
                              onChange={(e) => setImportData(e.target.value)}
                              className="h-32 font-mono text-sm"
                            />
                          </div>
                          
                          {/* Import Info */}
                          {importData && (
                            <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                              📊 JSON boyutu: {(importData.length / 1024).toFixed(1)} KB
                              {importData.trim().startsWith('{') && ' • ✅ Geçerli JSON formatı'}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleImport(true)}
                          disabled={!importData || importLoading}
                          variant="outline"
                          className="flex-1"
                        >
                          🔍 Validasyon
                        </Button>
                        
                        <Button
                          onClick={() => handleImport(false)}
                          disabled={!importData || importLoading}
                          className="flex-1"
                        >
                          📥 Import Et
                        </Button>
                      </div>
                    </div>

                    <div>
                      {importResults && (
                        <div className="space-y-4">
                          <h4 className="font-semibold">Import Sonuçları</h4>
                          <div className="space-y-2">
                            {Object.entries(importResults.validation_results).map(([collection, result]) => (
                              <div key={collection} className={`p-3 rounded border ${
                                result.status === 'success' ? 'bg-green-50 border-green-200' :
                                result.status === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                                'bg-red-50 border-red-200'
                              }`}>
                                <div className="font-medium text-sm">
                                  {collection}: {result.valid_records}/{result.total_records} geçerli
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Delete Tab */}
            <TabsContent value="delete" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Seçici Veri Silme
                  </CardTitle>
                  <CardDescription className="text-red-600">
                    ⚠️ Bu işlem seçili verileri kalıcı olarak siler. Geri alınamaz!
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Koleksiyonlar</Label>
                      <Input
                        placeholder="customers,transactions veya boş bırakın"
                        value={deleteSettings.collections}
                        onChange={(e) => setDeleteSettings(prev => ({ ...prev, collections: e.target.value }))}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Başlangıç Tarihi</Label>
                        <Input
                          type="date"
                          value={deleteSettings.dateFrom}
                          onChange={(e) => setDeleteSettings(prev => ({ ...prev, dateFrom: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Bitiş Tarihi</Label>
                        <Input
                          type="date"
                          value={deleteSettings.dateTo}
                          onChange={(e) => setDeleteSettings(prev => ({ ...prev, dateTo: e.target.value }))}
                        />
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleSelectiveDelete}
                      disabled={deleteLoading}
                      variant="destructive"
                      className="w-full"
                    >
                      {deleteLoading ? 'Siliniyor...' : '🗑️ Seçici Silme Yap'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Integrity Tab */}
            <TabsContent value="integrity" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>🔍 Veri Bütünlüğü Kontrolü</CardTitle>
                  <CardDescription>Sistem verilerinin tutarlılığını kontrol edin</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex justify-center">
                    <Button 
                      onClick={handleIntegrityCheck}
                      disabled={integrityLoading}
                      size="lg"
                      variant="outline"
                    >
                      {integrityLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          Kontrol Ediliyor...
                        </>
                      ) : (
                        <>🔍 Bütünlük Kontrolü Başlat</>
                      )}
                    </Button>
                  </div>

                  {integrityResults && (
                    <div className="space-y-4">
                      <div className={`p-4 rounded-lg border ${
                        integrityResults.integrity_status === 'healthy' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">
                            {integrityResults.integrity_status === 'healthy' ? '✅' : '⚠️'}
                          </span>
                          <h3 className="font-semibold">
                            {integrityResults.integrity_status === 'healthy' 
                              ? 'Veri Bütünlüğü Sağlam' 
                              : `${integrityResults.total_issues} Sorun Tespit Edildi`
                            }
                          </h3>
                        </div>
                        <p className="text-sm">{integrityResults.recommendation}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Nihai Müşteri Senkronizasyonu */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🔄 Nihai Müşteri Senkronizasyonu
                  </CardTitle>
                  <CardDescription>
                    Üretim kayıtlarındaki nihai müşteri bilgilerini satış işlemlerinden güncelle
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      Bu işlem, üretim planlamasındaki siparişlerin "Nihai Tüketici" bilgilerini 
                      ilgili satış işlemlerinden alarak günceller. Sadece boş olan alanlar güncellenir.
                    </p>
                  </div>

                  <Button 
                    onClick={async () => {
                      if (!window.confirm('Üretim kayıtlarını güncellemek istediğinize emin misiniz?')) return;
                      
                      setIntegrityLoading(true);
                      try {
                        const response = await axios.post(
                          `${API}/data-management/sync-production-end-customers?session_token=${sessionToken}`
                        );
                        
                        const result = response.data;
                        toast.success(
                          `✅ ${result.updated_count} kayıt güncellendi!\n` +
                          `• Zaten güncel: ${result.already_has_end_customer}\n` +
                          `• İşlem eksik: ${result.no_transaction}\n` +
                          `• Nihai müşteri yok: ${result.no_end_customer_in_transaction}`
                        );
                      } catch (error) {
                        console.error('Senkronizasyon hatası:', error);
                        toast.error('Senkronizasyon sırasında hata oluştu');
                      } finally {
                        setIntegrityLoading(false);
                      }
                    }}
                    disabled={integrityLoading}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {integrityLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Senkronize Ediliyor...
                      </>
                    ) : (
                      <>🔄 Nihai Müşteri Bilgilerini Güncelle</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Telegram Tab */}
            <TabsContent value="telegram" className="mt-6" data-testid="telegram-panel">
              <div className="space-y-6">
                {/* Kurulum / Durum */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5 text-blue-500" />
                      Telegram Bot Bildirimleri
                    </CardTitle>
                    <CardDescription>
                      Anlık işlem bildirimleri, günlük/haftalık/aylık raporlar ve vadesi geçen borç uyarıları
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {telegramLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : !telegramConfig || !telegramConfig.enabled ? (
                      /* Kurulum Formu */
                      <div className="space-y-4" data-testid="telegram-setup-form">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <h3 className="font-semibold text-blue-800 mb-2">Telegram Bot Kurulumu</h3>
                          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                            <li>Telegram'da <b>@BotFather</b>'a gidin ve <code>/newbot</code> komutu ile bot oluşturun</li>
                            <li>Size verilen <b>Bot Token</b>'ı aşağıya yapıştırın</li>
                            <li>Bota bir mesaj gönderin, ardından Chat ID'nizi bulun</li>
                          </ol>
                        </div>
                        <div>
                          <Label>Bot Token</Label>
                          <Input
                            type="text"
                            placeholder="123456789:ABCdefGHIjklMNOpqrs..."
                            value={telegramSetup.bot_token}
                            onChange={(e) => setTelegramSetup(prev => ({ ...prev, bot_token: e.target.value }))}
                            data-testid="telegram-bot-token-input"
                          />
                        </div>
                        <div>
                          <Label>Chat ID</Label>
                          <Input
                            type="text"
                            placeholder="1234567890"
                            value={telegramSetup.chat_id}
                            onChange={(e) => setTelegramSetup(prev => ({ ...prev, chat_id: e.target.value }))}
                            data-testid="telegram-chat-id-input"
                          />
                        </div>
                        <Button onClick={handleTelegramSetup} disabled={telegramLoading} data-testid="telegram-setup-btn" className="w-full">
                          {telegramLoading ? 'Kuruluyor...' : 'Telegram Bot Kur ve Aktifleştir'}
                        </Button>
                      </div>
                    ) : (
                      /* Ayarlar Paneli */
                      <div className="space-y-6" data-testid="telegram-settings-panel">
                        {/* Durum */}
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <div>
                              <span className="font-semibold text-green-800">Bot Aktif</span>
                              <p className="text-xs text-green-600">Token: {telegramConfig.bot_token_masked}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={handleTelegramTest} disabled={telegramTestLoading} data-testid="telegram-test-btn">
                              {telegramTestLoading ? 'Gönderiliyor...' : 'Test Gönder'}
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleTelegramConfigUpdate({ enabled: false })} data-testid="telegram-disable-btn">
                              Devre Dışı Bırak
                            </Button>
                          </div>
                        </div>

                        {/* Anlık Bildirim Ayarları */}
                        <div>
                          <h3 className="font-semibold mb-3 text-sm">Anlık Bildirimler</h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                              { key: 'notify_transactions', label: 'Satış / İşlem', icon: '📦' },
                              { key: 'notify_collections', label: 'Tahsilat', icon: '🟢' },
                              { key: 'notify_payments', label: 'Ödeme', icon: '🔴' },
                              { key: 'notify_expenses', label: 'Gider', icon: '🟠' },
                              { key: 'notify_temlik', label: 'Temlik', icon: '🔄' },
                              { key: 'notify_other_sales', label: 'Diğer Satış', icon: '🛒' },
                            ].map(item => (
                              <label key={item.key} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid={`telegram-toggle-${item.key}`}>
                                <input
                                  type="checkbox"
                                  checked={telegramConfig[item.key] || false}
                                  onChange={(e) => handleTelegramConfigUpdate({ [item.key]: e.target.checked })}
                                  className="rounded"
                                />
                                <span className="text-sm">{item.icon} {item.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Rapor Ayarları */}
                        <div>
                          <h3 className="font-semibold mb-3 text-sm">Zamanlanmış Raporlar</h3>
                          <div className="space-y-3">
                            {[
                              { key: 'daily_report', label: 'Günlük Rapor', desc: 'Her gün saat 23:00', type: 'daily' },
                              { key: 'weekly_report', label: 'Haftalık Rapor', desc: 'Her pazar saat 23:00', type: 'weekly' },
                              { key: 'monthly_report', label: 'Aylık Rapor', desc: 'Her ayın son günü saat 23:00', type: 'monthly' },
                              { key: 'overdue_report', label: 'Vadesi Geçen Borçlar', desc: 'Her gün saat 07:00', type: 'overdue' },
                            ].map(item => (
                              <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`telegram-report-${item.key}`}>
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={telegramConfig[item.key] || false}
                                    onChange={(e) => handleTelegramConfigUpdate({ [item.key]: e.target.checked })}
                                    className="rounded"
                                  />
                                  <div>
                                    <span className="font-medium text-sm">{item.label}</span>
                                    <p className="text-xs text-gray-500">{item.desc}</p>
                                  </div>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => handleSendReport(item.type)} disabled={telegramTestLoading} data-testid={`telegram-send-${item.type}-btn`}>
                                  Şimdi Gönder
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Yedekleme Bildirimi */}
                        <div>
                          <h3 className="font-semibold mb-3 text-sm">Diğer Bildirimler</h3>
                          <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="telegram-toggle-backup_alerts">
                            <input
                              type="checkbox"
                              checked={telegramConfig.backup_alerts || false}
                              onChange={(e) => handleTelegramConfigUpdate({ backup_alerts: e.target.checked })}
                              className="rounded"
                            />
                            <div>
                              <span className="font-medium text-sm">Dropbox Yedekleme Hata Bildirimi</span>
                              <p className="text-xs text-gray-500">Yedeklemede hata olursa bildirim gönderir</p>
                            </div>
                          </label>
                        </div>

                        {/* Vade Gün Sayısı */}
                        <div>
                          <h3 className="font-semibold mb-3 text-sm">Vadesi Geçen Borç Eşik Günü</h3>
                          <div className="flex items-center gap-3">
                            <Input
                              type="number"
                              min="1"
                              max="365"
                              value={telegramConfig.overdue_days || 20}
                              onChange={(e) => handleTelegramConfigUpdate({ overdue_days: parseInt(e.target.value) || 20 })}
                              className="w-24"
                              data-testid="telegram-overdue-days-input"
                            />
                            <span className="text-sm text-gray-600">gün (satış tarihinden itibaren)</span>
                          </div>
                        </div>

                        {/* Bot Değiştirme */}
                        <div className="pt-4 border-t">
                          <details className="text-sm">
                            <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Bot ayarlarını değiştir</summary>
                            <div className="mt-3 space-y-3">
                              <div>
                                <Label>Yeni Bot Token</Label>
                                <Input
                                  type="text"
                                  placeholder="Yeni bot token..."
                                  value={telegramSetup.bot_token}
                                  onChange={(e) => setTelegramSetup(prev => ({ ...prev, bot_token: e.target.value }))}
                                />
                              </div>
                              <div>
                                <Label>Yeni Chat ID</Label>
                                <Input
                                  type="text"
                                  placeholder="Yeni chat ID..."
                                  value={telegramSetup.chat_id}
                                  onChange={(e) => setTelegramSetup(prev => ({ ...prev, chat_id: e.target.value }))}
                                />
                              </div>
                              <Button onClick={handleTelegramSetup} disabled={telegramLoading} size="sm">
                                Bot Ayarlarını Güncelle
                              </Button>
                            </div>
                          </details>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Reset Tab */}
            <TabsContent value="reset" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    ⚠️ Sistem Tamamen Sıfırlama
                  </CardTitle>
                  <CardDescription className="text-red-600 font-semibold">
                    🚨 TEHLİKELİ BÖLGE: Bu işlem tüm sistem verilerini kalıcı olarak siler!
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-6 bg-red-50 border-2 border-red-300 rounded-lg">
                    <div className="space-y-4">
                      <div className="text-red-800">
                        <h3 className="font-bold text-lg mb-3">⚠️ ÇOK TEHLİKELİ İŞLEM!</h3>
                        <p className="mb-4">Bu işlem aşağıdaki TÜM verileri kalıcı olarak silecektir:</p>
                        <ul className="space-y-1 text-sm ml-4">
                          <li>• 👥 Tüm müşteri kayıtları</li>
                          <li>• 💰 Tüm satış işlemleri</li>
                          <li>• 🏭 Tüm tedarikçi kayıtları</li>
                          <li>• 🏪 Tüm bayi kayıtları</li>
                          <li>• 📋 Tüm teklif kayıtları</li>
                        </ul>
                        <div className="mt-4 p-3 bg-red-200 rounded font-bold text-center">
                          Bu işlem GERİ ALINAMAZ!
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <div className="text-sm font-semibold text-red-800">
                            Bu işlemi yapmadan önce:
                          </div>
                          <ul className="text-sm text-red-700 space-y-1">
                            <li>• Manuel yedek aldığınızdan emin olun</li>
                            <li>• Tüm kullanıcıları bilgilendirin</li>
                            <li>• Test ortamında deneyin</li>
                            <li>• Kritik dosyaları kaydedin</li>
                          </ul>
                        </div>

                        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                          <input type="checkbox" id="backupBeforeReset" className="rounded" />
                          <Label htmlFor="backupBeforeReset" className="text-sm">
                            ✅ Silmeden önce otomatik yedek al (önerilen)
                          </Label>
                        </div>

                        <Button 
                          onClick={handleCompleteReset}
                          disabled={resetLoading}
                          variant="destructive"
                          size="lg"
                          className="w-full"
                        >
                          {resetLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sıfırlanıyor...
                            </>
                          ) : (
                            <>🚨 SİSTEMİ TAMAMEN SIFIRLA</>
                          )}
                        </Button>
                        
                        <div className="text-center text-gray-600 text-sm">
                          ⚠️ Onay kodu zorunluluğu kaldırıldı - Sadece yukarıdaki butona tıklayın
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </CardHeader>
      </Card>
    </div>
  );
};

// Reports Page Component (Raporlar Sayfası)
const ReportsPage = ({ sessionToken }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Data states
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [productAnalysis, setProductAnalysis] = useState(null);
  const [productionLineStats, setProductionLineStats] = useState(null);
  const [customerAnalysis, setCustomerAnalysis] = useState(null);
  const [kpiTargets, setKpiTargets] = useState(null);
  const [dashboardWidgets, setDashboardWidgets] = useState(null);
  
  // UI states
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSection, setExpandedSection] = useState(null);

  const API = `${BACKEND_URL}/api`;

  // Debug için session token kontrol et
  useEffect(() => {
    if (!sessionToken) {
      toast.error('Session token bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }
    console.log('ReportsPage sessionToken:', sessionToken?.substring(0, 20) + '...');
  }, [sessionToken]);

  useEffect(() => {
    fetchAllData();
  }, [selectedPeriod, selectedYear, selectedMonth]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const promises = [];
      
      if (selectedPeriod === 'monthly') {
        promises.push(fetchMonthlyStats());
        promises.push(fetchProductAnalysis(selectedYear, selectedMonth));
        promises.push(fetchProductionLineStats(selectedYear, selectedMonth));
        promises.push(fetchCustomerAnalysis(selectedYear, selectedMonth));
      } else {
        promises.push(fetchYearlyStats());
        promises.push(fetchProductAnalysis(selectedYear));
        promises.push(fetchProductionLineStats(selectedYear));
        promises.push(fetchCustomerAnalysis(selectedYear));
      }
      
      promises.push(fetchKpiTargets());
      promises.push(fetchDashboardWidgets());
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      toast.error('Raporlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyStats = async () => {
    try {
      const response = await axios.get(`${API}/reports/monthly-stats`, {
        params: {
          year: selectedYear,
          month: selectedMonth,
          session_token: sessionToken
        }
      });
      setMonthlyStats(response.data);
    } catch (error) {
      console.error('Aylık istatistikler hata:', error);
    }
  };

  const fetchYearlyStats = async () => {
    try {
      const response = await axios.get(`${API}/reports/yearly-stats`, {
        params: {
          year: selectedYear,
          session_token: sessionToken
        }
      });
      setYearlyStats(response.data);
    } catch (error) {
      console.error('Yıllık istatistikler hata:', error);
    }
  };

  const fetchProductAnalysis = async (year, month = null) => {
    try {
      const params = {
        year,
        session_token: sessionToken
      };
      if (month) params.month = month;
      
      const response = await axios.get(`${API}/reports/product-analysis`, { params });
      setProductAnalysis(response.data);
    } catch (error) {
      console.error('Ürün analizi hata:', error);
    }
  };

  const fetchProductionLineStats = async (year, month = null) => {
    try {
      const params = {
        year,
        session_token: sessionToken
      };
      if (month) params.month = month;
      
      const response = await axios.get(`${API}/reports/production-line-stats`, { params });
      setProductionLineStats(response.data);
    } catch (error) {
      console.error('Üretim hattı istatistikleri hata:', error);
    }
  };

  const fetchCustomerAnalysis = async (year, month = null) => {
    try {
      const params = {
        year,
        session_token: sessionToken
      };
      if (month) params.month = month;
      
      const response = await axios.get(`${API}/reports/customer-analysis`, { params });
      setCustomerAnalysis(response.data);
    } catch (error) {
      console.error('Müşteri analizi hata:', error);
    }
  };

  const fetchKpiTargets = async () => {
    try {
      const response = await axios.get(`${API}/reports/kpi-targets`, {
        params: { session_token: sessionToken }
      });
      setKpiTargets(response.data);
    } catch (error) {
      console.error('KPI hedefleri hata:', error);
    }
  };

  const fetchDashboardWidgets = async () => {
    try {
      const response = await axios.get(`${API}/reports/dashboard-widgets`, {
        params: { session_token: sessionToken }
      });
      setDashboardWidgets(response.data);
    } catch (error) {
      console.error('Dashboard widget verileri hata:', error);
    }
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();

      // Temel istatistikler sayfası
      if (selectedPeriod === 'monthly' && monthlyStats) {
        const monthlyData = [
          ['AY İSTATİSTİKLERİ', '', '', ''],
          ['', 'Bu Ay', 'Önceki Ay', 'Değişim %'],
          ['Toplam Satış', monthlyStats.current_month.sales_total, monthlyStats.previous_month.sales_total, monthlyStats.changes.sales_change],
          ['Toplam Tahsilat', monthlyStats.current_month.collections_total, monthlyStats.previous_month.collections_total, monthlyStats.changes.collections_change],
          ['Toplam Kapı Adedi', monthlyStats.current_month.door_count, monthlyStats.previous_month.door_count, monthlyStats.changes.door_change]
        ];
        const monthlySheet = XLSX.utils.aoa_to_sheet(monthlyData);
        XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Aylık İstatistikler');
      }

      // Ürün analizi sayfası
      if (productAnalysis) {
        const productData = [
          ['ÜRÜN ANALİZİ', '', ''],
          ['En Çok Satılan Modeller', '', ''],
          ['Model', 'Adet', ''],
          ...productAnalysis.top_models.map(model => [model.name, model.count, '']),
          ['', '', ''],
          ['En Çok Kullanılan Renkler', '', ''],
          ['Renk', 'Adet', ''],
          ...productAnalysis.top_colors.map(color => [color.name, color.count, '']),
          ['', '', ''],
          ['EN ÖLÇÜLERİ İSTATİSTİKLERİ', '', ''],
          ['En Ölçüsü', 'Adet', ''],
          ...productAnalysis.top_widths.map(width => [width.name, width.count, '']),
          ['', '', ''],
          ['BOY ÖLÇÜLERİ İSTATİSTİKLERİ', '', ''],
          ['Boy Ölçüsü', 'Adet', ''],
          ...productAnalysis.top_heights.map(height => [height.name, height.count, '']),
          ['', '', ''],
          ['KASA KALINLIĞI İSTATİSTİKLERİ', '', ''],
          ['Kasa Kalınlığı', 'Adet', ''],
          ...productAnalysis.top_thickness.map(thickness => [thickness.name, thickness.count, '']),
          ['', '', ''],
          ['CAM TÜRÜ İSTATİSTİKLERİ', '', ''],
          ['Cam Türü', 'Adet', ''],
          ...productAnalysis.top_glass_types.map(glass => [glass.name, glass.count, ''])
        ];
        const productSheet = XLSX.utils.aoa_to_sheet(productData);
        XLSX.utils.book_append_sheet(workbook, productSheet, 'Ürün Analizi');
      }

      // Müşteri analizi sayfası
      if (customerAnalysis) {
        const customerData = [
          ['MÜŞTERİ ANALİZİ', '', '', ''],
          ['En Çok Alışveriş Yapan Müşteriler (Tutar)', '', '', ''],
          ['Müşteri', 'Toplam Tutar', 'İşlem Sayısı', 'Ortalama Sipariş'],
          ...customerAnalysis.top_customers_by_amount.map(customer => [
            customer.customer_name,
            customer.total_amount,
            customer.transaction_count,
            customer.average_order
          ])
        ];
        const customerSheet = XLSX.utils.aoa_to_sheet(customerData);
        XLSX.utils.book_append_sheet(workbook, customerSheet, 'Müşteri Analizi');
      }

      // Excel dosyasını indir
      const periodText = selectedPeriod === 'monthly' 
        ? `${selectedYear}_${selectedMonth.toString().padStart(2, '0')}_Aylik` 
        : `${selectedYear}_Yillik`;
      const filename = `DoorNeo_Rapor_${periodText}.xlsx`;
      
      XLSX.writeFile(workbook, filename);
      toast.success('Excel raporu başarıyla indirildi');
    } catch (error) {
      console.error('Excel export hata:', error);
      toast.error('Excel raporu oluşturulurken hata oluştu');
    }
  };

  const getChangeIndicator = (change) => {
    if (change > 0) return { icon: '↗️', color: 'text-green-600', text: 'artış' };
    if (change < 0) return { icon: '↘️', color: 'text-red-600', text: 'azalış' };
    return { icon: '➖', color: 'text-gray-600', text: 'değişim yok' };
  };

  const monthNames = [
    '', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Raporlar yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold tracking-tight">Raporlar</h1>
        
        <div className="flex items-center gap-4">
          {/* Period Selection */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setSelectedPeriod('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === 'monthly'
                  ? 'bg-white shadow text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setSelectedPeriod('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === 'yearly'
                  ? 'bg-white shadow text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yıllık
            </button>
          </div>

          {/* Date Filters */}
          <div className="flex items-center gap-2">
            <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedPeriod === 'monthly' && (
              <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.slice(1).map((month, index) => (
                    <SelectItem key={index + 1} value={(index + 1).toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Export Button */}
          <Button onClick={exportToExcel} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Excel İndir
          </Button>
        </div>
      </div>

      {/* KPI Targets Section */}
      {kpiTargets && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                KPI Hedefleri (Bu Dönem)
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab('targets')}
              >
                ⚙️ Hedefleri Ayarla
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Aylık Kapı Hedefi */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Aylık Kapı Hedefi</span>
                  <span className="text-sm text-gray-600">
                    {kpiTargets.monthly_targets.door_current} / {kpiTargets.monthly_targets.door_target}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(kpiTargets.monthly_targets.door_percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-600">
                  {kpiTargets.monthly_targets.door_percentage}% tamamlandı
                </div>
              </div>

              {/* Aylık Ciro Hedefi */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Aylık Ciro Hedefi</span>
                  <span className="text-sm text-gray-600">
                    {kpiTargets.monthly_targets.sales_current.toLocaleString('tr-TR')} ₺ / 
                    {kpiTargets.monthly_targets.sales_target.toLocaleString('tr-TR')} ₺
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(kpiTargets.monthly_targets.sales_percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-600">
                  {kpiTargets.monthly_targets.sales_percentage}% tamamlandı
                </div>
              </div>

              {/* Yıllık Kapı Hedefi */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Yıllık Kapı Hedefi</span>
                  <span className="text-sm text-gray-600">
                    {kpiTargets.yearly_targets.door_current} / {kpiTargets.yearly_targets.door_target}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(kpiTargets.yearly_targets.door_percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-600">
                  {kpiTargets.yearly_targets.door_percentage}% tamamlandı
                </div>
              </div>

              {/* Yıllık Ciro Hedefi */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Yıllık Ciro Hedefi</span>
                  <span className="text-sm text-gray-600">
                    {kpiTargets.yearly_targets.sales_current.toLocaleString('tr-TR')} ₺ / 
                    {kpiTargets.yearly_targets.sales_target.toLocaleString('tr-TR')} ₺
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(kpiTargets.yearly_targets.sales_percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-600">
                  {kpiTargets.yearly_targets.sales_percentage}% tamamlandı
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Stats Cards */}
      {selectedPeriod === 'monthly' && monthlyStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">Toplam Satış</p>
                  <p className="text-2xl font-bold">
                    {monthlyStats.current_month.sales_total.toLocaleString('tr-TR')} ₺
                  </p>
                  {monthlyStats.changes.sales_change !== 0 && (
                    <div className="flex items-center mt-1">
                      <span className={`text-xs font-medium ${getChangeIndicator(monthlyStats.changes.sales_change).color}`}>
                        {getChangeIndicator(monthlyStats.changes.sales_change).icon}
                        {Math.abs(monthlyStats.changes.sales_change)}% {getChangeIndicator(monthlyStats.changes.sales_change).text}
                      </span>
                    </div>
                  )}
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">Toplam Tahsilat</p>
                  <p className="text-2xl font-bold">
                    {monthlyStats.current_month.collections_total.toLocaleString('tr-TR')} ₺
                  </p>
                  {monthlyStats.changes.collections_change !== 0 && (
                    <div className="flex items-center mt-1">
                      <span className={`text-xs font-medium ${getChangeIndicator(monthlyStats.changes.collections_change).color}`}>
                        {getChangeIndicator(monthlyStats.changes.collections_change).icon}
                        {Math.abs(monthlyStats.changes.collections_change)}% {getChangeIndicator(monthlyStats.changes.collections_change).text}
                      </span>
                    </div>
                  )}
                </div>
                <CreditCard className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">Kapı Adedi</p>
                  <p className="text-2xl font-bold">{monthlyStats.current_month.door_count}</p>
                  {monthlyStats.changes.door_change !== 0 && (
                    <div className="flex items-center mt-1">
                      <span className={`text-xs font-medium ${getChangeIndicator(monthlyStats.changes.door_change).color}`}>
                        {getChangeIndicator(monthlyStats.changes.door_change).icon}
                        {Math.abs(monthlyStats.changes.door_change)}% {getChangeIndicator(monthlyStats.changes.door_change).text}
                      </span>
                    </div>
                  )}
                </div>
                <Factory className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">Ödeme Tutarı</p>
                  <p className="text-2xl font-bold">
                    {monthlyStats.current_month.payments_total.toLocaleString('tr-TR')} ₺
                  </p>
                  {monthlyStats.changes.payments_change !== 0 && (
                    <div className="flex items-center mt-1">
                      <span className={`text-xs font-medium ${getChangeIndicator(monthlyStats.changes.payments_change).color}`}>
                        {getChangeIndicator(monthlyStats.changes.payments_change).icon}
                        {Math.abs(monthlyStats.changes.payments_change)}% {getChangeIndicator(monthlyStats.changes.payments_change).text}
                      </span>
                    </div>
                  )}
                </div>
                <Banknote className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for detailed analysis */}
      <Card>
        <CardHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
              <TabsTrigger value="products">Ürün Analizi</TabsTrigger>
              <TabsTrigger value="customers">Müşteri Analizi</TabsTrigger>
              <TabsTrigger value="production">Üretim Hattı</TabsTrigger>
              <TabsTrigger value="targets">🎯 Hedef Yönetimi</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview">
              <ReportsOverviewExtra api={API} sessionToken={sessionToken} year={selectedYear} />
              {dashboardWidgets && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm text-gray-600 mb-2">En Çok Satılan Model</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-blue-600">
                          {dashboardWidgets.top_model.name}
                        </span>
                        <Badge>{dashboardWidgets.top_model.count} adet</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm text-gray-600 mb-2">Üretim Hattı Dağılımı</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span>Lake: {dashboardWidgets.production_ratio.lake_percentage}%</span>
                          <span>Melamin: {dashboardWidgets.production_ratio.melamin_percentage}%</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          Toplam {dashboardWidgets.production_ratio.total_doors} kapı
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm text-gray-600 mb-2">Bu Ay Performansı</h3>
                      <div className="space-y-1">
                        <div className="text-lg font-bold">
                          {dashboardWidgets.monthly_performance.revenue.toLocaleString('tr-TR')} ₺
                        </div>
                        <div className="text-xs text-gray-600">
                          {dashboardWidgets.monthly_performance.door_count} kapı • 
                          Ort. {dashboardWidgets.monthly_performance.average_order.toLocaleString('tr-TR')} ₺
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="products">
              {productAnalysis && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  {/* Top Models */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">En Çok Satılan Modeller</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {productAnalysis.top_models.slice(0, 20).map((model, index) => (
                          <div key={model.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <span className="font-medium">{model.name}</span>
                            </div>
                            <Badge variant="secondary">{model.count} adet</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Colors */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">En Çok Kullanılan Renkler</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {productAnalysis.top_colors.slice(0, 20).map((color, index) => (
                          <div key={color.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <span className="font-medium">{color.name}</span>
                            </div>
                            <Badge variant="secondary">{color.count} adet</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* En İstatistikleri */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">En Çok Kullanılan En Ölçüleri</CardTitle>
                      <CardDescription>Kapı genişliği ölçüleri</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {productAnalysis.top_widths.slice(0, 20).map((width, index) => (
                          <div key={width.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <span className="font-medium">{width.name}</span>
                            </div>
                            <Badge variant="outline">{width.count} adet</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Boy İstatistikleri */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">En Çok Kullanılan Boy Ölçüleri</CardTitle>
                      <CardDescription>Kapı yüksekliği ölçüleri</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {productAnalysis.top_heights.slice(0, 20).map((height, index) => (
                          <div key={height.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <span className="font-medium">{height.name}</span>
                            </div>
                            <Badge variant="outline">{height.count} adet</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Kasa Kalınlığı İstatistikleri */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Kasa Kalınlığı İstatistikleri</CardTitle>
                      <CardDescription>Kapı kasası kalınlık ölçüleri</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {productAnalysis.top_thickness.slice(0, 20).map((thickness, index) => (
                          <div key={thickness.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <span className="font-medium">{thickness.name}</span>
                            </div>
                            <Badge variant="outline">{thickness.count} adet</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Cam Türü İstatistikleri */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Cam Türü İstatistikleri</CardTitle>
                      <CardDescription>Camlı kapı türleri ve cam seçenekleri</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {productAnalysis.top_glass_types.slice(0, 20).map((glass, index) => (
                          <div key={glass.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <span className="font-medium">
                                {glass.name === 'Belirsiz' ? 'Camlı Değil' : glass.name}
                              </span>
                            </div>
                            <Badge variant="secondary" className={glass.name !== 'Belirsiz' ? 'bg-blue-100 text-blue-800' : ''}>
                              {glass.count} adet
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* YENİ: En x Duvar Kalınlığı Kombinasyonu */}
                  {productAnalysis.top_width_thickness && productAnalysis.top_width_thickness.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">En x Duvar Kalınlığı</CardTitle>
                        <CardDescription>En çok tercih edilen ölçü kombinasyonları</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {productAnalysis.top_width_thickness.slice(0, 10).map((combo, index) => (
                            <div key={combo.name} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                <span className="font-medium">
                                  {combo.name} cm
                                </span>
                              </div>
                              <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">
                                {combo.count} adet
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Özet Kart */}
                  {productAnalysis.measurement_summary && (
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="text-lg">Ölçü Çeşitliliği Özeti</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">
                              {productAnalysis.measurement_summary.unique_widths}
                            </div>
                            <div className="text-sm text-gray-600">Farklı En Ölçüsü</div>
                          </div>
                          <div className="text-center p-4 bg-green-50 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">
                              {productAnalysis.measurement_summary.unique_heights}
                            </div>
                            <div className="text-sm text-gray-600">Farklı Boy Ölçüsü</div>
                          </div>
                          <div className="text-center p-4 bg-purple-50 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">
                              {productAnalysis.measurement_summary.unique_thickness}
                            </div>
                            <div className="text-sm text-gray-600">Farklı Kasa Kalınlığı</div>
                          </div>
                          <div className="text-center p-4 bg-orange-50 rounded-lg">
                            <div className="text-2xl font-bold text-orange-600">
                              {productAnalysis.measurement_summary.glass_varieties}
                            </div>
                            <div className="text-sm text-gray-600">Farklı Cam Türü</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Yeni Eklenen: Toplam Kapı Sayısı */}
                  {productAnalysis && (
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <BarChart3 className="h-5 w-5" />
                          Kapı Üretim İstatistikleri
                        </CardTitle>
                        <CardDescription>Ölçü bazında gerçek kapı sayıları</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <div className="text-3xl font-bold text-blue-600">
                              {productAnalysis.total_doors}
                            </div>
                            <div className="text-sm text-gray-600">Toplam Üretilen Kapı</div>
                            <div className="text-xs text-gray-500 mt-1">(Ölçü adeti hesaplı)</div>
                          </div>
                          <div className="text-center p-4 bg-green-50 rounded-lg">
                            <div className="text-3xl font-bold text-green-600">
                              {productAnalysis.total_transactions}
                            </div>
                            <div className="text-sm text-gray-600">Toplam Sipariş</div>
                            <div className="text-xs text-gray-500 mt-1">(Transaction sayısı)</div>
                          </div>
                          <div className="text-center p-4 bg-orange-50 rounded-lg">
                            <div className="text-3xl font-bold text-orange-600">
                              {productAnalysis.measurement_summary.average_doors_per_order}
                            </div>
                            <div className="text-sm text-gray-600">Sipariş Başı Kapı</div>
                            <div className="text-xs text-gray-500 mt-1">(Ortalama adet)</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Yeni Eklenen: Ölçü Dağılım Raporları */}
                  {productAnalysis && productAnalysis.dimension_reports && (
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="text-lg">Ölçü Dağılım Raporları</CardTitle>
                        <CardDescription>Hangi ölçülerde kaç kapı satıldığı</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Tabs defaultValue="width" className="w-full">
                          <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="width">En Ölçüleri</TabsTrigger>
                            <TabsTrigger value="height">Boy Ölçüleri</TabsTrigger>
                            <TabsTrigger value="thickness">Kasa Ölçüleri</TabsTrigger>
                            <TabsTrigger value="glass">Cam Türleri</TabsTrigger>
                          </TabsList>
                          
                          <TabsContent value="width" className="mt-4">
                            <div className="space-y-3">
                              {productAnalysis.dimension_reports.width_distribution.map((item, index) => (
                                <div key={item.dimension} className="flex items-center justify-between p-3 border rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                    <span className="font-medium">{item.dimension}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{item.count} kapı</Badge>
                                    <span className="text-sm text-gray-500">%{item.percentage}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="height" className="mt-4">
                            <div className="space-y-3">
                              {productAnalysis.dimension_reports.height_distribution.map((item, index) => (
                                <div key={item.dimension} className="flex items-center justify-between p-3 border rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                    <span className="font-medium">{item.dimension}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{item.count} kapı</Badge>
                                    <span className="text-sm text-gray-500">%{item.percentage}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="thickness" className="mt-4">
                            <div className="space-y-3">
                              {productAnalysis.dimension_reports.thickness_distribution.map((item, index) => (
                                <div key={item.dimension} className="flex items-center justify-between p-3 border rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                    <span className="font-medium">{item.dimension}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{item.count} kapı</Badge>
                                    <span className="text-sm text-gray-500">%{item.percentage}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="glass" className="mt-4">
                            <div className="space-y-3">
                              {productAnalysis.dimension_reports.glass_distribution.map((item, index) => (
                                <div key={item.type} className="flex items-center justify-between p-3 border rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                    <span className="font-medium">{item.type}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{item.count} kapı</Badge>
                                    <span className="text-sm text-gray-500">%{item.percentage}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                        </Tabs>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Yeni Eklenen: Detaylı Ölçü Listesi */}
                  {productAnalysis && productAnalysis.measurements_details && productAnalysis.measurements_details.length > 0 && (
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="text-lg">Satılan Kapıların Detaylı Ölçü Listesi</CardTitle>
                        <CardDescription>Tüm satış işlemlerindeki kapı ölçüleri</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-gray-300 text-sm">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="border border-gray-300 px-3 py-2 text-center font-bold">#</th>
                                <th className="border border-gray-300 px-3 py-2 text-center font-bold">En</th>
                                <th className="border border-gray-300 px-3 py-2 text-center font-bold">Boy</th>
                                <th className="border border-gray-300 px-3 py-2 text-center font-bold">Kasa</th>
                                <th className="border border-gray-300 px-3 py-2 text-center font-bold">Cam Türü</th>
                                <th className="border border-gray-300 px-3 py-2 text-center font-bold">Oda</th>
                                <th className="border border-gray-300 px-3 py-2 text-center font-bold">Adet</th>
                              </tr>
                            </thead>
                            <tbody>
                              {productAnalysis.measurements_details.slice(0, 20).map((measurement, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="border border-gray-300 px-3 py-2 text-center">{index + 1}</td>
                                  <td className="border border-gray-300 px-3 py-2 text-center">{measurement.width} cm</td>
                                  <td className="border border-gray-300 px-3 py-2 text-center">{measurement.height} cm</td>
                                  <td className="border border-gray-300 px-3 py-2 text-center">{measurement.thickness}</td>
                                  <td className="border border-gray-300 px-3 py-2 text-center">{measurement.glass_type}</td>
                                  <td className="border border-gray-300 px-3 py-2 text-center">{measurement.room}</td>
                                  <td className="border border-gray-300 px-3 py-2 text-center font-bold text-blue-600">{measurement.quantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {productAnalysis.measurements_details.length > 20 && (
                            <div className="text-center mt-3 text-sm text-gray-500">
                              İlk 20 kayıt gösterildi, toplam {productAnalysis.measurements_details.length} ölçü kaydı mevcut
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="customers">
              {customerAnalysis && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  {/* Top customers by amount */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">En Değerli Müşteriler</CardTitle>
                      <CardDescription>Toplam satış tutarına göre</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {customerAnalysis.top_customers_by_amount.slice(0, 20).map((customer, index) => (
                          <div key={customer.customer_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                <span className="font-semibold">{customer.customer_name}</span>
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {customer.transaction_count} işlem • 
                                Ort. {customer.average_order.toLocaleString('tr-TR')} ₺
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-green-600">
                                {customer.total_amount.toLocaleString('tr-TR')} ₺
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Customer metrics */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Müşteri Metrikleri</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                          <span className="font-medium">Toplam Müşteri</span>
                          <span className="font-bold text-blue-600">
                            {customerAnalysis.customer_metrics.total_customers}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                          <span className="font-medium">Sipariş Veren Müşteri</span>
                          <span className="font-bold text-green-600">
                            {customerAnalysis.customer_metrics.customers_with_orders}
                          </span>
                        </div>

                        <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                          <span className="font-medium">Ortalama Sipariş</span>
                          <span className="font-bold text-purple-600">
                            {customerAnalysis.customer_metrics.average_order_value.toLocaleString('tr-TR')} ₺
                          </span>
                        </div>

                        {selectedPeriod === 'monthly' && (
                          <>
                            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                              <span className="font-medium">Yeni Müşteri</span>
                              <span className="font-bold text-orange-600">
                                {customerAnalysis.customer_metrics.new_customers}
                              </span>
                            </div>

                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">Tekrar Eden Müşteri</span>
                              <span className="font-bold text-gray-600">
                                {customerAnalysis.customer_metrics.returning_customers}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="targets">
              <MonthlyTargets api={API} sessionToken={sessionToken} year={selectedYear} />
            </TabsContent>

            <TabsContent value="production">
              {!productionLineStats && <p className="text-center text-sm text-gray-500 py-8">Üretim hattı verisi yükleniyor...</p>}
              <ProductionLineExtra stats={productionLineStats} />
              {productionLineStats && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Üretim Hattı Dağılımı</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Lake Stats */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-blue-600">Lake Hattı</span>
                            <span className="text-sm text-gray-600">
                              {productionLineStats.production_lines.lake.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-blue-600 h-3 rounded-full"
                              style={{ width: `${productionLineStats.production_lines.lake.percentage}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>{productionLineStats.production_lines.lake.count} adet</span>
                            <span>{productionLineStats.production_lines.lake.amount.toLocaleString('tr-TR')} ₺</span>
                          </div>
                        </div>

                        {/* Melamin Stats */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-green-600">Melamin Hattı</span>
                            <span className="text-sm text-gray-600">
                              {productionLineStats.production_lines.melamin.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-green-600 h-3 rounded-full"
                              style={{ width: `${productionLineStats.production_lines.melamin.percentage}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>{productionLineStats.production_lines.melamin.count} adet</span>
                            <span>{productionLineStats.production_lines.melamin.amount.toLocaleString('tr-TR')} ₺</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Toplam Üretim</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-gray-900">
                            {productionLineStats.totals.total_doors}
                          </div>
                          <div className="text-sm text-gray-600">Toplam Kapı Adedi</div>
                        </div>

                        <div className="text-center">
                          <div className="text-3xl font-bold text-green-600">
                            {productionLineStats.totals.total_amount.toLocaleString('tr-TR')} ₺
                          </div>
                          <div className="text-sm text-gray-600">Toplam Satış Tutarı</div>
                        </div>

                        {productionLineStats.production_lines.no_line.count > 0 && (
                          <div className="mt-6 p-3 bg-yellow-50 rounded-lg">
                            <div className="text-sm text-yellow-800">
                              <strong>{productionLineStats.production_lines.no_line.count}</strong> adet kapı için üretim hattı belirtilmemiş.
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardHeader>
      </Card>
    </div>
  );
};

// ============================================
// STOCK MANAGEMENT PAGE (Stok Yönetimi)
// ============================================


// ============================================
// STOCK MANAGEMENT PAGE (Stok Yönetimi)
// ============================================

const StockManagement = ({ sessionToken }) => {
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [inventoryRequirements, setInventoryRequirements] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [showStockTransactionForm, setShowStockTransactionForm] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('stock'); // 'stock', 'cost', or 'requirements'
  const [costSummary, setCostSummary] = useState(null);

  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    fetchInventory(selectedCategory);
    fetchInventoryRequirements();
    if (activeView === 'cost') {
      fetchCostSummary();
    }
  }, [selectedCategory, activeView]);

  const fetchInventory = async (category = null) => {
    try {
      setLoading(true);
      const params = { session_token: sessionToken };
      if (category && category !== 'all') {
        params.category = category;
      }
      const response = await axios.get(`${API}/inventory`, { params });
      setInventory(response.data || []);
    } catch (error) {
      console.error('Stok listesi yükleme hatası:', error);
      toast.error('Stok listesi yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostSummary = async () => {
    try {
      const response = await axios.get(`${API}/inventory/cost-summary`, {
        params: { session_token: sessionToken }
      });
      setCostSummary(response.data);
    } catch (error) {
      console.error('Maliyet özeti yükleme hatası:', error);
      toast.error('Maliyet özeti yüklenirken hata oluştu');
    }
  };

  const handleUnitPriceUpdate = async (itemId, newPrice) => {
    try {
      await axios.put(
        `${API}/inventory/${itemId}?session_token=${sessionToken}`,
        { unit_price: parseFloat(newPrice) }
      );
      toast.success('Birim fiyat güncellendi');
      fetchInventory(selectedCategory);
      fetchCostSummary();
    } catch (error) {
      console.error('Birim fiyat güncelleme hatası:', error);
      toast.error('Birim fiyat güncellenirken hata oluştu');
    }
  };

  const fetchInventoryRequirements = async () => {
    try {
      const response = await axios.get(`${API}/inventory/calculate-requirements`, {
        params: { session_token: sessionToken }
      });
      setInventoryRequirements(response.data);
    } catch (error) {
      console.error('Malzeme ihtiyaç hesaplama hatası:', error);
    }
  };

  const handleCreateInventoryItem = async (itemData) => {
    try {
      await axios.post(`${API}/inventory`, itemData, {
        params: { session_token: sessionToken }
      });
      toast.success('Stok kalemi başarıyla eklendi');
      fetchInventory(selectedCategory);
      fetchInventoryRequirements();
      setShowInventoryForm(false);
    } catch (error) {
      console.error('Stok kalemi ekleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Stok kalemi eklenirken hata oluştu');
    }
  };

  const handleUpdateInventoryItem = async (itemId, itemData) => {
    try {
      await axios.put(`${API}/inventory/${itemId}`, itemData, {
        params: { session_token: sessionToken }
      });
      toast.success('Stok kalemi başarıyla güncellendi');
      fetchInventory(selectedCategory);
      fetchInventoryRequirements();
      setSelectedInventoryItem(null);
    } catch (error) {
      console.error('Stok kalemi güncelleme hatası:', error);
      toast.error('Stok kalemi güncellenirken hata oluştu');
    }
  };

  const handleDeleteInventoryItem = async (itemId) => {
    if (!window.confirm('Bu stok kalemini silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await axios.delete(`${API}/inventory/${itemId}`, {
        params: { session_token: sessionToken }
      });
      toast.success('Stok kalemi başarıyla silindi');
      fetchInventory(selectedCategory);
      fetchInventoryRequirements();
    } catch (error) {
      console.error('Stok kalemi silme hatası:', error);
      toast.error('Stok kalemi silinirken hata oluştu');
    }
  };

  const handleStockTransaction = async (transactionData) => {
    try {
      await axios.post(`${API}/inventory/transaction`, transactionData, {
        params: { session_token: sessionToken }
      });
      toast.success('Stok hareketi başarıyla kaydedildi');
      fetchInventory(selectedCategory);
      fetchInventoryRequirements();
      setShowStockTransactionForm(false);
      setSelectedInventoryItem(null);
    } catch (error) {
      console.error('Stok hareketi kaydetme hatası:', error);
      toast.error('Stok hareketi kaydedilirken hata oluştu');
    }
  };

  const filteredInventory = inventory.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.item_name.toLowerCase().includes(search) ||
      item.item_code.toLowerCase().includes(search) ||
      item.category.toLowerCase().includes(search)
    );
  });

  const categoryStats = {
    total: inventory.length,
    critical: inventory.filter(i => i.quantity <= i.min_quantity).length,
    low: inventory.filter(i => i.quantity > i.min_quantity && i.quantity <= i.min_quantity * 1.5).length,
    sufficient: inventory.filter(i => i.quantity > i.min_quantity * 1.5).length
  };

  if (loading && inventory.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Stok yönetimi yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="h-8 w-8" />
            Stok Yönetimi
          </h1>
          <p className="text-gray-600 mt-1">Malzeme ve ürün stok takip sistemi</p>
        </div>
        <Button onClick={() => setShowInventoryForm(true)} size="lg">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Stok Kalemi
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Toplam Kalem</p>
                <p className="text-3xl font-bold mt-2">{categoryStats.total}</p>
              </div>
              <Package className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Yeterli Stok</p>
                <p className="text-3xl font-bold mt-2 text-green-600">{categoryStats.sufficient}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Düşük Seviye</p>
                <p className="text-3xl font-bold mt-2 text-yellow-600">{categoryStats.low}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Kritik Seviye</p>
                <p className="text-3xl font-bold mt-2 text-red-600">{categoryStats.critical}</p>
              </div>
              <AlertCircle className="h-10 w-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Tabs */}
      <Card>
        <CardHeader>
          <Tabs value={activeView} onValueChange={setActiveView}>
            <TabsList>
              <TabsTrigger value="stock">Stok Kalemleri</TabsTrigger>
              <TabsTrigger value="cost">Stok Maliyeti</TabsTrigger>
              <TabsTrigger value="requirements">Malzeme İhtiyaç Analizi</TabsTrigger>
            </TabsList>

            <TabsContent value="stock" className="mt-6">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Input
                    placeholder="Ürün adı, kodu veya kategori ile ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full sm:w-[250px]">
                    <SelectValue placeholder="Kategori Seç" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Kategoriler</SelectItem>
                    <SelectItem value="ortak_header" disabled className="font-bold">🪵 ORTAK MALZEMELER</SelectItem>
                    <SelectItem value="agac_seren">🪵 Ağaç Seren</SelectItem>
                    <SelectItem value="lake_header" disabled className="font-bold">🔵 LAKE ÜRETİMİ</SelectItem>
                    <SelectItem value="lake_yuzey_mdf_6mm">📦 Lake - MDF 6mm (Kapalı)</SelectItem>
                    <SelectItem value="lake_yuzey_mdf_18mm">📦 Lake - MDF 18mm (Camlı)</SelectItem>
                    <SelectItem value="lake_kasa_pp">🚪 Lake - PP Kasa</SelectItem>
                    <SelectItem value="lake_pervaz_pp">📐 Lake - PP Pervazlar</SelectItem>
                    <SelectItem value="melamin_header" disabled className="font-bold">🟢 MELAMİN ÜRETİMİ</SelectItem>
                    <SelectItem value="melamin_yuzey">🎨 Melamin - Yüzeyler</SelectItem>
                    <SelectItem value="melamin_kasa_pvc">🚪 Melamin - PVC Kasa</SelectItem>
                    <SelectItem value="melamin_pervaz_pvc">📐 Melamin - PVC Pervazlar</SelectItem>
                    <SelectItem value="aksesuar_header" disabled className="font-bold">🔧 AKSESUAR</SelectItem>
                    <SelectItem value="aksesuar">🔧 Aksesuarlar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Stock Table */}
              {filteredInventory.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg font-medium">Henüz stok kalemi eklenmemiş</p>
                  <p className="text-gray-500 mt-2">Yeni stok kalemi eklemek için yukarıdaki butona tıklayın</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2">
                      <tr>
                        <th className="text-left p-4 font-semibold">Ürün Kodu</th>
                        <th className="text-left p-4 font-semibold">Ürün Adı</th>
                        <th className="text-left p-4 font-semibold">Kategori</th>
                        <th className="text-right p-4 font-semibold">Mevcut Stok</th>
                        <th className="text-right p-4 font-semibold">Min. Stok</th>
                        <th className="text-right p-4 font-semibold">Eksik</th>
                        <th className="text-center p-4 font-semibold">Durum</th>
                        <th className="text-right p-4 font-semibold">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((item) => {
                        const minQty = item.min_stock_level || item.min_quantity || 0;
                        const currentStock = item.quantity || 0;
                        const shortage = Math.max(0, minQty - currentStock);
                        const stockStatus = 
                          currentStock === 0 ? 'out_of_stock' :
                          currentStock < minQty ? 'critical' :
                          currentStock <= minQty * 1.5 ? 'low' : 'sufficient';
                        
                        return (
                          <tr key={item.id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="p-4">
                              <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                                {item.item_code}
                              </code>
                            </td>
                            <td className="p-4 font-medium">{item.item_name}</td>
                            <td className="p-4">
                              <Badge variant="outline">{item.category}</Badge>
                            </td>
                            <td className="p-4 text-right">
                              <span className={`font-bold text-lg ${
                                currentStock === 0 ? 'text-red-600' :
                                stockStatus === 'critical' ? 'text-orange-600' :
                                stockStatus === 'low' ? 'text-yellow-600' : 'text-green-600'
                              }`}>
                                {currentStock}
                              </span>
                              <span className="text-gray-600 ml-1 text-sm">{item.unit}</span>
                            </td>
                            <td className="p-4 text-right text-gray-600">
                              {minQty} {item.unit}
                            </td>
                            <td className="p-4 text-right">
                              {shortage > 0 ? (
                                <span className="text-red-600 font-bold">{shortage} {item.unit}</span>
                              ) : (
                                <span className="text-green-600 font-bold">0</span>
                              )}
                            </td>
                            <td className="p-4 text-center">
                              {stockStatus === 'out_of_stock' && (
                                <Badge variant="destructive">
                                  ❌ Stokta Yok
                                </Badge>
                              )}
                              {stockStatus === 'sufficient' && (
                                <Badge variant="success" className="bg-green-100 text-green-800">
                                  ✓ Yeterli
                                </Badge>
                              )}
                              {stockStatus === 'low' && (
                                <Badge variant="warning" className="bg-yellow-100 text-yellow-800">
                                  ⚠ Düşük Stok
                                </Badge>
                              )}
                              {stockStatus === 'critical' && (
                                <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                                  📉 Eksik
                                </Badge>
                              )}
                            </td>
                            <td className="p-4 text-right">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  size="sm" 
                                  variant="default"
                                  onClick={() => {
                                    setSelectedInventoryItem(item);
                                    setShowStockTransactionForm(true);
                                  }}
                                >
                                  Stok Hareketi
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => setSelectedInventoryItem(item)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleDeleteInventoryItem(item.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="cost" className="mt-6">
              {costSummary ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-700">Toplam Stok Maliyeti</p>
                            <p className="text-3xl font-bold mt-2 text-blue-900">
                              ₺{costSummary.total_cost?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <DollarSign className="h-10 w-10 text-blue-600" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Toplam Ürün</p>
                            <p className="text-3xl font-bold mt-2">{costSummary.total_items}</p>
                          </div>
                          <Package className="h-10 w-10 text-gray-500" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Kategori Sayısı</p>
                            <p className="text-3xl font-bold mt-2">{costSummary.category_summary?.length}</p>
                          </div>
                          <Layers className="h-10 w-10 text-gray-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Items Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Ürün Bazında Maliyet</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b-2">
                            <tr>
                              <th className="text-left p-4 font-semibold">Ürün Kodu</th>
                              <th className="text-left p-4 font-semibold">Ürün Adı</th>
                              <th className="text-right p-4 font-semibold">Miktar</th>
                              <th className="text-right p-4 font-semibold">Birim Fiyat (₺)</th>
                              <th className="text-right p-4 font-semibold">Toplam Maliyet (₺)</th>
                              <th className="text-center p-4 font-semibold">İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costSummary.items?.map((item) => (
                              <tr key={item.id} className="border-b hover:bg-gray-50">
                                <td className="p-4">
                                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                                    {item.item_code}
                                  </code>
                                </td>
                                <td className="p-4 font-medium">{item.item_name}</td>
                                <td className="p-4 text-right">
                                  {item.quantity} {item.unit}
                                </td>
                                <td className="p-4 text-right">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={item.unit_price}
                                    onChange={(e) => {
                                      const newItems = costSummary.items.map(i =>
                                        i.id === item.id ? { ...i, unit_price: parseFloat(e.target.value) || 0, total_cost: (parseFloat(e.target.value) || 0) * i.quantity } : i
                                      );
                                      const newTotal = newItems.reduce((sum, i) => sum + i.total_cost, 0);
                                      setCostSummary({ ...costSummary, items: newItems, total_cost: newTotal });
                                    }}
                                    onBlur={(e) => handleUnitPriceUpdate(item.id, e.target.value)}
                                    className="w-28 px-2 py-1 border rounded text-right focus:ring-2 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="p-4 text-right font-bold text-green-600">
                                  ₺{item.total_cost?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="p-4 text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUnitPriceUpdate(item.id, item.unit_price)}
                                  >
                                    Kaydet
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50 border-t-2">
                            <tr>
                              <td colSpan="4" className="p-4 text-right font-bold text-lg">
                                GENEL TOPLAM:
                              </td>
                              <td className="p-4 text-right font-bold text-2xl text-green-600">
                                ₺{costSummary.total_cost?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Maliyet verileri yükleniyor...</p>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="requirements" className="mt-6">
              {inventoryRequirements && inventoryRequirements.inventory_status ? (
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start gap-3 mb-3">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-blue-900">Malzeme İhtiyaç Analizi</p>
                        <p className="text-blue-700 text-sm mt-1">
                          Bekleyen ve üretimdeki <strong>{inventoryRequirements.total_productions} sipariş</strong> için 
                          gerekli malzemeler hesaplanmıştır.
                        </p>
                      </div>
                    </div>
                    {(() => {
                      const missingItems = inventoryRequirements.inventory_status.filter(item => item.available === 0 && item.required > 0);
                      if (missingItems.length > 0) {
                        return (
                          <Button 
                            onClick={async () => {
                              try {
                                let addedCount = 0;
                                for (const item of missingItems) {
                                  try {
                                    await axios.post(`${API}/inventory`, {
                                      item_code: item.item_code,
                                      item_name: item.item_name,
                                      category: item.category,
                                      unit: item.unit,
                                      quantity: 0,
                                      min_quantity: item.required
                                    }, {
                                      params: { session_token: sessionToken }
                                    });
                                    addedCount++;
                                  } catch (err) {
                                    console.error(`${item.item_code} eklenirken hata:`, err);
                                  }
                                }
                                if (addedCount > 0) {
                                  toast.success(`${addedCount} malzeme stok kalemlerine eklendi`);
                                  fetchInventory(selectedCategory);
                                  fetchInventoryRequirements();
                                }
                              } catch (error) {
                                toast.error('Malzemeler eklenirken hata oluştu');
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Stokta Olmayan {missingItems.length} Malzemeyi Stok Kalemlerine Ekle
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {(() => {
                    // Kategorilere göre grupla
                    const requiredItems = inventoryRequirements.inventory_status.filter(item => item.required > 0);
                    
                    const categories = {
                      'ORTAK': { title: '🪵 ORTAK MALZEMELER', items: [], color: 'bg-yellow-50 border-yellow-200' },
                      'LAKE_YUZEY': { title: '🔵 LAKE - YÜZEY', items: [], color: 'bg-blue-50 border-blue-200' },
                      'LAKE_KASA': { title: '🔵 LAKE - KASA', items: [], color: 'bg-blue-50 border-blue-200' },
                      'LAKE_PERVAZ': { title: '🔵 LAKE - PERVAZ', items: [], color: 'bg-blue-50 border-blue-200' },
                      'MELAMIN_YUZEY': { title: '🟢 MELAMİN - YÜZEY', items: [], color: 'bg-green-50 border-green-200' },
                      'MELAMIN_KASA': { title: '🟢 MELAMİN - KASA', items: [], color: 'bg-green-50 border-green-200' },
                      'MELAMIN_PERVAZ': { title: '🟢 MELAMİN - PERVAZ', items: [], color: 'bg-green-50 border-green-200' },
                      'AKSESUAR': { title: '🔧 AKSESUAR', items: [], color: 'bg-orange-50 border-orange-200' }
                    };

                    requiredItems.forEach(item => {
                      const cat = item.category || '';
                      if (cat === 'agac_seren') {
                        categories.ORTAK.items.push(item);
                      } else if (cat.includes('lake_yuzey')) {
                        categories.LAKE_YUZEY.items.push(item);
                      } else if (cat === 'lake_kasa_pp') {
                        categories.LAKE_KASA.items.push(item);
                      } else if (cat === 'lake_pervaz_pp') {
                        categories.LAKE_PERVAZ.items.push(item);
                      } else if (cat === 'melamin_yuzey') {
                        categories.MELAMIN_YUZEY.items.push(item);
                      } else if (cat === 'melamin_kasa_pvc') {
                        categories.MELAMIN_KASA.items.push(item);
                      } else if (cat === 'melamin_pervaz_pvc') {
                        categories.MELAMIN_PERVAZ.items.push(item);
                      } else if (cat === 'aksesuar') {
                        categories.AKSESUAR.items.push(item);
                      }
                    });

                    return Object.entries(categories).map(([key, catData]) => {
                      if (catData.items.length === 0) return null;
                      
                      // Eksik olanları önce göster
                      const sortedItems = catData.items.sort((a, b) => b.shortage - a.shortage);
                      const totalShortage = sortedItems.filter(i => i.shortage > 0).length;

                      return (
                        <Card key={key} className={`border-2 ${catData.color}`}>
                          <CardHeader className="pb-3">
                            <div className="flex justify-between items-center">
                              <CardTitle className="text-lg">{catData.title}</CardTitle>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="bg-white">
                                  {catData.items.length} Malzeme
                                </Badge>
                                {totalShortage > 0 && (
                                  <Badge variant="destructive">
                                    {totalShortage} Eksik
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                  <tr>
                                    <th className="text-left p-3 text-sm font-semibold">Malzeme</th>
                                    <th className="text-right p-3 text-sm font-semibold">Mevcut</th>
                                    <th className="text-right p-3 text-sm font-semibold">İhtiyaç</th>
                                    <th className="text-right p-3 text-sm font-semibold">Eksik</th>
                                    <th className="text-center p-3 text-sm font-semibold">Durum</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedItems.map((item, idx) => (
                                    <tr key={idx} className="border-b last:border-0 hover:bg-white/50">
                                      <td className="p-3">
                                        <div>
                                          <div className="font-medium text-sm">{item.item_name}</div>
                                          <code className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded">
                                            {item.item_code}
                                          </code>
                                        </div>
                                      </td>
                                      <td className="p-3 text-right">
                                        <span className="font-medium">{item.available}</span>
                                        <span className="text-xs text-gray-600 ml-1">{item.unit}</span>
                                      </td>
                                      <td className="p-3 text-right">
                                        <span className="font-medium text-blue-600">{item.required}</span>
                                        <span className="text-xs text-gray-600 ml-1">{item.unit}</span>
                                      </td>
                                      <td className="p-3 text-right">
                                        {item.shortage > 0 ? (
                                          <div>
                                            <span className="text-red-600 font-bold">{item.shortage}</span>
                                            <span className="text-xs text-gray-600 ml-1">{item.unit}</span>
                                          </div>
                                        ) : (
                                          <span className="text-green-600 font-bold">0</span>
                                        )}
                                      </td>
                                      <td className="p-3 text-center">
                                        {item.available === 0 && item.required > 0 ? (
                                          <Badge variant="destructive">
                                            ❌ Stokta Yok
                                          </Badge>
                                        ) : item.status === 'sufficient' ? (
                                          <Badge className="bg-green-100 text-green-800 border-green-300">
                                            ✓ Yeterli
                                          </Badge>
                                        ) : item.status === 'low' ? (
                                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                                            ⚠ Düşük Stok
                                          </Badge>
                                        ) : (
                                          <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                                            📉 Eksik
                                          </Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Calculator className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg font-medium">Malzeme ihtiyaçları hesaplanamadı</p>
                  <p className="text-gray-500 mt-2">
                    Bekleyen veya üretimdeki sipariş bulunmuyor veya stok kalemleri tanımlanmamış
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardHeader>
      </Card>

      {/* Dialogs */}
      <Dialog open={showInventoryForm} onOpenChange={setShowInventoryForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Stok Kalemi Ekle</DialogTitle>
          </DialogHeader>
          <InventoryForm 
            onSubmit={handleCreateInventoryItem}
            onCancel={() => setShowInventoryForm(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedInventoryItem && !showStockTransactionForm} onOpenChange={(open) => !open && setSelectedInventoryItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stok Kalemi Güncelle</DialogTitle>
          </DialogHeader>
          {selectedInventoryItem && (
            <InventoryForm 
              item={selectedInventoryItem}
              onSubmit={(data) => handleUpdateInventoryItem(selectedInventoryItem.id, data)}
              onCancel={() => setSelectedInventoryItem(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showStockTransactionForm} onOpenChange={setShowStockTransactionForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stok Hareketi Ekle</DialogTitle>
            {selectedInventoryItem && (
              <p className="text-sm text-gray-600">
                {selectedInventoryItem.item_name} - Mevcut: {selectedInventoryItem.quantity} {selectedInventoryItem.unit}
              </p>
            )}
          </DialogHeader>
          {selectedInventoryItem && (
            <StockTransactionForm 
              inventoryItem={selectedInventoryItem}
              onSubmit={handleStockTransaction}
              onCancel={() => {
                setShowStockTransactionForm(false);
                setSelectedInventoryItem(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Inventory Form Component
const InventoryForm = ({ item, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    category: item?.category || 'agac_seren',
    item_name: item?.item_name || '',
    item_code: item?.item_code || '',
    unit: item?.unit || 'adet',
    quantity: item?.quantity || 0,
    min_quantity: item?.min_quantity || 0,
    specifications: item?.specifications || '',
    notes: item?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Kategori *</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ortak_header" disabled className="font-bold bg-yellow-50">🪵 ORTAK MALZEMELER</SelectItem>
              <SelectItem value="agac_seren">🪵 Ağaç Seren</SelectItem>
              <SelectItem value="lake_yuzey_header" disabled className="font-bold bg-blue-50">🔵 LAKE - YÜZEY</SelectItem>
              <SelectItem value="lake_yuzey_mdf_6mm">📦 Lake - MDF 6mm (Kapalı)</SelectItem>
              <SelectItem value="lake_yuzey_mdf_18mm">📦 Lake - MDF 18mm (Camlı)</SelectItem>
              <SelectItem value="lake_kasa_header" disabled className="font-bold bg-blue-50">🔵 LAKE - KASA</SelectItem>
              <SelectItem value="lake_kasa_pp">🚪 Lake - PP Kasa</SelectItem>
              <SelectItem value="lake_pervaz_header" disabled className="font-bold bg-blue-50">🔵 LAKE - PERVAZ</SelectItem>
              <SelectItem value="lake_pervaz_pp">📐 Lake - PP Pervazlar (Tüm Çeşitler)</SelectItem>
              <SelectItem value="melamin_yuzey_header" disabled className="font-bold bg-green-50">🟢 MELAMİN - YÜZEY</SelectItem>
              <SelectItem value="melamin_yuzey">🎨 Melamin Yüzeyler (Tüm Çeşitler)</SelectItem>
              <SelectItem value="melamin_kasa_header" disabled className="font-bold bg-green-50">🟢 MELAMİN - KASA</SelectItem>
              <SelectItem value="melamin_kasa_pvc">🚪 Melamin - PVC Kasa</SelectItem>
              <SelectItem value="melamin_pervaz_header" disabled className="font-bold bg-green-50">🟢 MELAMİN - PERVAZ</SelectItem>
              <SelectItem value="melamin_pervaz_pvc">📐 Melamin - PVC Pervazlar (Tüm Çeşitler)</SelectItem>
              <SelectItem value="aksesuar_header" disabled className="font-bold bg-orange-50">🔧 AKSESUAR</SelectItem>
              <SelectItem value="aksesuar">🔧 Aksesuarlar</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Ürün Kodu *</Label>
          <Input
            value={formData.item_code}
            onChange={(e) => setFormData({...formData, item_code: e.target.value})}
            placeholder="SEREN_30X42_210"
            required
          />
        </div>
      </div>

      <div>
        <Label>Ürün Adı *</Label>
        <Input
          value={formData.item_name}
          onChange={(e) => setFormData({...formData, item_name: e.target.value})}
          placeholder="30x42mm 210cm Ağaç Seren"
          required
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Birim *</Label>
          <Select value={formData.unit} onValueChange={(value) => setFormData({...formData, unit: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="adet">Adet</SelectItem>
              <SelectItem value="metre">Metre</SelectItem>
              <SelectItem value="kg">Kilogram</SelectItem>
              <SelectItem value="litre">Litre</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Başlangıç Miktarı</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.quantity}
            onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
          />
        </div>
        <div>
          <Label>Minimum Stok</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.min_quantity}
            onChange={(e) => setFormData({...formData, min_quantity: parseFloat(e.target.value) || 0})}
          />
        </div>
      </div>

      <div>
        <Label>Teknik Özellikler</Label>
        <Textarea
          value={formData.specifications}
          onChange={(e) => setFormData({...formData, specifications: e.target.value})}
          placeholder="Ölçüler, materyal özellikleri vb."
          rows={3}
        />
      </div>

      <div>
        <Label>Notlar</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Ek notlar"
          rows={2}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          İptal
        </Button>
        <Button type="submit">
          {item ? 'Güncelle' : 'Ekle'}
        </Button>
      </div>
    </form>
  );
};

// Stock Transaction Form Component
const StockTransactionForm = ({ inventoryItem, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    inventory_id: inventoryItem.id,
    transaction_type: 'giris',
    quantity: 0,
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>İşlem Tipi *</Label>
        <Select value={formData.transaction_type} onValueChange={(value) => setFormData({...formData, transaction_type: value})}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="giris">Stok Girişi</SelectItem>
            <SelectItem value="cikis">Stok Çıkışı</SelectItem>
            <SelectItem value="duzeltme">Stok Düzeltme</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Miktar * ({inventoryItem.unit})</Label>
        <Input
          type="number"
          step="0.01"
          value={formData.quantity}
          onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
          placeholder="0"
          required
        />
        {formData.transaction_type === 'duzeltme' && (
          <p className="text-xs text-gray-600 mt-1">
            Düzeltme: Stok miktarı girdiğiniz değere ayarlanacak (Mevcut: {inventoryItem.quantity})
          </p>
        )}
      </div>

      <div>
        <Label>Açıklama</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="İşlem ile ilgili açıklama"
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          İptal
        </Button>
        <Button type="submit">
          Kaydet
        </Button>
      </div>
    </form>
  );
};

// Control Component (Kontrol Sayfası - Müşteri Takip Sistemi)
const Control = ({ sessionToken, currentUser }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState({});
  const [loadingOrders, setLoadingOrders] = useState({});
  const [sortBy, setSortBy] = useState('serial_number'); // 'serial_number' veya 'created_at'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' veya 'desc'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('visible'); // 'visible' veya 'hidden'
  const [hidingCustomer, setHidingCustomer] = useState(null);
  const [selectedCustomerForMovements, setSelectedCustomerForMovements] = useState(null);
  const [accountMovements, setAccountMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [isMovementsDialogOpen, setIsMovementsDialogOpen] = useState(false);
  const [customerExtraInfo, setCustomerExtraInfo] = useState({});
  const [loadingExtraInfo, setLoadingExtraInfo] = useState({});

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customers`, {
        params: {
          limit: 500,
          session_token: sessionToken
        }
      });
      
      // API yanıtı {customers: [...]} formatında
      let customerData = [];
      if (response.data && response.data.customers) {
        customerData = response.data.customers;
      } else if (Array.isArray(response.data)) {
        customerData = response.data;
      }
      
      console.log('Customers loaded:', customerData.length);
      setCustomers(customerData);
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
      toast.error('Müşteriler yüklenirken hata oluştu');
      setCustomers([]); // Hata durumunda boş array
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerOrders = async (customerId) => {
    if (customerOrders[customerId]) {
      // Zaten yüklenmişse tekrar yükleme
      return;
    }

    try {
      setLoadingOrders(prev => ({ ...prev, [customerId]: true }));
      
      // Müşterinin transaction'larını çek
      const response = await axios.get(
        `${API}/customer-order-timeline/${customerId}?session_token=${sessionToken}`
      );
      
      setCustomerOrders(prev => ({
        ...prev,
        [customerId]: response.data.orders || []
      }));
      
      // Ekstra bilgileri de çek
      fetchCustomerExtraInfo(customerId);
    } catch (error) {
      console.error('Sipariş bilgileri yüklenirken hata:', error);
      toast.error('Sipariş bilgileri yüklenirken hata oluştu');
    } finally {
      setLoadingOrders(prev => ({ ...prev, [customerId]: false }));
    }
  };

  const fetchCustomerExtraInfo = async (customerId) => {
    if (customerExtraInfo[customerId]) {
      return; // Zaten yüklendi
    }

    try {
      setLoadingExtraInfo(prev => ({ ...prev, [customerId]: true }));
      
      // Paralel olarak tüm bilgileri çek
      const [installationsRes, agendasRes] = await Promise.all([
        // Montaj bilgileri
        axios.get(`${API}/installation-schedules-with-sales`, {
          params: { session_token: sessionToken }
        }).catch(() => ({ data: [] })),
        
        // Ajanda notları
        axios.get(`${API}/agenda-notes`, {
          params: { session_token: sessionToken }
        }).catch(() => ({ data: [] }))
      ]);

      // Sadece bu müşteriye ait olanları filtrele
      const customerInstallations = (installationsRes.data || []).filter(inst => 
        inst.customer_id === customerId || inst.sale_details?.account_id === customerId
      );

      const customerAgendas = (agendasRes.data || []).filter(agenda => 
        agenda.customer_id === customerId
      );

      // Bekleyen notlar ve eksik işler installation'lardan çıkar
      const pendingNotes = customerInstallations
        .filter(inst => inst.collection_notes)
        .map(inst => ({
          date: inst.installation_date,
          note: inst.collection_notes,
          type: 'Bekleyen'
        }));

      const incompleteNotes = customerInstallations
        .filter(inst => inst.incomplete_work_notes)
        .map(inst => ({
          date: inst.installation_date,
          note: inst.incomplete_work_notes,
          type: 'Eksik/Yarım İş'
        }));

      setCustomerExtraInfo(prev => ({
        ...prev,
        [customerId]: {
          installations: customerInstallations,
          agendas: customerAgendas,
          pendingNotes,
          incompleteNotes
        }
      }));
    } catch (error) {
      console.error('Ekstra bilgiler yüklenirken hata:', error);
    } finally {
      setLoadingExtraInfo(prev => ({ ...prev, [customerId]: false }));
    }
  };

  const toggleCustomer = (customerId) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
    } else {
      setExpandedCustomer(customerId);
      fetchCustomerOrders(customerId);
    }
  };

  const handleHideCustomer = async (customerId, e) => {
    e.stopPropagation(); // Prevent row expansion
    
    try {
      setHidingCustomer(customerId);
      
      // Müşteriyi gizle (yeni endpoint)
      await axios.patch(
        `${API}/customers/${customerId}/visibility`,
        null,
        { 
          params: { 
            session_token: sessionToken,
            is_hidden: true
          } 
        }
      );
      
      toast.success('Müşteri gizlendi');
      
      // Müşteri listesini güncelle
      setCustomers(prevCustomers => 
        prevCustomers.map(c => 
          c.id === customerId ? { ...c, is_hidden: true } : c
        )
      );
    } catch (error) {
      console.error('Müşteri gizlenirken hata:', error);
      toast.error('Müşteri gizlenirken hata oluştu');
    } finally {
      setHidingCustomer(null);
    }
  };

  const handleShowCustomer = async (customerId, e) => {
    e.stopPropagation(); // Prevent row expansion
    
    try {
      setHidingCustomer(customerId);
      
      // Müşteriyi göster (yeni endpoint)
      await axios.patch(
        `${API}/customers/${customerId}/visibility`,
        null,
        { 
          params: { 
            session_token: sessionToken,
            is_hidden: false
          } 
        }
      );
      
      toast.success('Müşteri gösterildi');
      
      // Müşteri listesini güncelle
      setCustomers(prevCustomers => 
        prevCustomers.map(c => 
          c.id === customerId ? { ...c, is_hidden: false } : c
        )
      );
    } catch (error) {
      console.error('Müşteri gösterilirken hata:', error);
      toast.error('Müşteri gösterilirken hata oluştu');
    } finally {
      setHidingCustomer(null);
    }
  };

  const fetchAccountMovements = async (customer) => {
    try {
      setLoadingMovements(true);
      setSelectedCustomerForMovements(customer);
      setIsMovementsDialogOpen(true);
      
      const response = await axios.get(
        `${API}/customer-order-timeline/${customer.id}?session_token=${sessionToken}`
      );
      
      setAccountMovements(response.data.account_movements || []);
    } catch (error) {
      console.error('Hesap hareketleri yüklenirken hata:', error);
      toast.error('Hesap hareketleri yüklenirken hata oluştu');
      setAccountMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Müşterileri sırala ve filtrele
  const getSortedAndFilteredCustomers = () => {
    // Customers'ın array olduğundan emin ol
    if (!Array.isArray(customers)) {
      return [];
    }
    
    let filtered = [...customers];

    // Aktif sekmeye göre filtrele
    filtered = filtered.filter(c => {
      if (activeTab === 'visible') {
        return !c.is_hidden; // Gizli olmayanlar
      } else {
        return c.is_hidden === true; // Gizli olanlar
      }
    });

    // Arama filtresi
    if (searchTerm && searchTerm.trim()) {
      const search = searchTerm.trim().toLocaleLowerCase('tr-TR');
      filtered = filtered.filter(c => {
        const name = (c.name || '').toLocaleLowerCase('tr-TR');
        const surname = (c.surname || '').toLocaleLowerCase('tr-TR');
        const serialNumber = (c.serial_number || '').toLocaleLowerCase('tr-TR');
        const fullName = `${name} ${surname}`;
        
        return name.includes(search) || 
               surname.includes(search) || 
               serialNumber.includes(search) ||
               fullName.includes(search);
      });
    }

    // Sıralama
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'serial_number') {
        aVal = a.serial_number || '';
        bVal = b.serial_number || '';
      } else if (sortBy === 'created_at') {
        aVal = new Date(a.created_at || 0);
        bVal = new Date(b.created_at || 0);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return `₺${parseFloat(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const sortedCustomers = getSortedAndFilteredCustomers();
  
  // Sayaçları hesapla
  const visibleCount = customers.filter(c => !c.is_hidden).length;
  const hiddenCount = customers.filter(c => c.is_hidden).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kontrol - Müşteri Takip</h1>
          <p className="text-gray-600">Müşteri hesapları ve işlem geçmişi</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-lg px-4 py-2">
            Toplam: {customers.length}
          </Badge>
          <Badge variant={activeTab === 'visible' ? 'default' : 'outline'} className="text-lg px-4 py-2">
            Gösterilen: {visibleCount}
          </Badge>
          <Badge variant={activeTab === 'hidden' ? 'default' : 'outline'} className="text-lg px-4 py-2">
            Gizlenen: {hiddenCount}
          </Badge>
        </div>
      </div>

      {/* Sekme Butonları */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2 mb-4">
            <Button
              variant={activeTab === 'visible' ? 'default' : 'outline'}
              onClick={() => setActiveTab('visible')}
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-2" />
              Gösterilen Müşteriler ({visibleCount})
            </Button>
            <Button
              variant={activeTab === 'hidden' ? 'default' : 'outline'}
              onClick={() => setActiveTab('hidden')}
              className="flex-1"
            >
              <EyeOff className="h-4 w-4 mr-2" />
              Gizlenen Müşteriler ({hiddenCount})
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Ara</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Müşteri adı, cari kod..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>Sırala</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="serial_number">Cari Hesap Kodu</SelectItem>
                  <SelectItem value="created_at">Hesap Açılış Tarihi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sıralama Yönü</Label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Artan</SelectItem>
                  <SelectItem value="desc">Azalan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Müşteri Listesi */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {sortedCustomers.map((customer) => (
              <div key={customer.id} className="hover:bg-gray-50 transition-colors">
                {/* Müşteri Satırı */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleCustomer(customer.id)}
                >
                  <div className="grid grid-cols-5 gap-4 items-center">
                    <div>
                      <p className="text-xs text-gray-500">Açılış Tarihi</p>
                      <p className="font-medium">{formatDate(customer.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Cari Kod</p>
                      <p className="font-medium">{customer.serial_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Müşteri</p>
                      <p className="font-medium">{customer.name} {customer.surname}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Güncel Bakiye</p>
                      <p className={`font-bold ${(customer.balance || 0) > 0 ? 'text-red-600' : (customer.balance || 0) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                        {formatCurrency(customer.balance)}
                      </p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchAccountMovements(customer);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Hesap Hareketleri
                      </Button>
                      {activeTab === 'visible' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleHideCustomer(customer.id, e)}
                          disabled={hidingCustomer === customer.id}
                        >
                          {hidingCustomer === customer.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <EyeOff className="h-4 w-4 mr-1" />
                              Gizle
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleShowCustomer(customer.id, e)}
                          disabled={hidingCustomer === customer.id}
                        >
                          {hidingCustomer === customer.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-1" />
                              Göster
                            </>
                          )}
                        </Button>
                      )}
                      {expandedCustomer === customer.id ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Genişletilmiş Detay */}
                {expandedCustomer === customer.id && (
                  <div className="bg-gray-50 border-t p-4">
                    {loadingOrders[customer.id] || loadingExtraInfo[customer.id] ? (
                      <div className="flex justify-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-lg">Müşteri Detayları</h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedCustomer(null)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Gizle
                          </Button>
                        </div>

                        {/* Sekmeler */}
                        <Tabs defaultValue="orders" className="w-full">
                          <TabsList className="grid w-full grid-cols-5">
                            <TabsTrigger value="orders">
                              Satış Geçmişi ({customerOrders[customer.id]?.length || 0})
                            </TabsTrigger>
                            <TabsTrigger value="installations">
                              Montajlar ({customerExtraInfo[customer.id]?.installations?.length || 0})
                            </TabsTrigger>
                            <TabsTrigger value="pending">
                              Bekleyen ({customerExtraInfo[customer.id]?.pendingNotes?.length || 0})
                            </TabsTrigger>
                            <TabsTrigger value="agenda">
                              Ajanda ({customerExtraInfo[customer.id]?.agendas?.length || 0})
                            </TabsTrigger>
                            <TabsTrigger value="incomplete">
                              Eksik İşler ({customerExtraInfo[customer.id]?.incompleteNotes?.length || 0})
                            </TabsTrigger>
                          </TabsList>

                          {/* Satış Geçmişi */}
                          <TabsContent value="orders" className="space-y-2">
                            {customerOrders[customer.id]?.length > 0 ? (
                              customerOrders[customer.id].map((order, index) => (
                                <Card key={index} className="bg-white">
                                  <CardContent className="p-4">
                                    <div className="grid grid-cols-4 gap-4 items-center">
                                      <div>
                                        <p className="text-xs text-gray-500">Satış Tarihi</p>
                                        <p className="font-medium">{formatDate(order.timeline?.[0]?.date)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Sipariş Tutarı</p>
                                        <p className="font-bold text-blue-600">{formatCurrency(order.amount)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Model / Renk</p>
                                        <p className="font-medium">{order.model || 'N/A'} / {order.color || 'N/A'}</p>
                                      </div>
                                      <div className="flex justify-end">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedOrder(order);
                                            setIsOrderDetailOpen(true);
                                          }}
                                        >
                                          <FileText className="h-4 w-4 mr-1" />
                                          Detay
                                        </Button>
                                      </div>
                                    </div>
                                    {order.timeline && order.timeline.length > 0 && (
                                      <div className="mt-3 pt-3 border-t">
                                        <p className="text-xs text-gray-500 mb-2">İş Akışı</p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {order.timeline.map((step, idx) => (
                                            <div key={idx} className="flex items-center">
                                              <Badge variant={step.status === 'completed' ? 'default' : 'outline'} className="text-xs">
                                                {step.event}
                                              </Badge>
                                              {idx < order.timeline.length - 1 && (
                                                <ArrowRight className="h-3 w-3 text-gray-400 mx-1" />
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">Satış kaydı yok</div>
                            )}
                          </TabsContent>

                          {/* Montajlar */}
                          <TabsContent value="installations" className="space-y-2">
                            {customerExtraInfo[customer.id]?.installations?.length > 0 ? (
                              customerExtraInfo[customer.id].installations.map((inst, index) => (
                                <Card key={index} className="bg-white">
                                  <CardContent className="p-4">
                                    <div className="grid grid-cols-3 gap-4">
                                      <div>
                                        <p className="text-xs text-gray-500">Planlanan Tarih</p>
                                        <p className="font-medium">{formatDate(inst.installation_date)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Montajcı</p>
                                        <p className="font-medium">{inst.installer_name || 'Atanmadı'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Durum</p>
                                        <Badge>{inst.status || 'Bekliyor'}</Badge>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">Montaj kaydı yok</div>
                            )}
                          </TabsContent>

                          {/* Bekleyen Notlar */}
                          <TabsContent value="pending" className="space-y-2">
                            {customerExtraInfo[customer.id]?.pendingNotes?.length > 0 ? (
                              customerExtraInfo[customer.id].pendingNotes.map((note, index) => (
                                <Card key={index} className="bg-yellow-50 border-yellow-200">
                                  <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-yellow-900">{note.note}</p>
                                        <p className="text-xs text-yellow-700 mt-1">{formatDate(note.date)}</p>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">Bekleyen not yok</div>
                            )}
                          </TabsContent>

                          {/* Ajanda */}
                          <TabsContent value="agenda" className="space-y-2">
                            {customerExtraInfo[customer.id]?.agendas?.length > 0 ? (
                              customerExtraInfo[customer.id].agendas.map((agenda, index) => (
                                <Card key={index} className="bg-white">
                                  <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                      <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium">{agenda.title || agenda.notes}</p>
                                        <p className="text-xs text-gray-500 mt-1">{formatDate(agenda.date || agenda.created_at)}</p>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">Ajanda kaydı yok</div>
                            )}
                          </TabsContent>

                          {/* Eksik/Yarım İşler */}
                          <TabsContent value="incomplete" className="space-y-2">
                            {customerExtraInfo[customer.id]?.incompleteNotes?.length > 0 ? (
                              customerExtraInfo[customer.id].incompleteNotes.map((note, index) => (
                                <Card key={index} className="bg-red-50 border-red-200">
                                  <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-red-900">{note.note}</p>
                                        <p className="text-xs text-red-700 mt-1">{formatDate(note.date)}</p>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">Eksik iş kaydı yok</div>
                            )}
                          </TabsContent>
                        </Tabs>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sipariş Detay Dialog */}
      <Dialog open={isOrderDetailOpen} onOpenChange={setIsOrderDetailOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Sipariş Detayları</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Müşteri</Label>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                </div>
                <div>
                  <Label>Nihai Müşteri</Label>
                  <p className="font-medium">{selectedOrder.end_customer || 'N/A'}</p>
                </div>
                <div>
                  <Label>Model</Label>
                  <p className="font-medium">{selectedOrder.model || 'N/A'}</p>
                </div>
                <div>
                  <Label>Renk</Label>
                  <p className="font-medium">{selectedOrder.color || 'N/A'}</p>
                </div>
                <div>
                  <Label>Tutar</Label>
                  <p className="font-bold text-blue-600">{formatCurrency(selectedOrder.amount)}</p>
                </div>
                <div>
                  <Label>Durum</Label>
                  <Badge>{selectedOrder.current_status || 'Bilinmiyor'}</Badge>
                </div>
              </div>
              <div>
                <Label>Açıklama</Label>
                <p className="text-sm bg-gray-50 p-3 rounded">{selectedOrder.description || 'Açıklama yok'}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hesap Hareketleri Dialog */}
      <Dialog open={isMovementsDialogOpen} onOpenChange={setIsMovementsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Hesap Hareketleri - {selectedCustomerForMovements?.name} {selectedCustomerForMovements?.surname}
            </DialogTitle>
            <DialogDescription>
              Cari Kod: {selectedCustomerForMovements?.serial_number} | 
              Bakiye: <span className={`font-bold ${(selectedCustomerForMovements?.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(selectedCustomerForMovements?.balance)}
              </span>
            </DialogDescription>
          </DialogHeader>
          
          {loadingMovements ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : accountMovements.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium">Toplam {accountMovements.length} işlem</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left">Tarih</th>
                      <th className="px-4 py-3 text-left">İşlem Tipi</th>
                      <th className="px-4 py-3 text-left">Açıklama</th>
                      <th className="px-4 py-3 text-left">Nihai Müşteri</th>
                      <th className="px-4 py-3 text-right">Borç</th>
                      <th className="px-4 py-3 text-right">Alacak</th>
                      <th className="px-4 py-3 text-right">Bakiye</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {accountMovements.map((movement, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDate(movement.date)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={
                            movement.type === 'Satış' || movement.type === 'Diğer Satış' ? 'destructive' :
                            movement.type === 'Tahsilat' || movement.type === 'Temlik' ? 'default' :
                            'secondary'
                          }>
                            {movement.type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 max-w-xs truncate">
                          {movement.description}
                        </td>
                        <td className="px-4 py-3">
                          {movement.end_customer || '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-red-600 font-medium">
                          {movement.debit > 0 ? formatCurrency(movement.debit) : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600 font-medium">
                          {movement.credit > 0 ? formatCurrency(movement.credit) : '-'}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${movement.balance > 0 ? 'text-red-600' : movement.balance < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          {formatCurrency(movement.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Hesap hareketi bulunamadı</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Expenses Component (Gider Yönetimi)
const Expenses = ({ sessionToken }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    payment_method: "safe",
    expense_date: new Date().toISOString().split('T')[0]
  });

  const EXPENSES_PER_PAGE = 50;
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseRefreshKey, setExpenseRefreshKey] = useState(0);
  const expenseFiltersRef = useRef({ start_date: "", end_date: "", category_id: "" });
  const fetchExpenseCategories = () => axios.get(`${API}/expense-categories`)
    .then((r) => setExpenseCategories(r.data || [])).catch((e) => console.error(e));
  useEffect(() => { fetchExpenseCategories(); }, []);
  const handleExpenseFilters = (f) => {
    const changed = JSON.stringify(f) !== JSON.stringify(expenseFiltersRef.current);
    expenseFiltersRef.current = f;
    if (changed) fetchExpenses(1, expenseSearch);
  };
  const [expensePage, setExpensePage] = useState(1);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [expenseTotalAmount, setExpenseTotalAmount] = useState(0);
  const [expenseSearch, setExpenseSearch] = useState("");
  const [expenseFetching, setExpenseFetching] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => fetchExpenses(1, expenseSearch), expenseSearch ? 400 : 0);
    return () => clearTimeout(t);
  }, [expenseSearch]);

  const fetchExpenses = async (page = expensePage, q = expenseSearch) => {
    try {
      setExpenseFetching(true);
      const response = await axios.get(`${API}/expenses`, {
        params: { limit: EXPENSES_PER_PAGE, skip: (page - 1) * EXPENSES_PER_PAGE, q: q.trim(), ...expenseFiltersRef.current }
      });
      setExpenses(response.data.expenses || []);
      setExpenseTotal(response.data.total || 0);
      setExpenseTotalAmount(response.data.total_amount || 0);
      setExpensePage(page);
    } catch (error) {
      console.error("Giderler yüklenirken hata:", error);
      toast.error("Giderler yüklenirken hata oluştu");
    } finally {
      setExpenseFetching(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_id) {
      toast.error("Lütfen gider kategorisi seçin");
      return;
    }
    try {
      const expense_data = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        category_id: formData.category_id,
        expense_date: new Date(formData.expense_date).toISOString()
      };

      await axios.post(`${API}/expenses`, expense_data);
      toast.success("Gider kaydı eklendi");
      setExpenseRefreshKey((k) => k + 1);
      fetchExpenseCategories();
      setIsDialogOpen(false);
      setFormData({
        description: "",
        amount: "",
        payment_method: "safe",
        expense_date: new Date().toISOString().split('T')[0]
      });
      fetchExpenses();
    } catch (error) {
      console.error("Gider eklenirken hata:", error);
      toast.error(error.response?.data?.detail || "Gider eklenirken hata oluştu");
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount,
      payment_method: expense.payment_method,
      category_id: expense.category_id || "",
      expense_date: new Date(expense.expense_date).toISOString().split('T')[0]
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.category_id) {
      toast.error("Lütfen gider kategorisi seçin");
      return;
    }
    setPendingAction({
      type: 'edit',
      data: {
        description: formData.description,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        category_id: formData.category_id,
        expense_date: new Date(formData.expense_date).toISOString()
      }
    });
    setIsPasswordDialogOpen(true);
  };

  const handleDelete = async () => {
    setPendingAction({
      type: 'delete',
      expense: expenseToDelete
    });
    setDeleteConfirmOpen(false);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async (password) => {
    try {
      if (pendingAction.type === 'delete') {
        await axios.delete(`${API}/expenses/${pendingAction.expense.id}`, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Gider kaydı silindi");
        setExpenseToDelete(null);
        fetchExpenses();
      } else if (pendingAction.type === 'edit') {
        await axios.put(`${API}/expenses/${editingExpense.id}`, pendingAction.data, {
          params: { 
            secure_password: password,
            session_token: sessionToken 
          }
        });
        toast.success("Gider kaydı güncellendi");
        setExpenseRefreshKey((k) => k + 1);
        fetchExpenseCategories();
        setIsEditDialogOpen(false);
        setEditingExpense(null);
        setFormData({
          description: "",
          amount: "",
          payment_method: "safe",
          expense_date: new Date().toISOString().split('T')[0]
        });
        fetchExpenses();
      }
      
      setIsPasswordDialogOpen(false);
      setPendingAction(null);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Güvenlik şifresi hatalı!");
      } else {
        console.error("İşlem sırasında hata:", error);
        toast.error("İşlem sırasında hata oluştu");
      }
    }
  };

  const openDeleteConfirm = (expense) => {
    setExpenseToDelete(expense);
    setDeleteConfirmOpen(true);
  };

  const getPaymentMethodBadge = (method) => {
    return method === 'safe' ? 
      <Badge className="bg-purple-100 text-purple-800">Kasa</Badge> : 
      <Badge className="bg-blue-100 text-blue-800">Banka</Badge>;
  };

  // Dialog açma/kapama fonksiyonu
  const handleOpenDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setFormData({
        description: "",
        amount: "",
        payment_method: "safe",
        expense_date: new Date().toISOString().split('T')[0]
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gider Yönetimi</h1>
          <p className="text-muted-foreground">Tüm gider hareketlerinizi yönetin</p>
        </div>
        <div className="flex gap-2">
        <ExpenseCategoryManager api={API} sessionToken={sessionToken} categories={expenseCategories} onChanged={fetchExpenseCategories} />
        <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
          <DialogTrigger asChild>
            <Button data-testid="expense-new-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Gider
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Gider Kaydı</DialogTitle>
              <DialogDescription>
                Gider bilgilerini girin. Ödeme otomatik olarak kasa/banka hareketlerine kaydedilecek.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="expense_date">Gider Tarihi</Label>
                <Input
                  type="date"
                  id="expense_date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Gider Açıklaması</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Gider açıklaması (örn: Ofis kirası, elektrik faturası, yakıt gideri...)"
                  required
                />
              </div>
              <div>
                <Label htmlFor="amount">Tutar (TL)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="0.00"
                  required
                />
              </div>
              <ExpenseCategorySelect categories={expenseCategories} value={formData.category_id}
                onChange={(v) => setFormData({ ...formData, category_id: v })} id="expense_category" />
              <div>
                <Label htmlFor="payment_method">Ödeme Yöntemi</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ödeme yöntemi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safe">Kasa</SelectItem>
                    <SelectItem value="bank">Banka</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit">
                  <Plus className="h-4 w-4 mr-2" />
                  Gider Ekle
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Gider Listesi */}
      <ExpenseFilterBar api={API} categories={expenseCategories} onChange={handleExpenseFilters} refreshKey={expenseRefreshKey} />

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle>Gider Hareketleri</CardTitle>
            <CardDescription>
              Tüm gider kayıtları ve ödeme bilgileri
              <span className="ml-2 font-semibold text-red-600" data-testid="expenses-total-amount">
                Toplam: ₺{Number(expenseTotalAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Gider açıklaması ara..."
              value={expenseSearch}
              onChange={(e) => setExpenseSearch(e.target.value)}
              className="pl-8"
              data-testid="expenses-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <div className="text-center py-8" data-testid="expenses-empty">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">{expenseSearch.trim() ? "Aramaya uygun gider bulunamadı" : "Henüz gider kaydı yok"}</p>
              <p className="text-sm text-gray-400">İlk gideri eklemek için "Yeni Gider" butonunu kullanın</p>
            </div>
          ) : (
            <div className="space-y-4">
              {expenses.map((expense) => (
                <div key={expense.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 rounded-full bg-red-500" /> {/* Giderler hep çıkış */}
                    <div className="flex-1">
                      <p className="font-medium">
                        {expense.description}
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${expense.category_name ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-800'}`} data-testid={`expense-category-badge-${expense.id}`}>
                          {expense.category_name || 'Kategorisiz'}
                        </span>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(expense.expense_date).toLocaleDateString('tr-TR')} • 
                        {new Date(expense.created_at).toLocaleTimeString('tr-TR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getPaymentMethodBadge(expense.payment_method)}
                    <p className="text-lg font-bold text-red-600">
                      -₺{Math.abs(expense.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`expense-edit-btn-${expense.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDeleteConfirm(expense)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`expense-delete-btn-${expense.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Sil
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <PaginationBar
            page={expensePage}
            total={expenseTotal}
            perPage={EXPENSES_PER_PAGE}
            onPageChange={(p) => fetchExpenses(p, expenseSearch)}
            loading={expenseFetching}
            label="gider"
            search={expenseSearch.trim()}
            testIdPrefix="expenses-pagination"
          />
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gider Düzenle</DialogTitle>
            <DialogDescription>
              Gider bilgilerini güncelleyin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label htmlFor="edit_expense_date">Gider Tarihi</Label>
              <Input
                type="date"
                id="edit_expense_date"
                value={formData.expense_date}
                onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_description">Gider Açıklaması</Label>
              <Textarea
                id="edit_description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Gider açıklaması"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_amount">Tutar (TL)</Label>
              <Input
                type="number"
                step="0.01"
                id="edit_amount"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                required
              />
            </div>
            <ExpenseCategorySelect categories={expenseCategories} value={formData.category_id}
              onChange={(v) => setFormData({ ...formData, category_id: v })} id="edit_expense_category" />
            <div>
              <Label htmlFor="edit_payment_method">Ödeme Yöntemi</Label>
              <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Ödeme yöntemi seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="safe">Kasa</SelectItem>
                  <SelectItem value="bank">Banka</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                <Edit className="h-4 w-4 mr-2" />
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gider Sil</DialogTitle>
            <DialogDescription>
              Bu gider kaydını silmek istediğinizden emin misiniz?
            </DialogDescription>
          </DialogHeader>
          {expenseToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p><strong>Açıklama:</strong> {expenseToDelete.description}</p>
              <p><strong>Tutar:</strong> ₺{expenseToDelete.amount?.toLocaleString('tr-TR')}</p>
              <p><strong>Tarih:</strong> {new Date(expenseToDelete.expense_date).toLocaleDateString('tr-TR')}</p>
              <p><strong>Ödeme:</strong> {expenseToDelete.payment_method === 'safe' ? 'Kasa' : 'Banka'}</p>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SecurePasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title="Güvenlik Onayı"
        message="Bu işlemi yapmak için admin şifresini girin"
      />
    </div>
  );
};

// Simple bayi components that are under development
const BayiInternalDashboard = () => <div className="p-8 text-center"><h1 className="text-2xl">İç Muhasebe Dashboard (Geliştiriliyor)</h1></div>;
const BayiInternalCustomers = () => <div className="p-8 text-center"><h1 className="text-2xl">Müşterilerim (Geliştiriliyor)</h1></div>;
const BayiInternalSuppliers = () => <div className="p-8 text-center"><h1 className="text-2xl">Tedarikçilerim (Geliştiriliyor)</h1></div>;
const BayiInternalSales = () => <div className="p-8 text-center"><h1 className="text-2xl">Satış (Geliştiriliyor)</h1></div>;
const BayiInternalPurchases = () => <div className="p-8 text-center"><h1 className="text-2xl">Mal Alış (Geliştiriliyor)</h1></div>;
const BayiInternalCollections = () => <div className="p-8 text-center"><h1 className="text-2xl">Tahsilat (Geliştiriliyor)</h1></div>;
const BayiInternalPayments = () => <div className="p-8 text-center"><h1 className="text-2xl">Tediye (Geliştiriliyor)</h1></div>;
const BayiInternalDataManagement = () => <div className="p-8 text-center"><h1 className="text-2xl">Veri Yönetimi (Geliştiriliyor)</h1></div>;

// Admin Activities Component - Gelişmiş versiyon
const AdminActivities = ({ sessionToken, currentUser }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = Yeniden Eskiye, 'asc' = Eskiden Yeniye
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const API = `${BACKEND_URL}/api`;
  const limit = 20;

  useEffect(() => {
    fetchActivities();
  }, [page, sortOrder]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/activities`, {
        params: {
          session_token: sessionToken,
          page: page,
          limit: limit
        }
      });
      
      if (response.data && response.data.activities) {
        let sortedActivities = response.data.activities;
        
        // Frontend'de sıralama
        sortedActivities.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        
        setActivities(sortedActivities);
        
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.total_pages);
        }
      }
    } catch (error) {
      console.error('Etkinlikler yüklenirken hata:', error);
      toast.error('Etkinlikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Aktivite tipine göre ikon ve renk
  const getActivityIcon = (activityType) => {
    const iconMap = {
      'customer_create': { icon: UserPlus, color: 'text-blue-500', bg: 'bg-blue-50' },
      'customer_update': { icon: UserCheck, color: 'text-blue-600', bg: 'bg-blue-50' },
      'supplier_create': { icon: Package, color: 'text-purple-500', bg: 'bg-purple-50' },
      'supplier_update': { icon: Package, color: 'text-purple-600', bg: 'bg-purple-50' },
      'order_create': { icon: ShoppingCart, color: 'text-green-500', bg: 'bg-green-50' },
      'collection_create': { icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-50' },
      'collection_update': { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
      'collection_delete': { icon: Trash2, color: 'text-red-500', bg: 'bg-red-50' },
      'payment_create': { icon: CreditCard, color: 'text-indigo-500', bg: 'bg-indigo-50' },
      'payment_update': { icon: CreditCard, color: 'text-indigo-600', bg: 'bg-indigo-50' },
      'payment_delete': { icon: Trash2, color: 'text-red-500', bg: 'bg-red-50' },
      'production_status_change': { icon: Factory, color: 'text-orange-500', bg: 'bg-orange-50' },
      'production_transfer': { icon: Factory, color: 'text-orange-600', bg: 'bg-orange-50' },
      'installation_status_change': { icon: Wrench, color: 'text-teal-500', bg: 'bg-teal-50' },
      'installation_complete': { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
      'user_login': { icon: LogIn, color: 'text-gray-500', bg: 'bg-gray-50' },
      'user_logout': { icon: LogOut, color: 'text-gray-500', bg: 'bg-gray-50' }
    };
    return iconMap[activityType] || { icon: AlertCircle, color: 'text-gray-500', bg: 'bg-gray-50' };
  };

  // Aktivite detayını göster
  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Etkinlikler yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Sistem Etkinlikleri</h1>
        
        {/* Sıralama Seçici */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Sıralama:</label>
          <select
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
              setPage(1); // Sıralama değiştiğinde ilk sayfaya dön
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="desc">Yeniden Eskiye</option>
            <option value="asc">Eskiden Yeniye</option>
          </select>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Son Etkinlikler</CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Henüz etkinlik kaydı yok.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity) => {
                const iconInfo = getActivityIcon(activity.activity_type);
                const IconComponent = iconInfo.icon;
                
                return (
                  <div 
                    key={activity.id} 
                    className={`p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer ${iconInfo.bg}`}
                    onClick={() => handleActivityClick(activity)}
                  >
                    <div className="flex items-start gap-3">
                      {/* İkon */}
                      <div className={`${iconInfo.bg} p-2 rounded-full`}>
                        <IconComponent className={`w-5 h-5 ${iconInfo.color}`} />
                      </div>
                      
                      {/* İçerik */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-900">
                            {activity.user_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(activity.created_at).toLocaleString('tr-TR')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{activity.description}</p>
                        
                        {/* Hedef isim - tıklanabilir */}
                        {activity.target_name && (
                          <div className="mt-2">
                            <span className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                              🔗 {activity.target_name} → Detay Gör
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Sayfalama */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Önceki
              </button>
              <span className="text-sm text-gray-600">
                Sayfa {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sonraki
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aktivite Detay Modalı */}
      {showDetailModal && selectedActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between border-b pb-4">
                <h2 className="text-xl font-bold">Aktivite Detayı</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Kullanıcı:</label>
                  <p className="text-gray-900">{selectedActivity.user_name} ({selectedActivity.user_role})</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-gray-600">İşlem Tipi:</label>
                  <p className="text-gray-900">{selectedActivity.activity_type}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-gray-600">Açıklama:</label>
                  <p className="text-gray-900">{selectedActivity.description}</p>
                </div>
                
                {selectedActivity.target_name && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Hedef:</label>
                    <p className="text-gray-900">{selectedActivity.target_name}</p>
                  </div>
                )}
                
                {selectedActivity.amount && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Tutar:</label>
                    <p className="text-gray-900">{selectedActivity.amount.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</p>
                  </div>
                )}
                
                {(selectedActivity.old_value || selectedActivity.new_value) && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Değişiklik:</label>
                    <p className="text-gray-900">
                      {selectedActivity.old_value} → {selectedActivity.new_value}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-semibold text-gray-600">Tarih:</label>
                  <p className="text-gray-900">{new Date(selectedActivity.created_at).toLocaleString('tr-TR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    second: '2-digit' 
                  })}</p>
                </div>
              </div>
              
              <div className="flex justify-end border-t pt-4">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(localStorage.getItem('session_token'));
  const [loading, setLoading] = useState(true);

  const API = `${BACKEND_URL}/api`;

  // loginCompleted ref: handleLogin başarılı olduğunda true yapılır,
  // böylece mount-time checkAuthStatus login'i ezmez
  const loginCompletedRef = React.useRef(false);

  useEffect(() => {
    // Her zaman mount'ta çalıştır - eski/geçersiz token varsa temizler
    checkAuthStatus();
  }, []); // Empty dependency array - run only once on mount

  const checkAuthStatus = async () => {
    // Login tamamlandıysa, bu check'i atla (race condition önlemi)
    if (loginCompletedRef.current) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('session_token');
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me?session_token=${token}`);
      // Login arada tamamlandıysa, onun state'ini ezme
      if (!loginCompletedRef.current) {
        setCurrentUser(response.data);
        setSessionToken(token);
      }
    } catch (error) {
      // Geçersiz token - temizle
      if (!loginCompletedRef.current) {
        localStorage.removeItem('session_token');
        setSessionToken(null);
        setCurrentUser(null);
      }
    }
    setLoading(false);
  };

  const handleLogin = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { user, session_token, installer_info } = response.data;
      
      // Installation personnel için installer_info'yu user object'ine ekle
      if (user.role === 'installation' && installer_info) {
        user.installer_id = installer_info.installer_id;
        user.installer_name = installer_info.installer_name;
      }
      
      // Race condition önlemi: önce ref'i set et
      loginCompletedRef.current = true;
      
      // localStorage'a yaz
      localStorage.setItem('session_token', session_token);
      
      // State'i güncelle
      setCurrentUser(user);
      setSessionToken(session_token);
      setLoading(false);
      
      toast.success(`Hoş geldiniz, ${user.full_name}!`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
      return false;
    }
  };

  const handleLogout = async () => {
    try {
      if (sessionToken) {
        await axios.post(`${API}/auth/logout?session_token=${sessionToken}`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setCurrentUser(null);
    setSessionToken(null);
    localStorage.removeItem('session_token');
    toast.success('Başarıyla çıkış yapıldı');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Show customer portal if user is a customer
  if (currentUser.role === 'customer') {
    return (
      <BrowserRouter>
        <CustomerPortal 
          currentUser={currentUser} 
          sessionToken={sessionToken}
          onLogout={handleLogout}
        />
      </BrowserRouter>
    );
  }

  // Show main app if authenticated (admin, sales, production, etc.)
  return (
    <BrowserRouter>
      <MainApp 
        currentUser={currentUser} 
        sessionToken={sessionToken}
        onLogout={handleLogout}
      />
    </BrowserRouter>
  );
};



// Customer Portal Component - Müşteri Portalı
const CustomerPortal = ({ currentUser, sessionToken, onLogout }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [myOrders, setMyOrders] = useState([]);
  const [myProductions, setMyProductions] = useState([]);
  const [myInstallations, setMyInstallations] = useState([]);
  const [accountStatement, setAccountStatement] = useState(null);
  const [myPreSales, setMyPreSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPreSale, setShowNewPreSale] = useState(false);
  const [preSaleForm, setPreSaleForm] = useState({
    end_customer: '', production_line: 'lake', model: '', color: '',
    door_handle: '', surface: '', installation: '', installation_location: '',
    inner_frame: '', outer_frame: '', measurements: '', details: '',
    target_installation_date: '', notes: ''
  });
  const [preSaleSubmitting, setPreSaleSubmitting] = useState(false);
  const [psMeasurements, setPsMeasurements] = useState([
    { id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 },
    { id: 2, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 },
    { id: 3, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }
  ]);
  const [psShowQuantity, setPsShowQuantity] = useState(false);
  const [editingPreSale, setEditingPreSale] = useState(null);
  const [editPsForm, setEditPsForm] = useState({});
  const [editPsMeasurements, setEditPsMeasurements] = useState([]);
  const [editPsSubmitting, setEditPsSubmitting] = useState(false);
  // Dropdown seçenekleri - Satış ekranıyla birebir aynı
  const psModelOptions = ["QUEEN", "ZEUS DİAMOND", "MERT P1", "MERT P2", "MERT P3", "NEW YORK", "TOKYO", "BERLİN", "PARİS", "VENEDİK", "HONG KONG", "LİZBON", "PETRA", "SİNGAPUR", "LYON PLUS", "FLORANSA", "SİDNEY", "BUDAPEŞTE", "SAN FRANSİNCO", "PRAG", "LYON", "RİO", "İSTANBUL", "MİAMİ", "BLADE", "ROMA", "OSLO", "BARCELONA", "BASEL", "SELANİK", "SARMAŞIK", "WANKEL", "LALE", "PLEKSİ A", "PALMİYE", "HİRA", "NEWA", "MODEL X", "PATRAS", "DALGA", "DİKDÖRTGEN", "ARTI", "PORTO", "ZAMBAK", "ATLANTİS", "ZEUS", "MELAMİN MERDİVEN", "MELAMİN TEK GÖBEK"];
  const psColorOptions = ["BEYAZ", "K-168", "RALL 7044"];
  const psDoorHandleOptions = ["SİYAH PRİZMA", "KROM"];
  const psSurfaceOptions = ["6mm", "8mm", "10mm", "18mm", "6mm-18mm", "MELAMİN"];
  const psInstallationOptions = ["DAHİL", "HARİÇ"];
  const psInstallationLocationOptions = ["BOŞ", "DOLU"];
  const psInnerFrameOptions = ["DÜZ", "DEKORATİF"];
  const psOuterFrameOptions = ["DÜZ", "DEKORATİF"];
  const [psCustom, setPsCustom] = useState({ model: false, color: false, door_handle: false, surface: false, installation: false, installation_location: false, inner_frame: false, outer_frame: false });
  const psWidthOptions = [61,66,71,76,81,86,91,96,101,106,111,116,121,126,131,136,141,146,151,156,161,166,171,176,181,186,191,196];
  const psHeightOptions = [190,191,192,193,194,195,196,197,198,199,200,201,202,203,204,205,206,207,208,209];
  const psFrameOptions = [8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30];
  const psDirectionOptions = ["SAĞ", "SOL", "DIŞA SAĞ", "DIŞA SOL", "SÜRGÜLÜ"];
  const psGlassOptions = ["CAMLI", "KAPALI"];
  const psRoomOptions = ["YATAK ODASI", "OTURMA ODASI", "MUTFAK", "BANYO", "EVEBEYN BANYOSU", "HOL"];
  
  // Detail Dialog States
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Installation filter state
  const [installationFilter, setInstallationFilter] = useState('pending');
  const [paintFilter, setPaintFilter] = useState('all'); // 'all', 'painted', 'unpainted'

  useEffect(() => {
    if (activeView === 'dashboard') {
      fetchDashboard();
    } else if (activeView === 'orders') {
      fetchMyOrders();
    } else if (activeView === 'productions') {
      fetchMyProductions();
    } else if (activeView === 'installations') {
      fetchMyInstallations();
    } else if (activeView === 'account') {
      fetchAccountStatement();
    } else if (activeView === 'presales') {
      fetchMyPreSales();
    }
  }, [activeView]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customer-portal/dashboard?session_token=${sessionToken}`);
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Dashboard yüklenirken hata:", error);
      toast.error("Dashboard yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchMyOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customer-portal/my-orders?session_token=${sessionToken}`);
      setMyOrders(response.data.orders || []);
      setLoading(false);
    } catch (error) {
      console.error("Siparişler yüklenirken hata:", error);
      toast.error("Siparişler yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchMyProductions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customer-portal/my-productions?session_token=${sessionToken}`);
      setMyProductions(response.data.productions || []);
      setLoading(false);
    } catch (error) {
      console.error("Üretim bilgileri yüklenirken hata:", error);
      toast.error("Üretim bilgileri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchMyInstallations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customer-portal/my-installations?session_token=${sessionToken}`);
      setMyInstallations(response.data.installations || []);
      setLoading(false);
    } catch (error) {
      console.error("Montaj bilgileri yüklenirken hata:", error);
      toast.error("Montaj bilgileri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchAccountStatement = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customer-portal/account-statement?session_token=${sessionToken}`);
      setAccountStatement(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Hesap hareketleri yüklenirken hata:", error);
      toast.error("Hesap hareketleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const fetchMyPreSales = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customer-portal/my-pre-sales?session_token=${sessionToken}`);
      setMyPreSales(response.data.pre_sales || []);
      setLoading(false);
    } catch (error) {
      console.error("Ön satışlar yüklenirken hata:", error);
      setLoading(false);
    }
  };

  const submitPreSale = async () => {
    if (!preSaleForm.production_line) {
      toast.error("Üretim hattı seçilmeli");
      return;
    }
    try {
      setPreSaleSubmitting(true);
      const filledMeasurements = psMeasurements.filter(m => m.width || m.height);
      const payload = {
        ...preSaleForm,
        measurements: filledMeasurements.length > 0 ? JSON.stringify(filledMeasurements) : ""
      };
      await axios.post(`${API}/customer-portal/pre-sales?session_token=${sessionToken}`, payload);
      toast.success("Sipariş talebi gönderildi!");
      setShowNewPreSale(false);
      setPreSaleForm({
        end_customer: '', production_line: 'lake', model: '', color: '',
        door_handle: '', surface: '', installation: '', installation_location: '',
        inner_frame: '', outer_frame: '', measurements: '', details: '',
        target_installation_date: '', notes: ''
      });
      setPsMeasurements([
        { id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 },
        { id: 2, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 },
        { id: 3, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }
      ]);
      setActiveView('presales');
      fetchMyPreSales();
      setPreSaleSubmitting(false);
    } catch (error) {
      toast.error("Sipariş talebi oluşturulurken hata oluştu");
      setPreSaleSubmitting(false);
    }
  };

  const openEditPreSale = (ps) => {
    setEditingPreSale(ps);
    setEditPsForm({
      end_customer: ps.end_customer || '',
      production_line: ps.production_line || 'lake',
      model: ps.model || '',
      color: ps.color || '',
      door_handle: ps.door_handle || '',
      surface: ps.surface || '',
      installation: ps.installation || '',
      installation_location: ps.installation_location || '',
      inner_frame: ps.inner_frame || '',
      outer_frame: ps.outer_frame || '',
      details: ps.details || '',
      notes: ps.notes || '',
      target_installation_date: ps.target_installation_date || ''
    });
    try {
      const m = ps.measurements ? JSON.parse(ps.measurements) : [];
      setEditPsMeasurements(m.length > 0 ? m : [{ id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
    } catch {
      setEditPsMeasurements([{ id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
    }
  };

  const submitEditPreSale = async () => {
    if (!editPsForm.production_line) { toast.error("Üretim hattı seçilmeli"); return; }
    try {
      setEditPsSubmitting(true);
      const filled = editPsMeasurements.filter(m => m.width || m.height);
      const payload = { ...editPsForm, measurements: filled.length > 0 ? JSON.stringify(filled) : "" };
      await axios.put(`${API}/customer-portal/pre-sales/${editingPreSale.id}?session_token=${sessionToken}`, payload);
      toast.success("Sipariş güncellendi!");
      setEditingPreSale(null);
      fetchMyPreSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncelleme başarısız");
    } finally {
      setEditPsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'beklemede': { color: 'bg-yellow-100 text-yellow-800', text: 'Beklemede' },
      'uretimde': { color: 'bg-blue-100 text-blue-800', text: 'Üretimde' },
      'hazir': { color: 'bg-green-100 text-green-800', text: 'Hazır' },
      'planlandi': { color: 'bg-indigo-100 text-indigo-800', text: 'Planlandı' },
      'tamamlandi': { color: 'bg-green-100 text-green-800', text: 'Tamamlandı' }
    };
    const badge = statusMap[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <Badge className={badge.color}>{badge.text}</Badge>;
  };


  const handleViewOrderDetails = async (transactionId) => {
    try {
      setDetailsLoading(true);
      setIsDetailDialogOpen(true);
      const response = await axios.get(`${API}/transactions/${transactionId}/details?session_token=${sessionToken}`);
      setSelectedOrderDetails(response.data);
      setDetailsLoading(false);
    } catch (error) {
      console.error("Sipariş detayları yüklenirken hata:", error);
      toast.error("Sipariş detayları yüklenirken hata oluştu");
      setDetailsLoading(false);
      setIsDetailDialogOpen(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-600 text-white px-4 py-2 rounded font-bold text-sm">
                ESKAY AHŞAP
              </div>
              <h1 className="text-xl font-bold text-gray-900">Müşteri Portalı</h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{currentUser.full_name}</p>
                <p className="text-xs text-gray-500">Müşteri</p>
              </div>
              <Button variant="outline" size="sm" onClick={onLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4 py-3 overflow-x-auto">
            <button
              onClick={() => setActiveView('dashboard')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeView === 'dashboard'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Home className="h-4 w-4 inline mr-2" />
              Ana Sayfa
            </button>
            <button
              onClick={() => { setShowNewPreSale(true); setActiveView('new-order'); }}
              className="px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap bg-green-600 text-white hover:bg-green-700"
              data-testid="new-presale-btn"
            >
              <Plus className="h-4 w-4 inline mr-2" />
              Yeni Sipariş
            </button>
            <button
              onClick={() => setActiveView('presales')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeView === 'presales'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="presales-tab"
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Taleplerim
            </button>
            <button
              onClick={() => setActiveView('orders')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeView === 'orders'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ShoppingCart className="h-4 w-4 inline mr-2" />
              Siparişlerim
            </button>
            <button
              onClick={() => setActiveView('productions')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeView === 'productions'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Package className="h-4 w-4 inline mr-2" />
              Üretim Durumu
            </button>
            <button
              onClick={() => setActiveView('installations')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeView === 'installations'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Wrench className="h-4 w-4 inline mr-2" />
              Montaj Durumu
            </button>
            <button
              onClick={() => setActiveView('account')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeView === 'account'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Receipt className="h-4 w-4 inline mr-2" />
              Cari Hesap
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Yükleniyor...</div>
          </div>
        ) : (
          <>
            {/* Dashboard View */}
            {activeView === 'dashboard' && dashboardData && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Hoş Geldiniz</h2>
                
                {/* Mali Özet */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Toplam Satış</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(dashboardData.financial_summary?.total_sales)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Toplam Tahsilat</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(dashboardData.financial_summary?.total_collected)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Kalan Bakiye</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={`text-2xl font-bold ${(dashboardData.financial_summary?.remaining_balance || 0) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(dashboardData.financial_summary?.remaining_balance)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(dashboardData.financial_summary?.remaining_balance || 0) >= 0 ? 'Borç' : 'Alacak'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Sipariş Özeti */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Toplam Sipariş</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-gray-900">
                        {dashboardData.order_counts?.total_orders || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Üretimdeki Siparişler</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-blue-600">
                        {dashboardData.order_counts?.in_production || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Montajdaki Siparişler</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-indigo-600">
                        {dashboardData.order_counts?.in_installation || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Bilgi Kartı */}
                <Card>
                  <CardHeader>
                    <CardTitle>Müşteri Bilgileriniz</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Ad Soyad</p>
                        <p className="font-medium">{dashboardData.customer?.name} {dashboardData.customer?.surname}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Cari Hesap No</p>
                        <p className="font-medium">{dashboardData.customer?.serial_number || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Telefon</p>
                        <p className="font-medium">{dashboardData.customer?.phone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{dashboardData.customer?.email || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* New Pre-Sale Form */}
            {activeView === 'new-order' && showNewPreSale && (
              <div className="space-y-6" data-testid="presale-form">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Yeni Sipariş Talebi</h2>
                  <Button variant="outline" onClick={() => { setShowNewPreSale(false); setActiveView('dashboard'); }}>Vazgeç</Button>
                </div>
                <Card>
                  <CardContent className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nihai Müşteri</Label>
                        <Input value={preSaleForm.end_customer} onChange={(e) => setPreSaleForm({...preSaleForm, end_customer: e.target.value})} placeholder="Nihai müşteri adı" data-testid="ps-end-customer" />
                      </div>
                      <div>
                        <Label>Üretim Hattı *</Label>
                        <Select value={preSaleForm.production_line} onValueChange={(val) => setPreSaleForm({...preSaleForm, production_line: val})}>
                          <SelectTrigger data-testid="ps-production-line"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lake">Lake</SelectItem>
                            <SelectItem value="melamin">Melamin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Model</Label>
                        {psCustom.model ? (
                          <Input value={preSaleForm.model} onChange={(e) => setPreSaleForm({...preSaleForm, model: e.target.value})} placeholder="Özel model adını yazın" onBlur={() => { if (!preSaleForm.model) setPsCustom({...psCustom, model: false}); }} data-testid="ps-model" />
                        ) : (
                          <Select value={preSaleForm.model} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, model: true}); setPreSaleForm({...preSaleForm, model: ""}); } else { setPreSaleForm({...preSaleForm, model: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="Model seçin" /></SelectTrigger>
                            <SelectContent className="max-h-60 overflow-y-auto">
                              {psModelOptions.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label>Renk</Label>
                        {psCustom.color ? (
                          <Input value={preSaleForm.color} onChange={(e) => setPreSaleForm({...preSaleForm, color: e.target.value})} placeholder="Özel renk adını yazın" onBlur={() => { if (!preSaleForm.color) setPsCustom({...psCustom, color: false}); }} data-testid="ps-color" />
                        ) : (
                          <Select value={preSaleForm.color} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, color: true}); setPreSaleForm({...preSaleForm, color: ""}); } else { setPreSaleForm({...preSaleForm, color: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="Renk seçin" /></SelectTrigger>
                            <SelectContent className="max-h-60 overflow-y-auto">
                              {psColorOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label>Kapı Kolu</Label>
                        {psCustom.door_handle ? (
                          <Input value={preSaleForm.door_handle} onChange={(e) => setPreSaleForm({...preSaleForm, door_handle: e.target.value})} placeholder="Özel kapı kolu türü yazın" onBlur={() => { if (!preSaleForm.door_handle) setPsCustom({...psCustom, door_handle: false}); }} />
                        ) : (
                          <Select value={preSaleForm.door_handle} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, door_handle: true}); setPreSaleForm({...preSaleForm, door_handle: ""}); } else { setPreSaleForm({...preSaleForm, door_handle: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="Kapı kolu seçin" /></SelectTrigger>
                            <SelectContent>
                              {psDoorHandleOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label>Yüzey</Label>
                        {psCustom.surface ? (
                          <Input value={preSaleForm.surface} onChange={(e) => setPreSaleForm({...preSaleForm, surface: e.target.value})} placeholder="Özel yüzey türü yazın" onBlur={() => { if (!preSaleForm.surface) setPsCustom({...psCustom, surface: false}); }} />
                        ) : (
                          <Select value={preSaleForm.surface} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, surface: true}); setPreSaleForm({...preSaleForm, surface: ""}); } else { setPreSaleForm({...preSaleForm, surface: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="Yüzey seçin" /></SelectTrigger>
                            <SelectContent>
                              {psSurfaceOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Montaj</Label>
                        {psCustom.installation ? (
                          <Input value={preSaleForm.installation} onChange={(e) => setPreSaleForm({...preSaleForm, installation: e.target.value})} placeholder="Özel montaj türü yazın" onBlur={() => { if (!preSaleForm.installation) setPsCustom({...psCustom, installation: false}); }} />
                        ) : (
                          <Select value={preSaleForm.installation} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, installation: true}); setPreSaleForm({...preSaleForm, installation: ""}); } else { setPreSaleForm({...preSaleForm, installation: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="Montaj seçin" /></SelectTrigger>
                            <SelectContent>
                              {psInstallationOptions.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label>Montaj Yeri</Label>
                        {psCustom.installation_location ? (
                          <Input value={preSaleForm.installation_location} onChange={(e) => setPreSaleForm({...preSaleForm, installation_location: e.target.value})} placeholder="Özel montaj yeri yazın" onBlur={() => { if (!preSaleForm.installation_location) setPsCustom({...psCustom, installation_location: false}); }} />
                        ) : (
                          <Select value={preSaleForm.installation_location} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, installation_location: true}); setPreSaleForm({...preSaleForm, installation_location: ""}); } else { setPreSaleForm({...preSaleForm, installation_location: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="Montaj yeri seçin" /></SelectTrigger>
                            <SelectContent>
                              {psInstallationLocationOptions.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label>İç Pervaz</Label>
                        {psCustom.inner_frame ? (
                          <Input value={preSaleForm.inner_frame} onChange={(e) => setPreSaleForm({...preSaleForm, inner_frame: e.target.value})} placeholder="Özel iç pervaz türü yazın" onBlur={() => { if (!preSaleForm.inner_frame) setPsCustom({...psCustom, inner_frame: false}); }} />
                        ) : (
                          <Select value={preSaleForm.inner_frame} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, inner_frame: true}); setPreSaleForm({...preSaleForm, inner_frame: ""}); } else { setPreSaleForm({...preSaleForm, inner_frame: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="İç pervaz seçin" /></SelectTrigger>
                            <SelectContent>
                              {psInnerFrameOptions.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label>Dış Pervaz</Label>
                        {psCustom.outer_frame ? (
                          <Input value={preSaleForm.outer_frame} onChange={(e) => setPreSaleForm({...preSaleForm, outer_frame: e.target.value})} placeholder="Özel dış pervaz türü yazın" onBlur={() => { if (!preSaleForm.outer_frame) setPsCustom({...psCustom, outer_frame: false}); }} />
                        ) : (
                          <Select value={preSaleForm.outer_frame} onValueChange={(v) => { if (v === "özel") { setPsCustom({...psCustom, outer_frame: true}); setPreSaleForm({...preSaleForm, outer_frame: ""}); } else { setPreSaleForm({...preSaleForm, outer_frame: v}); } }}>
                            <SelectTrigger><SelectValue placeholder="Dış pervaz seçin" /></SelectTrigger>
                            <SelectContent>
                              {psOuterFrameOptions.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                              <SelectItem value="özel">Özel (Manuel Giriş)</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Ölçü Tablosu</Label>
                        <Button type="button" size="sm" variant={psShowQuantity ? "default" : "outline"} onClick={() => setPsShowQuantity(!psShowQuantity)} className="h-7">
                          {psShowQuantity ? "✓ Adet Aktif" : "+ Adet Ekle"}
                        </Button>
                      </div>
                      <div className="max-h-96 overflow-y-auto border rounded-md">
                        <table className="w-full text-sm" data-testid="ps-measurements">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="w-8 p-2 text-left font-medium">#</th>
                              <th className="p-2 text-left font-medium">En</th>
                              <th className="p-2 text-left font-medium">Boy</th>
                              <th className="p-2 text-left font-medium">Kasa</th>
                              <th className="p-2 text-left font-medium">Yönü</th>
                              <th className="p-2 text-left font-medium">Cam</th>
                              <th className="p-2 text-left font-medium">Oda</th>
                              <th className="p-2 text-left font-medium">Açıklama</th>
                              {psShowQuantity && <th className="p-2 text-left font-medium bg-purple-50">Adet</th>}
                              <th className="p-2 text-left font-medium">İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {psMeasurements.map((row, index) => (
                              <tr key={row.id} className="border-t">
                                <td className="p-2 font-medium text-gray-500">{row.id}</td>
                                <td className="p-1">
                                  {row.width === "özel" || (row.width && !psWidthOptions.includes(parseInt(row.width))) ? (
                                    <Input value={row.width === "özel" ? "" : row.width} onChange={(e) => { const n = [...psMeasurements]; n[index].width = e.target.value; setPsMeasurements(n); }} className="h-8 text-xs" placeholder="Özel" />
                                  ) : (
                                    <Select value={psWidthOptions.includes(parseInt(row.width)) ? row.width : ""} onValueChange={(v) => { const n = [...psMeasurements]; n[index].width = v; setPsMeasurements(n); }}>
                                      <SelectTrigger className="h-8"><SelectValue placeholder="En" /></SelectTrigger>
                                      <SelectContent className="max-h-[400px] overflow-y-auto">
                                        {psWidthOptions.map(w => <SelectItem key={w} value={w.toString()}>{w}</SelectItem>)}
                                        <SelectItem value="özel">Özel</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                </td>
                                <td className="p-1">
                                  {row.height === "özel" || (row.height && !psHeightOptions.includes(parseInt(row.height))) ? (
                                    <Input value={row.height === "özel" ? "" : row.height} onChange={(e) => { const n = [...psMeasurements]; n[index].height = e.target.value; setPsMeasurements(n); }} className="h-8 text-xs" placeholder="Özel" />
                                  ) : (
                                    <Select value={psHeightOptions.includes(parseInt(row.height)) ? row.height : ""} onValueChange={(v) => { const n = [...psMeasurements]; n[index].height = v; setPsMeasurements(n); }}>
                                      <SelectTrigger className="h-8"><SelectValue placeholder="Boy" /></SelectTrigger>
                                      <SelectContent className="max-h-[400px] overflow-y-auto">
                                        {psHeightOptions.map(h => <SelectItem key={h} value={h.toString()}>{h}</SelectItem>)}
                                        <SelectItem value="özel">Özel</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                </td>
                                <td className="p-1">
                                  {row.frame === "özel" || (row.frame && !psFrameOptions.includes(parseInt(row.frame))) ? (
                                    <Input value={row.frame === "özel" ? "" : row.frame} onChange={(e) => { const n = [...psMeasurements]; n[index].frame = e.target.value; setPsMeasurements(n); }} className="h-8 text-xs" placeholder="Özel" />
                                  ) : (
                                    <Select value={psFrameOptions.includes(parseInt(row.frame)) ? row.frame : ""} onValueChange={(v) => { const n = [...psMeasurements]; n[index].frame = v; setPsMeasurements(n); }}>
                                      <SelectTrigger className="h-8"><SelectValue placeholder="Kasa" /></SelectTrigger>
                                      <SelectContent className="max-h-[400px] overflow-y-auto">
                                        {psFrameOptions.map(f => <SelectItem key={f} value={f.toString()}>{f}</SelectItem>)}
                                        <SelectItem value="özel">Özel</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                </td>
                                <td className="p-1">
                                  {row.direction === "özel" || (row.direction && !psDirectionOptions.includes(row.direction)) ? (
                                    <Input value={row.direction === "özel" ? "" : row.direction} onChange={(e) => { const n = [...psMeasurements]; n[index].direction = e.target.value; setPsMeasurements(n); }} className="h-8 text-xs" placeholder="Özel" />
                                  ) : (
                                    <Select value={psDirectionOptions.includes(row.direction) ? row.direction : ""} onValueChange={(v) => { const n = [...psMeasurements]; n[index].direction = v; setPsMeasurements(n); }}>
                                      <SelectTrigger className="h-8"><SelectValue placeholder="Yön" /></SelectTrigger>
                                      <SelectContent>{psDirectionOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}<SelectItem value="özel">Özel</SelectItem></SelectContent>
                                    </Select>
                                  )}
                                </td>
                                <td className="p-1">
                                  {row.glass === "özel" || (row.glass && !psGlassOptions.includes(row.glass)) ? (
                                    <Input value={row.glass === "özel" ? "" : row.glass} onChange={(e) => { const n = [...psMeasurements]; n[index].glass = e.target.value; setPsMeasurements(n); }} className="h-8 text-xs" placeholder="Özel" />
                                  ) : (
                                    <Select value={psGlassOptions.includes(row.glass) ? row.glass : ""} onValueChange={(v) => { const n = [...psMeasurements]; n[index].glass = v; setPsMeasurements(n); }}>
                                      <SelectTrigger className="h-8"><SelectValue placeholder="Cam" /></SelectTrigger>
                                      <SelectContent>{psGlassOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}<SelectItem value="özel">Özel</SelectItem></SelectContent>
                                    </Select>
                                  )}
                                </td>
                                <td className="p-1">
                                  {row.room === "özel" || (row.room && !psRoomOptions.includes(row.room)) ? (
                                    <Input value={row.room === "özel" ? "" : row.room} onChange={(e) => { const n = [...psMeasurements]; n[index].room = e.target.value; setPsMeasurements(n); }} className="h-8 text-xs" placeholder="Özel" />
                                  ) : (
                                    <Select value={psRoomOptions.includes(row.room) ? row.room : ""} onValueChange={(v) => { const n = [...psMeasurements]; n[index].room = v; setPsMeasurements(n); }}>
                                      <SelectTrigger className="h-8"><SelectValue placeholder="Oda" /></SelectTrigger>
                                      <SelectContent>{psRoomOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}<SelectItem value="özel">Özel</SelectItem></SelectContent>
                                    </Select>
                                  )}
                                </td>
                                <td className="p-1">
                                  <Input placeholder="Açıklama" value={row.description} onChange={(e) => { const n = [...psMeasurements]; n[index].description = e.target.value; setPsMeasurements(n); }} className="h-8" />
                                </td>
                                {psShowQuantity && (
                                  <td className="p-1 bg-purple-50">
                                    <Input type="number" min="1" value={row.quantity || 1} onChange={(e) => { const n = [...psMeasurements]; n[index].quantity = parseInt(e.target.value) || 1; setPsMeasurements(n); }} className="h-8 text-center font-medium" />
                                  </td>
                                )}
                                <td className="p-1">
                                  <Button type="button" variant="ghost" size="sm" onClick={() => { if (psMeasurements.length > 1) setPsMeasurements(psMeasurements.filter((_, i) => i !== index)); }} className="h-8 w-8 p-0 text-red-600 hover:text-red-700" disabled={psMeasurements.length <= 1}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          const newId = Math.max(...psMeasurements.map(m => m.id), 0) + 1;
                          setPsMeasurements([...psMeasurements, { id: newId, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
                        }}>
                          <Plus className="h-4 w-4 mr-1" /> Yeni Satır Ekle
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Hedef Montaj Tarihi</Label>
                        <Input type="date" value={preSaleForm.target_installation_date} onChange={(e) => setPreSaleForm({...preSaleForm, target_installation_date: e.target.value})} />
                      </div>
                    </div>
                    <div>
                      <Label>İmalat İçin Önemli Notlar</Label>
                      <textarea className="w-full p-3 border rounded-md min-h-[120px] text-sm" value={preSaleForm.notes} onChange={(e) => setPreSaleForm({...preSaleForm, notes: e.target.value})} placeholder="İmalat ekibine iletilecek önemli notları buraya yazın (malzeme tercihleri, özel istekler, dikkat edilecek hususlar vb.)" data-testid="ps-notes" />
                    </div>
                    <div>
                      <Label>Detay / Açıklama</Label>
                      <textarea className="w-full p-3 border rounded-md min-h-[80px] text-sm" value={preSaleForm.details} onChange={(e) => setPreSaleForm({...preSaleForm, details: e.target.value})} placeholder="Ek detaylar" />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={() => { setShowNewPreSale(false); setActiveView('dashboard'); }}>İptal</Button>
                      <Button onClick={submitPreSale} disabled={preSaleSubmitting} className="bg-green-600 hover:bg-green-700" data-testid="ps-submit-btn">
                        {preSaleSubmitting ? 'Gönderiliyor...' : 'Sipariş Talebini Gönder'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Pre-Sales List View */}
            {activeView === 'presales' && (
              <>
              <div className="space-y-4" data-testid="presales-list">
                <h2 className="text-2xl font-bold text-gray-900">Sipariş Taleplerim</h2>
                {myPreSales.length > 0 ? (
                  <div className="space-y-3">
                    {myPreSales.map((ps) => (
                      <Card key={ps.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{ps.end_customer || 'Belirtilmedi'}</span>
                                <Badge className={
                                  ps.status === 'beklemede' ? 'bg-yellow-100 text-yellow-800' :
                                  ps.status === 'onaylandi' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                                }>
                                  {ps.status === 'beklemede' ? 'Onay Bekliyor' : ps.status === 'onaylandi' ? 'Onaylandı' : 'Reddedildi'}
                                </Badge>
                                <Badge variant="outline">{ps.production_line === 'lake' ? 'Lake' : 'Melamin'}</Badge>
                              </div>
                              <p className="text-sm text-gray-600">
                                Model: {ps.model || '-'} | Renk: {ps.color || '-'} | Yüzey: {ps.surface || '-'}
                              </p>
                              {ps.notes && <p className="text-sm text-gray-500">Not: {ps.notes}</p>}
                              {ps.status === 'onaylandi' && ps.price > 0 && (
                                <p className="text-sm font-semibold text-green-700">Fiyat: {formatCurrency(ps.price)}</p>
                              )}
                              {ps.status === 'reddedildi' && ps.rejected_reason && (
                                <p className="text-sm text-red-600">Red sebebi: {ps.rejected_reason}</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className="text-xs text-gray-400">{formatDate(ps.created_at)}</span>
                              {ps.status === 'beklemede' && (
                                <button
                                  onClick={() => openEditPreSale(ps)}
                                  className="flex items-center gap-1.5 text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                  Düzenle
                                </button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card><CardContent className="p-8 text-center text-gray-500">Henüz sipariş talebiniz bulunmuyor</CardContent></Card>
                )}
              </div>
              {/* Edit Dialog */}
              {editingPreSale && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b flex justify-between items-center">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Siparişi Düzenle</h2>
                        <p className="text-sm text-gray-500 mt-1">Onay bekleyen siparişinizi güncelleyin</p>
                      </div>
                      <button onClick={() => setEditingPreSale(null)} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nihai Müşteri</label>
                          <input className="w-full border rounded px-3 py-2 text-sm" value={editPsForm.end_customer} onChange={e => setEditPsForm({...editPsForm, end_customer: e.target.value})} placeholder="Nihai müşteri adı" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Üretim Hattı</label>
                          <select className="w-full border rounded px-3 py-2 text-sm" value={editPsForm.production_line} onChange={e => setEditPsForm({...editPsForm, production_line: e.target.value})}>
                            <option value="lake">Lake</option>
                            <option value="melamin">Melamin</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                          <input className="w-full border rounded px-3 py-2 text-sm" value={editPsForm.model} onChange={e => setEditPsForm({...editPsForm, model: e.target.value})} placeholder="Model" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Renk</label>
                          <input className="w-full border rounded px-3 py-2 text-sm" value={editPsForm.color} onChange={e => setEditPsForm({...editPsForm, color: e.target.value})} placeholder="Renk" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Kapı Kolu</label>
                          <input className="w-full border rounded px-3 py-2 text-sm" value={editPsForm.door_handle} onChange={e => setEditPsForm({...editPsForm, door_handle: e.target.value})} placeholder="Kapı kolu" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Yüzey</label>
                          <input className="w-full border rounded px-3 py-2 text-sm" value={editPsForm.surface} onChange={e => setEditPsForm({...editPsForm, surface: e.target.value})} placeholder="Yüzey" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ölçü Tablosu</label>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border-b p-2 text-left">#</th>
                                <th className="border-b p-2 text-left">En</th>
                                <th className="border-b p-2 text-left">Boy</th>
                                <th className="border-b p-2 text-left">Kasa</th>
                                <th className="border-b p-2 text-left">Yön</th>
                                <th className="border-b p-2 text-left">Cam</th>
                                <th className="border-b p-2 text-left">Oda</th>
                                <th className="border-b p-2 text-left">Açıklama</th>
                                <th className="border-b p-2 text-center bg-purple-50">Adet</th>
                                <th className="border-b p-2 text-left">İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editPsMeasurements.map((row, idx) => (
                                <tr key={row.id || idx} className="border-b">
                                  <td className="p-1 text-gray-500">{idx + 1}</td>
                                  {['width','height','frame'].map(field => (
                                    <td key={field} className="p-1">
                                      <input type="number" className="w-14 border rounded px-1 py-1 text-xs" value={row[field] || ''} onChange={e => { const n=[...editPsMeasurements]; n[idx]={...n[idx],[field]:e.target.value}; setEditPsMeasurements(n); }} />
                                    </td>
                                  ))}
                                  <td className="p-1">
                                    <select className="border rounded px-1 py-1 text-xs w-16" value={row.direction||''} onChange={e=>{const n=[...editPsMeasurements];n[idx]={...n[idx],direction:e.target.value};setEditPsMeasurements(n);}}>
                                      <option value="">-</option><option>SAĞ</option><option>SOL</option><option>SÜRGÜLÜ</option>
                                    </select>
                                  </td>
                                  <td className="p-1">
                                    <select className="border rounded px-1 py-1 text-xs w-16" value={row.glass||''} onChange={e=>{const n=[...editPsMeasurements];n[idx]={...n[idx],glass:e.target.value};setEditPsMeasurements(n);}}>
                                      <option value="">-</option><option>KAPALI</option><option>CAMLI</option>
                                    </select>
                                  </td>
                                  <td className="p-1"><input className="w-16 border rounded px-1 py-1 text-xs" value={row.room||''} onChange={e=>{const n=[...editPsMeasurements];n[idx]={...n[idx],room:e.target.value};setEditPsMeasurements(n);}}/></td>
                                  <td className="p-1"><input className="w-24 border rounded px-1 py-1 text-xs" value={row.description||''} onChange={e=>{const n=[...editPsMeasurements];n[idx]={...n[idx],description:e.target.value};setEditPsMeasurements(n);}}/></td>
                                  <td className="p-1 bg-purple-50">
                                    <input type="number" min="1" className="w-12 border rounded px-1 py-1 text-xs text-center" value={row.quantity||1} onChange={e=>{const n=[...editPsMeasurements];n[idx]={...n[idx],quantity:parseInt(e.target.value)||1};setEditPsMeasurements(n);}}/>
                                  </td>
                                  <td className="p-1">
                                    <div className="flex gap-1">
                                      <button title="Satırı kopyala" onClick={()=>{const newId=Math.max(...editPsMeasurements.map(m=>m.id||0),0)+1;const copy={...row,id:newId};const nl=[...editPsMeasurements];nl.splice(idx+1,0,copy);setEditPsMeasurements(nl);}} className="text-blue-600 hover:bg-blue-50 rounded p-1"><Copy className="h-3.5 w-3.5"/></button>
                                      {editPsMeasurements.length > 1 && <button onClick={()=>setEditPsMeasurements(editPsMeasurements.filter((_,i)=>i!==idx))} className="text-red-500 hover:bg-red-50 rounded p-1"><Trash2 className="h-3.5 w-3.5"/></button>}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <button onClick={()=>{const newId=Math.max(...editPsMeasurements.map(m=>m.id||0),0)+1;setEditPsMeasurements([...editPsMeasurements,{id:newId,width:"",height:"",frame:"",direction:"",glass:"",room:"",description:"",quantity:1}]);}} className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          <Plus className="h-4 w-4"/> Satır Ekle
                        </button>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                        <textarea className="w-full border rounded px-3 py-2 text-sm" rows={2} value={editPsForm.notes} onChange={e=>setEditPsForm({...editPsForm, notes: e.target.value})} placeholder="Özel notlar..." />
                      </div>
                    </div>
                    <div className="p-6 border-t flex justify-end gap-3">
                      <button onClick={() => setEditingPreSale(null)} className="px-4 py-2 border rounded text-sm text-gray-600 hover:bg-gray-50">İptal</button>
                      <button onClick={submitEditPreSale} disabled={editPsSubmitting} className="px-5 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                        {editPsSubmitting ? <RefreshCw className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4"/>}
                        Kaydet
                      </button>
                    </div>
                  </div>
                </div>
              )}
              </>
            )}

            {/* My Orders View */}
            {activeView === 'orders' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Siparişlerim</h2>
                {myOrders.length > 0 ? (
                  <div className="space-y-3">
                    {myOrders.map((order) => (
                      <Card key={order.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{order.description}</h3>
                              {/* Nihai Tüketici */}
                              {order.end_customer && (
                                <p className="text-sm text-purple-600 mt-1">
                                  <User className="h-3 w-3 inline mr-1" />
                                  Nihai Tüketici: {order.end_customer}
                                </p>
                              )}
                              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                <div><strong>Model:</strong> {order.model || '-'}</div>
                                <div><strong>Renk:</strong> {order.color || '-'}</div>
                                <div><strong>Sipariş Tarihi:</strong> {formatDate(order.created_at)}</div>
                                <div><strong>Hedef Montaj:</strong> {formatDate(order.target_installation_date)}</div>
                              </div>
                              <div className="mt-2 flex gap-2">
                                {order.production_status && (
                                  <div className="flex items-center gap-1">
                                    <Package className="h-4 w-4" />
                                    <span className="text-xs">Üretim:</span>
                                    {getStatusBadge(order.production_status)}
                                  </div>
                                )}
                                {order.installation_status && (
                                  <div className="flex items-center gap-1">
                                    <Wrench className="h-4 w-4" />
                                    <span className="text-xs">Montaj:</span>
                                    {getStatusBadge(order.installation_status)}
                                  </div>
                                )}
                              </div>
                              <div className="mt-3">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleViewOrderDetails(order.id)}
                                  className="w-full"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Sipariş Detayı
                                </Button>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-600">{formatCurrency(order.amount)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <ShoppingCart className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">Henüz sipariş bulunmamaktadır</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* My Productions View */}
            {activeView === 'productions' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Üretim Durumu</h2>
                {myProductions.length > 0 ? (
                  <div className="space-y-3">
                    {myProductions.map((prod) => (
                      <Card key={prod.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-lg">{prod.model} - {prod.color}</h3>
                                {getStatusBadge(prod.status)}
                              </div>
                              {/* Nihai Tüketici */}
                              {prod.end_customer && (
                                <p className="text-sm text-purple-600 mb-2">
                                  <User className="h-3 w-3 inline mr-1" />
                                  Nihai Tüketici: {prod.end_customer}
                                </p>
                              )}
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                <div><strong>Üretim Hattı:</strong> {prod.production_line?.toUpperCase()}</div>
                                <div><strong>Oluşturma:</strong> {formatDate(prod.created_at)}</div>
                                {prod.production_date && (
                                  <div><strong>Üretim Tarihi:</strong> {formatDate(prod.production_date)}</div>
                                )}
                                {prod.ready_date && (
                                  <div><strong>Hazır Olma:</strong> {formatDate(prod.ready_date)}</div>
                                )}
                              </div>
                              
                              {/* Üretim İşlem Durumları */}
                              <div className="mt-3 flex gap-2">
                                {prod.cnc_completed !== undefined && (
                                  <div className={`px-3 py-1 rounded text-xs font-medium ${prod.cnc_completed ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                                    {prod.cnc_completed ? '✅ CNC Tamamlandı' : '⏳ CNC Bekliyor'}
                                  </div>
                                )}
                                {prod.aksesuar_ready !== undefined && (
                                  <div className={`px-3 py-1 rounded text-xs font-medium ${prod.aksesuar_ready ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                                    {prod.aksesuar_ready ? '✅ Aksesuar Hazır' : '⏳ Aksesuar Bekliyor'}
                                  </div>
                                )}
                              </div>
                              
                              {prod.notes && (
                                <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
                                  <strong>Not:</strong> {prod.notes}
                                </div>
                              )}
                              
                              {/* Sipariş Detayı Butonu */}
                              {prod.transaction_id && (
                                <div className="mt-3">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleViewOrderDetails(prod.transaction_id)}
                                    className="w-full"
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Sipariş Detayı
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">Üretimde sipariş bulunmamaktadır</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* My Installations View */}
            {activeView === 'installations' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Montaj Programları</h2>
                  <div className="flex gap-2 flex-wrap">
                    {/* Status Filters */}
                    <div className="flex gap-2">
                      <Button
                        variant={installationFilter === 'pending' ? "default" : "outline"}
                        size="sm"
                        onClick={() => setInstallationFilter('pending')}
                        className={installationFilter === 'pending' ? "bg-orange-600 hover:bg-orange-700" : ""}
                      >
                        Bekleyen ve Planlanmış ({myInstallations.filter(inst => inst.status !== 'tamamlandi').length})
                      </Button>
                      <Button
                        variant={installationFilter === 'completed' ? "default" : "outline"}
                        size="sm"
                        onClick={() => setInstallationFilter('completed')}
                        className={installationFilter === 'completed' ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        Tamamlanan ({myInstallations.filter(inst => inst.status === 'tamamlandi').length})
                      </Button>
                    </div>
                    
                    {/* Paint Status Filters */}
                    <div className="flex gap-2 border-l pl-2">
                      <Button
                        variant={paintFilter === 'unpainted' ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPaintFilter('unpainted')}
                        className={paintFilter === 'unpainted' ? "bg-red-600 hover:bg-red-700" : ""}
                      >
                        Boyanmadı ({myInstallations.filter(inst => !inst.is_painted).length})
                      </Button>
                      <Button
                        variant={paintFilter === 'painted' ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPaintFilter('painted')}
                        className={paintFilter === 'painted' ? "bg-blue-600 hover:bg-blue-700" : ""}
                      >
                        Boyandı ({myInstallations.filter(inst => inst.is_painted).length})
                      </Button>
                      <Button
                        variant={paintFilter === 'all' ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPaintFilter('all')}
                        className={paintFilter === 'all' ? "bg-gray-600 hover:bg-gray-700" : ""}
                      >
                        Tümü
                      </Button>
                    </div>
                  </div>
                </div>
                
                {myInstallations.length > 0 ? (
                  <div className="space-y-3">
                    {myInstallations
                      .filter(inst => {
                        // İlk filtre: Status (Bekleyen/Tamamlanan)
                        const statusMatch = installationFilter === 'pending' 
                          ? inst.status !== 'tamamlandi' 
                          : inst.status === 'tamamlandi';
                        
                        // İkinci filtre: Paint Status (Boyanmadı/Boyandı/Tümü)
                        let paintMatch = true;
                        if (paintFilter === 'painted') {
                          paintMatch = inst.is_painted === true;
                        } else if (paintFilter === 'unpainted') {
                          paintMatch = !inst.is_painted;
                        }
                        // paintFilter === 'all' ise tümünü göster
                        
                        return statusMatch && paintMatch;
                      })
                      .map((inst) => (
                        <Card key={inst.id} className={installationFilter === 'pending' ? 'border-orange-200' : 'border-green-200'}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-lg">{inst.door_type}</h3>
                                  {getStatusBadge(inst.status)}
                                </div>
                                {/* Nihai Tüketici */}
                                {inst.sale_details?.end_customer && (
                                  <p className="text-sm text-purple-600 mb-2">
                                    <User className="h-3 w-3 inline mr-1" />
                                    Nihai Tüketici: {inst.sale_details.end_customer}
                                  </p>
                                )}
                                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                  <div><strong>Montajcı:</strong> {inst.installer_name}</div>
                                  <div><strong>Montaj Tarihi:</strong> {formatDate(inst.installation_date)}</div>
                                  {inst.status === 'tamamlandi' && inst.completed_date && (
                                    <div><strong>Tamamlanma:</strong> <span className="text-green-600 font-medium">{formatDate(inst.completed_date)}</span></div>
                                  )}
                                  <div><strong>Adres:</strong> {inst.address}</div>
                                  {inst.target_date && (
                                    <div><strong>Hedef Tarih:</strong> {formatDate(inst.target_date)}</div>
                                  )}
                                </div>
                                
                                {/* Boya/Ebatlama Durumu */}
                                {inst.is_painted !== undefined && (
                                  <div className="mt-2">
                                    <div className={`inline-flex px-3 py-1 rounded text-xs font-medium ${inst.is_painted ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                                      {inst.sale_details?.production_line === 'melamin' ? (
                                        inst.is_painted ? '✅ Ebatlandı' : '⏳ Ebatlanmadı'
                                      ) : (
                                        inst.is_painted ? '✅ Boyandı' : '⏳ Boyanmadı'
                                      )}
                                      {inst.is_painted && inst.painted_date && (
                                        <span>
                                          {' - '}
                                          {inst.sale_details?.production_line === 'melamin' ? 'Ebatlama: ' : ''}
                                          {formatDate(inst.painted_date)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {inst.notes && (
                                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                                    <strong>Not:</strong> {inst.notes}
                                  </div>
                                )}
                                
                                {/* Sipariş Detayları */}
                                {inst.sale_details && (
                                  <div className="mt-3 p-3 bg-gray-50 rounded">
                                    <p className="text-xs font-medium text-gray-700 mb-2">Sipariş Detayları:</p>
                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                      <div><strong>Model:</strong> {inst.sale_details.model || '-'}</div>
                                      <div><strong>Renk:</strong> {inst.sale_details.color || '-'}</div>
                                      <div><strong>Tutar:</strong> {formatCurrency(inst.sale_details.amount)}</div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Sipariş Detayı Butonu */}
                                {inst.transaction_id && (
                                  <div className="mt-3">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleViewOrderDetails(inst.transaction_id)}
                                      className="w-full"
                                    >
                                      <Eye className="h-4 w-4 mr-2" />
                                      Sipariş Detayı
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    
                    {/* Filtrelenmiş sonuç boşsa mesaj göster */}
                    {myInstallations
                      .filter(inst => {
                        const statusMatch = installationFilter === 'pending' 
                          ? inst.status !== 'tamamlandi' 
                          : inst.status === 'tamamlandi';
                        
                        let paintMatch = true;
                        if (paintFilter === 'painted') {
                          paintMatch = inst.is_painted === true;
                        } else if (paintFilter === 'unpainted') {
                          paintMatch = !inst.is_painted;
                        }
                        
                        return statusMatch && paintMatch;
                      }).length === 0 && (
                      <Card>
                        <CardContent className="p-12 text-center">
                          <Wrench className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-600">
                            {paintFilter === 'painted' && 'Boyalı montaj bulunmamaktadır'}
                            {paintFilter === 'unpainted' && 'Boyanmamış montaj bulunmamaktadır'}
                            {paintFilter === 'all' && installationFilter === 'pending' && 'Bekleyen montaj bulunmamaktadır'}
                            {paintFilter === 'all' && installationFilter === 'completed' && 'Tamamlanan montaj bulunmamaktadır'}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Wrench className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">Montaj programı bulunmamaktadır</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Account Statement View */}
            {activeView === 'account' && accountStatement && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Cari Hesap Ekstresi</h2>
                
                {/* Özet Kartlar */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Toplam Satış</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(
                          accountStatement.transactions?.reduce((sum, tx) => 
                            tx.transaction_type === 'debit' ? sum + (tx.amount || 0) : sum, 0
                          ) || 0
                        )}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Toplam Tahsilat</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(
                          ((accountStatement.collection_receipts?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0) +
                          (accountStatement.temlik_transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0))
                        )}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Kalan Bakiye</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={`text-2xl font-bold ${accountStatement.customer?.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(accountStatement.customer?.balance || 0)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {accountStatement.customer?.balance >= 0 ? 'Borç' : 'Alacak'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Birleşik Hesap Hareketleri - Tarih Sırasına Göre */}
                <Card>
                  <CardHeader>
                    <CardTitle>Hesap Hareketleri</CardTitle>
                    <CardDescription>Tüm işlemler tarih sırasına göre</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      // Tüm işlemleri birleştir ve tarih sırasına göre sırala
                      const allMovements = [];
                      
                      // Satış işlemleri ekle
                      (accountStatement.transactions || []).forEach(tx => {
                        allMovements.push({
                          id: tx.id,
                          date: tx.created_at,
                          type: 'sale',
                          description: tx.description,
                          end_customer: tx.end_customer,
                          debit: tx.transaction_type === 'debit' ? tx.amount : 0,
                          credit: tx.transaction_type === 'credit' ? tx.amount : 0,
                          badge: tx.transaction_type === 'debit' ? 'Satış' : 'Ödeme',
                          color: 'blue',
                          transaction_id: tx.id
                        });
                      });
                      
                      // Tahsilat makbuzları ekle
                      (accountStatement.collection_receipts || []).forEach(receipt => {
                        allMovements.push({
                          id: receipt.id,
                          date: receipt.receipt_date || receipt.created_at,
                          type: 'collection',
                          description: receipt.description,
                          debit: 0,
                          credit: receipt.amount,
                          badge: 'Tahsilat',
                          payment_method: receipt.payment_method,
                          color: 'green'
                        });
                      });
                      
                      // Temlik işlemleri ekle
                      (accountStatement.temlik_transactions || []).forEach(temlik => {
                        allMovements.push({
                          id: temlik.id,
                          date: temlik.created_at,
                          type: 'temlik',
                          description: `Temlik - ${temlik.supplier_name}${temlik.description ? ': ' + temlik.description : ''}`,
                          debit: 0,
                          credit: temlik.amount,
                          badge: 'Temlik',
                          color: 'purple'
                        });
                      });
                      
                      // Tarihe göre sırala (eski → yeni)
                      allMovements.sort((a, b) => new Date(a.date) - new Date(b.date));
                      
                      // Running balance hesapla
                      let runningBalance = 0;
                      allMovements.forEach(movement => {
                        runningBalance += movement.debit - movement.credit;
                        movement.balance = runningBalance;
                      });
                      
                      return allMovements.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b-2 border-gray-300">
                              <tr>
                                <th className="text-left p-3 font-semibold">Tarih</th>
                                <th className="text-left p-3 font-semibold">İşlem</th>
                                <th className="text-right p-3 font-semibold">Borç</th>
                                <th className="text-right p-3 font-semibold">Alacak</th>
                                <th className="text-right p-3 font-semibold">Kalan Bakiye</th>
                                <th className="text-center p-3 font-semibold">İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allMovements.map((movement, index) => (
                                <tr key={`${movement.type}-${movement.id}`} className="border-b hover:bg-gray-50">
                                  <td className="p-3 text-gray-600">
                                    {formatDate(movement.date)}
                                  </td>
                                  <td className="p-3">
                                    <div>
                                      <p className="font-medium">{movement.description}</p>
                                      {movement.end_customer && (
                                        <p className="text-xs text-purple-600 mt-1">
                                          <User className="h-3 w-3 inline mr-1" />
                                          {movement.end_customer}
                                        </p>
                                      )}
                                      <div className="mt-1">
                                        <Badge 
                                          className={
                                            movement.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                            movement.color === 'green' ? 'bg-green-100 text-green-800' :
                                            'bg-purple-100 text-purple-800'
                                          }
                                        >
                                          {movement.badge}
                                        </Badge>
                                        {movement.payment_method && (
                                          <Badge variant="outline" className="ml-1 text-xs">
                                            {movement.payment_method}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="p-3 text-right">
                                    {movement.debit > 0 && (
                                      <span className="font-semibold text-red-600">
                                        {formatCurrency(movement.debit)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-3 text-right">
                                    {movement.credit > 0 && (
                                      <span className="font-semibold text-green-600">
                                        {formatCurrency(movement.credit)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-3 text-right">
                                    <span className={`font-bold text-lg ${movement.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                      {formatCurrency(movement.balance)}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center">
                                    {movement.transaction_id && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={() => handleViewOrderDetails(movement.transaction_id)}
                                        className="h-8"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                              <tr>
                                <td colSpan="2" className="p-3 font-bold text-right">TOPLAM:</td>
                                <td className="p-3 text-right font-bold text-red-600">
                                  {formatCurrency(allMovements.reduce((sum, m) => sum + m.debit, 0))}
                                </td>
                                <td className="p-3 text-right font-bold text-green-600">
                                  {formatCurrency(allMovements.reduce((sum, m) => sum + m.credit, 0))}
                                </td>
                                <td className="p-3 text-right font-bold text-xl" colSpan="2">
                                  <span className={accountStatement.customer?.balance >= 0 ? 'text-red-600' : 'text-green-600'}>
                                    {formatCurrency(accountStatement.customer?.balance || 0)}
                                  </span>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        <div className="p-12 text-center text-gray-600">
                          Hesap hareketi bulunmamaktadır
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Sipariş Detayı Dialog - Kapsamlı Görünüm */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Sipariş Detayları</DialogTitle>
            <DialogDescription>Sipariş ile ilgili tüm bilgiler</DialogDescription>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-lg text-gray-600">Yükleniyor...</div>
            </div>
          ) : selectedOrderDetails && selectedOrderDetails.transaction ? (
            <div className="space-y-4">
              {/* Sipariş Bilgileri */}
              <Card>
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Sipariş Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Sipariş No:</strong> {selectedOrderDetails.transaction.id?.slice(0, 8).toUpperCase()}</div>
                    <div><strong>Sipariş Tarihi:</strong> {formatDate(selectedOrderDetails.transaction.created_at)}</div>
                    <div><strong>Açıklama:</strong> {selectedOrderDetails.transaction.description || '-'}</div>
                    <div><strong>Üretim Hattı:</strong> <Badge>{selectedOrderDetails.transaction.production_line?.toUpperCase() || '-'}</Badge></div>
                    <div><strong>Hedef Montaj:</strong> <span className="text-blue-600">{formatDate(selectedOrderDetails.transaction.target_installation_date) || 'Belirlenmemiş'}</span></div>
                    <div><strong>Tutar:</strong> <span className="text-2xl font-bold text-green-600">{formatCurrency(selectedOrderDetails.transaction.amount)}</span></div>
                  </div>
                  {selectedOrderDetails.transaction.details && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <strong className="text-sm flex items-center gap-1">
                        <Clipboard className="h-4 w-4" />
                        İmalat Notu:
                      </strong>
                      <p className="text-sm mt-1">{selectedOrderDetails.transaction.details}</p>
                    </div>
                  )}
                  {selectedOrderDetails.transaction.end_customer && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-600" />
                        <strong className="text-sm text-blue-700">Nihai Tüketici:</strong>
                        <span className="text-sm text-blue-900">{selectedOrderDetails.transaction.end_customer}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ürün Özellikleri */}
              <Card>
                <CardHeader className="bg-purple-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Ürün Özellikleri
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Model:</strong> {selectedOrderDetails.transaction.model || '-'}</div>
                    <div><strong>Renk:</strong> {selectedOrderDetails.transaction.color || '-'}</div>
                    <div><strong>Kapı Kolu:</strong> {selectedOrderDetails.transaction.door_handle || '-'}</div>
                    <div><strong>Yüzey:</strong> {selectedOrderDetails.transaction.surface || '-'}</div>
                    <div><strong>Montaj:</strong> {selectedOrderDetails.transaction.installation || '-'}</div>
                    <div><strong>Montaj Yeri:</strong> {selectedOrderDetails.transaction.installation_location || '-'}</div>
                    <div><strong>İç Pervaz:</strong> {selectedOrderDetails.transaction.inner_frame || '-'}</div>
                    <div><strong>Dış Pervaz:</strong> {selectedOrderDetails.transaction.outer_frame || '-'}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Ölçü Tablosu */}
              {selectedOrderDetails.transaction.measurements && (
                <Card>
                  <CardHeader className="bg-gray-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      Ölçü Tablosu
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {(() => {
                      try {
                        const measurements = typeof selectedOrderDetails.transaction.measurements === 'string' 
                          ? JSON.parse(selectedOrderDetails.transaction.measurements)
                          : selectedOrderDetails.transaction.measurements;
                        
                        if (Array.isArray(measurements) && measurements.length > 0) {
                          return (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-xs border border-gray-300">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="border border-gray-300 px-2 py-1 text-left">#</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">En</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Boy</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Kasa</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Yönü</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Cam</th>
                                    <th className="border border-gray-300 px-2 py-1 text-left">Oda</th>
                                    {measurements.some(m => m.quantity) && (
                                      <th className="border border-gray-300 px-2 py-1 text-left">Adet</th>
                                    )}
                                    <th className="border border-gray-300 px-2 py-1 text-left">Açıklama</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {measurements.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="border border-gray-300 px-2 py-1">{idx + 1}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.width || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.height || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.thickness || row.frame || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.direction || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.glass || '-'}</td>
                                      <td className="border border-gray-300 px-2 py-1">{row.room || '-'}</td>
                                      {measurements.some(m => m.quantity) && (
                                        <td className="border border-gray-300 px-2 py-1">{row.quantity || 1}</td>
                                      )}
                                      <td className="border border-gray-300 px-2 py-1">{row.notes || row.description || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }
                      } catch (e) {
                        return <p className="text-sm text-gray-500">Ölçü bilgisi yok</p>;
                      }
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Üretim ve Sipariş Durumu */}
              {selectedOrderDetails.productions && selectedOrderDetails.productions.length > 0 && (
                <Card>
                  <CardHeader className="bg-orange-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Üretim ve Sipariş Durumu
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {selectedOrderDetails.productions.map((prod, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                          <div className="flex justify-between items-start mb-3">
                            <div className="text-sm">
                              <div className="text-lg font-semibold">{prod.production_line?.toUpperCase()} - {prod.model}</div>
                              <div className="text-xs text-gray-600 mt-1">
                                Oluşturma: {formatDate(prod.created_at)}
                              </div>
                              {prod.production_date && (
                                <div className="text-xs text-gray-600">
                                  Üretim: {formatDate(prod.production_date)}
                                </div>
                              )}
                            </div>
                            <Badge className={
                              prod.status === 'hazir' ? 'bg-green-100 text-green-800' :
                              prod.status === 'uretimde' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {prod.status?.toUpperCase()}
                            </Badge>
                          </div>
                          
                          {/* Üretim İşlem Durumları */}
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            {prod.production_line === 'lake' && (
                              <div className={`p-3 rounded-lg border ${prod.cnc_completed ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                                <div className={`text-sm font-semibold ${prod.cnc_completed ? 'text-green-700' : 'text-orange-700'}`}>
                                  {prod.cnc_completed ? '✅ CNC İşlemi Tamamlandı' : '⏳ CNC İşlemi Yapılmadı'}
                                </div>
                                {prod.cnc_completed && prod.cnc_date && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    Tamamlanma: {formatDate(prod.cnc_date)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className={`p-3 rounded-lg border ${prod.aksesuar_ready ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                              <div className={`text-sm font-semibold ${prod.aksesuar_ready ? 'text-green-700' : 'text-orange-700'}`}>
                                {prod.aksesuar_ready ? '✅ Aksesuar Tamamlandı' : '⏳ Aksesuar Hazır Değil'}
                              </div>
                              {prod.aksesuar_ready && prod.aksesuar_date && (
                                <div className="text-xs text-gray-600 mt-1">
                                  Tamamlanma: {formatDate(prod.aksesuar_date)}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {prod.notes && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <strong className="text-xs flex items-center gap-1 text-yellow-700">
                                <Clipboard className="h-3 w-3" />
                                Üretim Notu:
                              </strong>
                              <p className="text-xs mt-1 text-gray-700">{prod.notes}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Montaj Durumu */}
              {selectedOrderDetails.installation && (
                <Card>
                  <CardHeader className="bg-indigo-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="h-5 w-5" />
                      Montaj Durumu
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><strong>Montaj Tarihi:</strong> {formatDate(selectedOrderDetails.installation.installation_date) || 'Belirlenmemiş'}</div>
                        <div><strong>Durum:</strong> {getStatusBadge(selectedOrderDetails.installation.status)}</div>
                        {selectedOrderDetails.installation.installer_name && (
                          <div><strong>Montajcı:</strong> {selectedOrderDetails.installation.installer_name}</div>
                        )}
                        <div><strong>Adres:</strong> {selectedOrderDetails.installation.address}</div>
                      </div>
                      
                      {/* Boya/Ebatlama Durumu */}
                      {selectedOrderDetails.installation.is_painted !== undefined && (
                        <div className={`p-3 rounded-lg border ${selectedOrderDetails.installation.is_painted ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                          <div className={`text-sm font-semibold ${selectedOrderDetails.installation.is_painted ? 'text-green-700' : 'text-orange-700'}`}>
                            {selectedOrderDetails.transaction?.production_line === 'melamin' ? (
                              selectedOrderDetails.installation.is_painted ? '✅ Ebatlandı' : '⏳ Ebatlanmadı'
                            ) : (
                              selectedOrderDetails.installation.is_painted ? '✅ Boyandı' : '⏳ Boyanmadı'
                            )}
                          </div>
                          {selectedOrderDetails.installation.is_painted && selectedOrderDetails.installation.painted_date && (
                            <div className="text-xs text-gray-600 mt-1">
                              {selectedOrderDetails.transaction?.production_line === 'melamin' ? 'Ebatlama Tarihi: ' : 'Boyanma Tarihi: '}
                              {formatDate(selectedOrderDetails.installation.painted_date)}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {selectedOrderDetails.installation.notes && (
                        <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                          <strong className="text-xs">Montaj Notu:</strong>
                          <p className="text-xs mt-1">{selectedOrderDetails.installation.notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Finansal Özet */}
              {selectedOrderDetails.financial_summary && (
                <Card>
                  <CardHeader className="bg-emerald-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Finansal Bilgiler
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><strong>Toplam Tutar:</strong> <span className="text-lg font-bold text-green-600">{formatCurrency(selectedOrderDetails.financial_summary.total_amount)}</span></div>
                      <div><strong>Peşinat:</strong> <span className="text-blue-600">{formatCurrency(selectedOrderDetails.financial_summary.advance_payment)}</span></div>
                      <div><strong>Kalan (Başlangıç):</strong> <span className="text-orange-600">{formatCurrency(selectedOrderDetails.financial_summary.remaining)}</span></div>
                      <div><strong>Toplam Tahsilat:</strong> <span className="text-green-600">{formatCurrency(selectedOrderDetails.financial_summary.total_collected)}</span></div>
                      <div className="col-span-2 pt-2 border-t">
                        <strong>Güncel Bakiye:</strong> 
                        <span className={`text-xl font-bold ml-2 ${selectedOrderDetails.financial_summary.final_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(selectedOrderDetails.financial_summary.final_balance)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ödeme Geçmişi */}
              {selectedOrderDetails.payments && selectedOrderDetails.payments.length > 0 && (
                <Card>
                  <CardHeader className="bg-purple-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Ödeme Geçmişi ({selectedOrderDetails.payments.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      {selectedOrderDetails.payments.map((payment, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <div className="text-sm">
                            <div><strong>{formatDate(payment.created_at)}</strong></div>
                            <div className="text-xs text-gray-600">{payment.payment_method || 'Nakit'}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-green-600">{formatCurrency(payment.amount)}</div>
                            <Badge variant="outline" className="text-xs">{payment.type}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="p-12 text-center text-gray-600">
              Sipariş detayları bulunamadı
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setIsDetailDialogOpen(false)}>Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Admin Pre-Sales Management Component
const AdminPreSales = ({ sessionToken }) => {
  const [preSales, setPreSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveDialog, setApproveDialog] = useState(null);
  const [approvePrice, setApprovePrice] = useState('');
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filter, setFilter] = useState('beklemede');
  const [viewDialog, setViewDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editMeasurements, setEditMeasurements] = useState([]);

  const fetchPreSales = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pre-sales?session_token=${sessionToken}`);
      setPreSales(response.data.pre_sales || []);
    } catch (error) {
      toast.error("Ön satışlar yüklenirken hata");
    }
    setLoading(false);
  };

  useEffect(() => { fetchPreSales(); }, []);

  const handleApprove = async () => {
    if (!approvePrice || parseFloat(approvePrice) <= 0) {
      toast.error("Geçerli bir fiyat girin");
      return;
    }
    try {
      await axios.put(`${API}/pre-sales/${approveDialog}/approve?price=${parseFloat(approvePrice)}&session_token=${sessionToken}`);
      toast.success("Ön satış onaylandı ve satışa aktarıldı!");
      setApproveDialog(null);
      setApprovePrice('');
      fetchPreSales();
    } catch (error) {
      toast.error("Onaylama hatası");
    }
  };

  const handleReject = async () => {
    try {
      await axios.put(`${API}/pre-sales/${rejectDialog}/reject?reason=${encodeURIComponent(rejectReason)}&session_token=${sessionToken}`);
      toast.success("Ön satış reddedildi");
      setRejectDialog(null);
      setRejectReason('');
      fetchPreSales();
    } catch (error) {
      toast.error("Reddetme hatası");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu ön satışı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/pre-sales/${id}?session_token=${sessionToken}`);
      toast.success("Ön satış silindi");
      fetchPreSales();
    } catch (error) {
      toast.error("Silme hatası");
    }
  };

  const openEdit = (ps) => {
    setEditForm({
      end_customer: ps.end_customer || '',
      production_line: ps.production_line || 'lake',
      model: ps.model || '',
      color: ps.color || '',
      door_handle: ps.door_handle || '',
      surface: ps.surface || '',
      installation: ps.installation || '',
      installation_location: ps.installation_location || '',
      inner_frame: ps.inner_frame || '',
      outer_frame: ps.outer_frame || '',
      details: ps.details || '',
      target_installation_date: ps.target_installation_date || '',
      notes: ps.notes || ''
    });
    const rows = parseMeasurementsForView(ps.measurements);
    setEditMeasurements(rows.length > 0 ? rows : [{ id: 1, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
    setEditDialog(ps.id);
  };

  const handleEdit = async () => {
    try {
      const filledMeasurements = editMeasurements.filter(m => m.width || m.height);
      const payload = { ...editForm, measurements: filledMeasurements.length > 0 ? JSON.stringify(filledMeasurements) : "" };
      await axios.put(`${API}/pre-sales/${editDialog}?session_token=${sessionToken}`, payload);
      toast.success("Ön satış güncellendi");
      setEditDialog(null);
      fetchPreSales();
    } catch (error) {
      toast.error("Güncelleme hatası");
    }
  };

  const parseMeasurementsForView = (m) => {
    try { return JSON.parse(m || '[]'); } catch { return []; }
  };

  const filteredPreSales = filter === 'all' ? preSales : preSales.filter(ps => ps.status === filter);
  const pendingCount = preSales.filter(ps => ps.status === 'beklemede').length;

  const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '-';

  return (
    <div className="space-y-6" data-testid="admin-presales">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Müşteri Ön Satışları</h2>
          <p className="text-sm text-gray-500">Müşterilerin gönderdiği sipariş taleplerini yönetin</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-orange-100 text-orange-800 text-lg px-4 py-2">{pendingCount} Onay Bekliyor</Badge>
        )}
      </div>

      <div className="flex gap-2">
        {[{v:'beklemede',l:'Beklemede'},{v:'onaylandi',l:'Onaylandı'},{v:'reddedildi',l:'Reddedildi'},{v:'all',l:'Tümü'}].map(f => (
          <Button key={f.v} variant={filter === f.v ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f.v)}>{f.l}</Button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8">Yükleniyor...</div>
      ) : filteredPreSales.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">Ön satış bulunamadı</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filteredPreSales.map((ps) => (
            <Card key={ps.id} className={ps.status === 'beklemede' ? 'border-orange-300 border-2' : ''}>
              <CardContent className="p-5">
                <div className="flex justify-between items-start">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-lg">{ps.customer_name}</span>
                      <Badge className={
                        ps.status === 'beklemede' ? 'bg-yellow-100 text-yellow-800' :
                        ps.status === 'onaylandi' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {ps.status === 'beklemede' ? 'Onay Bekliyor' : ps.status === 'onaylandi' ? 'Onaylandı' : 'Reddedildi'}
                      </Badge>
                      <Badge variant="outline">{ps.production_line === 'lake' ? 'Lake' : 'Melamin'}</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div><span className="text-gray-500">Nihai Müşteri:</span> {ps.end_customer || '-'}</div>
                      <div><span className="text-gray-500">Model:</span> {ps.model || '-'}</div>
                      <div><span className="text-gray-500">Renk:</span> {ps.color || '-'}</div>
                      <div><span className="text-gray-500">Yüzey:</span> {ps.surface || '-'}</div>
                      <div><span className="text-gray-500">Kapı Kolu:</span> {ps.door_handle || '-'}</div>
                      <div><span className="text-gray-500">İç Pervaz:</span> {ps.inner_frame || '-'}</div>
                      <div><span className="text-gray-500">Dış Pervaz:</span> {ps.outer_frame || '-'}</div>
                      <div><span className="text-gray-500">Hedef Tarih:</span> {ps.target_installation_date || '-'}</div>
                    </div>
                    {ps.measurements && (() => {
                      const rows = parseMeasurementsForView(ps.measurements);
                      return rows.length > 0 ? (
                        <div className="text-sm mt-1">
                          <span className="text-gray-500 font-medium">Ölçüler:</span>
                          <div className="border rounded overflow-x-auto mt-1">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100"><tr>
                                <th className="p-1.5 text-left">#</th><th className="p-1.5 text-left">En</th><th className="p-1.5 text-left">Boy</th>
                                <th className="p-1.5 text-left">Kasa</th><th className="p-1.5 text-left">Yön</th><th className="p-1.5 text-left">Cam</th>
                                <th className="p-1.5 text-left">Oda</th><th className="p-1.5 text-left">Açıklama</th>
                                {rows.some(r => r.quantity > 1) && <th className="p-1.5 text-left">Adet</th>}
                              </tr></thead>
                              <tbody>{rows.map((r, i) => (
                                <tr key={i} className="border-t hover:bg-gray-50">
                                  <td className="p-1.5 text-gray-500">{r.id || i+1}</td><td className="p-1.5 font-medium">{r.width || '-'}</td><td className="p-1.5 font-medium">{r.height || '-'}</td>
                                  <td className="p-1.5">{r.frame || '-'}</td><td className="p-1.5">{r.direction || '-'}</td><td className="p-1.5">{r.glass || '-'}</td>
                                  <td className="p-1.5">{r.room || '-'}</td><td className="p-1.5">{r.description || '-'}</td>
                                  {rows.some(r => r.quantity > 1) && <td className="p-1.5 font-medium">{r.quantity || 1}</td>}
                                </tr>
                              ))}</tbody>
                            </table>
                          </div>
                        </div>
                      ) : null;
                    })()}
                    {ps.notes && <div className="text-sm"><span className="text-gray-500">Notlar:</span> {ps.notes}</div>}
                    {ps.status === 'onaylandi' && <div className="text-sm font-bold text-green-700">Fiyat: {formatCurrency(ps.price)}</div>}
                    {ps.status === 'reddedildi' && ps.rejected_reason && <div className="text-sm text-red-600">Red: {ps.rejected_reason}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <span className="text-xs text-gray-400">{formatDate(ps.created_at)}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setViewDialog(ps)} title="Görüntüle">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {ps.status === 'beklemede' && (
                        <Button size="sm" variant="outline" onClick={() => openEdit(ps)} title="Düzenle">
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(ps.id)} title="Sil">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {ps.status === 'beklemede' && (
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setApproveDialog(ps.id)} data-testid={`approve-${ps.id}`}>
                          <Check className="h-4 w-4 mr-1" /> Onayla
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setRejectDialog(ps.id)} data-testid={`reject-${ps.id}`}>
                          <X className="h-4 w-4 mr-1" /> Reddet
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Approve Dialog */}
      <Dialog open={!!approveDialog} onOpenChange={() => setApproveDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ön Satışı Onayla</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Satış Fiyatı (TL) *</Label>
              <Input type="number" value={approvePrice} onChange={(e) => setApprovePrice(e.target.value)} placeholder="Fiyat girin" data-testid="approve-price-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setApproveDialog(null)}>İptal</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove} data-testid="approve-confirm-btn">Onayla ve Satışa Aktar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ön Satışı Reddet</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Red Sebebi</Label>
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Red sebebi (opsiyonel)" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectDialog(null)}>İptal</Button>
              <Button variant="destructive" onClick={handleReject}>Reddet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewDialog} onOpenChange={() => setViewDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sipariş Detayı</DialogTitle></DialogHeader>
          {viewDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold text-gray-500">Müşteri:</span> <span className="font-bold">{viewDialog.customer_name}</span></div>
                <div><span className="font-semibold text-gray-500">Nihai Müşteri:</span> {viewDialog.end_customer || '-'}</div>
                <div><span className="font-semibold text-gray-500">Üretim Hattı:</span> <Badge variant="outline">{viewDialog.production_line === 'lake' ? 'Lake' : 'Melamin'}</Badge></div>
                <div><span className="font-semibold text-gray-500">Durum:</span> <Badge className={viewDialog.status === 'beklemede' ? 'bg-yellow-100 text-yellow-800' : viewDialog.status === 'onaylandi' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{viewDialog.status === 'beklemede' ? 'Onay Bekliyor' : viewDialog.status === 'onaylandi' ? 'Onaylandı' : 'Reddedildi'}</Badge></div>
                <div><span className="font-semibold text-gray-500">Model:</span> {viewDialog.model || '-'}</div>
                <div><span className="font-semibold text-gray-500">Renk:</span> {viewDialog.color || '-'}</div>
                <div><span className="font-semibold text-gray-500">Kapı Kolu:</span> {viewDialog.door_handle || '-'}</div>
                <div><span className="font-semibold text-gray-500">Yüzey:</span> {viewDialog.surface || '-'}</div>
                <div><span className="font-semibold text-gray-500">Montaj:</span> {viewDialog.installation || '-'}</div>
                <div><span className="font-semibold text-gray-500">Montaj Yeri:</span> {viewDialog.installation_location || '-'}</div>
                <div><span className="font-semibold text-gray-500">İç Pervaz:</span> {viewDialog.inner_frame || '-'}</div>
                <div><span className="font-semibold text-gray-500">Dış Pervaz:</span> {viewDialog.outer_frame || '-'}</div>
                <div><span className="font-semibold text-gray-500">Hedef Montaj:</span> {viewDialog.target_installation_date || '-'}</div>
                <div><span className="font-semibold text-gray-500">Tarih:</span> {formatDate(viewDialog.created_at)}</div>
                {viewDialog.status === 'onaylandi' && <div className="col-span-2"><span className="font-semibold text-gray-500">Fiyat:</span> <span className="font-bold text-green-700">{formatCurrency(viewDialog.price)}</span></div>}
                {viewDialog.status === 'reddedildi' && viewDialog.rejected_reason && <div className="col-span-2"><span className="font-semibold text-gray-500">Red Sebebi:</span> <span className="text-red-600">{viewDialog.rejected_reason}</span></div>}
              </div>
              {viewDialog.measurements && (() => {
                const rows = parseMeasurementsForView(viewDialog.measurements);
                return rows.length > 0 ? (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Ölçü Tablosu</h4>
                    <div className="border rounded overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 text-left">#</th><th className="p-2 text-left">En</th><th className="p-2 text-left">Boy</th>
                            <th className="p-2 text-left">Kasa</th><th className="p-2 text-left">Yön</th><th className="p-2 text-left">Cam</th>
                            <th className="p-2 text-left">Oda</th><th className="p-2 text-left">Açıklama</th>
                            {rows.some(r => r.quantity > 1) && <th className="p-2 text-left">Adet</th>}
                          </tr>
                        </thead>
                        <tbody>{rows.map((r, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{r.id || i+1}</td><td className="p-2">{r.width || '-'}</td><td className="p-2">{r.height || '-'}</td>
                            <td className="p-2">{r.frame || '-'}</td><td className="p-2">{r.direction || '-'}</td><td className="p-2">{r.glass || '-'}</td>
                            <td className="p-2">{r.room || '-'}</td><td className="p-2">{r.description || '-'}</td>
                            {rows.some(r => r.quantity > 1) && <td className="p-2">{r.quantity || 1}</td>}
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                ) : null;
              })()}
              {viewDialog.notes && <div><h4 className="font-semibold text-sm mb-1">İmalat Notları</h4><p className="text-sm bg-yellow-50 p-3 rounded whitespace-pre-wrap">{viewDialog.notes}</p></div>}
              {viewDialog.details && <div><h4 className="font-semibold text-sm mb-1">Detay</h4><p className="text-sm bg-gray-50 p-3 rounded">{viewDialog.details}</p></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ön Satışı Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Nihai Müşteri</Label><Input value={editForm.end_customer || ''} onChange={(e) => setEditForm({...editForm, end_customer: e.target.value})} /></div>
              <div><Label>Üretim Hattı</Label>
                <Select value={editForm.production_line || 'lake'} onValueChange={(v) => setEditForm({...editForm, production_line: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="lake">Lake</SelectItem><SelectItem value="melamin">Melamin</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div><Label>Model</Label><Input value={editForm.model || ''} onChange={(e) => setEditForm({...editForm, model: e.target.value})} /></div>
              <div><Label>Renk</Label><Input value={editForm.color || ''} onChange={(e) => setEditForm({...editForm, color: e.target.value})} /></div>
              <div><Label>Kapı Kolu</Label><Input value={editForm.door_handle || ''} onChange={(e) => setEditForm({...editForm, door_handle: e.target.value})} /></div>
              <div><Label>Yüzey</Label><Input value={editForm.surface || ''} onChange={(e) => setEditForm({...editForm, surface: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div><Label>Montaj</Label><Input value={editForm.installation || ''} onChange={(e) => setEditForm({...editForm, installation: e.target.value})} /></div>
              <div><Label>Montaj Yeri</Label><Input value={editForm.installation_location || ''} onChange={(e) => setEditForm({...editForm, installation_location: e.target.value})} /></div>
              <div><Label>İç Pervaz</Label><Input value={editForm.inner_frame || ''} onChange={(e) => setEditForm({...editForm, inner_frame: e.target.value})} /></div>
              <div><Label>Dış Pervaz</Label><Input value={editForm.outer_frame || ''} onChange={(e) => setEditForm({...editForm, outer_frame: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Hedef Montaj Tarihi</Label><Input type="date" value={editForm.target_installation_date || ''} onChange={(e) => setEditForm({...editForm, target_installation_date: e.target.value})} /></div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Ölçü Tablosu</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => {
                  const newId = Math.max(...editMeasurements.map(m => m.id || 0), 0) + 1;
                  setEditMeasurements([...editMeasurements, { id: newId, width: "", height: "", frame: "", direction: "", glass: "", room: "", description: "", quantity: 1 }]);
                }}><Plus className="h-3 w-3 mr-1" /> Satır Ekle</Button>
              </div>
              <div className="border rounded overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="p-1.5 text-left">#</th><th className="p-1.5 text-left">En</th><th className="p-1.5 text-left">Boy</th>
                    <th className="p-1.5 text-left">Kasa</th><th className="p-1.5 text-left">Yön</th><th className="p-1.5 text-left">Cam</th>
                    <th className="p-1.5 text-left">Oda</th><th className="p-1.5 text-left">Açıklama</th><th className="p-1.5 text-left">Adet</th><th className="p-1.5"></th>
                  </tr></thead>
                  <tbody>{editMeasurements.map((row, index) => (
                    <tr key={row.id || index} className="border-t">
                      <td className="p-1 text-gray-500">{row.id || index+1}</td>
                      <td className="p-1"><Input className="h-7 text-xs" value={row.width || ''} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], width: e.target.value}; setEditMeasurements(n); }} placeholder="En" /></td>
                      <td className="p-1"><Input className="h-7 text-xs" value={row.height || ''} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], height: e.target.value}; setEditMeasurements(n); }} placeholder="Boy" /></td>
                      <td className="p-1"><Input className="h-7 text-xs" value={row.frame || ''} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], frame: e.target.value}; setEditMeasurements(n); }} placeholder="Kasa" /></td>
                      <td className="p-1"><Input className="h-7 text-xs" value={row.direction || ''} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], direction: e.target.value}; setEditMeasurements(n); }} placeholder="Yön" /></td>
                      <td className="p-1"><Input className="h-7 text-xs" value={row.glass || ''} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], glass: e.target.value}; setEditMeasurements(n); }} placeholder="Cam" /></td>
                      <td className="p-1"><Input className="h-7 text-xs" value={row.room || ''} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], room: e.target.value}; setEditMeasurements(n); }} placeholder="Oda" /></td>
                      <td className="p-1"><Input className="h-7 text-xs" value={row.description || ''} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], description: e.target.value}; setEditMeasurements(n); }} placeholder="Açıklama" /></td>
                      <td className="p-1"><Input className="h-7 text-xs w-14 text-center" type="number" min="1" value={row.quantity || 1} onChange={(e) => { const n = [...editMeasurements]; n[index] = {...n[index], quantity: parseInt(e.target.value) || 1}; setEditMeasurements(n); }} /></td>
                      <td className="p-1"><Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => { if (editMeasurements.length > 1) setEditMeasurements(editMeasurements.filter((_, i) => i !== index)); }} disabled={editMeasurements.length <= 1}><Trash2 className="h-3 w-3" /></Button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
            <div><Label>İmalat Notları</Label><textarea className="w-full p-3 border rounded-md min-h-[100px] text-sm" value={editForm.notes || ''} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} /></div>
            <div><Label>Detay</Label><textarea className="w-full p-2 border rounded-md min-h-[60px] text-sm" value={editForm.details || ''} onChange={(e) => setEditForm({...editForm, details: e.target.value})} /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialog(null)}>İptal</Button>
              <Button onClick={handleEdit}>Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Login Page Component
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Kullanıcı adı ve şifre gerekli');
      return;
    }

    setLoading(true);
    const success = await onLogin(username, password);
    setLoading(false);

    if (!success) {
      setPassword(''); // Clear password on failed login
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Nature Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1600257729950-13a634d32697?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxuYXR1cmUlMjBsYW5kc2NhcGUlMjBmb3Jlc3QlMjBtb3VudGFpbiUyMHNjZW5pY3xlbnwwfHx8fDE3NzYwMjcxNzZ8MA&ixlib=rb-4.1.0&q=85')`,
        }}
      />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/40" />
      
      {/* Login Card */}
      <Card className="w-full max-w-md relative z-10 bg-white/90 backdrop-blur-md shadow-2xl border-0" data-testid="login-card">
        <CardHeader className="text-center">
          {/* ESKAY AHŞAP Logo */}
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_finsys/artifacts/2r02pdgk_SUNTALAM-F3-K%20Kopyas%C4%B1.png" 
              alt="ESKAY AHŞAP" 
              className="h-24 mx-auto object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">ERP Sistemi</CardTitle>
          <CardDescription className="text-gray-600">Hesabınıza giriş yapın</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-gray-700">Kullanıcı Adı</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Kullanıcı adınızı girin"
                disabled={loading}
                required
                data-testid="login-username-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-gray-700">Şifre</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Şifrenizi girin"
                disabled={loading}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ element, currentUser, requiredScreens = [] }) => {
  // Admin can access all screens
  if (currentUser?.role === 'admin') {
    return element;
  }
  
  // Bayi users: redirect root path to bayi dashboard
  if (currentUser?.role === 'bayi' && requiredScreens.includes('/')) {
    window.location.href = '/bayi/dashboard';
    return null;
  }
  
  // Installation users: allow root path access for installer dashboard but block production
  if (currentUser?.role === 'installation') {
    if (requiredScreens.includes('/') || requiredScreens.includes('/installations') || requiredScreens.includes('/collection-tracking') || requiredScreens.includes('/customer-search')) {
      return element;
    }
    // Block access to production and other restricted screens for installation users
    const restrictedScreens = ['/production', '/admin/users', '/admin/personnel', '/admin/data'];
    const isRestricted = requiredScreens.some(screen => restrictedScreens.includes(screen));
    if (isRestricted) {
      return (
        <div className="flex justify-center items-center h-96">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-red-600">Erişim Engellendi</CardTitle>
              <CardDescription>
                Bu sayfaya montaj personeli olarak erişim yetkiniz bulunmamaktadır.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      );
    }
  }
  
  // Bayi can access their specific screens
  if (currentUser?.role === 'bayi') {
    const bayiScreens = ['/bayi/sales', '/bayi/orders', '/bayi/account', '/bayi/dashboard', '/bayi/internal/customers', '/bayi/internal/suppliers', '/bayi/internal/sales', '/bayi/internal/purchases', '/bayi/internal/collections', '/bayi/internal/payments', '/bayi/internal/data', '/bayi/profile'];
    const hasAccess = requiredScreens.some(screen => bayiScreens.includes(screen));
    if (hasAccess) {
      return element;
    }
  }
  
  // Check if user has access to required screens
  if (requiredScreens.length > 0) {
    const hasAccess = requiredScreens.some(screen => 
      currentUser?.allowed_screens?.includes(screen) || 
      currentUser?.allowed_screens?.includes('*')
    );
    
    if (!hasAccess) {
      return (
        <div className="flex justify-center items-center h-96">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-red-600">Erişim Engellendi</CardTitle>
              <CardDescription>
                Bu sayfaya erişim yetkiniz bulunmamaktadır.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="text-sm text-gray-600">
                Gerekli yetki: {requiredScreens.join(', ')}
              </div>
              <div className="text-sm text-gray-600 mt-2">
                Mevcut yetkileriniz: {currentUser?.allowed_screens?.join(', ') || 'Hiçbiri'}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
  }
  
  return element;
};

// Main App Component (Authenticated)
const MainApp = ({ currentUser, sessionToken, onLogout }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation onLogout={onLogout} currentUser={currentUser} />
      <main className="pt-16 p-8 overflow-auto">
        <Routes>
          <Route path="/" element={
            <ProtectedRoute 
              element={
                currentUser?.role === 'installation' ? 
                  <InstallerDashboard currentUser={currentUser} sessionToken={sessionToken} /> :
                  <Dashboard sessionToken={sessionToken} currentUser={currentUser} />
              } 
              currentUser={currentUser} 
              requiredScreens={['/']} 
            />
          } />
          <Route path="/customer-search" element={
            <ProtectedRoute 
              element={<CustomerSearch sessionToken={sessionToken} currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/customer-search']} 
            />
          } />
          <Route path="/agenda" element={
            <ProtectedRoute 
              element={<Agenda sessionToken={sessionToken} currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/agenda']} 
            />
          } />
          <Route path="/control" element={
            <ProtectedRoute 
              element={<Control sessionToken={sessionToken} currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/control']} 
            />
          } />
          <Route path="/customers" element={
            <ProtectedRoute 
              element={<Customers currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/customers']} 
            />
          } />
          <Route path="/bayiler" element={
            <ProtectedRoute 
              element={<Bayiler currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayiler']} 
            />
          } />
          <Route path="/bayi-orders" element={
            <ProtectedRoute 
              element={<BayiOrders currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi-orders']} 
            />
          } />
          <Route path="/bayi/sales" element={
            <ProtectedRoute 
              element={<BayiSales currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/sales']} 
            />
          } />
          <Route path="/bayi/orders" element={
            <ProtectedRoute 
              element={<BayiMyOrders currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/orders']} 
            />
          } />
          <Route path="/bayi/account" element={
            <ProtectedRoute 
              element={<BayiAccount currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/account']} 
            />
          } />
          
          {/* Bayi İç Muhasebe Routes */}
          <Route path="/bayi/dashboard" element={
            <ProtectedRoute 
              element={<BayiInternalDashboard currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/dashboard']} 
            />
          } />
          <Route path="/bayi/internal/customers" element={
            <ProtectedRoute 
              element={<BayiInternalCustomers currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/internal/customers']} 
            />
          } />
          <Route path="/bayi/internal/suppliers" element={
            <ProtectedRoute 
              element={<BayiInternalSuppliers currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/internal/suppliers']} 
            />
          } />
          <Route path="/bayi/internal/sales" element={
            <ProtectedRoute 
              element={<BayiInternalSales currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/internal/sales']} 
            />
          } />
          <Route path="/bayi/internal/purchases" element={
            <ProtectedRoute 
              element={<BayiInternalPurchases currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/internal/purchases']} 
            />
          } />
          <Route path="/bayi/internal/collections" element={
            <ProtectedRoute 
              element={<BayiInternalCollections currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/internal/collections']} 
            />
          } />
          <Route path="/bayi/internal/payments" element={
            <ProtectedRoute 
              element={<BayiInternalPayments currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/internal/payments']} 
            />
          } />
          <Route path="/bayi/internal/data" element={
            <ProtectedRoute 
              element={<BayiInternalDataManagement currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bayi/internal/data']} 
            />
          } />
          <Route path="/suppliers" element={
            <ProtectedRoute 
              element={<Suppliers />} 
              currentUser={currentUser} 
              requiredScreens={['/suppliers']} 
            />
          } />
          <Route path="/sales" element={
            <ProtectedRoute 
              element={<Sales sessionToken={sessionToken} currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/sales']} 
            />
          } />
          <Route path="/pre-sales" element={
            <ProtectedRoute 
              element={<AdminPreSales sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/pre-sales']} 
            />
          } />
          <Route path="/purchases" element={
            <ProtectedRoute 
              element={<Purchases sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/purchases']} 
            />
          } />
          <Route path="/temlik" element={
            <ProtectedRoute 
              element={<Temlik sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/temlik']} 
            />
          } />
          <Route path="/receipts" element={
            <ProtectedRoute 
              element={<CollectionReceipts />} 
              currentUser={currentUser} 
              requiredScreens={['/receipts']} 
            />
          } />
          <Route path="/payments" element={
            <ProtectedRoute 
              element={<PaymentReceipts />} 
              currentUser={currentUser} 
              requiredScreens={['/payments']} 
            />
          } />
          
          {/* Kasa ve Banka Defteri */}
          <Route path="/cash-ledger" element={
            <ProtectedRoute 
              element={<CashLedger sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/cash-ledger']} 
            />
          } />
          <Route path="/bank-ledger" element={
            <ProtectedRoute 
              element={<BankLedger sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bank-ledger']} 
            />
          } />
          
          {/* HIDDEN per user request
          <Route path="/supplier-payments" element={
            <ProtectedRoute 
              element={<SupplierPayments sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/supplier-payments']} 
            />
          } />
          */}
          <Route path="/collection-tracking" element={
            <ProtectedRoute 
              element={<CollectionTracking sessionToken={sessionToken} currentUser={currentUser} />} 
              currentUser={currentUser} 
              requiredScreens={['/collection-tracking']} 
            />
          } />
          <Route path="/expenses" element={
            <ProtectedRoute 
              element={<Expenses sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/expenses']} 
            />
          } />
          <Route path="/installations" element={
            <ProtectedRoute 
              element={<InstallationSchedules currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/installations']} 
            />
          } />
          <Route path="/target-installations" element={
            <ProtectedRoute 
              element={<TargetInstallations currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/target-installations']} 
            />
          } />
          <Route path="/cash-transactions" element={
            <ProtectedRoute 
              element={<CashTransactions sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/cash-transactions']} 
            />
          } />
          <Route path="/bank-transactions" element={
            <ProtectedRoute 
              element={<BankTransactions sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/bank-transactions']} 
            />
          } />
          <Route path="/expenses" element={
            <ProtectedRoute 
              element={<Expenses sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/expenses']} 
            />
          } />
          <Route path="/production" element={
            <ProtectedRoute 
              element={<Production currentUser={currentUser} sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/production']} 
            />
          } />
          <Route path="/production-monitoring" element={
            <ProtectedRoute 
              element={<ProductionMonitoring sessionToken={sessionToken} />} 
              currentUser={currentUser} 
              requiredScreens={['/production-monitoring']} 
            />
          } />
          {currentUser?.role === 'admin' && (
            <>
              {/* <Route path="/employees" element={<EmployeeManagement currentUser={currentUser} />} /> */}
              {/* <Route path="/salary-payments" element={<SalaryPayments currentUser={currentUser} />} /> */}
              <Route path="/admin/users" element={<UserManagement currentUser={currentUser} sessionToken={sessionToken} />} />
              <Route path="/admin/personnel" element={<PersonnelManagement currentUser={currentUser} sessionToken={sessionToken} />} />
              <Route path="/admin/activities" element={<AdminActivities sessionToken={sessionToken} currentUser={currentUser} />} />
              <Route path="/admin/data" element={<DataManagement currentUser={currentUser} sessionToken={sessionToken} />} />
              <Route path="/admin/backup" element={<GoogleDriveBackup currentUser={currentUser} sessionToken={sessionToken} />} />
              <Route path="/admin/reports" element={<ReportsPage sessionToken={sessionToken} />} />
              <Route path="/stock-management" element={<StockManagement sessionToken={sessionToken} />} />
              <Route path="/salary-management" element={<SalaryManagement sessionToken={sessionToken} currentUser={currentUser} />} />
            </>
          )}
        </Routes>
      </main>
      <Toaster />
    </div>
  );
};

// Customer Search Component - Müşteri Detay Arama Sayfası
const CustomerSearch = ({ sessionToken, currentUser }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Müşteri arama fonksiyonu
  const handleSearch = async (value) => {
    setSearchTerm(value);
    
    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers/search`, {
        params: { 
          q: value,
          session_token: sessionToken 
        }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("Müşteri arama hatası:", error);
      toast.error("Müşteri arama sırasında hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Müşteri seçimi ve detay yükleme
  const handleCustomerSelect = async (customer) => {
    setSelectedCustomer(customer);
    setDetailsLoading(true);
    setSearchResults([]);  // Dropdown'u kapat
    setSearchTerm(`${customer.name} ${customer.surname}`);  // Seçilen müşteriyi göster

    try {
      // Müşteri detaylarını al
      const customerDetailsResponse = await axios.get(`${API}/customers/${customer.id}/details`, {
        params: { session_token: sessionToken }
      });

      setCustomerDetails(customerDetailsResponse.data);
    } catch (error) {
      console.error("Müşteri detayları yüklenirken hata:", error);
      toast.error("Müşteri detayları yüklenirken hata oluştu");
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      beklemede: { color: 'bg-orange-100 text-orange-800', text: 'Beklemede' },
      uretimde: { color: 'bg-blue-100 text-blue-800', text: 'Üretimde' },
      boyahane: { color: 'bg-purple-100 text-purple-800', text: 'Boyahane' },
      tamamlandi: { color: 'bg-green-100 text-green-800', text: 'Tamamlandı' },
      montajda: { color: 'bg-yellow-100 text-yellow-800', text: 'Montajda' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.text}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Müşteri Detay Arama</h1>
          <p className="text-muted-foreground">
            Müşteri adı veya telefon numarası ile arama yapın ve tüm detayları görüntüleyin
          </p>
        </div>
      </div>

      {/* Arama Bölümü */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Müşteri Arama
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="relative">
              <Input
                placeholder="Müşteri adı veya telefon numarası ile ara..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full"
              />
              {loading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Arama Sonuçları ({searchResults.length}):</h3>
                <div className="max-h-96 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {searchResults.slice(0, 50).map((customer) => (
                    <div 
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      className="p-3 border rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{customer.name} {customer.surname}</p>
                          <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline cursor-pointer text-sm" onClick={(e) => e.stopPropagation()}>
                            📞 {customer.phone}
                          </a>
                        </div>
                        <Badge variant="secondary">#{customer.serial_number}</Badge>
                      </div>
                    </div>
                  ))}
                  {searchResults.length > 50 && (
                    <div className="p-2 text-center text-xs text-gray-500 bg-gray-50 rounded">
                      {searchResults.length - 50} müşteri daha var, aramayı daraltın
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Müşteri Detayları */}
      {selectedCustomer && (
        <div className="space-y-6">
          {detailsLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2">Müşteri detayları yükleniyor...</span>
              </CardContent>
            </Card>
          ) : customerDetails ? (
            <>
              {/* Müşteri Temel Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {customerDetails.customer.name} {customerDetails.customer.surname}
                    <Badge variant="outline">#{customerDetails.customer.serial_number}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="font-medium text-gray-600">Telefon</p>
                      <a href={`tel:${customerDetails.customer.phone}`} className="text-blue-600 hover:underline cursor-pointer">
                        📞 {customerDetails.customer.phone}
                      </a>
                    </div>
                    <div className="md:col-span-2">
                      <p className="font-medium text-gray-600">Adres</p>
                      <p>{customerDetails.customer.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Mali Durum */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Mali Durum
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600">Toplam Satış Tutarı</p>
                      <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(customerDetails.financial_summary?.total_sales || 0)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600">Toplam Tahsil Edilen</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(customerDetails.financial_summary?.total_collected || 0)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm text-gray-600">Kalan Borç/Alacak</p>
                      <p className={`text-xl font-bold ${(customerDetails.financial_summary?.remaining_balance || 0) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(customerDetails.financial_summary?.remaining_balance || 0)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Mali Durum'a Ödeme Bilgileri Eklendi */}
                  {(customerDetails.customer.last_transaction_payment_method || customerDetails.customer.payment_method) && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-800 mb-3">💳 Ödeme Bilgileri</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {customerDetails.customer.last_transaction_payment_method && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="font-medium text-blue-800 text-sm">Son İşlem Ödeme Yöntemi</p>
                            <p className="text-blue-700">{customerDetails.customer.last_transaction_payment_method}</p>
                          </div>
                        )}
                        {customerDetails.customer.last_transaction_payment_description && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="font-medium text-blue-800 text-sm">Son İşlem Ödeme Notu</p>
                            <p className="text-blue-700">{customerDetails.customer.last_transaction_payment_description}</p>
                          </div>
                        )}
                        {customerDetails.customer.payment_method && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="font-medium text-gray-700 text-sm">Varsayılan Ödeme Yöntemi</p>
                            <p className="text-gray-600">{customerDetails.customer.payment_method}</p>
                          </div>
                        )}
                        {customerDetails.customer.payment_notes && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="font-medium text-gray-700 text-sm">Ödeme Notları</p>
                            <p className="text-gray-600">{customerDetails.customer.payment_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ödeme Bilgileri */}
              {(customerDetails.customer.last_transaction_payment_method || customerDetails.customer.payment_method) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Ödeme Bilgileri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {customerDetails.customer.last_transaction_payment_method && (
                        <div>
                          <p className="font-medium text-gray-600">Son İşlem Ödeme Yöntemi</p>
                          <p>{customerDetails.customer.last_transaction_payment_method}</p>
                        </div>
                      )}
                      {customerDetails.customer.last_transaction_payment_description && (
                        <div>
                          <p className="font-medium text-gray-600">Son İşlem Ödeme Notu</p>
                          <p>{customerDetails.customer.last_transaction_payment_description}</p>
                        </div>
                      )}
                      {customerDetails.customer.payment_method && (
                        <div>
                          <p className="font-medium text-gray-600">Varsayılan Ödeme Yöntemi</p>
                          <p>{customerDetails.customer.payment_method}</p>
                        </div>
                      )}
                      {customerDetails.customer.payment_notes && (
                        <div>
                          <p className="font-medium text-gray-600">Ödeme Notları</p>
                          <p>{customerDetails.customer.payment_notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Hesap Hareketleri */}
              {customerDetails.transactions && customerDetails.transactions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="h-5 w-5" />
                      Hesap Hareketleri ({customerDetails.transactions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {customerDetails.transactions.map((transaction) => (
                        <div key={transaction.id} className="flex justify-between items-center p-3 border rounded">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{transaction.description}</p>
                              {/* Nihai Müşteri Badge - Sadece satışlarda göster */}
                              {transaction.end_customer && transaction.transaction_type === 'sale' && (
                                <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded border border-yellow-300 font-medium">
                                  👤 {transaction.end_customer}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{formatDate(transaction.created_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${transaction.transaction_type === 'sale' ? 'text-blue-600' : 'text-green-600'}`}>
                              {transaction.transaction_type === 'sale' ? '+' : '-'}{formatCurrency(transaction.amount)}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {transaction.transaction_type === 'sale' ? 'Satış' : 'Ödeme'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Satış Detayları */}
              {customerDetails.sales && customerDetails.sales.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      Satış Detayları ({customerDetails.sales.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {customerDetails.sales.map((sale, index) => (
                        <div key={sale.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold">Satış #{index + 1}</p>
                                {/* Nihai Müşteri Badge */}
                                {sale.end_customer && (
                                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded font-medium">
                                    👤 {sale.end_customer}
                                  </span>
                                )}
                                {/* Üretim Durumu Badge */}
                                {sale.production_status && (
                                  <div className="flex items-center gap-2">
                                    {getStatusBadge(sale.production_status)}
                                    {sale.production_line && (
                                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                        📍 {sale.production_line}
                                      </span>
                                    )}
                                    {sale.boyahane_info && sale.production_status === 'boyahane' && (
                                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                        🎨 {sale.boyahane_info}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{formatDate(sale.created_at)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-blue-600">{formatCurrency(sale.amount)}</p>
                              {sale.target_installation_date && (
                                <p className="text-xs text-gray-500">Hedef Montaj: {formatDate(sale.target_installation_date)}</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Temel Ürün Bilgileri */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-3">
                            {sale.model && <div><span className="font-medium">Model:</span> {sale.model}</div>}
                            {sale.color && <div><span className="font-medium">Renk:</span> {sale.color}</div>}
                            {sale.door_handle && <div><span className="font-medium">Kapı Kolu:</span> {sale.door_handle}</div>}
                            {sale.surface && <div><span className="font-medium">Yüzey:</span> {sale.surface}</div>}
                          </div>

                          {/* Montaj ve Konum Bilgileri */}
                          {(sale.installation || sale.installation_location || sale.inner_frame || sale.outer_frame) && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-3 bg-gray-50 p-3 rounded">
                              <div className="col-span-full mb-2">
                                <span className="font-medium text-gray-700">🔧 Montaj Bilgileri</span>
                              </div>
                              {sale.installation && <div><span className="font-medium">Montaj Türü:</span> {sale.installation}</div>}
                              {sale.installation_location && <div><span className="font-medium">Montaj Yeri:</span> {sale.installation_location}</div>}
                              {sale.inner_frame && <div><span className="font-medium">İç Kasa:</span> {sale.inner_frame}</div>}
                              {sale.outer_frame && <div><span className="font-medium">Dış Kasa:</span> {sale.outer_frame}</div>}
                            </div>
                          )}

                          {/* Ölçüler */}
                          {sale.measurements && (
                            <div className="mb-3">
                              <p className="font-medium text-sm mb-2">📏 Ölçüler:</p>
                              <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200">
                                {(() => {
                                  try {
                                    const measurements = JSON.parse(sale.measurements);
                                    if (measurements && measurements.length > 0) {
                                      return (
                                        <div className="overflow-x-auto">
                                          <table className="w-full text-xs">
                                            <thead>
                                              <tr className="border-b border-blue-300">
                                                <th className="text-center p-2 font-bold">Sıra</th>
                                                <th className="text-center p-2 font-bold">En</th>
                                                <th className="text-center p-2 font-bold">Boy</th>
                                                <th className="text-center p-2 font-bold">Kasa</th>
                                                <th className="text-center p-2 font-bold">Yön</th>
                                                <th className="text-center p-2 font-bold">Cam</th>
                                                <th className="text-center p-2 font-bold">Oda</th>
                                                <th className="text-center p-2 font-bold">Açıklama</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {measurements.map((measurement, idx) => (
                                                <tr key={idx} className="border-b border-blue-200">
                                                  <td className="p-2 text-center">{idx + 1}</td>
                                                  <td className="p-2 text-center">{measurement.width || '-'}</td>
                                                  <td className="p-2 text-center">{measurement.height || '-'}</td>
                                                  <td className="p-2 text-center">{measurement.thickness || measurement.frame || '-'}</td>
                                                  <td className="p-2 text-center">{measurement.direction || '-'}</td>
                                                  <td className="p-2 text-center">{measurement.glass || '-'}</td>
                                                  <td className="p-2 text-center">{measurement.room || '-'}</td>
                                                  <td className="p-2 text-center">{measurement.notes || '-'}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      );
                                    } else {
                                      return <p className="text-gray-500">Ölçü bilgisi yok</p>;
                                    }
                                  } catch (e) {
                                    return <p className="text-gray-500">Ölçü formatı hatalı: {sale.measurements}</p>;
                                  }
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Ödeme Bilgileri */}
                          {(sale.payment_method || sale.payment_description) && (
                            <div className="mb-3 bg-green-50 p-3 rounded border border-green-200">
                              <p className="font-medium text-sm mb-2 text-green-800">💳 Ödeme Bilgileri</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                {sale.payment_method && (
                                  <div>
                                    <span className="font-medium text-green-700">Ödeme Yöntemi:</span>
                                    <span className="ml-2 text-green-600">{sale.payment_method}</span>
                                  </div>
                                )}
                                {sale.payment_description && (
                                  <div>
                                    <span className="font-medium text-green-700">Ödeme Açıklaması:</span>
                                    <span className="ml-2 text-green-600">{sale.payment_description}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Ek Detaylar */}
                          {sale.details && (
                            <div className="mb-3 bg-yellow-50 p-3 rounded border border-yellow-200">
                              <p className="font-medium text-sm mb-1 text-yellow-800">📝 Ek Detaylar:</p>
                              <p className="text-sm text-yellow-700">{sale.details}</p>
                            </div>
                          )}

                          {/* Açıklama */}
                          {sale.description && (
                            <div className="bg-gray-50 p-3 rounded border">
                              <p className="font-medium text-sm mb-1 text-gray-700">📋 Satış Açıklaması:</p>
                              <p className="text-sm text-gray-600">{sale.description}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Üretim Durumu */}
              {customerDetails.productions && customerDetails.productions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Factory className="h-5 w-5" />
                      Üretim Aşaması ({customerDetails.productions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {customerDetails.productions.map((production) => (
                        <div key={production.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{production.model || 'Model belirtilmemiş'}</p>
                              <p className="text-sm text-gray-600">Üretim Hattı: {production.production_line}</p>
                            </div>
                            <div className="text-right">
                              {getStatusBadge(production.status)}
                              {production.boyahane_info && (
                                <p className="text-xs text-purple-600 mt-1">Boyahane: {production.boyahane_info}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p><strong>Başlangıç:</strong> {formatDate(production.created_at)}</p>
                              {production.production_date && (
                                <p><strong>Üretim Tarihi:</strong> {formatDate(production.production_date)}</p>
                              )}
                            </div>
                            <div>
                              {production.target_installation_date && (
                                <p><strong>Hedef Montaj:</strong> {formatDate(production.target_installation_date)}</p>
                              )}
                              {production.ready_date && (
                                <p><strong>Hazır:</strong> {formatDate(production.ready_date)}</p>
                              )}
                            </div>
                          </div>

                          {production.notes && (
                            <p className="text-sm text-gray-600 mt-2"><strong>Notlar:</strong> {production.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Montaj Durumu */}
              {customerDetails.installations && customerDetails.installations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wrench className="h-5 w-5" />
                      Montaj Aşaması ({customerDetails.installations.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {customerDetails.installations.map((installation) => (
                        <div key={installation.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{installation.door_type}</p>
                              <p className="text-sm text-gray-600">Montajcı: {installation.installer_name}</p>
                            </div>
                            <div className="text-right">
                              {getStatusBadge(installation.status)}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              {installation.target_date && (
                                <p><strong>Hedef Tarih:</strong> {formatDate(installation.target_date)}</p>
                              )}
                              {installation.installation_date && (
                                <p><strong>Montaj Tarihi:</strong> {formatDate(installation.installation_date)}</p>
                              )}
                            </div>
                            <div>
                              {installation.completed_date && (
                                <p><strong>Tamamlanma:</strong> {formatDate(installation.completed_date)}</p>
                              )}
                            </div>
                          </div>

                          {installation.has_incomplete_work && (
                            <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                              <p className="text-sm text-orange-800 font-medium">⚠️ Eksik İş Var</p>
                              {installation.incomplete_work_notes && (
                                <p className="text-sm text-orange-700 mt-1">
                                  <strong>Notlar:</strong> {installation.incomplete_work_notes}
                                </p>
                              )}
                            </div>
                          )}

                          {installation.notes && (
                            <p className="text-sm text-gray-600 mt-2"><strong>Notlar:</strong> {installation.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tahsilat Durumu */}
              {customerDetails.collections && customerDetails.collections.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      Tahsilat Aşaması ({customerDetails.collections.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {customerDetails.collections.map((collection) => (
                        <div key={collection.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{formatCurrency(collection.amount)}</p>
                              <p className="text-sm text-gray-600">{formatDate(collection.created_at)}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant={collection.is_collected ? "default" : "secondary"}>
                                {collection.is_collected ? 'Tahsil Edildi' : 'Beklemede'}
                              </Badge>
                            </div>
                          </div>
                          
                          {collection.collection_notes && (
                            <p className="text-sm text-gray-600 mt-2"><strong>Notlar:</strong> {collection.collection_notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Production Monitoring Component - Üretim İzleme Sekmesi
const ProductionMonitoring = ({ sessionToken }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/summary`, {
        params: { session_token: sessionToken }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Dashboard verisi yüklenirken hata:", error);
      toast.error("Üretim verileri yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const doors = dashboardData?.doors || {};
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">🏭 Üretim İzleme</h1>
          <p className="text-gray-600 mt-2">
            Üretim hatları, montaj bölümü ve teslim istatistikleri
          </p>
        </div>
        <Button onClick={fetchDashboardData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Üretim Hatlarındaki Kapılar */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Factory className="h-5 w-5 mr-2 text-blue-600" />
          Üretim Hatlarında
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Melamin Üretim Hattı
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {doors.in_production?.melamin || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Lake Üretim Hattı
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {doors.in_production?.lake || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Toplam Üretimde
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {doors.in_production?.total || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Montaj Bölümündeki Kapılar */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Wrench className="h-5 w-5 mr-2 text-orange-600" />
          Montaj Bölümünde
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Melamin Montaj Bekleyen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {doors.in_installation?.melamin || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Lake Montaj Bekleyen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {doors.in_installation?.lake || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Toplam Montajda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {doors.in_installation?.total || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bu Ay Teslim Edilen Kapılar */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
          Bu Ay Teslim Edilen Kapılar
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Melamin Teslim
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {doors.delivered?.monthly?.melamin || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Lake Teslim
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {doors.delivered?.monthly?.lake || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Bu Ay Toplam Teslim
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {doors.delivered?.monthly?.total || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">adet kapı</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Özet İstatistikler */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-gray-600" />
          Özet İstatistikler
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Toplam Süreçte Olan Kapılar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Üretimde:</span>
                  <Badge variant="secondary">{doors.in_production?.total || 0} kapı</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Montajda:</span>
                  <Badge variant="secondary">{doors.in_installation?.total || 0} kapı</Badge>
                </div>
                <hr />
                <div className="flex justify-between items-center font-medium">
                  <span>Toplam İşlemde:</span>
                  <Badge className="bg-blue-600">
                    {(doors.in_production?.total || 0) + (doors.in_installation?.total || 0)} kapı
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Teslim Performansı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Bu Ay Teslim:</span>
                  <Badge variant="default" className="bg-green-600">
                    {doors.delivered?.monthly?.total || 0} kapı
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Günlük Ortalama:</span>
                  <Badge variant="outline">
                    {Math.round((doors.delivered?.monthly?.total || 0) / new Date().getDate())} kapı/gün
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};


// ===== SALARY MANAGEMENT COMPONENT - PERSONEL MAAŞ YÖNETİMİ =====
const SalaryManagement = ({ sessionToken, currentUser }) => {
  const [employees, setEmployees] = useState([]);
  const [transactions, setTransactions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Dialog states
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
  const [isEditEmployeeOpen, setIsEditEmployeeOpen] = useState(false);
  const [isAccrualOpen, setIsAccrualOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isTransactionsOpen, setIsTransactionsOpen] = useState(false);
  
  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    position: "",
    salary: "",
    start_date: ""
  });
  
  const [accrualForm, setAccrualForm] = useState({
    amount: "",
    description: ""
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "Nakit",
    description: ""
  });

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/employees`, {
        params: { session_token: sessionToken }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error("Personel listesi getirme hatası:", error);
      toast.error("Personel listesi yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee balance
  const fetchEmployeeBalance = async (employeeId) => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/balance`, {
        params: { session_token: sessionToken }
      });
      return response.data;
    } catch (error) {
      console.error("Bakiye getirme hatası:", error);
      return null;
    }
  };

  // Fetch employee transactions
  const fetchEmployeeTransactions = async (employeeId) => {
    try {
      const response = await axios.get(`${API}/salary-transactions/${employeeId}`, {
        params: { session_token: sessionToken }
      });
      setTransactions(prev => ({
        ...prev,
        [employeeId]: response.data
      }));
    } catch (error) {
      console.error("Hareketler getirme hatası:", error);
      toast.error("Hareketler yüklenemedi");
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Add Employee
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/employees`,
        employeeForm,
        { params: { session_token: sessionToken } }
      );
      toast.success("Personel başarıyla eklendi");
      setIsAddEmployeeOpen(false);
      setEmployeeForm({
        first_name: "",
        last_name: "",
        phone: "",
        position: "",
        salary: "",
        start_date: ""
      });
      fetchEmployees();
    } catch (error) {
      console.error("Personel ekleme hatası:", error);
      toast.error("Personel eklenemedi");
    }
  };

  // Update Employee
  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/employees/${selectedEmployee.id}`,
        employeeForm,
        { params: { session_token: sessionToken } }
      );
      toast.success("Personel başarıyla güncellendi");
      setIsEditEmployeeOpen(false);
      fetchEmployees();
    } catch (error) {
      console.error("Personel güncelleme hatası:", error);
      toast.error("Personel güncellenemedi");
    }
  };

  // Delete Employee
  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm("Bu personeli silmek istediğinizden emin misiniz?")) {
      return;
    }
    try {
      await axios.delete(`${API}/employees/${employeeId}`, {
        params: { session_token: sessionToken }
      });
      toast.success("Personel başarıyla silindi");
      fetchEmployees();
    } catch (error) {
      console.error("Personel silme hatası:", error);
      toast.error("Personel silinemedi");
    }
  };

  // Add Accrual
  const handleAddAccrual = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/salary-transactions/accrual`,
        {
          employee_id: selectedEmployee.id,
          ...accrualForm
        },
        { params: { session_token: sessionToken } }
      );
      toast.success("Hak ediş başarıyla eklendi");
      setIsAccrualOpen(false);
      setAccrualForm({ amount: "", description: "" });
      fetchEmployees();
    } catch (error) {
      console.error("Hak ediş ekleme hatası:", error);
      toast.error("Hak ediş eklenemedi");
    }
  };

  // Add Payment
  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/salary-transactions/payment`,
        {
          employee_id: selectedEmployee.id,
          ...paymentForm
        },
        { params: { session_token: sessionToken } }
      );
      toast.success("Ödeme başarıyla yapıldı");
      setIsPaymentOpen(false);
      setPaymentForm({ amount: "", payment_method: "Nakit", description: "" });
      fetchEmployees();
    } catch (error) {
      console.error("Ödeme yapma hatası:", error);
      toast.error("Ödeme yapılamadı");
    }
  };

  // Open Edit Dialog
  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeForm({
      first_name: employee.first_name,
      last_name: employee.last_name,
      phone: employee.phone,
      position: employee.position,
      salary: employee.salary.toString(),
      start_date: employee.start_date
    });
    setIsEditEmployeeOpen(true);
  };

  // Open Transactions Dialog
  const openTransactionsDialog = async (employee) => {
    setSelectedEmployee(employee);
    await fetchEmployeeTransactions(employee.id);
    setIsTransactionsOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Personel Maaş Yönetimi</h1>
          <p className="text-gray-600 mt-1">Personel bilgileri ve maaş hareketleri</p>
        </div>
        <Button onClick={() => setIsAddEmployeeOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Personel Ekle
        </Button>
      </div>

      {/* Employee Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.map((employee) => (
          <EmployeeCard
            key={employee.id}
            employee={employee}
            onEdit={() => openEditDialog(employee)}
            onDelete={() => handleDeleteEmployee(employee.id)}
            onViewTransactions={() => openTransactionsDialog(employee)}
            onAddAccrual={() => {
              setSelectedEmployee(employee);
              setIsAccrualOpen(true);
            }}
            onAddPayment={() => {
              setSelectedEmployee(employee);
              setIsPaymentOpen(true);
            }}
            sessionToken={sessionToken}
          />
        ))}
      </div>

      {employees.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="h-12 w-12 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-600">Henüz personel eklenmemiş</p>
            <Button onClick={() => setIsAddEmployeeOpen(true)} className="mt-4">
              İlk Personeli Ekle
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Employee Dialog */}
      <Dialog open={isAddEmployeeOpen} onOpenChange={setIsAddEmployeeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Personel Ekle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEmployee} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Ad</Label>
                <Input
                  id="first_name"
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, first_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Soyad</Label>
                <Input
                  id="last_name"
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, last_name: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="position">Pozisyon</Label>
              <Input
                id="position"
                value={employeeForm.position}
                onChange={(e) => setEmployeeForm({...employeeForm, position: e.target.value})}
                placeholder="Örn: Montajcı, Satış, Üretim"
                required
              />
            </div>
            <div>
              <Label htmlFor="salary">Maaş Tutarı (₺)</Label>
              <Input
                id="salary"
                type="number"
                step="0.01"
                value={employeeForm.salary}
                onChange={(e) => setEmployeeForm({...employeeForm, salary: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="start_date">İşe Başlama Tarihi</Label>
              <Input
                id="start_date"
                type="date"
                value={employeeForm.start_date}
                onChange={(e) => setEmployeeForm({...employeeForm, start_date: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAddEmployeeOpen(false)}>
                İptal
              </Button>
              <Button type="submit">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={isEditEmployeeOpen} onOpenChange={setIsEditEmployeeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Personel Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEmployee} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_first_name">Ad</Label>
                <Input
                  id="edit_first_name"
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, first_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_last_name">Soyad</Label>
                <Input
                  id="edit_last_name"
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, last_name: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_phone">Telefon</Label>
              <Input
                id="edit_phone"
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_position">Pozisyon</Label>
              <Input
                id="edit_position"
                value={employeeForm.position}
                onChange={(e) => setEmployeeForm({...employeeForm, position: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_salary">Maaş Tutarı (₺)</Label>
              <Input
                id="edit_salary"
                type="number"
                step="0.01"
                value={employeeForm.salary}
                onChange={(e) => setEmployeeForm({...employeeForm, salary: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_start_date">İşe Başlama Tarihi</Label>
              <Input
                id="edit_start_date"
                type="date"
                value={employeeForm.start_date}
                onChange={(e) => setEmployeeForm({...employeeForm, start_date: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditEmployeeOpen(false)}>
                İptal
              </Button>
              <Button type="submit">Güncelle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Accrual Dialog */}
      <Dialog open={isAccrualOpen} onOpenChange={setIsAccrualOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Maaş Hak Edişi Ekle</DialogTitle>
            <DialogDescription>
              {selectedEmployee && `${selectedEmployee.first_name} ${selectedEmployee.last_name} için hak ediş ekleyin`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAccrual} className="space-y-4">
            <div>
              <Label htmlFor="accrual_amount">Tutar (₺)</Label>
              <Input
                id="accrual_amount"
                type="number"
                step="0.01"
                value={accrualForm.amount}
                onChange={(e) => setAccrualForm({...accrualForm, amount: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="accrual_description">Açıklama</Label>
              <Textarea
                id="accrual_description"
                value={accrualForm.description}
                onChange={(e) => setAccrualForm({...accrualForm, description: e.target.value})}
                placeholder="Örn: Ocak 2024 Maaş"
                rows={3}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAccrualOpen(false)}>
                İptal
              </Button>
              <Button type="submit">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Maaş Ödemesi Yap</DialogTitle>
            <DialogDescription>
              {selectedEmployee && `${selectedEmployee.first_name} ${selectedEmployee.last_name} için ödeme yapın`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div>
              <Label htmlFor="payment_amount">Tutar (₺)</Label>
              <Input
                id="payment_amount"
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="payment_method">Ödeme Yöntemi</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(value) => setPaymentForm({...paymentForm, payment_method: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nakit">Nakit</SelectItem>
                  <SelectItem value="Banka Havalesi">Banka Havalesi</SelectItem>
                  <SelectItem value="EFT">EFT</SelectItem>
                  <SelectItem value="Çek">Çek</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="payment_description">Açıklama</Label>
              <Textarea
                id="payment_description"
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                placeholder="Örn: Ocak 2024 Maaş Ödemesi"
                rows={3}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsPaymentOpen(false)}>
                İptal
              </Button>
              <Button type="submit">Ödeme Yap</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transactions Dialog */}
      <Dialog open={isTransactionsOpen} onOpenChange={setIsTransactionsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEmployee && `${selectedEmployee.first_name} ${selectedEmployee.last_name} - Maaş Hareketleri`}
            </DialogTitle>
          </DialogHeader>
          <TransactionsTable
            transactions={transactions[selectedEmployee?.id] || []}
            employeeId={selectedEmployee?.id}
            sessionToken={sessionToken}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Employee Card Component
const EmployeeCard = ({ employee, onEdit, onDelete, onViewTransactions, onAddAccrual, onAddPayment, sessionToken }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await axios.get(`${API}/employees/${employee.id}/balance`, {
          params: { session_token: sessionToken }
        });
        setBalance(response.data);
      } catch (error) {
        console.error("Bakiye getirme hatası:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBalance();
  }, [employee.id, sessionToken]);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{`${employee.first_name} ${employee.last_name}`}</CardTitle>
            <Badge variant="outline" className="mt-1">{employee.position}</Badge>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center text-sm">
          <User className="h-4 w-4 mr-2 text-gray-500" />
          <span>{employee.phone}</span>
        </div>
        <div className="flex items-center text-sm">
          <Calendar className="h-4 w-4 mr-2 text-gray-500" />
          <span>Başlangıç: {new Date(employee.start_date).toLocaleDateString('tr-TR')}</span>
        </div>
        <div className="flex items-center text-sm">
          <DollarSign className="h-4 w-4 mr-2 text-gray-500" />
          <span>Maaş: ₺{employee.salary?.toLocaleString('tr-TR')}</span>
        </div>

        {loading ? (
          <div className="text-center py-2">
            <RefreshCw className="h-4 w-4 animate-spin mx-auto" />
          </div>
        ) : balance && (
          <div className="pt-3 border-t">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Toplam Hak Ediş:</span>
                <span className="font-medium">₺{balance.total_accruals?.toLocaleString('tr-TR')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Toplam Ödeme:</span>
                <span className="font-medium">₺{balance.total_payments?.toLocaleString('tr-TR')}</span>
              </div>
              <div className="flex justify-between text-sm font-bold">
                <span>Borç Bakiyesi:</span>
                <span className={balance.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                  ₺{balance.balance?.toLocaleString('tr-TR')}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onAddAccrual} className="text-xs">
            <Plus className="h-3 w-3 mr-1" />
            Hak Ediş
          </Button>
          <Button variant="outline" size="sm" onClick={onAddPayment} className="text-xs">
            <DollarSign className="h-3 w-3 mr-1" />
            Ödeme
          </Button>
          <Button variant="outline" size="sm" onClick={onViewTransactions} className="text-xs">
            <List className="h-3 w-3 mr-1" />
            Hareketler
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Transactions Table Component
const TransactionsTable = ({ transactions, employeeId, sessionToken }) => {
  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <ClipboardList className="h-12 w-12 mx-auto mb-2 text-gray-400" />
        <p>Henüz hareket bulunmuyor</p>
      </div>
    );
  }

  // Calculate running balance for each transaction
  // Transactions come from backend sorted oldest to newest (ascending)
  const transactionsWithBalance = transactions.map((transaction, index, arr) => {
    // Calculate balance up to this point (from oldest to current)
    let balance = 0;
    for (let i = 0; i <= index; i++) {
      if (arr[i].transaction_type === 'accrual') {
        balance += arr[i].amount;
      } else {
        balance -= arr[i].amount;
      }
    }
    return { ...transaction, balance };
  });

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-2">Tarih</th>
              <th className="text-left py-2 px-2">Tip</th>
              <th className="text-left py-2 px-2">Açıklama</th>
              <th className="text-left py-2 px-2">Ödeme Yöntemi</th>
              <th className="text-right py-2 px-2">Tutar</th>
              <th className="text-right py-2 px-2">Kalan Bakiye</th>
            </tr>
          </thead>
          <tbody>
            {transactionsWithBalance.map((transaction) => (
              <tr key={transaction.id} className="border-b hover:bg-gray-50">
                <td className="py-2 px-2 text-sm">
                  {new Date(transaction.transaction_date).toLocaleDateString('tr-TR')}
                </td>
                <td className="py-2 px-2">
                  {transaction.transaction_type === 'accrual' ? (
                    <Badge variant="destructive" className="text-xs">Hak Ediş</Badge>
                  ) : (
                    <Badge variant="default" className="text-xs bg-green-600">Ödeme</Badge>
                  )}
                </td>
                <td className="py-2 px-2 text-sm">{transaction.description}</td>
                <td className="py-2 px-2 text-sm">{transaction.payment_method || '-'}</td>
                <td className={`py-2 px-2 text-sm text-right font-medium ${
                  transaction.transaction_type === 'accrual' ? 'text-red-600' : 'text-green-600'
                }`}>
                  {transaction.transaction_type === 'accrual' ? '+' : '-'}₺{transaction.amount?.toLocaleString('tr-TR')}
                </td>
                <td className={`py-2 px-2 text-sm text-right font-bold ${
                  transaction.balance > 0 ? 'text-red-600' : transaction.balance < 0 ? 'text-green-600' : 'text-gray-600'
                }`}>
                  ₺{transaction.balance?.toLocaleString('tr-TR')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


export default App;